import type {
  BusinessQuoteCheckoutMode,
  CartItem,
  Product,
  ProductBoxOption,
  ProductCourse,
  ProductManual,
  ProductVariationAttribute,
  ProductVariationSelectorConfig,
  ProductVideoAsset,
  VehicleFilter,
} from './types'
import { shouldCacheStorefrontCatalogResponse } from './catalog-response-cache'
import { API_BASE_URL } from './apiBaseUrl'
import {
  clearAuthenticatedEntidadeId,
  getAuthenticatedEntidadeId,
  setAuthenticatedEntidadeId,
} from './authenticated-entidade'
import {
  getBackendAvailabilityState,
  markBackendAvailable,
  markBackendUnavailable,
  subscribeBackendAvailability,
  type BackendAvailabilityState,
} from './backend-availability'
import {
  isPossiblePhoneDigits,
  normalizePhoneDigits,
  normalizePhoneToStoredDigits,
  toInternationalRequestPhone,
} from './phone'
import {
  isMidProductImageUrl,
  normalizeProductImageUrl,
  withProductImagePlaceholder,
} from './product-image-placeholder'
import {
  configureStorefrontPublicPricing,
  getStorefrontPublicPricingEntidadeId,
} from './storefront-public-pricing'
import {
  isSimplifiedStorefrontEnabled,
  isSimplifiedStorefrontPlaceholderPrice,
} from './simplified-storefront'
import {
  assertStorefrontConfigIdentity,
  normalizeStorefrontIntegrationServer,
} from './storefront-config-identity'
import {
  normalizeBuyerCommerceContext,
  type BuyerCommerceContext,
} from './commerce-context'
import {
  BUYER_SESSION_EXPIRED_EVENT,
  BUYER_SESSION_TOKEN_RESET_EVENT,
  getBuyerSessionToken,
  setBuyerSessionToken,
} from './buyer-session'
import {
  buildStorefrontPurchaseOptionProduct,
  resolveStorefrontCartBaseProductId,
  resolveStorefrontCartBoxQuantity,
} from './storefront-purchase-option'
import {
  getLojaVirtualDefaultLojaId,
  LOJA_VIRTUAL_DEFAULT_LOJA_ID,
  STOREFRONT_RUNTIME_IDENTITY,
} from './storefront-runtime'

export type { BackendAvailabilityState } from './backend-availability'
export type { ProductManual } from './types'
export {
  BUYER_SESSION_EXPIRED_EVENT,
  BUYER_SESSION_TOKEN_RESET_EVENT,
  buildStorefrontPurchaseOptionProduct,
  clearAuthenticatedEntidadeId,
  getBuyerSessionToken,
  getAuthenticatedEntidadeId,
  getBackendAvailabilityState,
  getLojaVirtualDefaultLojaId,
  resolveStorefrontCartBaseProductId,
  resolveStorefrontCartBoxQuantity,
  setBuyerSessionToken,
  setAuthenticatedEntidadeId,
  subscribeBackendAvailability,
}

export type AtendimentoAvaliacaoGuestIdentity = {
  nome: string
  whatsapp: string
  cnpj: string
}

export type AtendimentoAvaliacaoSubmission = {
  audio: Blob
  submissionId: string
  lojaId: number
  durationSeconds: number
  guestIdentity?: AtendimentoAvaliacaoGuestIdentity
}

export type AtendimentoAvaliacaoSubmissionResponse = {
  status: number
  avaliacao_id: number
  submission_id: string
  message: string
}

export type QuickCheckoutItemInput = {
  produto_id: number | string
  produto_grade_id?: number | string | null
  quantidade: number
}

export type QuickCheckoutPrepareInput = {
  tipoPessoa: 'pf' | 'pj'
  nome: string
  responsavel?: string
  razaoSocial?: string
  inscricaoEstadual?: string
  whatsapp: string
  email: string
  documento: string
  cpf?: string
  cnpj?: string
  cep: string
  cepFiscal?: string
  enderecoFiscal?: string
  numeroFiscal?: string
  bairroFiscal?: string
  complementoFiscal?: string
  cidadeFiscal?: string
  ufFiscal?: string
  enderecoEntrega?: string
  numeroEntrega?: string
  bairroEntrega?: string
  complementoEntrega?: string
  cidadeEntrega?: string
  ufEntrega?: string
  itens: QuickCheckoutItemInput[]
  lojaId?: number
  idempotencyKey?: string
  affiliateCode?: string
}

export type QuickCheckoutFreightQuoteInput = {
  cep: string
  cepFiscal?: string
  itens: QuickCheckoutItemInput[]
  lojaId?: number
  affiliateCode?: string
}

export type QuickCheckoutFreteOption = {
  id: string
  label: string
  modo: 'cif'
  transportadora_id: number | null
  valor: number
  valor_original?: number | null
  numero_cotacao?: string | null
  operadora?: string | null
  prazo_dias: number | null
  media_avaliacao?: number | null
  total_avaliacoes?: number | null
  retira_no_local?: number | boolean | null
  alerta_texto?: string | null
}

export type QuickCheckoutPaymentOption = {
  id: 'pix' | 'mercado_pago_cartao'
  forma_pagamento_id: 8 | 9
  label: string
}

export type QuickCheckoutResumoItem = {
  produto_id: number
  produto_grade_id: number
  nome: string
  codigo: number | null
  referencia: string | null
  quantidade: number
  valor_unitario: number
  subtotal: number
  ipi: number
  st: number
  total: number
}

export type QuickCheckoutResumo = {
  loja_id: number
  entidade_id: number
  pre_cadastro_id: number
  comprador: {
    nome: string
    whatsapp: string
    cpf: string
    cep: string
  }
  itens: QuickCheckoutResumoItem[]
  subtotal: number
  ipi: number
  st: number
  impostos: number
  frete: number
  total: number
  oferta_afiliado: {
    preco_anuncio: number
    preco_recalculado: number
    desconto: number
    comissao_afiliado: number
    base_comercial_protegida: number
    valido_ate: string | null
    aplicado: 0 | 1
  } | null
  expires_at: string
}

export type QuickCheckoutPrepareResponse = {
  status: 1
  checkout_id: string
  status_fluxo: string
  calculo_hash: string
  resumo: QuickCheckoutResumo
  fretes: QuickCheckoutFreteOption[]
  pagamentos: QuickCheckoutPaymentOption[]
  affiliate?: QuickCheckoutAffiliateSummary | null
}

export type QuickCheckoutFreightQuoteResponse = {
  status: 1
  status_fluxo: string
  resumo: QuickCheckoutResumo
  fretes: QuickCheckoutFreteOption[]
  pagamentos: QuickCheckoutPaymentOption[]
  affiliate?: QuickCheckoutAffiliateSummary | null
}

export type QuickCheckoutAffiliateSummary = {
  code: string
  buyer_locked: number
  affiliate_name: string
}

export type AffiliateBuyerInput = {
  entidadeId: number
} | {
  tipoPessoa: 'pf' | 'pj'
  nome: string
  razaoSocial?: string
  contato?: string
  contatoCargo?: string
  aceitaMarketing?: boolean
  cpfCnpj: string
  inscricaoEstadual?: string
  simples?: 0 | 1
  email?: string
  whatsapp: string
  atividadeId: number
  atividade?: string
  finalidade?: 0 | 1 | 2
  cep: string
  endereco: string
  numero: string
  bairro: string
  complemento?: string
  cidade: string
  uf: string
}

export type AffiliatePriceResponse = {
  status: 1
  base_price: number
  buyer_base_price: number
  protected_base_price: number
  desired_earnings: number
  affiliate_price: number
  ipi: number
  st: number
  difal: number
  taxes: number
  final_sale_price: number
  loja_id: number
}

export type AffiliateLinkCreateResponse = AffiliatePriceResponse & {
  link_id: number
  link_code: string
  commission_value: number
  buyer_locked: number
  buyer?: Record<string, unknown> | null
  motivo?: string
}

export type AffiliateCheckoutContextResponse = {
  status: 1
  link: {
    code: string
    loja_id: number
    produto_id: number
    valor_unitario: number
    afiliado_nome: string
    produto: {
      id: number
      name: string
      code: number
      reference: string
      image: string
    }
    items?: Array<{
      produto_id: number
      quantidade: number
      valor_unitario: number
      valor_base: number
      final_sale_price: number
      desired_earnings: number
      produto: {
        id: number
        name: string
        code: number
        reference: string
        image: string
      }
    }>
    comprador_bloqueado: number
    comprador: {
      tipoPessoa: 0 | 1
      nome: string
      responsavel: string
      razaoSocial: string
      inscricaoEstadual: string
      email: string
      whatsapp: string
      cpf: string
      cep: string
    } | null
    endereco: {
      endereco: string
      numero: string
      bairro: string
      complemento: string
      cidade: string
      uf: string
    } | null
  }
}

export type AffiliateCartItemInput = {
  productId: number
  quantity: number
  desiredEarnings: number
}

export type AffiliateCartPriceItem = {
  produto_id: number
  quantidade: number
  base_price: number
  buyer_base_price: number
  protected_base_price: number
  desired_earnings: number
  affiliate_price: number
  ipi: number
  st: number
  difal: number
  taxes: number
  final_sale_price: number
}

export type AffiliateCartPriceResponse = {
  status: 1
  loja_id: number | null
  items: AffiliateCartPriceItem[]
}

export type AffiliateCartLinkCreateResponse = {
  status: 1
  loja_id: number | null
  link_code: string
  motivo?: string
  items: Array<{
    link_id: number
    produto_id: number
    quantidade: number
    desired_earnings: number
    final_sale_price: number
  }>
}

export type QuickCheckoutGenerateInput = {
  checkoutId: string
  calculoHash: string
  freteId: string
  formaPagamentoId: 8 | 9
  idempotencyKey?: string
  finalizarCadastroAposPagamento?: boolean
  email?: string
  cep?: string
  endereco?: string
  numero?: string
  bairro?: string
  complemento?: string
  cidade?: string
  uf?: string
  analytics?: LojaVirtualCheckoutAnalyticsContextInput | null
}

export type LojaVirtualCheckoutAnalyticsContextInput = {
  visitor_id?: string
  session_id?: string
  cart_instance_id?: string
  referrer_url?: string
  referrer_host?: string
  utm_source?: string
  utm_medium?: string
  utm_campaign?: string
  utm_term?: string
  utm_content?: string
  gclid?: string
}

export type QuickCheckoutPaymentResult = {
  metodo: 'pix' | 'mercado_pago_cartao'
  pix?: {
    txid?: string
    brcode?: string
    valor?: number
    expiracao?: number
    expiracao_segundos?: number
  }
  mercado_pago?: {
    preference_id?: string
    url?: string
    init_point?: string | null
    sandbox_init_point?: string | null
    external_reference?: string
    valor?: number
  }
}

export type QuickCheckoutGenerateResponse = {
  status: 1
  checkout_id: string
  status_fluxo: string
  pedido_id: number
  numero_pedido: number
  calculo_hash: string
  resumo: QuickCheckoutResumo
  pagamento: QuickCheckoutPaymentResult
  completion_token: string
}

export type QuickCheckoutCompleteInput = {
  pedidoId: number
  checkoutId: string
  completionToken: string
  email?: string
  cep?: string
  endereco?: string
  numero?: string
  bairro?: string
  complemento?: string
  cidade?: string
  uf?: string
}

export type QuickCheckoutCompleteResponse = {
  status: 1
  checkout_id: string
  pedido_id: number
  status_fluxo: string
  cadastro_completo: number
}

// The browser only talks to the proxy; credentials stay in its environment.
const API_KEY = ''
const INTEGRATION_AUTH_TOKEN_ENDPOINT = '/integracoes/v1/auth/token'
const INTEGRATION_AUTH_REFRESH_ENDPOINT = '/integracoes/v1/auth/refresh'
// Runtime validation remains mandatory before any business configuration is
// consumed. Do not, however, throw while this module is being imported: the
// site-config gate must be able to render its neutral unavailable state.
const INTEGRATION_AUTH_SERVER = STOREFRONT_RUNTIME_IDENTITY?.integrationServer ?? ''
const INTEGRATION_PUBLIC_SERVER = `c2s_${normalizeStorefrontIntegrationServer(INTEGRATION_AUTH_SERVER)}`
// Credenciais de integracao nao podem mais ser embarcadas no bundle do navegador.
const INTEGRATION_AUTH_EMAIL = ''
const INTEGRATION_AUTH_PASSWORD = ''
const INTEGRATION_AUTH_SCOPE = ''
const LOJA_VIRTUAL_PRODUCTS_DEFAULT_QUERY = (import.meta.env.VITE_API_PRODUCTS_DEFAULT_QUERY || '%').trim() || '%'
const BUYER_PRICE_TABLE_REDIRECT_PATH = '/tabela-de-precos'
const FRETE_DEFAULT_ORIGEM_CEP = (import.meta.env.VITE_API_FRETE_ORIGEM_CEP || '89010025').replace(/\D/g, '')
const FRETE_DEFAULT_ORIGEM_CNPJ = (import.meta.env.VITE_API_FRETE_ORIGEM_CNPJ || '').replace(/\D/g, '')
const FRETE_MAX_ATTEMPTS = 4
const FRETE_RETRY_DELAY_MS = 700
const CHECKOUT_FRETE_TRANSPORTADORA_TIMEOUT_MS = 3_500
const CHECKOUT_FRETE_TRANSPORTADORA_CONCURRENCY = 6
const ENABLE_CHECKOUT_FRETE_DEBUG = import.meta.env.DEV
  || String(import.meta.env.VITE_DEBUG_CHECKOUT_FRETE || '').trim() === '1'
const FRETE_TRANSPORTADORAS_DISPONIVEIS_STORAGE_TTL_MS = 30 * 60 * 1000
const FRETE_TRANSPORTADORAS_DISPONIVEIS_STORAGE_KEY = [
  'b2b-commerce',
  'frete-transportadoras-disponiveis',
  'v1',
  INTEGRATION_PUBLIC_SERVER,
  LOJA_VIRTUAL_DEFAULT_LOJA_ID,
].join(':')
const SHOULD_USE_INTEGRATION_AUTH = Boolean(INTEGRATION_AUTH_EMAIL && INTEGRATION_AUTH_PASSWORD)
const ENABLE_STOREFRONT_ANALYTICS = ['1', 'true', 'yes', 'on'].includes(
  String(import.meta.env.VITE_ENABLE_STOREFRONT_ANALYTICS || '').trim().toLowerCase()
)

const TOKEN_EXPIRY_SAFETY_WINDOW_MS = 30_000
const INTEGRATION_AUTH_FAILURE_COOLDOWN_MS = 15_000
const PROXY_INTEGRATION_AUTH_RETRY_DELAY_MS = 350

type ApiVideoItem = {
  id?: number
  url?: string
  descricao?: string
}

type ApiVideoList = Array<string | ApiVideoItem> | string | ApiVideoItem | null | undefined

interface ApiProduct {
  id: number
  title: string
  nome: string
  descricao: string
  codigo: number
  produto_principal_variacao: number
  referencia: string
  produto_com_variacoes: number
  produto_alvo_id: number
  installments: number
  image: string
  image_contextual: number
  images: string[]
  images_contextual: number[]
  originalPrice: number
  discountedPrice: number
  discountPercentage: number
  installmentValue: number
  hasVariations: number
  distribuitionPrice: number | null
  distributionPriceAllowed: boolean
  distributionPriceEnabled: boolean
  marca?: string
  produto_grupo_id?: number
  fichatecnica_json?: string
  videos?: ApiVideoList
}

interface ApiProductDetail {
  id: number
  title: string
  nome: string
  descricao: string
  referencia: string
  marca: string
  produto_grupo_id: number
  originalprice: number
  discountedprice: number
  discountpercentage: number
  fichatecnica_json: string
  produto_com_variacoes: number
  images: string[]
  images_contextual: number[]
  produtoGrupoId: number
  videos?: ApiVideoList
  distribuitionPrice: number | null
  distributionPriceAllowed: boolean
  distributionPriceEnabled: boolean
}

interface ApiResponse<T> {
  data: T
}

interface ApiListResponse<T> {
  data: T[]
  pagination?: {
    limit: number
    offset: number
  }
}

export type PaginatedListResult<T> = {
  items: T[]
  page: number
  perPage: number
  total: number
  totalPages: number
}

type ClienteExistsApiResponse = {
  exists?: boolean | number | string
  id?: string | number | null
  nome?: string | null
  razao?: string | null
  acesso_loja_configurado?: boolean | number | string | null
  acessoLojaConfigurado?: boolean | number | string | null
  recuperacao_acesso_disponivel?: boolean | number | string | null
  recuperacaoAcessoDisponivel?: boolean | number | string | null
  cadastro_loja_completar_permitido?: boolean | number | string | null
  cadastroLojaCompletarPermitido?: boolean | number | string | null
}

type AtividadeTreeApiResponse = {
  items?: unknown
  data?: unknown
}

type LojaVirtualPreCadastroApiResponse = {
  status?: number | boolean | string
  id?: number | string | null
  nome?: string | null
  whatsapp?: string | null
  cpf_cnpj?: string | null
  origem?: string | null
  created_at?: string | null
  cadastrado?: number | boolean | string
  ja_existia?: number | boolean | string
  motivo?: string | null
}

type LojaVirtualPreCadastroRetomadaApiResponse = {
  status?: number | boolean | string
  pre_cadastro?: {
    id?: number | string | null
    nome?: string | null
    whatsapp?: string | null
    cpf_cnpj?: string | null
    atividade_id?: number | string | null
    atividade?: string | null
    loja_id?: number | string | null
    motivo?: string | null
  } | null
  whatsapp_validado?: number | boolean | string
  motivo?: string | null
}

type LojaVirtualPreCadastroPhoneCodeApiResponse = {
  status?: number | boolean | string
  id?: number | string | null
  whatsapp?: string | null
  codigo?: string | null
  expires_at?: string | null
  validated_at?: string | null
  created_at?: string | null
  updated_at?: string | null
  whatsapp_oficial_status?: 'sent' | 'error' | null
  whatsapp_oficial_motivo?: string | null
  whatsapp_legado_status?: 'sent' | 'error' | null
  whatsapp_legado_motivo?: string | null
  motivo?: string | null
}

type LojaVirtualPreCadastroPhoneCodeEmailApiResponse = {
  status?: number | boolean | string
  whatsapp?: string | null
  expires_at?: string | null
  motivo?: string | null
}

type LojaVirtualPreCadastroPhoneValidationApiResponse = {
  status?: number | boolean | string
  id?: number | string | null
  precadastro_id?: number | string | null
  precadastroId?: number | string | null
  pre_cadastro_id?: number | string | null
  whatsapp?: string | null
  validado?: number | boolean | string
  validated_at?: string | null
  expires_at?: string | null
  motivo?: string | null
}

type IntegrationWhatsappSendApiResponse = {
  status?: number | boolean | string
  numero?: string | null
  chat_id?: string | null
  message_id?: string | null
  motivo?: string | null
}

export type AtividadeTreeItem = {
  id: string | number
  descricao?: string | null
  pai_id?: number | null
  icone?: string | null
  filhos?: number | null
  pessoa_fisica_cpf?: 0 | 1 | null
  consumo?: 0 | 1 | null
  revenda?: 0 | 1 | null
  fabricacao?: 0 | 1 | null
}

export type AtividadesFiltersResponse = {
  atividades?: Array<string | { id?: string | number; descricao?: string | null }>
  [key: string]: unknown
}

export type ClienteExistsResult = {
  exists: boolean
  id: string
  nome: string
  acessoLojaConfigurado: boolean
  recuperacaoAcessoDisponivel: boolean
  cadastroLojaCompletarPermitido: boolean
}

export type OrderTrackingLookupBuyer = {
  id: number
  nome?: string
  email?: string
  cpfCnpj?: string
  endereco?: string
  numero?: string
  bairro?: string
  complemento?: string
  cep?: string
  cidade?: string
  uf?: string
}

export type OrderTrackingLookupResult = {
  entidadeId: number
  buyer: OrderTrackingLookupBuyer
  order: any
}

/**
 * O endpoint público de rastreamento usa 404 quando a combinação de pedido e
 * documento não existe. Isso é um resultado esperado da consulta, não uma
 * rota ausente.
 */
export class OrderTrackingLookupNotFoundError extends Error {
  constructor() {
    super('Order tracking lookup did not find a matching order.')
    this.name = 'OrderTrackingLookupNotFoundError'
  }
}

export function isOrderTrackingLookupNotFoundError(error: unknown): error is OrderTrackingLookupNotFoundError {
  return error instanceof OrderTrackingLookupNotFoundError
}

export type ClienteCreatePayload = {
  tipopessoa: number
  cpf_cnpj: string
  precadastro_id?: number
  vendedor_cadastro_token?: string
  loja_id?: number
  origem?: string
  senha?: string
  acesso_loja_senha?: string
  acesso_loja_ativo?: number | boolean
  tpcliente?: number | boolean
  inativo?: number | boolean
  razao: string
  nome?: string
  ie?: string
  simples?: number
  telefone: string
  whatsapp: string
  contato?: string
  contato_cargo?: string
  contato_aceita_marketing?: number
  email?: string
  tipo_entidade_atividade_id: number
  atividade?: string
  cep: string
  endereco: string
  numero: string
  complemento?: string
  bairro: string
  cidade: string
  uf: string
  finalidade?: number
}

export type LojaVirtualPreCadastroResult = {
  id: number
  nome: string
  whatsapp: string
  cpfCnpj: string
  origem: string
  createdAt: string | null
  cadastrado: boolean
  jaExistia: boolean
  motivo: string
}

export type LojaVirtualPreCadastroRetomadaResult = {
  preCadastroId: number
  nome: string
  whatsapp: string
  cpfCnpj: string
  atividadeId: number
  atividade: string
  lojaId: number
  documentType: 'cpf' | 'cnpj'
  whatsappValidado: boolean
  motivo: string
}

export type LojaVirtualPreCadastroPhoneCodeResult = {
  id: number
  whatsapp: string
  codigo: string
  expiresAt: string | null
  validatedAt: string | null
  createdAt: string | null
  updatedAt: string | null
  whatsappOfficialStatus: 'sent' | 'error' | null
  whatsappOfficialMotivo: string
  whatsappLegacyStatus: 'sent' | 'error' | null
  whatsappLegacyMotivo: string
  motivo: string
}

export type LojaVirtualPreCadastroPhoneValidationResult = {
  preCadastroId: number
  whatsapp: string
  validado: boolean
  validatedAt: string | null
  expiresAt: string | null
  motivo: string
}

export type LojaVirtualPreCadastroPhoneCodeEmailResult = {
  whatsapp: string
  expiresAt: string | null
  motivo: string
}

export type IntegrationWhatsappSendResult = {
  status: boolean
  numero: string
  chatId: string
  messageId: string
  motivo: string
}

export type StorefrontSiteConfig = {
  loja_id: number
  global_store_id?: string
  rede_lojas_menu_ativo?: boolean
  visitor_country_code?: string
  // Only stores explicitly activated on the canonical contract expose this
  // envelope. Legacy remains the default during the migration.
  storefront_identity?: {
    version: 1
    integration_server: string
    loja_id: number
    public_url: string
  }
  experiencia_config?: StorefrontExperienceConfig
  nome: string
  tipo: number
  modelo_categoria: number
  logo_url: string
  logo_url_escuro: string
  favicon_url: string
  cor_primaria: string
  cor_secundaria: string
  cor_lancamentos: string
  cor_acabou_chegar: string
  cor_card_preco: string
  cor_preco: string
  cor_botao_comprar: string
  cor_botao_adicionar_carrinho: string
  cor_fundo_foto_grande: string
  whatsapp: string
  whatsapp_compras: string
  whatsapp_financeiro: string
  atendimento_modelo: 'ticket' | 'whatsapp'
  whatsapp_suporte_tecnico: string
  whatsapp_notificacoes_pedido: string
  whatsapp_notificacoes_pedido_habilitado: boolean
  cadastro_codigo_email_habilitado: boolean
  telefone: string
  email: string
  empresa_nome: string
  empresa_documento: string
  endereco: string
  mostrar_precos: boolean
  cliente_pior_cenario_id: number | null
  mostrar_menu_aplicativos: boolean
  exibir_apenas_produtos_com_estoque: boolean
  produto_sem_estoque_modo: number
  mostrar_produtos_sem_estoque_indisponiveis: boolean
  filtrar_marcas_busca: boolean
  filtrar_placa_modelo_busca: boolean
  exibir_carrossel_marcas: boolean
  banner_url: string
  redes_sociais: Record<string, unknown>
  busca_config: Record<string, unknown>
  scripts: string
  textos_institucionais: Record<string, unknown>
  seo_title: string
  seo_description: string
  checkout_config: {
    attempt_recovery_enabled: boolean
    site_config_cache_ttl_ms: number | null
  }
}

export type StorefrontSiteMetrics = {
  itens_vendidos: number
  atualizado_em: string | null
}

export type StorefrontExperienceConfig = {
  version: 1
  recursos: {
    comunidade: boolean
    carreiras: boolean
    universidade: boolean
    politicas: boolean
  }
  navegacao: Array<{
    key: string
    ativo: boolean
    label: string
    url: string
    ordem: number
    externo?: boolean
  }>
  microtextos: {
    descricao_rodape?: string
    copyright?: string
    nome_assistente?: string
    mensagem_whatsapp?: string
  }
  assets: { public_base_url: string }
  pwa: {
    enabled: boolean
    short_name: string
    background_color: string
    theme_color: string
    icon_192_url?: string
    icon_512_url?: string
    icon_maskable_512_url?: string
  }
}

type StorefrontSiteConfigApiResponse = Partial<StorefrontSiteConfig> | null | undefined
type StorefrontSiteConfigRequestOptions = {
  signal?: AbortSignal
  headers?: HeadersInit
  cache?: RequestCache
  cacheBust?: string | number | boolean
}

interface LojaVirtualProdutoItem {
  produto_id: number
  produto_principal_variacao?: number | null
  navigation_target_produto_id?: number | string | null
  navigationTargetProductId?: number | string | null
  navigation_target_produto_nome?: string | null
  navigationTargetProductName?: string | null
  navigation_parent_context_id?: number | string | null
  navigationParentContextId?: number | string | null
  referencia?: string | null
  nome?: string | null
  nome_localizado?: number | boolean | null
  nome_curto?: string | null
  serie?: string | null
  tamanho?: string | null
  modelo?: string | null
  informacoes_adicionais?: string | null
  ordem?: number | string | null
  order?: number | string | null
  sequencia?: number | string | null
  posicao?: number | string | null
  position?: number | string | null
  classe_id?: number | null
  produto_grupo_id?: number | null
  produto_grupo_nome?: string | null
  produto_grupo_familia_nome?: string | null
  possui_variacoes?: number
  exibir_variacoes_busca?: number | null
  usar_seletor_variacao?: number | null
  marca_fabricante_id?: number | null
  marca_fabricante_nome?: string | null
  marca_fabricante_logo_url?: string | null
  marca?: string | null
  estoque?: number
  imagem?: string | null
  imagem_mid?: string | null
  imagem_high?: string | null
  fotos?: LojaVirtualProdutoDetalheFoto[] | null
  url?: string | null
  avaliacoes?: number | string | null
  nota?: number | string | null
  preco?: number | string | null
  preco_atacado?: number | string | null
  precoAtacado?: number | string | null
  preco_cnpj_ie?: number | string | null
  preco_distribuicao?: number | string | null
  preco_pendente?: number | boolean | null
  peso_bruto?: number | string | null
  altura?: number | string | null
  largura?: number | string | null
  comprimento?: number | string | null
  ncm?: string | null
  NCM?: string | null
  codigo_ncm?: string | null
  codigoNcm?: string | null
  produto_ncm?: string | null
  produtoNcm?: string | null
  classificacao_fiscal?: { ncm?: string | null; codigo?: string | null } | string | null
  classificacaoFiscal?: { ncm?: string | null; codigo?: string | null } | string | null
  classificacao_fiscal_ncm?: string | null
  classificacaoFiscalNcm?: string | null
  hs_code?: string | null
  hsCode?: string | null
  fichatecnica?: unknown
  fichatecnica_json?: unknown
  atributos?: LojaVirtualVariacaoCaracteristica[] | null
  caracteristicas?: LojaVirtualVariacaoCaracteristica[] | null
}

interface LojaVirtualProdutosResponse {
  status?: number
  loja_id?: number
  entidade_id?: number | null
  exibir_precos?: number
  precos_pendentes?: number
  q?: string
  limit?: number
  offset?: number
  total?: number
  items?: LojaVirtualProdutoItem[]
}

interface LojaVirtualHomeProdutosSectionResponse {
  status?: 'ok' | 'error'
  limit?: number
  offset?: number
  total?: number
  items?: LojaVirtualProdutoItem[]
}

interface LojaVirtualHomeProdutosResponse {
  status?: number
  loja_id?: number
  entidade_id?: number | null
  exibir_precos?: number
  precos_pendentes?: number
  sections?: {
    lancamentos?: LojaVirtualHomeProdutosSectionResponse
    acabaram_de_chegar?: LojaVirtualHomeProdutosSectionResponse
  }
}

type StorefrontImageSearchConfidenceBand = 'high' | 'medium' | 'none'
type StorefrontImageSearchMatchStatus = 'ok' | 'fora_do_catalogo'

interface LojaVirtualBuscaImagemResponse extends LojaVirtualProdutosResponse {
  match_status?: StorefrontImageSearchMatchStatus
  confidence_band?: StorefrontImageSearchConfidenceBand
  message?: string | null
}

export type StorefrontImageSearchResult = {
  matchStatus: StorefrontImageSearchMatchStatus
  confidenceBand: StorefrontImageSearchConfidenceBand
  message: string | null
  total: number
  items: Product[]
}

interface LojaVirtualVariacaoConfiguracao {
  variacao_id?: number | null
  produto_variacao_id?: number | null
  variacao?: string | null
  variacao_descricao?: string | null
  descricao?: string | null
  ordem?: number | string | null
  order?: number | string | null
  sequencia?: number | string | null
  posicao?: number | string | null
  position?: number | string | null
  atributo_selecionavel?: number | boolean | null
  atributo_selecionavel_tipo?: string | null
}

export type PaginatedProductListResult = {
  items: Product[]
  limit: number
  offset: number
  total: number | null
  pricingPending?: boolean
}

export type HomeProductShowcasesResult = {
  launches: PaginatedProductListResult
  justArrived: PaginatedProductListResult
}

export type PaginatedFilteredProductListResult = PaginatedProductListResult & {
  nextOffset: number
  hasMore: boolean
}

type ProductManualApiItem = {
  id?: string | number | null
  tipo?: string | number | null
  nome?: string | null
  download_url?: string | null
}

type ProductManualsApiResponse = {
  status?: number
  produto_id?: number
  total?: number
  manuais?: ProductManualApiItem[]
}

type ProductSiteDescriptionHtmlApiResponse = {
  status?: number | string | boolean
  produto_id?: number | string | null
  site_descricao_html?: string | null
  siteDescricaoHtml?: string | null
  descricao_html?: string | null
  descricaoHtml?: string | null
  html?: string | null
  conteudo?: string | null
  content?: string | null
  produto?: {
    site_descricao_html?: string | null
    siteDescricaoHtml?: string | null
    descricao_html?: string | null
    descricaoHtml?: string | null
  } | null
}

export type ProductManualDownloadResult =
  | { type: 'url'; url: string }
  | { type: 'blob'; blob: Blob; fileName?: string | null }

export type StorefrontManufacturerBrand = {
  id: number
  name: string
  logoUrl: string | null
  productCount: number
}

export type StorefrontVehicleOption = {
  id: string
  code: string
  name: string
}

export type StorefrontVehiclePlate = {
  placa: string
  marca: string
  modelo: string
  ano: string
  versao: string
}

type StorefrontManufacturerBrandsCacheEntry = {
  expiresAt: number
  items: StorefrontManufacturerBrand[]
}

interface LojaVirtualMarcaItem {
  id?: number | null
  nome?: string | null
  logo_url?: string | null
  total_produtos?: number | null
}

interface LojaVirtualMarcasResponse {
  status?: number
  loja_id?: number
  q?: string | null
  produto_q?: string | null
  total?: number
  items?: LojaVirtualMarcaItem[]
}

const STOREFRONT_MANUFACTURER_BRANDS_CACHE_TTL_MS = 10 * 60 * 1000
const STOREFRONT_MANUFACTURER_BRANDS_SEARCH_CACHE_TTL_MS = 90 * 1000
const STOREFRONT_MANUFACTURER_BRANDS_STORAGE_PREFIX = 'storefront-manufacturer-brands:v2:'
const storefrontManufacturerBrandsCache = new Map<string, StorefrontManufacturerBrandsCacheEntry>()
const storefrontManufacturerBrandsRequestCache = new Map<string, Promise<StorefrontManufacturerBrand[]>>()

function cloneStorefrontManufacturerBrands(
  items: StorefrontManufacturerBrand[]
): StorefrontManufacturerBrand[] {
  return items.map((item) => ({ ...item }))
}

function normalizeCachedStorefrontManufacturerBrand(
  value: unknown
): StorefrontManufacturerBrand | null {
  if (!value || typeof value !== 'object') return null

  const source = value as Record<string, unknown>
  const rawId = Number(source.id ?? 0)
  if (!Number.isFinite(rawId) || rawId <= 0) return null

  const name = String(source.name ?? '').trim()
  if (!name) return null

  return {
    id: Math.trunc(rawId),
    name,
    logoUrl: normalizeImageUrl(String(source.logoUrl ?? '')) || null,
    productCount: Number.isFinite(Number(source.productCount))
      ? Math.max(0, Math.trunc(Number(source.productCount)))
      : 0,
  }
}

function getStorefrontManufacturerBrandsStorage(): Storage | null {
  if (typeof window === 'undefined') return null

  try {
    return window.localStorage
  } catch {
    return null
  }
}

function readPersistedStorefrontManufacturerBrandsCache(
  cacheKey: string
): StorefrontManufacturerBrandsCacheEntry | null {
  const storage = getStorefrontManufacturerBrandsStorage()
  if (!storage) return null

  const storageKey = `${STOREFRONT_MANUFACTURER_BRANDS_STORAGE_PREFIX}${cacheKey}`

  try {
    const rawValue = storage.getItem(storageKey)
    if (!rawValue) return null

    const parsed = JSON.parse(rawValue) as {
      expiresAt?: unknown
      items?: unknown
    }
    const expiresAt = Number(parsed.expiresAt)
    if (!Number.isFinite(expiresAt)) {
      storage.removeItem(storageKey)
      return null
    }

    const items = Array.isArray(parsed.items)
      ? parsed.items
        .map((item) => normalizeCachedStorefrontManufacturerBrand(item))
        .filter((item): item is StorefrontManufacturerBrand => Boolean(item))
      : []

    return {
      expiresAt,
      items,
    }
  } catch {
    storage.removeItem(storageKey)
    return null
  }
}

function persistStorefrontManufacturerBrandsCache(
  cacheKey: string,
  entry: StorefrontManufacturerBrandsCacheEntry
) {
  const storage = getStorefrontManufacturerBrandsStorage()
  if (!storage) return

  const storageKey = `${STOREFRONT_MANUFACTURER_BRANDS_STORAGE_PREFIX}${cacheKey}`

  try {
    storage.setItem(storageKey, JSON.stringify(entry))
  } catch {
    // Ignore storage write failures and keep the in-memory cache alive.
  }
}

function getStorefrontManufacturerBrandsCacheEntry(
  cacheKey: string,
  allowExpired: boolean = false
): StorefrontManufacturerBrandsCacheEntry | null {
  const now = Date.now()
  const cached = storefrontManufacturerBrandsCache.get(cacheKey)

  if (cached) {
    if (allowExpired || cached.expiresAt > now) {
      return {
        expiresAt: cached.expiresAt,
        items: cloneStorefrontManufacturerBrands(cached.items),
      }
    }

    storefrontManufacturerBrandsCache.delete(cacheKey)
  }

  const persisted = readPersistedStorefrontManufacturerBrandsCache(cacheKey)
  if (!persisted) return null

  if (!allowExpired && persisted.expiresAt <= now) {
    storefrontManufacturerBrandsCache.delete(cacheKey)
    return null
  }

  storefrontManufacturerBrandsCache.set(cacheKey, {
    expiresAt: persisted.expiresAt,
    items: cloneStorefrontManufacturerBrands(persisted.items),
  })

  return {
    expiresAt: persisted.expiresAt,
    items: cloneStorefrontManufacturerBrands(persisted.items),
  }
}

function setCachedStorefrontManufacturerBrands(
  cacheKey: string,
  items: StorefrontManufacturerBrand[],
  ttlMs: number
) {
  const entry: StorefrontManufacturerBrandsCacheEntry = {
    expiresAt: Date.now() + ttlMs,
    items: cloneStorefrontManufacturerBrands(items),
  }

  storefrontManufacturerBrandsCache.set(cacheKey, entry)
  persistStorefrontManufacturerBrandsCache(cacheKey, entry)
}

export type LojaVirtualAnalyticsEventPayload = Record<string, unknown>

export type LojaVirtualAnalyticsEventInput = {
  visitor_id?: string
  session_id?: string
  entidade_id?: number
  produto_id?: number
  site_pagina_id?: number
  device_type?: 'desktop' | 'mobile'
  deviceType?: 'desktop' | 'mobile'
  event_type: string
  page_kind?: string
  page_key?: string
  page_path?: string
  event_key?: string
  client_event_id?: string
  occurred_at?: string
  referrer_url?: string
  referrer_host?: string
  landing_path?: string
  utm_source?: string
  utm_medium?: string
  utm_campaign?: string
  utm_term?: string
  utm_content?: string
  gclid?: string
  payload?: LojaVirtualAnalyticsEventPayload
}

export type LojaVirtualAnalyticsBatchRequest = {
  loja_id?: number
  entidade_id?: number
  visitor_id?: string
  session_id?: string
  device_type?: 'desktop' | 'mobile'
  deviceType?: 'desktop' | 'mobile'
  page_kind?: string
  page_key?: string
  page_path?: string
  referrer_url?: string
  referrer_host?: string
  landing_path?: string
  utm_source?: string
  utm_medium?: string
  utm_campaign?: string
  utm_term?: string
  utm_content?: string
  gclid?: string
  events: LojaVirtualAnalyticsEventInput[]
}

export type LojaVirtualAnalyticsBatchResponse = {
  status?: number
  total?: number
  inserted?: number
  skipped?: number
  motivo?: string
}

interface LojaVirtualProdutoDetalheFoto {
  id?: number
  principal?: number
  ordem?: number | null
  descricao?: string | null
  obsfoto?: string | null
  imagem?: string | null
  imagem_mid?: string | null
  imagem_high?: string | null
}

interface LojaVirtualProdutoDetalheVideo {
  id?: number
  descricao?: string | null
  url?: string | null
  gif_url?: string | null
  exibir_busca?: number
}

interface LojaVirtualProductCourse {
  id?: string | number | null
  curso_id?: number | null
  curso_externo_id?: string | null
  curso_legado_id?: number | null
  course_id?: string | null
  legacy_course_id?: number | null
  titulo?: string | null
  title?: string | null
  curso_descricao?: string | null
  slug?: string | null
  curso_slug?: string | null
  resumo?: string | null
  summary?: string | null
  cargahoraria?: string | null
  duration?: string | null
  categoria?: string | null
  category?: string | null
  instrutor_nome?: string | null
  instructor_name?: string | null
  thumbnail?: string | null
  universidade_url?: string | null
  university_url?: string | null
}

interface LojaVirtualProdutoDetalhe {
  id: number
  produto_principal_variacao?: number | null
  referencia?: string | null
  ncm?: string | null
  NCM?: string | null
  codigo_ncm?: string | null
  codigoNcm?: string | null
  produto_ncm?: string | null
  produtoNcm?: string | null
  classificacao_fiscal?: { ncm?: string | null; codigo?: string | null } | string | null
  classificacaoFiscal?: { ncm?: string | null; codigo?: string | null } | string | null
  classificacao_fiscal_ncm?: string | null
  classificacaoFiscalNcm?: string | null
  hs_code?: string | null
  hsCode?: string | null
  nome?: string | null
  nome_exibicao?: string | null
  nome_localizado?: number | boolean | null
  ordem?: number | string | null
  order?: number | string | null
  sequencia?: number | string | null
  posicao?: number | string | null
  position?: number | string | null
  marca?: string | null
  marca_fabricante_id?: number | null
  marca_fabricante_nome?: string | null
  marca_fabricante_logo_url?: string | null
  serie?: string | null
  tamanho?: string | null
  modelo?: string | null
  informacoes_adicionais?: string | null
  classe_id?: number | null
  produto_grupo_id?: number | null
  produto_grupo_nome?: string | null
  produto_grupo_familia_nome?: string | null
  avaliacoes?: number | string | null
  nota?: number | string | null
  possui_variacoes?: number
  exibir_variacoes_busca?: number | null
  usar_seletor_variacao?: number | null
  estoque?: number
  preco?: number | string | null
  preco_atacado?: number | string | null
  precoAtacado?: number | string | null
  preco_cnpj_ie?: number | string | null
  preco_distribuicao?: number | string | null
  imagem?: string | null
  fichatecnica?: unknown
  fotos?: LojaVirtualProdutoDetalheFoto[]
  videos?: LojaVirtualProdutoDetalheVideo[]
  cursos?: LojaVirtualProductCourse[]
  courses?: LojaVirtualProductCourse[]
  configuracoes_variacao?: LojaVirtualVariacaoConfiguracao[] | null
  variacoes?: LojaVirtualProdutoItem[]
}

interface LojaVirtualProdutoDetalheResponse {
  status?: number
  loja_id?: number
  entidade_id?: number | null
  exibir_precos?: number
  produto?: LojaVirtualProdutoDetalhe | null
}

interface LojaVirtualProdutoFormularioRelacaoResponse {
  campo_origem_id?: string | null
  valor_origem?: string | null
  opcoes?: string[]
}

interface LojaVirtualProdutoFormularioCampoResponse {
  id?: string | null
  tipo?: 'select' | 'text' | 'number' | 'info' | 'alert' | null
  nome?: string | null
  observacao?: string | null
  obrigatorio?: number
  opcoes?: string[]
  relacoes?: LojaVirtualProdutoFormularioRelacaoResponse[]
  min?: number | null
  max?: number | null
}

interface LojaVirtualProdutoFormularioResponse {
  status?: number
  produto_id?: number
  exibir_itens_agrupados?: number
  campos?: LojaVirtualProdutoFormularioCampoResponse[]
}

interface LojaVirtualProdutoFormularioComponenteProdutoResponse {
  produto_id?: number
  referencia?: string | null
  ncm?: string | null
  NCM?: string | null
  codigo_ncm?: string | null
  codigoNcm?: string | null
  produto_ncm?: string | null
  produtoNcm?: string | null
  classificacao_fiscal?: { ncm?: string | null; codigo?: string | null } | string | null
  classificacaoFiscal?: { ncm?: string | null; codigo?: string | null } | string | null
  classificacao_fiscal_ncm?: string | null
  classificacaoFiscalNcm?: string | null
  hs_code?: string | null
  hsCode?: string | null
  nome?: string | null
  marca?: string | null
  serie?: string | null
  tamanho?: string | null
  modelo?: string | null
  informacoes_adicionais?: string | null
  classe_id?: number | null
  produto_grupo_id?: number | null
  produto_grupo_nome?: string | null
  produto_grupo_familia_nome?: string | null
  possui_variacoes?: number
  estoque?: number
  preco?: number | string | null
  preco_atacado?: number | string | null
  precoAtacado?: number | string | null
  preco_cnpj_ie?: number | string | null
  preco_distribuicao?: number | string | null
  imagem?: string | null
  url?: string | null
}

interface LojaVirtualProdutoFormularioComponenteItemResponse {
  config_id?: string | null
  produto_id?: number
  quantidade?: number
  selecionado?: number
  bloqueado?: number
  produto?: LojaVirtualProdutoFormularioComponenteProdutoResponse | null
}

interface LojaVirtualProdutoFormularioComponenteGrupoResponse {
  id?: string | null
  nome?: string | null
  obrigatorio?: number
  todos_obrigatorios?: number
  itens?: LojaVirtualProdutoFormularioComponenteItemResponse[]
}

interface LojaVirtualProdutoFormularioComponentesResponse {
  status?: number
  produto_id?: number
  entidade_id?: number | null
  exibir_precos?: number
  total_selecionado?: number | null
  grupos?: LojaVirtualProdutoFormularioComponenteGrupoResponse[]
  manuais?: ProductManualApiItem[]
}

interface LojaVirtualProdutoVariacoesResponse {
  status?: number
  loja_id?: number
  entidade_id?: number | null
  exibir_precos?: number
  produto_id?: number
  configuracoes_variacao?: LojaVirtualVariacaoConfiguracao[] | null
  variacoes?: LojaVirtualProdutoItem[]
}

interface LojaVirtualProdutoVariacoesBatchResponse {
  status?: number
  loja_id?: number
  entidade_id?: number | null
  exibir_precos?: number
  items?: LojaVirtualProdutoVariacoesResponse[]
  ausentes?: number[]
}

interface LojaVirtualVariacaoCaracteristica {
  variacao_id?: number | null
  variacao?: string | null
  valor?: string | null
  atributo_selecionavel?: number | boolean | null
  atributo_selecionavel_tipo?: string | null
  varicao_cor?: string | null
  variacao_cor?: string | null
}

interface LojaVirtualVariacaoDetalhe {
  produto_id: number
  referencia?: string | null
  ncm?: string | null
  NCM?: string | null
  codigo_ncm?: string | null
  codigoNcm?: string | null
  produto_ncm?: string | null
  produtoNcm?: string | null
  classificacao_fiscal?: { ncm?: string | null; codigo?: string | null } | string | null
  classificacaoFiscal?: { ncm?: string | null; codigo?: string | null } | string | null
  classificacao_fiscal_ncm?: string | null
  classificacaoFiscalNcm?: string | null
  hs_code?: string | null
  hsCode?: string | null
  nome?: string | null
  ordem?: number | string | null
  order?: number | string | null
  sequencia?: number | string | null
  posicao?: number | string | null
  position?: number | string | null
  marca_fabricante_id?: number | null
  marca_fabricante_nome?: string | null
  marca_fabricante_logo_url?: string | null
  serie?: string | null
  tamanho?: string | null
  modelo?: string | null
  informacoes_adicionais?: string | null
  classe_id?: number | null
  estoque?: number | string | null
  preco?: number | string | null
  preco_atacado?: number | string | null
  precoAtacado?: number | string | null
  preco_cnpj_ie?: number | string | null
  preco_distribuicao?: number | string | null
  imagem?: string | null
  url?: string | null
  atributos?: LojaVirtualVariacaoCaracteristica[]
  caracteristicas?: LojaVirtualVariacaoCaracteristica[]
  configuracoes_variacao?: LojaVirtualVariacaoConfiguracao[] | null
  fotos?: LojaVirtualProdutoDetalheFoto[]
  videos?: LojaVirtualProdutoDetalheVideo[]
}

interface LojaVirtualVariacaoDetalheResponse {
  status?: number
  loja_id?: number
  entidade_id?: number | null
  exibir_precos?: number
  configuracoes_variacao?: LojaVirtualVariacaoConfiguracao[] | null
  // The dedicated /variacoes/:id endpoint may wrap in 'variacao' or 'produto'
  variacao?: LojaVirtualProdutoDetalhe | LojaVirtualVariacaoDetalhe | null
  produto?: LojaVirtualProdutoDetalhe | null
}

export type ProductFormFieldType = 'select' | 'text' | 'number' | 'info' | 'alert'

export type ProductFormFieldRelation = {
  sourceFieldId: string
  sourceValue: string
  options: string[]
}

export type ProductFormField = {
  id: string
  type: ProductFormFieldType
  name: string
  note: string
  required: boolean
  options: string[]
  relations: ProductFormFieldRelation[]
  min: number | null
  max: number | null
}

export type ProductFormDefinition = {
  productId: string
  showGroupedItems: boolean
  fields: ProductFormField[]
}

export type ProductFormComponentItem = {
  configId: string
  minimumQuantity: number
  quantity: number
  selected: boolean
  locked: boolean
  product: Product
}

export type ProductFormComponentGroup = {
  id: string
  name: string
  required: boolean
  allRequired: boolean
  items: ProductFormComponentItem[]
}

export type ProductFormComponentsResult = {
  productId: string
  showPrices: boolean
  totalSelected: number | null
  groups: ProductFormComponentGroup[]
  manuals: ProductManual[]
}

interface LojaVirtualComprador {
  id?: number
  entidade_id?: number | null
  usuario_id?: number | null
  nome?: string
  email?: string | null
  cnpj?: string | null
  cpf_cnpj?: string | null
  cpfCnpj?: string | null
  documento?: string | null
  ie?: string | null
  inscricao_estadual?: string | null
  tipoPessoa?: number | null
  tipo_pessoa?: number | null
  tipopessoa?: number | null
  loja_id?: number
}

interface LojaVirtualCompradorUsuarioLoja {
  contatoId?: number | null
  contato_id?: number | null
  nome?: string | null
  tipoUsuario?: 'admin' | 'vendedor' | string | null
  tipo_usuario?: 'admin' | 'vendedor' | string | null
  papel?: 'admin' | 'vendedor' | string | null
  markupPercentual?: number | string | null
  markup_percentual?: number | string | null
  acessoFinanceiro?: boolean | number | string | null
  acesso_financeiro?: boolean | number | string | null
  acessoPedidos?: boolean | number | string | null
  acesso_pedidos?: boolean | number | string | null
  acessoUsuarios?: boolean | number | string | null
  acesso_usuarios?: boolean | number | string | null
  acessoMarketplaceAnuncios?: boolean | number | string | null
  acessoMarketplacePerguntas?: boolean | number | string | null
  modoAcesso?: 'empresa' | 'contato' | string | null
  modo_acesso?: 'empresa' | 'contato' | string | null
  buyerSessionToken?: string | null
  buyer_session_token?: string | null
}

interface LojaVirtualCompradorLoginResponse {
  status?: number
  error?: string | null
  motivo?: string
  comprador?: LojaVirtualComprador | null
  usuarioLoja?: LojaVirtualCompradorUsuarioLoja | null
  usuario_loja?: LojaVirtualCompradorUsuarioLoja | null
  buyerSessionToken?: string | null
  buyer_session_token?: string | null
  requires_document?: boolean | number | string | null
  authenticated?: boolean | number | string | null
  access_scope?: string | null
  accessScope?: string | null
  legacy_checkout?: {
    tipo?: 'orcamento' | 'pedido' | string | null
    id?: number | string | null
    hash?: string | null
    numero?: number | string | null
    checkout_source?: string | null
    site_orcamento_id?: number | string | null
    redirect_path?: string | null
  } | null
}

interface LojaVirtualCompradorAccessConsumeResponse extends LojaVirtualCompradorLoginResponse {
  redirect_path?: string | null
  redirectPath?: string | null
}

interface LojaVirtualCompradorPasswordlessRequestResponse {
  status?: number
  error?: string | null
  motivo?: string | null
  requires_document?: boolean | number | string | null
  canal?: string | null
  destino_mascarado?: string | null
  solicitacao_id?: string | number | null
  expires_in?: number | string | null
  expires_at?: string | null
  retry_after_seconds?: number | string | null
  canais_disponiveis?: {
    whatsapp?: boolean | number | string | null
    email?: boolean | number | string | null
  } | null
  whatsapp_destinos?: Array<{
    destino_tipo?: 'entidade' | 'contato' | null
    contato_id?: number | string | null
    last4?: string | null
    nome_exibicao?: string | null
    principal?: boolean | number | string | null
    whatsapp?: string | null
    telefone?: string | null
    celular?: string | null
    numero?: string | null
    phone?: string | null
    destino?: string | null
  }> | null
  email_destinos?: Array<{
    destino_tipo?: 'entidade' | 'contato' | null
    contato_id?: number | string | null
    email_mascarado?: string | null
    nome_exibicao?: string | null
    principal?: boolean | number | string | null
  }> | null
}

interface LojaVirtualCompradorPasswordlessDestinationsResponse {
  status?: number
  error?: string | null
  motivo?: string | null
  requires_document?: boolean | number | string | null
  canais_disponiveis?: {
    whatsapp?: boolean | number | string | null
    email?: boolean | number | string | null
  } | null
  whatsapp_destinos?: LojaVirtualCompradorPasswordlessRequestResponse['whatsapp_destinos']
  email_destinos?: LojaVirtualCompradorPasswordlessRequestResponse['email_destinos']
}

interface LojaVirtualCompradorPasswordChangeDestinationsResponse extends LojaVirtualCompradorPasswordlessDestinationsResponse {
  principal_tipo?: 'empresa' | 'contato' | string | null
  email_mascarado?: string | null
}

interface LojaVirtualCompradorPasswordChangeRequestResponse extends LojaVirtualCompradorPasswordlessRequestResponse {
  principal_tipo?: 'empresa' | 'contato' | string | null
}

interface LojaVirtualCompradorPasswordlessContactRequestResponse {
  status?: number
  error?: string | null
  motivo?: string | null
  requires_document?: boolean | number | string | null
  contato_id?: number | string | null
}

interface LojaVirtualCompradorPasswordRecoveryVerifyResponse {
  status?: number
  error?: string | null
  motivo?: string | null
  requires_document?: boolean | number | string | null
  reset_token?: string | null
  resetToken?: string | null
  token?: string | null
  expires_in?: number | string | null
}

interface LojaVirtualCompradorPasswordRecoveryResetResponse {
  status?: number
  error?: string | null
  motivo?: string | null
  message?: string | null
}

interface LojaVirtualCarrinhoItemUpsertResponse {
  status?: number
  id?: number | null
  entidade_id?: number
  loja_id?: number
  produto_id?: number
  qtde_caixa?: number
  qtde?: number
  acao?: 'inserted' | 'updated' | 'removed' | 'noop'
  motivo?: string | null
}

type CarrinhoItemKeyParts = {
  produtoId: number
  qtdeCaixa: number
  itemKey: string
}

interface LojaVirtualCarrinhoQuantidadesResponse {
  status?: number
  entidade_id?: number
  loja_id?: number
  itens?: Record<string, unknown>
}

interface LojaVirtualCarrinhoTotaisResponse {
  status?: number
  entidade_id?: number
  loja_id?: number
  subtotal?: number
  impostos?: number
  ipi?: number
  valor_ipi?: number
  st?: number
  valor_st?: number
  difal?: number
  valor_difal?: number
  total?: number
  itens?: number
  contexto_preco?: LojaVirtualCheckoutPagamentosResponse['contexto_preco']
}

interface LojaVirtualSimpleStatusResponse {
  status?: number
  motivo?: string | null
}

export type LojaVirtualCarrinhoRecalculoResponse = LojaVirtualSimpleStatusResponse & {
  contextoPreco?: CheckoutPriceContext
}

interface LojaVirtualCheckoutPagamentosResponse {
  status?: number
  entidade_id?: number
  loja_id?: number
  subtotal?: number
  itens?: number
  primeira_compra?: number
  habilitar_programar_faturamento?: number
  habilitar_programar_faturamento_dias?: number | null
  contexto_preco?: {
    modo?: 'NORMAL' | 'BENEFICIO' | string
    percentual_reducao_impostos?: number
    percentual_impostos?: number
    origem?: 'CLIENTE' | 'NORMAL_SOLICITADO' | 'PRECO_TRAVADO' | string
  } | null
  condicoes_x_elegiveis?: Array<{
    forma_pagamento_id?: number
    forma_pagamento?: string | null
    condicao_pagamento_id?: number
    condicao_pagamento?: string | null
  }>
  formas?: Array<{
    forma_pagamento_id?: number
    descricao?: string | null
    detalhe?: string | null
    condicoes?: Array<{
      condicao_pagamento_id?: number
      descricao?: string | null
      condicao?: string | null
      considera_avista?: number
      obs?: string | null
    }>
  }>
}

interface LojaVirtualCheckoutFreteResponse {
  status?: number
  entidade_id?: number
  loja_id?: number
  endereco_id?: number
  modo_frete?: 'auto' | 'fob' | 'cif'
  pesagem?: {
    peso?: number
    m3?: number
  }
  tem_cif?: number
  cotacao_cache_hit?: number
  cotacao_cache_hash?: string | null
  cotacao_cache_miss?: number
  redespacho?: {
    ativo?: number
    transportadora_id?: number | null
    transportadora_nome?: string | null
    endereco_resumo?: string | null
  } | null
  transportadoras?: Array<{
    transportadora_id?: number
    razao?: string | null
    telefone?: string | null
    whatsapp?: string | null
    email?: string | null
    media?: number | null
    avaliacoes?: number | null
    correio?: number
    preferencial?: number
    calculado?: number
    tipo_frete?: 'cif' | 'fob'
    cotacao_id?: number | null
    valor_frete?: number
    valor_original?: number | null
    prazo_dias_min?: number | null
    prazo_dias_max?: number | null
    retira_no_local?: number
    alerta?: number
    alerta_texto?: string | null
    transportadora_taxa_armazenagem?: number
    transportadora_taxa_armazenagem_obs_fob?: string | null
    transportadora_taxa_armazenagem_obs_cif?: string | null
    redespacho?: {
      ativo?: number
      transportadora_id?: number | null
      transportadora_nome?: string | null
      endereco_resumo?: string | null
    } | null
  }>
}

interface LojaVirtualCheckoutSimularResponse {
  status?: number
  entidade_id?: number
  loja_id?: number
  subtotal?: number
  desconto?: number
  desconto_percentual?: number
  frete?: number
  impostos?: number
  ipi?: number
  valor_ipi?: number
  st?: number
  valor_st?: number
  difal?: number
  valor_difal?: number
  total?: number
  valor_total_fiscal?: number
  faturar_painel?: number
  faturamento_painel_disponivel?: number
  desconto_painel?: number
  desconto_painel_percentual?: number
  cupom_ok?: number
  cupom_mensagem?: string | null
  cashback_disponivel?: number
  cashback_aplicado?: number
  partner_quote_summary?: {
    products_total?: number | string | null
    taxes_total?: number | string | null
    commission_total?: number | string | null
    total?: number | string | null
    items?: Array<{
      item_key?: string | null
      product_id?: number | string | null
      product_grade_id?: number | string | null
      quantity?: number | string | null
      unit_price?: number | string | null
      commission_percent?: number | string | null
      commission_value?: number | string | null
      value_unit_price?: number | string | null
    }>
  } | null
  status_fluxo?: CheckoutStatusFluxo
  itens?: Array<{
    carrinho_id?: number | string | null
    produto_id?: number | string | null
    produto_grade_id?: number | string | null
    qtde?: number | string | null
    valor_total_base?: number | string | null
    valor_desconto?: number | string | null
    valor_frete_rateado?: number | string | null
    valor_total_liquido?: number | string | null
    impostos?: number | string | null
    valor_impostos?: number | string | null
    ipi?: number | string | null
    valor_ipi?: number | string | null
    st?: number | string | null
    valor_st?: number | string | null
    difal?: number | string | null
    valor_difal?: number | string | null
  }>
}

interface LojaVirtualPedidoGerarResponse {
  status?: number
  motivo?: string | null
  attempt_id?: string | null
  attempt_source?: string | null
  attempt_status?: string | null
  entidade_id?: number
  loja_id?: number
  pedido_id?: number
  numero_pedido?: number
  id?: string
  parcial?: number
  itens_processados?: number
  status_numero?: number | string | null
  status_descricao?: string | null
  status_fluxo?: CheckoutStatusFluxo
  payment_link?: string | null
  payment_link_kind?: 'short' | 'direct' | null
}

export type CheckoutAddressOption = {
  id: number
  label: string
  recipient: string
  street: string
  number: string
  complement: string
  neighborhood: string
  city: string
  state: string
  zipCode: string
  isDefault: boolean
  isValidated?: boolean | null
}

export type CheckoutAddressCreateInput = {
  label?: string
  recipient?: string
  zipCode: string
  street: string
  number?: string
  complement?: string
  neighborhood: string
  city: string
  state: string
}

export type CheckoutLatestOrderAddressPreference = {
  id: number | null
  street: string
  number: string
  complement: string
  neighborhood: string
  city: string
  state: string
  zipCode: string
}

export type CheckoutCustomerRedespachoTransportadora = {
  id: string
  transportadoraId: number
  nome: string
  cpfCnpj: string
  cidade: string
  uf: string
  enderecoResumo: string
  preferencial: boolean
}

export type CheckoutCustomerProfile = {
  id: number
  corporateName: string
  name: string
  document: string
  activity: string
  stateRegistration: string
  email: string
  phone: string
  street: string
  number: string
  complement: string
  neighborhood: string
  city: string
  state: string
  zipCode: string
  tipoPessoa: number | null
  modalidadeFretePadrao: 'cif' | 'fob'
  transportadoraPreferencialId: number | null
  /**
   * Verdadeiro quando o comprador já tem um pedido recente com dados de checkout
   * úteis para repetir preferências nas etapas seguintes.
   */
  temUltimaCompraFinalizada: boolean
  ultimaCompraTransportadoraId: number | null
  ultimaCompraModalidadeFrete: 'cif' | 'fob' | null
  ultimaCompraEnderecoEntrega: CheckoutLatestOrderAddressPreference | null
  ultimaCompraContatoNome: string
  ultimaCompraContatoTelefone: string
  ultimaCompraFormaPagamentoId: number | null
  ultimaCompraCondicaoPagamentoId: number | null
  ultimaCompraCondicaoPagamentoTexto: string
  ultimaCompraFinalidade: number | null
  transportadoraRedespachoAtiva: boolean
  transportadoraRedespachoId: number | null
  transportadoraRedespachoNome: string
  transportadoraRedespachoEnderecoResumo: string
  transportadoraRedespachoPreferencialVinculada: boolean
  transportadorasRedespachoVinculadas: CheckoutCustomerRedespachoTransportadora[]
  finalidadePadrao: number | null
  clientePossuiMaisFinalidades: boolean
  habilitarProgramarFaturamento: boolean
  habilitarProgramarFaturamentoDias: number | null
}

export type CheckoutRedespachoInfo = {
  transportadoraId: number
  transportadoraNome: string
  enderecoResumo: string
}

export type CheckoutAddressData = {
  customer: CheckoutCustomerProfile | null
  addresses: CheckoutAddressOption[]
}

export type CheckoutPaymentOption = {
  id: string
  formaPagamentoId: number
  condicaoPagamentoId: number
  condicaoPagamentoTexto: string
  label: string
  description: string
  detail: string
  badge: string
}

export type CheckoutPriceContext = {
  modo: 'NORMAL' | 'BENEFICIO'
  percentualReducaoImpostos: 0 | 50 | 70 | 90 | 95 | 100
  percentualImpostos: 0 | 5 | 10 | 30 | 50 | 100
  origem: 'CLIENTE' | 'NORMAL_SOLICITADO' | 'PRECO_TRAVADO'
}

export type CheckoutPaymentPreparation = {
  options: CheckoutPaymentOption[]
  contextoPreco: CheckoutPriceContext
  condicoesXElegiveis: CheckoutPaymentOption[]
}

const normalizeCheckoutPriceContext = (
  contextoRaw: LojaVirtualCheckoutPagamentosResponse['contexto_preco']
): CheckoutPriceContext => {
  const modo = String(contextoRaw?.modo ?? '').toUpperCase() === 'BENEFICIO' ? 'BENEFICIO' : 'NORMAL'
  const percentualReducaoRaw = Math.trunc(normalizeFreteNumber(contextoRaw?.percentual_reducao_impostos) ?? 0)
  const percentualReducaoImpostos = (
    modo === 'BENEFICIO' && [50, 70, 90, 95, 100].includes(percentualReducaoRaw)
      ? percentualReducaoRaw
      : 0
  ) as CheckoutPriceContext['percentualReducaoImpostos']
  const percentualImpostos = (modo === 'BENEFICIO'
    ? Math.max(0, 100 - percentualReducaoImpostos)
    : 100) as CheckoutPriceContext['percentualImpostos']
  const origemRaw = normalizeCheckoutText(contextoRaw?.origem).toUpperCase()
  const origem = (
    origemRaw === 'NORMAL_SOLICITADO' || origemRaw === 'PRECO_TRAVADO'
      ? origemRaw
      : 'CLIENTE'
  ) as CheckoutPriceContext['origem']

  return {
    modo,
    percentualReducaoImpostos,
    percentualImpostos,
    origem,
  }
}

export type CheckoutShippingOption = {
  id: string
  transportadoraId: number
  cotacaoId: number
  quoteNumber?: string
  tipoFrete: 'cif' | 'fob'
  label: string
  description: string
  price: number
  originalPrice: number
  eta: string
  mediaAvaliacao: number
  totalAvaliacoes: number
  calculado: boolean
  retiraNoLocal: boolean
  alertaTexto: string
  redespacho: CheckoutRedespachoInfo | null
}

export const CHECKOUT_SHIPPING_CALCULATING_MESSAGE = 'Calculando opções de frete...'

export type CheckoutStatusFluxo =
  | 'em_analise'
  | 'aprovado'
  | 'aguardando_confirmacao'
  | 'aguardando_pagamento'
  | 'em_separacao'

export type CheckoutAttemptSource =
  | 'fresh_submit'
  | 'retry'
  | 'refresh_recovery'
  | 'fallback_recovery'

export type CheckoutOrderAttemptStatusValue =
  | 'processing'
  | 'success'
  | 'failed'
  | 'expired'

export type CheckoutSimulationItemTaxDetail = {
  checkoutSource?: 'cart' | 'quote' | 'order'
  cartItemId: number
  quoteId?: number
  quoteItemId?: number
  orderId?: number
  orderItemId?: number
  productId: number
  productGradeId: number
  quantity: number
  subtotal: number
  discount: number
  freight: number
  total: number
  impostos: number
  ipi: number
  difal: number
}

export type CheckoutSimulationResult = {
  subtotal: number
  desconto: number
  descontoPercentual: number
  frete: number
  impostos: number
  ipi: number
  difal: number
  total: number
  valorTotalFiscal?: number
  faturarPainel?: boolean
  faturamentoPainelDisponivel?: boolean
  descontoPainel?: number
  descontoPainelPercentual?: number
  cupomOk: boolean
  cupomMensagem: string
  cashbackDisponivel: number
  cashbackAplicado: number
  partnerQuoteSummary: {
    productsTotal: number
    taxesTotal: number
    commissionTotal: number
    total: number
    items: Array<{
      key: string
      productId: number
      productGradeId: number
      quantity: number
      unitPrice: number
      commissionPercent: number
      commissionValue: number
      valueUnitPrice: number
    }>
  } | null
  statusFluxo: CheckoutStatusFluxo
  items: CheckoutSimulationItemTaxDetail[]
}

export type CheckoutOrderResult = {
  pedidoId: number
  numeroPedido: number
  hash: string
  statusFluxo: CheckoutStatusFluxo
  statusNumero?: number | null
  statusDescricao?: string
  itensProcessados: number
  checkoutSnapshot?: CheckoutOrderSnapshot | null
  paymentMethod: {
    formaPagamentoId: number
    condicaoPagamentoId: number
    label: string
    description: string
    detail: string
  } | null
  paymentInstructions: CheckoutPaymentInstructions | null
  paymentLink?: string | null
}

export type CheckoutOrderGenerationResult =
  | {
      kind: 'success'
      result: CheckoutOrderResult
      attemptId: string | null
      attemptStatus: 'success'
    }
  | {
      kind: 'processing'
      attemptId: string | null
      attemptStatus: 'processing'
    }

export type CheckoutOrderAttemptStatusResult = {
  attemptId: string
  attemptSource: CheckoutAttemptSource | null
  attemptStatus: CheckoutOrderAttemptStatusValue
  pedidoId: number
  numeroPedido: number
  hash: string
  statusFluxo: CheckoutStatusFluxo
  statusNumero: number | null
  statusDescricao: string
  itensProcessados: number
  parcial: number | null
  motivo: string
  paymentInstructions: CheckoutPaymentInstructions | null
  paymentLink?: string | null
}

export type CheckoutOrderSnapshot = {
  subtotal: number
  desconto: number
  frete: number
  impostos: number
  total: number
  freteSelecionado?: {
    transportadoraId: number
    cotacaoId: number
    tipoFrete: 'cif' | 'fob'
    fretePorConta: 0 | 1
    valorFrete: number
    valorOriginal: number
    usarRedespacho?: 0 | 1
    transportadoraRedespachoId?: number | null
  } | null
}

export type CheckoutOrderGenerateInput = {
  formaPagamentoId?: number
  condicaoPagamentoId?: number
  formaPagamentoXId?: number
  condicaoPagamentoXId?: number
  orcamentoId?: number
  quoteMode?: BusinessQuoteCheckoutMode
  useCartItems?: boolean
  orcamentoItensOverride?: Array<{ itemId: number; quantity: number }>
  pedidoId?: number
  pedidoItensOverride?: Array<{ itemId: number; quantity: number }>
  recompraDireta?: boolean
  enderecoId?: number
  transportadoraId?: number
  cotacaoId?: number
  transportadoraCotacaoFreteNumero?: string
  transportadoraCotacaoFreteValor?: number
  valorFrete?: number
  fretePorConta?: 0 | 1
  usarRedespacho?: 0 | 1
  transportadoraRedespachoId?: number | null
  cupom?: string
  usarCashback?: boolean
  faturarPainel?: boolean
  modoFrete?: 'auto' | 'fob' | 'cif'
  lojaId?: number
  origem?: string
  nomeContato?: string
  telefoneContato?: string
  aceitaMarketing?: boolean
  numeroOrdem?: string
  finalidade?: number
  habilitarProgramarFaturamento?: boolean
  habilitar_programar_faturamento?: boolean | 0 | 1
  dataProgramacao?: string
  data_programacao?: string
  obsFichaCarregamento?: string
  carrinhoIds?: number[]
  checkoutSnapshot?: CheckoutOrderSnapshot | null
  attemptId?: string
  attemptSource?: CheckoutAttemptSource
  analytics?: LojaVirtualCheckoutAnalyticsContextInput | null
}

export type CheckoutPaymentInstructionKind = 'pix' | 'bank_transfer' | 'mercado_pago'

export type CheckoutPixInstructions = {
  qrCodeImageUrl: string
  qrCodeText: string
  copyPasteCode: string
  pixKey: string
  beneficiary: string
  amount: number | null
  expiresAt: string
  note: string
}

export type CheckoutBankTransferAccount = {
  bankName: string
  bankCode: string
  agency: string
  account: string
  accountType: string
  holderName: string
  document: string
  pixKey: string
  note: string
}

export type CheckoutBankTransferInstructions = {
  accounts: CheckoutBankTransferAccount[]
  note: string
  receiptHint: string
}

export type CheckoutMercadoPagoFormField = {
  name: string
  value: string
}

export type CheckoutMercadoPagoInstructions = {
  html: string
  checkoutUrl: string
  formAction: string
  formMethod: 'GET' | 'POST'
  formFields: CheckoutMercadoPagoFormField[]
  submitLabel: string
}

export type CheckoutMercadoPagoBackUrls = {
  success?: string
  failure?: string
  pending?: string
}

export type CheckoutMercadoPagoActionResult = {
  status: number
  motivo: string
  url: string
  id: string
  collectorId: string
  sandboxUrl: string
  checkoutPro: boolean
  paymentInstructions: CheckoutPaymentInstructions | null
}

export type CheckoutPaymentInstructions = {
  kind: CheckoutPaymentInstructionKind
  pix?: CheckoutPixInstructions | null
  bankTransfer?: CheckoutBankTransferInstructions | null
  mercadoPago?: CheckoutMercadoPagoInstructions | null
}

export type CheckoutPaymentReceiptSummary = {
  canUpload: boolean
  uploaded: boolean
  latestAttachmentId: number | null
  latestUploadedAt: string
  latestFileName: string
}

export type CheckoutPaymentReceiptUploadResult = {
  message: string
  attachmentId: number | null
  size: number | null
  uploadedAt: string
  fileName: string
  paymentReceipt: CheckoutPaymentReceiptSummary | null
}

export type CheckoutPaymentReceiptDownloadResult = {
  blob: Blob
  fileName?: string | null
}

export type CreatePixChargeInput = {
  notaId: number
  contaId?: number
  identificadorInterno?: string
  expiracao?: number
  solicitacaoPagador?: string
  valor?: number
  valorCobrado?: number
  devedorNome?: string
  devedorCpfCnpj?: string
  webhookUrl?: string
}

interface FreteCotarEndereco {
  cep: string
  cnpj?: string
}

interface FreteCotarItem {
  qtde: number
  peso: number
  altura: number
  largura: number
  comprimento: number
}

interface FreteCotarRequest {
  nota_id?: number
  transportadora_id?: number
  forcar_recalculo?: 0 | 1 | boolean
  requisicao: {
    origem: FreteCotarEndereco
    destino: FreteCotarEndereco
    peso?: number
    m3?: number
    itens: FreteCotarItem[]
  }
  cotacao?: Record<string, unknown> | Array<Record<string, unknown>>
}

interface FreteCotarResponse {
  status?: number
  cache_hit?: number
  cache_hash?: string
  cache_key?: string
  requisicao?: FreteCotarRequest['requisicao']
  cotacao?: Record<string, unknown> | Array<Record<string, unknown>> | null
  motivo?: string | null
}

interface FreteTransportadorasDisponiveisResponse {
  status?: number
  total?: number
  transportadora_ids?: Array<number | string | null>
  motivo?: string | null
}

export type FreteCotacaoInputItem = {
  qtde: number
  peso: number
  altura: number
  largura: number
  comprimento: number
}

export type FreteCotacaoOption = {
  id: string
  label: string
  description: string
  price: number
  eta: string
  raw: Record<string, unknown>
}

export type FreteCotacaoResult = {
  status: number
  cacheHit: number
  motivo?: string
  options: FreteCotacaoOption[]
  rawCotacao: FreteCotarResponse['cotacao']
}

const checkoutFreteTransportadoraCache = new Map<string, CheckoutShippingOption[]>()
const checkoutFreteTransportadoraRequests = new Map<string, Promise<CheckoutShippingOption[]>>()

interface MainMenuItem {
  id: number
  descricao: string
  slug?: string | null
  children?: MainMenuItem[] | null
}

interface MainMenuResponse {
  status?: number
  loja_id?: number
  total?: number
  items?: MainMenuItem[]
}

interface MenuTreeItem {
  id: number
  descricao: string
  slug?: string
  children?: MenuTreeItem[]
}

export type StorefrontCategoryModel = 'grupos' | 'classes'

interface IntegrationTokenResponse {
  token_type?: string
  access_token?: string
  expires_in?: number
  refresh_token?: string
  refresh_expires_in?: number
  scope?: string
  message?: string
  error?: string
}

interface IntegrationTokenState {
  tokenType: string
  accessToken: string
  accessTokenExpiresAt: number
  refreshToken?: string
  refreshTokenExpiresAt?: number
}

interface IntegrationTokenFailureState {
  error: Error
  retryAt: number
}

let integrationTokenState: IntegrationTokenState | null = null
let integrationTokenRequest: Promise<IntegrationTokenState> | null = null
let integrationTokenFailureState: IntegrationTokenFailureState | null = null
const menuTreeCache = new Map<string, MenuTreeItem[]>()
const menuTreeRequest = new Map<string, Promise<MenuTreeItem[]>>()
const menuTreeShallowCache = new Map<string, MenuTreeItem[]>()
const menuTreeShallowRequest = new Map<string, Promise<MenuTreeItem[]>>()
const menuTreeTopLevelCache = new Map<string, MenuTreeItem[]>()
const menuTreeTopLevelRequest = new Map<string, Promise<MenuTreeItem[]>>()
const MENU_TREE_STALE_STORAGE_TTL_MS = 7 * 24 * 60 * 60 * 1000
const MENU_TREE_STALE_STORAGE_KEY_PREFIX = [
  'b2b-commerce',
  'menu-tree',
  'v1',
  INTEGRATION_PUBLIC_SERVER,
  LOJA_VIRTUAL_DEFAULT_LOJA_ID,
].join(':')
type BuyerSessionLifecycleEventDetail = {
  endpoint?: string
  status?: number
  detail?: string
  reason?: string
}

const dispatchBuyerSessionLifecycleEvent = (
  eventName: string,
  detail?: BuyerSessionLifecycleEventDetail
) => {
  if (typeof window === 'undefined' || typeof window.dispatchEvent !== 'function') return

  try {
    window.dispatchEvent(new CustomEvent(eventName, { detail: detail ?? {} }))
  } catch {
    // Ignore browser event dispatch errors.
  }
}

const isCurrentBuyerSessionToken = (expectedToken: string | null | undefined) => {
  const normalizedExpectedToken = String(expectedToken ?? '').trim()
  return Boolean(normalizedExpectedToken && getBuyerSessionToken() === normalizedExpectedToken)
}

const clearBuyerSessionTokenState = (
  detail?: BuyerSessionLifecycleEventDetail,
  expectedToken?: string | null
) => {
  const normalizedExpectedToken = String(expectedToken ?? '').trim()
  if (normalizedExpectedToken && !isCurrentBuyerSessionToken(normalizedExpectedToken)) {
    return false
  }

  const currentToken = getBuyerSessionToken()
  setBuyerSessionToken(null)

  if (currentToken) {
    dispatchBuyerSessionLifecycleEvent(BUYER_SESSION_TOKEN_RESET_EVENT, detail)
  }

  return true
}

const buildRetryHeadersWithoutBuyerSession = (headers: Headers) => {
  const retryHeaders = new Headers(headers)
  retryHeaders.delete('X-Loja-Comprador-Session')
  return retryHeaders
}

const notifyBuyerSessionExpired = (detail?: BuyerSessionLifecycleEventDetail) => {
  const normalizedReason = String(
    detail?.reason
    ?? detail?.detail
    ?? 'Sua sessão expirou. Faça login novamente.'
  ).trim() || 'Sua sessão expirou. Faça login novamente.'

  dispatchBuyerSessionLifecycleEvent(BUYER_SESSION_EXPIRED_EVENT, {
    ...(detail ?? {}),
    reason: normalizedReason,
  })
}

function getFutureTimestamp(expiresInSeconds: number | undefined, fallbackSeconds: number): number {
  const safeSeconds = typeof expiresInSeconds === 'number' && expiresInSeconds > 0
    ? expiresInSeconds
    : fallbackSeconds
  return Date.now() + safeSeconds * 1000
}

function normalizeEntidadeId(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
    return Math.trunc(value)
  }
  if (typeof value === 'string') {
    const parsed = Number.parseInt(value, 10)
    if (Number.isFinite(parsed) && parsed > 0) {
      return Math.trunc(parsed)
    }
  }
  return undefined
}

function isTokenStillValid(expiresAt?: number): boolean {
  return Boolean(expiresAt && Date.now() + TOKEN_EXPIRY_SAFETY_WINDOW_MS < expiresAt)
}

const HTML_ERROR_DOCUMENT_PATTERN = /<!doctype\s+html|<html\b|<head\b|<body\b/i
const HTML_ERROR_TITLE_PATTERN = /<title[^>]*>([\s\S]*?)<\/title>/i
const HTML_ERROR_HEADING_PATTERN = /<h1[^>]*>([\s\S]*?)<\/h1>/i
const HTML_TAG_PATTERN = /<[^>]+>/g
const ERROR_MESSAGE_MAX_LENGTH = 240

const decodeHtmlErrorEntities = (value: string) => value
  .replace(/&nbsp;/gi, ' ')
  .replace(/&amp;/gi, '&')
  .replace(/&lt;/gi, '<')
  .replace(/&gt;/gi, '>')
  .replace(/&quot;/gi, '"')
  .replace(/&#39;/gi, "'")

const normalizeErrorMessageDetail = (value: string): string | undefined => {
  const trimmedValue = value.trim()
  if (!trimmedValue) return undefined

  if (HTML_ERROR_DOCUMENT_PATTERN.test(trimmedValue)) {
    const htmlMatch = trimmedValue.match(HTML_ERROR_TITLE_PATTERN) ?? trimmedValue.match(HTML_ERROR_HEADING_PATTERN)
    const htmlText = decodeHtmlErrorEntities(
      String(htmlMatch?.[1] ?? '')
        .replace(HTML_TAG_PATTERN, ' ')
        .replace(/\s+/g, ' ')
        .trim()
    )

    if (/manuten[cç][aã]o/i.test(htmlText)) {
      return 'Estamos em manutenção no momento. Tente novamente em instantes.'
    }

    return htmlText || 'Serviço temporariamente indisponível.'
  }

  const normalizedValue = trimmedValue.replace(/\s+/g, ' ')
  if (normalizedValue.length <= ERROR_MESSAGE_MAX_LENGTH) {
    return normalizedValue
  }

  return `${normalizedValue.slice(0, ERROR_MESSAGE_MAX_LENGTH - 3).trimEnd()}...`
}

function extractErrorMessage(payload: unknown): string | undefined {
  if (typeof payload === 'string') return normalizeErrorMessageDetail(payload)
  if (!payload || typeof payload !== 'object') return undefined

  const record = payload as Record<string, unknown>
  const candidateKeys = ['message', 'motivo', 'detail', 'details', 'reason', 'error']
  for (const key of candidateKeys) {
    const value = record[key]
    if (typeof value === 'string') {
      const normalizedValue = normalizeErrorMessageDetail(value)
      if (normalizedValue) return normalizedValue
    }
  }

  return undefined
}

function extractErrorCode(payload: unknown): string | undefined {
  if (!payload || typeof payload !== 'object') return undefined
  const value = (payload as Record<string, unknown>).error
  return typeof value === 'string' ? value.trim() || undefined : undefined
}

async function parseJsonSafe(response: Response): Promise<unknown> {
  const text = await response.text()
  if (!text) return null

  try {
    return JSON.parse(text)
  } catch {
    return text
  }
}

function mapToIntegrationTokenState(payload: unknown): IntegrationTokenState {
  const tokenPayload = payload as IntegrationTokenResponse

  if (!tokenPayload?.access_token || typeof tokenPayload.access_token !== 'string') {
    throw new Error('Integration auth failed: access_token ausente na resposta.')
  }

  return {
    tokenType: tokenPayload.token_type || 'Bearer',
    accessToken: tokenPayload.access_token,
    accessTokenExpiresAt: getFutureTimestamp(tokenPayload.expires_in, 3600),
    refreshToken: tokenPayload.refresh_token,
    refreshTokenExpiresAt: tokenPayload.refresh_token
      ? getFutureTimestamp(tokenPayload.refresh_expires_in, 30 * 24 * 3600)
      : undefined,
  }
}

function buildRequestHeaders(customHeaders?: HeadersInit): Headers {
  const headers = new Headers(customHeaders)

  if (!headers.has('accept')) headers.set('accept', 'application/json')
  if (API_KEY && !headers.has('X-API-KEY')) headers.set('X-API-KEY', API_KEY)

  return headers
}

function normalizeError(error: unknown): Error {
  return error instanceof Error ? error : new Error(String(error))
}

function shouldRetryProxyManagedIntegrationAuth(detail?: string): boolean {
  if (SHOULD_USE_INTEGRATION_AUTH) return false
  if (!API_BASE_URL.startsWith('/api')) return false

  const normalizedDetail = detail?.trim().toLowerCase() || ''
  if (!normalizedDetail) return true

  return (
    normalizedDetail.includes('invalid_integration_token')
    || normalizedDetail.includes('unauthorized')
    || normalizedDetail.includes('invalid token')
    || normalizedDetail.includes('token')
    || normalizedDetail.includes('auth')
  )
}

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms)
  })
}

function isBackendUnavailableStatus(status: number) {
  return [502, 503, 504, 521, 522, 523, 524].includes(status)
}

async function requestIntegrationAuth(
  endpoint: string,
  payload: Record<string, unknown>
): Promise<IntegrationTokenState> {
  const headers = buildRequestHeaders()
  if (!headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json')
  }

  let response: Response
  try {
    response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
    })
  } catch (error) {
    markBackendUnavailable(error)
    throw normalizeError(error)
  }

  if (isBackendUnavailableStatus(response.status)) {
    markBackendUnavailable(new Error(`API error: ${response.status}`))
  } else {
    markBackendAvailable()
  }

  const responsePayload = await parseJsonSafe(response)

  if (!response.ok) {
    const detail = extractErrorMessage(responsePayload)
    throw new Error(`Integration auth error: ${response.status}${detail ? ` - ${detail}` : ''}`)
  }

  return mapToIntegrationTokenState(responsePayload)
}

async function authenticateWithIntegrationCredentials(): Promise<IntegrationTokenState> {
  const authPayload: Record<string, unknown> = {
    email: INTEGRATION_AUTH_EMAIL,
    senha: INTEGRATION_AUTH_PASSWORD,
    server: INTEGRATION_AUTH_SERVER,
  }

  if (INTEGRATION_AUTH_SCOPE) {
    authPayload.scope = INTEGRATION_AUTH_SCOPE
  }

  return requestIntegrationAuth(INTEGRATION_AUTH_TOKEN_ENDPOINT, authPayload)
}

async function refreshIntegrationAccessToken(refreshToken: string): Promise<IntegrationTokenState> {
  return requestIntegrationAuth(INTEGRATION_AUTH_REFRESH_ENDPOINT, {
    refresh_token: refreshToken,
  })
}

async function resolveIntegrationTokenState(forceReauthenticate: boolean = false): Promise<IntegrationTokenState> {
  if (integrationTokenRequest) {
    return integrationTokenRequest
  }

  if (
    !forceReauthenticate &&
    integrationTokenFailureState
    && integrationTokenFailureState.retryAt > Date.now()
  ) {
    throw integrationTokenFailureState.error
  }

  const request = (async () => {
    try {
      if (
        !forceReauthenticate &&
        integrationTokenState &&
        isTokenStillValid(integrationTokenState.accessTokenExpiresAt)
      ) {
        return integrationTokenState
      }

      if (
        !forceReauthenticate &&
        integrationTokenState?.refreshToken &&
        isTokenStillValid(integrationTokenState.refreshTokenExpiresAt)
      ) {
        try {
          const refreshed = await refreshIntegrationAccessToken(integrationTokenState.refreshToken)
          integrationTokenState = refreshed
          integrationTokenFailureState = null
          return refreshed
        } catch (error) {
          console.warn('Falha ao renovar token de integracao, tentando nova autenticacao.', error)
        }
      }

      const authenticated = await authenticateWithIntegrationCredentials()
      integrationTokenState = authenticated
      integrationTokenFailureState = null
      return authenticated
    } catch (error) {
      const normalizedError = normalizeError(error)
      integrationTokenState = null
      integrationTokenFailureState = {
        error: normalizedError,
        retryAt: Date.now() + INTEGRATION_AUTH_FAILURE_COOLDOWN_MS,
      }
      throw normalizedError
    }
  })()

  integrationTokenRequest = request

  try {
    return await request
  } finally {
    integrationTokenRequest = null
  }
}

async function getIntegrationAuthorizationHeader(forceReauthenticate: boolean = false): Promise<string> {
  const tokenState = await resolveIntegrationTokenState(forceReauthenticate)
  return `${tokenState.tokenType} ${tokenState.accessToken}`
}

function getLojaIdQuery(): string {
  const parsed = Number.parseInt(LOJA_VIRTUAL_DEFAULT_LOJA_ID, 10)
  if (!Number.isFinite(parsed)) return ''
  return `?loja_id=${parsed}`
}

const PT_BR_TITLECASE_LOWER_WORDS = new Set([
  'a',
  'ao',
  'aos',
  'as',
  'com',
  'da',
  'das',
  'de',
  'do',
  'dos',
  'e',
  'em',
  'na',
  'nas',
  'no',
  'nos',
  'o',
  'os',
  'ou',
  'para',
  'por',
  'sem',
])

export function toPtBrTitleCase(value: string): string {
  const normalized = value.trim().toLocaleLowerCase('pt-BR')
  if (!normalized) return ''

  let wordIndex = 0
  return normalized.replace(/\p{L}+/gu, (word) => {
    const shouldKeepLower = wordIndex > 0 && PT_BR_TITLECASE_LOWER_WORDS.has(word)
    wordIndex += 1

    if (shouldKeepLower) {
      return word
    }

    const [first = '', ...rest] = Array.from(word)
    return `${first.toLocaleUpperCase('pt-BR')}${rest.join('')}`
  })
}

function normalizeMenuTitle(value: string): string {
  return toPtBrTitleCase(value)
}

function normalizeProductName(value: string): string {
  return toPtBrTitleCase(value)
}

function mapMainMenuItem(item: MainMenuItem | null | undefined): MenuTreeItem | null {
  if (!item || typeof item.id !== 'number') return null
  const descricao = typeof item.descricao === 'string' && item.descricao.trim()
    ? normalizeMenuTitle(item.descricao)
    : `Categoria ${item.id}`
  const slug = typeof item.slug === 'string' && item.slug.trim() ? item.slug.trim() : undefined
  return {
    id: item.id,
    descricao,
    slug,
  }
}

function mapMenuTreeNode(item: MainMenuItem | null | undefined): MenuTreeItem | null {
  const base = mapMainMenuItem(item)
  if (!base) return null
  const children = Array.isArray(item?.children)
    ? item.children.map(mapMenuTreeNode).filter((child): child is MenuTreeItem => Boolean(child))
    : []
  if (children.length > 0) {
    return {
      ...base,
      children,
    }
  }
  return base
}

function mapMenuTreeNodes(items: MainMenuItem[] | undefined): MenuTreeItem[] {
  if (!Array.isArray(items)) return []
  return items
    .map(mapMenuTreeNode)
    .filter((item): item is MenuTreeItem => Boolean(item))
}

function getMenuTreeStaleStorageKey(includeProductGroups: boolean): string {
  return `${MENU_TREE_STALE_STORAGE_KEY_PREFIX}:${includeProductGroups ? 'full' : 'shallow'}`
}

function readMenuTreeStaleSnapshotExact(includeProductGroups: boolean): MenuTreeItem[] | null {
  if (typeof window === 'undefined') return null

  const storageKey = getMenuTreeStaleStorageKey(includeProductGroups)
  try {
    const raw = window.localStorage.getItem(storageKey)
    if (!raw) return null

    const snapshot = JSON.parse(raw) as {
      version?: unknown
      storedAt?: unknown
      items?: unknown
    }
    const storedAt = Number(snapshot?.storedAt)
    if (
      snapshot?.version !== 1
      || !Number.isFinite(storedAt)
      || storedAt <= 0
      || Date.now() - storedAt > MENU_TREE_STALE_STORAGE_TTL_MS
    ) {
      window.localStorage.removeItem(storageKey)
      return null
    }

    const tree = mapMenuTreeNodes(
      Array.isArray(snapshot.items) ? snapshot.items as MainMenuItem[] : undefined
    )
    if (tree.length === 0) {
      window.localStorage.removeItem(storageKey)
      return null
    }
    return tree
  } catch {
    try {
      window.localStorage.removeItem(storageKey)
    } catch {
      // Storage indisponível: o endpoint agregado continua sendo a fonte.
    }
    return null
  }
}

function readMenuTreeStaleSnapshot(includeProductGroups: boolean): MenuTreeItem[] | null {
  const exactSnapshot = readMenuTreeStaleSnapshotExact(includeProductGroups)
  if (exactSnapshot) return exactSnapshot

  if (!includeProductGroups) {
    const fullSnapshot = readMenuTreeStaleSnapshotExact(true)
    if (fullSnapshot) return stripMenuTreeToShallow(fullSnapshot)
  }

  return null
}

function writeMenuTreeStaleSnapshot(
  includeProductGroups: boolean,
  tree: MenuTreeItem[]
): void {
  if (tree.length === 0 || typeof window === 'undefined') return

  try {
    window.localStorage.setItem(
      getMenuTreeStaleStorageKey(includeProductGroups),
      JSON.stringify({
        version: 1,
        storedAt: Date.now(),
        items: tree,
      })
    )

    if (includeProductGroups) {
      window.localStorage.setItem(
        getMenuTreeStaleStorageKey(false),
        JSON.stringify({
          version: 1,
          storedAt: Date.now(),
          items: stripMenuTreeToShallow(tree),
        })
      )
    }
  } catch {
    // Cache persistente é best effort; uma falha não pode impedir o menu atual.
  }
}

function stripMenuTreeToShallow(items: MenuTreeItem[]): MenuTreeItem[] {
  return items.map((item) => ({
    id: item.id,
    descricao: item.descricao,
    ...(item.slug ? { slug: item.slug } : {}),
    ...(item.children && item.children.length > 0
      ? {
          children: item.children.map((child) => ({
            id: child.id,
            descricao: child.descricao,
            ...(child.slug ? { slug: child.slug } : {}),
          })),
        }
      : {}),
  }))
}

function stripMenuTreeToTopLevel(items: MenuTreeItem[]): MenuTreeItem[] {
  return items.map((item) => ({
    id: item.id,
    descricao: item.descricao,
    ...(item.slug ? { slug: item.slug } : {}),
  }))
}

// Uma chamada ao endpoint menu-arvore do Flexi2 devolve a
// árvore completa (menu → departamentos → produto-grupos) num único request,
// sem fan-out para endpoints legados.
async function fetchMenuTreeFromArvoreEndpoint(includeProductGroups: boolean): Promise<MenuTreeItem[]> {
  const lojaIdQuery = getLojaIdQuery()
  const incluirGrupos = includeProductGroups ? 1 : 0
  const querySeparator = lojaIdQuery.startsWith('?') ? '&' : '?'
  const response = await fetchApi<MainMenuResponse>(
    `/loja-virtual/v1/outros/menu-arvore${lojaIdQuery}${querySeparator}incluir_grupos=${incluirGrupos}`
  )
  return mapMenuTreeNodes(response.items)
}

// Em degradação, usa somente a última árvore válida da própria loja. Nunca
// tenta reconstruir a árvore com chamadas por menu/departamento.
async function fetchMenuTreeWithStaleFallback(includeProductGroups: boolean): Promise<MenuTreeItem[]> {
  try {
    const tree = await fetchMenuTreeFromArvoreEndpoint(includeProductGroups)
    if (tree.length > 0) {
      writeMenuTreeStaleSnapshot(includeProductGroups, tree)
      return tree
    }

    const staleTree = readMenuTreeStaleSnapshot(includeProductGroups)
    if (staleTree) {
      console.warn('menu-arvore retornou vazio; servindo a última árvore válida em cache stale.')
      return staleTree
    }

    return []
  } catch (error) {
    const staleTree = readMenuTreeStaleSnapshot(includeProductGroups)
    if (staleTree) {
      console.warn('menu-arvore indisponível; servindo a última árvore válida em cache stale.', error)
      return staleTree
    }

    console.warn('menu-arvore indisponível e sem cache stale para esta loja.', error)
    throw error
  }
}

async function fetchMenuTreeFromClassesEndpoint(): Promise<MenuTreeItem[]> {
  const response = await fetchApi<MainMenuResponse>(
    '/loja-virtual/v1/outros/classes'
  )
  return mapMenuTreeNodes(response.items)
}

function formatSpecValue(value: unknown): string {
  if (value === null || value === undefined) return ''
  if (typeof value === 'string') return value.trim()
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  if (Array.isArray(value)) {
    return value.map(formatSpecValue).filter(Boolean).join(', ')
  }
  if (typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>)
    if (entries.length === 0) return ''
    return entries
      .map(([key, val]) => {
        const formatted = formatSpecValue(val)
        if (!formatted) return key.trim()
        return `${key.trim()}: ${formatted}`
      })
      .join(' | ')
  }
  return String(value)
}

function addSpec(specs: Record<string, string | undefined>, key: string, value: unknown) {
  const normalizedKey = key.trim()
  if (!normalizedKey) return
  const formatted = formatSpecValue(value)
  if (specs[normalizedKey] && formatted) {
    specs[normalizedKey] = `${specs[normalizedKey]} / ${formatted}`
    return
  }
  specs[normalizedKey] = formatted
}

function parseTechnicalSpecsValue(raw: unknown): Record<string, string> {
  const specs: Record<string, string> = {}

  if (raw === null || raw === undefined) return specs

  if (typeof raw === 'string') {
    const trimmed = raw.trim()
    if (!trimmed) return specs
    try {
      return parseTechnicalSpecsValue(JSON.parse(trimmed))
    } catch (e) {
      console.warn('Failed to parse fichatecnica_json', e)
      addSpec(specs, 'Detalhe', trimmed)
      return specs
    }
  }

  if (Array.isArray(raw)) {
    raw.forEach((item, index) => {
      if (item && typeof item === 'object' && !Array.isArray(item)) {
        Object.entries(item).forEach(([key, value]) => addSpec(specs, key, value))
      } else if (item !== null && item !== undefined) {
        addSpec(specs, `Item ${index + 1}`, item)
      }
    })
    return specs
  }

  if (raw && typeof raw === 'object') {
    Object.entries(raw as Record<string, unknown>).forEach(([key, value]) => {
      if (/^\d+$/.test(key) && value && typeof value === 'object' && !Array.isArray(value)) {
        Object.entries(value as Record<string, unknown>).forEach(([innerKey, innerValue]) =>
          addSpec(specs, innerKey, innerValue)
        )
        return
      }
      addSpec(specs, key, value)
    })
    return specs
  }

  addSpec(specs, 'Detalhe', raw)
  return specs
}

function parseTechnicalSpecs(raw?: string): Record<string, string> {
  return parseTechnicalSpecsValue(raw)
}

function parseVariationAttributes(
  raw?: LojaVirtualVariacaoCaracteristica[] | null
): ProductVariationAttribute[] {
  if (!Array.isArray(raw)) return []

  return raw.reduce<ProductVariationAttribute[]>((entries, item, index) => {
    const name = typeof item?.variacao === 'string' ? item.variacao.trim() : ''
    const value = typeof item?.valor === 'string' ? item.valor.trim() : ''
    const variationId = Number(item?.variacao_id ?? 0)
    const selectable = Number(item?.atributo_selecionavel ?? 0) === 1 || item?.atributo_selecionavel === true
    const selectorType = normalizeVariationSelectorType(item?.atributo_selecionavel_tipo)
    const color = typeof item?.varicao_cor === 'string'
      ? item.varicao_cor.trim()
      : (typeof item?.variacao_cor === 'string' ? item.variacao_cor.trim() : '')
    const colors = color
      .split(',')
      .map((entry) => normalizeVariationSelectorColor(entry))
      .filter(Boolean)
      .slice(0, 2)

    if (!name || !value) return entries

    entries.push({
      variationId: Number.isFinite(variationId) && variationId > 0 ? String(Math.trunc(variationId)) : undefined,
      name: name || `Caracteristica ${index + 1}`,
      value,
      selectable,
      selectorType,
      color: colors[0] || undefined,
      colors: colors.length > 0 ? colors : undefined,
    })

    return entries
  }, [])
}

function normalizeVariationAttributeName(value: string): string {
  return value
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .toLocaleLowerCase('pt-BR')
}

function buildVariationAttributeKey(name: string, variationId?: string | number | null): string {
  const numericVariationId = Number(variationId ?? 0)
  if (Number.isFinite(numericVariationId) && numericVariationId > 0) {
    return `variacao:${Math.trunc(numericVariationId)}`
  }

  return normalizeVariationAttributeName(name)
}

function normalizeVariationSelectorType(value: unknown): 'foto' | 'caixa' | 'cor' | undefined {
  const normalized = String(value ?? '')
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .toLocaleLowerCase('pt-BR')

  if (['foto', 'imagem', 'image', 'photo'].includes(normalized)) return 'foto'
  if (['caixa', 'caixa de selecao', 'box', 'texto', 'text'].includes(normalized)) return 'caixa'
  if (['cor', 'color'].includes(normalized)) return 'cor'
  return undefined
}

function normalizeVariationSelectorColor(value: unknown): string {
  const normalized = typeof value === 'string'
    ? value.trim().toUpperCase()
    : ''

  return /^#[0-9A-F]{6}$/.test(normalized) || /^#[0-9A-F]{3}$/.test(normalized)
    ? normalized
    : ''
}

function normalizeProductOrder(value: unknown): number | undefined {
  if (typeof value === 'number') {
    return Number.isFinite(value) && value > 0
      ? Math.trunc(value)
      : undefined
  }

  if (typeof value === 'string') {
    const trimmedValue = value.trim()
    if (!trimmedValue) return undefined

    const normalized = Number(trimmedValue)
    return Number.isFinite(normalized) && normalized > 0
      ? Math.trunc(normalized)
      : undefined
  }

  return undefined
}

type ProductOrderCarrier = {
  ordem?: unknown
  order?: unknown
  sequencia?: unknown
  posicao?: unknown
  position?: unknown
}

function resolveProductOrderValue(item: ProductOrderCarrier | null | undefined): number | undefined {
  if (!item) return undefined

  const candidates = [
    item.ordem,
    item.order,
    item.sequencia,
    item.posicao,
    item.position,
  ]

  for (const candidate of candidates) {
    const normalizedOrder = normalizeProductOrder(candidate)
    if (typeof normalizedOrder === 'number') return normalizedOrder
  }

  return undefined
}

function sortItemsByOrder<T extends ProductOrderCarrier>(items: readonly T[]): T[] {
  return items
    .map((item, index) => ({ item, index }))
    .sort((left, right) => {
      const leftOrder = resolveProductOrderValue(left.item) ?? Number.MAX_SAFE_INTEGER
      const rightOrder = resolveProductOrderValue(right.item) ?? Number.MAX_SAFE_INTEGER

      if (leftOrder !== rightOrder) return leftOrder - rightOrder
      return left.index - right.index
    })
    .map(({ item }) => item)
}

function parseVariationSelectorConfig(
  raw?: LojaVirtualVariacaoConfiguracao[] | null
): ProductVariationSelectorConfig[] {
  if (!Array.isArray(raw)) return []

  const configByKey = new Map<string, ProductVariationSelectorConfig>()
  const orderedItems = sortItemsByOrder(raw).map((item, index) => ({ item, index }))

  orderedItems.forEach(({ item }, index) => {
    const selectable = Number(item?.atributo_selecionavel ?? 0) === 1 || item?.atributo_selecionavel === true
    const selectorType = normalizeVariationSelectorType(item?.atributo_selecionavel_tipo)
      || (selectable ? 'foto' : undefined)
    const variationId = Number(item?.variacao_id ?? item?.produto_variacao_id ?? 0)
    const name = [
      item?.variacao,
      item?.variacao_descricao,
      item?.descricao,
    ].find((value) => typeof value === 'string' && value.trim())
    const normalizedName = typeof name === 'string' ? name.trim() : ''

    if (!normalizedName || !selectable || !selectorType) return

    const normalizedVariationId = Number.isFinite(variationId) && variationId > 0
      ? String(Math.trunc(variationId))
      : undefined
    const key = buildVariationAttributeKey(normalizedName, normalizedVariationId) || `configuracao-${index + 1}`
    if (configByKey.has(key)) return

    configByKey.set(key, {
      variationId: normalizedVariationId,
      key,
      name: normalizedName,
      order: resolveProductOrderValue(item),
      selectorType,
    })
  })

  return Array.from(configByKey.values())
}

function parseVariationCharacteristics(
  raw?: LojaVirtualVariacaoCaracteristica[] | null
): Record<string, string> {
  const specs: Record<string, string> = {}

  parseVariationAttributes(raw).forEach(({ name, value }) => {
    addSpec(specs, name, value)
  })

  return specs
}

function addPositiveMeasurementSpec(
  specs: Record<string, string | undefined>,
  key: string,
  value: unknown
) {
  const numeric = normalizeFreteNumber(value)
  if (typeof numeric !== 'number' || !Number.isFinite(numeric) || numeric <= 0) return
  addSpec(specs, key, numeric)
}

function buildLojaVirtualProductListSpecs(item: LojaVirtualProdutoItem): Record<string, string> {
  const specs = parseTechnicalSpecsValue(item.fichatecnica ?? item.fichatecnica_json)

  addPositiveMeasurementSpec(specs, 'Peso bruto', item.peso_bruto)
  addPositiveMeasurementSpec(specs, 'Altura', item.altura)
  addPositiveMeasurementSpec(specs, 'Largura', item.largura)
  addPositiveMeasurementSpec(specs, 'Comprimento', item.comprimento)

  return Object.fromEntries(
    Object.entries(specs).filter(([, value]) => typeof value === 'string' && value.trim())
  )
}

function getPrimaryVariationValue(
  raw?: LojaVirtualVariacaoCaracteristica[] | null
): string | undefined {
  return parseVariationAttributes(raw).find((item) => item.value)?.value
}

function normalizeStringList(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.filter((item): item is string => typeof item === 'string' && item.trim() !== '')
  }
  if (typeof value === 'string') {
    const trimmed = value.trim()
    return trimmed ? [trimmed] : []
  }
  return []
}

function normalizeVideoUrl(value: string): string {
  const trimmed = value.trim()
  if (!trimmed) return ''

  try {
    const parsed = new URL(trimmed)
    const hostname = parsed.hostname.toLowerCase()

    if (
      hostname === 'youtube.com'
      || hostname === 'www.youtube.com'
      || hostname === 'm.youtube.com'
      || hostname === 'youtu.be'
      || hostname === 'www.youtu.be'
    ) {
      const pathSegments = parsed.pathname.split('/').filter(Boolean)
      const directVideoId = hostname.includes('youtu.be')
        ? pathSegments[0] || ''
        : parsed.searchParams.get('v')
          || (
            pathSegments[0] === 'watch'
            || pathSegments[0] === 'embed'
            || pathSegments[0] === 'shorts'
            || pathSegments[0] === 'live'
          )
            ? pathSegments[1] || ''
            : ''

      if (directVideoId) {
        return `https://www.youtube.com/watch?v=${directVideoId}`
      }
    }
  } catch {
    return trimmed
  }

  return trimmed
}

function normalizeVideoAssets(value: unknown): ProductVideoAsset[] {
  const extractAsset = (item: unknown): ProductVideoAsset | null => {
    if (typeof item === 'string') {
      const normalized = normalizeVideoUrl(item)
      return normalized ? { url: normalized } : null
    }
    if (item && typeof item === 'object') {
      const url = (item as { url?: unknown }).url
      if (typeof url === 'string') {
        const normalized = normalizeVideoUrl(url)
        if (!normalized) return null
        const description = normalizeCheckoutText(
          (item as { descricao?: unknown; description?: unknown }).descricao
          ?? (item as { descricao?: unknown; description?: unknown }).description
        )
        return description
          ? { url: normalized, description }
          : { url: normalized }
      }
    }
    return null
  }

  const assets = Array.isArray(value)
    ? value.map(extractAsset).filter((asset): asset is ProductVideoAsset => Boolean(asset))
    : (() => {
        const asset = extractAsset(value)
        return asset ? [asset] : []
      })()

  const dedupedAssets = new Map<string, ProductVideoAsset>()
  assets.forEach((asset) => {
    const existing = dedupedAssets.get(asset.url)
    if (!existing || (!existing.description && asset.description)) {
      dedupedAssets.set(asset.url, asset)
    }
  })

  return Array.from(dedupedAssets.values())
}

function normalizeVideoList(value: ApiVideoList): string[] {
  return normalizeVideoAssets(value).map((asset) => asset.url)
}

function getOptionalVideoAssets(assets: ProductVideoAsset[]): ProductVideoAsset[] | undefined {
  return assets.length > 0 ? assets : undefined
}

function normalizeImageUrl(value: string | null | undefined): string {
  if (typeof value !== 'string') return ''
  const trimmed = value.trim()
  if (!trimmed) return ''
  return trimmed
}

function normalizePriceValue(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value
  }

  if (typeof value !== 'string') return undefined

  const trimmed = value.trim()
  if (!trimmed) return undefined

  const cleaned = trimmed.replace(/[^\d,.-]/g, '')
  if (!cleaned) return undefined

  let normalized = cleaned
  if (cleaned.includes(',') && cleaned.includes('.')) {
    if (cleaned.lastIndexOf(',') > cleaned.lastIndexOf('.')) {
      normalized = cleaned.replace(/\./g, '').replace(',', '.')
    } else {
      normalized = cleaned.replace(/,/g, '')
    }
  } else if (cleaned.includes(',')) {
    normalized = cleaned.replace(',', '.')
  }

  const parsed = Number.parseFloat(normalized)
  if (!Number.isFinite(parsed)) return undefined

  return parsed
}

const STOREFRONT_BOX_VARIANT_ID_SEPARATOR = '__cx__'
const STOREFRONT_BOX_SELECTOR_KEY = 'embalagem'
const STOREFRONT_BOX_SELECTOR_LABEL = 'Embalagem'

function normalizePositiveIntegerValue(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
    return Math.trunc(value)
  }

  if (typeof value !== 'string') return undefined

  const trimmed = value.trim()
  if (!/^\d+$/.test(trimmed)) return undefined

  const parsed = Number.parseInt(trimmed, 10)
  if (!Number.isFinite(parsed) || parsed <= 0) return undefined

  return Math.trunc(parsed)
}

function roundStorefrontPrice(value: number, decimals = 2): number {
  if (!Number.isFinite(value)) return 0
  return Number(value.toFixed(decimals))
}

function formatStorefrontCurrencyValue(value: number): string {
  if (!Number.isFinite(value)) return 'R$ 0,00'

  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)
}

function buildStorefrontBoxVariantId(baseProductId: string, quantity: number): string {
  return `${baseProductId}${STOREFRONT_BOX_VARIANT_ID_SEPARATOR}${Math.max(1, Math.trunc(quantity))}`
}

function parseStorefrontBoxVariantId(value: string | null | undefined): { baseProductId: string; quantity: number } | null {
  const normalized = String(value ?? '').trim()
  if (!normalized) return null

  const separatorIndex = normalized.indexOf(STOREFRONT_BOX_VARIANT_ID_SEPARATOR)
  if (separatorIndex <= 0) return null

  const baseProductId = normalized.slice(0, separatorIndex).trim()
  const quantity = normalizePositiveIntegerValue(
    normalized.slice(separatorIndex + STOREFRONT_BOX_VARIANT_ID_SEPARATOR.length)
  )

  if (!baseProductId || !quantity) return null

  return { baseProductId, quantity }
}

function buildStorefrontBoxSavingsPercent(compareAtPrice: number | undefined, actualPrice: number | undefined): number | undefined {
  if (
    typeof compareAtPrice !== 'number'
    || !Number.isFinite(compareAtPrice)
    || compareAtPrice <= 0
    || typeof actualPrice !== 'number'
    || !Number.isFinite(actualPrice)
    || actualPrice <= 0
    || compareAtPrice <= actualPrice
  ) {
    return undefined
  }

  return roundStorefrontPrice(((compareAtPrice - actualPrice) / compareAtPrice) * 100, 2)
}

function buildStorefrontBoxLabel(quantity: number): string {
  return `CAIXA C/ ${Math.max(1, Math.trunc(quantity))}`
}

function parseStorefrontProductBoxOptions(source: unknown): ProductBoxOption[] {
  if (!source || typeof source !== 'object') return []

  const record = source as Record<string, unknown>
  const candidates = [
    record.produto_caixa,
    record.produtoCaixa,
    record.caixas,
    record.boxes,
    record.box_options,
    record.boxOptions,
  ]

  const rawEntries = candidates.find(Array.isArray)
  if (!Array.isArray(rawEntries)) return []

  return rawEntries.reduce<ProductBoxOption[]>((entries, item) => {
    if (!item || typeof item !== 'object') return entries

    const option = item as Record<string, unknown>
    const quantity = normalizePositiveIntegerValue(
      option.qtde_caixa
      ?? option.qtdeCaixa
      ?? option.quantidade
      ?? option.quantity
      ?? option.caixa
    )

    if (!quantity || quantity <= 1) return entries

    const isExcluded = Number(option.excluido ?? option.deleted ?? 0) === 1
    const isActiveCandidate = option.ativo ?? option.active ?? option.situacao
    const isInactive = isActiveCandidate === false
      || isActiveCandidate === 0
      || isActiveCandidate === '0'
      || String(isActiveCandidate ?? '').trim().toLowerCase() === 'inativo'

    if (isExcluded || isInactive) return entries

    const totalPriceCandidate = normalizePriceValue(
      option.preco_caixa
      ?? option.precoCaixa
      ?? option.preco_total
      ?? option.precoTotal
      ?? option.total
      ?? option.valor_caixa
      ?? option.valorCaixa
    )

    const unitPriceCandidate = normalizePriceValue(
      option.preco_unitario
      ?? option.precoUnitario
      ?? option.valor_unitario
      ?? option.valorUnitario
      ?? option.preco_venda
      ?? option.precoVenda
      ?? option.preco
      ?? option.price
    )

    const originalTotalCandidate = normalizePriceValue(
      option.preco_caixa_original
      ?? option.precoCaixaOriginal
      ?? option.preco_total_original
      ?? option.precoTotalOriginal
      ?? option.valor_caixa_original
      ?? option.valorCaixaOriginal
    )

    const originalUnitCandidate = normalizePriceValue(
      option.preco_semdesconto
      ?? option.precoSemDesconto
      ?? option.preco_original
      ?? option.precoOriginal
      ?? option.preco_base
      ?? option.precoBase
      ?? option.preco_unitario_original
      ?? option.precoUnitarioOriginal
    )

    const totalPrice = typeof totalPriceCandidate === 'number'
      ? roundStorefrontPrice(totalPriceCandidate, 4)
      : typeof unitPriceCandidate === 'number'
        ? roundStorefrontPrice(unitPriceCandidate * quantity, 4)
        : undefined

    const unitPrice = typeof unitPriceCandidate === 'number'
      ? roundStorefrontPrice(unitPriceCandidate, 4)
      : typeof totalPrice === 'number'
        ? roundStorefrontPrice(totalPrice / quantity, 4)
        : undefined

    const originalTotalPrice = typeof originalTotalCandidate === 'number'
      ? roundStorefrontPrice(originalTotalCandidate, 4)
      : typeof originalUnitCandidate === 'number'
        ? roundStorefrontPrice(originalUnitCandidate * quantity, 4)
        : undefined

    const originalUnitPrice = typeof originalUnitCandidate === 'number'
      ? roundStorefrontPrice(originalUnitCandidate, 4)
      : typeof originalTotalPrice === 'number'
        ? roundStorefrontPrice(originalTotalPrice / quantity, 4)
        : undefined

    if (typeof totalPrice !== 'number' && typeof unitPrice !== 'number') return entries

    const savingsAmount = normalizePriceValue(
      option.economia
      ?? option.economia_valor
      ?? option.economiaValor
    ) ?? (
      typeof originalTotalPrice === 'number' && typeof totalPrice === 'number'
        ? roundStorefrontPrice(Math.max(originalTotalPrice - totalPrice, 0), 4)
        : undefined
    )

    const savingsPercent = normalizePriceValue(
      option.desconto_percentual
      ?? option.descontoPercentual
      ?? option.percentual_desconto
      ?? option.percentualDesconto
      ?? option.economia_percentual
      ?? option.economiaPercentual
    ) ?? buildStorefrontBoxSavingsPercent(originalTotalPrice, totalPrice)

    entries.push({
      quantity,
      label: buildStorefrontBoxLabel(quantity),
      unitPrice,
      totalPrice,
      originalUnitPrice,
      originalTotalPrice,
      savingsAmount: typeof savingsAmount === 'number' ? roundStorefrontPrice(savingsAmount, 4) : undefined,
      savingsPercent,
    })

    return entries
  }, [])
}

function appendStorefrontPackagingSelectorConfig(
  configs: ProductVariationSelectorConfig[] | undefined
): ProductVariationSelectorConfig[] {
  const current = Array.isArray(configs) ? [...configs] : []
  const alreadyConfigured = current.some((config) => config.key === STOREFRONT_BOX_SELECTOR_KEY)
  if (!alreadyConfigured) {
    current.push({
      key: STOREFRONT_BOX_SELECTOR_KEY,
      name: STOREFRONT_BOX_SELECTOR_LABEL,
      order: current.length + 1,
      selectorType: 'caixa',
    })
  }
  return current
}

function appendStorefrontPackagingVariationAttributes(
  attributes: ProductVariationAttribute[] | undefined,
  packagingLabel: string,
  savingsText?: string,
): ProductVariationAttribute[] {
  const current = Array.isArray(attributes) ? [...attributes] : []
  current.push({
    variationId: STOREFRONT_BOX_SELECTOR_KEY,
    name: STOREFRONT_BOX_SELECTOR_LABEL,
    value: packagingLabel,
    selectable: true,
    selectorType: 'caixa',
  })

  if (savingsText) {
    current.push({
      name: 'Oferta',
      value: savingsText,
    })
  }

  return current
}

function buildStorefrontBoxSavingsText(product: Pick<Product, 'boxSavingsAmount' | 'boxSavingsPercent' | 'packagingLabel' | 'price'>): string | undefined {
  const parts: string[] = []

  if (typeof product.boxSavingsPercent === 'number' && Number.isFinite(product.boxSavingsPercent) && product.boxSavingsPercent > 0) {
    parts.push(`${product.boxSavingsPercent.toFixed(2).replace('.', ',')}% OFF`)
  }

  if (typeof product.boxSavingsAmount === 'number' && Number.isFinite(product.boxSavingsAmount) && product.boxSavingsAmount > 0) {
    parts.push(`Economia de ${formatStorefrontCurrencyValue(product.boxSavingsAmount)} por caixa`)
  }

  if (typeof product.price === 'number' && Number.isFinite(product.price) && product.price > 0) {
    parts.push(`Valor da caixa fechada ${formatStorefrontCurrencyValue(product.price)}`)
  }

  return parts.length > 0 ? parts.join(' - ') : undefined
}

const PRODUCT_DISTRIBUTION_PRICE_KEYS = [
  'preco_distribuicao',
  'precoDistribuicao',
  'preco_distribuidor',
  'precoDistribuidor',
  'distributionPrice',
  'distribution_price',
  'distribuitionPrice',
  'distribuition_price',
  'catalog_preco_distribuicao',
  'catalogPrecoDistribuicao',
  'preco_distribuicao_catalogo',
  'precoDistribuicaoCatalogo',
]

function normalizeDistributionPriceValue(value: unknown): number | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined

  const record = value as Record<string, unknown>
  for (const key of PRODUCT_DISTRIBUTION_PRICE_KEYS) {
    if (!(key in record)) continue

    const price = normalizePriceValue(record[key])
    if (typeof price === 'number') return price
  }

  return undefined
}

function sortProductPhotoRecords(photos?: LojaVirtualProdutoDetalheFoto[] | null) {
  if (!Array.isArray(photos)) return []

  return [...photos].sort((a, b) => {
    const principalDiff = Number(b?.principal || 0) - Number(a?.principal || 0)
    if (principalDiff !== 0) return principalDiff
    const ordemA = typeof a?.ordem === 'number' ? a.ordem : Number.MAX_SAFE_INTEGER
    const ordemB = typeof b?.ordem === 'number' ? b.ordem : Number.MAX_SAFE_INTEGER
    return ordemA - ordemB
  })
}

function resolveProductPhotoUrl(photo?: LojaVirtualProdutoDetalheFoto | null) {
  const high = normalizeProductImageUrl(photo?.imagem_high)
  if (high) return high

  const original = normalizeProductImageUrl(photo?.imagem)
  if (original && !isMidProductImageUrl(original)) return original

  return normalizeProductImageUrl(photo?.imagem_mid) || original || ''
}

function buildPreferredProductImageList({
  image,
  imageMid,
  imageHigh,
  photos,
  extraImages = [],
}: {
  image?: string | null
  imageMid?: string | null
  imageHigh?: string | null
  photos?: LojaVirtualProdutoDetalheFoto[] | null
  extraImages?: Array<string | null | undefined>
}) {
  const original = normalizeProductImageUrl(image)
  const high = normalizeProductImageUrl(imageHigh)
  const mid = normalizeProductImageUrl(imageMid)
  const preferredOriginal = original && !isMidProductImageUrl(original) ? original : ''
  const photoImages = sortProductPhotoRecords(photos)
    .map((photo) => resolveProductPhotoUrl(photo))
    .filter(Boolean)

  return withProductImagePlaceholder([
    ...photoImages,
    high,
    preferredOriginal,
    ...extraImages,
    mid,
    original,
  ], { includePlaceholder: false })
}

function isLojaVirtualProductDetail(value: unknown): value is LojaVirtualProdutoDetalhe {
  if (!value || typeof value !== 'object') return false
  const detailId = Number((value as { id?: unknown }).id)
  return Number.isFinite(detailId) && detailId > 0
}

function isLojaVirtualVariationDetail(value: unknown): value is LojaVirtualVariacaoDetalhe {
  if (!value || typeof value !== 'object') return false
  const variationId = Number((value as { produto_id?: unknown }).produto_id)
  return Number.isFinite(variationId) && variationId > 0
}

function getLojaIdValue(lojaId?: number): number | undefined {
  if (typeof lojaId === 'number' && Number.isFinite(lojaId) && lojaId > 0) {
    return lojaId
  }

  const defaultLojaId = Number.parseInt(LOJA_VIRTUAL_DEFAULT_LOJA_ID, 10)
  if (Number.isFinite(defaultLojaId) && defaultLojaId > 0) {
    return defaultLojaId
  }

  return undefined
}

function resolveLojaIdOrThrow(context: string, lojaId?: number): number {
  const resolved = getLojaIdValue(lojaId)
  if (typeof resolved !== 'number' || !Number.isFinite(resolved) || resolved <= 0) {
    throw new Error(`${context} requer loja_id configurado.`)
  }
  return Math.trunc(resolved)
}

export type LojaVirtualAnalyticsStatus = {
  enabled: boolean
  reason: 'flag_disabled' | 'loja_id_missing' | null
  flagEnabled: boolean
  integrationAuthConfigured: boolean
  lojaId?: number
}

export function getLojaVirtualAnalyticsStatus(): LojaVirtualAnalyticsStatus {
  const lojaId = getLojaIdValue()
  const flagEnabled = ENABLE_STOREFRONT_ANALYTICS
  const integrationAuthConfigured = SHOULD_USE_INTEGRATION_AUTH || API_BASE_URL.startsWith('/api')

  if (!flagEnabled) {
    return {
      enabled: false,
      reason: 'flag_disabled',
      flagEnabled,
      integrationAuthConfigured,
      ...(typeof lojaId === 'number' ? { lojaId } : {}),
    }
  }

  if (typeof lojaId !== 'number') {
    return {
      enabled: false,
      reason: 'loja_id_missing',
      flagEnabled,
      integrationAuthConfigured,
    }
  }

  return {
    enabled: true,
    reason: null,
    flagEnabled,
    integrationAuthConfigured,
    lojaId,
  }
}

export function canSendLojaVirtualAnalytics(): boolean {
  return getLojaVirtualAnalyticsStatus().enabled
}

const QUICK_CHECKOUT_REQUEST_OPTIONS = {
  skipIntegrationAuth: true,
  skipBuyerSessionToken: true,
  skipBuyerSessionCredentials: true,
} as const

export async function quoteQuickCheckoutFreight(
  input: QuickCheckoutFreightQuoteInput
): Promise<QuickCheckoutFreightQuoteResponse> {
  const lojaId = resolveLojaIdOrThrow('Finalizar compra', input.lojaId)
  return fetchApi<QuickCheckoutFreightQuoteResponse>('/loja-virtual/v1/checkout-rapido/fretes/cotar', {
    ...QUICK_CHECKOUT_REQUEST_OPTIONS,
    method: 'POST',
    body: JSON.stringify({
      loja_id: lojaId,
      cep: input.cep,
      cep_fiscal: input.cepFiscal,
      itens: input.itens,
      affiliate_code: input.affiliateCode || undefined,
    }),
  })
}

export async function prepareQuickCheckout(input: QuickCheckoutPrepareInput): Promise<QuickCheckoutPrepareResponse> {
  const lojaId = resolveLojaIdOrThrow('Finalizar compra', input.lojaId)
  return fetchApi<QuickCheckoutPrepareResponse>('/loja-virtual/v1/checkout-rapido/preparar', {
    ...QUICK_CHECKOUT_REQUEST_OPTIONS,
    method: 'POST',
    body: JSON.stringify({
      loja_id: lojaId,
      tipo_pessoa: input.tipoPessoa,
      nome: input.nome,
      responsavel: input.responsavel,
      razao_social: input.razaoSocial,
      inscricao_estadual: input.inscricaoEstadual,
      whatsapp: input.whatsapp,
      email: input.email,
      documento: input.documento,
      cpf: input.cpf,
      cnpj: input.cnpj,
      cep: input.cep,
      cep_fiscal: input.cepFiscal,
      endereco_fiscal: input.enderecoFiscal,
      numero_fiscal: input.numeroFiscal,
      bairro_fiscal: input.bairroFiscal,
      complemento_fiscal: input.complementoFiscal,
      cidade_fiscal: input.cidadeFiscal,
      uf_fiscal: input.ufFiscal,
      endereco_entrega: input.enderecoEntrega,
      numero_entrega: input.numeroEntrega,
      bairro_entrega: input.bairroEntrega,
      complemento_entrega: input.complementoEntrega,
      cidade_entrega: input.cidadeEntrega,
      uf_entrega: input.ufEntrega,
      itens: input.itens,
      idempotency_key: input.idempotencyKey,
      affiliate_code: input.affiliateCode || undefined,
    }),
  })
}

export async function generateQuickCheckoutOrder(
  input: QuickCheckoutGenerateInput
): Promise<QuickCheckoutGenerateResponse> {
  return fetchApi<QuickCheckoutGenerateResponse>('/loja-virtual/v1/checkout-rapido/pedidos/gerar', {
    ...QUICK_CHECKOUT_REQUEST_OPTIONS,
    method: 'POST',
    body: JSON.stringify({
      checkout_id: input.checkoutId,
      calculo_hash: input.calculoHash,
      frete_id: input.freteId,
      forma_pagamento_id: input.formaPagamentoId,
      idempotency_key: input.idempotencyKey,
      finalizar_cadastro_apos_pagamento: input.finalizarCadastroAposPagamento ? 1 : undefined,
      email: input.email,
      cep: input.cep,
      endereco: input.endereco,
      numero: input.numero,
      bairro: input.bairro,
      complemento: input.complemento,
      cidade: input.cidade,
      uf: input.uf,
      ...(input.analytics ? { analytics: input.analytics } : {}),
    }),
  })
}

export async function completeQuickCheckoutRegistration(
  input: QuickCheckoutCompleteInput
): Promise<QuickCheckoutCompleteResponse> {
  return fetchApi<QuickCheckoutCompleteResponse>(
    `/loja-virtual/v1/checkout-rapido/pedidos/${input.pedidoId}/completar-cadastro`,
    {
      ...QUICK_CHECKOUT_REQUEST_OPTIONS,
      method: 'POST',
      body: JSON.stringify({
        checkout_id: input.checkoutId,
        completion_token: input.completionToken,
        email: input.email,
        cep: input.cep,
        endereco: input.endereco,
        numero: input.numero,
        bairro: input.bairro,
        complemento: input.complemento,
        cidade: input.cidade,
        uf: input.uf,
      }),
    }
  )
}

function resolveAuthenticatedEntidadeIdOrThrow(context: string): number {
  const resolved = normalizeEntidadeId(getAuthenticatedEntidadeId())
  if (typeof resolved !== 'number' || !Number.isFinite(resolved) || resolved <= 0) {
    throw new Error(`${context} requer entidade_id autenticado.`)
  }
  return Math.trunc(resolved)
}

function resolveRequestedEntidadeIdOrThrow(entidadeId: number | undefined, context: string): number {
  const resolved = normalizeEntidadeId(entidadeId) ?? normalizeEntidadeId(getAuthenticatedEntidadeId())
  if (typeof resolved !== 'number' || !Number.isFinite(resolved) || resolved <= 0) {
    throw new Error(`${context} requer entidade_id autenticado.`)
  }
  return Math.trunc(resolved)
}

function resolveCarrinhoEntidadeId(entidadeId?: number): number {
  const resolved = normalizeEntidadeId(entidadeId) ?? normalizeEntidadeId(getAuthenticatedEntidadeId())
  if (typeof resolved !== 'number' || !Number.isFinite(resolved) || resolved <= 0) {
    throw new Error('Operacao de carrinho requer entidade_id autenticado.')
  }
  return Math.trunc(resolved)
}

function buildCarrinhoEndpoint(
  entidadeId: number,
  operation: 'itens' | 'quantidades' | 'totais' | 'limpar' | 'recalcular',
  lojaId?: number
): string {
  const normalizedEntidadeId = Math.trunc(entidadeId)
  const params = new URLSearchParams()
  const normalizedLojaId = getLojaIdValue(lojaId)

  if (normalizedLojaId && operation !== 'itens') {
    params.set('loja_id', normalizedLojaId.toString())
  }

  const suffix = params.toString()
  return `/loja-virtual/v1/compradores/${normalizedEntidadeId}/carrinho/${operation}${suffix ? `?${suffix}` : ''}`
}

function normalizeCarrinhoQuantidade(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value) && value >= 0) {
    return value
  }

  if (typeof value !== 'string') return undefined

  const trimmed = value.trim()
  if (!trimmed) return undefined

  const parsed = Number.parseFloat(trimmed.replace(',', '.'))
  if (!Number.isFinite(parsed) || parsed < 0) return undefined

  return parsed
}

function parseCarrinhoItemKey(itemKey: string): CarrinhoItemKeyParts | null {
  const normalizedKey = typeof itemKey === 'string' ? itemKey.trim() : ''
  if (!normalizedKey) return null

  const [rawProdutoId = '', rawQtdeCaixa = '1'] = normalizedKey.split('_')
  const produtoId = Number.parseInt(rawProdutoId, 10)
  if (!Number.isFinite(produtoId) || produtoId <= 0) return null

  const parsedQtdeCaixa = Number.parseInt(rawQtdeCaixa, 10)
  const qtdeCaixa = Number.isFinite(parsedQtdeCaixa) ? Math.max(1, Math.trunc(parsedQtdeCaixa)) : 1

  return {
    produtoId: Math.trunc(produtoId),
    qtdeCaixa,
    itemKey: `${Math.trunc(produtoId)}_${qtdeCaixa}`,
  }
}

function normalizeDigits(value: string | null | undefined): string {
  if (typeof value !== 'string') return ''
  return value.replace(/\D/g, '')
}

function parseBooleanLike(value: unknown): boolean {
  return value === true
    || value === 1
    || value === '1'
    || value === 'true'
    || value === 'TRUE'
}

function normalizeFreteNumber(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value
  }

  if (typeof value !== 'string') return undefined
  const trimmed = value.trim()
  if (!trimmed) return undefined

  const normalized = trimmed
    .replace(/[^\d,.-]/g, '')
    .replace(/\.(?=\d{3}(?:\D|$))/g, '')
    .replace(',', '.')

  const parsed = Number.parseFloat(normalized)
  if (!Number.isFinite(parsed)) return undefined
  return parsed
}

function normalizeFreteSpecKey(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase('pt-BR')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
}

function normalizeFreteMeasurementSource(key: string, value: string): string {
  return `${key} ${value}`
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase('pt-BR')
}

function parseFreteMeasurement(
  key: string,
  value: string,
  type: 'weight' | 'length'
): number | undefined {
  const source = normalizeFreteMeasurementSource(key, value)
  const numberMatches = source.match(/\d+(?:[.,]\d+)?/g) ?? []
  if (type === 'length' && numberMatches.length > 1) {
    return undefined
  }

  const parsed = normalizeFreteNumber(value) ?? normalizeFreteNumber(source)
  if (typeof parsed !== 'number' || parsed <= 0) {
    return undefined
  }

  if (type === 'weight') {
    if (/\b(?:mg|miligramas?)\b/.test(source)) return parsed / 1_000_000
    if (/\b(?:g|gramas?)\b/.test(source) && !/\b(?:kg|kgs|kilogramas?)\b/.test(source)) return parsed / 1_000
    return parsed
  }

  if (/\b(?:mm|milimetros?)\b/.test(source)) return parsed / 10
  if (/\b(?:cm|centimetros?)\b/.test(source)) return parsed
  if (/\b(?:m|metro|metros)\b/.test(source)) return parsed * 100
  return parsed
}

function findFreteMeasurementInSpecs(
  specs: Product['specs'],
  keys: string[],
  type: 'weight' | 'length'
): number | undefined {
  const normalizedKeys = keys.map((key) => normalizeFreteSpecKey(key))

  for (const [specKey, specValue] of Object.entries(specs ?? {})) {
    if (typeof specValue !== 'string' || !specValue.trim()) continue

    const normalizedSpecKey = normalizeFreteSpecKey(specKey)
    if (!normalizedKeys.some((key) => normalizedSpecKey.includes(key))) continue

    const parsed = parseFreteMeasurement(specKey, specValue, type)
    if (typeof parsed === 'number' && parsed > 0) {
      return parsed
    }
  }

  return undefined
}

function buildFreteCotacaoItemFromProduct(product: Product, quantity: number): FreteCotacaoInputItem | null {
  const qtde = Math.max(0, Math.trunc(normalizeFreteNumber(quantity) ?? 0))
  if (qtde <= 0) return null

  const peso = findFreteMeasurementInSpecs(product.specs, ['peso', 'peso bruto', 'peso liquido'], 'weight')
  const altura = findFreteMeasurementInSpecs(product.specs, ['altura'], 'length')
  const largura = findFreteMeasurementInSpecs(product.specs, ['largura'], 'length')
  const comprimento = findFreteMeasurementInSpecs(product.specs, ['comprimento', 'profundidade'], 'length')

  if (
    typeof peso !== 'number' || peso <= 0
    || typeof altura !== 'number' || altura <= 0
    || typeof largura !== 'number' || largura <= 0
    || typeof comprimento !== 'number' || comprimento <= 0
  ) {
    return null
  }

  return {
    qtde,
    peso,
    altura,
    largura,
    comprimento,
  }
}

async function resolveFreteCotacaoItemFromCartItem(
  item: CartItem,
  detailedProductOverride?: Product | null,
): Promise<FreteCotacaoInputItem | null> {
  const fromCurrentProduct = buildFreteCotacaoItemFromProduct(item.product, item.quantity)
  if (fromCurrentProduct) {
    return fromCurrentProduct
  }

  const detailedProduct = detailedProductOverride
    ?? await (async () => {
      try {
        return await getProductById(item.product.id)
      } catch (error) {
        console.warn(`Falha ao carregar dimensões de frete para o produto ${item.product.id}.`, error)
        return null
      }
    })()

  if (!detailedProduct) return null
  return buildFreteCotacaoItemFromProduct(detailedProduct, item.quantity)
}

function buildFreteCotarPayload(input: {
  destinoCep: string
  itens: FreteCotacaoInputItem[]
  destinoCnpj?: string
  origemCep?: string
  origemCnpj?: string
  forcarRecalculo?: boolean
  notaId?: number
  transportadoraId?: number
}): FreteCotarRequest {
  const origemCep = normalizeDigits(input.origemCep || FRETE_DEFAULT_ORIGEM_CEP).slice(0, 8)
  const destinoCep = normalizeDigits(input.destinoCep).slice(0, 8)

  if (origemCep.length !== 8) {
    throw new Error('Origem do frete inválida. Configure VITE_API_FRETE_ORIGEM_CEP.')
  }
  if (destinoCep.length !== 8) {
    throw new Error('CEP de destino inválido para cotação de frete.')
  }

  const itens = input.itens
    .map((item) => {
      const qtde = normalizeFreteNumber(item.qtde) ?? 0
      const peso = normalizeFreteNumber(item.peso) ?? 0
      const altura = normalizeFreteNumber(item.altura) ?? 0
      const largura = normalizeFreteNumber(item.largura) ?? 0
      const comprimento = normalizeFreteNumber(item.comprimento) ?? 0
      return {
        qtde: Math.max(0, qtde),
        peso: Math.max(0, peso),
        altura: Math.max(0, altura),
        largura: Math.max(0, largura),
        comprimento: Math.max(0, comprimento),
      }
    })
    .filter((item) => item.qtde > 0)

  if (itens.length === 0) {
    throw new Error('Sem itens válidos para cotação de frete.')
  }

  const pesoTotal = itens.reduce((sum, item) => sum + item.qtde * item.peso, 0)
  const m3Total = itens.reduce(
    (sum, item) => sum + item.qtde * ((item.altura / 100) * (item.largura / 100) * (item.comprimento / 100)),
    0
  )

  return {
    ...(typeof input.notaId === 'number' && Number.isFinite(input.notaId) ? { nota_id: Math.trunc(input.notaId) } : {}),
    ...(typeof input.transportadoraId === 'number' && Number.isFinite(input.transportadoraId) && input.transportadoraId > 0
      ? { transportadora_id: Math.trunc(input.transportadoraId) }
      : {}),
    forcar_recalculo: input.forcarRecalculo ? 1 : 0,
    requisicao: {
      origem: {
        cep: origemCep,
        ...(normalizeDigits(input.origemCnpj || FRETE_DEFAULT_ORIGEM_CNPJ)
          ? { cnpj: normalizeDigits(input.origemCnpj || FRETE_DEFAULT_ORIGEM_CNPJ) }
          : {}),
      },
      destino: {
        cep: destinoCep,
        ...(normalizeDigits(input.destinoCnpj) ? { cnpj: normalizeDigits(input.destinoCnpj) } : {}),
      },
      peso: Math.max(0.001, Number(pesoTotal.toFixed(3))),
      m3: Math.max(0.000001, Number(m3Total.toFixed(6))),
      itens,
    },
  }
}

async function executeFreteCotacaoRequest(
  payload: FreteCotarRequest,
  options?: {
    signal?: AbortSignal
    retryCacheMiss?: boolean
    trackBackendAvailability?: boolean
  }
): Promise<{ response: FreteCotarResponse; options: FreteCotacaoOption[] }> {
  const wait = (ms: number) => new Promise<void>((resolve) => {
    setTimeout(resolve, ms)
  })

  let response = await fetchApi<FreteCotarResponse>('/frete/cotar', {
    method: 'POST',
    body: JSON.stringify(payload),
    signal: options?.signal,
    trackBackendAvailability: options?.trackBackendAvailability,
  })
  let mappedOptions = mapFreteCotacaoOptions(response.cotacao)

  if (options?.retryCacheMiss !== false) {
    for (let attempt = 1; attempt < FRETE_MAX_ATTEMPTS && mappedOptions.length === 0; attempt += 1) {
      const motivo = (response.motivo || '').toLocaleLowerCase('pt-BR')
      const isCacheMiss = motivo.includes('cache_miss') || motivo.includes('cache miss')
      if (!isCacheMiss) break

      await wait(FRETE_RETRY_DELAY_MS * attempt)
      response = await fetchApi<FreteCotarResponse>('/frete/cotar', {
        method: 'POST',
        body: JSON.stringify(payload),
        signal: options?.signal,
        trackBackendAvailability: options?.trackBackendAvailability,
      })
      mappedOptions = mapFreteCotacaoOptions(response.cotacao)
    }
  }

  return {
    response,
    options: mappedOptions,
  }
}

function buildCheckoutShippingOptionFromFreteCotacaoOption(
  option: FreteCotacaoOption,
  tipoFrete: 'cif' | 'fob',
  redespacho: CheckoutRedespachoInfo | null = null
): CheckoutShippingOption | null {
  const transportadoraId = Math.trunc(normalizeFreteNumber(option.raw.transportadora_id) ?? 0)
  if (transportadoraId <= 0) {
    return null
  }

  const cotacaoId = Math.trunc(normalizeFreteNumber(option.raw.cotacao_id) ?? 0)
  const quoteNumber = normalizeCheckoutText(option.raw.numero_cotacao)
  const serviceLabel = normalizeCheckoutText(option.raw.operadora ?? option.raw.modalidade ?? option.raw.servico)

  return {
    id: `${transportadoraId}:${cotacaoId > 0 ? cotacaoId : 0}:${quoteNumber || option.id}:${tipoFrete}`,
    transportadoraId,
    cotacaoId,
    quoteNumber: quoteNumber || undefined,
    tipoFrete,
    label: option.label,
    description: serviceLabel,
    price: Math.max(0, option.price),
    eta: option.eta,
    mediaAvaliacao: 0,
    totalAvaliacoes: 0,
    calculado: true,
    retiraNoLocal: false,
    alertaTexto: '',
    redespacho: tipoFrete === 'cif' ? redespacho : null,
  }
}

function buildCheckoutFreteTransportadoraCacheKey(input: {
  destinoCep: string
  destinoCnpj?: string
  transportadoraId: number
  itens: FreteCotacaoInputItem[]
}): string {
  return JSON.stringify({
    destinoCep: normalizeDigits(input.destinoCep).slice(0, 8),
    destinoCnpj: normalizeDigits(input.destinoCnpj),
    transportadoraId: Math.max(0, Math.trunc(input.transportadoraId)),
    itens: input.itens.map((item) => ({
      qtde: Math.max(0, Math.trunc(normalizeFreteNumber(item.qtde) ?? 0)),
      peso: Number((normalizeFreteNumber(item.peso) ?? 0).toFixed(3)),
      altura: Number((normalizeFreteNumber(item.altura) ?? 0).toFixed(2)),
      largura: Number((normalizeFreteNumber(item.largura) ?? 0).toFixed(2)),
      comprimento: Number((normalizeFreteNumber(item.comprimento) ?? 0).toFixed(2)),
    })),
  })
}

type FreteTransportadorasDisponiveisStorageEntry = {
  transportadoraIds: number[]
  expiresAt: number
}

const normalizeFreteTransportadoraIdsDisponiveis = (values: unknown) => {
  if (!Array.isArray(values)) return []

  return Array.from(new Set(
    values
      .map((value) => Math.trunc(normalizeFreteNumber(value) ?? 0))
      .filter((value) => value > 0)
  ))
}

const getFreteTransportadorasDisponiveisStorage = () => {
  if (typeof window === 'undefined') return null

  try {
    return window.localStorage
  } catch {
    return null
  }
}

const readFreteTransportadorasDisponiveisStorage = () => {
  const storage = getFreteTransportadorasDisponiveisStorage()
  if (!storage) return null

  try {
    const rawEntry = storage.getItem(FRETE_TRANSPORTADORAS_DISPONIVEIS_STORAGE_KEY)
    if (!rawEntry) return null

    const entry = JSON.parse(rawEntry) as Partial<FreteTransportadorasDisponiveisStorageEntry>
    const transportadoraIds = normalizeFreteTransportadoraIdsDisponiveis(entry.transportadoraIds)

    if (transportadoraIds.length === 0 || !entry.expiresAt || entry.expiresAt <= Date.now()) {
      storage.removeItem(FRETE_TRANSPORTADORAS_DISPONIVEIS_STORAGE_KEY)
      return null
    }

    return transportadoraIds
  } catch {
    storage.removeItem(FRETE_TRANSPORTADORAS_DISPONIVEIS_STORAGE_KEY)
    return null
  }
}

const writeFreteTransportadorasDisponiveisStorage = (transportadoraIds: number[]) => {
  const storage = getFreteTransportadorasDisponiveisStorage()
  if (!storage || transportadoraIds.length === 0) return

  try {
    storage.setItem(
      FRETE_TRANSPORTADORAS_DISPONIVEIS_STORAGE_KEY,
      JSON.stringify({
        transportadoraIds,
        expiresAt: Date.now() + FRETE_TRANSPORTADORAS_DISPONIVEIS_STORAGE_TTL_MS,
      } satisfies FreteTransportadorasDisponiveisStorageEntry)
    )
  } catch {
    // Ignore storage write failures and keep the live response path working.
  }
}

async function getFreteTransportadoraIdsDisponiveis(options?: {
  trackBackendAvailability?: boolean
}): Promise<number[]> {
  const cachedTransportadoraIds = readFreteTransportadorasDisponiveisStorage()
  if (cachedTransportadoraIds) return cachedTransportadoraIds

  const response = await fetchApi<FreteTransportadorasDisponiveisResponse>('/frete/transportadoras/disponiveis', {
    method: 'GET',
    trackBackendAvailability: options?.trackBackendAvailability,
  })

  const transportadoraIds = normalizeFreteTransportadoraIdsDisponiveis(response.transportadora_ids)
  writeFreteTransportadorasDisponiveisStorage(transportadoraIds)
  return transportadoraIds
}

async function getCheckoutShippingOptionsForTransportadora(input: {
  transportadoraId: number
  destinoCep: string
  destinoCnpj?: string
  itens: FreteCotacaoInputItem[]
  tipoFrete: 'cif' | 'fob'
  trackBackendAvailability?: boolean
}): Promise<CheckoutShippingOption[]> {
  const cacheKey = buildCheckoutFreteTransportadoraCacheKey(input)
  const cached = checkoutFreteTransportadoraCache.get(cacheKey)
  if (cached) {
    return cached
  }

  const inFlight = checkoutFreteTransportadoraRequests.get(cacheKey)
  if (inFlight) {
    return inFlight
  }

  const request = (async () => {
    const controller = typeof AbortController === 'undefined' ? null : new AbortController()
    const timeoutId = controller
      ? globalThis.setTimeout(() => controller.abort(), CHECKOUT_FRETE_TRANSPORTADORA_TIMEOUT_MS)
      : null

    try {
      const payload = buildFreteCotarPayload({
        destinoCep: input.destinoCep,
        destinoCnpj: input.destinoCnpj,
        itens: input.itens,
        transportadoraId: input.transportadoraId,
      })
      const result = await executeFreteCotacaoRequest(payload, {
        signal: controller?.signal,
        retryCacheMiss: false,
        trackBackendAvailability: input.trackBackendAvailability,
      })

      const options = result.options
        .map((option) => buildCheckoutShippingOptionFromFreteCotacaoOption(option, input.tipoFrete))
        .filter((option): option is CheckoutShippingOption => Boolean(option))

      if (options.length > 0) {
        checkoutFreteTransportadoraCache.set(cacheKey, options)
      }

      return options
    } finally {
      if (timeoutId !== null) {
        globalThis.clearTimeout(timeoutId)
      }
      checkoutFreteTransportadoraRequests.delete(cacheKey)
    }
  })()

  checkoutFreteTransportadoraRequests.set(cacheKey, request)
  return request
}

function mapFreteCotacaoItem(
  rawItem: Record<string, unknown>,
  index: number
): FreteCotacaoOption | null {
  const price = normalizeFreteNumber(rawItem.valor ?? rawItem.preco ?? rawItem.price)
  if (typeof price !== 'number' || price < 0) return null

  const transportadora = normalizeDescriptorValue(
    typeof rawItem.transportadora === 'string' ? rawItem.transportadora : undefined
  )
  const operadora = normalizeDescriptorValue(
    typeof rawItem.operadora === 'string' ? rawItem.operadora : undefined
  )
  const numeroCotacao = normalizeDescriptorValue(
    typeof rawItem.numero_cotacao === 'string' ? rawItem.numero_cotacao : undefined
  )
  const transportadoraId = Math.trunc(normalizeFreteNumber(rawItem.transportadora_id) ?? 0)
  const prazo = Math.trunc(normalizeFreteNumber(rawItem.prazo) ?? 0)

  const eta = prazo > 0 ? `${prazo} ${prazo === 1 ? 'dia útil' : 'dias úteis'}` : 'Prazo sob consulta'
  const label = transportadora
    || (operadora ? `Operadora ${operadora}` : `Transportadora ${index + 1}`)

  const idParts = [
    transportadoraId > 0 ? `t${transportadoraId}` : '',
    operadora ? `o${operadora}` : '',
    numeroCotacao ? `n${numeroCotacao}` : '',
  ].filter(Boolean)

  return {
    id: idParts.length > 0 ? idParts.join('-') : `frete-${index + 1}`,
    label,
    description: eta,
    eta,
    price,
    raw: rawItem,
  }
}

function mapFreteCotacaoOptions(
  rawCotacao: FreteCotarResponse['cotacao']
): FreteCotacaoOption[] {
  if (!rawCotacao) return []

  const rows = Array.isArray(rawCotacao)
    ? rawCotacao
    : (typeof rawCotacao === 'object' ? [rawCotacao] : [])

  return rows
    .filter((row): row is Record<string, unknown> => Boolean(row && typeof row === 'object'))
    .map((row, index) => mapFreteCotacaoItem(row, index))
    .filter((option): option is FreteCotacaoOption => Boolean(option))
    .sort((a, b) => a.price - b.price)
}

function mapCarrinhoQuantidadesMap(rawItens: Record<string, unknown> | undefined): Record<string, number> {
  if (!rawItens || typeof rawItens !== 'object') return {}

  return Object.entries(rawItens).reduce<Record<string, number>>((acc, [itemKey, value]) => {
    const quantity = normalizeCarrinhoQuantidade(value)
    if (typeof quantity === 'number') {
      acc[itemKey] = quantity
    }
    return acc
  }, {})
}

function extractListFromResponse(payload: unknown, keys: string[]): any[] {
  if (Array.isArray(payload)) return payload
  if (!payload || typeof payload !== 'object') return []

  const record = payload as Record<string, unknown>
  for (const key of keys) {
    const value = record[key]
    if (Array.isArray(value)) return value
  }

  if (Array.isArray(record.data)) return record.data
  if (Array.isArray(record.items)) return record.items
  return []
}

function normalizePositiveInteger(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
    return Math.trunc(value)
  }

  if (typeof value === 'string') {
    const parsed = Number.parseInt(value, 10)
    if (Number.isFinite(parsed) && parsed > 0) {
      return parsed
    }
  }

  return null
}

function extractTotalPagesFromResponse(payload: unknown): number | null {
  if (!payload || typeof payload !== 'object') return null

  const record = payload as Record<string, unknown>
  const pagination = record.pagination && typeof record.pagination === 'object'
    ? record.pagination as Record<string, unknown>
    : null

  return normalizePositiveInteger(
    pagination?.totalPages
    ?? pagination?.total_pages
    ?? pagination?.pages
    ?? pagination?.pageCount
    ?? pagination?.page_count
    ?? record.totalPages
    ?? record.total_pages
    ?? record.pages
    ?? record.pageCount
    ?? record.page_count
  )
}

function extractCurrentPageFromResponse(payload: unknown): number | null {
  if (!payload || typeof payload !== 'object') return null

  const record = payload as Record<string, unknown>
  const pagination = record.pagination && typeof record.pagination === 'object'
    ? record.pagination as Record<string, unknown>
    : null

  return normalizePositiveInteger(
    pagination?.page
    ?? pagination?.currentPage
    ?? pagination?.current_page
    ?? pagination?.pagina
    ?? record.page
    ?? record.currentPage
    ?? record.current_page
    ?? record.pagina
  )
}

function extractPerPageFromResponse(payload: unknown): number | null {
  if (!payload || typeof payload !== 'object') return null

  const record = payload as Record<string, unknown>
  const pagination = record.pagination && typeof record.pagination === 'object'
    ? record.pagination as Record<string, unknown>
    : null

  return normalizePositiveInteger(
    pagination?.perPage
    ?? pagination?.per_page
    ?? pagination?.pageSize
    ?? pagination?.page_size
    ?? pagination?.limit
    ?? record.perPage
    ?? record.per_page
    ?? record.pageSize
    ?? record.page_size
    ?? record.limit
  )
}

function extractTotalCountFromResponse(payload: unknown): number | null {
  if (!payload || typeof payload !== 'object') return null

  const record = payload as Record<string, unknown>
  const pagination = record.pagination && typeof record.pagination === 'object'
    ? record.pagination as Record<string, unknown>
    : null

  return normalizePositiveInteger(
    pagination?.total
    ?? pagination?.totalItems
    ?? pagination?.total_items
    ?? pagination?.count
    ?? pagination?.totalCount
    ?? pagination?.total_count
    ?? record.total
    ?? record.totalItems
    ?? record.total_items
    ?? record.count
    ?? record.totalCount
    ?? record.total_count
  )
}

function buildPaginatedListResult<T>(
  items: T[],
  payload: unknown,
  fallbackPage: number,
  fallbackPerPage: number,
  fallbackTotal?: number
): PaginatedListResult<T> {
  const page = extractCurrentPageFromResponse(payload) ?? Math.max(1, Math.trunc(fallbackPage))
  const perPage = extractPerPageFromResponse(payload) ?? Math.max(1, Math.trunc(fallbackPerPage))
  const total = extractTotalCountFromResponse(payload) ?? Math.max(0, Math.trunc(fallbackTotal ?? items.length))
  const derivedTotalPages = total > 0 ? Math.ceil(total / perPage) : 1
  const totalPages = extractTotalPagesFromResponse(payload) ?? derivedTotalPages

  return {
    items,
    page,
    perPage,
    total,
    totalPages: Math.max(1, totalPages),
  }
}

function buildLocalPaginatedListResult<T>(
  items: T[],
  page: number,
  perPage: number
): PaginatedListResult<T> {
  const normalizedPage = Math.max(1, Math.trunc(page))
  const normalizedPerPage = Math.max(1, Math.trunc(perPage))
  const start = (normalizedPage - 1) * normalizedPerPage

  return {
    items: items.slice(start, start + normalizedPerPage),
    page: normalizedPage,
    perPage: normalizedPerPage,
    total: items.length,
    totalPages: Math.max(1, Math.ceil(items.length / normalizedPerPage)),
  }
}

function normalizeDescriptorValue(value: string | null | undefined): string {
  if (typeof value !== 'string') return ''
  return value.trim()
}

function normalizeCheckoutText(value: unknown): string {
  if (typeof value === 'string') return value.trim()
  if (typeof value === 'number' && Number.isFinite(value)) return String(value)
  return ''
}

function resolveFirstCheckoutText(...values: unknown[]): string {
  for (const value of values) {
    const normalizedValue = normalizeCheckoutText(value)
    if (normalizedValue) return normalizedValue
  }

  return ''
}

function resolveFirstFreteNumber(...values: unknown[]): number | undefined {
  for (const value of values) {
    const normalizedValue = normalizeFreteNumber(value)
    if (typeof normalizedValue === 'number' && Number.isFinite(normalizedValue)) {
      return normalizedValue
    }
  }

  return undefined
}

function resolveFirstPositiveFreteNumber(...values: unknown[]): number | undefined {
  for (const value of values) {
    const normalizedValue = normalizeFreteNumber(value)
    if (typeof normalizedValue === 'number' && Number.isFinite(normalizedValue) && normalizedValue > 0) {
      return normalizedValue
    }
  }

  return undefined
}

function normalizeCheckoutRichText(value: unknown): string {
  const raw = typeof value === 'string'
    ? value
    : (typeof value === 'number' && Number.isFinite(value) ? String(value) : '')
  if (!raw) return ''

  return raw
    .replace(/<\s*br\s*\/?>/gi, '\n')
    .replace(/<\s*hr\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/\r\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .split('\n')
    .map((line) => line.replace(/\s+/g, ' ').trim())
    .filter(Boolean)
    .join('\n')
}

function hasCheckoutField(record: Record<string, unknown>, keys: string[]): boolean {
  return keys.some((key) => normalizeCheckoutText(record[key]) !== '')
}

function normalizeCheckoutStatusFluxo(value: unknown): CheckoutStatusFluxo {
  const normalized = normalizeCheckoutText(value).toLocaleLowerCase('pt-BR')
  if (normalized === 'aguardando_pagamento') return 'aguardando_pagamento'
  if (normalized === 'aguardando_confirmacao') return 'aguardando_confirmacao'
  if (normalized === 'em_separacao') return 'em_separacao'
  if (normalized === 'aprovado') return 'aprovado'
  return 'em_analise'
}

function normalizeCheckoutAttemptSource(value: unknown): CheckoutAttemptSource | null {
  const normalized = normalizeCheckoutText(value).toLocaleLowerCase('pt-BR')
  if (
    normalized === 'fresh_submit'
    || normalized === 'retry'
    || normalized === 'refresh_recovery'
    || normalized === 'fallback_recovery'
  ) {
    return normalized
  }
  return null
}

function normalizeCheckoutAttemptStatus(value: unknown): CheckoutOrderAttemptStatusValue {
  const normalized = normalizeCheckoutText(value).toLocaleLowerCase('pt-BR')
  if (normalized === 'success' || normalized === 'failed' || normalized === 'expired') {
    return normalized
  }
  return 'processing'
}

function resolveCheckoutAttemptStatusFromResponse(
  attemptStatusValue: unknown,
  pedidoId: number,
  numeroPedido: number
): CheckoutOrderAttemptStatusValue {
  const rawStatus = normalizeCheckoutText(attemptStatusValue).toLocaleLowerCase('pt-BR')
  const normalizedStatus = normalizeCheckoutAttemptStatus(attemptStatusValue)

  if (rawStatus) {
    return normalizedStatus
  }

  if (pedidoId > 0 || numeroPedido > 0) {
    return 'success'
  }

  return normalizedStatus
}

function resolveCheckoutStatusNumero(statusFluxo: CheckoutStatusFluxo): number | null {
  if (statusFluxo === 'aguardando_confirmacao') return 2
  if (statusFluxo === 'aguardando_pagamento') return 3
  if (statusFluxo === 'em_separacao') return 4
  if (statusFluxo === 'aprovado') return 1
  return null
}

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === 'object' && !Array.isArray(value))
}

const CHECKOUT_CONTACT_COLLECTION_KEYS = [
  'contatos',
  'contacts',
  'contatos_cliente',
  'cliente_contatos',
  'contatos_entidade',
  'entidade_contatos',
  'whatsapp_destinos',
  'whatsappDestinos',
]

const CHECKOUT_CONTACT_PHONE_KEYS = [
  'whatsapp',
  'telefone',
  'celular',
  'phone',
  'fone',
  'numero',
  'number',
  'destino',
  'valor',
  'contato_telefone',
  'telefone_contato',
  'telefone_celular',
  'celular_whatsapp',
  'whatsapp_numero',
]

const CHECKOUT_CONTACT_LOGIN_FLAG_KEYS = [
  'whatsapp_login',
  'login_whatsapp',
  'permite_login_whatsapp',
  'pode_logar_whatsapp',
  'habilitar_login_whatsapp',
  'habilitado_login_whatsapp',
  'whatsapp_validado',
  'validado_whatsapp',
  'telefone_validado',
  'validado',
  'principal',
]

function readCheckoutContactRecords(value: unknown): Record<string, unknown>[] {
  if (Array.isArray(value)) return value.filter(isPlainRecord)
  if (!isPlainRecord(value)) return []

  return Object.values(value).filter(isPlainRecord)
}

function resolveCheckoutContactPhone(record: Record<string, unknown>) {
  for (const key of CHECKOUT_CONTACT_PHONE_KEYS) {
    const phone = normalizePossibleWhatsappDigits(record[key])
    if (phone) return phone
  }

  return ''
}

function hasCheckoutLoginWhatsappFlag(record: Record<string, unknown>) {
  return CHECKOUT_CONTACT_LOGIN_FLAG_KEYS.some((key) => parseBooleanLike(record[key]))
}

function resolveCheckoutCustomerWhatsapp(raw: Record<string, unknown>) {
  const candidates: Array<{ phone: string; score: number; index: number }> = []

  const addCandidate = (record: Record<string, unknown>, index: number, baseScore: number) => {
    const phone = resolveCheckoutContactPhone(record)
    if (!phone) return

    const hasWhatsappField = record.whatsapp !== undefined || record.whatsapp_numero !== undefined
    const score = baseScore
      + (hasCheckoutLoginWhatsappFlag(record) ? 8 : 0)
      + (hasWhatsappField ? 3 : 0)

    candidates.push({ phone, score, index })
  }

  addCandidate(raw, 0, 2)

  CHECKOUT_CONTACT_COLLECTION_KEYS.forEach((key, collectionIndex) => {
    readCheckoutContactRecords(raw[key]).forEach((record, recordIndex) => {
      addCandidate(record, (collectionIndex + 1) * 100 + recordIndex, 4)
    })
  })

  return candidates
    .sort((first, second) => second.score - first.score || first.index - second.index)[0]?.phone ?? ''
}

async function enrichCheckoutCustomerProfileWithLoginWhatsapp(
  customer: CheckoutCustomerProfile
): Promise<CheckoutCustomerProfile> {
  const currentPhone = normalizePossibleWhatsappDigits(customer.phone)
  if (currentPhone) return { ...customer, phone: currentPhone }
  if (!customer.document) return customer

  try {
    const destinations = await getPasswordlessDestinations(customer.document)
    const loginPhone = destinations.whatsappDestinations
      .map((destination) => destination.phone)
      .find((phone): phone is string => Boolean(phone && isPossiblePhoneDigits(phone)))

    return loginPhone ? { ...customer, phone: loginPhone } : customer
  } catch {
    return customer
  }
}

function parseCheckoutJsonCandidate(value: unknown): unknown | null {
  if (typeof value !== 'string') return null

  const normalized = value.trim()
  if (!normalized || !/^[{\[]/.test(normalized)) return null

  try {
    return JSON.parse(normalized)
  } catch {
    return null
  }
}

function collectCheckoutInstructionRecords(
  value: unknown,
  depth: number = 3,
  seen: Set<Record<string, unknown>> = new Set()
): Record<string, unknown>[] {
  if (!isPlainRecord(value) || seen.has(value)) return []
  seen.add(value)

  const records = [value]
  if (depth <= 0) return records

  Object.values(value).forEach((entry) => {
    if (Array.isArray(entry)) {
      entry.forEach((item) => {
        records.push(...collectCheckoutInstructionRecords(item, depth - 1, seen))
      })
      return
    }

    if (isPlainRecord(entry)) {
      records.push(...collectCheckoutInstructionRecords(entry, depth - 1, seen))
      return
    }

    const parsedJson = parseCheckoutJsonCandidate(entry)
    if (Array.isArray(parsedJson)) {
      parsedJson.forEach((item) => {
        records.push(...collectCheckoutInstructionRecords(item, depth - 1, seen))
      })
      return
    }

    if (isPlainRecord(parsedJson)) {
      records.push(...collectCheckoutInstructionRecords(parsedJson, depth - 1, seen))
    }
  })

  return records
}

function getFirstCheckoutString(candidates: Record<string, unknown>[], keys: string[]): string {
  for (const candidate of candidates) {
    for (const key of keys) {
      const value = normalizeCheckoutText(candidate[key])
      if (value) return value
    }
  }

  return ''
}

function getFirstCheckoutNumber(candidates: Record<string, unknown>[], keys: string[]): number | null {
  for (const candidate of candidates) {
    for (const key of keys) {
      const value = normalizeFreteNumber(candidate[key])
      if (typeof value === 'number' && Number.isFinite(value)) {
        return value
      }
    }
  }

  return null
}

function getCheckoutArrayCandidates(candidates: Record<string, unknown>[], keys: string[]): unknown[] {
  for (const candidate of candidates) {
    for (const key of keys) {
      const value = candidate[key]
      if (Array.isArray(value) && value.length > 0) return value
      if (isPlainRecord(value)) return [value]
    }
  }

  return []
}

function normalizeCheckoutImageSource(value: string): string {
  const normalized = value.trim()
  if (!normalized) return ''
  if (/^data:image\//i.test(normalized)) return normalized
  if (/^(https?:)?\/\//i.test(normalized) || normalized.startsWith('/')) return normalized
  if (/^[A-Za-z0-9+/=]+$/.test(normalized) && normalized.length > 96) {
    return `data:image/png;base64,${normalized}`
  }
  return ''
}

function normalizeMercadoPagoFormMethod(value: string): 'GET' | 'POST' {
  return value.toUpperCase() === 'GET' ? 'GET' : 'POST'
}

function normalizeMercadoPagoFields(value: unknown): CheckoutMercadoPagoFormField[] {
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (!isPlainRecord(item)) return null
        const name = normalizeCheckoutText(item.name ?? item.key ?? item.campo)
        if (!name) return null
        return {
          name,
          value: normalizeCheckoutText(item.value ?? item.valor),
        }
      })
      .filter((item): item is CheckoutMercadoPagoFormField => Boolean(item))
  }

  if (!isPlainRecord(value)) return []

  return Object.entries(value)
    .map(([name, fieldValue]) => ({
      name: normalizeCheckoutText(name),
      value: normalizeCheckoutText(fieldValue),
    }))
    .filter((item) => item.name)
}

function buildCheckoutBankTransferAccount(value: unknown): CheckoutBankTransferAccount | null {
  if (!isPlainRecord(value)) return null

  const bankName = normalizeCheckoutText(value.banco ?? value.bank ?? value.bank_name)
  const bankCode = normalizeCheckoutText(value.banco_codigo ?? value.bank_code ?? value.codigo_banco)
  const agency = normalizeCheckoutText(value.agencia ?? value.agency)
  const account = normalizeCheckoutText(value.conta ?? value.account ?? value.numero_conta)
  const accountType = normalizeCheckoutText(value.tipo_conta ?? value.account_type)
  const explicitHolderName = normalizeCheckoutText(
    value.favorecido
    ?? value.titular
    ?? value.holder
    ?? value.holder_name
    ?? value.nome_favorecido
    ?? value.nome_titular
    ?? value.beneficiario
  )
  const hasStructuredAccountFields = hasCheckoutField(value, [
    'banco',
    'bank',
    'bank_name',
    'banco_codigo',
    'bank_code',
    'codigo_banco',
    'agencia',
    'agency',
    'conta',
    'account',
    'numero_conta',
    'tipo_conta',
    'account_type',
    'favorecido',
    'titular',
    'holder',
    'holder_name',
    'nome_favorecido',
    'nome_titular',
    'beneficiario',
    'documento',
    'doc',
    'cnpj',
    'cpf',
    'chave_pix',
    'pix_key',
    'dados_deposito',
    'dados_bancarios',
    'bank_data',
  ])
  const holderName = explicitHolderName || (
    hasStructuredAccountFields
      ? normalizeCheckoutText(value.nome)
      : ''
  )
  const document = normalizeCheckoutText(value.documento ?? value.doc ?? value.cnpj ?? value.cpf)
  const pixKey = normalizeCheckoutText(value.chave_pix ?? value.pix_key ?? value.chave)
  const legacyDepositData = normalizeCheckoutRichText(
    value.dados_deposito
    ?? value.dadosDeposito
    ?? value.dados_bancarios
    ?? value.bank_data
  )
  const note = legacyDepositData || normalizeCheckoutRichText(
    value.observacao
    ?? value.obs
    ?? value.note
    ?? value.instrucao
  )
  const hasAccountIdentity = Boolean(
    bankName
    || bankCode
    || agency
    || account
    || accountType
    || holderName
    || document
    || pixKey
  )

  if (!hasAccountIdentity && !legacyDepositData) return null

  return {
    bankName,
    bankCode,
    agency,
    account,
    accountType,
    holderName,
    document,
    pixKey,
    note,
  }
}

function normalizeCheckoutPaymentReceiptSummary(value: unknown): CheckoutPaymentReceiptSummary | null {
  if (!isPlainRecord(value)) return null

  const canUpload = parseBooleanLike(value.can_upload ?? value.canUpload)
  const uploaded = parseBooleanLike(value.uploaded)
  const latestAttachmentId = normalizePositiveInteger(
    value.latest_attachment_id
    ?? value.latestAttachmentId
    ?? value.anexo_id
    ?? value.anexoId
  )
  const latestUploadedAt = normalizeCheckoutText(
    value.latest_uploaded_at
    ?? value.latestUploadedAt
    ?? value.uploaded_at
    ?? value.uploadedAt
  )
  const latestFileName = normalizeCheckoutText(
    value.latest_file_name
    ?? value.latestFileName
    ?? value.file_name
    ?? value.fileName
  )

  return {
    canUpload,
    uploaded,
    latestAttachmentId,
    latestUploadedAt,
    latestFileName,
  }
}

export function extractCheckoutPaymentReceiptSummary(payload: unknown): CheckoutPaymentReceiptSummary | null {
  const candidates = collectCheckoutInstructionRecords(payload)
  for (const candidate of candidates) {
    const summary = normalizeCheckoutPaymentReceiptSummary(
      candidate.payment_receipt
      ?? candidate.paymentReceipt
      ?? candidate.comprovante_pagamento
      ?? candidate.comprovantePagamento
    )
    if (summary) return summary
  }

  return normalizeCheckoutPaymentReceiptSummary(payload)
}

export function extractCheckoutPaymentInstructions(payload: unknown): CheckoutPaymentInstructions | null {
  const candidates = collectCheckoutInstructionRecords(payload)
  if (candidates.length === 0) return null

  const pixQrCandidate = getFirstCheckoutString(candidates, [
    'qr_code_imagem',
    'qr_code_image',
    'qr_code_url',
    'qrCodeImage',
    'qrCodeUrl',
    'qrCode',
    'qrcode_base64',
    'qr_code_base64',
    'imagem_qr',
  ])
  const pixQrCodeImageUrl = normalizeCheckoutImageSource(pixQrCandidate)
  const pixTextCandidate = getFirstCheckoutString(candidates, [
    'pix_copia_cola',
    'pix_copia_e_cola',
    'copia_cola',
    'copiaecola',
    'copiaecola_pix',
    'pix_code',
    'pix_codigo',
    'codigo_pix',
    'br_code',
    'qr_code_text',
    'qrcode_text',
    'payload',
    'emv',
    'brcode',
    'qr_code',
    'qrcode',
  ])
  const pixQrCodeText = pixQrCodeImageUrl ? pixTextCandidate : (pixTextCandidate || pixQrCandidate)
  const pixCopyPasteCode = getFirstCheckoutString(candidates, [
    'pix_copia_cola',
    'pix_copia_e_cola',
    'copia_cola',
    'copiaecola',
    'copiaecola_pix',
    'pix_code',
    'pix_codigo',
    'codigo_pix',
    'br_code',
    'payload',
    'emv',
    'brcode',
  ]) || pixQrCodeText
  const pixKey = getFirstCheckoutString(candidates, [
    'chave_pix',
    'pix_key',
    'pixkey',
    'chave',
  ])
  const pixMethodHint = getFirstCheckoutString(candidates, [
    'forma_pagamento',
    'payment_method',
    'paymentMethod',
    'metodo_pagamento',
    'kind',
    'tipo',
  ]).toLocaleLowerCase('pt-BR')

  if (pixQrCodeImageUrl || pixQrCodeText || pixCopyPasteCode || (pixKey && pixMethodHint.includes('pix'))) {
    return {
      kind: 'pix',
      pix: {
        qrCodeImageUrl: pixQrCodeImageUrl,
        qrCodeText: pixQrCodeText,
        copyPasteCode: pixCopyPasteCode,
        pixKey,
        beneficiary: getFirstCheckoutString(candidates, [
          'favorecido',
          'beneficiario',
          'beneficiary',
          'recebedor',
          'nome',
        ]),
        amount: getFirstCheckoutNumber(candidates, [
          'valor',
          'valor_cobrado',
          'valor_original',
          'original',
          'amount',
          'value',
          'total',
          'valor_total',
        ]),
        expiresAt: getFirstCheckoutString(candidates, [
          'expira_em',
          'expires_at',
          'expiresAt',
          'validade',
          'vencimento',
          'data_vencimento',
        ]),
        note: getFirstCheckoutString(candidates, [
          'mensagem',
          'message',
          'instrucao',
          'instrucoes',
          'solicitacao_pagador',
          'observacao',
          'obs',
          'note',
        ]),
      },
    }
  }

  const transferAccounts = getCheckoutArrayCandidates(candidates, [
    'contas',
    'contas_bancarias',
    'bank_accounts',
    'accounts',
    'transfer_accounts',
    'contas_transferencia',
  ])
    .map((account) => buildCheckoutBankTransferAccount(account))
    .filter((account): account is CheckoutBankTransferAccount => Boolean(account))

  if (transferAccounts.length === 0) {
    candidates.forEach((candidate) => {
      const account = buildCheckoutBankTransferAccount(candidate)
      if (!account) return
      const accountKey = [
        account.bankName,
        account.agency,
        account.account,
        account.pixKey,
      ].join('|')
      if (transferAccounts.some((item) => [
        item.bankName,
        item.agency,
        item.account,
        item.pixKey,
      ].join('|') === accountKey)) {
        return
      }
      transferAccounts.push(account)
    })
  }

  const bankTransferNote = transferAccounts.length > 0
    ? normalizeCheckoutRichText(getFirstCheckoutString(candidates, [
      'dados_deposito',
      'dadosDeposito',
      'dados_bancarios',
      'bank_data',
      'mensagem',
      'message',
      'instrucao',
      'instrucoes',
      'observacao',
      'obs',
      'note',
    ]))
    : normalizeCheckoutRichText(getFirstCheckoutString(candidates, [
      'dados_deposito',
      'dadosDeposito',
      'dados_bancarios',
      'bank_data',
    ]))
  const bankTransferReceiptHint = normalizeCheckoutRichText(getFirstCheckoutString(candidates, [
    'comprovante_hint',
    'receipt_hint',
    'enviar_comprovante',
    'comprovante',
  ]))

  if (transferAccounts.length > 0 || bankTransferNote || bankTransferReceiptHint) {
    return {
      kind: 'bank_transfer',
      bankTransfer: {
        accounts: transferAccounts,
        note: bankTransferNote,
        receiptHint: bankTransferReceiptHint,
      },
    }
  }

  const mercadoPagoHtml = getFirstCheckoutString(candidates, [
    'form_html',
    'html_form',
    'payment_form_html',
    'checkout_html',
    'html',
  ])
  const mercadoPagoCheckoutUrl = getFirstCheckoutString(candidates, [
    'checkout_url',
    'redirect_url',
    'payment_url',
    'url',
    'mp_url',
    'init_point',
    'sandbox_init_point',
    'sandbox_url',
  ])
  const mercadoPagoFormAction = getFirstCheckoutString(candidates, [
    'form_action',
    'action',
  ])
  const mercadoPagoFormMethod = normalizeMercadoPagoFormMethod(
    getFirstCheckoutString(candidates, ['form_method', 'method']) || (mercadoPagoFormAction ? 'POST' : 'GET')
  )
  const mercadoPagoFields = candidates.flatMap((candidate) => (
    [
      normalizeMercadoPagoFields(candidate.fields),
      normalizeMercadoPagoFields(candidate.campos),
      normalizeMercadoPagoFields(candidate.inputs),
      normalizeMercadoPagoFields(candidate.hidden_fields),
    ].flat()
  ))

  if (mercadoPagoHtml || mercadoPagoCheckoutUrl || mercadoPagoFormAction) {
    return {
      kind: 'mercado_pago',
      mercadoPago: {
        html: mercadoPagoHtml,
        checkoutUrl: mercadoPagoCheckoutUrl,
        formAction: mercadoPagoFormAction,
        formMethod: mercadoPagoFormMethod,
        formFields: mercadoPagoFields,
        submitLabel: getFirstCheckoutString(candidates, [
          'submit_label',
          'button_label',
          'cta',
          'cta_label',
        ]) || 'Pagar com Mercado Pago',
      },
    }
  }

  return null
}

function buildCheckoutEndpoint(
  entidadeId: number,
  operation: 'pagamentos/opcoes' | 'frete/opcoes' | 'simular',
  lojaId?: number
): string {
  const normalizedEntidadeId = Math.trunc(entidadeId)
  const params = new URLSearchParams()
  const normalizedLojaId = getLojaIdValue(lojaId)

  if (normalizedLojaId) {
    params.set('loja_id', normalizedLojaId.toString())
  }

  const suffix = params.toString()
  return `/loja-virtual/v1/compradores/${normalizedEntidadeId}/checkout/${operation}${suffix ? `?${suffix}` : ''}`
}

function buildPedidoGerarEndpoint(entidadeId: number): string {
  const normalizedEntidadeId = Math.trunc(entidadeId)
  return `/loja-virtual/v1/compradores/${normalizedEntidadeId}/pedidos/gerar`
}

function buildPedidoAttemptStatusEndpoint(
  entidadeId: number,
  attemptId: string,
  lojaId?: number
): string {
  const normalizedEntidadeId = Math.trunc(entidadeId)
  const params = new URLSearchParams()
  const normalizedLojaId = getLojaIdValue(lojaId)

  if (normalizedLojaId) {
    params.set('loja_id', normalizedLojaId.toString())
  }

  const suffix = params.toString()
  return `/loja-virtual/v1/compradores/${normalizedEntidadeId}/pedidos/tentativas/${encodeURIComponent(attemptId)}${suffix ? `?${suffix}` : ''}`
}

function buildPedidoMercadoPagoEndpoint(
  entidadeId: number,
  pedidoId: number,
  action: 'gerar-link' | 'atualizar-link' | 'cancelar-link'
): string {
  return `/loja-virtual/v1/compradores/${Math.trunc(entidadeId)}/pedidos/${encodeURIComponent(String(Math.trunc(pedidoId)))}/mercadopago/${action}`
}

function buildPedidoMercadoPagoCheckoutProPreferenceEndpoint(
  entidadeId: number,
  pedidoId: number
): string {
  return `/loja-virtual/v1/compradores/${Math.trunc(entidadeId)}/pedidos/${encodeURIComponent(String(Math.trunc(pedidoId)))}/mercadopago-checkout-pro/preference`
}

function isInactiveCheckoutAddress(raw: Record<string, unknown>, isDefault: boolean): boolean {
  if (isDefault) return false

  const inactiveValue = raw.inativo ?? raw.inactive ?? raw.disabled ?? raw.excluido ?? raw.deleted
  if (parseBooleanLike(inactiveValue)) return true
  if (typeof inactiveValue === 'string') {
    const normalized = inactiveValue.trim().toLowerCase()
    if (['sim', 'yes', 'inativo', 'inactive', 'disabled', 'excluido', 'deleted'].includes(normalized)) {
      return true
    }
  }

  const activeValue = raw.ativo ?? raw.active
  if (activeValue === false || activeValue === 0) return true
  if (typeof activeValue === 'string') {
    const normalized = activeValue.trim().toLowerCase()
    if (['0', 'false', 'nao', 'não', 'no', 'inativo', 'inactive', 'disabled'].includes(normalized)) {
      return true
    }
  }

  return false
}

function buildCheckoutAddressOption(
  raw: Record<string, unknown>,
  isDefault: boolean
): CheckoutAddressOption | null {
  if (isInactiveCheckoutAddress(raw, isDefault)) {
    return null
  }

  const id = Math.trunc(normalizeFreteNumber(raw.id) ?? (isDefault ? 0 : 0))
  const street = normalizeCheckoutText(raw.endereco ?? raw.logradouro ?? raw.rua)
  const city = normalizeCheckoutText(raw.cidade)
  const state = normalizeCheckoutText(raw.uf ?? raw.estado).toUpperCase()
  const zipCode = normalizeDigits(normalizeCheckoutText(raw.cep)).slice(0, 8)

  if (!street || !city || !state || zipCode.length !== 8) {
    return null
  }

  const label = normalizeCheckoutText(raw.descricao ?? raw.nome ?? raw.razao) || (isDefault ? 'Endereço padrão' : 'Endereço')
  const hasValidationStatus =
    Object.prototype.hasOwnProperty.call(raw, 'validado')
    || Object.prototype.hasOwnProperty.call(raw, 'validated')
    || Object.prototype.hasOwnProperty.call(raw, 'endereco_validado')
  const validationStatus = raw.validado ?? raw.validated ?? raw.endereco_validado

  return {
    id: isDefault ? 0 : Math.max(0, id),
    label,
    recipient: normalizeCheckoutText(raw.nome ?? raw.razao),
    street,
    number: normalizeCheckoutText(raw.numero),
    complement: normalizeCheckoutText(raw.complemento),
    neighborhood: normalizeCheckoutText(raw.bairro),
    city,
    state,
    zipCode,
    isDefault,
    isValidated: isDefault ? true : hasValidationStatus ? parseBooleanLike(validationStatus) : null,
  }
}

function buildLatestOrderCheckoutAddressOption(
  address: CheckoutLatestOrderAddressPreference | null | undefined,
  recipient: string
): CheckoutAddressOption | null {
  if (!address) return null

  return buildCheckoutAddressOption({
    id: address.id,
    descricao: 'Endereço da última compra',
    nome: recipient,
    endereco: address.street,
    numero: address.number,
    complemento: address.complement,
    bairro: address.neighborhood,
    cidade: address.city,
    uf: address.state,
    cep: address.zipCode,
    validado: 1,
  }, false)
}

const normalizeCheckoutAddressIdentityText = (value: string) =>
  normalizeCheckoutText(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')

const normalizeCheckoutAddressIdentityNumber = (value: string) => {
  const digits = normalizeDigits(value)
  if (digits) return digits

  const normalized = normalizeCheckoutAddressIdentityText(value)
  return !normalized || normalized === 'sn' || normalized === 'semnumero'
    ? 'sn'
    : normalized
}

function buildCheckoutAddressPhysicalKey(address: CheckoutAddressOption): string {
  return [
    normalizeDigits(address.zipCode).slice(0, 8),
    normalizeCheckoutAddressIdentityText(address.state),
    normalizeCheckoutAddressIdentityText(address.city),
    normalizeCheckoutAddressIdentityText(address.street),
    normalizeCheckoutAddressIdentityNumber(address.number),
    normalizeCheckoutAddressIdentityText(address.complement),
  ].join(':')
}

function normalizeTaxBreakdown(
  totalTaxes: number,
  rawIpi: unknown,
  rawDifal: unknown,
  normalize: (value: unknown) => number | undefined
): { ipi: number; difal: number } {
  const normalizedTotalTaxes = Math.max(0, totalTaxes)
  const ipi = Math.min(
    normalizedTotalTaxes,
    Math.max(0, normalize(rawIpi) ?? 0)
  )
  const difal = Math.min(
    Math.max(0, normalizedTotalTaxes - ipi),
    Math.max(0, normalize(rawDifal) ?? 0)
  )

  return {
    ipi,
    difal,
  }
}

function normalizeTaxes(
  rawTotalTaxes: unknown,
  rawIpi: unknown,
  rawDifal: unknown,
  normalize: (value: unknown) => number | undefined
): { impostos: number; ipi: number; difal: number } {
  const normalizedIpi = Math.max(0, normalize(rawIpi) ?? 0)
  const normalizedDifal = Math.max(0, normalize(rawDifal) ?? 0)
  const impostos = Math.max(0, normalize(rawTotalTaxes) ?? (normalizedIpi + normalizedDifal))
  const { ipi, difal } = normalizeTaxBreakdown(
    impostos,
    normalizedIpi,
    normalizedDifal,
    normalize
  )

  return {
    impostos,
    ipi,
    difal,
  }
}

function normalizeCheckoutSimulationItems(rawItems: unknown): CheckoutSimulationItemTaxDetail[] {
  if (!Array.isArray(rawItems)) return []

  return rawItems.reduce<CheckoutSimulationItemTaxDetail[]>((items, rawItem) => {
    if (!rawItem || typeof rawItem !== 'object') return items

    const item = rawItem as Record<string, unknown>
    const checkoutSource = item.checkout_source === 'quote'
      ? 'quote'
      : item.checkout_source === 'order'
        ? 'order'
        : 'cart'
    const cartItemId = Math.max(0, Math.trunc(normalizeFreteNumber(item.carrinho_id ?? item.carrinhoId) ?? 0))
    const quoteId = Math.max(0, Math.trunc(normalizeFreteNumber(item.orcamento_id ?? item.orcamentoId) ?? 0))
    const quoteItemId = Math.max(0, Math.trunc(normalizeFreteNumber(item.orcamento_item_id ?? item.orcamentoItemId) ?? 0))
    const orderId = Math.max(0, Math.trunc(normalizeFreteNumber(item.pedido_id ?? item.pedidoId) ?? 0))
    const orderItemId = Math.max(0, Math.trunc(normalizeFreteNumber(item.pedido_item_id ?? item.pedidoItemId) ?? 0))
    const productId = Math.max(0, Math.trunc(normalizeFreteNumber(item.produto_id ?? item.produtoId) ?? 0))
    const productGradeId = Math.max(0, Math.trunc(normalizeFreteNumber(item.produto_grade_id ?? item.produtoGradeId) ?? 0))

    if (cartItemId <= 0 && quoteItemId <= 0 && orderItemId <= 0 && productId <= 0 && productGradeId <= 0) {
      return items
    }

    const { impostos, ipi, difal } = normalizeTaxes(
      item.impostos ?? item.valor_impostos,
      item.ipi ?? item.valor_ipi,
      item.difal ?? item.valor_difal ?? item.st ?? item.valor_st,
      normalizeFreteNumber
    )

    items.push({
      checkoutSource,
      cartItemId,
      quoteId,
      quoteItemId,
      orderId,
      orderItemId,
      productId,
      productGradeId,
      quantity: Math.max(0, normalizeFreteNumber(item.qtde ?? item.quantidade) ?? 0),
      subtotal: normalizeFreteNumber(item.valor_total_base) ?? 0,
      discount: normalizeFreteNumber(item.valor_desconto) ?? 0,
      freight: normalizeFreteNumber(item.valor_frete_rateado) ?? 0,
      total: normalizeFreteNumber(item.valor_total_liquido) ?? 0,
      impostos,
      ipi,
      difal,
    })

    return items
  }, [])
}

function normalizeCheckoutQuoteItemOverridesPayload(
  overrides: Array<{ itemId: number; quantity: number }> | undefined
) {
  if (!Array.isArray(overrides)) return []

  const overridesByItemId = new Map<number, { item_id: number; qtde: number }>()

  overrides.forEach((override) => {
    const itemId = Number(override?.itemId ?? 0)
    const quantity = Number(override?.quantity ?? 0)
    if (!Number.isFinite(itemId) || itemId <= 0) return
    if (!Number.isFinite(quantity) || quantity < 0) return

    overridesByItemId.set(Math.trunc(itemId), {
      item_id: Math.trunc(itemId),
      qtde: Math.max(0, quantity),
    })
  })

  return Array.from(overridesByItemId.values()).sort((left, right) => left.item_id - right.item_id)
}

function normalizeCheckoutOrderItemOverridesPayload(
  overrides: Array<{ itemId: number; quantity: number }> | undefined
) {
  if (!Array.isArray(overrides)) return []

  const overridesByItemId = new Map<number, { item_id: number; qtde: number }>()

  overrides.forEach((override) => {
    const itemId = Number(override?.itemId ?? 0)
    const quantity = Number(override?.quantity ?? 0)
    if (!Number.isFinite(itemId) || itemId <= 0) return
    if (!Number.isFinite(quantity) || quantity < 0) return

    overridesByItemId.set(Math.trunc(itemId), {
      item_id: Math.trunc(itemId),
      qtde: Math.max(0, quantity),
    })
  })

  return Array.from(overridesByItemId.values()).sort((left, right) => left.item_id - right.item_id)
}

function buildCheckoutCustomerProfile(raw: Record<string, unknown>): CheckoutCustomerProfile | null {
  const id = Math.trunc(normalizeFreteNumber(raw.id) ?? 0)
  if (id <= 0) return null

  const zipCode = normalizeDigits(normalizeCheckoutText(raw.cep)).slice(0, 8)
  const tipoPessoaRaw = normalizeFreteNumber(raw.tipoPessoa ?? raw.tipo_pessoa ?? raw.tipopessoa)
  const modalidadeFretePadraoRaw = normalizeFreteNumber(
    raw.modalidadeFretePadrao
    ?? raw.modalidade_frete_padrao
    ?? raw.fretePorContaPadrao
    ?? raw.frete_por_conta_padrao
  )
  const transportadoraPreferencialIdRaw = normalizeFreteNumber(
    raw.transportadoraPreferencialId
    ?? raw.transportadora_preferencial_id
    ?? raw.transportadora_id
  )
  const ultimaCompraFormaPagamentoIdRaw = normalizeFreteNumber(
    raw.ultimaCompraFormaPagamentoId
    ?? raw.ultima_compra_forma_pagamento_id
  )
  const ultimaCompraCondicaoPagamentoIdRaw = normalizeFreteNumber(
    raw.ultimaCompraCondicaoPagamentoId
    ?? raw.ultima_compra_condicao_pagamento_id
  )
  const ultimaCompraCondicaoPagamentoTexto = normalizeCheckoutText(
    raw.ultimaCompraCondicaoPagamentoTexto
    ?? raw.ultima_compra_condicao_pagamento_texto
    ?? raw.ultimaCompraCondicaoPagamentoDescricao
    ?? raw.ultima_compra_condicao_pagamento_descricao
  )
  const ultimaCompraTransportadoraIdRaw = normalizeFreteNumber(
    raw.ultimaCompraTransportadoraId
    ?? raw.ultima_compra_transportadora_id
  )
  const ultimaCompraModalidadeFrete = resolveCheckoutOrderFreightType({
    frete_por_conta:
      raw.ultimaCompraFretePorConta
      ?? raw.ultima_compra_frete_por_conta,
    tipo_frete:
      raw.ultimaCompraModalidadeFrete
      ?? raw.ultima_compra_modalidade_frete
      ?? raw.ultimaCompraTipoFrete
      ?? raw.ultima_compra_tipo_frete,
  })
  const ultimaCompraEnderecoEntrega = resolveCheckoutCustomerLatestOrderAddressPreference(raw)
  const ultimaCompraContatoNome = normalizeCheckoutText(
    raw.ultimaCompraContatoNome
    ?? raw.ultima_compra_contato_nome
    ?? raw.ultimaCompraNomeContato
    ?? raw.ultima_compra_nome_contato
  )
  const ultimaCompraContatoTelefone = normalizeDigits(normalizeCheckoutText(
    raw.ultimaCompraContatoTelefone
    ?? raw.ultima_compra_contato_telefone
    ?? raw.ultimaCompraTelefoneContato
    ?? raw.ultima_compra_telefone_contato
    ?? raw.ultimaCompraWhatsappContato
    ?? raw.ultima_compra_whatsapp_contato
  ))
  const ultimaCompraFinalidadeRaw = normalizeFreteNumber(
    raw.ultimaCompraFinalidade
    ?? raw.ultima_compra_finalidade
    ?? raw.ultimaCompraFinalidadeId
    ?? raw.ultima_compra_finalidade_id
  )
  const transportadoraRedespachoAtiva = parseBooleanLike(
    raw.transportadoraRedespachoAtiva
    ?? raw.transportadora_redespacho_ativo
  )
  const transportadoraRedespachoIdRaw = normalizeFreteNumber(
    raw.transportadoraRedespachoId
    ?? raw.transportadora_redespacho_id
  )
  const transportadorasRedespachoVinculadasRaw = raw.transportadorasRedespachoVinculadas
    ?? raw.transportadoras_redespacho_vinculadas
  const finalidadePadraoRaw = normalizeFreteNumber(raw.finalidadePadrao ?? raw.finalidade_padrao ?? raw.finalidade)
  const habilitarProgramarFaturamentoDiasRaw = normalizeFreteNumber(
    raw.habilitarProgramarFaturamentoDias ?? raw.habilitar_programar_faturamento_dias
  )
  const ultimaCompraFormaPagamentoId =
    typeof ultimaCompraFormaPagamentoIdRaw === 'number'
    && Number.isFinite(ultimaCompraFormaPagamentoIdRaw)
    && Math.trunc(ultimaCompraFormaPagamentoIdRaw) > 0
      ? Math.trunc(ultimaCompraFormaPagamentoIdRaw)
      : null
  const ultimaCompraCondicaoPagamentoId =
    typeof ultimaCompraCondicaoPagamentoIdRaw === 'number'
    && Number.isFinite(ultimaCompraCondicaoPagamentoIdRaw)
    && Math.trunc(ultimaCompraCondicaoPagamentoIdRaw) > 0
      ? Math.trunc(ultimaCompraCondicaoPagamentoIdRaw)
      : null
  const linkedTransportadoras = (Array.isArray(transportadorasRedespachoVinculadasRaw) ? transportadorasRedespachoVinculadasRaw : [])
    .map((item) => {
      const source = item && typeof item === 'object' ? item as Record<string, unknown> : {}
      const vinculoId = String(source.id ?? '').trim()
      const transportadoraId = Math.trunc(
        normalizeFreteNumber(source.transportadoraId ?? source.transportadora_id) ?? 0
      )

      if (!vinculoId || transportadoraId <= 0) return null

      return {
        id: vinculoId,
        transportadoraId,
        nome: normalizeCheckoutText(source.nome ?? source.razao) || `Transportadora ${transportadoraId}`,
        cpfCnpj: normalizeDigits(normalizeCheckoutText(source.cpfCnpj ?? source.cpf_cnpj)).slice(0, 14),
        cidade: normalizeCheckoutText(source.cidade),
        uf: normalizeCheckoutText(source.uf).toUpperCase(),
        enderecoResumo: normalizeCheckoutText(source.enderecoResumo ?? source.endereco_resumo),
        preferencial: parseBooleanLike(source.preferencial),
      } as CheckoutCustomerRedespachoTransportadora
    })
    .filter((item): item is CheckoutCustomerRedespachoTransportadora => Boolean(item))

  return {
    id,
    corporateName: normalizeCheckoutText(raw.razao ?? raw.nome_fiscal ?? raw.nome),
    name: normalizeCheckoutText(raw.nome ?? raw.razao),
    document: normalizeDigits(normalizeCheckoutText(raw.cpfCnpj ?? raw.cpf_cnpj ?? raw.cpf_cnpj_texto)),
    activity: normalizeCheckoutText(
      raw.atividade
      ?? raw.atividade_descricao
      ?? raw.atividadeDescricao
      ?? raw.tipo_entidade_atividade_descricao
      ?? raw.tipoEntidadeAtividadeDescricao
      ?? raw.ramo_atividade
      ?? raw.ramoAtividade
      ?? raw.segmento
      ?? raw.segmento_nome
    ),
    stateRegistration: normalizeCheckoutText(raw.ie ?? raw.inscricao_estadual),
    email: normalizeCheckoutText(raw.email),
    phone: resolveCheckoutCustomerWhatsapp(raw) || normalizeCheckoutText(raw.whatsapp ?? raw.telefone ?? raw.fax),
    street: normalizeCheckoutText(raw.endereco ?? raw.logradouro ?? raw.rua),
    number: normalizeCheckoutText(raw.numero),
    complement: normalizeCheckoutText(raw.complemento),
    neighborhood: normalizeCheckoutText(raw.bairro),
    city: normalizeCheckoutText(raw.cidade),
    state: normalizeCheckoutText(raw.uf ?? raw.estado).toUpperCase(),
    zipCode,
    tipoPessoa: typeof tipoPessoaRaw === 'number' && Number.isFinite(tipoPessoaRaw)
      ? Math.trunc(tipoPessoaRaw)
      : null,
    modalidadeFretePadrao:
      typeof modalidadeFretePadraoRaw === 'number' && Number.isFinite(modalidadeFretePadraoRaw) && Math.trunc(modalidadeFretePadraoRaw) === 1
        ? 'fob'
        : 'cif',
    transportadoraPreferencialId:
      typeof transportadoraPreferencialIdRaw === 'number'
      && Number.isFinite(transportadoraPreferencialIdRaw)
      && Math.trunc(transportadoraPreferencialIdRaw) > 0
        ? Math.trunc(transportadoraPreferencialIdRaw)
        : null,
    // Definido no enrich a partir do histórico de pedidos finalizados.
    temUltimaCompraFinalizada: false,
    ultimaCompraTransportadoraId:
      typeof ultimaCompraTransportadoraIdRaw === 'number'
      && Number.isFinite(ultimaCompraTransportadoraIdRaw)
      && Math.trunc(ultimaCompraTransportadoraIdRaw) > 0
        ? Math.trunc(ultimaCompraTransportadoraIdRaw)
        : null,
    ultimaCompraModalidadeFrete,
    ultimaCompraEnderecoEntrega,
    ultimaCompraContatoNome,
    ultimaCompraContatoTelefone,
    ultimaCompraFormaPagamentoId,
    ultimaCompraCondicaoPagamentoId,
    ultimaCompraCondicaoPagamentoTexto,
    ultimaCompraFinalidade:
      typeof ultimaCompraFinalidadeRaw === 'number'
      && Number.isFinite(ultimaCompraFinalidadeRaw)
      && Math.trunc(ultimaCompraFinalidadeRaw) > 0
        ? Math.trunc(ultimaCompraFinalidadeRaw)
        : null,
    transportadoraRedespachoAtiva,
    transportadoraRedespachoId:
      typeof transportadoraRedespachoIdRaw === 'number'
      && Number.isFinite(transportadoraRedespachoIdRaw)
      && Math.trunc(transportadoraRedespachoIdRaw) > 0
        ? Math.trunc(transportadoraRedespachoIdRaw)
        : null,
    transportadoraRedespachoNome: normalizeCheckoutText(
      raw.transportadoraRedespachoNome
      ?? raw.transportadora_redespacho_nome
    ),
    transportadoraRedespachoEnderecoResumo: normalizeCheckoutText(
      raw.transportadoraRedespachoEnderecoResumo
      ?? raw.transportadora_redespacho_endereco_resumo
      ?? raw.transportadoraRedespachoEndereco
      ?? raw.transportadora_redespacho_endereco
    ),
    transportadoraRedespachoPreferencialVinculada: parseBooleanLike(
      raw.transportadoraRedespachoPreferencialVinculada
      ?? raw.transportadora_redespacho_preferencial_vinculada
      ?? raw.preferencialVinculada
    ),
    transportadorasRedespachoVinculadas: linkedTransportadoras,
    finalidadePadrao: typeof finalidadePadraoRaw === 'number' && Number.isFinite(finalidadePadraoRaw)
      ? Math.trunc(finalidadePadraoRaw)
      : null,
    clientePossuiMaisFinalidades: parseBooleanLike(
      raw.clientePossuiMaisFinalidades ?? raw.cliente_possui_mais_finalidades
    ),
    habilitarProgramarFaturamento: parseBooleanLike(
      raw.habilitarProgramarFaturamento ?? raw.habilitar_programar_faturamento
    ),
    habilitarProgramarFaturamentoDias:
      typeof habilitarProgramarFaturamentoDiasRaw === 'number' && Number.isFinite(habilitarProgramarFaturamentoDiasRaw)
        ? Math.trunc(habilitarProgramarFaturamentoDiasRaw)
        : null,
  }
}

function resolveCheckoutOrderCarrierId(raw: Record<string, unknown>): number | null {
  const nestedTransportadora = isPlainRecord(raw.transportadora)
    ? raw.transportadora
    : null

  const candidates = [
    raw.transportadora_id,
    raw.transportadoraId,
    raw.shipping_carrier_id,
    nestedTransportadora?.id,
    nestedTransportadora?.transportadora_id,
  ]

  for (const candidate of candidates) {
    const resolved = normalizePositiveInteger(candidate)
    if (resolved) return resolved
  }

  return null
}

function resolveCheckoutOrderId(raw: Record<string, unknown>): number | null {
  const candidates = [
    raw.id,
    raw.pedido_id,
    raw.pedidoId,
  ]

  for (const candidate of candidates) {
    const resolved = normalizePositiveInteger(candidate)
    if (resolved) return resolved
  }

  return null
}

function normalizeCheckoutOrderRecordKey(key: string): string {
  return key.replace(/[^a-z0-9]+/gi, '').toLocaleLowerCase('pt-BR')
}

function shouldVisitCheckoutOrderNestedKey(key: string): boolean {
  const normalizedKey = normalizeCheckoutOrderRecordKey(key)
  if (!normalizedKey) return false

  if (
    normalizedKey.includes('produto')
    || normalizedKey.includes('product')
    || normalizedKey.includes('itens')
    || normalizedKey.includes('items')
    || normalizedKey === 'item'
    || normalizedKey.includes('variacao')
    || normalizedKey.includes('variation')
    || normalizedKey.includes('imagem')
    || normalizedKey.includes('image')
    || normalizedKey.includes('foto')
    || normalizedKey.includes('video')
    || normalizedKey.includes('manual')
    || normalizedKey.includes('estoque')
    || normalizedKey.includes('stock')
  ) {
    return false
  }

  return (
    normalizedKey === 'data'
    || normalizedKey === 'pedido'
    || normalizedKey === 'order'
    || normalizedKey === 'venda'
    || normalizedKey === 'sale'
    || normalizedKey === 'nota'
    || normalizedKey === 'notafiscal'
    || normalizedKey === 'documentos'
    || normalizedKey.includes('checkout')
    || normalizedKey.includes('financeiro')
    || normalizedKey.includes('financial')
    || normalizedKey.includes('pagamento')
    || normalizedKey.includes('payment')
    || normalizedKey.includes('condicao')
    || normalizedKey.includes('frete')
    || normalizedKey.includes('shipping')
    || normalizedKey.includes('entrega')
    || normalizedKey.includes('delivery')
    || normalizedKey.includes('endereco')
    || normalizedKey.includes('address')
    || normalizedKey.includes('transportadora')
    || normalizedKey.includes('carrier')
    || normalizedKey.includes('cliente')
    || normalizedKey.includes('customer')
    || normalizedKey.includes('comprador')
    || normalizedKey.includes('buyer')
    || normalizedKey.includes('contato')
    || normalizedKey.includes('contact')
  )
}

function collectCheckoutOrderRecords(raw: Record<string, unknown>, depth: number = 3): Record<string, unknown>[] {
  const records: Record<string, unknown>[] = []
  const seen = new Set<Record<string, unknown>>()

  const visit = (record: Record<string, unknown>, remainingDepth: number) => {
    if (!isPlainRecord(record) || seen.has(record)) return

    seen.add(record)
    records.push(record)

    if (remainingDepth <= 0) return

    Object.entries(record).forEach(([key, value]) => {
      if (!shouldVisitCheckoutOrderNestedKey(key)) return

      if (Array.isArray(value)) {
        value.forEach((item) => {
          if (isPlainRecord(item)) {
            visit(item, remainingDepth - 1)
          }
        })
        return
      }

      if (isPlainRecord(value)) {
        visit(value, remainingDepth - 1)
        return
      }

      const parsedJson = parseCheckoutJsonCandidate(value)
      if (Array.isArray(parsedJson)) {
        parsedJson.forEach((item) => {
          if (isPlainRecord(item)) {
            visit(item, remainingDepth - 1)
          }
        })
        return
      }

      if (isPlainRecord(parsedJson)) {
        visit(parsedJson, remainingDepth - 1)
      }
    })
  }

  visit(raw, depth)
  return records
}

type CheckoutLatestOrderFallback = {
  carrierId: number | null
  freightType: 'cif' | 'fob' | null
  address: CheckoutLatestOrderAddressPreference | null
  paymentSelection: {
    formaPagamentoId: number
    condicaoPagamentoId: number | null
    condicaoPagamentoTexto: string
  } | null
  contactName: string
  contactPhone: string
  finalidade: number | null
  reachedPayment: boolean
}

const CHECKOUT_ORDER_CONTACT_NAME_KEYS = [
  'nome_contato',
  'nomeContato',
  'nome_contato_pedido',
  'pedido_contato_nome',
  'contato_nome',
  'contatoName',
  'contact_name',
  'delivery_contact_name',
  'shipping_contact_name',
  'contato',
]

const CHECKOUT_ORDER_EXPLICIT_CONTACT_PHONE_KEYS = [
  'telefone_contato',
  'telefoneContato',
  'telefone_contato_pedido',
  'pedido_contato_telefone',
  'contato_telefone',
  'contatoTelefone',
  'whatsapp_contato',
  'whatsappContato',
  'contato_whatsapp',
  'contatoWhatsapp',
  'whatsapp_numero',
  'delivery_contact_phone',
  'shipping_contact_phone',
]

const CHECKOUT_ORDER_GENERIC_CONTACT_PHONE_KEYS = [
  'whatsapp',
  'telefone',
  'celular',
  'fone',
  'telefone1',
  'telefone_1',
  'celular_whatsapp',
]

function normalizeCheckoutComparableToken(value: unknown): string {
  return normalizeCheckoutText(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .toLocaleLowerCase('pt-BR')
}

function getFirstCheckoutOrderText(records: Record<string, unknown>[], keys: string[]): string {
  for (const record of records) {
    for (const key of keys) {
      const resolved = normalizeCheckoutText(record[key])
      if (resolved) return resolved
    }
  }

  return ''
}

function getFirstCheckoutOrderPositiveInteger(records: Record<string, unknown>[], keys: string[]): number | null {
  for (const record of records) {
    for (const key of keys) {
      const resolved = normalizePositiveInteger(record[key])
      if (resolved) return resolved
    }
  }

  return null
}

function getFirstCheckoutOrderNumber(records: Record<string, unknown>[], keys: string[]): number | null {
  for (const record of records) {
    for (const key of keys) {
      const resolved = normalizeFreteNumber(record[key])
      if (typeof resolved === 'number' && Number.isFinite(resolved)) return resolved
    }
  }

  return null
}

function getCheckoutOrderPreferenceRecords(raw: Record<string, unknown>): Record<string, unknown>[] {
  const explicitAddress = isPlainRecord(raw.endereco_entrega)
    ? raw.endereco_entrega
    : isPlainRecord(raw.enderecoEntrega)
      ? raw.enderecoEntrega
      : isPlainRecord(raw.delivery_address)
        ? raw.delivery_address
        : isPlainRecord(raw.shipping_address)
          ? raw.shipping_address
          : null
  const directAddress = isPlainRecord(raw.endereco)
    ? raw.endereco
    : isPlainRecord(raw.address)
      ? raw.address
      : null
  const candidates = [
    explicitAddress,
    directAddress,
    isPlainRecord(raw.pedido) ? raw.pedido : null,
    isPlainRecord(raw.venda) ? raw.venda : null,
    isPlainRecord(raw.nota) ? raw.nota : null,
    isPlainRecord(raw.nota_fiscal) ? raw.nota_fiscal : null,
    isPlainRecord(raw.notaFiscal) ? raw.notaFiscal : null,
    raw,
  ]
  const records: Record<string, unknown>[] = []
  const seen = new Set<Record<string, unknown>>()

  candidates.forEach((candidate) => {
    if (!candidate || seen.has(candidate)) return
    seen.add(candidate)
    records.push(candidate)
  })

  return records
}

function resolveCheckoutOrderFreightType(raw: Record<string, unknown>): 'cif' | 'fob' | null {
  const records = collectCheckoutOrderRecords(raw)
  const freightResponsibility = getFirstCheckoutOrderNumber(records, [
    'frete_por_conta',
    'fretePorConta',
    'freight_responsibility',
    'freightResponsibility',
  ])

  if (freightResponsibility === 1) return 'fob'
  if (freightResponsibility === 0) return 'cif'

  const normalizedFreightType = normalizeCheckoutComparableToken(getFirstCheckoutOrderText(records, [
    'tipo_frete',
    'tipoFrete',
    'shipping_type',
    'shippingType',
    'modo_frete',
    'modoFrete',
    'modalidade_frete',
    'modalidadeFrete',
    'freight_type',
    'freightType',
    'frete',
  ]))

  if (normalizedFreightType.includes('cif')) return 'cif'
  if (normalizedFreightType.includes('fob')) return 'fob'
  return null
}

function resolveCheckoutOrderDeliveryAddress(
  raw: Record<string, unknown>
): CheckoutLatestOrderAddressPreference | null {
  const records = getCheckoutOrderPreferenceRecords(raw)
  const addressId = getFirstCheckoutOrderPositiveInteger(records, [
    'endereco_entrega_id',
    'enderecoEntregaId',
    'shipping_address_id',
    'shippingAddressId',
    'delivery_address_id',
    'deliveryAddressId',
    'endereco_id',
    'enderecoId',
    'id',
  ])
  const street = getFirstCheckoutOrderText(records, [
    'endereco_entrega',
    'logradouro_entrega',
    'rua_entrega',
    'shipping_address',
    'delivery_address',
    'endereco',
    'logradouro',
    'rua',
    'street',
  ])
  const number = getFirstCheckoutOrderText(records, [
    'numero_entrega',
    'shipping_number',
    'delivery_number',
    'numero',
    'number',
  ])
  const complement = getFirstCheckoutOrderText(records, [
    'complemento_entrega',
    'shipping_complement',
    'delivery_complement',
    'complemento',
    'complement',
  ])
  const neighborhood = getFirstCheckoutOrderText(records, [
    'bairro_entrega',
    'shipping_neighborhood',
    'delivery_neighborhood',
    'bairro',
    'neighborhood',
  ])
  const city = getFirstCheckoutOrderText(records, [
    'cidade_entrega',
    'shipping_city',
    'delivery_city',
    'cidade',
    'city',
  ])
  const state = getFirstCheckoutOrderText(records, [
    'uf_entrega',
    'shipping_state',
    'delivery_state',
    'uf',
    'estado',
    'state',
  ]).toUpperCase()
  const zipCode = normalizeDigits(getFirstCheckoutOrderText(records, [
    'cep_entrega',
    'shipping_zip',
    'delivery_zip',
    'cep',
    'zipCode',
    'zip_code',
  ])).slice(0, 8)

  if (!addressId && !street && !city && !state && !zipCode) return null

  return {
    id: addressId,
    street,
    number,
    complement,
    neighborhood,
    city,
    state,
    zipCode,
  }
}

function resolveCheckoutCustomerLatestOrderAddressPreference(
  raw: Record<string, unknown>
): CheckoutLatestOrderAddressPreference | null {
  const nestedAddress = isPlainRecord(raw.ultimaCompraEnderecoEntrega)
    ? raw.ultimaCompraEnderecoEntrega
    : isPlainRecord(raw.ultima_compra_endereco_entrega)
      ? raw.ultima_compra_endereco_entrega
      : null

  const addressFromNested = nestedAddress
    ? resolveCheckoutOrderDeliveryAddress(nestedAddress)
    : null
  if (addressFromNested) return addressFromNested

  return resolveCheckoutOrderDeliveryAddress({
    endereco_entrega_id:
      raw.ultimaCompraEnderecoEntregaId
      ?? raw.ultima_compra_endereco_entrega_id,
    endereco_entrega:
      raw.ultimaCompraEnderecoEntregaTexto
      ?? raw.ultima_compra_endereco_entrega_texto
      ?? raw.ultimaCompraEndereco
      ?? raw.ultima_compra_endereco,
    numero_entrega:
      raw.ultimaCompraEnderecoNumero
      ?? raw.ultima_compra_endereco_numero,
    complemento_entrega:
      raw.ultimaCompraEnderecoComplemento
      ?? raw.ultima_compra_endereco_complemento,
    bairro_entrega:
      raw.ultimaCompraEnderecoBairro
      ?? raw.ultima_compra_endereco_bairro,
    cidade_entrega:
      raw.ultimaCompraEnderecoCidade
      ?? raw.ultima_compra_endereco_cidade,
    uf_entrega:
      raw.ultimaCompraEnderecoUf
      ?? raw.ultima_compra_endereco_uf,
    cep_entrega:
      raw.ultimaCompraEnderecoCep
      ?? raw.ultima_compra_endereco_cep,
  })
}

function resolveCheckoutOrderRecordContactName(record: Record<string, unknown>): string {
  return getFirstCheckoutOrderText([record], CHECKOUT_ORDER_CONTACT_NAME_KEYS)
}

function resolveCheckoutOrderRecordExplicitContactPhone(record: Record<string, unknown>): string {
  return normalizeDigits(getFirstCheckoutOrderText([record], CHECKOUT_ORDER_EXPLICIT_CONTACT_PHONE_KEYS))
}

function resolveCheckoutOrderRecordGenericContactPhone(record: Record<string, unknown>): string {
  return normalizeDigits(getFirstCheckoutOrderText([record], CHECKOUT_ORDER_GENERIC_CONTACT_PHONE_KEYS))
}

function resolveCheckoutOrderContactName(raw: Record<string, unknown>): string {
  return getFirstCheckoutOrderText(collectCheckoutOrderRecords(raw), CHECKOUT_ORDER_CONTACT_NAME_KEYS)
}

function resolveCheckoutOrderContactPhone(raw: Record<string, unknown>): string {
  const records = collectCheckoutOrderRecords(raw)
  for (const record of records) {
    const recordContactName = resolveCheckoutOrderRecordContactName(record)
    const resolvedPhone = resolveCheckoutOrderRecordExplicitContactPhone(record)
      || (recordContactName ? resolveCheckoutOrderRecordGenericContactPhone(record) : '')

    if (resolvedPhone) return resolvedPhone
  }

  return ''
}

function resolveCheckoutOrderFinalidade(raw: Record<string, unknown>): number | null {
  return getFirstCheckoutOrderPositiveInteger(collectCheckoutOrderRecords(raw), [
    'finalidade',
    'finalidade_id',
    'finalidadeId',
  ])
}

export function isCheckoutOrderCancelled(raw: Record<string, unknown>): boolean {
  if (
    parseBooleanLike(raw.cancelado)
    || parseBooleanLike(raw.cancelada)
    || parseBooleanLike(raw.cancelled)
    || parseBooleanLike(raw.canceled)
  ) {
    return true
  }

  const records = collectCheckoutOrderRecords(raw)
  const statusToken = records
    .flatMap((record) => [
      record.status_descricao,
      record.statusDescricao,
      record.situacao_descricao,
      record.descricao_status,
      record.status_label,
      record.statusLabel,
      record.situacao_label,
      record.status_texto,
      record.situacao_texto,
      record.status_nome,
      record.situacao_nome,
      record.status_fluxo,
      record.statusFluxo,
      record.status,
      record.situacao,
      record.state,
    ])
    .map(normalizeCheckoutComparableToken)
    .filter(Boolean)
    .join(' ')

  return (
    statusToken.includes('cancel')
    || statusToken.includes('recus')
    || statusToken.includes('rejeit')
  )
}

function isCheckoutOrderUsableForPrefill(raw: Record<string, unknown>): boolean {
  return !isCheckoutOrderCancelled(raw)
}

function hasCheckoutOrderPaymentStatusSignal(raw: Record<string, unknown>): boolean {
  const records = collectCheckoutOrderRecords(raw)
  const statusToken = records
    .flatMap((record) => [
      record.status_fluxo,
      record.statusFluxo,
      record.status_descricao,
      record.statusDescricao,
      record.situacao_descricao,
      record.situacao_texto,
      record.status_texto,
      record.status,
      record.situacao,
      record.state,
    ])
    .map(normalizeCheckoutComparableToken)
    .filter(Boolean)
    .join(' ')

  return (
    statusToken.includes('pagamento')
    || statusToken.includes('payment')
    || statusToken.includes('pago')
    || statusToken.includes('paid')
  )
}

function buildCheckoutLatestOrderFallback(raw: Record<string, unknown>): CheckoutLatestOrderFallback {
  const paymentSelection = resolveCheckoutOrderPaymentSelection(raw)

  return {
    carrierId: resolveCheckoutOrderCarrierId(raw),
    freightType: resolveCheckoutOrderFreightType(raw),
    address: resolveCheckoutOrderDeliveryAddress(raw),
    paymentSelection,
    contactName: resolveCheckoutOrderContactName(raw),
    contactPhone: resolveCheckoutOrderContactPhone(raw),
    finalidade: resolveCheckoutOrderFinalidade(raw),
    reachedPayment: Boolean(paymentSelection?.formaPagamentoId) || hasCheckoutOrderPaymentStatusSignal(raw),
  }
}

function hasCheckoutLatestOrderFallbackData(fallback: CheckoutLatestOrderFallback | null): fallback is CheckoutLatestOrderFallback {
  return Boolean(
    fallback
    && (
      fallback.carrierId
      || fallback.freightType
      || fallback.address
      || fallback.paymentSelection
      || fallback.contactName
      || fallback.contactPhone
      || fallback.finalidade
    )
  )
}

function isCheckoutLatestOrderFallbackComplete(fallback: CheckoutLatestOrderFallback): boolean {
  return Boolean(
    fallback.carrierId
    && fallback.freightType
    && fallback.address
    && fallback.paymentSelection
    && fallback.contactName
    && fallback.contactPhone
  )
}

function hasCheckoutLatestOrderReachedPaymentStep(fallback: CheckoutLatestOrderFallback | null): fallback is CheckoutLatestOrderFallback {
  return Boolean(fallback?.reachedPayment || fallback?.paymentSelection?.formaPagamentoId)
}

function mergeCheckoutOrderPaymentSelections(
  summarySelection: CheckoutLatestOrderFallback['paymentSelection'],
  detailSelection: CheckoutLatestOrderFallback['paymentSelection']
): CheckoutLatestOrderFallback['paymentSelection'] {
  if (!summarySelection) return detailSelection
  if (!detailSelection) return summarySelection

  return {
    formaPagamentoId: summarySelection.formaPagamentoId || detailSelection.formaPagamentoId,
    condicaoPagamentoId: summarySelection.condicaoPagamentoId ?? detailSelection.condicaoPagamentoId,
    condicaoPagamentoTexto: summarySelection.condicaoPagamentoTexto || detailSelection.condicaoPagamentoTexto,
  }
}

function mergeCheckoutLatestOrderFallbacks(
  summaryFallback: CheckoutLatestOrderFallback,
  detailFallback: CheckoutLatestOrderFallback | null
): CheckoutLatestOrderFallback {
  if (!detailFallback) return summaryFallback

  return {
    carrierId: summaryFallback.carrierId ?? detailFallback.carrierId,
    freightType: summaryFallback.freightType ?? detailFallback.freightType,
    address: summaryFallback.address ?? detailFallback.address,
    paymentSelection: mergeCheckoutOrderPaymentSelections(summaryFallback.paymentSelection, detailFallback.paymentSelection),
    contactName: summaryFallback.contactName || detailFallback.contactName,
    contactPhone: summaryFallback.contactPhone || detailFallback.contactPhone,
    finalidade: summaryFallback.finalidade ?? detailFallback.finalidade,
    reachedPayment: summaryFallback.reachedPayment || detailFallback.reachedPayment,
  }
}

function shouldFetchCheckoutLatestOrderDetail(summaryFallback: CheckoutLatestOrderFallback): boolean {
  return !isCheckoutLatestOrderFallbackComplete(summaryFallback)
}

function resolveCheckoutCustomerLatestOrderPaymentSelection(
  customer: CheckoutCustomerProfile
): { formaPagamentoId: number; condicaoPagamentoId: number } | null {
  const formaPagamentoId = normalizePositiveInteger(customer.ultimaCompraFormaPagamentoId)
  const condicaoPagamentoId = normalizePositiveInteger(customer.ultimaCompraCondicaoPagamentoId)

  return formaPagamentoId && condicaoPagamentoId
    ? { formaPagamentoId, condicaoPagamentoId }
    : null
}

function resolveCheckoutCustomerLatestOrderPaymentPreference(
  customer: CheckoutCustomerProfile
): { formaPagamentoId: number; condicaoPagamentoId: number | null; condicaoPagamentoTexto: string } | null {
  const formaPagamentoId = normalizePositiveInteger(customer.ultimaCompraFormaPagamentoId)
  if (!formaPagamentoId) return null

  return {
    formaPagamentoId,
    condicaoPagamentoId: normalizePositiveInteger(customer.ultimaCompraCondicaoPagamentoId),
    condicaoPagamentoTexto: normalizeCheckoutText(customer.ultimaCompraCondicaoPagamentoTexto),
  }
}

function normalizeCheckoutContactIdentityToken(value: unknown): string {
  return normalizeCheckoutComparableToken(value)
}

function normalizeCheckoutContactPhoneIdentity(value: unknown): string {
  const digits = normalizePhoneToStoredDigits(normalizeCheckoutText(value))
  return digits.startsWith('55') && digits.length > 11 ? digits.slice(2) : digits
}

function isCheckoutCustomerProfileContactName(
  customer: CheckoutCustomerProfile,
  contactName: unknown
): boolean {
  const normalizedContactName = normalizeCheckoutContactIdentityToken(contactName)
  if (!normalizedContactName) return false

  return [customer.name, customer.corporateName]
    .some((candidate) => normalizeCheckoutContactIdentityToken(candidate) === normalizedContactName)
}

function isCheckoutCustomerProfileContactPhone(
  customer: CheckoutCustomerProfile,
  contactPhone: unknown
): boolean {
  const normalizedContactPhone = normalizeCheckoutContactPhoneIdentity(contactPhone)
  if (!normalizedContactPhone) return false

  return normalizeCheckoutContactPhoneIdentity(customer.phone) === normalizedContactPhone
}

function resolveCheckoutOrderPaymentSelection(
  raw: Record<string, unknown>
): { formaPagamentoId: number; condicaoPagamentoId: number | null; condicaoPagamentoTexto: string } | null {
  const records = collectCheckoutOrderRecords(raw)

  const resolveCheckoutOrderPaymentMethodId = (record: Record<string, unknown>) => {
    const directCandidates = [
      record.forma_pagamento_id,
      record.formaPagamentoId,
      record.payment_method_id,
      record.paymentMethodId,
      record.metodo_pagamento_id,
      record.metodoPagamentoId,
    ]
    const nestedCandidates = [
      isPlainRecord(record.forma_pagamento) ? record.forma_pagamento : null,
      isPlainRecord(record.formaPagamento) ? record.formaPagamento : null,
      isPlainRecord(record.payment_method) ? record.payment_method : null,
      isPlainRecord(record.paymentMethod) ? record.paymentMethod : null,
      isPlainRecord(record.metodo_pagamento) ? record.metodo_pagamento : null,
      isPlainRecord(record.metodoPagamento) ? record.metodoPagamento : null,
    ]

    for (const candidate of directCandidates) {
      const resolved = normalizePositiveInteger(candidate)
      if (resolved) return resolved
    }

    for (const nestedRecord of nestedCandidates) {
      if (!nestedRecord) continue

      for (const candidate of [
        nestedRecord.forma_pagamento_id,
        nestedRecord.formaPagamentoId,
        nestedRecord.payment_method_id,
        nestedRecord.paymentMethodId,
        nestedRecord.metodo_pagamento_id,
        nestedRecord.metodoPagamentoId,
        nestedRecord.id,
      ]) {
        const resolved = normalizePositiveInteger(candidate)
        if (resolved) return resolved
      }
    }

    return null
  }

  const resolveCheckoutOrderPaymentConditionId = (record: Record<string, unknown>) => {
    const directCandidates = [
      record.condicao_pagamento_id,
      record.condicaoPagamentoId,
      record.payment_condition_id,
      record.paymentConditionId,
      record.condicao_id,
      record.condicaoId,
    ]
    const nestedCandidates = [
      isPlainRecord(record.condicao_pagamento) ? record.condicao_pagamento : null,
      isPlainRecord(record.condicaoPagamento) ? record.condicaoPagamento : null,
      isPlainRecord(record.payment_condition) ? record.payment_condition : null,
      isPlainRecord(record.paymentCondition) ? record.paymentCondition : null,
      isPlainRecord(record.condicao) ? record.condicao : null,
    ]

    for (const candidate of directCandidates) {
      const resolved = normalizePositiveInteger(candidate)
      if (resolved) return resolved
    }

    for (const nestedRecord of nestedCandidates) {
      if (!nestedRecord) continue

      for (const candidate of [
        nestedRecord.condicao_pagamento_id,
        nestedRecord.condicaoPagamentoId,
        nestedRecord.payment_condition_id,
        nestedRecord.paymentConditionId,
        nestedRecord.condicao_id,
        nestedRecord.condicaoId,
        nestedRecord.id,
      ]) {
        const resolved = normalizePositiveInteger(candidate)
        if (resolved) return resolved
      }
    }

    return null
  }

  const resolveCheckoutOrderPaymentConditionText = (record: Record<string, unknown>) => {
    const directCandidates = [
      record.condicao_pagamento,
      record.condicaoPagamento,
      record.payment_condition,
      record.paymentCondition,
      record.condicao,
    ]
    const nestedCandidates = [
      isPlainRecord(record.condicao_pagamento) ? record.condicao_pagamento : null,
      isPlainRecord(record.condicaoPagamento) ? record.condicaoPagamento : null,
      isPlainRecord(record.payment_condition) ? record.payment_condition : null,
      isPlainRecord(record.paymentCondition) ? record.paymentCondition : null,
      isPlainRecord(record.condicao) ? record.condicao : null,
    ]

    for (const candidate of directCandidates) {
      const resolved = normalizeCheckoutText(candidate)
      if (resolved) return resolved
    }

    for (const nestedRecord of nestedCandidates) {
      if (!nestedRecord) continue

      for (const candidate of [
        nestedRecord.condicao_pagamento,
        nestedRecord.condicaoPagamento,
        nestedRecord.payment_condition,
        nestedRecord.paymentCondition,
        nestedRecord.condicao,
        nestedRecord.descricao,
        nestedRecord.description,
      ]) {
        const resolved = normalizeCheckoutText(candidate)
        if (resolved) return resolved
      }
    }

    return ''
  }

  let formaPagamentoId: number | null = null
  let condicaoPagamentoId: number | null = null
  let condicaoPagamentoTexto = ''

  for (const record of records) {
    formaPagamentoId ??= resolveCheckoutOrderPaymentMethodId(record)
    condicaoPagamentoId ??= resolveCheckoutOrderPaymentConditionId(record)
    condicaoPagamentoTexto ||= resolveCheckoutOrderPaymentConditionText(record)

    if (formaPagamentoId && (condicaoPagamentoId || condicaoPagamentoTexto)) {
      return {
        formaPagamentoId,
        condicaoPagamentoId,
        condicaoPagamentoTexto,
      }
    }
  }

  return formaPagamentoId
    ? {
        formaPagamentoId,
        condicaoPagamentoId,
        condicaoPagamentoTexto,
      }
    : null
}

/**
 * Considera um pedido "finalizado" (compra concluída) quando já tem nota fiscal,
 * já possui código de rastreio ou está em status numérico >= 3 (Faturado /
 * Entregue). Espelha a semântica de `account-utils.resolveOrderStatusLabel`, mas
 * sem importar a camada de componentes para dentro de `lib/`.
 */
export function isCheckoutOrderFinished(raw: Record<string, unknown>): boolean {
  const hasInvoice = Boolean(
    raw.chave
    || raw.nota_fiscal
    || raw.notaFiscal
    || (isPlainRecord(raw.documentos)
      && (
        (isPlainRecord(raw.documentos.danfe) && raw.documentos.danfe.disponivel)
        || (isPlainRecord(raw.documentos.xml) && raw.documentos.xml.disponivel)
      ))
  )
  if (hasInvoice) return true

  const hasTrackingCode = Boolean(
    String(
      raw.correios_numerorastreio
      ?? raw.codigo_rastreio
      ?? raw.tracking_code
      ?? ''
    ).trim()
  )
  if (hasTrackingCode) return true

  const statusCandidate = raw.status_numero ?? raw.statusNumero ?? raw.status ?? raw.situacao
  const numericStatus = Number.parseInt(String(statusCandidate ?? ''), 10)
  if (Number.isFinite(numericStatus) && numericStatus >= 3) return true

  return false
}

async function fetchCheckoutLatestOrderFallback(
  entidadeId: number
): Promise<CheckoutLatestOrderFallback | null> {
  try {
    const authenticatedEntidadeId = resolveAuthenticatedEntidadeIdOrThrow('Preferências da última compra')
    const params = new URLSearchParams()
    const lojaId = getLojaIdValue()
    if (lojaId) params.set('loja_id', String(lojaId))
    if (entidadeId !== authenticatedEntidadeId) {
      params.set('cliente_entidade_id', String(entidadeId))
    }
    const query = params.toString()
    const response = await fetchApi<unknown>(
      `/loja-virtual/v1/compradores/${authenticatedEntidadeId}/checkout/preferencias-ultima-compra${query ? `?${query}` : ''}`,
      { suppressBuyerSessionExpiry: true }
    )
    if (!isPlainRecord(response) || !isPlainRecord(response.preferencia)) return null

    const fallback = buildCheckoutLatestOrderFallback(response.preferencia)
    return hasCheckoutLatestOrderFallbackData(fallback)
      ? { ...fallback, reachedPayment: true }
      : null
  } catch {
    return null
  }
}

async function enrichCheckoutCustomerProfileWithLatestOrderFallback(
  customer: CheckoutCustomerProfile,
  entidadeId: number,
  prefetchedLatestOrderFallback?: CheckoutLatestOrderFallback | null
): Promise<CheckoutCustomerProfile> {
  const [enrichedProfile, latestOrderFallback] = await Promise.all([
    enrichCheckoutCustomerProfileWithLoginWhatsapp(customer),
    prefetchedLatestOrderFallback === undefined
      ? fetchCheckoutLatestOrderFallback(entidadeId)
      : Promise.resolve(prefetchedLatestOrderFallback),
  ])

  if (!latestOrderFallback) return enrichedProfile

  const latestOrderReachedPayment = hasCheckoutLatestOrderReachedPaymentStep(latestOrderFallback)

  return {
    ...enrichedProfile,
    // A venda concluída é a fonte principal. O cadastro só completa campos que
    // não existiam naquele pedido.
    temUltimaCompraFinalizada:
      enrichedProfile.temUltimaCompraFinalizada || latestOrderReachedPayment,
    ultimaCompraTransportadoraId:
      latestOrderReachedPayment
        ? latestOrderFallback.carrierId
          ?? enrichedProfile.ultimaCompraTransportadoraId
          ?? null
        : enrichedProfile.ultimaCompraTransportadoraId,
    ultimaCompraModalidadeFrete:
      latestOrderReachedPayment
        ? latestOrderFallback.freightType
          ?? enrichedProfile.ultimaCompraModalidadeFrete
          ?? null
        : enrichedProfile.ultimaCompraModalidadeFrete,
    ultimaCompraEnderecoEntrega:
      latestOrderReachedPayment
        ? latestOrderFallback.address
          ?? enrichedProfile.ultimaCompraEnderecoEntrega
          ?? null
        : enrichedProfile.ultimaCompraEnderecoEntrega,
    ultimaCompraContatoNome:
      latestOrderFallback.contactName
      || enrichedProfile.ultimaCompraContatoNome
      || '',
    ultimaCompraContatoTelefone:
      latestOrderFallback.contactPhone
      || enrichedProfile.ultimaCompraContatoTelefone
      || '',
    transportadoraPreferencialId:
      latestOrderReachedPayment
        ? latestOrderFallback.carrierId
          ?? enrichedProfile.transportadoraPreferencialId
          ?? null
        : enrichedProfile.transportadoraPreferencialId,
    ultimaCompraFormaPagamentoId:
      (latestOrderReachedPayment ? latestOrderFallback.paymentSelection?.formaPagamentoId : null)
      ?? enrichedProfile.ultimaCompraFormaPagamentoId
      ?? null,
    ultimaCompraCondicaoPagamentoId:
      (latestOrderReachedPayment ? latestOrderFallback.paymentSelection?.condicaoPagamentoId : null)
      ?? enrichedProfile.ultimaCompraCondicaoPagamentoId
      ?? null,
    ultimaCompraCondicaoPagamentoTexto:
      (latestOrderReachedPayment ? latestOrderFallback.paymentSelection?.condicaoPagamentoTexto : '')
      || enrichedProfile.ultimaCompraCondicaoPagamentoTexto
      || '',
    ultimaCompraFinalidade:
      latestOrderReachedPayment
        ? latestOrderFallback.finalidade
          ?? enrichedProfile.ultimaCompraFinalidade
          ?? null
        : enrichedProfile.ultimaCompraFinalidade,
  }
}

export function resolveCheckoutRedespachoInfo(
  raw:
    | CheckoutRedespachoInfo
    | {
        transportadoraRedespachoAtiva?: boolean
        transportadoraRedespachoId?: number | null
        transportadoraRedespachoNome?: string
        transportadoraRedespachoEnderecoResumo?: string
      }
    | {
        ativo?: number
        transportadora_id?: number | null
        transportadora_nome?: string | null
        endereco_resumo?: string | null
      }
    | null
    | undefined
): CheckoutRedespachoInfo | null {
  if (!raw || typeof raw !== 'object') return null

  if ('transportadoraId' in raw && 'transportadoraNome' in raw) {
    const transportadoraId = Math.trunc(normalizeFreteNumber(raw.transportadoraId) ?? 0)
    if (transportadoraId <= 0) return null

    return {
      transportadoraId,
      transportadoraNome: normalizeCheckoutText(raw.transportadoraNome) || `Transportadora ${transportadoraId}`,
      enderecoResumo: normalizeCheckoutText(raw.enderecoResumo),
    }
  }

  const isAtivo = 'transportadoraRedespachoAtiva' in raw
    ? Boolean(raw.transportadoraRedespachoAtiva)
    : Number((raw as { ativo?: number }).ativo ?? 0) === 1
  const transportadoraId = Math.trunc(
    normalizeFreteNumber(
      'transportadoraRedespachoId' in raw
        ? raw.transportadoraRedespachoId
        : (raw as { transportadora_id?: number | null }).transportadora_id
    ) ?? 0
  )

  if (!isAtivo || transportadoraId <= 0) return null

  const transportadoraNome = normalizeCheckoutText(
    'transportadoraRedespachoNome' in raw
      ? raw.transportadoraRedespachoNome
      : (raw as { transportadora_nome?: string | null }).transportadora_nome
  )
  const enderecoResumo = normalizeCheckoutText(
    'transportadoraRedespachoEnderecoResumo' in raw
      ? raw.transportadoraRedespachoEnderecoResumo
      : (raw as { endereco_resumo?: string | null }).endereco_resumo
  )

  return {
    transportadoraId,
    transportadoraNome: transportadoraNome || `Transportadora ${transportadoraId}`,
    enderecoResumo,
  }
}

export function resolveCheckoutCustomerRedespachoInfoByChoice(
  customer: CheckoutCustomerProfile | null | undefined,
  usarRedespacho: boolean | number | null | undefined,
  transportadoraRedespachoId?: number | null
): CheckoutRedespachoInfo | null {
  if (!customer) return null

  const shouldUse = usarRedespacho === true || usarRedespacho === 1
  if (!shouldUse) return null

  const selectedTransportadoraId = Math.trunc(
    normalizeFreteNumber(
      transportadoraRedespachoId ?? customer.transportadoraRedespachoId
    ) ?? 0
  )
  if (selectedTransportadoraId <= 0) return null

  const selectedTransportadora = customer.transportadorasRedespachoVinculadas.find(
    (item) => item.transportadoraId === selectedTransportadoraId
  )

  return {
    transportadoraId: selectedTransportadoraId,
    transportadoraNome:
      selectedTransportadora?.nome
      || customer.transportadoraRedespachoNome
      || `Transportadora ${selectedTransportadoraId}`,
    enderecoResumo:
      selectedTransportadora?.enderecoResumo
      || customer.transportadoraRedespachoEnderecoResumo,
  }
}

function buildCheckoutPaymentOption(
  forma: NonNullable<LojaVirtualCheckoutPagamentosResponse['formas']>[number],
  condicao: NonNullable<NonNullable<LojaVirtualCheckoutPagamentosResponse['formas']>[number]['condicoes']>[number]
): CheckoutPaymentOption | null {
  const formaPagamentoId = Math.trunc(normalizeFreteNumber(forma.forma_pagamento_id) ?? 0)
  const condicaoPagamentoId = Math.trunc(normalizeFreteNumber(condicao.condicao_pagamento_id) ?? 0)
  if (formaPagamentoId <= 0 || condicaoPagamentoId <= 0) return null

  const label = normalizeCheckoutText(forma.descricao) || `Pagamento ${formaPagamentoId}`
  const descriptionParts = [
    normalizeCheckoutText(condicao.descricao),
    normalizeCheckoutText(condicao.obs),
  ].filter(Boolean)

  return {
    id: `${formaPagamentoId}:${condicaoPagamentoId}`,
    formaPagamentoId,
    condicaoPagamentoId,
    condicaoPagamentoTexto: normalizeCheckoutText(condicao.condicao ?? condicao.descricao),
    label,
    description: descriptionParts.join(' '),
    detail: normalizeCheckoutText(forma.detalhe),
    badge: condicao.considera_avista === 1 ? 'À vista' : '',
  }
}

function buildCheckoutShippingOption(
  raw: NonNullable<LojaVirtualCheckoutFreteResponse['transportadoras']>[number],
  fallbackRedespacho: CheckoutRedespachoInfo | null = null
): CheckoutShippingOption | null {
  const rawRecord = raw as Record<string, unknown>
  const nestedTransportadora = isPlainRecord(rawRecord.transportadora)
    ? rawRecord.transportadora
    : null
  const transportadoraId = Math.trunc(resolveFirstPositiveFreteNumber(
    raw.transportadora_id,
    rawRecord.transportadoraId,
    rawRecord.shipping_carrier_id,
    nestedTransportadora?.id,
    nestedTransportadora?.transportadora_id
  ) ?? 0)
  if (transportadoraId <= 0) return null

  const cotacaoId = Math.trunc(resolveFirstPositiveFreteNumber(raw.cotacao_id, rawRecord.cotacaoId) ?? 0)
  const quoteNumber = resolveFirstCheckoutText(rawRecord.numero_cotacao, rawRecord.numeroCotacao)
  const price = resolveFirstFreteNumber(raw.valor_frete, rawRecord.valorFrete) ?? 0
  const prazoMin = Math.trunc(resolveFirstPositiveFreteNumber(raw.prazo_dias_min, rawRecord.prazoDiasMin) ?? 0)
  const prazoMax = Math.trunc(resolveFirstPositiveFreteNumber(raw.prazo_dias_max, rawRecord.prazoDiasMax) ?? 0)
  const mediaAvaliacao = Math.min(5, Math.max(0, normalizeFreteNumber(raw.media) ?? 0))
  const totalAvaliacoes = Math.max(0, Math.trunc(normalizeFreteNumber(raw.avaliacoes) ?? 0))
  const eta = prazoMin > 0 && prazoMax > 0
    ? prazoMin === prazoMax
      ? `${prazoMin} ${prazoMin === 1 ? 'dia útil' : 'dias úteis'}`
      : `${prazoMin}-${prazoMax} dias úteis`
    : prazoMax > 0
      ? `${prazoMax} ${prazoMax === 1 ? 'dia útil' : 'dias úteis'}`
      : 'Prazo sob consulta'

  const label = resolveFirstCheckoutText(
    raw.razao,
    rawRecord.nome,
    nestedTransportadora?.razao,
    nestedTransportadora?.nome
  ) || `Transportadora ${transportadoraId}`
  const tipoFrete = raw.tipo_frete === 'cif' || rawRecord.tipoFrete === 'cif' ? 'cif' : 'fob'
  const retiraNoLocal = [raw.retira_no_local, rawRecord.retiraNoLocal, rawRecord.pickup].some((value) => parseBooleanLike(value))
  const descriptionTokens = [
    retiraNoLocal ? 'Retirada no local' : '',
  ].filter(Boolean)

  return {
    id: `${transportadoraId}:${cotacaoId > 0 ? cotacaoId : 0}:${tipoFrete}`,
    transportadoraId,
    cotacaoId,
    quoteNumber: quoteNumber || undefined,
    tipoFrete,
    label,
    description: descriptionTokens.join(' • '),
    price: Math.max(0, price),
    originalPrice: Math.max(0, resolveFirstFreteNumber(raw.valor_original, rawRecord.valorOriginal) ?? price),
    eta,
    mediaAvaliacao,
    totalAvaliacoes,
    calculado: [raw.calculado, rawRecord.calculated].some((value) => parseBooleanLike(value)),
    retiraNoLocal,
    alertaTexto: resolveFirstCheckoutText(raw.alerta_texto, rawRecord.alertaTexto),
    redespacho: tipoFrete === 'cif' ? resolveCheckoutRedespachoInfo(raw.redespacho) ?? fallbackRedespacho : null,
  }
}

function buildProductDisplayName(
  baseName: string,
  details: {
    serie?: string | null
    modelo?: string | null
    tamanho?: string | null
  }
): string {
  const name = baseName.trim()

  const tokens: string[] = []
  const serie = normalizeDescriptorValue(details.serie)
  const modelo = normalizeDescriptorValue(details.modelo)
  const tamanho = normalizeDescriptorValue(details.tamanho)

  if (shouldAppendProductDescriptor(name, 's[eé]rie', serie)) {
    tokens.push(`Serie ${serie}`)
  }
  if (shouldAppendProductDescriptor(name, 'modelo', modelo)) {
    tokens.push(`Modelo ${modelo}`)
  }
  if (shouldAppendProductDescriptor(name, 'tamanho', tamanho)) {
    tokens.push(`Tamanho ${tamanho}`)
  }

  if (tokens.length === 0) return name
  return `${name} - ${tokens.join(' - ')}`
}

function shouldAppendProductDescriptor(
  name: string,
  labelPattern: string,
  descriptorValue?: string | null,
): boolean {
  const normalizedDescriptorValue = normalizeDescriptorValue(descriptorValue)
  if (!normalizedDescriptorValue) return false

  const labeledPattern = new RegExp(
    `(?:^|[\\s\\-(/])${labelPattern}\\s*:?\\s*${escapeProductNameRegExp(normalizedDescriptorValue)}(?=$|[\\s.,;:)\\-])`,
    'i',
  )
  if (labeledPattern.test(name)) return false

  const normalizedValue = normalizedDescriptorValue.toLocaleLowerCase('pt-BR')
  if (normalizedValue.length <= 2 || /^\d+$/.test(normalizedValue)) return true

  return !name.toLocaleLowerCase('pt-BR').includes(normalizedValue)
}

function escapeProductNameRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

function normalizeProductBaseNameSpacing(value: string): string {
  return value
    .replace(/\s*-\s*/g, ' - ')
    .replace(/\s{2,}/g, ' ')
    .replace(/^-\s*|\s*-\s*$/g, '')
    .trim()
}

function stripTrailingProductDescriptor(
  name: string,
  labelPattern: string,
  descriptorValue?: string | null,
): string {
  const normalizedName = normalizeProductBaseNameSpacing(normalizeCheckoutText(name) || name)
  const normalizedDescriptorValue = normalizeDescriptorValue(descriptorValue)
  if (!normalizedName || !normalizedDescriptorValue) return normalizedName

  const suffixPattern = new RegExp(
    `(?:\\s*-\\s*|\\s+)${labelPattern}\\s+${escapeProductNameRegExp(normalizedDescriptorValue)}\\s*$`,
    'i',
  )

  return normalizeProductBaseNameSpacing(normalizedName.replace(suffixPattern, ' '))
}

function buildProductBaseName(
  baseName: string,
  details: {
    serie?: string | null
    modelo?: string | null
    tamanho?: string | null
  }
): string {
  const normalizedName = normalizeProductBaseNameSpacing(normalizeCheckoutText(baseName) || baseName)
  if (!normalizedName) return ''

  let cleanedName = normalizedName
  let previousName = ''

  while (cleanedName && cleanedName !== previousName) {
    previousName = cleanedName
    cleanedName = stripTrailingProductDescriptor(cleanedName, 's[eé]rie', details.serie)
    cleanedName = stripTrailingProductDescriptor(cleanedName, 'modelo', details.modelo)
    cleanedName = stripTrailingProductDescriptor(cleanedName, 'tamanho', details.tamanho)
  }

  return cleanedName || normalizedName
}

function resolveProductBaseName(
  baseName: string,
  details: {
    serie?: string | null
    modelo?: string | null
    tamanho?: string | null
  }
): string {
  return normalizeProductName(buildProductBaseName(baseName, details))
}

function resolveParentProductBaseName(
  product?: Pick<LojaVirtualProdutoDetalhe, 'nome_exibicao' | 'nome' | 'serie' | 'modelo' | 'tamanho'> | null,
): string | null {
  if (!product) return null

  const rawName = product.nome_exibicao?.trim() || product.nome?.trim() || ''
  if (!rawName) return null

  return resolveProductBaseName(rawName, {
    serie: product.serie,
    modelo: product.modelo,
    tamanho: product.tamanho,
  }) || null
}

function normalizeProductRatingAverage(value: unknown): number | undefined {
  const numeric = normalizeFreteNumber(value)
  if (typeof numeric !== 'number' || !Number.isFinite(numeric) || numeric <= 0) {
    return undefined
  }

  return Number(Math.max(0, Math.min(5, numeric)).toFixed(1))
}

function normalizeProductReviewsCount(value: unknown): number | undefined {
  const numeric = normalizeFreteNumber(value)
  if (typeof numeric !== 'number' || !Number.isFinite(numeric) || numeric <= 0) {
    return undefined
  }

  return Math.max(0, Math.trunc(numeric))
}

function normalizeProductNcmValue(value: unknown): string | undefined {
  if (value === null || value === undefined) return undefined

  if (typeof value === 'object') {
    const record = value as Record<string, unknown>
    return normalizeProductNcmValue(
      record.ncm
      ?? record.codigo
      ?? record.code
      ?? record.value
      ?? record.id
    )
  }

  const rawValue = String(value).trim()
  if (!rawValue) return undefined

  const digitValue = rawValue.replace(/\D+/g, '')
  if (digitValue.length >= 8) return digitValue.slice(0, 8)

  return rawValue
}

function resolveLojaVirtualProductNcm(source: unknown): string | undefined {
  if (!source || typeof source !== 'object') return undefined

  const record = source as Record<string, unknown>
  const candidates = [
    record.ncm,
    record.NCM,
    record.codigo_ncm,
    record.codigoNcm,
    record.produto_ncm,
    record.produtoNcm,
    record.classificacao_fiscal_ncm,
    record.classificacaoFiscalNcm,
    record.hs_code,
    record.hsCode,
    record.classificacao_fiscal,
    record.classificacaoFiscal,
    (record.classificacao_fiscal as Record<string, unknown> | undefined)?.ncm,
    (record.classificacao_fiscal as Record<string, unknown> | undefined)?.codigo,
    (record.classificacaoFiscal as Record<string, unknown> | undefined)?.ncm,
    (record.classificacaoFiscal as Record<string, unknown> | undefined)?.codigo,
  ]

  for (const candidate of candidates) {
    const normalized = normalizeProductNcmValue(candidate)
    if (normalized) return normalized
  }

  return undefined
}

function mapLojaVirtualProductToProduct(
  item: LojaVirtualProdutoItem,
  options: {
    parentProductId?: string | null
    parentProductName?: string | null
    usarSeletorVariacao?: boolean | null
    variationSelectorConfig?: LojaVirtualVariacaoConfiguracao[] | null
  } = {}
): Product {
  const id = String(item.produto_id)
  const normalizedParentProductId = options.parentProductId?.trim()
  const normalizedParentProductName = options.parentProductName?.trim()
  const parentProductIdFromItem =
    typeof item.produto_principal_variacao === 'number'
    && Number.isFinite(item.produto_principal_variacao)
    && item.produto_principal_variacao > 0
      ? String(Math.trunc(item.produto_principal_variacao))
      : undefined
  const resolvedParentProductId = normalizedParentProductId || parentProductIdFromItem
  const parentProductId = resolvedParentProductId && resolvedParentProductId !== id
    ? resolvedParentProductId
    : undefined
  const parentProductName = parentProductId && normalizedParentProductName
    ? normalizedParentProductName
    : undefined
  const code = item.referencia?.trim() || id
  const images = buildPreferredProductImageList({
    image: item.imagem,
    imageMid: item.imagem_mid,
    imageHigh: item.imagem_high,
    photos: item.fotos,
  })
  const image = images[0] || ''
  const normalizedPrice = normalizePriceValue(item.preco)
  const usesPlaceholderPrice = isSimplifiedStorefrontPlaceholderPrice(normalizedPrice)
  const price = usesPlaceholderPrice ? undefined : normalizedPrice
  const wholesalePrice = normalizePriceValue(item.preco_atacado ?? item.precoAtacado)
  const cnpjIePrice = normalizePriceValue(item.preco_cnpj_ie)
  const distributionPrice = normalizeDistributionPriceValue(item)
  const ratingAverage = normalizeProductRatingAverage(item.nota)
  const reviewsCount = normalizeProductReviewsCount(item.avaliacoes)
  const specs = buildLojaVirtualProductListSpecs(item)
  const baseName = item.nome_curto?.trim() || item.nome?.trim() || `Produto ${id}`
  const name = normalizeProductName(
    buildProductDisplayName(baseName, {
      serie: item.serie,
      modelo: item.modelo,
      tamanho: item.tamanho,
    })
  )
  const serie = normalizeDescriptorValue(item.serie) || undefined
  const tamanho = normalizeDescriptorValue(item.tamanho) || undefined
  const modelo = normalizeDescriptorValue(item.modelo) || undefined
  const variationAttributes = parseVariationAttributes(item.caracteristicas ?? item.atributos)
  const variationSelectorConfig = parseVariationSelectorConfig(options.variationSelectorConfig)
  const variationLabel = variationAttributes.find((attribute) => attribute.value)?.value
  const variationAdditionalInfo = normalizeCheckoutText(item.informacoes_adicionais)
  const manufacturerBrandId =
    typeof item.marca_fabricante_id === 'number' && Number.isFinite(item.marca_fabricante_id) && item.marca_fabricante_id > 0
      ? String(Math.trunc(item.marca_fabricante_id))
      : undefined
  const classId =
    typeof item.classe_id === 'number' && Number.isFinite(item.classe_id) && item.classe_id > 0
      ? String(Math.trunc(item.classe_id))
      : undefined
  const manufacturerBrandName = item.marca_fabricante_nome?.trim() || undefined
  const manufacturerBrandLogoUrl = normalizeImageUrl(item.marca_fabricante_logo_url) || undefined
  const normalizedBaseName = resolveProductBaseName(baseName, {
    serie: item.serie,
    modelo: item.modelo,
    tamanho: item.tamanho,
  })
  const navigationTargetProductId = normalizeLojaVirtualNumericId(
    item.navigation_target_produto_id ?? item.navigationTargetProductId
  ) ?? undefined
  const navigationTargetProductName = normalizeCheckoutText(
    item.navigation_target_produto_nome ?? item.navigationTargetProductName
  ) || undefined
  const navigationParentContextId = normalizeLojaVirtualNumericId(
    item.navigation_parent_context_id ?? item.navigationParentContextId
  ) ?? undefined
  const stock = normalizePriceValue(item.estoque ?? item.estoque_atual ?? item.stock ?? item.stockQuantity)
  const boxOptions = parseStorefrontProductBoxOptions(item)

  return {
    id,
    cartBaseProductId: id,
    packagingMode: 'unit',
    packagingQuantity: 1,
    packagingLabel: boxOptions.length > 0 ? 'UNITARIO' : undefined,
    unitPrice: price,
    boxOptions: boxOptions.length > 0 ? boxOptions : undefined,
    parentProductId,
    navigationTargetProductId,
    navigationTargetProductName,
    navigationParentContextId,
    code,
    manufacturerCode: code,
    ncm: resolveLojaVirtualProductNcm(item),
    order: resolveProductOrderValue(item),
    name,
    isNameLocalized: item.nome_localizado === true || Number(item.nome_localizado ?? 0) === 1,
    baseName: normalizedBaseName,
    serie,
    tamanho,
    modelo,
    variationLabel,
    variationAdditionalInfo: variationAdditionalInfo || undefined,
    variationAttributes,
    variationSelectorConfig: variationSelectorConfig.length > 0 ? variationSelectorConfig : undefined,
    brand: item.marca?.trim() || manufacturerBrandName || 'Sem marca',
    manufacturerBrandId,
    manufacturerBrandName,
    manufacturerBrandLogoUrl: manufacturerBrandLogoUrl ?? null,
    category: item.produto_grupo_familia_nome?.trim() || item.produto_grupo_nome?.trim() || 'Produtos Industriais',
    subcategory: item.produto_grupo_nome?.trim() || '',
    classId,
    image,
    images,
    specs,
    inStock: typeof stock === 'number' ? stock > 0 : true,
    stockQuantity: typeof stock === 'number' ? stock : null,
    priceVisible: [price, wholesalePrice, cnpjIePrice, distributionPrice].some((value) => typeof value === 'number' && value > 0),
    needsQuote: ![price, wholesalePrice, cnpjIePrice, distributionPrice].some((value) => typeof value === 'number' && value > 0),
    pricingPending: Number(item.preco_pendente ?? 0) === 1,
    groupId: item.produto_grupo_id ? String(item.produto_grupo_id) : undefined,
    hasVariations: Boolean(item.possui_variacoes),
    showVariationsInSearch: Number(item.exibir_variacoes_busca ?? 0) === 1,
    hasForm: inferHasProductFormFlag(item) ?? (usesPlaceholderPrice ? true : undefined),
    usarSeletorVariacao: boxOptions.length > 0
      ? true
      : (options.usarSeletorVariacao ?? (Number(item.usar_seletor_variacao ?? 0) === 1)),
    parentProductName,
    price,
    originalPrice: price,
    discountedPrice: price,
    wholesalePrice,
    cnpjIePrice,
    distributionPrice,
    ratingAverage,
    reviewsCount,
  }
}

function mapProductCourses(value: unknown): ProductCourse[] {
  if (!Array.isArray(value)) return []

  const courses: ProductCourse[] = []

  value.forEach((item) => {
    if (!item || typeof item !== 'object') return

    const record = item as LojaVirtualProductCourse
    const externalId = String(record.curso_externo_id ?? record.course_id ?? record.id ?? '').trim()
    const rawTitle = String(record.titulo ?? record.title ?? record.curso_descricao ?? '').trim()
    if (!rawTitle) return

    const slug = String(record.slug ?? record.curso_slug ?? '').trim() || undefined
    const description = String(record.resumo ?? record.summary ?? '').trim() || undefined
    const duration = String(record.cargahoraria ?? record.duration ?? '').trim() || undefined
    const category = String(record.categoria ?? record.category ?? '').trim() || undefined
    const instructorName = String(record.instrutor_nome ?? record.instructor_name ?? '').trim() || undefined
    const universityUrl = String(record.universidade_url ?? record.university_url ?? '').trim() || undefined
    const thumbnail = normalizeImageUrl(record.thumbnail) || null
    const legacyCourseId = Number(record.curso_legado_id ?? record.legacy_course_id ?? record.curso_id ?? 0)

    courses.push({
      id: externalId || rawTitle,
      externalId: externalId || undefined,
      legacyId:
        Number.isFinite(legacyCourseId) && legacyCourseId > 0
          ? String(Math.trunc(legacyCourseId))
          : undefined,
      title: rawTitle,
      slug,
      description,
      duration,
      thumbnail,
      category,
      instructorName,
      universityUrl,
    })
  })

  return courses
}

function mapLojaVirtualProductDetailToProduct(detail: LojaVirtualProdutoDetalhe): Product {
  const id = String(detail.id)
  const parentProductIdFromDetail =
    typeof detail.produto_principal_variacao === 'number'
    && Number.isFinite(detail.produto_principal_variacao)
    && detail.produto_principal_variacao > 0
      ? String(Math.trunc(detail.produto_principal_variacao))
      : undefined
  const parentProductId = parentProductIdFromDetail && parentProductIdFromDetail !== id
    ? parentProductIdFromDetail
    : undefined
  const code = detail.referencia?.trim() || id
  const baseName = detail.nome_exibicao?.trim() || detail.nome?.trim() || `Produto ${id}`
  const name = normalizeProductName(
    buildProductDisplayName(baseName, {
      serie: detail.serie,
      modelo: detail.modelo,
      tamanho: detail.tamanho,
    })
  )
  const serie = normalizeDescriptorValue(detail.serie) || undefined
  const tamanho = normalizeDescriptorValue(detail.tamanho) || undefined
  const modelo = normalizeDescriptorValue(detail.modelo) || undefined

  const mapDetailImages = (
    photos?: LojaVirtualProdutoDetalheFoto[] | null,
    fallbackImageRaw?: string | null
  ) => {
    return buildPreferredProductImageList({
      image: fallbackImageRaw,
      photos,
    })
  }

  const mapDetailVideoAssets = (videos?: LojaVirtualProdutoDetalheVideo[] | null) => normalizeVideoAssets(videos)

  const images = mapDetailImages(detail.fotos, detail.imagem)
  const videoAssets = mapDetailVideoAssets(detail.videos)
  const videos = videoAssets.map((asset) => asset.url)
  const courses = mapProductCourses(detail.cursos ?? detail.courses)

  const variationPrices = Array.isArray(detail.variacoes)
    ? detail.variacoes
        .map((variation) => normalizePriceValue(variation.preco))
        .filter((value): value is number => typeof value === 'number' && value > 0)
    : []
  const variationWholesalePrices = Array.isArray(detail.variacoes)
    ? detail.variacoes
        .map((variation) => normalizePriceValue(variation.preco_atacado ?? variation.precoAtacado))
        .filter((value): value is number => typeof value === 'number' && value > 0)
    : []
  const variationCnpjIePrices = Array.isArray(detail.variacoes)
    ? detail.variacoes
        .map((variation) => normalizePriceValue(variation.preco_cnpj_ie))
        .filter((value): value is number => typeof value === 'number' && value > 0)
    : []
  const variationDistributionPrices = Array.isArray(detail.variacoes)
    ? detail.variacoes
        .map((variation) => normalizeDistributionPriceValue(variation))
        .filter((value): value is number => typeof value === 'number' && value > 0)
    : []
  const productPrice = normalizePriceValue(detail.preco)
  const productWholesalePrice = normalizePriceValue(detail.preco_atacado ?? detail.precoAtacado)
  const productCnpjIePrice = normalizePriceValue(detail.preco_cnpj_ie)
  const productDistributionPrice = normalizeDistributionPriceValue(detail)
  const ratingAverage = normalizeProductRatingAverage(detail.nota)
  const reviewsCount = normalizeProductReviewsCount(detail.avaliacoes)
  const minVariationPrice = variationPrices.length > 0 ? Math.min(...variationPrices) : undefined
  const minVariationWholesalePrice = variationWholesalePrices.length > 0 ? Math.min(...variationWholesalePrices) : undefined
  const minVariationCnpjIePrice = variationCnpjIePrices.length > 0 ? Math.min(...variationCnpjIePrices) : undefined
  const minVariationDistributionPrice = variationDistributionPrices.length > 0 ? Math.min(...variationDistributionPrices) : undefined
  const price = productPrice ?? minVariationPrice
  const wholesalePrice = productWholesalePrice ?? minVariationWholesalePrice
  const cnpjIePrice = productCnpjIePrice ?? minVariationCnpjIePrice
  const distributionPrice = productDistributionPrice ?? minVariationDistributionPrice
  const specs = parseTechnicalSpecsValue(detail.fichatecnica)
  const manufacturerBrandId =
    typeof detail.marca_fabricante_id === 'number' && Number.isFinite(detail.marca_fabricante_id) && detail.marca_fabricante_id > 0
      ? String(Math.trunc(detail.marca_fabricante_id))
      : undefined
  const classId =
    typeof detail.classe_id === 'number' && Number.isFinite(detail.classe_id) && detail.classe_id > 0
      ? String(Math.trunc(detail.classe_id))
      : undefined
  const manufacturerBrandName = detail.marca_fabricante_nome?.trim() || undefined
  const manufacturerBrandLogoUrl = normalizeImageUrl(detail.marca_fabricante_logo_url) || undefined
  const variationAdditionalInfo = normalizeCheckoutText(detail.informacoes_adicionais)
  const variationSelectorConfig = parseVariationSelectorConfig(detail.configuracoes_variacao)
  const normalizedBaseName = resolveProductBaseName(baseName, {
    serie: detail.serie,
    modelo: detail.modelo,
    tamanho: detail.tamanho,
  })
  const stock = normalizePriceValue(detail.estoque ?? detail.estoque_atual ?? detail.stock ?? detail.stockQuantity)
  const boxOptions = parseStorefrontProductBoxOptions(detail)

  return {
    id,
    cartBaseProductId: id,
    packagingMode: 'unit',
    packagingQuantity: 1,
    packagingLabel: boxOptions.length > 0 ? 'UNITARIO' : undefined,
    unitPrice: price,
    boxOptions: boxOptions.length > 0 ? boxOptions : undefined,
    parentProductId,
    code,
    manufacturerCode: code,
    ncm: resolveLojaVirtualProductNcm(detail),
    order: resolveProductOrderValue(detail),
    name,
    isNameLocalized: detail.nome_localizado === true || Number(detail.nome_localizado ?? 0) === 1,
    baseName: normalizedBaseName,
    serie,
    tamanho,
    modelo,
    variationAdditionalInfo: variationAdditionalInfo || undefined,
    variationSelectorConfig: variationSelectorConfig.length > 0 ? variationSelectorConfig : undefined,
    brand: detail.marca?.trim() || 'Sem marca',
    manufacturerBrandId,
    manufacturerBrandName,
    manufacturerBrandLogoUrl: manufacturerBrandLogoUrl ?? null,
    category: detail.produto_grupo_familia_nome?.trim() || detail.produto_grupo_nome?.trim() || 'Produtos Industriais',
    subcategory: detail.produto_grupo_nome?.trim() || '',
    classId,
    image: images[0] || '',
    images,
    specs,
    inStock: typeof stock === 'number' ? stock > 0 : true,
    stockQuantity: typeof stock === 'number' ? stock : null,
    priceVisible: [price, wholesalePrice, cnpjIePrice, distributionPrice].some((value) => typeof value === 'number' && value > 0),
    needsQuote: ![price, wholesalePrice, cnpjIePrice, distributionPrice].some((value) => typeof value === 'number' && value > 0),
    groupId: detail.produto_grupo_id ? String(detail.produto_grupo_id) : undefined,
    hasVariations: Boolean(detail.possui_variacoes || (detail.variacoes && detail.variacoes.length > 0)),
    hasForm: inferHasProductFormFlag(detail),
    usarSeletorVariacao: boxOptions.length > 0 || Number(detail.usar_seletor_variacao ?? 0) === 1,
    price,
    // For variable products, the highest variation price is a range indicator, not a real compare-at price.
    originalPrice: price,
    discountedPrice: price,
    wholesalePrice,
    cnpjIePrice,
    distributionPrice,
    ratingAverage,
    reviewsCount,
    videoUrl: videos[0],
    videos,
    videoAssets: getOptionalVideoAssets(videoAssets),
    courses: courses.length > 0 ? courses : undefined,
  }
}

const isApiStatusError = (error: unknown, status: number) =>
  error instanceof Error && error.message.startsWith(`API error: ${status}`)

const shouldRetryLojaVirtualWithoutEntidade = (error: unknown) => {
  if (!getAuthenticatedEntidadeId()) return false

  if (isApiRequestError(error)) {
    return [401, 403, 500, 502].includes(error.status)
  }

  return [401, 403, 500, 502].some((status) => isApiStatusError(error, status))
}

const shouldRetryLojaVirtualColdCatalog = (error: unknown) => {
  if (!isApiRequestError(error)) return false

  const detail = error.detail.trim().toLowerCase()
  return (
    error.status === 504
    || (error.status === 503 && detail === 'catalog_index_temporarily_unavailable')
  )
}

const shouldRetryLojaVirtualProductSearch = (error: unknown) => (
  isApiRequestError(error)
  && error.status === 504
)

const LOJA_VIRTUAL_PRODUCT_SEARCH_RETRY_DELAY_MS = 1_100

const shouldWaitForLojaVirtualProductSearchCircuit = (error: unknown) => (
  isApiRequestError(error)
  && error.status === 504
  && error.detail.trim().toLowerCase() === 'product_search_upstream_timeout'
)

const inferHasProductFormFlag = (payload: unknown): boolean | undefined => {
  if (!payload || typeof payload !== 'object') return undefined

  const record = payload as Record<string, unknown>
  const candidates = [
    record.possui_formulario,
    record.tem_formulario,
    record.produto_com_formulario,
    record.has_form,
    record.hasForm,
    record.exibir_formulario,
  ]

  for (const candidate of candidates) {
    if (typeof candidate === 'boolean') return candidate
    if (typeof candidate === 'number') return candidate === 1
    if (typeof candidate === 'string') {
      const normalized = candidate.trim().toLowerCase()
      if (normalized === '1' || normalized === 'true' || normalized === 'sim') return true
      if (normalized === '0' || normalized === 'false' || normalized === 'nao' || normalized === 'não') return false
    }
  }

  return undefined
}

function mapProductFormResolvedProductToProduct(
  item: LojaVirtualProdutoFormularioComponenteProdutoResponse
): Product | null {
  const id = Number(item.produto_id ?? 0)
  if (!Number.isFinite(id) || id <= 0) return null

  const code = item.referencia?.trim() || String(id)
  const images = withProductImagePlaceholder([item.imagem], { includePlaceholder: false })
  const image = images[0] || ''
  const price = normalizePriceValue(item.preco)
  const wholesalePrice = normalizePriceValue(item.preco_atacado ?? item.precoAtacado)
  const cnpjIePrice = normalizePriceValue(item.preco_cnpj_ie)
  const distributionPrice = normalizeDistributionPriceValue(item)
  const baseName = item.nome?.trim() || `Produto ${id}`
  const name = normalizeProductName(
    buildProductDisplayName(baseName, {
      serie: item.serie,
      modelo: item.modelo,
      tamanho: item.tamanho,
    })
  )
  const serie = normalizeDescriptorValue(item.serie) || undefined
  const tamanho = normalizeDescriptorValue(item.tamanho) || undefined
  const modelo = normalizeDescriptorValue(item.modelo) || undefined
  const normalizedBaseName = resolveProductBaseName(baseName, {
    serie: item.serie,
    modelo: item.modelo,
    tamanho: item.tamanho,
  })
  const stock = normalizePriceValue(item.estoque ?? item.estoque_atual ?? item.stock ?? item.stockQuantity)

  return {
    id: String(id),
    code,
    manufacturerCode: code,
    ncm: resolveLojaVirtualProductNcm(item),
    name,
    baseName: normalizedBaseName,
    serie,
    tamanho,
    modelo,
    brand: item.marca?.trim() || 'Sem marca',
    manufacturerBrandId: undefined,
    manufacturerBrandName: undefined,
    manufacturerBrandLogoUrl: null,
    category: item.produto_grupo_familia_nome?.trim() || item.produto_grupo_nome?.trim() || 'Produtos Industriais',
    subcategory: item.produto_grupo_nome?.trim() || '',
    classId: typeof item.classe_id === 'number' && Number.isFinite(item.classe_id) && item.classe_id > 0
      ? String(Math.trunc(item.classe_id))
      : undefined,
    image,
    images,
    specs: {},
    inStock: typeof stock === 'number' ? stock > 0 : true,
    stockQuantity: typeof stock === 'number' ? stock : null,
    priceVisible: [price, wholesalePrice, cnpjIePrice, distributionPrice].some((value) => typeof value === 'number' && value > 0),
    needsQuote: ![price, wholesalePrice, cnpjIePrice, distributionPrice].some((value) => typeof value === 'number' && value > 0),
    groupId: item.produto_grupo_id ? String(item.produto_grupo_id) : undefined,
    hasVariations: Boolean(item.possui_variacoes),
    hasForm: inferHasProductFormFlag(item),
    price,
    originalPrice: price,
    discountedPrice: price,
    wholesalePrice,
    cnpjIePrice,
    distributionPrice,
  }
}

function mapLojaVirtualVariationDetailToProduct(
  variation: LojaVirtualVariacaoDetalhe,
  parentDetail?: LojaVirtualProdutoDetalhe | null
): Product {
  const baseProduct = parentDetail ? mapLojaVirtualProductDetailToProduct(parentDetail) : null
  const id = String(variation.produto_id)
  const normalizedParentProductId = parentDetail?.id ? String(parentDetail.id) : undefined
  const parentProductId = normalizedParentProductId && normalizedParentProductId !== id
    ? normalizedParentProductId
    : undefined
  const code = variation.referencia?.trim() || id
  const baseName = variation.nome?.trim()
    || parentDetail?.nome_exibicao?.trim()
    || parentDetail?.nome?.trim()
    || baseProduct?.name?.trim()
    || `Produto ${id}`
  const name = normalizeProductName(
    buildProductDisplayName(baseName, {
      serie: variation.serie,
      modelo: variation.modelo,
      tamanho: variation.tamanho,
    })
  )
  const serie = normalizeDescriptorValue(variation.serie) || undefined
  const tamanho = normalizeDescriptorValue(variation.tamanho) || undefined
  const modelo = normalizeDescriptorValue(variation.modelo) || undefined
  const normalizedImages = buildPreferredProductImageList({
    image: variation.imagem,
    photos: variation.fotos,
  })
  const variationVideoAssets = normalizeVideoAssets(variation.videos)
  const videoAssets = variationVideoAssets.length > 0 ? variationVideoAssets : (baseProduct?.videoAssets ?? [])
  const videos = videoAssets.map((asset) => asset.url)
  const price = normalizePriceValue(variation.preco)
    ?? baseProduct?.discountedPrice
    ?? baseProduct?.originalPrice
  const wholesalePrice = normalizePriceValue(variation.preco_atacado ?? variation.precoAtacado)
    ?? baseProduct?.wholesalePrice
  const cnpjIePrice = normalizePriceValue(variation.preco_cnpj_ie)
  const distributionPrice = normalizeDistributionPriceValue(variation)
  const stock = normalizePriceValue(variation.estoque)
  const specs = { ...(baseProduct?.specs ?? {}) }
  const variationAttributes = parseVariationAttributes(variation.caracteristicas ?? variation.atributos)
  const variationSelectorConfig = parseVariationSelectorConfig(
    parentDetail?.configuracoes_variacao
    ?? variation.configuracoes_variacao
  )
  const variationLabel = variationAttributes.find((attribute) => attribute.value)?.value
  const variationAdditionalInfo = normalizeCheckoutText(variation.informacoes_adicionais)
  const manufacturerBrandId =
    typeof variation.marca_fabricante_id === 'number' && Number.isFinite(variation.marca_fabricante_id) && variation.marca_fabricante_id > 0
      ? String(Math.trunc(variation.marca_fabricante_id))
      : baseProduct?.manufacturerBrandId
  const classId =
    typeof variation.classe_id === 'number' && Number.isFinite(variation.classe_id) && variation.classe_id > 0
      ? String(Math.trunc(variation.classe_id))
      : baseProduct?.classId
  const manufacturerBrandName =
    variation.marca_fabricante_nome?.trim()
    || baseProduct?.manufacturerBrandName
    || parentDetail?.marca_fabricante_nome?.trim()
    || undefined
  const manufacturerBrandLogoUrl =
    normalizeImageUrl(variation.marca_fabricante_logo_url)
    || baseProduct?.manufacturerBrandLogoUrl
    || normalizeImageUrl(parentDetail?.marca_fabricante_logo_url)
    || undefined
  const parentProductName =
    baseProduct?.baseName
    || resolveParentProductBaseName(parentDetail)
    || undefined
  const normalizedBaseName = resolveProductBaseName(baseName, {
    serie: variation.serie,
    modelo: variation.modelo,
    tamanho: variation.tamanho,
  })
  const boxOptions = parseStorefrontProductBoxOptions(variation)

  Object.entries(parseVariationCharacteristics(variation.caracteristicas ?? variation.atributos))
    .forEach(([key, value]) => addSpec(specs, key, value))

  return {
    id,
    cartBaseProductId: id,
    packagingMode: 'unit',
    packagingQuantity: 1,
    packagingLabel: boxOptions.length > 0 ? 'UNITARIO' : undefined,
    unitPrice: price,
    boxOptions: boxOptions.length > 0 ? boxOptions : undefined,
    parentProductId,
    code,
    manufacturerCode: code,
    ncm: resolveLojaVirtualProductNcm(variation) || baseProduct?.ncm || resolveLojaVirtualProductNcm(parentDetail),
    order: resolveProductOrderValue(variation),
    name,
    baseName: normalizedBaseName,
    serie,
    tamanho,
    modelo,
    variationLabel,
    variationAdditionalInfo: variationAdditionalInfo || undefined,
    variationAttributes,
    variationSelectorConfig: variationSelectorConfig.length > 0
      ? variationSelectorConfig
      : (baseProduct?.variationSelectorConfig ?? undefined),
    brand: baseProduct?.brand || parentDetail?.marca?.trim() || 'Sem marca',
    manufacturerBrandId,
    manufacturerBrandName,
    manufacturerBrandLogoUrl: manufacturerBrandLogoUrl ?? null,
    category: baseProduct?.category
      || parentDetail?.produto_grupo_familia_nome?.trim()
      || parentDetail?.produto_grupo_nome?.trim()
      || 'Produtos Industriais',
    subcategory: baseProduct?.subcategory || parentDetail?.produto_grupo_nome?.trim() || '',
    classId,
    image: normalizedImages[0] || normalizeProductImageUrl(variation.imagem) || '',
    images: normalizedImages,
    specs,
    inStock: typeof stock === 'number' ? stock > 0 : baseProduct?.inStock ?? true,
    stockQuantity: typeof stock === 'number' ? stock : (baseProduct?.stockQuantity ?? null),
    priceVisible: [price, wholesalePrice, cnpjIePrice, distributionPrice].some((value) => typeof value === 'number' && value > 0),
    needsQuote: ![price, wholesalePrice, cnpjIePrice, distributionPrice].some((value) => typeof value === 'number' && value > 0),
    minOrder: baseProduct?.minOrder,
    groupId: baseProduct?.groupId || (parentDetail?.produto_grupo_id ? String(parentDetail.produto_grupo_id) : undefined),
    supplierName: baseProduct?.supplierName,
    videoUrl: videos[0],
    videos,
    videoAssets: getOptionalVideoAssets(videoAssets),
    hasVariations: false,
    hasForm: baseProduct?.hasForm,
    usarSeletorVariacao: boxOptions.length > 0
      ? true
      : (baseProduct?.usarSeletorVariacao ?? (Number(parentDetail?.usar_seletor_variacao ?? 0) === 1)),
    parentProductName,
    price,
    originalPrice: price,
    discountedPrice: price,
    wholesalePrice,
    cnpjIePrice,
    distributionPrice,
    ratingAverage: baseProduct?.ratingAverage,
    reviewsCount: baseProduct?.reviewsCount,
    discountPercentage: baseProduct?.discountPercentage,
    installmentValue: baseProduct?.installmentValue,
    courses: baseProduct?.courses,
  }
}

function mapLojaVirtualProducts(
  items?: LojaVirtualProdutoItem[],
  options: {
    parentProductId?: string | null
    parentProductName?: string | null
    usarSeletorVariacao?: boolean | null
    variationSelectorConfig?: LojaVirtualVariacaoConfiguracao[] | null
  } = {}
): Product[] {
  if (!Array.isArray(items)) return []
  return items
    .filter((item): item is LojaVirtualProdutoItem => Boolean(item && item.produto_id))
    .map((item) => mapLojaVirtualProductToProduct(item, options))
}

function expandProductsWithBoxPurchaseOptions(products: Product[]): Product[] {
  if (!Array.isArray(products) || products.length === 0) return []

  return products.flatMap((product) => {
    const boxOptions = product.boxOptions ?? []
    if (boxOptions.length === 0) return [product]

    return [
      buildStorefrontPurchaseOptionProduct(product, 1),
      ...boxOptions
        .filter((option) => option.quantity > 1)
        .map((option) => buildStorefrontPurchaseOptionProduct(product, option.quantity)),
    ]
  })
}

function mapApiProductToProduct(apiProduct: ApiProduct): Product {
  const specs = parseTechnicalSpecs(apiProduct.fichatecnica_json)
  const images = withProductImagePlaceholder(
    [apiProduct.image, ...normalizeStringList(apiProduct.images)],
    { includePlaceholder: false },
  )
  const videoAssets = normalizeVideoAssets(apiProduct.videos)
  const videos = videoAssets.map((asset) => asset.url)

  const product = {
    id: apiProduct.id.toString(),
    code: apiProduct.referencia || apiProduct.codigo.toString(),
    manufacturerCode: apiProduct.codigo.toString(),
    name: normalizeProductName(apiProduct.nome || apiProduct.title || `Produto ${apiProduct.id}`),
    baseName: normalizeProductName(apiProduct.nome || apiProduct.title || `Produto ${apiProduct.id}`),
    brand: apiProduct.marca || 'Sem marca',
    category: 'Produtos Industriais',
    subcategory: '',
    image: images[0] || '',
    images,
    specs,
    inStock: true,
    priceVisible: false,
    needsQuote: apiProduct.originalPrice === 0,
    groupId: apiProduct.produto_grupo_id?.toString(),
    videoUrl: videos[0],
    videos,
    videoAssets: getOptionalVideoAssets(videoAssets),
    hasVariations: Boolean(apiProduct.produto_com_variacoes || apiProduct.hasVariations),
    hasForm: inferHasProductFormFlag(apiProduct),
    price: apiProduct.discountedPrice,
    originalPrice: apiProduct.originalPrice,
    discountedPrice: apiProduct.discountedPrice,
    distributionPrice: normalizeDistributionPriceValue(apiProduct),
    discountPercentage: apiProduct.discountPercentage,
    installmentValue: apiProduct.installmentValue,
  }
  return product
}

function mapApiDetailToProduct(apiDetail: ApiProductDetail): Product {
  const specs = parseTechnicalSpecs(apiDetail.fichatecnica_json)
  const images = withProductImagePlaceholder(
    normalizeStringList(apiDetail.images),
    { includePlaceholder: false },
  )
  const videoAssets = normalizeVideoAssets(apiDetail.videos)
  const videos = videoAssets.map((asset) => asset.url)

  return {
    id: apiDetail.id.toString(),
    code: apiDetail.referencia,
    manufacturerCode: apiDetail.id.toString(),
    name: normalizeProductName(apiDetail.nome || apiDetail.title || `Produto ${apiDetail.id}`),
    baseName: normalizeProductName(apiDetail.nome || apiDetail.title || `Produto ${apiDetail.id}`),
    brand: apiDetail.marca || 'Sem marca',
    category: 'Produtos Industriais',
    subcategory: '',
    image: images[0] || '',
    images,
    specs,
    inStock: true,
    priceVisible: false,
    needsQuote: apiDetail.originalprice === 0,
    groupId: apiDetail.produto_grupo_id?.toString(),
    videoUrl: videos[0],
    videos,
    videoAssets: getOptionalVideoAssets(videoAssets),
    hasVariations: Boolean(apiDetail.produto_com_variacoes),
    hasForm: inferHasProductFormFlag(apiDetail),
    price: apiDetail.discountedprice,
    originalPrice: apiDetail.originalprice,
    discountedPrice: apiDetail.discountedprice,
    distributionPrice: normalizeDistributionPriceValue(apiDetail),
    discountPercentage: apiDetail.discountpercentage,
  }
}

type FetchApiOptions = RequestInit & {
  skipIntegrationAuth?: boolean
  skipBuyerSessionCredentials?: boolean
  skipBuyerSessionToken?: boolean
  suppressBuyerSessionExpiry?: boolean
  trackBackendAvailability?: boolean
}

const SKIP_INTEGRATION_AUTH_HEADER = 'x-c2s-skip-integration-auth'

class ApiRequestError extends Error {
  status: number
  detail: string
  code: string

  constructor(status: number, detail?: string, code?: string) {
    super(`API error: ${status}${detail ? ` - ${detail}` : ''}`)
    this.name = 'ApiRequestError'
    this.status = status
    this.detail = detail ?? ''
    this.code = code ?? ''
  }
}

class PixUnavailableError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'PixUnavailableError'
  }
}

function isApiRequestError(error: unknown): error is ApiRequestError {
  return error instanceof ApiRequestError
}

export function isCheckoutCartPriceContextStaleError(error: unknown): boolean {
  return isApiRequestError(error)
    && (
      error.code === 'checkout_carrinho_beneficio_desatualizado'
      || error.code === 'carrinho_beneficio_desatualizado'
    )
}

export function isPixUnavailableError(error: unknown): error is Error {
  return error instanceof PixUnavailableError
}

export function isAtendimentoAvaliacaoGuestIdentityRequiredError(error: unknown): boolean {
  return isApiRequestError(error) && error.code === 'guest_identity_required'
}

export async function submitAtendimentoAvaliacao(
  submission: AtendimentoAvaliacaoSubmission
): Promise<AtendimentoAvaliacaoSubmissionResponse> {
  const isGuestSubmission = Boolean(submission.guestIdentity)
  const formData = new FormData()
  formData.set('submission_id', submission.submissionId)
  formData.set('loja_id', String(submission.lojaId))
  formData.set('duration_seconds', String(Math.max(1, Math.ceil(submission.durationSeconds))))
  if (submission.guestIdentity) {
    formData.set('nome', submission.guestIdentity.nome)
    formData.set('whatsapp', submission.guestIdentity.whatsapp)
    formData.set('cnpj', submission.guestIdentity.cnpj)
  }
  const extension = submission.audio.type.includes('mp4')
    ? 'm4a'
    : submission.audio.type.includes('ogg')
      ? 'ogg'
      : 'webm'
  formData.set('audio', submission.audio, `avaliacao.${extension}`)

  return fetchApi<AtendimentoAvaliacaoSubmissionResponse>(
    '/loja-virtual/v1/avaliacoes-atendimento',
    {
      method: 'POST',
      body: formData,
      // A presença dos campos identifica explicitamente o envio como visitante.
      // Não permita que um token antigo do localStorage ou o cookie HttpOnly
      // transforme silenciosamente essa avaliação em uma avaliação autenticada.
      skipBuyerSessionToken: isGuestSubmission,
      skipBuyerSessionCredentials: isGuestSubmission,
    }
  )
}

export function isIntegrationRouteNotAllowedError(error: unknown): boolean {
  if (isApiRequestError(error)) {
    return error.status === 403 && error.detail.trim().toLocaleLowerCase('pt-BR') === 'integration_route_not_allowed'
  }

  return (
    error instanceof Error
    && /API error:\s*403(?:\s*-\s*integration_route_not_allowed)?/i.test(error.message)
    && error.message.toLocaleLowerCase('pt-BR').includes('integration_route_not_allowed')
  )
}

function isAuthenticationFailureError(error: unknown): boolean {
  if (isApiRequestError(error)) {
    return error.status === 401 || error.status === 403
  }

  return error instanceof Error && /\bAPI error:\s*(401|403)\b/i.test(error.message)
}

const normalizeBuyerSessionConflictDetail = (value?: string | null) => String(value ?? '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLocaleLowerCase('pt-BR')

const CHECKOUT_CART_UNAVAILABLE_CONFLICT_TOKENS = [
  'carrinho nao esta mais disponivel',
  'carrinho nao esta disponivel',
  'cart is no longer available',
  'cart is not available',
]

const BUYER_SESSION_RECOVERABLE_CONFLICT_TOKENS = [
  'buyer_session_entity_mismatch',
  'buyer_session_required',
  'sessao do comprador nao corresponde',
  ...CHECKOUT_CART_UNAVAILABLE_CONFLICT_TOKENS,
]

function isBuyerSessionRequired(detail?: string, payload?: unknown): boolean {
  if (payload && typeof payload === 'object') {
    const record = payload as Record<string, unknown>
    if (record.error === 'buyer_session_required') return true
  }

  const normalizedDetail = normalizeBuyerSessionConflictDetail(detail)
  return normalizedDetail.includes('buyer_session_required')
    || normalizedDetail.includes('faca login novamente para continuar')
    || normalizedDetail.includes('sua sessao expirou')
}

function isBuyerSessionEntityMismatch(detail?: string, payload?: unknown): boolean {
  if (payload && typeof payload === 'object') {
    const record = payload as Record<string, unknown>
    if (record.error === 'buyer_session_entity_mismatch') return true
  }

  const normalizedDetail = normalizeBuyerSessionConflictDetail(detail)

  return normalizedDetail.includes('buyer_session_entity_mismatch')
    || normalizedDetail.includes('sessao do comprador nao corresponde')
}

function isRecoverableBuyerSessionCartConflict(detail?: string, payload?: unknown): boolean {
  if (payload && typeof payload === 'object') {
    const record = payload as Record<string, unknown>
    const normalizedError = normalizeBuyerSessionConflictDetail(
      typeof record.error === 'string' ? record.error : ''
    )

    if (BUYER_SESSION_RECOVERABLE_CONFLICT_TOKENS.some((token) => normalizedError.includes(token))) {
      return true
    }
  }

  const normalizedDetail = normalizeBuyerSessionConflictDetail(detail)
  return BUYER_SESSION_RECOVERABLE_CONFLICT_TOKENS.some((token) => normalizedDetail.includes(token))
}

function extractCheckoutRetryErrorInfo(error: unknown): { status: number; detail: string } | null {
  if (isApiRequestError(error)) {
    return {
      status: error.status,
      detail: error.detail,
    }
  }

  if (!(error instanceof Error)) return null

  const statusMatch = error.message.match(/\bAPI error:\s*(\d{3})\b/i)
  if (!statusMatch) return null

  const status = Number.parseInt(statusMatch[1], 10)
  if (!Number.isFinite(status)) return null

  return {
    status,
    detail: error.message
      .replace(/^API error:\s*\d+\s*-\s*/i, '')
      .replace(/^API error:\s*\d+\s*/i, '')
      .trim(),
  }
}

export function isCheckoutCartUnavailableError(error: unknown): boolean {
  const errorInfo = extractCheckoutRetryErrorInfo(error)
  if (!errorInfo || errorInfo.status !== 409) return false

  const normalizedDetail = normalizeBuyerSessionConflictDetail(errorInfo.detail)
  return CHECKOUT_CART_UNAVAILABLE_CONFLICT_TOKENS.some((token) => normalizedDetail.includes(token))
}

function isTerminalPixCreateError(error: unknown): boolean {
  return isApiRequestError(error) && (error.status === 400 || error.status === 404)
}

// Dedupe de GETs idênticos em voo. Sem react-query/SWR no projeto, requests
// iguais disparadas em paralelo (ex.: F5 que monta menu/relacionados antes de
// qualquer cache pegar) saíam todas para a rede. Aqui, enquanto uma request GET
// estiver em andamento, chamadas idênticas reusam a mesma Promise.
const inFlightGetRequests = new Map<string, Promise<unknown>>()

function buildInFlightGetKey(endpoint: string, options?: FetchApiOptions): string | null {
  const method = (options?.method ?? 'GET').toUpperCase()
  if (method !== 'GET') {
    return null
  }
  const hasBody = options?.body !== undefined && options?.body !== null
  if (hasBody) {
    return null
  }
  // A identidade da request também depende do token de sessão do comprador e dos
  // skips de auth/sessão, pois mudam os headers e, portanto, a resposta.
  const buyerSessionToken = options?.skipBuyerSessionToken ? '' : getBuyerSessionToken() ?? ''
  return [
    endpoint,
    options?.skipIntegrationAuth ? '1' : '0',
    options?.skipBuyerSessionToken ? '1' : '0',
    options?.skipBuyerSessionCredentials ? '1' : '0',
    options?.suppressBuyerSessionExpiry ? '1' : '0',
    buyerSessionToken,
  ].join('|')
}

// Cache curto de resposta para GETs de CATÁLOGO (dados pouco voláteis e
// compartilhados). Reduz re-buscas quando deps de useEffect "assentam" durante
// o carregamento (gap de segundos, fora do alcance do dedupe em voo).
// ALLOWLIST conservadora: só catálogo. NUNCA cachear carrinho/conta/checkout
// (estado mutável do comprador precisa ficar sempre fresco).
const GET_RESPONSE_CACHE_TTL_MS = 60_000
const getResponseCache = new Map<string, { expiresAt: number; value: unknown }>()

// Caminhos de catálogo seguros para cachear. Comparado contra o endpoint
// (sem querystring). Tudo que envolva compradores/{id}/*, checkout, sessão ou
// dados de conta fica de fora por omissão (allowlist, não denylist).
const CACHEABLE_GET_PATTERNS: RegExp[] = [
  /^\/loja-virtual\/v1\/home\/produtos(\?|$)/,
  /^\/loja-virtual\/v1\/outros\/menu-arvore(\?|$)/,
  /^\/loja-virtual\/v1\/outros\/classes\b/,
  /^\/loja-virtual\/v1\/produtos\/\d+(\?|$)/,
  /^\/loja-virtual\/v1\/produtos\/\d+\/variacoes(\?|$)/,
  /^\/loja-virtual\/v1\/produtos\/\d+\/avaliacoes(\?|$)/,
  /^\/loja-virtual\/v1\/produtos\/\d+\/perguntas(\?|$)/,
  /^\/loja-virtual\/v1\/produtos\/relacionados(\?|$)/,
  /^\/loja-virtual\/v1\/produtos\/relacionados-atividade(\?|$)/,
  /^\/loja-virtual\/v1\/produtos\/relacionados-comprejunto(\?|$)/,
  /^\/loja-virtual\/v1\/variacoes\/\d+(\?|$)/,
]

function isCacheableGetEndpoint(endpoint: string): boolean {
  const path = endpoint.split('?')[0]
  // Defesa extra: nunca cachear nada de comprador/checkout, mesmo que um padrão
  // futuro acidentalmente case.
  if (/\/compradores\//.test(endpoint) || /\/checkout\b/.test(endpoint)) {
    return false
  }
  return CACHEABLE_GET_PATTERNS.some((pattern) => pattern.test(path) || pattern.test(endpoint))
}

export async function fetchApi<T>(
  endpoint: string,
  options?: FetchApiOptions,
  retryAfterAuthFailure: boolean = true
): Promise<T> {
  const dedupeKey = buildInFlightGetKey(endpoint, options)
  if (dedupeKey === null) {
    return performFetchApi<T>(endpoint, options, retryAfterAuthFailure)
  }

  const cacheable = isCacheableGetEndpoint(endpoint)
  if (cacheable) {
    const cached = getResponseCache.get(dedupeKey)
    if (cached) {
      if (
        Date.now() < cached.expiresAt
        && shouldCacheStorefrontCatalogResponse(endpoint, cached.value)
      ) {
        return cached.value as T
      }
      getResponseCache.delete(dedupeKey)
    }
  }

  const existing = inFlightGetRequests.get(dedupeKey)
  if (existing) {
    return existing as Promise<T>
  }

  const request = performFetchApi<T>(endpoint, options, retryAfterAuthFailure)
    .then((value) => {
      if (cacheable && shouldCacheStorefrontCatalogResponse(endpoint, value)) {
        getResponseCache.set(dedupeKey, { expiresAt: Date.now() + GET_RESPONSE_CACHE_TTL_MS, value })
      }
      return value
    })
    .finally(() => {
      inFlightGetRequests.delete(dedupeKey)
    })
  inFlightGetRequests.set(dedupeKey, request)
  return request
}

async function performFetchApi<T>(
  endpoint: string,
  options?: FetchApiOptions,
  retryAfterAuthFailure: boolean = true
): Promise<T> {
  const {
    skipIntegrationAuth = false,
    skipBuyerSessionCredentials = false,
    skipBuyerSessionToken = false,
    suppressBuyerSessionExpiry = false,
    trackBackendAvailability = true,
    ...requestOptions
  } = options ?? {}
  const shouldSkipBuyerSessionCredentials = skipBuyerSessionCredentials
  const headers = buildRequestHeaders(requestOptions.headers)
  const currentBuyerSessionToken = skipBuyerSessionToken ? null : getBuyerSessionToken()
  const hasBody = requestOptions.body !== undefined && requestOptions.body !== null
  const isFormDataBody = typeof FormData !== 'undefined' && requestOptions.body instanceof FormData

  if (!hasBody) {
    headers.delete('Content-Type')
  } else if (!isFormDataBody && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json')
  }

  if (skipIntegrationAuth) {
    headers.set(SKIP_INTEGRATION_AUTH_HEADER, '1')
  }

  if (skipBuyerSessionToken) {
    headers.delete('X-Loja-Comprador-Session')
  } else {
    if (currentBuyerSessionToken && !headers.has('X-Loja-Comprador-Session')) {
      headers.set('X-Loja-Comprador-Session', currentBuyerSessionToken)
    }
  }

  if (SHOULD_USE_INTEGRATION_AUTH && !skipIntegrationAuth && !headers.has('Authorization')) {
    const authorization = await getIntegrationAuthorizationHeader()
    headers.set('Authorization', authorization)
  }

  let response: Response
  try {
    response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...requestOptions,
      credentials: shouldSkipBuyerSessionCredentials ? 'omit' : requestOptions.credentials ?? 'include',
      headers,
    })
  } catch (error) {
    if (trackBackendAvailability) {
      markBackendUnavailable(error)
    }
    throw normalizeError(error)
  }

  if (trackBackendAvailability) {
    if (isBackendUnavailableStatus(response.status)) {
      markBackendUnavailable(new Error(`API error: ${response.status}`))
    } else {
      markBackendAvailable()
    }
  }

  if (response.status === 401 && SHOULD_USE_INTEGRATION_AUTH && !skipIntegrationAuth && retryAfterAuthFailure) {
    integrationTokenState = null
    integrationTokenFailureState = null
    const renewedAuthorization = await getIntegrationAuthorizationHeader(true)
    headers.set('Authorization', renewedAuthorization)
    return performFetchApi<T>(endpoint, { ...requestOptions, headers, skipIntegrationAuth, suppressBuyerSessionExpiry, trackBackendAvailability }, false)
  }

  if (!response.ok) {
    const responsePayload = await parseJsonSafe(response)
    const detail = extractErrorMessage(responsePayload)

    if (
      response.status === 401
      && retryAfterAuthFailure
      && !suppressBuyerSessionExpiry
      && !skipBuyerSessionToken
      && currentBuyerSessionToken
      && isBuyerSessionRequired(detail, responsePayload)
    ) {
      const didClearFailedSession = clearBuyerSessionTokenState(
        { endpoint, status: response.status, detail },
        currentBuyerSessionToken
      )
      return performFetchApi<T>(
        endpoint,
        {
          ...requestOptions,
          headers: buildRetryHeadersWithoutBuyerSession(headers),
          skipIntegrationAuth,
          skipBuyerSessionCredentials: shouldSkipBuyerSessionCredentials,
          skipBuyerSessionToken: didClearFailedSession,
          suppressBuyerSessionExpiry,
          trackBackendAvailability,
        },
        false
      )
    }

    if (
      response.status === 401
      && retryAfterAuthFailure
      && shouldRetryProxyManagedIntegrationAuth(detail)
    ) {
      await wait(PROXY_INTEGRATION_AUTH_RETRY_DELAY_MS)
      return performFetchApi<T>(endpoint, { ...requestOptions, headers, skipIntegrationAuth, suppressBuyerSessionExpiry, trackBackendAvailability }, false)
    }

    if (
      response.status === 403
      && retryAfterAuthFailure
      && !suppressBuyerSessionExpiry
      && !skipBuyerSessionToken
      && currentBuyerSessionToken
      && isBuyerSessionEntityMismatch(detail, responsePayload)
    ) {
      const didClearFailedSession = clearBuyerSessionTokenState(
        { endpoint, status: response.status, detail },
        currentBuyerSessionToken
      )
      return performFetchApi<T>(
        endpoint,
        {
          ...requestOptions,
          headers: buildRetryHeadersWithoutBuyerSession(headers),
          skipIntegrationAuth,
          skipBuyerSessionCredentials: didClearFailedSession || shouldSkipBuyerSessionCredentials,
          skipBuyerSessionToken: didClearFailedSession,
          suppressBuyerSessionExpiry,
          trackBackendAvailability,
        },
        false
      )
    }

    if (
      !suppressBuyerSessionExpiry
      && response.status === 401
      && isBuyerSessionRequired(detail, responsePayload)
      && (!currentBuyerSessionToken || isCurrentBuyerSessionToken(currentBuyerSessionToken))
    ) {
      notifyBuyerSessionExpired({ endpoint, status: response.status, detail })
    }

    throw new ApiRequestError(response.status, detail, extractErrorCode(responsePayload))
  }

  return response.json()
}

async function fetchApiResponse(
  endpoint: string,
  options?: FetchApiOptions,
  retryAfterAuthFailure: boolean = true
): Promise<Response> {
  const {
    skipIntegrationAuth = false,
    skipBuyerSessionCredentials = false,
    skipBuyerSessionToken = false,
    suppressBuyerSessionExpiry = false,
    trackBackendAvailability = true,
    ...requestOptions
  } = options ?? {}
  const shouldSkipBuyerSessionCredentials = skipBuyerSessionCredentials
  const headers = buildRequestHeaders(requestOptions.headers)
  const currentBuyerSessionToken = skipBuyerSessionToken ? null : getBuyerSessionToken()
  const hasBody = requestOptions.body !== undefined && requestOptions.body !== null
  const isFormDataBody = typeof FormData !== 'undefined' && requestOptions.body instanceof FormData

  if (!hasBody) {
    headers.delete('Content-Type')
  } else if (!isFormDataBody && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json')
  }

  if (skipIntegrationAuth) {
    headers.set(SKIP_INTEGRATION_AUTH_HEADER, '1')
  }

  if (skipBuyerSessionToken) {
    headers.delete('X-Loja-Comprador-Session')
  } else {
    if (currentBuyerSessionToken && !headers.has('X-Loja-Comprador-Session')) {
      headers.set('X-Loja-Comprador-Session', currentBuyerSessionToken)
    }
  }

  if (SHOULD_USE_INTEGRATION_AUTH && !skipIntegrationAuth && !headers.has('Authorization')) {
    const authorization = await getIntegrationAuthorizationHeader()
    headers.set('Authorization', authorization)
  }

  let response: Response
  try {
    response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...requestOptions,
      credentials: shouldSkipBuyerSessionCredentials ? 'omit' : requestOptions.credentials ?? 'include',
      headers,
    })
  } catch (error) {
    if (trackBackendAvailability) {
      markBackendUnavailable(error)
    }
    throw normalizeError(error)
  }

  if (trackBackendAvailability) {
    if (isBackendUnavailableStatus(response.status)) {
      markBackendUnavailable(new Error(`API error: ${response.status}`))
    } else {
      markBackendAvailable()
    }
  }

  if (response.status === 401 && SHOULD_USE_INTEGRATION_AUTH && !skipIntegrationAuth && retryAfterAuthFailure) {
    integrationTokenState = null
    integrationTokenFailureState = null
    const renewedAuthorization = await getIntegrationAuthorizationHeader(true)
    headers.set('Authorization', renewedAuthorization)
    return fetchApiResponse(endpoint, { ...requestOptions, headers, skipIntegrationAuth, suppressBuyerSessionExpiry, trackBackendAvailability }, false)
  }

  if (!response.ok) {
    const responsePayload = await parseJsonSafe(response)
    const detail = extractErrorMessage(responsePayload)

    if (
      response.status === 401
      && retryAfterAuthFailure
      && !suppressBuyerSessionExpiry
      && !skipBuyerSessionToken
      && currentBuyerSessionToken
      && isBuyerSessionRequired(detail, responsePayload)
    ) {
      const didClearFailedSession = clearBuyerSessionTokenState(
        { endpoint, status: response.status, detail },
        currentBuyerSessionToken
      )
      return fetchApiResponse(
        endpoint,
        {
          ...requestOptions,
          headers: buildRetryHeadersWithoutBuyerSession(headers),
          skipIntegrationAuth,
          skipBuyerSessionCredentials: shouldSkipBuyerSessionCredentials,
          skipBuyerSessionToken: didClearFailedSession,
          suppressBuyerSessionExpiry,
          trackBackendAvailability,
        },
        false
      )
    }

    if (
      response.status === 401
      && retryAfterAuthFailure
      && shouldRetryProxyManagedIntegrationAuth(detail)
    ) {
      await wait(PROXY_INTEGRATION_AUTH_RETRY_DELAY_MS)
      return fetchApiResponse(endpoint, { ...requestOptions, headers, skipIntegrationAuth, suppressBuyerSessionExpiry, trackBackendAvailability }, false)
    }

    if (
      response.status === 403
      && retryAfterAuthFailure
      && !suppressBuyerSessionExpiry
      && !skipBuyerSessionToken
      && currentBuyerSessionToken
      && isBuyerSessionEntityMismatch(detail, responsePayload)
    ) {
      const didClearFailedSession = clearBuyerSessionTokenState(
        { endpoint, status: response.status, detail },
        currentBuyerSessionToken
      )
      return fetchApiResponse(
        endpoint,
        {
          ...requestOptions,
          headers: buildRetryHeadersWithoutBuyerSession(headers),
          skipIntegrationAuth,
          skipBuyerSessionCredentials: didClearFailedSession || shouldSkipBuyerSessionCredentials,
          skipBuyerSessionToken: didClearFailedSession,
          suppressBuyerSessionExpiry,
          trackBackendAvailability,
        },
        false
      )
    }

    if (
      !suppressBuyerSessionExpiry
      && response.status === 401
      && isBuyerSessionRequired(detail, responsePayload)
      && (!currentBuyerSessionToken || isCurrentBuyerSessionToken(currentBuyerSessionToken))
    ) {
      notifyBuyerSessionExpired({ endpoint, status: response.status, detail })
    }

    throw new Error(`API error: ${response.status}${detail ? ` - ${detail}` : ''}`)
  }

  return response
}

const shouldRetryCheckoutWithoutBuyerSessionToken = (error: unknown) => {
  if (!getBuyerSessionToken()) return false

  const errorInfo = extractCheckoutRetryErrorInfo(error)
  if (!errorInfo) return false

  if (errorInfo.status === 401 || errorInfo.status === 403) return true

  return errorInfo.status === 409 && isRecoverableBuyerSessionCartConflict(errorInfo.detail)
}

const shouldClearBuyerSessionTokenBeforeCheckoutRetry = (error: unknown) => {
  const errorInfo = extractCheckoutRetryErrorInfo(error)
  if (!errorInfo) return false

  return errorInfo.status === 409 && isRecoverableBuyerSessionCartConflict(errorInfo.detail)
}

async function fetchCheckoutApi<T>(
  endpoint: string,
  options?: FetchApiOptions
): Promise<T> {
  try {
    return await fetchApi<T>(endpoint, options)
  } catch (error) {
    if (
      options?.skipBuyerSessionToken
      || !shouldRetryCheckoutWithoutBuyerSessionToken(error)
    ) {
      throw error
    }

    if (!options?.suppressBuyerSessionExpiry && shouldClearBuyerSessionTokenBeforeCheckoutRetry(error)) {
      clearBuyerSessionTokenState({ endpoint })
    }

    console.warn(`[checkout] Repetindo ${endpoint} sem X-Loja-Comprador-Session após falha de autenticação do comprador.`)
    return fetchApi<T>(endpoint, { ...(options ?? {}), skipBuyerSessionToken: true })
  }
}

async function fetchCheckoutApiResponse(
  endpoint: string,
  options?: FetchApiOptions
): Promise<Response> {
  try {
    return await fetchApiResponse(endpoint, options)
  } catch (error) {
    if (
      options?.skipBuyerSessionToken
      || !shouldRetryCheckoutWithoutBuyerSessionToken(error)
    ) {
      throw error
    }

    if (!options?.suppressBuyerSessionExpiry && shouldClearBuyerSessionTokenBeforeCheckoutRetry(error)) {
      clearBuyerSessionTokenState({ endpoint })
    }

    console.warn(`[checkout] Repetindo ${endpoint} sem X-Loja-Comprador-Session após falha de autenticação do comprador.`)
    return fetchApiResponse(endpoint, { ...(options ?? {}), skipBuyerSessionToken: true })
  }
}

const normalizeSiteConfigText = (value: unknown) => String(value ?? '').trim()

const normalizeSiteConfigBoolean = (value: unknown, fallback: boolean = false) => {
  if (typeof value === 'boolean') return value
  if (typeof value === 'number') return value > 0
  const normalized = String(value ?? '').trim().toLowerCase()
  if (!normalized) return fallback
  if (['1', 'true', 'yes', 'on', 'sim'].includes(normalized)) return true
  if (['0', 'false', 'no', 'off', 'nao', 'não'].includes(normalized)) return false
  return fallback
}

const normalizeSiteConfigInteger = (value: unknown) => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? Math.trunc(parsed) : 0
}

const normalizeStorefrontExperienceConfig = (value: unknown): StorefrontExperienceConfig => {
  const root = normalizeSiteConfigRecord(value)
  const resources = normalizeSiteConfigRecord(root.recursos)
  const microcopy = normalizeSiteConfigRecord(root.microtextos)
  const assets = normalizeSiteConfigRecord(root.assets)
  const pwa = normalizeSiteConfigRecord(root.pwa)
  const requiredResourceKeys = ['comunidade', 'carreiras', 'universidade', 'politicas'] as const

  if (normalizeSiteConfigInteger(root.version) !== 1 || !Array.isArray(root.navegacao)) {
    throw new Error('site_config_experience_invalid')
  }

  const normalizedResources = Object.fromEntries(requiredResourceKeys.map((key) => {
    if (typeof resources[key] !== 'boolean') throw new Error('site_config_experience_invalid')
    return [key, resources[key]]
  })) as StorefrontExperienceConfig['recursos']

  const navigation = root.navegacao.map((entry) => {
    const item = normalizeSiteConfigRecord(entry)
    const key = normalizeSiteConfigText(item.key)
    const label = normalizeSiteConfigText(item.label)
    const url = normalizeSiteConfigText(item.url)
    const order = normalizeSiteConfigInteger(item.ordem)
    if (!key || !label || !url || typeof item.ativo !== 'boolean' || !Number.isFinite(order)) {
      throw new Error('site_config_experience_invalid')
    }
    if (item.externo !== undefined && typeof item.externo !== 'boolean') {
      throw new Error('site_config_experience_invalid')
    }
    return { key, label, url, ordem: order, ativo: item.ativo, ...(item.externo === undefined ? {} : { externo: item.externo }) }
  })

  const publicBaseUrl = normalizeSiteConfigText(assets.public_base_url)
  const pwaEnabled = pwa.enabled
  const shortName = normalizeSiteConfigText(pwa.short_name)
  const backgroundColor = normalizeSiteConfigText(pwa.background_color)
  const themeColor = normalizeSiteConfigText(pwa.theme_color)
  const icon192 = normalizeSiteConfigText(pwa.icon_192_url)
  const icon512 = normalizeSiteConfigText(pwa.icon_512_url)
  const maskable = normalizeSiteConfigText(pwa.icon_maskable_512_url)
  if (
    !publicBaseUrl || typeof pwaEnabled !== 'boolean' || !shortName || !backgroundColor || !themeColor
    || (pwaEnabled && (!icon192 || !icon512))
  ) {
    throw new Error('site_config_experience_invalid')
  }

  return {
    version: 1,
    recursos: normalizedResources,
    navegacao: navigation,
    microtextos: {
      ...(normalizeSiteConfigText(microcopy.descricao_rodape) ? { descricao_rodape: normalizeSiteConfigText(microcopy.descricao_rodape) } : {}),
      ...(normalizeSiteConfigText(microcopy.copyright) ? { copyright: normalizeSiteConfigText(microcopy.copyright) } : {}),
      ...(normalizeSiteConfigText(microcopy.nome_assistente) ? { nome_assistente: normalizeSiteConfigText(microcopy.nome_assistente) } : {}),
      ...(normalizeSiteConfigText(microcopy.mensagem_whatsapp) ? { mensagem_whatsapp: normalizeSiteConfigText(microcopy.mensagem_whatsapp) } : {}),
    },
    assets: { public_base_url: publicBaseUrl },
    pwa: {
      enabled: pwaEnabled,
      short_name: shortName,
      background_color: backgroundColor,
      theme_color: themeColor,
      ...(icon192 ? { icon_192_url: icon192 } : {}),
      ...(icon512 ? { icon_512_url: icon512 } : {}),
      ...(maskable ? { icon_maskable_512_url: maskable } : {}),
    },
  }
}

const normalizeSiteConfigAtendimentoModelo = (value: unknown): StorefrontSiteConfig['atendimento_modelo'] =>
  normalizeSiteConfigText(value).toLowerCase() === 'whatsapp' ? 'whatsapp' : 'ticket'

const normalizeOptionalSiteConfigInteger = (value: unknown) => {
  const parsed = Number(value)
  if (!Number.isFinite(parsed) || parsed < 0) return null
  return Math.trunc(parsed)
}

const normalizeSiteConfigRecord = (value: unknown) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {}
  return { ...(value as Record<string, unknown>) }
}

export async function getStorefrontSiteConfig(
  lojaId: string | number | null = LOJA_VIRTUAL_DEFAULT_LOJA_ID,
  options?: StorefrontSiteConfigRequestOptions
): Promise<StorefrontSiteConfig> {
  const params = new URLSearchParams()
  const normalizedLojaId = String(lojaId ?? '').trim()
  if (/^\d+$/.test(normalizedLojaId) && Number.parseInt(normalizedLojaId, 10) > 0) {
    params.set('loja_id', normalizedLojaId)
  }

  if (options?.cacheBust !== undefined && options.cacheBust !== null && options.cacheBust !== false) {
    const cacheBustToken = options.cacheBust === true
      ? String(Date.now())
      : String(options.cacheBust).trim()
    if (cacheBustToken) {
      params.set('site_config_refresh', cacheBustToken)
    }
  }

  const response = await fetchApi<StorefrontSiteConfigApiResponse>(
    `/site/config${params.size > 0 ? `?${params.toString()}` : ''}`,
    {
      signal: options?.signal,
      headers: options?.headers,
      cache: options?.cache,
    }
  )
  const source = response ?? {}
  const expectedIdentity = STOREFRONT_RUNTIME_IDENTITY
  if (expectedIdentity) {
    if (normalizedLojaId && Number(normalizedLojaId) !== expectedIdentity.lojaId) {
      throw new Error('site_config_identity_mismatch')
    }
    assertStorefrontConfigIdentity(source, expectedIdentity)
  }
  const sourceRecord = source as Partial<StorefrontSiteConfig> & Record<string, unknown>
  const checkoutConfigSource = normalizeSiteConfigRecord(
    sourceRecord.checkout_config
    ?? sourceRecord.checkoutConfig
  )
  const mostrarPrecos = normalizeSiteConfigBoolean(sourceRecord.mostrar_precos)
  const clientePiorCenarioId = normalizeEntidadeId(
    sourceRecord.cliente_pior_cenario_id
    ?? sourceRecord.clientePiorCenarioId
    ?? sourceRecord.site_cliente_pior_cenario_id
    ?? sourceRecord.siteClientePiorCenarioId
  ) ?? null
  configureStorefrontPublicPricing({
    cliente_pior_cenario_id: clientePiorCenarioId,
  })
  const storefrontIdentitySource = expectedIdentity
    ? normalizeSiteConfigRecord(
        sourceRecord.storefront_identity
        ?? sourceRecord.storefrontIdentity
      )
    : null

  return {
    loja_id: normalizeSiteConfigInteger(sourceRecord.loja_id ?? sourceRecord.lojaId),
    global_store_id: normalizeSiteConfigText(sourceRecord.global_store_id ?? sourceRecord.globalStoreId),
    rede_lojas_menu_ativo: normalizeSiteConfigBoolean(sourceRecord.rede_lojas_menu_ativo ?? sourceRecord.redeLojasMenuAtivo),
    visitor_country_code: normalizeSiteConfigText(
      sourceRecord.visitor_country_code
      ?? sourceRecord.visitorCountryCode
    ).toUpperCase(),
    ...(storefrontIdentitySource ? {
      storefront_identity: {
        version: 1 as const,
        integration_server: normalizeSiteConfigText(
          storefrontIdentitySource.integration_server
          ?? storefrontIdentitySource.integrationServer
        ),
        loja_id: normalizeSiteConfigInteger(
          storefrontIdentitySource.loja_id
          ?? storefrontIdentitySource.lojaId
        ),
        public_url: normalizeSiteConfigText(
          storefrontIdentitySource.public_url
          ?? storefrontIdentitySource.publicUrl
        ),
      },
      experiencia_config: normalizeStorefrontExperienceConfig(
        sourceRecord.experiencia_config
        ?? sourceRecord.experienciaConfig
      ),
    } : {}),
    nome: normalizeSiteConfigText(source.nome),
    tipo: normalizeSiteConfigInteger(source.tipo),
    modelo_categoria: normalizeSiteConfigInteger(source.modelo_categoria),
    logo_url: normalizeSiteConfigText(source.logo_url),
    logo_url_escuro: normalizeSiteConfigText(source.logo_url_escuro),
    favicon_url: normalizeSiteConfigText(source.favicon_url),
    cor_primaria: normalizeSiteConfigText(source.cor_primaria),
    cor_secundaria: normalizeSiteConfigText(source.cor_secundaria),
    cor_lancamentos: normalizeSiteConfigText(source.cor_lancamentos),
    cor_acabou_chegar: normalizeSiteConfigText(source.cor_acabou_chegar),
    cor_card_preco: normalizeSiteConfigText(source.cor_card_preco),
    cor_preco: normalizeSiteConfigText(source.cor_preco),
    cor_botao_comprar: normalizeSiteConfigText(source.cor_botao_comprar),
    cor_botao_adicionar_carrinho: normalizeSiteConfigText(source.cor_botao_adicionar_carrinho),
    cor_fundo_foto_grande: normalizeSiteConfigText(source.cor_fundo_foto_grande),
    whatsapp: normalizeSiteConfigText(source.whatsapp),
    whatsapp_compras: normalizeSiteConfigText(source.whatsapp_compras),
    whatsapp_financeiro: normalizeSiteConfigText(source.whatsapp_financeiro),
    atendimento_modelo: normalizeSiteConfigAtendimentoModelo(
      sourceRecord.atendimento_modelo
      ?? sourceRecord.atendimentoModelo
      ?? sourceRecord.site_atendimento_modelo
      ?? sourceRecord.siteAtendimentoModelo
    ),
    whatsapp_suporte_tecnico: normalizeSiteConfigText(
      sourceRecord.whatsapp_suporte_tecnico
      ?? sourceRecord.whatsappSuporteTecnico
      ?? sourceRecord.site_whatsapp_suporte_tecnico
      ?? sourceRecord.siteWhatsappSuporteTecnico
    ),
    whatsapp_notificacoes_pedido: normalizeSiteConfigText(
      sourceRecord.whatsapp_notificacoes_pedido
      ?? sourceRecord.whatsappNotificacoesPedido
      ?? sourceRecord.site_whatsapp_notificacoes_pedido
      ?? sourceRecord.siteWhatsappNotificacoesPedido
    ),
    whatsapp_notificacoes_pedido_habilitado: sourceRecord.whatsapp_notificacoes_pedido_habilitado === undefined
      && sourceRecord.whatsappNotificacoesPedidoHabilitado === undefined
      ? Boolean(normalizeSiteConfigText(
          sourceRecord.whatsapp_notificacoes_pedido
          ?? sourceRecord.whatsappNotificacoesPedido
          ?? sourceRecord.site_whatsapp_notificacoes_pedido
          ?? sourceRecord.siteWhatsappNotificacoesPedido
        ))
      : normalizeSiteConfigBoolean(
          sourceRecord.whatsapp_notificacoes_pedido_habilitado
          ?? sourceRecord.whatsappNotificacoesPedidoHabilitado
        ),
    cadastro_codigo_email_habilitado: normalizeSiteConfigBoolean(
      sourceRecord.cadastro_codigo_email_habilitado
      ?? sourceRecord.cadastroCodigoEmailHabilitado
    ),
    telefone: normalizeSiteConfigText(source.telefone),
    email: normalizeSiteConfigText(source.email),
    empresa_nome: normalizeSiteConfigText(source.empresa_nome),
    empresa_documento: normalizeSiteConfigText(source.empresa_documento),
    endereco: normalizeSiteConfigText(source.endereco),
    mostrar_precos: mostrarPrecos,
    cliente_pior_cenario_id: clientePiorCenarioId,
    mostrar_menu_aplicativos: source.mostrar_menu_aplicativos === undefined
      ? true
      : normalizeSiteConfigBoolean(source.mostrar_menu_aplicativos),
    exibir_apenas_produtos_com_estoque: normalizeSiteConfigBoolean(
      sourceRecord.exibir_apenas_produtos_com_estoque
      ?? sourceRecord.site_exibir_apenas_produtos_com_estoque
    ),
    produto_sem_estoque_modo: [0, 1, 2].includes(normalizeSiteConfigInteger(source.produto_sem_estoque_modo ?? sourceRecord.site_produto_sem_estoque_modo))
      ? normalizeSiteConfigInteger(source.produto_sem_estoque_modo ?? sourceRecord.site_produto_sem_estoque_modo)
      : normalizeSiteConfigBoolean(sourceRecord.exibir_apenas_produtos_com_estoque ?? sourceRecord.site_exibir_apenas_produtos_com_estoque) ? 1 : 0,
    mostrar_produtos_sem_estoque_indisponiveis: normalizeSiteConfigBoolean(
      source.mostrar_produtos_sem_estoque_indisponiveis
    ),
    filtrar_marcas_busca: normalizeSiteConfigBoolean(source.filtrar_marcas_busca),
    filtrar_placa_modelo_busca: normalizeSiteConfigBoolean(source.filtrar_placa_modelo_busca),
    exibir_carrossel_marcas: normalizeSiteConfigBoolean(source.exibir_carrossel_marcas),
    banner_url: normalizeSiteConfigText(source.banner_url),
    redes_sociais: normalizeSiteConfigRecord(source.redes_sociais),
    busca_config: normalizeSiteConfigRecord(source.busca_config),
    scripts: normalizeSiteConfigText(source.scripts),
    textos_institucionais: normalizeSiteConfigRecord(source.textos_institucionais),
    seo_title: normalizeSiteConfigText(source.seo_title),
    seo_description: normalizeSiteConfigText(source.seo_description),
    checkout_config: {
      attempt_recovery_enabled: normalizeSiteConfigBoolean(
        checkoutConfigSource.attempt_recovery_enabled,
        true
      ),
      site_config_cache_ttl_ms: normalizeOptionalSiteConfigInteger(checkoutConfigSource.site_config_cache_ttl_ms),
    },
  }
}

export type NfeQrDocumentType = 'danfe' | 'xml' | 'boleto'

export type NfeQrEmailOptions = {
  status: 1
  numero: string
  cancelada: boolean
  documentos: Array<{
    tipo: NfeQrDocumentType
    titulo: string
    descricao: string
  }>
  destinatarios: Array<{
    destino_tipo: 'entidade' | 'contato'
    contato_id: number | null
    email_mascarado: string
  }>
  pode_enviar: boolean
  motivo?: string
}

export type NfeQrEmailSendInput = {
  documento: NfeQrDocumentType
  destino_tipo: 'entidade' | 'contato'
  contato_id: number | null
}

export type NfeQrEmailSendResult = {
  status: 1
  documento: NfeQrDocumentType
  destino_mascarado: string
  motivo: string
  aviso?: string
}

export const buildNfeQrEndpoint = (hash: string, lojaId?: number, suffix: '' | '/envio' = '') => {
  const normalizedHash = String(hash ?? '').trim().toLowerCase()
  const params = new URLSearchParams()
  const resolvedLojaId = getLojaIdValue(lojaId)
  if (resolvedLojaId) params.set('loja_id', String(resolvedLojaId))
  const query = params.toString()
  return `/loja-virtual/v1/nfe/qr/${encodeURIComponent(normalizedHash)}${suffix}${query ? `?${query}` : ''}`
}

export const getNfeQrEmailOptions = async (hash: string, lojaId?: number) =>
  fetchApi<NfeQrEmailOptions>(buildNfeQrEndpoint(hash, lojaId), {
    cache: 'no-store',
    skipBuyerSessionCredentials: true,
    skipBuyerSessionToken: true,
  })

export const sendNfeQrEmail = async (
  hash: string,
  input: NfeQrEmailSendInput,
  lojaId?: number,
) => fetchApi<NfeQrEmailSendResult>(buildNfeQrEndpoint(hash, lojaId, '/envio'), {
  method: 'POST',
  body: JSON.stringify(input),
  cache: 'no-store',
  skipBuyerSessionCredentials: true,
  skipBuyerSessionToken: true,
})

export async function getStorefrontSiteMetrics(options?: {
  signal?: AbortSignal
}): Promise<StorefrontSiteMetrics> {
  const response = await fetchApi<Partial<StorefrontSiteMetrics> | null>('/site/metricas', {
    signal: options?.signal,
  })
  const parsedTotal = Number(response?.itens_vendidos ?? 0)

  return {
    itens_vendidos: Number.isFinite(parsedTotal) && parsedTotal >= 0
      ? Math.trunc(parsedTotal)
      : 0,
    atualizado_em: response?.atualizado_em ? String(response.atualizado_em) : null,
  }
}

export async function getStorefrontManufacturerBrands(options: {
  lojaId?: number | string | null
  onlyWithLogo?: boolean
  limit?: number
  search?: string
  productSearch?: string
} = {}): Promise<StorefrontManufacturerBrand[]> {
  const params = new URLSearchParams()
  const lojaId = getLojaIdValue(typeof options.lojaId === 'number' ? options.lojaId : undefined)
    ?? getLojaIdValue()
  if (typeof lojaId === 'number' && lojaId > 0) {
    params.set('loja_id', String(lojaId))
  }

  const normalizedSearch = String(options.search ?? '').trim()
  if (normalizedSearch) {
    params.set('q', normalizedSearch)
  }

  const normalizedProductSearch = String(options.productSearch ?? '').trim()
  if (normalizedProductSearch) {
    params.set('produto_q', normalizedProductSearch)
  }

  const normalizedLimit = typeof options.limit === 'number' && Number.isFinite(options.limit)
    ? Math.max(1, Math.min(Math.trunc(options.limit), 60))
    : 24
  params.set('limit', String(normalizedLimit))

  if (options.onlyWithLogo !== false) {
    params.set('somente_com_logo', '1')
  }

  const cacheKey = params.toString()
  const cached = getStorefrontManufacturerBrandsCacheEntry(cacheKey)
  if (cached) {
    return cached.items
  }

  const inFlight = storefrontManufacturerBrandsRequestCache.get(cacheKey)
  if (inFlight) {
    return inFlight
  }

  const ttlMs = normalizedSearch || normalizedProductSearch
    ? STOREFRONT_MANUFACTURER_BRANDS_SEARCH_CACHE_TTL_MS
    : STOREFRONT_MANUFACTURER_BRANDS_CACHE_TTL_MS

  const request = (async () => {
    try {
      const response = await fetchApi<LojaVirtualMarcasResponse>(`/loja-virtual/v1/marcas?${cacheKey}`)
      const items = Array.isArray(response.items)
        ? response.items
          .map((item) => {
            const id = Number(item.id ?? 0)
            if (!Number.isFinite(id) || id <= 0) return null
            const name = String(item.nome ?? '').trim()
            if (!name) return null
            return {
              id: Math.trunc(id),
              name,
              logoUrl: normalizeImageUrl(item.logo_url) || null,
              productCount: Number.isFinite(Number(item.total_produtos))
                ? Math.max(0, Math.trunc(Number(item.total_produtos)))
                : 0,
            } satisfies StorefrontManufacturerBrand
          })
          .filter((item): item is StorefrontManufacturerBrand => Boolean(item))
        : []

      setCachedStorefrontManufacturerBrands(cacheKey, items, ttlMs)
      return cloneStorefrontManufacturerBrands(items)
    } catch (error) {
      const staleCache = getStorefrontManufacturerBrandsCacheEntry(cacheKey, true)
      if (staleCache) {
        return staleCache.items
      }

      throw error
    } finally {
      storefrontManufacturerBrandsRequestCache.delete(cacheKey)
    }
  })()

  storefrontManufacturerBrandsRequestCache.set(cacheKey, request)
  return request
}

type StorefrontVehicleOptionsResponse = {
  status?: number | string
  items?: unknown[]
  motivo?: string
  message?: string
}

type StorefrontVehiclePlateResponse = Partial<StorefrontVehiclePlate> & {
  status?: number | string
  motivo?: string
  message?: string
}

const normalizeStorefrontVehicleOption = (value: unknown): StorefrontVehicleOption | null => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  const record = value as Record<string, unknown>
  const code = String(record.code ?? record.codigo ?? record.id ?? '').trim()
  const name = String(record.name ?? record.nome ?? record.label ?? '').trim()
  if (!code || !name) return null
  return { id: code, code, name }
}

const normalizeStorefrontVehicleOptionsResponse = (response: StorefrontVehicleOptionsResponse) => {
  if (Number(response.status ?? 1) === 0) {
    throw new Error(String(response.motivo || response.message || 'vehicle_lookup_failed'))
  }

  return Array.isArray(response.items)
    ? response.items
      .map(normalizeStorefrontVehicleOption)
      .filter((item): item is StorefrontVehicleOption => Boolean(item))
    : []
}

const appendVehicleTypeParams = (
  params: URLSearchParams,
  options?: { vehicleType?: string; reference?: string | number | null; lojaId?: number }
) => {
  const lojaId = getLojaIdValue(options?.lojaId)
  if (lojaId) params.set('loja_id', lojaId.toString())
  const vehicleType = String(options?.vehicleType ?? '').trim()
  if (vehicleType) params.set('vehicleType', vehicleType)
  const reference = String(options?.reference ?? '').trim()
  if (reference) params.set('reference', reference)
}

async function fetchStorefrontVehicleOptionsWithIntegrationFallback(
  endpoint: string
): Promise<StorefrontVehicleOptionsResponse> {
  const publicRequestOptions = {
    skipIntegrationAuth: true,
    skipBuyerSessionToken: true,
  } satisfies FetchApiOptions

  try {
    return await fetchApi<StorefrontVehicleOptionsResponse>(endpoint, publicRequestOptions)
  } catch (error) {
    if (!isIntegrationRouteNotAllowedError(error) && !isAuthenticationFailureError(error)) throw error
    return fetchApi<StorefrontVehicleOptionsResponse>(endpoint, { skipBuyerSessionToken: true })
  }
}

async function fetchStorefrontVehiclePlateWithIntegrationFallback(
  endpoint: string
): Promise<StorefrontVehiclePlateResponse> {
  const publicRequestOptions = {
    cache: 'no-store',
    skipIntegrationAuth: true,
    skipBuyerSessionToken: true,
  } satisfies FetchApiOptions

  try {
    return await fetchApi<StorefrontVehiclePlateResponse>(endpoint, publicRequestOptions)
  } catch (error) {
    if (!isIntegrationRouteNotAllowedError(error) && !isAuthenticationFailureError(error)) throw error
    return fetchApi<StorefrontVehiclePlateResponse>(endpoint, { cache: 'no-store', skipBuyerSessionToken: true })
  }
}

export async function getStorefrontVehicleBrands(options?: {
  vehicleType?: string
  reference?: string | number | null
  lojaId?: number
}): Promise<StorefrontVehicleOption[]> {
  const params = new URLSearchParams()
  appendVehicleTypeParams(params, options)
  const response = await fetchStorefrontVehicleOptionsWithIntegrationFallback(
    `/loja-virtual/v1/veiculos/marcas${params.size > 0 ? `?${params.toString()}` : ''}`
  )
  return normalizeStorefrontVehicleOptionsResponse(response)
}

export async function getStorefrontVehicleModels(
  brandId: string | number,
  options?: {
    vehicleType?: string
    reference?: string | number | null
    lojaId?: number
  }
): Promise<StorefrontVehicleOption[]> {
  const normalizedBrandId = String(brandId ?? '').trim()
  if (!normalizedBrandId) return []
  const params = new URLSearchParams()
  appendVehicleTypeParams(params, options)
  const response = await fetchStorefrontVehicleOptionsWithIntegrationFallback(
    `/loja-virtual/v1/veiculos/marcas/${encodeURIComponent(normalizedBrandId)}/modelos${params.size > 0 ? `?${params.toString()}` : ''}`
  )
  return normalizeStorefrontVehicleOptionsResponse(response)
}

export async function getStorefrontVehicleYears(
  brandId: string | number,
  modelId: string | number,
  options?: {
    vehicleType?: string
    reference?: string | number | null
    lojaId?: number
  }
): Promise<StorefrontVehicleOption[]> {
  const normalizedBrandId = String(brandId ?? '').trim()
  const normalizedModelId = String(modelId ?? '').trim()
  if (!normalizedBrandId || !normalizedModelId) return []
  const params = new URLSearchParams()
  appendVehicleTypeParams(params, options)
  const response = await fetchStorefrontVehicleOptionsWithIntegrationFallback(
    `/loja-virtual/v1/veiculos/marcas/${encodeURIComponent(normalizedBrandId)}/modelos/${encodeURIComponent(normalizedModelId)}/anos${params.size > 0 ? `?${params.toString()}` : ''}`
  )
  return normalizeStorefrontVehicleOptionsResponse(response)
}

export async function resolveStorefrontVehicleByPlate(plate: string): Promise<StorefrontVehiclePlate> {
  const normalizedPlate = String(plate ?? '').trim()
  if (!normalizedPlate) {
    throw new Error('vehicle_plate_required')
  }

  const params = new URLSearchParams()
  const lojaId = getLojaIdValue()
  if (lojaId) params.set('loja_id', lojaId.toString())
  const response = await fetchStorefrontVehiclePlateWithIntegrationFallback(
    `/loja-virtual/v1/veiculos/placa/${encodeURIComponent(normalizedPlate)}${params.size > 0 ? `?${params.toString()}` : ''}`
  )

  if (Number(response.status ?? 1) === 0) {
    throw new Error(String(response.motivo || response.message || 'vehicle_lookup_failed'))
  }

  const vehicle = {
    placa: String(response.placa ?? normalizedPlate).trim().toLocaleUpperCase('pt-BR'),
    marca: String(response.marca ?? '').trim(),
    modelo: String(response.modelo ?? '').trim(),
    ano: String(response.ano ?? '').trim(),
    versao: String(response.versao ?? '').trim(),
  }

  if (!vehicle.placa || (!vehicle.marca && !vehicle.modelo && !vehicle.ano)) {
    throw new Error('vehicle_plate_not_found')
  }

  return vehicle
}

export type TransportadoraLogoFetchResult =
  | { status: 'ok'; response: Response }
  | { status: 'missing' }
  | { status: 'not-modified' }

export async function fetchTransportadoraLogoUrls(
  transportadoraIds: number[]
): Promise<Record<number, string>> {
  const normalizedIds = Array.from(new Set(
    transportadoraIds
      .map((value) => Math.trunc(value))
      .filter((value) => Number.isFinite(value) && value > 0)
  )).sort((left, right) => left - right)

  if (normalizedIds.length === 0) {
    return {}
  }

  const params = new URLSearchParams({
    ids: normalizedIds.join(','),
    server: INTEGRATION_PUBLIC_SERVER,
  })
  const response = await fetchApi<{
    logos?: Array<{
      transportadora_id?: number | string | null
      url?: string | null
    }>
  }>(`/docs/transportadoras/logos?${params.toString()}`)

  return (response.logos ?? []).reduce<Record<number, string>>((result, logo) => {
    const id = Math.trunc(Number(logo.transportadora_id ?? 0))
    const url = String(logo.url ?? '').trim()
    if (id > 0 && url) {
      result[id] = url
    }
    return result
  }, {})
}

export async function fetchTransportadoraLogoResponse(
  transportadoraId: number,
  options?: {
    ifNoneMatch?: string | null
    ifModifiedSince?: string | null
  },
  retryAfterAuthFailure: boolean = true
): Promise<TransportadoraLogoFetchResult> {
  const normalizedTransportadoraId = Math.trunc(transportadoraId)
  if (normalizedTransportadoraId <= 0) {
    return { status: 'missing' }
  }

  const endpoint = `/docs/transportadoras/${normalizedTransportadoraId}/logo?server=${encodeURIComponent(INTEGRATION_PUBLIC_SERVER)}`
  const headers = buildRequestHeaders({
    accept: 'image/*',
  })
  headers.delete('Content-Type')
  const normalizedIfNoneMatch = options?.ifNoneMatch?.trim()
  const normalizedIfModifiedSince = options?.ifModifiedSince?.trim()

  if (normalizedIfNoneMatch) {
    headers.set('If-None-Match', normalizedIfNoneMatch)
  }

  if (normalizedIfModifiedSince) {
    headers.set('If-Modified-Since', normalizedIfModifiedSince)
  }

  if (SHOULD_USE_INTEGRATION_AUTH && !headers.has('Authorization')) {
    const authorization = await getIntegrationAuthorizationHeader()
    headers.set('Authorization', authorization)
  }

  let response: Response
  try {
    response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'GET',
      headers,
    })
  } catch (error) {
    throw normalizeError(error)
  }

  if (response.status === 401 && SHOULD_USE_INTEGRATION_AUTH && retryAfterAuthFailure) {
    integrationTokenState = null
    integrationTokenFailureState = null
    const renewedAuthorization = await getIntegrationAuthorizationHeader(true)
    headers.set('Authorization', renewedAuthorization)

    let renewedResponse: Response
    try {
      renewedResponse = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'GET',
        headers,
      })
    } catch (error) {
      throw normalizeError(error)
    }

    if (renewedResponse.status === 304) {
      return { status: 'not-modified' }
    }

    if (renewedResponse.status === 404 || isBackendUnavailableStatus(renewedResponse.status)) {
      return { status: 'missing' }
    }

    if (!renewedResponse.ok) {
      const responsePayload = await parseJsonSafe(renewedResponse)
      const detail = extractErrorMessage(responsePayload)
      throw new Error(`API error: ${renewedResponse.status}${detail ? ` - ${detail}` : ''}`)
    }

    return {
      status: 'ok',
      response: renewedResponse,
    }
  }

  if (response.status === 304) {
    return { status: 'not-modified' }
  }

  if (response.status === 404 || isBackendUnavailableStatus(response.status)) {
    return { status: 'missing' }
  }

  if (!response.ok) {
    const responsePayload = await parseJsonSafe(response)
    const detail = extractErrorMessage(responsePayload)

    if (retryAfterAuthFailure && shouldRetryProxyManagedIntegrationAuth(detail)) {
      await wait(PROXY_INTEGRATION_AUTH_RETRY_DELAY_MS)
      return fetchTransportadoraLogoResponse(transportadoraId, options, false)
    }

    throw new Error(`API error: ${response.status}${detail ? ` - ${detail}` : ''}`)
  }

  return {
    status: 'ok',
    response,
  }
}

export async function fetchTransportadoraLogo(transportadoraId: number): Promise<Blob | null> {
  const result = await fetchTransportadoraLogoResponse(transportadoraId)
  if (result.status !== 'ok') {
    return null
  }

  return result.response.blob()
}

export type ClienteLogoFetchResult =
  | { status: 'ok'; response: Response }
  | { status: 'missing' }

export async function fetchClienteLogoResponse(
  clienteId: number,
  options?: {
    version?: string | number | null
  },
  retryAfterAuthFailure: boolean = true
): Promise<ClienteLogoFetchResult> {
  const normalizedClienteId = Math.trunc(clienteId)
  if (normalizedClienteId <= 0) {
    return { status: 'missing' }
  }

  const params = new URLSearchParams()
  if (options?.version !== null && options?.version !== undefined) {
    const normalizedVersion = String(options.version).trim()
    if (normalizedVersion) {
      params.set('v', normalizedVersion)
    }
  }

  const query = params.toString()
  const endpoint = `/clientes/${normalizedClienteId}/logo${query ? `?${query}` : ''}`
  const headers = buildRequestHeaders({
    accept: 'image/*',
  })
  headers.delete('Content-Type')

  if (SHOULD_USE_INTEGRATION_AUTH && !headers.has('Authorization')) {
    const authorization = await getIntegrationAuthorizationHeader()
    headers.set('Authorization', authorization)
  }

  let response: Response
  try {
    response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'GET',
      credentials: 'include',
      headers,
    })
  } catch (error) {
    markBackendUnavailable(error)
    throw normalizeError(error)
  }

  if (isBackendUnavailableStatus(response.status)) {
    markBackendUnavailable(new Error(`API error: ${response.status}`))
  } else {
    markBackendAvailable()
  }

  if (response.status === 401 && SHOULD_USE_INTEGRATION_AUTH && retryAfterAuthFailure) {
    integrationTokenState = null
    integrationTokenFailureState = null
    const renewedAuthorization = await getIntegrationAuthorizationHeader(true)
    headers.set('Authorization', renewedAuthorization)

    let renewedResponse: Response
    try {
      renewedResponse = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'GET',
        credentials: 'include',
        headers,
      })
    } catch (error) {
      markBackendUnavailable(error)
      throw normalizeError(error)
    }

    if (isBackendUnavailableStatus(renewedResponse.status)) {
      markBackendUnavailable(new Error(`API error: ${renewedResponse.status}`))
    } else {
      markBackendAvailable()
    }

    if (renewedResponse.status === 404) {
      return { status: 'missing' }
    }

    if (!renewedResponse.ok) {
      const responsePayload = await parseJsonSafe(renewedResponse)
      const detail = extractErrorMessage(responsePayload)
      throw new Error(`API error: ${renewedResponse.status}${detail ? ` - ${detail}` : ''}`)
    }

    return {
      status: 'ok',
      response: renewedResponse,
    }
  }

  if (response.status === 404) {
    return { status: 'missing' }
  }

  if (!response.ok) {
    const responsePayload = await parseJsonSafe(response)
    const detail = extractErrorMessage(responsePayload)

    if (retryAfterAuthFailure && shouldRetryProxyManagedIntegrationAuth(detail)) {
      await wait(PROXY_INTEGRATION_AUTH_RETRY_DELAY_MS)
      return fetchClienteLogoResponse(clienteId, options, false)
    }

    throw new Error(`API error: ${response.status}${detail ? ` - ${detail}` : ''}`)
  }

  return {
    status: 'ok',
    response,
  }
}

export async function fetchClienteLogo(
  clienteId: number,
  options?: {
    version?: string | number | null
  }
): Promise<Blob | null> {
  const result = await fetchClienteLogoResponse(clienteId, options)
  if (result.status !== 'ok') {
    return null
  }

  const blob = await result.response.blob()
  if (await isImageBlob(blob)) {
    return blob
  }

  const logoUrl = await resolveLogoUrlFromBlob(blob)
  if (!logoUrl) {
    return null
  }

  const headers = buildRequestHeaders({
    accept: 'image/*',
  })
  headers.delete('Content-Type')

  try {
    const response = await fetch(resolveLogoAssetUrl(logoUrl), {
      method: 'GET',
      credentials: 'include',
      headers,
    })

    if (!response.ok) return null

    const resolvedBlob = await response.blob()
    return await isImageBlob(resolvedBlob) ? resolvedBlob : null
  } catch {
    return null
  }
}

async function isImageBlob(blob: Blob | null | undefined): Promise<boolean> {
  if (!blob || blob.size <= 0) return false

  const contentType = blob.type.toLowerCase()
  if (contentType.startsWith('image/')) return true
  if (contentType && contentType !== 'application/octet-stream') return false

  const signature = new Uint8Array(await blob.slice(0, 16).arrayBuffer())
  if (signature.length < 4) return false

  const isPng = signature[0] === 0x89 && signature[1] === 0x50 && signature[2] === 0x4e && signature[3] === 0x47
  const isJpeg = signature[0] === 0xff && signature[1] === 0xd8
  const isGif = signature[0] === 0x47 && signature[1] === 0x49 && signature[2] === 0x46
  const isWebp = signature[0] === 0x52 && signature[1] === 0x49 && signature[2] === 0x46 && signature[3] === 0x46
    && signature[8] === 0x57 && signature[9] === 0x45 && signature[10] === 0x42 && signature[11] === 0x50
  const isBmp = signature[0] === 0x42 && signature[1] === 0x4d

  return isPng || isJpeg || isGif || isWebp || isBmp
}

async function resolveLogoUrlFromBlob(blob: Blob): Promise<string> {
  try {
    const payloadText = (await blob.text()).trim()
    if (!payloadText) return ''

    if (/^(https?:)?\/\//i.test(payloadText) || payloadText.startsWith('/') || payloadText.startsWith('data:image/')) {
      return payloadText
    }

    const payload = JSON.parse(payloadText)
    if (!payload || typeof payload !== 'object') return ''

    const record = payload as Record<string, unknown>
    const candidates = [
      record.logo,
      record.logoUrl,
      record.logo_url,
      record.url,
      record.arquivo,
      record.path,
    ]

    for (const candidate of candidates) {
      if (typeof candidate === 'string' && candidate.trim()) {
        return candidate.trim()
      }
    }
  } catch {
    return ''
  }

  return ''
}

function resolveLogoAssetUrl(value: string): string {
  const normalized = normalizeImageUrl(value)
  if (!normalized) return ''
  if (normalized.startsWith('data:image/')) return normalized
  if (/^(https?:)?\/\//i.test(normalized)) return normalized
  return `${API_BASE_URL}${normalized.startsWith('/') ? normalized : `/${normalized}`}`
}

export async function uploadClienteLogo(
  clienteId: number,
  file: File | Blob
): Promise<{
  id: number | null
  logoUrl: string | null
  message: string
}> {
  const normalizedClienteId = Math.trunc(clienteId)
  if (normalizedClienteId <= 0) {
    throw new Error('Cliente inválido para atualização da logo.')
  }

  const formData = new FormData()
  formData.append('file', file)

  const response = await fetchApi<any>(`/clientes/${normalizedClienteId}/logo`, {
    method: 'POST',
    body: formData,
  })

  const rawId = response?.id
  const parsedId = typeof rawId === 'number'
    ? (Number.isFinite(rawId) ? Math.trunc(rawId) : null)
    : typeof rawId === 'string'
      ? (() => {
        const normalized = rawId.trim()
        if (!/^\d+$/.test(normalized)) return null
        const parsed = Number.parseInt(normalized, 10)
        return Number.isFinite(parsed) ? parsed : null
      })()
      : null

  return {
    id: parsedId,
    logoUrl: typeof response?.logo === 'string' && response.logo.trim() ? response.logo.trim() : null,
    message: response?.message || response?.motivo || 'Logo atualizada com sucesso.',
  }
}

export async function checkHealth(): Promise<{ status: string; timestamp: string }> {
  await fetchApi<LojaVirtualProdutosResponse>('/loja-virtual/v1/produtos/lancamentos?limit=1&offset=0')
  return {
    status: 'ok',
    timestamp: new Date().toISOString(),
  }
}

export async function ingestLojaVirtualAnalyticsEvents(
  payload: LojaVirtualAnalyticsBatchRequest,
  options?: FetchApiOptions
): Promise<LojaVirtualAnalyticsBatchResponse> {
  if (!Array.isArray(payload.events) || payload.events.length === 0) {
    return {
      status: 1,
      total: 0,
      inserted: 0,
      skipped: 0,
      motivo: 'Nenhum evento informado.',
    }
  }

  const lojaId = getLojaIdValue(payload.loja_id)
  const normalizedPayload: LojaVirtualAnalyticsBatchRequest = {
    ...payload,
    ...(typeof lojaId === 'number' ? { loja_id: lojaId } : {}),
    events: payload.events,
  }

  return fetchApi<LojaVirtualAnalyticsBatchResponse>(
    '/loja-virtual/v1/analytics/eventos',
    {
      method: 'POST',
      body: JSON.stringify(normalizedPayload),
      ...options,
    }
  )
}

export function resolveLojaVirtualRequestLanguage(): 'pt' | 'es' | 'en' | 'zh' {
  const candidates: unknown[] = []
  try {
    candidates.push(globalThis.localStorage?.getItem('i18nextLng'))
  } catch {
    // Ambientes com storage bloqueado continuam usando o idioma do navegador.
  }
  if (typeof globalThis.navigator !== 'undefined') {
    candidates.push(globalThis.navigator.language)
  }

  for (const candidate of candidates) {
    const language = String(candidate ?? '').trim().toLowerCase().split(/[-_]/)[0]
    if (language === 'es' || language === 'en' || language === 'zh') return language
    if (language === 'pt') return 'pt'
  }
  return 'pt'
}

function appendLojaVirtualListParams(
  params: URLSearchParams,
  options: {
    limit: number
    offset: number
    loja_id?: number
    entidade_id?: number | null
    includeAuthenticatedEntidadeId?: boolean
    onlyAvailable?: boolean
  }
) {
  params.set('limit', options.limit.toString())
  params.set('offset', options.offset.toString())
  params.set('idioma', resolveLojaVirtualRequestLanguage())

  const lojaId = getLojaIdValue(options.loja_id)
  if (lojaId) params.set('loja_id', lojaId.toString())

  appendLojaVirtualCatalogPricingParams(params, {
    explicitEntidadeId: options.entidade_id,
    includeAuthenticatedEntidadeId: options.includeAuthenticatedEntidadeId,
  })

  if (options.onlyAvailable) {
    params.set('somente_disponiveis', '1')
  }
}

type LojaVirtualCatalogPricingResolution = {
  entidadeId?: number
  publicPricing: boolean
}

function resolveLojaVirtualCatalogPricing(options: {
  explicitEntidadeId?: unknown
  includeAuthenticatedEntidadeId?: boolean
} = {}): LojaVirtualCatalogPricingResolution {
  const publicEntidadeId = getStorefrontPublicPricingEntidadeId()
  if (isSimplifiedStorefrontEnabled() && publicEntidadeId) {
    return { entidadeId: publicEntidadeId, publicPricing: true }
  }

  const explicitEntidadeId = normalizeEntidadeId(options.explicitEntidadeId)
  if (explicitEntidadeId) {
    return { entidadeId: explicitEntidadeId, publicPricing: false }
  }

  const authenticatedEntidadeId = options.includeAuthenticatedEntidadeId === false
    ? undefined
    : getAuthenticatedEntidadeId()
  if (authenticatedEntidadeId) {
    return { entidadeId: authenticatedEntidadeId, publicPricing: false }
  }

  return publicEntidadeId
    ? { entidadeId: publicEntidadeId, publicPricing: true }
    : { publicPricing: false }
}

function appendLojaVirtualCatalogPricingParams(
  params: URLSearchParams,
  options: {
    explicitEntidadeId?: unknown
    includeAuthenticatedEntidadeId?: boolean
  } = {}
) {
  // Detalhe, variações e demais respostas de catálogo precisam usar o mesmo
  // idioma da busca. Sem este parâmetro, apenas os cards retornados pela busca
  // eram localizados e os endpoints de detalhe voltavam ao nome original.
  if (!params.has('idioma')) {
    params.set('idioma', resolveLojaVirtualRequestLanguage())
  }
  const pricing = resolveLojaVirtualCatalogPricing(options)
  if (pricing.entidadeId) {
    params.set('entidade_id', pricing.entidadeId.toString())
  }
  if (pricing.publicPricing) {
    params.set('preco_aberto', '1')
  }
}

function isLojaVirtualWildcardOnlyQuery(value: string): boolean {
  return /^[%_]+$/.test(value.trim())
}

function resolveLojaVirtualDefaultProductQuery(): string {
  const normalizedQuery = LOJA_VIRTUAL_PRODUCTS_DEFAULT_QUERY.trim()
  if (!normalizedQuery || isLojaVirtualWildcardOnlyQuery(normalizedQuery)) {
    return ''
  }

  return normalizedQuery
}

export async function getProducts(
  limit: number = 20,
  offset: number = 0,
  options: {
    onlyAvailable?: boolean
    signal?: AbortSignal
  } = {}
): Promise<Product[]> {
  const targetLimit = Math.max(1, Math.trunc(limit))
  const collected: Product[] = []
  const seenIds = new Set<string>()
  let nextOffset = Math.max(0, Math.trunc(offset))

  while (collected.length < targetLimit) {
    const page = await searchProductsPage('', {
      limit: targetLimit - collected.length,
      offset: nextOffset,
      onlyAvailable: options.onlyAvailable,
      signal: options.signal,
    })

    for (const product of page.items) {
      if (seenIds.has(product.id)) continue
      seenIds.add(product.id)
      collected.push(product)
      if (collected.length >= targetLimit) break
    }

    const rawNextOffset = page.offset + page.items.length
    const reachedTotal = page.total !== null && rawNextOffset >= page.total
    const exhaustedByShortPage = page.items.length < Math.max(1, page.limit)
    if (page.items.length === 0 || reachedTotal || exhaustedByShortPage || rawNextOffset <= nextOffset) {
      break
    }
    nextOffset = rawNextOffset
  }

  return collected
}

function normalizeLojaVirtualNumericId(value: unknown): string | null {
  const normalizedValue = Number(value ?? 0)
  if (!Number.isFinite(normalizedValue) || normalizedValue <= 0) {
    return null
  }

  return String(Math.trunc(normalizedValue))
}

function resolveLojaVirtualDetailParentId(detail?: LojaVirtualProdutoDetalhe | null): string | null {
  if (!detail) return null

  const detailId = normalizeLojaVirtualNumericId(detail.id)
  const parentProductId = normalizeLojaVirtualNumericId(detail.produto_principal_variacao)

  if (!parentProductId || parentProductId === detailId) {
    return null
  }

  return parentProductId
}

/**
 * Fetches full detail for a specific variation (including photos, videos, specs).
 *
 * Strategy:
 * 1. Call /loja-virtual/v1/variacoes/{variationId} — the dedicated variation endpoint.
 *    Handles both { variacao: ... } and { produto: ... } response shapes.
 * 2. Fallback: /loja-virtual/v1/produtos/{variationId} — may return the variation's
 *    own detail if it has a product page; validates the returned ID matches.
 * 3. Fallback: /loja-virtual/v1/produtos/{parentId} — fetch parent and extract the
 *    matching variation from response.produto.variacoes.
 * 4. If everything fails, throw so the caller falls back to the list data.
 */
export async function getVariationDetail(variationId: string, parentId: string): Promise<Product> {
  const syntheticBoxVariation = parseStorefrontBoxVariantId(variationId)
  if (syntheticBoxVariation) {
    const baseVariation = await getVariationDetail(syntheticBoxVariation.baseProductId, parentId)
    return buildStorefrontPurchaseOptionProduct(baseVariation, syntheticBoxVariation.quantity)
  }

  const params = new URLSearchParams()
  const lojaId = getLojaIdValue()
  if (lojaId) params.set('loja_id', lojaId.toString())
  appendLojaVirtualCatalogPricingParams(params)
  const suffix = params.toString() ? `?${params.toString()}` : ''

  const numericVariationId = Number.parseInt(variationId.trim(), 10)
  const numericParentId = Number.parseInt(parentId.trim(), 10)
  const expectedParentId = Number.isFinite(numericParentId) && numericParentId > 0
    ? String(Math.trunc(numericParentId))
    : null

  if (!Number.isFinite(numericVariationId) || numericVariationId <= 0) {
    throw new Error(`Invalid variation ID: ${variationId}`)
  }

  let dedicatedVariationDetail: LojaVirtualVariacaoDetalhe | null = null

  // Step 1: dedicated variation endpoint
  try {
    const response = await fetchApi<LojaVirtualVariacaoDetalheResponse>(
      `/loja-virtual/v1/variacoes/${numericVariationId}${suffix}`
    )
    if (isLojaVirtualVariationDetail(response.variacao) && String(response.variacao.produto_id) === variationId) {
      dedicatedVariationDetail = response.variacao
      const responseParentId = normalizeLojaVirtualNumericId(response.produto?.id)
      if (!expectedParentId || !responseParentId || responseParentId === expectedParentId) {
        return mapLojaVirtualVariationDetailToProduct(response.variacao, response.produto)
      }
    }
    if (isLojaVirtualProductDetail(response.variacao) && String(response.variacao.id) === variationId) {
      const responseParentId = resolveLojaVirtualDetailParentId(response.variacao)
      if (!expectedParentId || (responseParentId && responseParentId === expectedParentId)) {
        return mapLojaVirtualProductDetailToProduct(response.variacao)
      }
    }
    if (isLojaVirtualProductDetail(response.produto) && String(response.produto.id) === variationId) {
      const responseParentId = resolveLojaVirtualDetailParentId(response.produto)
      if (!expectedParentId || (responseParentId && responseParentId === expectedParentId)) {
        return mapLojaVirtualProductDetailToProduct(response.produto)
      }
    }
  } catch (error) {
    console.warn(`getVariationDetail: /variacoes/${variationId} failed:`, error)
  }

  // Step 2: product endpoint — only use if ID matches (variation has its own product page)
  try {
    const response = await fetchApi<LojaVirtualProdutoDetalheResponse>(
      `/loja-virtual/v1/produtos/${numericVariationId}${suffix}`
    )
    if (response.produto?.id && String(response.produto.id) === variationId) {
      const parentProductId = Number(response.produto.produto_principal_variacao ?? 0)
      const resolvedParentProductId = Number.isFinite(parentProductId) && parentProductId > 0
        ? String(Math.trunc(parentProductId))
        : null
      if (expectedParentId && resolvedParentProductId && resolvedParentProductId !== expectedParentId) {
        // Ignore standalone variation detail that points to a different parent than the one requested.
      } else if (Number.isFinite(parentProductId) && parentProductId > 0) {
        try {
          const parentResponse = await fetchApi<LojaVirtualProdutoDetalheResponse>(
            `/loja-virtual/v1/produtos/${Math.trunc(parentProductId)}${suffix}`
          )
          const variacoes = Array.isArray(parentResponse.produto?.variacoes)
            ? parentResponse.produto!.variacoes!
            : []
          const match = variacoes.find((v) => String(v.produto_id) === variationId)
          if (match) {
            if (dedicatedVariationDetail) {
              return mapLojaVirtualVariationDetailToProduct(dedicatedVariationDetail, parentResponse.produto)
            }
            return mapLojaVirtualProductToProduct(match, {
              parentProductId: String(parentResponse.produto?.id ?? ''),
              parentProductName: resolveParentProductBaseName(parentResponse.produto),
              usarSeletorVariacao: Number(parentResponse.produto?.usar_seletor_variacao ?? 0) === 1,
              variationSelectorConfig: parentResponse.produto?.configuracoes_variacao,
            })
          }
        } catch (parentError) {
          console.warn(`getVariationDetail: parent /produtos/${parentProductId} from variation ${variationId} failed:`, parentError)
        }
      } else if (!expectedParentId) {
        return mapLojaVirtualProductDetailToProduct(response.produto)
      }
    }
    // Parent returned — try to extract variation from its variacoes list
    if (response.produto && (!expectedParentId || String(response.produto.id) === expectedParentId)) {
      const variacoes = Array.isArray(response.produto.variacoes) ? response.produto.variacoes : []
      const match = variacoes.find((v) => String(v.produto_id) === variationId)
      if (match) {
        if (dedicatedVariationDetail) {
          return mapLojaVirtualVariationDetailToProduct(dedicatedVariationDetail, response.produto)
        }
        return mapLojaVirtualProductToProduct(match, {
          parentProductId: String(response.produto.id ?? ''),
          parentProductName: resolveParentProductBaseName(response.produto),
          usarSeletorVariacao: Number(response.produto.usar_seletor_variacao ?? 0) === 1,
          variationSelectorConfig: response.produto.configuracoes_variacao,
        })
      }
    }
  } catch (error) {
    console.warn(`getVariationDetail: /produtos/${variationId} failed:`, error)
  }

  // Step 3: fetch parent and extract variation from its list
  if (Number.isFinite(numericParentId) && numericParentId > 0) {
    try {
      const parentResponse = await fetchApi<LojaVirtualProdutoDetalheResponse>(
        `/loja-virtual/v1/produtos/${numericParentId}${suffix}`
      )
      const variacoes = Array.isArray(parentResponse.produto?.variacoes)
        ? parentResponse.produto!.variacoes!
        : []
      const match = variacoes.find((v) => String(v.produto_id) === variationId)
      if (match) {
        if (dedicatedVariationDetail) {
          return mapLojaVirtualVariationDetailToProduct(dedicatedVariationDetail, parentResponse.produto)
        }
        return mapLojaVirtualProductToProduct(match, {
          parentProductId: String(parentResponse.produto?.id ?? ''),
          parentProductName: resolveParentProductBaseName(parentResponse.produto),
          usarSeletorVariacao: Number(parentResponse.produto?.usar_seletor_variacao ?? 0) === 1,
          variationSelectorConfig: parentResponse.produto?.configuracoes_variacao,
        })
      }
    } catch (error) {
      console.warn(`getVariationDetail: parent /produtos/${parentId} failed:`, error)
    }
  }

  throw new Error(`Variation ${variationId} not found.`)
}

// Dedupe de getProductById em voo por id. A página de produto dispara o mesmo
// id a partir de efeitos distintos (cascata de "compre junto"/variações), e
// cada chamada roda os fallbacks sequenciais (produtos/{id} -> buscar ->
// variacoes). Memoizar a Promise em andamento colapsa essas chamadas
// concorrentes idênticas numa só, sem alterar a lógica de fallback.
const getProductByIdRequest = new Map<string, Promise<Product>>()

export async function getProductById(id: string): Promise<Product> {
  const normalizedId = id.trim()
  const requestLanguage = resolveLojaVirtualRequestLanguage()
  const requestKey = `${normalizedId}|${requestLanguage}`

  const pending = getProductByIdRequest.get(requestKey)
  if (pending) {
    return pending
  }

  const request = getProductByIdInner(id, normalizedId).finally(() => {
    getProductByIdRequest.delete(requestKey)
  })
  getProductByIdRequest.set(requestKey, request)
  return request
}

async function getProductByIdInner(id: string, normalizedId: string): Promise<Product> {
  const numericId = /^\d+$/.test(normalizedId) ? Number.parseInt(normalizedId, 10) : Number.NaN
  const searchProductById = async (includeAuthenticatedEntidadeId: boolean): Promise<Product | null> => {
    const params = new URLSearchParams({ q: normalizedId })
    appendLojaVirtualListParams(params, {
      limit: 20,
      offset: 0,
      includeAuthenticatedEntidadeId,
    })

    const response = await fetchApi<LojaVirtualProdutosResponse>(
      `/loja-virtual/v1/produtos/buscar?${params}`
    )
    const mapped = mapLojaVirtualProducts(response.items)
    const exactMatches = mapped.filter((item) => item.id === normalizedId)

    if (exactMatches.length > 0) {
      const standaloneMatch = exactMatches.find((item) => !item.parentProductId)
      if (standaloneMatch) return standaloneMatch

      const parentMatch = exactMatches.find((item) => item.hasVariations)
      if (parentMatch) return parentMatch

      return exactMatches[0]
    }

    if (!Number.isFinite(numericId) && mapped.length > 0) return mapped[0]
    return null
  }

  const searchProductByIdWithRetry = async (): Promise<Product | null> => {
    try {
      return await searchProductById(true)
    } catch (error) {
      if (shouldRetryLojaVirtualWithoutEntidade(error)) {
        try {
          return await searchProductById(false)
        } catch (retryError) {
          console.warn(`Falha ao buscar produto ${normalizedId} via Loja Virtual sem entidade_id.`, retryError)
          return null
        }
      }
      console.warn(`Falha ao buscar produto ${normalizedId} via Loja Virtual.`, error)
      return null
    }
  }

  if (Number.isFinite(numericId) && numericId > 0) {
    const params = new URLSearchParams()
    const lojaId = getLojaIdValue()
    if (lojaId) params.set('loja_id', lojaId.toString())
    appendLojaVirtualCatalogPricingParams(params)
    const suffix = params.toString() ? `?${params.toString()}` : ''

    try {
      const response = await fetchApi<LojaVirtualProdutoDetalheResponse>(
        `/loja-virtual/v1/produtos/${Math.trunc(numericId)}${suffix}`
      )

      if (response.produto?.id && String(response.produto.id) === normalizedId) {
        const parentProductId = Number(response.produto.produto_principal_variacao ?? 0)
        if (Number.isFinite(parentProductId) && parentProductId > 0) {
          try {
            return await getVariationDetail(normalizedId, String(Math.trunc(parentProductId)))
          } catch (variationError) {
            console.warn(`Falha ao carregar detalhe da variacao ${normalizedId} pelo produto pai ${parentProductId}.`, variationError)
          }
        }
        return mapLojaVirtualProductDetailToProduct(response.produto)
      }

      if (response.produto?.id) {
        console.warn(
          `Ignorando /produtos/${normalizedId} por responder com produto ${String(response.produto.id)} em vez do solicitado.`
        )
      }
    } catch (error) {
      console.warn(`Falha ao carregar detalhe do produto ${normalizedId} via Loja Virtual.`, error)
    }

    const matchedProductFromSearch = await searchProductByIdWithRetry()
    if (matchedProductFromSearch) {
      if (matchedProductFromSearch.parentProductId) {
        try {
          return await getVariationDetail(matchedProductFromSearch.id, matchedProductFromSearch.parentProductId)
        } catch (variationError) {
          console.warn(
            `Falha ao enriquecer variacao ${matchedProductFromSearch.id} a partir da busca exata pelo pai ${matchedProductFromSearch.parentProductId}.`,
            variationError,
          )
        }
      }

      return matchedProductFromSearch
    }

    try {
      const response = await fetchApi<LojaVirtualVariacaoDetalheResponse>(
        `/loja-virtual/v1/variacoes/${Math.trunc(numericId)}${suffix}`
      )

      if (isLojaVirtualVariationDetail(response.variacao) && String(response.variacao.produto_id) === normalizedId) {
        return mapLojaVirtualVariationDetailToProduct(response.variacao, response.produto)
      }

      if (isLojaVirtualProductDetail(response.variacao) && String(response.variacao.id) === normalizedId) {
        return mapLojaVirtualProductDetailToProduct(response.variacao)
      }

      if (isLojaVirtualProductDetail(response.produto) && String(response.produto.id) === normalizedId) {
        return mapLojaVirtualProductDetailToProduct(response.produto)
      }
    } catch (error) {
      console.warn(`Falha ao carregar variacao ${normalizedId} via Loja Virtual.`, error)
    }
  }

  if (normalizedId) {
    const matchedProduct = await searchProductByIdWithRetry()
    if (matchedProduct) return matchedProduct
  }

  throw new Error(`Produto ${normalizedId || id} não encontrado nos endpoints /loja-virtual/v1/produtos do backend.`)
}

export type AiSearchAttachment = { mediaType: string; base64: string; name?: string }

export type AiSearchResult = {
  produtoIds: string[]
  termos: string[]
  observacao: string | null
}

export type AiSearchAnalyzingProduct = { id: number; nome: string; imagem: string | null }

export type AiSearchCallbacks = {
  onStatus?: (message: string) => void
  onAnalyzing?: (produtos: AiSearchAnalyzingProduct[]) => void
  // Candidatos encontrados antes da validação final: a vitrine pode exibi-los
  // imediatamente e refinar quando o resultado definitivo chegar.
  onPartial?: (produtoIds: string[]) => void
}

const AI_SEARCH_FALLBACK_ERROR = 'Não foi possível concluir a busca inteligente. Tente novamente.'

/**
 * Busca por IA da loja: envia texto e/ou foto e recebe SOMENTE os códigos dos
 * produtos encontrados — a vitrine local monta os cards via getProductsByIds.
 * A resposta chega em SSE (status ao vivo + miniaturas dos candidatos) porque
 * as chamadas de IA passam do timeout de um POST comum.
 */
export async function aiSearchProducts(input: {
  message?: string
  attachment?: AiSearchAttachment | null
  lojaId?: number
  signal?: AbortSignal
}, callbacks: AiSearchCallbacks = {}): Promise<AiSearchResult> {
  const lojaId = resolveLojaIdOrThrow('aiSearchProducts', input.lojaId)
  let response: Response
  try {
    response = await fetchApiResponse('/docs/busca-ia', {
      method: 'POST',
      headers: { Accept: 'text/event-stream' },
      body: JSON.stringify({
        message: String(input.message ?? '').trim(),
        loja_id: lojaId,
        ...(input.attachment ? { attachments: [input.attachment] } : {}),
      }),
      signal: input.signal,
      skipBuyerSessionCredentials: true,
      skipBuyerSessionToken: true,
    })
  } catch (error) {
    if (error instanceof ApiRequestError) throw new Error(AI_SEARCH_FALLBACK_ERROR)
    throw error
  }

  // Antes do stream começar o backend ainda responde JSON (ex.: 422 com
  // "motivo" amigável quando a IA está indisponível).
  if (!response.ok) {
    let motivo = ''
    try {
      const parsed = await response.json() as { motivo?: unknown }
      motivo = String(parsed?.motivo ?? '').trim()
    } catch {
      motivo = ''
    }
    throw new Error(motivo || AI_SEARCH_FALLBACK_ERROR)
  }
  const reader = response.body?.getReader()
  if (!reader) throw new Error(AI_SEARCH_FALLBACK_ERROR)

  const decoder = new TextDecoder()
  let buffer = ''
  let result: AiSearchResult | null = null
  let streamError: string | null = null

  const dispatchBlock = (block: string) => {
    let event = 'message'
    const dataLines: string[] = []
    for (const rawLine of block.split(/\r?\n/)) {
      if (!rawLine || rawLine.startsWith(':')) continue
      if (rawLine.startsWith('event:')) event = rawLine.slice(6).trim()
      if (rawLine.startsWith('data:')) dataLines.push(rawLine.slice(5).trimStart())
    }
    if (dataLines.length === 0) return
    let data: Record<string, unknown>
    try {
      const parsed = JSON.parse(dataLines.join('\n')) as unknown
      data = parsed && typeof parsed === 'object' && !Array.isArray(parsed)
        ? parsed as Record<string, unknown>
        : {}
    } catch {
      return
    }

    if (event === 'status' && typeof data.message === 'string' && data.message.trim()) {
      callbacks.onStatus?.(data.message.trim())
    } else if (event === 'parcial') {
      const ids = (Array.isArray(data.produto_ids) ? data.produto_ids : [])
        .map((id) => Number.parseInt(String(id), 10))
        .filter((id) => Number.isFinite(id) && id > 0)
        .map((id) => String(id))
      if (ids.length > 0) callbacks.onPartial?.(ids)
    } else if (event === 'analyzing') {
      const produtos = (Array.isArray(data.produtos) ? data.produtos : [])
        .map((item) => {
          const record = (item && typeof item === 'object' ? item : {}) as Record<string, unknown>
          return {
            id: Number(record.id ?? 0),
            nome: String(record.nome ?? '').trim(),
            imagem: typeof record.imagem === 'string' && record.imagem ? record.imagem : null,
          }
        })
        .filter((item) => item.id > 0)
      if (produtos.length > 0) callbacks.onAnalyzing?.(produtos)
    } else if (event === 'result') {
      result = {
        produtoIds: (Array.isArray(data.produto_ids) ? data.produto_ids : [])
          .map((id) => Number.parseInt(String(id), 10))
          .filter((id) => Number.isFinite(id) && id > 0)
          .map((id) => String(id)),
        termos: (Array.isArray(data.termos) ? data.termos : [])
          .map((termo) => String(termo ?? '').trim())
          .filter(Boolean),
        observacao: typeof data.observacao === 'string' && data.observacao.trim()
          ? data.observacao.trim()
          : null,
      }
    } else if (event === 'error') {
      streamError = String(data.motivo ?? '').trim() || AI_SEARCH_FALLBACK_ERROR
    }
  }

  while (true) {
    const { done, value } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })
    let boundary = buffer.search(/\r?\n\r?\n/)
    while (boundary >= 0) {
      const block = buffer.slice(0, boundary)
      const separator = buffer.slice(boundary).match(/^\r?\n\r?\n/)?.[0] ?? '\n\n'
      buffer = buffer.slice(boundary + separator.length)
      dispatchBlock(block)
      boundary = buffer.search(/\r?\n\r?\n/)
    }
  }
  buffer += decoder.decode()
  if (buffer.trim()) dispatchBlock(buffer)

  if (streamError) throw new Error(streamError)
  if (!result) throw new Error(AI_SEARCH_FALLBACK_ERROR)
  return result
}

const LOJA_VIRTUAL_BATCH_PRODUTOS_CHUNK = 30

const getProductsByIdsRequest = new Map<string, Promise<Product[]>>()

/**
 * Busca vários produtos numa única chamada ao /produtos/buscar?produto_ids=...
 * em vez de N chamadas a getProductById. Faz chunking (teto do backend),
 * respeita entidade_id/loja_id e mantém a ordem e as duplicatas dos IDs.
 * Ausentes reais não disparam detalhe individual: o contrato de `produto_ids`
 * do backend é responsável por pais, variações, NCM, fotos e opções de caixa.
 */
export async function getProductsByIds(ids: string[], options?: {
  trackBackendAvailability?: boolean
  fallbackMissing?: boolean
  fallbackMissingNcm?: boolean
  fallbackMissingBoxOptionsForIds?: string[]
  throwOnBatchError?: boolean
  resolveRequestedVariations?: boolean
}): Promise<Product[]> {
  const requestedIds = ids
    .map((id) => String(id ?? '').trim())
    .filter((id) => /^\d+$/.test(id))
  if (requestedIds.length === 0) return []
  const normalizedIds = Array.from(new Set(requestedIds))

  const shouldFallbackMissing = options?.fallbackMissing !== false
  const shouldFallbackMissingNcm = options?.fallbackMissingNcm === true
  const fallbackMissingBoxOptionsForIds = Array.from(
    new Set(
      (options?.fallbackMissingBoxOptionsForIds ?? [])
        .map((id) => String(id ?? '').trim())
        .filter((id) => /^\d+$/.test(id))
    )
  )
  const dedupeKey = [
    requestedIds.join(','),
    options?.trackBackendAvailability === false ? 'track:0' : 'track:1',
    shouldFallbackMissing ? 'fallback:1' : 'fallback:0',
    shouldFallbackMissingNcm ? 'fallback-ncm:1' : 'fallback-ncm:0',
    `fallback-box:${fallbackMissingBoxOptionsForIds.join(',')}`,
    options?.throwOnBatchError ? 'throw:1' : 'throw:0',
    options?.resolveRequestedVariations ? 'variations:1' : 'variations:0',
  ].join('|')
  const pending = getProductsByIdsRequest.get(dedupeKey)
  if (pending) return pending

  const request = getProductsByIdsInner(requestedIds, normalizedIds, {
    trackBackendAvailability: options?.trackBackendAvailability,
    fallbackMissing: shouldFallbackMissing,
    fallbackMissingNcm: shouldFallbackMissingNcm,
    fallbackMissingBoxOptionsForIds,
    throwOnBatchError: options?.throwOnBatchError === true,
    resolveRequestedVariations: options?.resolveRequestedVariations === true,
  }).finally(() => {
    getProductsByIdsRequest.delete(dedupeKey)
  })
  getProductsByIdsRequest.set(dedupeKey, request)
  return request
}

async function getProductsByIdsInner(
  requestedIds: string[],
  normalizedIds: string[],
  options: {
    trackBackendAvailability?: boolean
    fallbackMissing: boolean
    fallbackMissingNcm: boolean
    fallbackMissingBoxOptionsForIds: string[]
    throwOnBatchError: boolean
    resolveRequestedVariations: boolean
  },
): Promise<Product[]> {
  const found = new Map<string, Product>()

  const runChunk = async (chunk: string[], includeAuthenticatedEntidadeId: boolean) => {
    const params = new URLSearchParams({ produto_ids: chunk.join(',') })
    appendLojaVirtualListParams(params, {
      limit: chunk.length,
      offset: 0,
      includeAuthenticatedEntidadeId,
    })
    const response = await fetchApi<LojaVirtualProdutosResponse>(
      `/loja-virtual/v1/produtos/buscar?${params}`,
      {
        trackBackendAvailability: options?.trackBackendAvailability,
      }
    )
    return mapLojaVirtualProducts(response.items)
  }

  for (let i = 0; i < normalizedIds.length; i += LOJA_VIRTUAL_BATCH_PRODUTOS_CHUNK) {
    const chunk = normalizedIds.slice(i, i + LOJA_VIRTUAL_BATCH_PRODUTOS_CHUNK)
    let mapped: Product[] = []
    try {
      mapped = await runChunk(chunk, true)
    } catch (error) {
      if (shouldRetryLojaVirtualWithoutEntidade(error)) {
        clearAuthenticatedEntidadeId()
        try {
          mapped = await runChunk(chunk, false)
        } catch (retryError) {
          if (options.throwOnBatchError) throw retryError
          console.warn('Falha no lote de produtos via Loja Virtual.', retryError)
        }
      } else {
        if (options.throwOnBatchError) throw error
        console.warn('Falha no lote de produtos via Loja Virtual.', error)
      }
    }
    for (const product of mapped) {
      if (!product?.id) continue
      found.set(product.id, product)

      // A busca agrupa variações no card do produto pai. Quando o lote é
      // solicitado pelo ID da variação, o proxy devolve o pai e informa o alvo
      // real em navigationTargetProductId. Indexamos também esse alvo para que
      // consumidores como o carrinho recebam o mesmo ID que solicitaram.
      const navigationTargetId = String(product.navigationTargetProductId ?? '').trim()
      if (!navigationTargetId || !chunk.includes(navigationTargetId)) continue

      const parentProductId = String(product.navigationParentContextId ?? product.id).trim()
      const targetName = product.navigationTargetProductName?.trim() || product.name
      found.set(navigationTargetId, {
        ...product,
        id: navigationTargetId,
        cartBaseProductId: navigationTargetId,
        parentProductId: parentProductId && parentProductId !== navigationTargetId
          ? parentProductId
          : product.parentProductId,
        name: targetName,
        baseName: targetName,
        navigationTargetProductId: navigationTargetId,
        navigationParentContextId: parentProductId || product.navigationParentContextId,
        hasVariations: false,
      })
    }

    const unresolvedChunkIds = chunk.filter((id) => !found.has(id))
    const parentIds = Array.from(new Set(mapped.map((product) => product.id).filter(Boolean)))
    if (options.resolveRequestedVariations && unresolvedChunkIds.length > 0 && parentIds.length > 0) {
      try {
        const variationsByParent = await getProductVariationsBatch(parentIds, { includeManuals: false })
        const unresolvedSet = new Set(unresolvedChunkIds)
        variationsByParent.forEach((variations) => {
          variations.forEach((variation) => {
            if (unresolvedSet.has(variation.id)) found.set(variation.id, variation)
          })
        })
      } catch (error) {
        if (options.throwOnBatchError) throw error
        console.warn('Falha no lote consolidado de variações da Loja Virtual.', error)
      }
    }
  }

  return requestedIds
    .map((id) => found.get(id))
    .filter((product): product is Product => Boolean(product))
}

export async function getProductFormDefinition(productId: string): Promise<ProductFormDefinition | null> {
  const normalizedId = productId.trim()
  const numericId = Number.parseInt(normalizedId, 10)
  if (!Number.isFinite(numericId) || numericId <= 0) return null

  try {
    const params = new URLSearchParams()
    const lojaId = getLojaIdValue()
    if (lojaId) params.set('loja_id', lojaId.toString())

    const response = await fetchApi<LojaVirtualProdutoFormularioResponse>(
      `/loja-virtual/v1/produtos/${Math.trunc(numericId)}/formulario${params.toString() ? `?${params.toString()}` : ''}`
    )

    const fields = (Array.isArray(response.campos) ? response.campos : [])
      .map((field): ProductFormField | null => {
        const id = String(field.id ?? '').trim()
        const type = field.tipo ?? null
        if (!id || !type) return null
        return {
          id,
          type,
          name: String(field.nome ?? '').trim(),
          note: String(field.observacao ?? '').trim(),
          required: Number(field.obrigatorio ?? 0) === 1,
          options: Array.isArray(field.opcoes) ? field.opcoes.map((option) => String(option).trim()).filter(Boolean) : [],
          relations: (Array.isArray(field.relacoes) ? field.relacoes : [])
            .map((relation): ProductFormFieldRelation | null => {
              const sourceFieldId = String(relation.campo_origem_id ?? '').trim()
              const sourceValue = String(relation.valor_origem ?? '').trim()
              const options = Array.isArray(relation.opcoes)
                ? relation.opcoes.map((option) => String(option).trim()).filter(Boolean)
                : []
              if (!sourceFieldId || !sourceValue || options.length === 0) return null
              return {
                sourceFieldId,
                sourceValue,
                options,
              }
            })
            .filter((relation): relation is ProductFormFieldRelation => Boolean(relation)),
          min: typeof field.min === 'number' && Number.isFinite(field.min) ? field.min : null,
          max: typeof field.max === 'number' && Number.isFinite(field.max) ? field.max : null,
        }
      })
      .filter((field): field is ProductFormField => Boolean(field))

    if (fields.length === 0) return null

    return {
      productId: String(response.produto_id ?? Math.trunc(numericId)),
      showGroupedItems: Number(response.exibir_itens_agrupados ?? 0) === 1,
      fields,
    }
  } catch (error) {
    if (isApiStatusError(error, 404)) {
      return null
    }
    throw error
  }
}

export async function getProductFormComponents(
  productId: string,
  values: Record<string, string>
): Promise<ProductFormComponentsResult> {
  const normalizedId = productId.trim()
  const numericId = Number.parseInt(normalizedId, 10)
  if (!Number.isFinite(numericId) || numericId <= 0) {
    throw new Error('Produto inválido para resolver componentes.')
  }

  const params = new URLSearchParams()
  const lojaId = getLojaIdValue()
  if (lojaId) params.set('loja_id', lojaId.toString())
  appendLojaVirtualCatalogPricingParams(params)

  const response = await fetchApi<LojaVirtualProdutoFormularioComponentesResponse>(
    `/loja-virtual/v1/produtos/${Math.trunc(numericId)}/formulario/componentes${params.toString() ? `?${params.toString()}` : ''}`,
    {
      method: 'POST',
      body: JSON.stringify({ values }),
    }
  )

  return {
    productId: String(response.produto_id ?? Math.trunc(numericId)),
    showPrices: Number(response.exibir_precos ?? 0) === 1,
    totalSelected: typeof response.total_selecionado === 'number' && Number.isFinite(response.total_selecionado)
      ? response.total_selecionado
      : null,
    manuals: mapProductManualItems(response.manuais),
    groups: (Array.isArray(response.grupos) ? response.grupos : [])
      .map((group): ProductFormComponentGroup | null => {
        const id = String(group.id ?? '').trim()
        if (!id) return null
        const groupAllRequired = Number(group.todos_obrigatorios ?? 0) === 1
        const items = (Array.isArray(group.itens) ? group.itens : [])
          .map((item): ProductFormComponentItem | null => {
            const product = item.produto ? mapProductFormResolvedProductToProduct(item.produto) : null
            const configId = String(item.config_id ?? '').trim()
            if (!product || !configId) return null
            const minimumQuantity = Math.max(1, Math.trunc(Number(item.quantidade ?? 1) || 1))
            return {
              configId,
              minimumQuantity,
              quantity: minimumQuantity,
              selected: groupAllRequired || Number(item.selecionado ?? 0) === 1,
              locked: Number(item.bloqueado ?? 0) === 1,
              product,
            }
          })
          .filter((item): item is ProductFormComponentItem => Boolean(item))

        if (items.length === 0) return null

        return {
          id,
          name: String(group.nome ?? '').trim() || id,
          required: Number(group.obrigatorio ?? 0) === 1,
          allRequired: groupAllRequired,
          items,
        }
      })
      .filter((group): group is ProductFormComponentGroup => Boolean(group)),
  }
}

type SearchOptions = {
  loja_id?: number
  entidade_id?: number
  hibrido?: number
  site_menu_id?: number
  departamento_id?: number
  classe_id?: number | number[]
  classe_ids?: number[]
  produto_grupo_id?: number | number[]
  produto_grupo_ids?: number[]
  marca_fabricante_id?: number
  marca_fabricante_ids?: number[]
  manufacturer_brand_id?: number
  manufacturer_brand_ids?: number[]
  brand_id?: number
  brand_ids?: number[]
  produto_ids?: number[]
  menu?: number
  departamento?: number
  classe?: number | number[]
  grupo?: number | number[]
  grupo_id?: number | number[]
  grupo_ids?: number | number[]
  vehicleFilter?: Partial<VehicleFilter> | null
  onlyAvailable?: boolean
  limit?: number
  offset?: number
  pricingMode?: 'complete' | 'cached'
  pricingRefresh?: string | number
  signal?: AbortSignal
}

type ImageSearchOptions = {
  loja_id?: number
  entidade_id?: number
  site_menu_id?: number
  departamento_id?: number
  classe_id?: number | number[]
  classe_ids?: number[]
  produto_grupo_id?: number | number[]
  produto_grupo_ids?: number[]
  marca_fabricante_id?: number
  marca_fabricante_ids?: number[]
  limit?: number
  signal?: AbortSignal
}

const VEHICLE_MODEL_GENERIC_PREFIXES = new Set(['all', 'new', 'novo', 'nova'])
const STOREFRONT_MIN_TEXT_SEARCH_CHARS = 2

const normalizeVehicleSearchText = (value?: string | null) => (
  String(value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase('pt-BR')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
)

const isTooShortStorefrontTextSearch = (value: string) => {
  const normalized = normalizeVehicleSearchText(value)
  if (!normalized || /^\d+$/.test(normalized)) return false

  const tokens = normalized.split(/\s+/).filter(Boolean)
  return tokens.length > 0
    && tokens.join('').length < STOREFRONT_MIN_TEXT_SEARCH_CHARS
}

const escapeVehicleSearchRegExp = (value: string) => (
  value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
)

const buildVehicleModelMatchPhrases = (model?: string | null): string[] => {
  const normalizedModel = normalizeVehicleSearchText(model)
  if (!normalizedModel) return []

  const tokens = normalizedModel.split(/\s+/).filter(Boolean)
  const coreTokens: string[] = []

  for (const token of tokens) {
    if (
      coreTokens.length > 0
      && (/^\d+$/.test(token) || /^\d+(?:p|pts?|v)$/.test(token))
    ) {
      break
    }

    coreTokens.push(token)
  }

  const phrases = new Set<string>()
  if (coreTokens.length > 0) {
    phrases.add(coreTokens.join(' '))
  }

  const firstMeaningfulToken = tokens.find((token, index) => (
    !(index === 0 && VEHICLE_MODEL_GENERIC_PREFIXES.has(token))
  ))
  if (firstMeaningfulToken) {
    phrases.add(firstMeaningfulToken)
  }

  return Array.from(phrases).filter((phrase) => phrase.length >= 2)
}

const productMatchesVehicleFilter = (
  product: Pick<Product, 'name' | 'baseName' | 'modelo' | 'variationLabel' | 'parentProductName'>,
  vehicleFilter?: Partial<VehicleFilter> | null,
) => {
  const modelPhrases = buildVehicleModelMatchPhrases(vehicleFilter?.modelo)
  if (modelPhrases.length === 0) return true

  const haystacks = [
    product.name,
    product.baseName,
    product.modelo,
    product.variationLabel,
    product.parentProductName,
  ]
    .map((value) => normalizeVehicleSearchText(value))
    .filter(Boolean)

  if (haystacks.length === 0) return false

  return modelPhrases.some((phrase) => {
    const pattern = new RegExp(`(?:^| )${escapeVehicleSearchRegExp(phrase)}(?:$| )`, 'i')
    return haystacks.some((haystack) => pattern.test(haystack))
  })
}

const filterProductsByVehicleFilter = (
  products: Product[],
  vehicleFilter?: Partial<VehicleFilter> | null,
) => {
  if (!vehicleFilter?.modelo?.trim()) {
    return products
  }

  return products.filter((product) => productMatchesVehicleFilter(product, vehicleFilter))
}

const shouldPreferExactSearchWord = (
  query: string,
  vehicleFilter?: Partial<VehicleFilter> | null,
) => {
  if (vehicleFilter?.modelo?.trim()) return false

  // Free-text searches should keep the backend ranking intact. We only
  // force exact matching for identifier-like queries such as SKU/reference.
  return isExactProductIdentifierQuery(query)
}

const productMatchesExactSearchWord = (
  product: Pick<Product, 'name' | 'baseName' | 'modelo' | 'variationLabel' | 'parentProductName' | 'code' | 'manufacturerCode'>,
  query: string,
) => {
  const normalizedQuery = normalizeVehicleSearchText(query)
  if (!normalizedQuery) return true

  const pattern = new RegExp(`(?:^| )${escapeVehicleSearchRegExp(normalizedQuery)}(?:$| )`, 'i')
  const haystacks = [
    product.name,
    product.baseName,
    product.modelo,
    product.variationLabel,
    product.parentProductName,
    product.code,
    product.manufacturerCode,
  ]
    .map((value) => normalizeVehicleSearchText(value))
    .filter(Boolean)

  if (haystacks.length === 0) return false
  return haystacks.some((haystack) => pattern.test(haystack))
}

const filterProductsByExactSearchWord = (
  products: Product[],
  query: string,
) => {
  if (!shouldPreferExactSearchWord(query)) {
    return products
  }

  return products.filter((product) => productMatchesExactSearchWord(product, query))
}

export async function searchProductsPage(
  query: string,
  options: SearchOptions = {}
): Promise<PaginatedProductListResult> {
  const {
    loja_id,
    entidade_id,
    hibrido,
    site_menu_id,
    departamento_id,
    classe_id,
    classe_ids,
    produto_grupo_id,
    produto_grupo_ids,
    marca_fabricante_id,
    marca_fabricante_ids,
    manufacturer_brand_id,
    manufacturer_brand_ids,
    brand_id,
    brand_ids,
    menu,
    departamento,
    classe,
    grupo,
    grupo_id,
    grupo_ids,
    onlyAvailable,
    limit = 100,
    offset = 0,
    pricingMode = 'complete',
    pricingRefresh,
    signal,
  } = options

  const resolvedSiteMenuId = site_menu_id ?? menu
  const resolvedDepartamentoId = departamento_id ?? departamento
  const normalizePositiveIntegerList = (values: unknown[]): number[] => {
    const normalized = new Set<number>()

    for (const value of values) {
      if (Array.isArray(value)) {
        for (const nestedValue of normalizePositiveIntegerList(value)) {
          normalized.add(nestedValue)
        }
        continue
      }

      const numericValue = Number(value)
      if (!Number.isFinite(numericValue) || numericValue <= 0) continue
      normalized.add(Math.trunc(numericValue))
    }

    return Array.from(normalized.values())
  }
  const resolvedClasseIds = normalizePositiveIntegerList([
    ...(Array.isArray(classe_ids) ? classe_ids : []),
    classe_id,
    classe,
  ])
  const resolvedProdutoGrupoIds = normalizePositiveIntegerList([
    ...(Array.isArray(produto_grupo_ids) ? produto_grupo_ids : []),
    produto_grupo_id,
    grupo_id,
    grupo,
    grupo_ids,
  ])
  const resolvedMarcaFabricanteIds = normalizePositiveIntegerList([
    ...(Array.isArray(marca_fabricante_ids) ? marca_fabricante_ids : []),
    ...(Array.isArray(manufacturer_brand_ids) ? manufacturer_brand_ids : []),
    ...(Array.isArray(brand_ids) ? brand_ids : []),
    marca_fabricante_id,
    manufacturer_brand_id,
    brand_id,
  ])
  const resolvedProdutoIds = normalizePositiveIntegerList([
    ...(Array.isArray(options.produto_ids) ? options.produto_ids : []),
  ])
  const hasNavigationFilter =
    typeof resolvedSiteMenuId === 'number'
    || typeof resolvedDepartamentoId === 'number'
    || resolvedClasseIds.length > 0
    || resolvedProdutoGrupoIds.length > 0
    || resolvedMarcaFabricanteIds.length > 0
    || resolvedProdutoIds.length > 0

  const normalizedQuery = query.trim()
  if (isTooShortStorefrontTextSearch(normalizedQuery)) {
    return {
      items: [],
      limit: Math.max(1, Math.trunc(limit)),
      offset: Math.max(0, Math.trunc(offset)),
      total: 0,
      pricingPending: false,
    }
  }
  const defaultQuery = !hasNavigationFilter
    ? resolveLojaVirtualDefaultProductQuery()
    : ''
  const effectiveQuery = normalizedQuery
    || defaultQuery

  const runSearch = async (
    includeAuthenticatedEntidadeId: boolean,
    pageLimit = limit,
    pageOffset = offset,
  ): Promise<PaginatedProductListResult> => {
    const params = new URLSearchParams()
    appendLojaVirtualListParams(params, {
      limit: pageLimit,
      offset: pageOffset,
      loja_id,
      entidade_id,
      includeAuthenticatedEntidadeId,
      onlyAvailable,
    })

    if (effectiveQuery) {
      params.set('q', effectiveQuery)
    }

    if (typeof hibrido === 'number' && Number.isFinite(hibrido)) {
      params.set('hibrido', Math.trunc(hibrido).toString())
    }

    if (pricingMode === 'cached') {
      params.set('precos_cache_only', '1')
      if (pricingRefresh !== undefined && pricingRefresh !== null) {
        params.set('pricing_refresh', String(pricingRefresh))
      }
    }

    if (resolvedProdutoIds.length > 0) {
      params.set('produto_ids', resolvedProdutoIds.join(','))
    }

    if (typeof resolvedSiteMenuId === 'number' && Number.isFinite(resolvedSiteMenuId)) {
      params.set('site_menu_id', Math.trunc(resolvedSiteMenuId).toString())
    }

    if (typeof resolvedDepartamentoId === 'number' && Number.isFinite(resolvedDepartamentoId)) {
      params.set('departamento_id', Math.trunc(resolvedDepartamentoId).toString())
    }

    if (resolvedClasseIds.length > 0) {
      params.set('classe_id', resolvedClasseIds.join(','))
    }

    if (resolvedProdutoGrupoIds.length > 0) {
      params.set('produto_grupo_id', resolvedProdutoGrupoIds.join(','))
    }

    if (resolvedMarcaFabricanteIds.length > 0) {
      params.set('marca_fabricante_id', resolvedMarcaFabricanteIds.join(','))
    }

    const response = await fetchApi<LojaVirtualProdutosResponse>(
      `/loja-virtual/v1/produtos/buscar?${params}`,
      { signal }
    )
    const items = (Array.isArray(response.items) ? response.items : [])
      .map(mapLojaVirtualProductToProduct)
      .filter((item): item is Product => Boolean(item))
    const responseLimit = typeof response.limit === 'number' && Number.isFinite(response.limit)
      ? Math.max(1, Math.trunc(response.limit))
      : Math.max(1, Math.trunc(pageLimit))
    const responseOffset = typeof response.offset === 'number' && Number.isFinite(response.offset)
      ? Math.max(0, Math.trunc(response.offset))
      : Math.max(0, Math.trunc(pageOffset))
    const total = typeof response.total === 'number' && Number.isFinite(response.total)
      ? Math.max(0, Math.trunc(response.total))
      : null

    return {
      items,
      limit: responseLimit,
      offset: responseOffset,
      total,
      pricingPending:
        Number(response.precos_pendentes ?? 0) === 1
        || items.some((item) => item.pricingPending === true),
    }
  }

  const runSearchWithTransientRetry = async (
    includeAuthenticatedEntidadeId: boolean,
    pageLimit = limit,
    pageOffset = offset,
  ) => {
    try {
      return await runSearch(includeAuthenticatedEntidadeId, pageLimit, pageOffset)
    } catch (error) {
      // A camada pública pode transformar o timeout JSON do proxy em uma
      // página HTML de manutenção. Repetimos somente falhas transitórias e
      // somente uma vez; respostas bem-sucedidas seguem pelo caminho original.
      if (!signal?.aborted && shouldRetryLojaVirtualProductSearch(error)) {
        // O circuito Redis permanece aberto por 1s quando uma execução excede
        // o limite. Repetir imediatamente só encontra o circuito ainda aberto;
        // esperamos a janela fechar.
        if (shouldWaitForLojaVirtualProductSearchCircuit(error)) {
          await wait(LOJA_VIRTUAL_PRODUCT_SEARCH_RETRY_DELAY_MS)
          signal?.throwIfAborted()
        }
        return runSearch(includeAuthenticatedEntidadeId, pageLimit, pageOffset)
      }
      throw error
    }
  }

  const runNormalSearch = async (pageLimit = limit, pageOffset = offset) => {
    try {
      return await runSearchWithTransientRetry(true, pageLimit, pageOffset)
    } catch (error) {
      if (shouldRetryLojaVirtualWithoutEntidade(error)) {
        clearAuthenticatedEntidadeId()
        return runSearchWithTransientRetry(false, pageLimit, pageOffset)
      }
      throw error
    }
  }

  return runNormalSearch()
}

export async function searchFilteredProductsPage(
  query: string,
  options: SearchOptions = {}
): Promise<PaginatedFilteredProductListResult> {
  const targetLimit = Math.max(1, Math.trunc(options.limit ?? 100))
  const initialOffset = Math.max(0, Math.trunc(options.offset ?? 0))
  const seenIds = new Set<string>()
  const collected: Product[] = []
  let nextOffset = initialOffset
  let total: number | null = null
  let hasMore = true

  while (collected.length < targetLimit && hasMore) {
    const page = await searchProductsPage(query, {
      ...options,
      limit: Math.max(1, targetLimit - collected.length),
      offset: nextOffset,
    })
    total = page.total

    const rawNextOffset = page.offset + page.items.length
    const filteredItems = filterProductsByVehicleFilter(page.items, options.vehicleFilter)
    for (const item of filteredItems) {
      if (seenIds.has(item.id)) continue
      seenIds.add(item.id)
      collected.push(item)
      if (collected.length >= targetLimit) break
    }

    const stalled = rawNextOffset <= nextOffset
    hasMore = page.items.length > 0
      && !stalled
      && (
        page.total !== null
          ? rawNextOffset < page.total
          : page.items.length >= Math.max(1, page.limit)
      )
    nextOffset = rawNextOffset
  }

  return {
    items: collected,
    limit: targetLimit,
    offset: initialOffset,
    total,
    nextOffset,
    hasMore,
  }
}

export async function searchProducts(
  query: string,
  options: SearchOptions = {}
): Promise<Product[]> {
  const targetLimit = Math.max(1, Math.trunc(options.limit ?? 100))
  const shouldUseExactSearchWordPreference = shouldPreferExactSearchWord(query, options.vehicleFilter)
  const response = await searchProductsPage(query, options)
  const baseFilteredItems = filterProductsByVehicleFilter(response.items, options.vehicleFilter)
  const filteredItems = shouldUseExactSearchWordPreference
    ? filterProductsByExactSearchWord(baseFilteredItems, query)
    : baseFilteredItems

  if (
    !options.vehicleFilter?.modelo?.trim()
    && !shouldUseExactSearchWordPreference
    && filteredItems.length >= targetLimit
  ) {
    return filteredItems
  }

  const seenIds = new Set(filteredItems.map((item) => item.id))
  const collected = [...filteredItems]
  const fallbackSeenIds = new Set(baseFilteredItems.map((item) => item.id))
  const fallbackCollected = [...baseFilteredItems]
  let rawOffset = response.offset + response.items.length
  let latestPage = response

  while (
    collected.length < targetLimit
    && latestPage.items.length > 0
    && (typeof latestPage.total !== 'number' || rawOffset < latestPage.total)
  ) {
    latestPage = await searchProductsPage(query, {
      ...options,
      offset: rawOffset,
    })

    if (latestPage.items.length === 0) {
      break
    }

    const nextBaseFilteredItems = filterProductsByVehicleFilter(latestPage.items, options.vehicleFilter)
    for (const item of nextBaseFilteredItems) {
      if (fallbackSeenIds.has(item.id)) continue
      fallbackSeenIds.add(item.id)
      fallbackCollected.push(item)
    }

    const nextFilteredItems = shouldUseExactSearchWordPreference
      ? filterProductsByExactSearchWord(nextBaseFilteredItems, query)
      : nextBaseFilteredItems
    for (const item of nextFilteredItems) {
      if (seenIds.has(item.id)) continue
      seenIds.add(item.id)
      collected.push(item)
      if (collected.length >= targetLimit) {
        break
      }
    }

    rawOffset = latestPage.offset + latestPage.items.length
  }

  if (shouldUseExactSearchWordPreference && collected.length === 0) {
    return fallbackCollected
  }

  return collected
}

export async function searchProductsByImage(
  file: Blob,
  options: ImageSearchOptions = {}
): Promise<StorefrontImageSearchResult> {
  const {
    loja_id,
    entidade_id,
    site_menu_id,
    departamento_id,
    classe_id,
    classe_ids,
    produto_grupo_id,
    produto_grupo_ids,
    marca_fabricante_id,
    marca_fabricante_ids,
    limit,
    signal,
  } = options
  const normalizePositiveIntegerList = (values: unknown[]): number[] => {
    const normalized = new Set<number>()

    for (const value of values) {
      if (Array.isArray(value)) {
        for (const nestedValue of normalizePositiveIntegerList(value)) {
          normalized.add(nestedValue)
        }
        continue
      }

      const numericValue = Number(value)
      if (!Number.isFinite(numericValue) || numericValue <= 0) continue
      normalized.add(Math.trunc(numericValue))
    }

    return Array.from(normalized.values())
  }
  const resolvedClasseIds = normalizePositiveIntegerList([
    ...(Array.isArray(classe_ids) ? classe_ids : []),
    classe_id,
  ])
  const resolvedProdutoGrupoIds = normalizePositiveIntegerList([
    ...(Array.isArray(produto_grupo_ids) ? produto_grupo_ids : []),
    produto_grupo_id,
  ])
  const resolvedMarcaFabricanteIds = normalizePositiveIntegerList([
    ...(Array.isArray(marca_fabricante_ids) ? marca_fabricante_ids : []),
    marca_fabricante_id,
  ])
  const formData = new FormData()
  formData.append('image', file)

  const resolvedLojaId = getLojaIdValue(loja_id) ?? getLojaIdValue()
  if (resolvedLojaId) {
    formData.append('loja_id', String(resolvedLojaId))
  }

  const resolvedPricing = resolveLojaVirtualCatalogPricing({
    explicitEntidadeId: entidade_id,
  })
  if (resolvedPricing.entidadeId) {
    formData.append('entidade_id', String(resolvedPricing.entidadeId))
  }
  if (resolvedPricing.publicPricing) {
    formData.append('preco_aberto', '1')
  }

  if (typeof site_menu_id === 'number' && Number.isFinite(site_menu_id) && site_menu_id > 0) {
    formData.append('site_menu_id', String(Math.trunc(site_menu_id)))
  }

  if (typeof departamento_id === 'number' && Number.isFinite(departamento_id) && departamento_id > 0) {
    formData.append('departamento_id', String(Math.trunc(departamento_id)))
  }

  if (resolvedClasseIds.length > 0) {
    formData.append('classe_ids', resolvedClasseIds.join(','))
  }

  if (resolvedProdutoGrupoIds.length > 0) {
    formData.append('produto_grupo_ids', resolvedProdutoGrupoIds.join(','))
  }

  if (resolvedMarcaFabricanteIds.length > 0) {
    formData.append('marca_fabricante_ids', resolvedMarcaFabricanteIds.join(','))
  }

  if (typeof limit === 'number' && Number.isFinite(limit) && limit > 0) {
    formData.append('limit', String(Math.trunc(limit)))
  }

  const response = await fetchApi<LojaVirtualBuscaImagemResponse>('/loja-virtual/v1/produtos/busca-imagem', {
    method: 'POST',
    body: formData,
    signal,
  })

  return {
    matchStatus: response.match_status === 'ok' ? 'ok' : 'fora_do_catalogo',
    confidenceBand: response.confidence_band === 'high' || response.confidence_band === 'medium'
      ? response.confidence_band
      : 'none',
    message: typeof response.message === 'string' && response.message.trim() ? response.message.trim() : null,
    total: typeof response.total === 'number' && Number.isFinite(response.total)
      ? Math.max(0, Math.trunc(response.total))
      : 0,
    items: mapLojaVirtualProducts(response.items),
  }
}

type GetProductVariationsOptions = {
  includeManuals?: boolean
  signal?: AbortSignal
}

type GetProductVariationsBatchOptions = GetProductVariationsOptions

function mapLojaVirtualProductVariationsResponse(
  id: string,
  response: LojaVirtualProdutoVariacoesResponse,
  includeManuals: boolean
): Product[] {
  const normalizedId = id.trim()
  const rawVariations = Array.isArray(response.variacoes)
    ? [...response.variacoes]
    : []
  const responseParentId = normalizeLojaVirtualNumericId(response.produto_id)
  const sanitizedVariations = rawVariations.filter((variation) => {
    const itemParentId = normalizeLojaVirtualNumericId(variation.produto_principal_variacao)
    return !itemParentId || itemParentId === normalizedId
  })
  const hasConflictingResponseParent = Boolean(responseParentId && responseParentId !== normalizedId)
  if (rawVariations.length === 0 || hasConflictingResponseParent || sanitizedVariations.length === 0) {
    return []
  }

  const sortedVariations = sortItemsByOrder(sanitizedVariations)
  const selectorConfig = Array.isArray(response.configuracoes_variacao)
    ? response.configuracoes_variacao
    : []
  const selectorEnabled = selectorConfig.length > 0
    || sortedVariations.some((variation) => Number(variation.usar_seletor_variacao ?? 0) === 1)
  const mappedVariations = mapLojaVirtualProducts(sortedVariations, {
    parentProductId: normalizedId,
    usarSeletorVariacao: selectorEnabled,
    variationSelectorConfig: selectorConfig,
  })
  const expandedVariations = expandProductsWithBoxPurchaseOptions(mappedVariations)
  return includeManuals ? attachManualsToProducts(expandedVariations) : expandedVariations
}

export async function getProductVariationsBatch(
  ids: string[],
  options: GetProductVariationsBatchOptions = {}
): Promise<Map<string, Product[]>> {
  const { includeManuals = true, signal } = options
  const normalizedIds = Array.from(new Set(
    ids
      .map((id) => id.trim())
      .filter((id) => /^\d+$/.test(id) && Number.parseInt(id, 10) > 0)
  ))
  if (normalizedIds.length > 100) {
    throw new RangeError('getProductVariationsBatch aceita no máximo 100 produtos-pai por chamada.')
  }
  const result = new Map<string, Product[]>()
  for (const id of normalizedIds) result.set(id, [])
  if (normalizedIds.length === 0) return result

  const params = new URLSearchParams()
  const lojaId = getLojaIdValue()
  if (lojaId) params.set('loja_id', lojaId.toString())
  appendLojaVirtualCatalogPricingParams(params)
  const suffix = params.toString()
  const response = await fetchApi<LojaVirtualProdutoVariacoesBatchResponse>(
    `/loja-virtual/v1/produtos/variacoes/batch${suffix ? `?${suffix}` : ''}`,
    {
      method: 'POST',
      signal,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        produto_ids: normalizedIds.map((id) => Number.parseInt(id, 10))
      })
    }
  )

  for (const item of response.items ?? []) {
    const parentId = normalizeLojaVirtualNumericId(item.produto_id)
    if (!parentId || !result.has(parentId)) continue
    result.set(parentId, mapLojaVirtualProductVariationsResponse(parentId, item, includeManuals))
  }
  return result
}

// Dedupe de getProductVariations em voo. resolveBuyTogetherOptions chama esta
// função 2x por item (antes e depois de getProductById) e a cascata de "compre
// junto" a dispara para vários itens. Só deduplicamos quando NÃO há AbortSignal
// (o abort de um caller não pode cancelar a request de outro) e a mesma variante
// de includeManuals — chaveado por id|includeManuals.
const getProductVariationsRequest = new Map<string, Promise<Product[]>>()

export async function getProductVariations(
  id: string,
  options: GetProductVariationsOptions = {}
): Promise<Product[]> {
  const { includeManuals = true, signal } = options

  if (signal) {
    return getProductVariationsInner(id, options)
  }

  const dedupeKey = `${id.trim()}|${includeManuals ? '1' : '0'}|${resolveLojaVirtualRequestLanguage()}`
  const pending = getProductVariationsRequest.get(dedupeKey)
  if (pending) {
    return pending
  }

  const request = getProductVariationsInner(id, options).finally(() => {
    getProductVariationsRequest.delete(dedupeKey)
  })
  getProductVariationsRequest.set(dedupeKey, request)
  return request
}

async function getProductVariationsInner(
  id: string,
  options: GetProductVariationsOptions = {}
): Promise<Product[]> {
  const { includeManuals = true, signal } = options
  const normalizedId = id.trim()
  const numericId = /^\d+$/.test(normalizedId) ? Number.parseInt(normalizedId, 10) : Number.NaN

  if (Number.isFinite(numericId) && numericId > 0) {
    try {
      const params = new URLSearchParams()
      const lojaId = getLojaIdValue()
      if (lojaId) params.set('loja_id', lojaId.toString())
      appendLojaVirtualCatalogPricingParams(params)

      const suffix = params.toString()
      const response = await fetchApi<LojaVirtualProdutoVariacoesResponse>(
        `/loja-virtual/v1/produtos/${Math.trunc(numericId)}/variacoes${suffix ? `?${suffix}` : ''}`,
        { signal }
      )

      const mappedBatchCompatibleVariations = mapLojaVirtualProductVariationsResponse(
        normalizedId,
        response,
        includeManuals
      )
      if (mappedBatchCompatibleVariations.length > 0) return mappedBatchCompatibleVariations

      if (Array.isArray(response.variacoes) && response.variacoes.length > 0) {
        const responseParentId = normalizeLojaVirtualNumericId(response.produto_id)
        console.warn(
          `Ignorando endpoint de variacoes do produto ${normalizedId} por responder com pai divergente (${responseParentId ?? 'indefinido'}).`
        )
      }
    } catch (error) {
      if (signal?.aborted) throw error
      console.warn(`Falha ao carregar variacoes do produto ${normalizedId} via endpoint de variacoes da Loja Virtual.`, error)

      if (isApiRequestError(error) && error.status === 503) {
        try {
          const batch = await getProductVariationsBatch([normalizedId], options)
          const batchVariations = batch.get(normalizedId) ?? []
          if (batchVariations.length > 0) return batchVariations
        } catch (batchError) {
          if (signal?.aborted) throw batchError
          console.warn(
            `Falha ao carregar variacoes do produto ${normalizedId} via endpoint batch consolidado da Loja Virtual.`,
            batchError,
          )
        }
      }
    }

    try {
      const params = new URLSearchParams()
      const lojaId = getLojaIdValue()
      if (lojaId) params.set('loja_id', lojaId.toString())
      appendLojaVirtualCatalogPricingParams(params)

      const suffix = params.toString()
      const response = await fetchApi<LojaVirtualProdutoDetalheResponse>(
        `/loja-virtual/v1/produtos/${Math.trunc(numericId)}${suffix ? `?${suffix}` : ''}`,
        { signal }
      )

      const responseProductId = normalizeLojaVirtualNumericId(response.produto?.id)
      const rawVariations = Array.isArray(response.produto?.variacoes)
        ? [...response.produto.variacoes]
        : []
      const sanitizedVariations = rawVariations.filter((variation) => {
        const itemParentId = normalizeLojaVirtualNumericId(variation.produto_principal_variacao)
        return !itemParentId || itemParentId === normalizedId
      })

      if (rawVariations.length > 0 && responseProductId === normalizedId && sanitizedVariations.length > 0) {
        const sortedVariations = sortItemsByOrder(sanitizedVariations)
        const mappedVariations = mapLojaVirtualProducts(sortedVariations, {
          parentProductId: response.produto?.id ? String(response.produto.id) : normalizedId,
          parentProductName: resolveParentProductBaseName(response.produto),
          usarSeletorVariacao: Number(response.produto?.usar_seletor_variacao ?? 0) === 1,
          variationSelectorConfig: response.produto?.configuracoes_variacao,
        })
        const expandedVariations = expandProductsWithBoxPurchaseOptions(mappedVariations)
        return includeManuals ? attachManualsToProducts(expandedVariations) : expandedVariations
      }

      if (rawVariations.length > 0 && responseProductId && responseProductId !== normalizedId) {
        console.warn(
          `Ignorando variacoes embutidas do produto ${normalizedId} por detalhe responder com produto ${responseProductId}.`
        )
      } else if (rawVariations.length > 0 && sanitizedVariations.length === 0) {
        console.warn(
          `Ignorando variacoes embutidas do produto ${normalizedId} por nenhuma pertencer ao pai solicitado.`
        )
      }

      const parentProductId = Number(response.produto?.produto_principal_variacao ?? 0)
      if (
        Number.isFinite(parentProductId)
        && parentProductId > 0
        && Math.trunc(parentProductId) !== Math.trunc(numericId)
      ) {
        return getProductVariations(String(Math.trunc(parentProductId)), options)
      }

      if (response.produto) {
        const mappedProduct = mapLojaVirtualProductDetailToProduct(response.produto)
        if ((mappedProduct.boxOptions ?? []).length > 0) {
          const expandedVariations = expandProductsWithBoxPurchaseOptions([mappedProduct])
          return includeManuals ? attachManualsToProducts(expandedVariations) : expandedVariations
        }
      }
    } catch (error) {
      console.warn(`Falha ao carregar variacoes do produto ${normalizedId} via detalhe da Loja Virtual.`, error)
    }
  }

  return []
}

type ProductSearchParentContextMatch = {
  parentProductId?: string
  variationId: string
  variationName: string
  variationInStock: boolean
  variationStockQuantity?: number | null
}

const SEARCH_PARENT_CONTEXT_PAGE_LIMIT = 100

const normalizeProductSearchIdentity = (value?: string | null) => (
  String(value ?? '').trim().toLowerCase()
)

const isExactProductIdentifierQuery = (query: string) => {
  const normalizedQuery = normalizeProductSearchIdentity(query)
  if (!normalizedQuery) return false

  return /^\d{4,}$/.test(normalizedQuery)
    || (
      /\d/.test(normalizedQuery)
      && /^[a-z0-9]+(?:[-_/][a-z0-9]+)+$/i.test(normalizedQuery)
    )
}

const productMatchesExactSearchIdentity = (
  product: Pick<Product, 'id' | 'code' | 'manufacturerCode'>,
  normalizedQuery: string,
) => (
  [
    product.id,
    product.code,
    product.manufacturerCode,
  ].some((candidate) => normalizeProductSearchIdentity(candidate) === normalizedQuery)
)

const appendUniqueParentContextMatch = (
  matches: ProductSearchParentContextMatch[],
  variation: Pick<Product, 'id' | 'name' | 'inStock' | 'stockQuantity'>,
  options: {
    parentProductId?: string | null
  } = {},
) => {
  const normalizedParentProductId = String(options.parentProductId ?? '').trim() || undefined
  const existingIndex = matches.findIndex((item) => item.variationId === variation.id)

  if (existingIndex >= 0) {
    const existingMatch = matches[existingIndex]
    if (!normalizedParentProductId || existingMatch.parentProductId === normalizedParentProductId) {
      return matches
    }

    const nextMatches = [...matches]
    nextMatches[existingIndex] = {
      ...existingMatch,
      parentProductId: normalizedParentProductId,
    }
    return nextMatches
  }

  return [
    ...matches,
    {
      parentProductId: normalizedParentProductId,
      variationId: variation.id,
      variationName: variation.name,
      variationInStock: variation.inStock,
      variationStockQuantity: variation.stockQuantity ?? null,
    },
  ]
}

const buildContextualParentProduct = (
  product: Product,
  targetMatch: ProductSearchParentContextMatch,
  parentContextId: string,
): Product => ({
  ...product,
  inStock: targetMatch.variationInStock,
  stockQuantity: targetMatch.variationStockQuantity ?? null,
  navigationTargetProductId: targetMatch.variationId,
  navigationTargetProductName: targetMatch.variationName,
  navigationParentContextId: parentContextId,
})

const loadProductsByGroupId = async (
  groupId: string,
  signal?: AbortSignal,
) => {
  const normalizedGroupId = groupId.trim()
  if (!/^\d+$/.test(normalizedGroupId)) {
    return [] as Product[]
  }

  const collected: Product[] = []
  const seenIds = new Set<string>()
  let offset = 0

  while (true) {
    const page = await searchProductsPage('', {
      produto_grupo_id: Number.parseInt(normalizedGroupId, 10),
      limit: SEARCH_PARENT_CONTEXT_PAGE_LIMIT,
      offset,
      signal,
    })

    if (page.items.length === 0) {
      break
    }

    page.items.forEach((item) => {
      if (seenIds.has(item.id)) return
      seenIds.add(item.id)
      collected.push(item)
    })

    offset += page.items.length

    if (
      page.items.length < SEARCH_PARENT_CONTEXT_PAGE_LIMIT
      || (typeof page.total === 'number' && offset >= page.total)
    ) {
      break
    }
  }

  return collected
}

export async function expandProductsWithParentContexts(
  query: string,
  results: Product[],
  options: {
    signal?: AbortSignal
  } = {},
): Promise<Product[]> {
  const normalizedQuery = normalizeProductSearchIdentity(query)
  if (!isExactProductIdentifierQuery(normalizedQuery) || results.length === 0) {
    return results
  }

  const variationListsByParentId = new Map<string, Product[]>()
  const groupTargetMatches = new Map<string, ProductSearchParentContextMatch[]>()
  const directVariationResultIds = new Set<string>()

  const loadParentVariations = async (parentId: string) => {
    const cachedVariations = variationListsByParentId.get(parentId)
    if (cachedVariations) {
      return cachedVariations
    }

    const loadedVariations = await getProductVariations(parentId, {
      includeManuals: false,
      signal: options.signal,
    })
    variationListsByParentId.set(parentId, loadedVariations)
    return loadedVariations
  }

  for (const result of results) {
    if (options.signal?.aborted) {
      return results
    }

    const normalizedGroupId = result.groupId?.trim()
    if (!normalizedGroupId) {
      continue
    }

    if (result.parentProductId && productMatchesExactSearchIdentity(result, normalizedQuery)) {
      if (result.showVariationsInSearch) {
        continue
      }
      directVariationResultIds.add(result.id)
      groupTargetMatches.set(
        normalizedGroupId,
        appendUniqueParentContextMatch(groupTargetMatches.get(normalizedGroupId) ?? [], result, {
          parentProductId: result.parentProductId,
        }),
      )
      continue
    }

    if (!result.hasVariations) {
      continue
    }

    const matchedVariation = (await loadParentVariations(result.id)).find((variation) => (
      productMatchesExactSearchIdentity(variation, normalizedQuery)
    ))

    if (!matchedVariation) {
      continue
    }

    groupTargetMatches.set(
      normalizedGroupId,
      appendUniqueParentContextMatch(groupTargetMatches.get(normalizedGroupId) ?? [], matchedVariation, {
        parentProductId: result.id,
      }),
    )
  }

  if (groupTargetMatches.size === 0) {
    return results
  }

  const resultsById = new Map(results.map((result) => [result.id, result] as const))
  const contextualProductsById = new Map<string, Product>()

  groupTargetMatches.forEach((targetMatches) => {
    targetMatches.forEach((targetMatch) => {
      const targetParentProductId = targetMatch.parentProductId?.trim()
      if (!targetParentProductId || contextualProductsById.has(targetParentProductId)) {
        return
      }

      const directParentResult = resultsById.get(targetParentProductId)
      if (!directParentResult || !directParentResult.hasVariations) {
        return
      }

      contextualProductsById.set(
        targetParentProductId,
        buildContextualParentProduct(directParentResult, targetMatch, targetParentProductId),
      )
    })
  })

  for (const [groupId, targetMatches] of groupTargetMatches.entries()) {
    const unresolvedTargetMatches = targetMatches.filter((targetMatch) => {
      const targetParentProductId = targetMatch.parentProductId?.trim()
      return !targetParentProductId || !contextualProductsById.has(targetParentProductId)
    })

    if (unresolvedTargetMatches.length === 0) {
      continue
    }

    const groupProducts = await loadProductsByGroupId(groupId, options.signal)

    for (const groupProduct of groupProducts) {
      if (!groupProduct.hasVariations) {
        continue
      }

      const groupProductVariations = await loadParentVariations(groupProduct.id)
      const matchedTarget = unresolvedTargetMatches.find((target) => {
        const targetParentProductId = target.parentProductId?.trim()
        if (targetParentProductId && targetParentProductId !== groupProduct.id) {
          return false
        }

        return groupProductVariations.some((variation) => variation.id === target.variationId)
      })

      if (!matchedTarget) {
        continue
      }

      contextualProductsById.set(
        groupProduct.id,
        buildContextualParentProduct(groupProduct, matchedTarget, groupProduct.id),
      )
    }
  }

  if (contextualProductsById.size === 0) {
    return results
  }

  const expandedResults: Product[] = []
  const appendedProductIds = new Set<string>()

  results.forEach((result) => {
    const contextualResult = contextualProductsById.get(result.id)

    if (contextualResult) {
      expandedResults.push(contextualResult)
      appendedProductIds.add(contextualResult.id)
      return
    }

    if (directVariationResultIds.has(result.id)) {
      return
    }

    expandedResults.push(result)
  })

  contextualProductsById.forEach((contextualProduct) => {
    if (appendedProductIds.has(contextualProduct.id)) {
      return
    }

    expandedResults.push(contextualProduct)
  })

  return expandedResults
}

export type ProductReview = {
  id: string
  rating: number
  comment: string | null
  author: string | null
  date: string | null
}

export type ProductReviewsSummary = {
  average: number
  total: number
  distribution: Array<{ rating: number; count: number }>
  reviews: ProductReview[]
}

function createEmptyProductReviewDistribution(): Array<{ rating: number; count: number }> {
  return [5, 4, 3, 2, 1].map((rating) => ({ rating, count: 0 }))
}

function createEmptyProductReviewsSummary(): ProductReviewsSummary {
  return {
    average: 0,
    total: 0,
    distribution: createEmptyProductReviewDistribution(),
    reviews: [],
  }
}

function getReviewNumber(record: Record<string, unknown>, keys: string[]): number | undefined {
  for (const key of keys) {
    const value = normalizeFreteNumber(record[key])
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value
    }
  }
  return undefined
}

function getReviewText(record: Record<string, unknown>, keys: string[]): string | null {
  for (const key of keys) {
    const value = record[key]
    if (typeof value === 'string' && value.trim()) {
      return value.trim()
    }
  }
  return null
}

function toReviewRecordList(value: unknown): Record<string, unknown>[] {
  if (!Array.isArray(value)) return []
  return value.filter((item): item is Record<string, unknown> => Boolean(item && typeof item === 'object'))
}

function mapProductReviewItems(value: unknown): ProductReview[] {
  const rows = toReviewRecordList(value)

  return rows
    .map((row, index) => {
      const ratingValue = getReviewNumber(row, ['nota', 'rating', 'stars', 'estrelas', 'score'])
      if (typeof ratingValue !== 'number') return null

      const rating = Math.max(1, Math.min(5, Math.round(ratingValue)))
      const idValue = getReviewNumber(row, ['id', 'review_id', 'avaliacao_id'])
      const id = typeof idValue === 'number' && idValue > 0
        ? String(Math.trunc(idValue))
        : `review-${index + 1}`

      return {
        id,
        rating,
        comment: getReviewText(row, ['observacao', 'avaliacao_texto', 'comentario', 'comment', 'texto']),
        author: getReviewText(row, ['nome_cliente', 'author', 'nome', 'cliente', 'razao']),
        date: getReviewText(row, ['data', 'created', 'created_at', 'data_criacao']),
      }
    })
    .filter((item): item is ProductReview => Boolean(item))
}

function mapProductReviewDistribution(
  value: unknown
): Array<{ rating: number; count: number }> | null {
  const rows = toReviewRecordList(value)
  if (rows.length === 0) return null

  const counter = new Map<number, number>([
    [1, 0],
    [2, 0],
    [3, 0],
    [4, 0],
    [5, 0],
  ])
  let hasParsedRating = false

  for (const row of rows) {
    const ratingValue = getReviewNumber(row, ['nota', 'rating', 'stars', 'estrelas', 'score'])
    if (typeof ratingValue !== 'number') continue

    const rating = Math.max(1, Math.min(5, Math.round(ratingValue)))
    const countValue = getReviewNumber(row, ['total', 'count', 'qtde', 'quantidade'])
    const count = typeof countValue === 'number' && countValue > 0 ? Math.trunc(countValue) : 0
    counter.set(rating, (counter.get(rating) ?? 0) + count)
    hasParsedRating = true
  }

  if (!hasParsedRating) return null

  return [5, 4, 3, 2, 1].map((rating) => ({
    rating,
    count: counter.get(rating) ?? 0,
  }))
}

function buildProductReviewDistributionFromReviews(
  reviews: ProductReview[]
): Array<{ rating: number; count: number }> {
  const counter = new Map<number, number>([
    [1, 0],
    [2, 0],
    [3, 0],
    [4, 0],
    [5, 0],
  ])

  for (const review of reviews) {
    const rating = Math.max(1, Math.min(5, Math.round(review.rating)))
    counter.set(rating, (counter.get(rating) ?? 0) + 1)
  }

  return [5, 4, 3, 2, 1].map((rating) => ({
    rating,
    count: counter.get(rating) ?? 0,
  }))
}

function normalizeProductReviewsSummary(payload: unknown): ProductReviewsSummary {
  const unwrapped = payload
    && typeof payload === 'object'
    && 'data' in payload
      ? (payload as { data: unknown }).data
      : payload

  const record = unwrapped && typeof unwrapped === 'object' && !Array.isArray(unwrapped)
    ? (unwrapped as Record<string, unknown>)
    : null

  const reviews = mapProductReviewItems(
    Array.isArray(unwrapped)
      ? unwrapped
      : record?.avaliacoes ?? record?.reviews ?? record?.data ?? []
  )

  const explicitDistribution = mapProductReviewDistribution(
    record?.estrelas ?? record?.distribution
  )
  const distribution = explicitDistribution ?? buildProductReviewDistributionFromReviews(reviews)
  const distributionTotal = distribution.reduce((sum, item) => sum + item.count, 0)

  const totalValue = record
    ? getReviewNumber(record, ['total', 'count', 'qtde', 'quantidade', 'reviews_count'])
    : undefined
  const total = typeof totalValue === 'number' && totalValue >= 0
    ? Math.trunc(totalValue)
    : distributionTotal > 0
      ? distributionTotal
      : reviews.length

  const averageValue = record
    ? getReviewNumber(record, ['nota', 'average', 'media', 'rating'])
    : undefined

  const calculatedAverage = (() => {
    if (typeof averageValue === 'number' && averageValue > 0) {
      return averageValue
    }

    if (distributionTotal > 0) {
      const weightedTotal = distribution.reduce((sum, item) => sum + (item.rating * item.count), 0)
      return weightedTotal / distributionTotal
    }

    if (reviews.length > 0) {
      return reviews.reduce((sum, review) => sum + review.rating, 0) / reviews.length
    }

    return 0
  })()

  const average = Number(Math.max(0, Math.min(5, calculatedAverage)).toFixed(1))

  return {
    average,
    total: Math.max(0, total),
    distribution,
    reviews,
  }
}

function shouldIgnoreProductReviewsError(error: unknown): boolean {
  if (!(error instanceof Error)) return false
  return /API error:\s*(401|403|404)\b/.test(error.message)
}

export async function getProductReviews(id: string): Promise<ProductReviewsSummary> {
  const normalizedId = id.trim()
  const numericId = /^\d+$/.test(normalizedId) ? Number.parseInt(normalizedId, 10) : Number.NaN

  if (!Number.isFinite(numericId) || numericId <= 0) {
    return createEmptyProductReviewsSummary()
  }

  const lojaId = getLojaIdValue()
  const lojaQuery = lojaId ? `?loja_id=${lojaId}` : ''
  const productId = Math.trunc(numericId)
  const attempts: Array<{ endpoint: string; options?: FetchApiOptions }> = [
    {
      endpoint: `/loja-virtual/v1/produtos/${productId}/avaliacoes${lojaQuery}`,
    },
  ]

  let unexpectedError: Error | null = null

  for (const attempt of attempts) {
    try {
      const response = await fetchApi<unknown>(attempt.endpoint, attempt.options)
      return normalizeProductReviewsSummary(response)
    } catch (error) {
      if (shouldIgnoreProductReviewsError(error)) {
        continue
      }
      unexpectedError = error instanceof Error ? error : new Error(String(error))
    }
  }

  if (unexpectedError) {
    throw unexpectedError
  }

  return createEmptyProductReviewsSummary()
}

export async function getProductQuestions(id: string): Promise<any[]> {
  const normalizedId = id.trim()
  const numericId = /^\d+$/.test(normalizedId) ? Number.parseInt(normalizedId, 10) : Number.NaN

  if (!Number.isFinite(numericId) || numericId <= 0) {
    return []
  }

  const lojaId = getLojaIdValue()
  const lojaQuery = lojaId ? `?loja_id=${lojaId}` : ''
  const productId = Math.trunc(numericId)
  const attempts: Array<{ endpoint: string; options?: FetchApiOptions }> = [
    {
      endpoint: `/loja-virtual/v1/produtos/${productId}/perguntas${lojaQuery}`,
    },
  ]

  const extractQuestions = (payload: unknown): any[] => {
    const unwrapped = payload
      && typeof payload === 'object'
      && 'data' in payload
        ? (payload as { data: unknown }).data
        : payload

    if (Array.isArray(unwrapped)) {
      return unwrapped
    }

    if (unwrapped && typeof unwrapped === 'object') {
      const record = unwrapped as Record<string, unknown>
      if (Array.isArray(record.perguntas)) return record.perguntas
      if (Array.isArray(record.questions)) return record.questions
      if (Array.isArray(record.items)) return record.items
      if (Array.isArray(record.data)) return record.data
    }

    return []
  }

  let unexpectedError: Error | null = null

  for (const attempt of attempts) {
    try {
      const response = await fetchApi<unknown>(attempt.endpoint, attempt.options)
      return extractQuestions(response)
    } catch (error) {
      if (error instanceof Error && /API error:\s*(401|403|404)\b/.test(error.message)) {
        continue
      }
      unexpectedError = error instanceof Error ? error : new Error(String(error))
    }
  }

  if (unexpectedError) {
    throw unexpectedError
  }

  return []
}

export async function createProductQuestion(
  id: string,
  question: string,
  options?: {
    entidadeId?: number
    lojaId?: number
    guestName?: string
    guestWhatsapp?: string
    guestEmail?: string
    notificationChannel?: 'whatsapp' | 'email'
    notificationContact?: string
    viewedProductId?: string
  }
): Promise<unknown> {
  const normalizedId = id.trim()
  const numericId = /^\d+$/.test(normalizedId) ? Number.parseInt(normalizedId, 10) : Number.NaN
  if (!Number.isFinite(numericId) || numericId <= 0) {
    throw new Error('Produto invalido para envio da pergunta.')
  }

  const normalizedQuestion = question.trim()
  if (!normalizedQuestion) {
    throw new Error('Pergunta vazia.')
  }

  const payload: Record<string, unknown> = {
    produto_id: Math.trunc(numericId),
    pergunta: normalizedQuestion,
  }

  const lojaId = getLojaIdValue(options?.lojaId)
  if (lojaId) {
    payload.loja_id = lojaId
  }

  const entidadeId = normalizeEntidadeId(options?.entidadeId) ?? getAuthenticatedEntidadeId()
  if (typeof entidadeId === 'number') {
    payload.entidade_id = entidadeId
  }

  if (options?.guestName) {
    payload.nome_visitante = options.guestName
  }
  if (options?.guestWhatsapp) {
    payload.whatsapp_visitante = options.guestWhatsapp
  }
  if (options?.guestEmail) {
    payload.email = options.guestEmail
  }
  if (options?.notificationChannel) {
    payload.notification_channel = options.notificationChannel
  }
  if (options?.notificationContact) {
    payload.notification_contact = options.notificationContact
  }
  if (options?.viewedProductId) {
    payload.produto_visualizado_id = options.viewedProductId
  }

  const query = new URLSearchParams()
  if (lojaId) {
    query.set('loja_id', String(lojaId))
  }

  return fetchApi<unknown>(
    `/loja-virtual/v1/produtos/${Math.trunc(numericId)}/perguntas${query.toString() ? `?${query.toString()}` : ''}`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    }
  )
}

export async function getProductCourses(id: string): Promise<any[]> {
  console.warn(`Endpoint de cursos do produto não existe no /api/docs (produto ${id}).`)
  return []
}

const productManualsRequestCache = new Map<string, Promise<ProductManual[]>>()
const BUY_TOGETHER_RESPONSE_CACHE_TTL_MS = 60_000
const BUY_TOGETHER_ENDPOINT_AVAILABLE_TTL_MS = 120_000
const BUY_TOGETHER_ENDPOINT_UNAVAILABLE_COOLDOWN_MS = 30_000
const buyTogetherProductsCache = new Map<string, {
  expiresAt: number
  items: Product[]
}>()
const buyTogetherProductsRequestCache = new Map<string, Promise<Product[]>>()
let buyTogetherEndpointHealth: 'unknown' | 'available' | 'unavailable' = 'unknown'
let buyTogetherEndpointHealthExpiresAt = 0
let buyTogetherEndpointProbePromise: Promise<void> | null = null
let buyTogetherEndpointWarningExpiresAt = 0

const buildProductManualListEndpoint = (productId: number) =>
  `/docs/produtos/${Math.trunc(productId)}/manuais?server=${encodeURIComponent(INTEGRATION_PUBLIC_SERVER)}`

const buildProductSiteDescriptionHtmlEndpoint = (productId: number) =>
  `/docs/produtos/${Math.trunc(productId)}/site-descricao-html?server=${encodeURIComponent(INTEGRATION_PUBLIC_SERVER)}`

const buildLegacyProductManualDownloadEndpoint = (productId: number) =>
  `/docs/produtos/${Math.trunc(productId)}/manual-legado/download?server=${encodeURIComponent(INTEGRATION_PUBLIC_SERVER)}`

const normalizeProductSiteDescriptionHtml = (response: ProductSiteDescriptionHtmlApiResponse): string => {
  const candidates = [
    response.site_descricao_html,
    response.siteDescricaoHtml,
    response.descricao_html,
    response.descricaoHtml,
    response.html,
    response.conteudo,
    response.content,
    response.produto?.site_descricao_html,
    response.produto?.siteDescricaoHtml,
    response.produto?.descricao_html,
    response.produto?.descricaoHtml,
  ]

  const html = candidates.find((candidate) => (
    typeof candidate === 'string'
    && candidate.replace(/<[^>]*>/g, '').replace(/&nbsp;/gi, ' ').trim()
  ))
  return typeof html === 'string' ? html : ''
}

const mapProductManualItems = (items: ProductManualsApiResponse['manuais']): ProductManual[] => (
  Array.isArray(items)
    ? items
        .map((item) => {
          const manualId = String(item.id ?? '').trim()
          const downloadUrl = String(item.download_url ?? '').trim()
          if (!manualId || !downloadUrl) return null

          return {
            id: manualId,
            type: String(item.tipo ?? '').trim() || '0',
            name: String(item.nome ?? '').trim() || 'Baixar manual',
            downloadUrl,
          } satisfies ProductManual
        })
        .filter((item): item is ProductManual => Boolean(item))
    : []
)

const normalizeManualDownloadEndpoint = (value: string) => {
  const normalized = value.trim()
  if (!normalized) return ''

  if (/^https?:\/\//i.test(normalized)) {
    try {
      const parsed = new URL(normalized)
      const endpoint = `${parsed.pathname}${parsed.search}`
      if (endpoint.startsWith('/api/')) {
        return endpoint.slice(4)
      }
      return endpoint
    } catch {
      return normalized
    }
  }

  if (normalized.startsWith('/api/')) {
    return normalized.slice(4)
  }

  return normalized.startsWith('/') ? normalized : `/${normalized}`
}

const normalizeManualBrowserRequestPath = (value: string) => {
  const normalized = normalizeManualDownloadEndpoint(value)
  if (!normalized) return ''
  return normalized.startsWith('/api/') ? normalized : `/api${normalized}`
}

async function probeLegacyProductManual(productId: number): Promise<ProductManual[]> {
  const endpoint = buildLegacyProductManualDownloadEndpoint(productId)

  try {
    await fetchApiResponse(endpoint, {
      method: 'HEAD',
      headers: {
        Accept: 'application/pdf,application/octet-stream,*/*',
      },
    })

    return [{
      id: 'legacy-direct',
      type: '0',
      name: 'Baixar manual',
      downloadUrl: endpoint,
    }]
  } catch (error) {
    if (isApiStatusError(error, 404) || isApiStatusError(error, 403)) {
      return []
    }

    console.warn(`Falha ao verificar manual legado do produto ${productId}.`, error)
    return []
  }
}

async function fetchProductManualsInternal(productId: number): Promise<ProductManual[]> {
  const cacheKey = String(Math.trunc(productId))
  const cachedRequest = productManualsRequestCache.get(cacheKey)
  if (cachedRequest) {
    return cachedRequest
  }

  const request = (async () => {
    const response = await fetchApi<ProductManualsApiResponse>(buildProductManualListEndpoint(productId))
    const mappedManuals = mapProductManualItems(response.manuais)
    if (mappedManuals.length > 0) {
      return mappedManuals
    }

    return probeLegacyProductManual(productId)
  })().catch((error) => {
    productManualsRequestCache.delete(cacheKey)
    throw error
  })

  productManualsRequestCache.set(cacheKey, request)
  return request
}

export async function getProductManuals(id: string): Promise<ProductManual[]> {
  const numericId = Number.parseInt(id.trim(), 10)
  if (!Number.isFinite(numericId) || numericId <= 0) {
    return []
  }

  return fetchProductManualsInternal(Math.trunc(numericId))
}

export async function getProductSiteDescriptionHtml(id: string): Promise<string> {
  const numericId = Number.parseInt(id.trim(), 10)
  if (!Number.isFinite(numericId) || numericId <= 0) {
    return ''
  }

  const response = await fetchApi<ProductSiteDescriptionHtmlApiResponse>(
    buildProductSiteDescriptionHtmlEndpoint(Math.trunc(numericId))
  )
  return normalizeProductSiteDescriptionHtml(response)
}

export function getProductManualViewerUrl(productId: string, manual: ProductManual): string {
  const numericProductId = Number.parseInt(productId.trim(), 10)
  const normalizedManualId = manual.id.trim()
  const normalizedDownloadUrl = manual.downloadUrl.trim()

  if (!Number.isFinite(numericProductId) || numericProductId <= 0 || !normalizedManualId) {
    throw new Error('Manual inválido.')
  }

  if (/^https?:\/\//i.test(normalizedDownloadUrl) && !normalizedDownloadUrl.includes('/api/')) {
    return normalizedDownloadUrl
  }

  const viewerUrl = new URL(
    `/api/docs/produtos/${Math.trunc(numericProductId)}/manuais/${encodeURIComponent(normalizedManualId)}/visualizar`,
    window.location.origin
  )
  const browserDownloadPath = normalizeManualBrowserRequestPath(normalizedDownloadUrl)

  if (browserDownloadPath) {
    viewerUrl.searchParams.set('download_path', browserDownloadPath)
  }

  viewerUrl.searchParams.set('server', INTEGRATION_PUBLIC_SERVER)
  return viewerUrl.toString()
}

export async function downloadProductManual(productId: string, manual: ProductManual): Promise<ProductManualDownloadResult> {
  const numericProductId = Number.parseInt(productId.trim(), 10)
  const normalizedManualId = manual.id.trim()
  const normalizedDownloadUrl = manual.downloadUrl.trim()

  if (!Number.isFinite(numericProductId) || numericProductId <= 0 || !normalizedManualId) {
    throw new Error('Manual inválido.')
  }

  if (/^https?:\/\//i.test(normalizedDownloadUrl) && !normalizedDownloadUrl.includes('/api/')) {
    return {
      type: 'url',
      url: normalizedDownloadUrl,
    }
  }

  const endpoint = normalizeManualDownloadEndpoint(normalizedDownloadUrl)
    || `/docs/produtos/${Math.trunc(numericProductId)}/manuais/${encodeURIComponent(normalizedManualId)}/download?server=${encodeURIComponent(INTEGRATION_PUBLIC_SERVER)}`

  const response = await fetchApiResponse(
    endpoint,
    {
      headers: {
        Accept: 'application/octet-stream,application/pdf,application/json,text/plain,*/*',
      },
    }
  )

  const contentType = (response.headers.get('content-type') || '').toLocaleLowerCase('pt-BR')

  if (contentType.includes('application/json')) {
    const payload = await response.json() as Record<string, unknown>
    const urlCandidate = [
      payload.url,
      payload.link,
      payload.href,
      payload.download_url,
      payload.downloadUrl,
      payload.file_url,
      payload.fileUrl,
    ].find((value) => typeof value === 'string' && value.trim())

    if (typeof urlCandidate === 'string' && urlCandidate.trim()) {
      return {
        type: 'url',
        url: urlCandidate.trim(),
      }
    }

    throw new Error('Manual indisponível no momento.')
  }

  if (contentType.startsWith('text/')) {
    const textPayload = (await response.text()).trim()
    if (/^https?:\/\//i.test(textPayload)) {
      return {
        type: 'url',
        url: textPayload,
      }
    }
  }

  return {
    type: 'blob',
    blob: await response.blob(),
    fileName: extractFileNameFromDisposition(response.headers.get('content-disposition')),
  }
}

async function attachManualsToProducts(products: Product[]): Promise<Product[]> {
  if (!Array.isArray(products) || products.length === 0) {
    return []
  }

  const uniqueBaseProductIds = Array.from(
    new Set(products.map((product) => resolveStorefrontCartBaseProductId(product)).filter(Boolean))
  )
  const manualsByProductId = new Map<string, ProductManual[]>(
    await Promise.all(
      uniqueBaseProductIds.map(async (productId) => {
        try {
          return [productId, await getProductManuals(productId)] as const
        } catch (error) {
          console.warn(`Falha ao carregar manuais da variação ${productId}.`, error)
          return [productId, [] as ProductManual[]] as const
        }
      })
    )
  )

  return products.map((product) => {
    const manuals = manualsByProductId.get(resolveStorefrontCartBaseProductId(product)) ?? []
    return manuals.length > 0
      ? { ...product, manuals }
      : product
  })
}

export async function getRelatedProducts(id: string, limit: number = 20, offset: number = 0): Promise<Product[]> {
  const numericId = Number.parseInt(id, 10)
  if (!Number.isFinite(numericId)) {
    return []
  }

  const params = new URLSearchParams({
    id: Math.trunc(numericId).toString(),
  })
  appendLojaVirtualListParams(params, { limit, offset })

  let response: LojaVirtualProdutosResponse
  try {
    response = await fetchApi<LojaVirtualProdutosResponse>(
      `/loja-virtual/v1/produtos/relacionados?${params}`
    )
  } catch (error) {
    // Relacionados é uma recomendação não crítica. Quando o índice Redis está
    // aquecendo ou o proxy encerra uma resposta lenta, usamos o mesmo fallback
    // de "compre junto" em vez de propagar a falha para a página.
    if (isBuyTogetherSoftFailure(error)) {
      return getBuyTogetherProducts(Math.trunc(numericId).toString(), limit, offset)
    }
    throw error
  }

  const related = mapLojaVirtualProducts(response.items)
  if (related.length > 0) {
    return related
  }

  return getBuyTogetherProducts(Math.trunc(numericId).toString(), limit, offset)
}

function getBuyTogetherEndpointCurrentHealth() {
  if (buyTogetherEndpointHealth !== 'unknown' && Date.now() >= buyTogetherEndpointHealthExpiresAt) {
    buyTogetherEndpointHealth = 'unknown'
    buyTogetherEndpointHealthExpiresAt = 0
  }

  return buyTogetherEndpointHealth
}

function setBuyTogetherEndpointHealth(health: 'available' | 'unavailable', ttlMs: number) {
  buyTogetherEndpointHealth = health
  buyTogetherEndpointHealthExpiresAt = Date.now() + ttlMs
}

function getCachedBuyTogetherProducts(cacheKey: string): Product[] | null {
  const cached = buyTogetherProductsCache.get(cacheKey)
  if (!cached) return null

  if (Date.now() >= cached.expiresAt || cached.items.length === 0) {
    buyTogetherProductsCache.delete(cacheKey)
    return null
  }

  return cached.items
}

function setCachedBuyTogetherProducts(cacheKey: string, items: Product[], ttlMs: number) {
  buyTogetherProductsCache.set(cacheKey, {
    expiresAt: Date.now() + ttlMs,
    items,
  })
}

// O resultado de comprejunto com limit maior cobre um pedido de limit menor
// (mesmo id/offset): a lista é um prefixo. A página de produto pede o MESMO id
// com limit=1 (checagem "tem?") e limit=4 (carrossel), gerando duas requests.
// Aqui servimos o pedido menor a partir de uma resposta maior já cacheada,
// eliminando a duplicação 1-vs-4. Conservador: só serve quando a entrada
// cacheada tem itens suficientes (>= limit). O caso vazio/insuficiente segue
// pela request normal (que pode esgotar o conjunto e cachear).
function findCachedBuyTogetherSubset(
  numericId: number,
  limit: number,
  offset: number
): Product[] | null {
  const idPrefix = `id=${numericId}&`
  const offsetPart = `offset=${offset}`
  for (const [key, cached] of buyTogetherProductsCache) {
    if (!key.startsWith(idPrefix) || !key.includes(offsetPart)) continue
    if (Date.now() >= cached.expiresAt) continue
    if (cached.items.length >= limit) {
      return cached.items.slice(0, limit)
    }
  }
  return null
}

function isBuyTogetherSoftFailure(error: unknown): boolean {
  return isApiRequestError(error) && (error.status === 404 || error.status >= 500)
}

export async function getBuyTogetherProducts(id: string, limit: number = 20, offset: number = 0): Promise<Product[]> {
  const numericId = Number.parseInt(id, 10)
  if (!Number.isFinite(numericId)) {
    return []
  }

  const params = new URLSearchParams({
    id: Math.trunc(numericId).toString(),
  })
  appendLojaVirtualListParams(params, { limit, offset })
  const cacheKey = params.toString()

  const cached = getCachedBuyTogetherProducts(cacheKey)
  if (cached) {
    return cached
  }

  // Reaproveita uma resposta já cacheada com limit maior do mesmo id/offset
  // (ex.: limit=1 servido a partir do resultado de limit=4).
  const subset = findCachedBuyTogetherSubset(Math.trunc(numericId), limit, offset)
  if (subset) {
    return subset
  }

  const currentHealth = getBuyTogetherEndpointCurrentHealth()
  if (currentHealth === 'unavailable') {
    return []
  }

  if (currentHealth === 'unknown' && buyTogetherEndpointProbePromise) {
    await buyTogetherEndpointProbePromise
    const cachedAfterProbe = getCachedBuyTogetherProducts(cacheKey)
    if (cachedAfterProbe) {
      return cachedAfterProbe
    }

    if (getBuyTogetherEndpointCurrentHealth() === 'unavailable') {
      return []
    }
  }

  const inFlight = buyTogetherProductsRequestCache.get(cacheKey)
  if (inFlight) {
    return inFlight
  }

  const request = (async () => {
    try {
      const response = await fetchApi<LojaVirtualProdutosResponse>(
        `/loja-virtual/v1/produtos/relacionados-comprejunto?${params}`
      )
      const items = mapLojaVirtualProducts(response.items)
      if (items.length > 0) {
        setCachedBuyTogetherProducts(cacheKey, items, BUY_TOGETHER_RESPONSE_CACHE_TTL_MS)
      } else {
        buyTogetherProductsCache.delete(cacheKey)
      }
      setBuyTogetherEndpointHealth('available', BUY_TOGETHER_ENDPOINT_AVAILABLE_TTL_MS)
      return items
    } catch (error) {
      if (isBuyTogetherSoftFailure(error)) {
        setBuyTogetherEndpointHealth('unavailable', BUY_TOGETHER_ENDPOINT_UNAVAILABLE_COOLDOWN_MS)
        setCachedBuyTogetherProducts(cacheKey, [], BUY_TOGETHER_ENDPOINT_UNAVAILABLE_COOLDOWN_MS)

        if (Date.now() >= buyTogetherEndpointWarningExpiresAt) {
          console.warn('Endpoint de compre junto indisponivel no backend. Aplicando fallback temporario sem itens.', error)
          buyTogetherEndpointWarningExpiresAt = Date.now() + BUY_TOGETHER_ENDPOINT_UNAVAILABLE_COOLDOWN_MS
        }

        return []
      }

      throw error
    } finally {
      buyTogetherProductsRequestCache.delete(cacheKey)
    }
  })()

  buyTogetherProductsRequestCache.set(cacheKey, request)

  if (currentHealth === 'unknown' && !buyTogetherEndpointProbePromise) {
    buyTogetherEndpointProbePromise = request
      .then(() => undefined)
      .catch(() => undefined)
      .finally(() => {
        buyTogetherEndpointProbePromise = null
      })
  }

  return request
}

export async function getLastVisited(): Promise<any[]> {
  console.warn('Endpoint de últimos vistos não existe no /api/docs.')
  return []
}

async function fetchLojaVirtualProductPage(
  endpoint: string,
  limit: number = 20,
  offset: number = 0,
  options: {
    onlyAvailable?: boolean
    signal?: AbortSignal
  } = {}
): Promise<PaginatedProductListResult> {
  const normalizedLimit = Math.max(1, Math.trunc(limit))
  const normalizedOffset = Math.max(0, Math.trunc(offset))
  const runRequest = async (includeAuthenticatedEntidadeId: boolean): Promise<PaginatedProductListResult> => {
    const params = new URLSearchParams()
    appendLojaVirtualListParams(params, {
      limit: normalizedLimit,
      offset: normalizedOffset,
      includeAuthenticatedEntidadeId,
      onlyAvailable: options.onlyAvailable,
    })

    const response = await fetchApi<LojaVirtualProdutosResponse>(`${endpoint}?${params}`, {
      signal: options.signal,
    })
    const items = mapLojaVirtualProducts(response.items)
    const responseLimit = typeof response.limit === 'number' && Number.isFinite(response.limit)
      ? Math.max(1, Math.trunc(response.limit))
      : normalizedLimit
    const responseOffset = typeof response.offset === 'number' && Number.isFinite(response.offset)
      ? Math.max(0, Math.trunc(response.offset))
      : normalizedOffset
    const total = typeof response.total === 'number' && Number.isFinite(response.total)
      ? Math.max(0, Math.trunc(response.total))
      : null

    return {
      items,
      limit: responseLimit,
      offset: responseOffset,
      total,
    }
  }

  const runRequestWithColdCatalogRetry = async (includeAuthenticatedEntidadeId: boolean) => {
    try {
      return await runRequest(includeAuthenticatedEntidadeId)
    } catch (error) {
      // A primeira chamada por entidade pode encontrar o catálogo ainda
      // consolidando a resposta em cache. O proxy encerra cada tentativa em
      // menos de um segundo; refazemos uma única vez para aproveitar a mesma
      // carga já em andamento, sem transformar a home em lista vazia.
      if (!options.signal?.aborted && shouldRetryLojaVirtualColdCatalog(error)) {
        return runRequest(includeAuthenticatedEntidadeId)
      }
      throw error
    }
  }

  try {
    return await runRequestWithColdCatalogRetry(true)
  } catch (error) {
    if (shouldRetryLojaVirtualWithoutEntidade(error)) {
      clearAuthenticatedEntidadeId()
      return runRequestWithColdCatalogRetry(false)
    }
    throw error
  }
}

function mapHomeProductShowcaseSection(
  section: LojaVirtualHomeProdutosSectionResponse | undefined,
  fallbackLimit: number,
  fallbackOffset: number,
  pricingPending: boolean
): PaginatedProductListResult {
  return {
    items: mapLojaVirtualProducts(section?.items),
    limit: typeof section?.limit === 'number' && Number.isFinite(section.limit)
      ? Math.max(1, Math.trunc(section.limit))
      : fallbackLimit,
    offset: typeof section?.offset === 'number' && Number.isFinite(section.offset)
      ? Math.max(0, Math.trunc(section.offset))
      : fallbackOffset,
    total: typeof section?.total === 'number' && Number.isFinite(section.total)
      ? Math.max(0, Math.trunc(section.total))
      : null,
    pricingPending,
  }
}

export async function getHomeProductShowcases(
  launchesLimit: number = 28,
  justArrivedLimit: number = 10,
  options: {
    onlyAvailable?: boolean
    signal?: AbortSignal
  } = {}
): Promise<HomeProductShowcasesResult> {
  const normalizedLaunchesLimit = Math.max(1, Math.min(Math.trunc(launchesLimit), 200))
  const normalizedJustArrivedLimit = Math.max(1, Math.min(Math.trunc(justArrivedLimit), 96))

  const runRequest = async (includeAuthenticatedEntidadeId: boolean): Promise<HomeProductShowcasesResult> => {
    const params = new URLSearchParams()
    appendLojaVirtualListParams(params, {
      limit: normalizedLaunchesLimit,
      offset: 0,
      includeAuthenticatedEntidadeId,
      onlyAvailable: options.onlyAvailable,
    })
    params.set('lancamentos_limit', normalizedLaunchesLimit.toString())
    params.set('acabaram_de_chegar_limit', normalizedJustArrivedLimit.toString())

    const response = await fetchApi<LojaVirtualHomeProdutosResponse>(
      `/loja-virtual/v1/home/produtos?${params}`,
      {
        signal: options.signal,
        // As vitrines têm fallback para o catálogo principal. Um timeout
        // isolado aqui não significa que a loja inteira está indisponível.
        trackBackendAvailability: false,
      }
    )
    const pricingPending = Number(response.precos_pendentes ?? 0) === 1

    return {
      launches: mapHomeProductShowcaseSection(
        response.sections?.lancamentos,
        normalizedLaunchesLimit,
        0,
        pricingPending
      ),
      justArrived: mapHomeProductShowcaseSection(
        response.sections?.acabaram_de_chegar,
        normalizedJustArrivedLimit,
        0,
        pricingPending
      ),
    }
  }

  const runRequestWithColdCatalogRetry = async (includeAuthenticatedEntidadeId: boolean) => {
    try {
      return await runRequest(includeAuthenticatedEntidadeId)
    } catch (error) {
      if (!options.signal?.aborted && shouldRetryLojaVirtualColdCatalog(error)) {
        return runRequest(includeAuthenticatedEntidadeId)
      }
      throw error
    }
  }

  try {
    return await runRequestWithColdCatalogRetry(true)
  } catch (error) {
    if (shouldRetryLojaVirtualWithoutEntidade(error)) {
      clearAuthenticatedEntidadeId()
      return runRequestWithColdCatalogRetry(false)
    }

    if (!isApiRequestError(error) || (error.status !== 404 && error.status !== 405)) {
      throw error
    }

    const [launches, justArrived] = await Promise.all([
      getLancamentosPage(normalizedLaunchesLimit, 0, options),
      getAcabaramDeChegarPage(normalizedJustArrivedLimit, 0, options),
    ])
    return { launches, justArrived }
  }
}

export async function getLancamentosPage(
  limit: number = 20,
  offset: number = 0,
  options?: {
    onlyAvailable?: boolean
    signal?: AbortSignal
  }
): Promise<PaginatedProductListResult> {
  return fetchLojaVirtualProductPage('/loja-virtual/v1/produtos/lancamentos', limit, offset, options)
}

export async function getLancamentos(limit: number = 20, offset: number = 0): Promise<Product[]> {
  const response = await getLancamentosPage(limit, offset)
  return response.items
}

export async function getRelatedProductsByActivity(
  entidadeId: number | null | undefined,
  limit: number = 20,
  offset: number = 0,
  activityId?: number | null
): Promise<Product[]> {
  const normalizedEntidadeId = Number(entidadeId ?? 0)
  const params = new URLSearchParams()
  if (Number.isFinite(normalizedEntidadeId) && normalizedEntidadeId > 0) {
    params.set('entidade_id', Math.trunc(normalizedEntidadeId).toString())
  }
  if (typeof activityId === 'number' && Number.isFinite(activityId) && activityId > 0) {
    params.set('atividade_id', Math.trunc(activityId).toString())
  }
  appendLojaVirtualListParams(params, {
    limit,
    offset,
    entidade_id: normalizedEntidadeId > 0 ? normalizedEntidadeId : null,
    includeAuthenticatedEntidadeId: false,
  })

  const response = await fetchApi<LojaVirtualProdutosResponse>(
    `/loja-virtual/v1/produtos/relacionados-atividade?${params}`
  )
  return mapLojaVirtualProducts(response.items)
}

type OutletOptions = {
  loja_id?: number
  site_menu_id?: number
  departamento_id?: number
  produto_grupo_id?: number
  limit?: number
  offset?: number
}

export async function getOutlet(limit?: number, offset?: number): Promise<Product[]>
export async function getOutlet(options?: OutletOptions): Promise<Product[]>
export async function getOutlet(
  limitOrOptions: number | OutletOptions = 20,
  offset: number = 0
): Promise<Product[]> {
  const options = typeof limitOrOptions === 'number'
    ? { limit: limitOrOptions, offset }
    : (limitOrOptions ?? {})

  const params = new URLSearchParams()
  appendLojaVirtualListParams(params, {
    limit: options.limit ?? 20,
    offset: options.offset ?? 0,
    loja_id: options.loja_id,
  })

  if (typeof options.site_menu_id === 'number' && Number.isFinite(options.site_menu_id)) {
    params.set('site_menu_id', Math.trunc(options.site_menu_id).toString())
  }

  if (typeof options.departamento_id === 'number' && Number.isFinite(options.departamento_id)) {
    params.set('departamento_id', Math.trunc(options.departamento_id).toString())
  }

  if (typeof options.produto_grupo_id === 'number' && Number.isFinite(options.produto_grupo_id)) {
    params.set('produto_grupo_id', Math.trunc(options.produto_grupo_id).toString())
  }

  const response = await fetchApi<LojaVirtualProdutosResponse>(
    `/loja-virtual/v1/produtos/outlet?${params}`
  )
  return mapLojaVirtualProducts(response.items)
}

export async function getAcabaramDeChegarPage(
  limit: number = 20,
  offset: number = 0,
  options?: {
    onlyAvailable?: boolean
    signal?: AbortSignal
  }
): Promise<PaginatedProductListResult> {
  return fetchLojaVirtualProductPage('/loja-virtual/v1/produtos/acabaramdechegar', limit, offset, options)
}

export async function getAcabaramDeChegar(limit: number = 20, offset: number = 0): Promise<Product[]> {
  const response = await getAcabaramDeChegarPage(limit, offset)
  return response.items
}

export async function getProductHighlights(): Promise<Product[]> {
  return getLancamentos(20, 0)
}

export async function getCategorias(): Promise<any[]> {
  const menu = await getMenu({ includeProductGroups: false })
  return menu.map((item: any) => ({
    id: Number(item.id) || 0,
    descricao: String(item.descricao || '').trim(),
  })).filter((item: { id: number; descricao: string }) => item.id > 0 && item.descricao.length > 0)
}

export async function getAtividades(): Promise<any[]> {
  const response = await fetchApi<unknown>('/atividades')
  if (Array.isArray(response)) return response
  if (response && typeof response === 'object') {
    const record = response as Record<string, unknown>
    if (Array.isArray(record.items)) return record.items
    if (Array.isArray(record.data)) return record.data
  }
  return []
}

export async function getPopularSearches(): Promise<any[]> {
  console.warn('Endpoint de buscas populares não existe no /api/docs.')
  return []
}

export async function getFavoritos(produtoId?: number, page: number = 1, perPage: number = 20): Promise<any[]> {
  const entidadeId = resolveAuthenticatedEntidadeIdOrThrow('Listagem de favoritos')
  const params = new URLSearchParams()
  const lojaId = getLojaIdValue()
  if (lojaId) params.set('loja_id', String(lojaId))
  params.set('page', String(Math.max(1, Math.trunc(page))))
  params.set('per_page', String(Math.max(1, Math.trunc(perPage))))
  if (typeof produtoId === 'number' && Number.isFinite(produtoId) && produtoId > 0) {
    params.set('produto_id', String(Math.trunc(produtoId)))
  }

  const response = await fetchApi<unknown>(
    `/loja-virtual/v1/compradores/${entidadeId}/favoritos?${params.toString()}`
  )

  return extractListFromResponse(response, ['favoritos', 'items'])
}

export async function addFavorito(produtoId: number): Promise<{ message: string }> {
  const entidadeId = resolveAuthenticatedEntidadeIdOrThrow('Inclusão de favorito')
  const normalizedProductId = Math.trunc(produtoId)
  if (!Number.isFinite(normalizedProductId) || normalizedProductId <= 0) {
    throw new Error('Produto inválido para adicionar aos favoritos.')
  }

  const lojaId = getLojaIdValue()
  const response = await fetchApi<any>(
    `/loja-virtual/v1/compradores/${entidadeId}/favoritos`,
    {
      method: 'POST',
      body: JSON.stringify({
        produto_id: normalizedProductId,
        ...(lojaId ? { loja_id: lojaId } : {}),
      }),
    }
  )

  return { message: response?.message || response?.motivo || 'Produto adicionado aos favoritos.' }
}

export async function removeFavorito(produtoId: number): Promise<{ message: string }> {
  const entidadeId = resolveAuthenticatedEntidadeIdOrThrow('Remoção de favorito')
  const normalizedProductId = Math.trunc(produtoId)
  if (!Number.isFinite(normalizedProductId) || normalizedProductId <= 0) {
    throw new Error('Produto inválido para remover dos favoritos.')
  }

  const lojaId = getLojaIdValue()
  const response = await fetchApi<any>(
    `/loja-virtual/v1/compradores/${entidadeId}/favoritos/remover`,
    {
      method: 'POST',
      body: JSON.stringify({
        produto_id: normalizedProductId,
        ...(lojaId ? { loja_id: lojaId } : {}),
      }),
    }
  )

  return { message: response?.message || response?.motivo || 'Produto removido dos favoritos.' }
}

// O catálogo pode ocultar um produto que continua salvo no carrinho. Esta
// consulta consolidada preserva esses itens para exibição e remoção.
export async function getCarrinhoProducts(
  entidadeId?: number,
  lojaId?: number,
  options?: { suppressBuyerSessionExpiry?: boolean }
): Promise<Product[]> {
  const resolvedEntidadeId = resolveCarrinhoEntidadeId(entidadeId)
  const params = new URLSearchParams()
  const normalizedLojaId = getLojaIdValue(lojaId)
  if (normalizedLojaId) params.set('loja_id', String(normalizedLojaId))
  const response = await fetchApi<unknown>(
    `${buildCarrinhoEndpoint(resolvedEntidadeId, 'itens')}?${params}`,
    options?.suppressBuyerSessionExpiry ? { suppressBuyerSessionExpiry: true } : undefined
  )

  return extractListFromResponse(response, ['itens', 'items']).flatMap((rawItem) => {
    if (!rawItem || typeof rawItem !== 'object') return []
    const item = rawItem as Record<string, unknown>
    const produtoId = normalizeEntidadeId(item.codigo)
    const quantity = normalizeCarrinhoQuantidade(item.qtde) ?? 0
    if (!produtoId || quantity <= 0) return []

    const boxQuantity = Math.max(1, Math.trunc(normalizeCarrinhoQuantidade(item.caixa_qtde_new) ?? 1))
    const price = normalizeCarrinhoQuantidade(item.preco)
    const unitPrice = typeof price === 'number' ? price / boxQuantity : undefined
    const product = mapLojaVirtualProductToProduct({
      produto_id: produtoId,
      referencia: normalizeCheckoutText(item.referencia),
      nome: normalizeCheckoutText(item.nome),
      preco: unitPrice,
    })

    return [{
      ...buildStorefrontPurchaseOptionProduct(product, boxQuantity),
      price,
      originalPrice: price,
      discountedPrice: price,
      inStock: false,
      stockQuantity: null,
      cartCatalogUnavailable: true,
    }]
  })
}

export async function getCarrinhoQuantidades(
  entidadeId?: number,
  lojaId?: number,
  options?: { suppressBuyerSessionExpiry?: boolean }
): Promise<{
  status?: number
  entidadeId: number
  lojaId?: number
  itens: Record<string, number>
}> {
  const resolvedEntidadeId = resolveCarrinhoEntidadeId(entidadeId)
  const response = await fetchApi<LojaVirtualCarrinhoQuantidadesResponse>(
    buildCarrinhoEndpoint(resolvedEntidadeId, 'quantidades', lojaId),
    options?.suppressBuyerSessionExpiry ? { suppressBuyerSessionExpiry: true } : undefined
  )

  return {
    status: response.status,
    entidadeId: normalizeEntidadeId(response.entidade_id) ?? resolvedEntidadeId,
    lojaId: getLojaIdValue(response.loja_id ?? lojaId),
    itens: mapCarrinhoQuantidadesMap(response.itens),
  }
}

export async function getCarrinhoTotal(
  entidadeId?: number,
  lojaId?: number
): Promise<{
  status?: number
  entidadeId: number
  lojaId?: number
  subtotal: number
  impostos: number
  ipi: number
  difal: number
  total: number
  itens: number
  contextoPreco?: LojaVirtualCarrinhoTotaisResponse['contexto_preco']
}> {
  const resolvedEntidadeId = resolveCarrinhoEntidadeId(entidadeId)
  const response = await fetchCheckoutApi<LojaVirtualCarrinhoTotaisResponse>(
    buildCarrinhoEndpoint(resolvedEntidadeId, 'totais', lojaId)
  )
  const { impostos, ipi, difal } = normalizeTaxes(
    response.impostos,
    response.ipi ?? response.valor_ipi,
    response.difal ?? response.valor_difal ?? response.st ?? response.valor_st,
    normalizeCarrinhoQuantidade
  )

  return {
    status: response.status,
    entidadeId: normalizeEntidadeId(response.entidade_id) ?? resolvedEntidadeId,
    lojaId: getLojaIdValue(response.loja_id ?? lojaId),
    subtotal: normalizeCarrinhoQuantidade(response.subtotal) ?? 0,
    impostos,
    ipi,
    difal,
    total: normalizeCarrinhoQuantidade(response.total) ?? 0,
    itens: Math.max(0, Math.trunc(normalizeCarrinhoQuantidade(response.itens) ?? 0)),
    contextoPreco: response.contexto_preco,
  }
}

export async function getCarrinho(
  entidadeId?: number,
  lojaId?: number
): Promise<{
  status?: number
  entidade_id: number
  loja_id?: number
  itens: Record<string, number>
  subtotal: number
  impostos: number
  ipi: number
  difal: number
  total: number
  quantidade_itens: number
  contexto_preco?: LojaVirtualCarrinhoTotaisResponse['contexto_preco']
}> {
  const [quantidades, totais] = await Promise.all([
    getCarrinhoQuantidades(entidadeId, lojaId),
    getCarrinhoTotal(entidadeId, lojaId),
  ])

  return {
    status: quantidades.status ?? totais.status,
    entidade_id: quantidades.entidadeId,
    loja_id: quantidades.lojaId ?? totais.lojaId,
    itens: quantidades.itens,
    subtotal: totais.subtotal,
    impostos: totais.impostos,
    ipi: totais.ipi,
    difal: totais.difal,
    total: totais.total,
    quantidade_itens: totais.itens,
    contexto_preco: totais.contextoPreco,
  }
}

const requestCarrinhoMutationWithBenefitRefresh = async <T>(params: {
  endpoint: string
  payload: Record<string, unknown>
  entidadeId: number
  lojaId?: number
}): Promise<T> => {
  for (let attempt = 0; attempt < 2; attempt += 1) {
    try {
      return await fetchApi<T>(params.endpoint, {
        method: 'POST',
        body: JSON.stringify(params.payload),
      })
    } catch (error) {
      if (attempt > 0 || !isCheckoutCartPriceContextStaleError(error)) throw error
      await recalculateCarrinho(params.entidadeId, params.lojaId)
    }
  }

  throw new Error('Não foi possível atualizar o carrinho após recalcular o benefício de preço.')
}

export async function addToCarrinho(
  produtoId: number,
  produtoGradeId: number,
  qtde: number,
  preco?: number,
  entidadeId?: number,
  lojaId?: number
): Promise<LojaVirtualCarrinhoItemUpsertResponse> {
  const resolvedEntidadeId = resolveCarrinhoEntidadeId(entidadeId)
  const normalizedProdutoId = Math.trunc(produtoId)
  if (!Number.isFinite(normalizedProdutoId) || normalizedProdutoId <= 0) {
    throw new Error('produtoId invalido para operacao de carrinho.')
  }

  const normalizedQtde = normalizeCarrinhoQuantidade(qtde)
  if (typeof normalizedQtde !== 'number') {
    throw new Error('Quantidade invalida para operacao de carrinho.')
  }

  const payload: Record<string, unknown> = {
    produto_id: normalizedProdutoId,
    qtde_caixa: Math.max(1, Math.trunc(produtoGradeId || 1)),
    qtde: normalizedQtde,
  }

  if (typeof preco === 'number' && Number.isFinite(preco) && preco > 0) {
    payload.preco = preco
  }

  const normalizedLojaId = getLojaIdValue(lojaId)
  if (normalizedLojaId) {
    payload.loja_id = normalizedLojaId
  }

  return requestCarrinhoMutationWithBenefitRefresh<LojaVirtualCarrinhoItemUpsertResponse>({
    endpoint: buildCarrinhoEndpoint(resolvedEntidadeId, 'itens'),
    payload,
    entidadeId: resolvedEntidadeId,
    lojaId: normalizedLojaId,
  })
}

export async function upsertCarrinhoItemByKey(
  itemKey: string,
  qtde: number,
  preco?: number,
  entidadeId?: number,
  lojaId?: number
): Promise<LojaVirtualCarrinhoItemUpsertResponse> {
  const resolvedEntidadeId = resolveCarrinhoEntidadeId(entidadeId)
  const normalizedQtde = normalizeCarrinhoQuantidade(qtde)
  if (typeof normalizedQtde !== 'number') {
    throw new Error('Quantidade invalida para operacao de carrinho.')
  }

  const parsedItem = parseCarrinhoItemKey(itemKey)
  if (!parsedItem) {
    throw new Error('itemKey invalido para operacao de carrinho.')
  }

  const payload: Record<string, unknown> = {
    item: parsedItem.itemKey,
    qtde: normalizedQtde,
  }

  if (typeof preco === 'number' && Number.isFinite(preco) && preco > 0) {
    payload.preco = preco
  }

  const normalizedLojaId = getLojaIdValue(lojaId)
  if (normalizedLojaId) {
    payload.loja_id = normalizedLojaId
  }

  return requestCarrinhoMutationWithBenefitRefresh<LojaVirtualCarrinhoItemUpsertResponse>({
    endpoint: buildCarrinhoEndpoint(resolvedEntidadeId, 'itens'),
    payload,
    entidadeId: resolvedEntidadeId,
    lojaId: normalizedLojaId,
  })
}

export type CarrinhoItemBatchInput = {
  requestId: string
  produtoId: number
  qtde: number
  qtdeCaixa?: number
  estoqueNivel?: number
  preco?: number
  afiliadoId?: number
}

export type CarrinhoItemBatchResult = {
  requestId: string
  id: number | null
  entidadeId: number
  lojaId?: number
  produtoId: number
  qtdeCaixa: number
  estoqueNivel: number
  qtde: number
  acao: string
}

export async function upsertCarrinhoItemsBatch(
  items: CarrinhoItemBatchInput[],
  entidadeId?: number,
  lojaId?: number,
  options?: { deferTaxes?: boolean }
): Promise<{
  status: number
  entidadeId: number
  lojaId?: number
  items: CarrinhoItemBatchResult[]
}> {
  const resolvedEntidadeId = resolveCarrinhoEntidadeId(entidadeId)
  if (!Array.isArray(items) || items.length === 0) {
    throw new Error('Informe ao menos um item para atualizar o carrinho.')
  }

  const normalizedLojaId = getLojaIdValue(lojaId)
  const payload = {
    ...(normalizedLojaId ? { loja_id: normalizedLojaId } : {}),
    ...(options?.deferTaxes ? { adiar_impostos: 1 } : {}),
    itens: items.map((item) => ({
      request_id: String(item.requestId),
      produto_id: Math.trunc(item.produtoId),
      qtde: item.qtde,
      qtde_caixa: Math.max(1, Math.trunc(item.qtdeCaixa ?? 1)),
      estoque_nivel: Math.max(0, Math.trunc(item.estoqueNivel ?? 0)),
      ...(typeof item.preco === 'number' && Number.isFinite(item.preco) && item.preco > 0
        ? { preco: item.preco }
        : {}),
      ...(typeof item.afiliadoId === 'number' && Number.isFinite(item.afiliadoId) && item.afiliadoId > 0
        ? { afiliado_id: Math.trunc(item.afiliadoId) }
        : {}),
    })),
  }
  const response = await requestCarrinhoMutationWithBenefitRefresh<any>({
    endpoint: buildCarrinhoEndpoint(resolvedEntidadeId, 'itens/batch'),
    payload,
    entidadeId: resolvedEntidadeId,
    lojaId: normalizedLojaId,
  })
  const responseItems = Array.isArray(response?.items)
    ? response.items
    : Array.isArray(response?.itens)
      ? response.itens
      : []

  if (Number(response?.status ?? 0) !== 1 || responseItems.length !== items.length) {
    throw new Error('Resposta incompleta ao atualizar o carrinho em lote.')
  }

  return {
    status: Number(response.status),
    entidadeId: normalizeEntidadeId(response.entidade_id) ?? resolvedEntidadeId,
    lojaId: getLojaIdValue(response.loja_id ?? lojaId),
    items: responseItems.map((item: any) => ({
      requestId: String(item?.request_id ?? item?.requestId ?? ''),
      id: normalizeEntidadeId(item?.id) ?? null,
      entidadeId: normalizeEntidadeId(item?.entidade_id) ?? resolvedEntidadeId,
      lojaId: getLojaIdValue(item?.loja_id ?? response?.loja_id ?? lojaId),
      produtoId: normalizeEntidadeId(item?.produto_id) ?? 0,
      qtdeCaixa: Math.max(1, Math.trunc(Number(item?.qtde_caixa ?? 1) || 1)),
      estoqueNivel: Math.max(0, Math.trunc(Number(item?.estoque_nivel ?? 0) || 0)),
      qtde: normalizeCarrinhoQuantidade(item?.qtde) ?? 0,
      acao: String(item?.acao ?? 'noop'),
    })),
  }
}

export async function updateCarrinho(
  id: number,
  qtde: number,
  preco?: number,
  entidadeId?: number,
  lojaId?: number,
  qtdeCaixa = 1,
): Promise<LojaVirtualCarrinhoItemUpsertResponse> {
  return addToCarrinho(id, qtdeCaixa, qtde, preco, entidadeId, lojaId)
}

export async function removeFromCarrinho(
  id: number,
  entidadeId?: number,
  lojaId?: number
): Promise<LojaVirtualCarrinhoItemUpsertResponse> {
  return addToCarrinho(id, 1, 0, undefined, entidadeId, lojaId)
}

export async function clearCarrinho(
  entidadeId?: number,
  lojaId?: number
): Promise<LojaVirtualSimpleStatusResponse> {
  const resolvedEntidadeId = resolveCarrinhoEntidadeId(entidadeId)
  return fetchApi<LojaVirtualSimpleStatusResponse>(
    buildCarrinhoEndpoint(resolvedEntidadeId, 'limpar', lojaId),
    { method: 'POST' }
  )
}

export async function recalculateCarrinho(
  entidadeId?: number,
  lojaId?: number,
  options?: { modoPreco?: 'NORMAL' | 'BENEFICIO' }
): Promise<LojaVirtualCarrinhoRecalculoResponse> {
  const resolvedEntidadeId = resolveCarrinhoEntidadeId(entidadeId)
  const normalizedLojaId = getLojaIdValue(lojaId)

  const response = await fetchApi<LojaVirtualSimpleStatusResponse & {
    contexto_preco?: LojaVirtualCheckoutPagamentosResponse['contexto_preco']
  }>(
    buildCarrinhoEndpoint(resolvedEntidadeId, 'recalcular', lojaId),
    {
      method: 'POST',
      body: JSON.stringify({
        ...(normalizedLojaId ? { loja_id: normalizedLojaId } : {}),
        ...(options?.modoPreco ? { modo_preco: options.modoPreco } : {}),
      }),
    }
  )

  return {
    ...response,
    contextoPreco: response.contexto_preco
      ? normalizeCheckoutPriceContext(response.contexto_preco)
      : undefined,
  }
}

export async function cotarFrete(input: {
  destinoCep: string
  itens: FreteCotacaoInputItem[]
  destinoCnpj?: string
  origemCep?: string
  origemCnpj?: string
  forcarRecalculo?: boolean
  notaId?: number
}): Promise<FreteCotacaoResult> {
  const payload = buildFreteCotarPayload(input)
  const { response, options } = await executeFreteCotacaoRequest(payload)

  return {
    status: Math.trunc(normalizeFreteNumber(response.status) ?? 0),
    cacheHit: Math.trunc(normalizeFreteNumber(response.cache_hit) ?? 0),
    motivo: response.motivo ?? undefined,
    options,
    rawCotacao: response.cotacao,
  }
}

// Compat legado: mantém assinatura antiga, agora usando /frete/cotar.
export async function cotacaoFrete(cep: string, document: string, cifAPrazo: boolean): Promise<FreteCotacaoResult> {
  return cotarFrete({
    destinoCep: cep,
    destinoCnpj: document,
    forcarRecalculo: cifAPrazo,
    itens: [
      {
        qtde: 1,
        peso: 1,
        altura: 10,
        largura: 10,
        comprimento: 10,
      },
    ],
  })
}

type LojaVirtualAuthenticatedBuyerResult = {
  identifier: string
  authenticated: boolean
  entidadeId?: number
  name?: string
  email?: string
  cnpj?: string
  document?: string
  stateRegistration?: string
  tipoPessoa?: number | null
  buyerUser?: {
    contatoId?: number | null
    nome?: string
    tipoUsuario: 'admin' | 'vendedor'
    papel: 'admin' | 'vendedor'
    markupPercentual: number
    acessoFinanceiro: boolean
    acessoPedidos: boolean
    acessoUsuarios: boolean
    acessoMarketplaceAnuncios: boolean
    acessoMarketplacePerguntas: boolean
    modoAcesso: 'empresa' | 'contato'
  }
  buyerSessionToken?: string
  reason?: string
  errorCode?: string
  requiresDocument?: boolean
  accessScope?: 'buyer' | 'order_tracking'
  legacyCheckout?: {
    type: 'quote' | 'order'
    id: number
    hash: string
    number: number | null
    checkoutSource?: string
    siteOrcamentoId?: number | null
    redirectPath?: string
  }
}

export type PasswordlessLoginChannel = 'whatsapp' | 'email'

export type PasswordlessWhatsappDestination = {
  destinationType: 'entidade' | 'contato'
  contactId: number | null
  last4: string
  displayName?: string
  principal: boolean
  phone?: string
}

export type PasswordlessEmailDestination = {
  destinationType: 'entidade' | 'contato'
  contactId: number | null
  maskedEmail: string
  displayName?: string
  principal: boolean
}

export type PasswordlessDestinationSelection = {
  destinationType?: 'entidade' | 'contato'
  contactId?: number | null
}

export type PasswordlessLoginRequestResult = {
  sent: boolean
  channel: PasswordlessLoginChannel
  maskedDestination: string
  requestId?: string | number | null
  expiresIn: number
  retryAfterSeconds?: number
  reason?: string
  errorCode?: string
  requiresDocument?: boolean
  availableChannels: {
    whatsapp: boolean
    email: boolean
  }
  whatsappDestinations: PasswordlessWhatsappDestination[]
  emailDestinations: PasswordlessEmailDestination[]
}

export type PasswordlessDestinationsResult = {
  loaded: boolean
  reason?: string
  errorCode?: string
  requiresDocument?: boolean
  availableChannels: {
    whatsapp: boolean
    email: boolean
  }
  whatsappDestinations: PasswordlessWhatsappDestination[]
  emailDestinations: PasswordlessEmailDestination[]
}

export type PasswordlessContactRequestResult = {
  requested: boolean
  contactId?: number | null
  reason?: string
  errorCode?: string
  requiresDocument?: boolean
}

export type PasswordRecoveryChannel = PasswordlessLoginChannel

export type PasswordRecoveryRequestResult = PasswordlessLoginRequestResult

export type PasswordRecoveryDestinationsResult = PasswordlessDestinationsResult

export type PasswordRecoveryVerifyResult = {
  verified: boolean
  resetToken?: string | null
  reason?: string
  errorCode?: string
  requiresDocument?: boolean
  expiresIn: number
}

export type PasswordRecoveryResetResult = {
  reset: boolean
  message?: string
  reason?: string
  errorCode?: string
}

export type BuyerPasswordChangeChannel = PasswordRecoveryChannel

export type BuyerPasswordChangeDestinationsResult = PasswordRecoveryDestinationsResult & {
  principalType?: 'empresa' | 'contato'
  maskedEmail?: string
}

export type BuyerPasswordChangeRequestResult = PasswordRecoveryRequestResult & {
  principalType?: 'empresa' | 'contato'
}

export type BuyerPasswordChangeVerifyResult = PasswordRecoveryVerifyResult

export type BuyerPasswordChangeResetResult = PasswordRecoveryResetResult

const normalizePasswordlessChannel = (value: unknown): PasswordlessLoginChannel => {
  const normalized = String(value ?? '').trim().toLowerCase()
  return normalized === 'email' || normalized === 'e-mail' ? 'email' : 'whatsapp'
}

const normalizePossibleWhatsappDigits = (value: unknown) => {
  const digits = normalizePhoneToStoredDigits(normalizeCheckoutText(value))
  return isPossiblePhoneDigits(digits) ? digits : ''
}

const resolvePasswordlessWhatsappDestinationPhone = (
  destination: NonNullable<LojaVirtualCompradorPasswordlessRequestResponse['whatsapp_destinos']>[number] | null | undefined
) => {
  if (!destination) return ''

  return normalizePossibleWhatsappDigits(
    destination.whatsapp
    ?? destination.telefone
    ?? destination.celular
    ?? destination.numero
    ?? destination.phone
    ?? destination.destino
  )
}

const mapPasswordlessWhatsappDestinations = (
  destinations: LojaVirtualCompradorPasswordlessRequestResponse['whatsapp_destinos']
): PasswordlessWhatsappDestination[] =>
  (destinations ?? [])
    .map((item) => {
      const destinationType = item?.destino_tipo === 'entidade' ? 'entidade' : 'contato'
      const last4 = String(item?.last4 ?? '').trim()
      if (!last4) return null
      const phone = resolvePasswordlessWhatsappDestinationPhone(item)
      const displayName = String(item?.nome_exibicao ?? '').trim()

      const destination: PasswordlessWhatsappDestination = {
        destinationType,
        contactId: normalizeEntidadeId(item?.contato_id) ?? null,
        last4,
        principal: parseBooleanLike(item?.principal),
      }

      if (displayName) destination.displayName = displayName
      if (phone) destination.phone = phone

      return destination
    })
    .filter((item): item is PasswordlessWhatsappDestination => Boolean(item))

const mapPasswordlessEmailDestinations = (
  destinations: LojaVirtualCompradorPasswordlessRequestResponse['email_destinos']
): PasswordlessEmailDestination[] =>
  (destinations ?? [])
    .map((item) => {
      const maskedEmail = String(item?.email_mascarado ?? '').trim()
      if (!maskedEmail) return null
      const displayName = String(item?.nome_exibicao ?? '').trim()
      const destination: PasswordlessEmailDestination = {
        destinationType: item?.destino_tipo === 'entidade' ? 'entidade' : 'contato',
        contactId: normalizeEntidadeId(item?.contato_id) ?? null,
        maskedEmail,
        principal: parseBooleanLike(item?.principal),
      }

      if (displayName) destination.displayName = displayName
      return destination
    })
    .filter((item): item is PasswordlessEmailDestination => Boolean(item))

const normalizeBuyerPasswordChangePrincipalType = (value: unknown): 'empresa' | 'contato' | undefined => {
  const normalized = String(value ?? '').trim().toLowerCase()
  if (normalized === 'contato') return 'contato'
  if (normalized === 'empresa') return 'empresa'
  return undefined
}

function normalizeStorefrontRelativePath(value: unknown): string | undefined {
  const raw = typeof value === 'string' ? value.trim() : ''
  if (!raw) return undefined
  if (/^[a-z][a-z0-9+.-]*:/i.test(raw) || raw.startsWith('//')) return undefined
  return raw.startsWith('/') ? raw : `/${raw}`
}

function normalizeBuyerUserPayload(
  value: LojaVirtualCompradorUsuarioLoja | null | undefined
): LojaVirtualAuthenticatedBuyerResult['buyerUser'] & { buyerSessionToken?: string } {
  const tipoUsuario = String(value?.tipoUsuario ?? value?.tipo_usuario ?? value?.papel ?? '').trim().toLowerCase() === 'vendedor'
    ? 'vendedor'
    : 'admin'
  const modoAcesso = String(value?.modoAcesso ?? value?.modo_acesso ?? '').trim().toLowerCase() === 'contato' ? 'contato' : 'empresa'
  const contatoId = normalizeEntidadeId(value?.contatoId ?? value?.contato_id) ?? null
  const markupPercentual = normalizeFreteNumber(value?.markupPercentual ?? value?.markup_percentual)
  const buyerSessionToken = String(value?.buyerSessionToken ?? value?.buyer_session_token ?? '').trim()

  return {
    contatoId,
    nome: String(value?.nome ?? '').trim() || undefined,
    tipoUsuario,
    papel: tipoUsuario,
    markupPercentual: typeof markupPercentual === 'number' && Number.isFinite(markupPercentual)
      ? Number(markupPercentual.toFixed(2))
      : 0,
    acessoFinanceiro: parseBooleanLike(value?.acessoFinanceiro ?? value?.acesso_financeiro),
    acessoPedidos: parseBooleanLike(value?.acessoPedidos ?? value?.acesso_pedidos),
    acessoUsuarios: parseBooleanLike(value?.acessoUsuarios ?? value?.acesso_usuarios),
    acessoMarketplaceAnuncios: parseBooleanLike(value?.acessoMarketplaceAnuncios),
    acessoMarketplacePerguntas: parseBooleanLike(value?.acessoMarketplacePerguntas),
    modoAcesso,
    ...(buyerSessionToken ? { buyerSessionToken } : {})
  }
}

function normalizeLegacyCheckoutTarget(
  value: LojaVirtualCompradorLoginResponse['legacy_checkout']
): LojaVirtualAuthenticatedBuyerResult['legacyCheckout'] | undefined {
  const id = normalizeEntidadeId(value?.id)
  if (!id) return undefined

  const rawType = normalizeCheckoutText(value?.tipo).toLocaleLowerCase('pt-BR')
  const type = rawType === 'orcamento' || rawType === 'quote'
    ? 'quote'
    : rawType === 'pedido' || rawType === 'order'
      ? 'order'
      : null
  if (!type) return undefined

  const hash = normalizeCheckoutText(value?.hash).toLocaleLowerCase('pt-BR')
  const number = normalizeEntidadeId(value?.numero) ?? null
  const checkoutSource = normalizeCheckoutText(value?.checkout_source)
  const siteOrcamentoId = normalizeEntidadeId(value?.site_orcamento_id) ?? null
  const redirectPath = normalizeStorefrontRelativePath(value?.redirect_path)

  return {
    type,
    id,
    hash,
    number,
    ...(checkoutSource ? { checkoutSource } : {}),
    ...(siteOrcamentoId ? { siteOrcamentoId } : {}),
    ...(redirectPath ? { redirectPath } : {}),
  }
}

function mapAuthenticatedBuyerResponse(
  response: LojaVirtualCompradorLoginResponse,
  fallbackIdentifier: string = ''
): LojaVirtualAuthenticatedBuyerResult {
  const comprador = response.comprador
  const identifier = comprador?.email?.trim()
    || fallbackIdentifier
    || (typeof comprador?.id === 'number' ? String(comprador.id) : '')
  const fallbackDocument = fallbackIdentifier.includes('@') ? '' : fallbackIdentifier
  const cnpj = normalizeDigits(
    comprador?.cpf_cnpj
    ?? comprador?.cpfCnpj
    ?? comprador?.cnpj
    ?? comprador?.documento
    ?? fallbackDocument
  )
  const entidadeId = normalizeEntidadeId(
    comprador?.entidade_id
    ?? comprador?.id
    ?? comprador?.usuario_id
  )
  const statusOk = response.status === 1
  const accessScope = String(response.access_scope ?? response.accessScope ?? '').trim().toLowerCase() === 'order_tracking'
    ? 'order_tracking' as const
    : 'buyer' as const
  const authenticated = accessScope === 'buyer' && statusOk && typeof entidadeId === 'number'
  const tipoPessoaRaw = normalizeFreteNumber(
    comprador?.tipoPessoa
    ?? comprador?.tipo_pessoa
    ?? comprador?.tipopessoa
  )
  const apiReason = typeof response.motivo === 'string' && response.motivo.trim()
    ? response.motivo.trim()
    : undefined
  const reason = statusOk && accessScope === 'buyer' && typeof entidadeId !== 'number'
    ? 'Login do comprador sem entidade_id válido.'
    : apiReason
  const errorCode = typeof response.error === 'string' && response.error.trim()
    ? response.error.trim()
    : undefined
  const requiresDocument = parseBooleanLike(response.requires_document)
  const buyerUser = normalizeBuyerUserPayload(response.usuarioLoja ?? response.usuario_loja)
  const legacyCheckout = normalizeLegacyCheckoutTarget(response.legacy_checkout)
  const buyerSessionToken = String(
    buyerUser.buyerSessionToken
    ?? response.buyerSessionToken
    ?? response.buyer_session_token
    ?? ''
  ).trim()

  if (authenticated) {
    if (buyerSessionToken) {
      setBuyerSessionToken(buyerSessionToken)
    }
    setAuthenticatedEntidadeId(entidadeId ?? null)
  } else if (statusOk && accessScope === 'buyer' && typeof entidadeId !== 'number') {
    setAuthenticatedEntidadeId(null)
  }

  return {
    identifier,
    authenticated,
    entidadeId,
    name: comprador?.nome?.trim() || undefined,
    email: comprador?.email?.trim() || undefined,
    cnpj: cnpj || undefined,
    document: cnpj || undefined,
    stateRegistration: String(comprador?.ie ?? comprador?.inscricao_estadual ?? '').trim() || undefined,
    tipoPessoa: typeof tipoPessoaRaw === 'number' && Number.isFinite(tipoPessoaRaw)
      ? Math.trunc(tipoPessoaRaw)
      : null,
    buyerUser: {
      contatoId: buyerUser.contatoId,
      nome: buyerUser.nome,
      tipoUsuario: buyerUser.tipoUsuario,
      papel: buyerUser.papel,
      markupPercentual: buyerUser.markupPercentual,
      acessoFinanceiro: buyerUser.acessoFinanceiro,
      acessoPedidos: buyerUser.acessoPedidos,
      acessoUsuarios: buyerUser.acessoUsuarios,
      acessoMarketplaceAnuncios: buyerUser.acessoMarketplaceAnuncios,
      acessoMarketplacePerguntas: buyerUser.acessoMarketplacePerguntas,
      modoAcesso: buyerUser.modoAcesso,
    },
    buyerSessionToken: buyerSessionToken || undefined,
    reason,
    errorCode,
    requiresDocument,
    accessScope,
    ...(legacyCheckout ? { legacyCheckout } : {}),
  }
}

export async function login(
  loginValue: string,
  senha: string,
  documento?: string
): Promise<LojaVirtualAuthenticatedBuyerResult> {
  const normalizedLogin = loginValue.trim()
  const payload: Record<string, unknown> = {
    login: normalizedLogin,
    senha,
  }
  const documentoDigits = normalizeDigits(documento)
  if (documentoDigits.length === 11 || documentoDigits.length === 14) {
    payload.documento = documentoDigits
  }

  const lojaId = getLojaIdValue()
  if (lojaId) {
    payload.loja_id = lojaId
  }

  const response = await fetchApi<LojaVirtualCompradorLoginResponse>(
    '/loja-virtual/v1/auth/comprador/login',
    {
      method: 'POST',
      body: JSON.stringify(payload),
      skipBuyerSessionToken: true,
    }
  )

  return mapAuthenticatedBuyerResponse(response, normalizedLogin)
}

export async function loginLegacyCheckout(input: {
  uid: number
  auth: string
  compra: string
}): Promise<LojaVirtualAuthenticatedBuyerResult> {
  const lojaId = getLojaIdValue()
  const response = await fetchApi<LojaVirtualCompradorLoginResponse>(
    '/loja-virtual/v1/auth/comprador/legacy-checkout',
    {
      method: 'POST',
      body: JSON.stringify({
        uid: Math.trunc(input.uid),
        auth: input.auth.trim(),
        compra: input.compra.trim(),
        ...(lojaId ? { loja_id: lojaId } : {}),
      }),
    }
  )

  return mapAuthenticatedBuyerResponse(response, String(Math.trunc(input.uid)))
}

export async function requestPasswordlessLogin(
  identifier: string,
  channel: PasswordlessLoginChannel = 'whatsapp',
  selection?: PasswordlessDestinationSelection,
  documento?: string
): Promise<PasswordlessLoginRequestResult> {
  const normalizedIdentifier = identifier.trim()
  const documentDigits = normalizeDigits(documento)
  const lojaId = getLojaIdValue()
  const response = await fetchApi<LojaVirtualCompradorPasswordlessRequestResponse>(
    '/loja-virtual/v1/auth/comprador/passwordless/request',
    {
      method: 'POST',
      body: JSON.stringify({
        identificador: normalizedIdentifier,
        canal: channel,
        ...(documentDigits ? { documento: documentDigits } : {}),
        ...(selection?.destinationType ? { destino_tipo: selection.destinationType } : {}),
        ...(selection?.contactId ? { contato_id: selection.contactId } : {}),
        ...(lojaId ? { loja_id: lojaId } : {}),
      }),
    }
  )

  const availableChannels = {
    whatsapp: parseBooleanLike(response?.canais_disponiveis?.whatsapp),
    email: parseBooleanLike(response?.canais_disponiveis?.email),
  }
  const expiresIn = Number.parseInt(String(response?.expires_in ?? ''), 10)
  const retryAfterSeconds = Number.parseInt(String(response?.retry_after_seconds ?? ''), 10)

  return {
    sent: response.status === 1,
    channel: normalizePasswordlessChannel(response.canal ?? channel),
    maskedDestination: String(response.destino_mascarado ?? '').trim(),
    requestId: response.solicitacao_id ?? null,
    expiresIn: Number.isFinite(expiresIn) && expiresIn > 0 ? expiresIn : 600,
    retryAfterSeconds: Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0 ? retryAfterSeconds : undefined,
    reason: String(response.motivo ?? '').trim() || undefined,
    errorCode: String(response.error ?? '').trim() || undefined,
    requiresDocument: parseBooleanLike(response.requires_document),
    availableChannels,
    whatsappDestinations: mapPasswordlessWhatsappDestinations(response.whatsapp_destinos),
    emailDestinations: mapPasswordlessEmailDestinations(response.email_destinos),
  }
}

export async function getPasswordlessDestinations(
  identifier: string,
  documento?: string
): Promise<PasswordlessDestinationsResult> {
  const normalizedIdentifier = identifier.trim()
  const documentDigits = normalizeDigits(documento)
  const lojaId = getLojaIdValue()
  const response = await fetchApi<LojaVirtualCompradorPasswordlessDestinationsResponse>(
    '/loja-virtual/v1/auth/comprador/passwordless/destinations',
    {
      method: 'POST',
      body: JSON.stringify({
        identificador: normalizedIdentifier,
        ...(documentDigits ? { documento: documentDigits } : {}),
        ...(lojaId ? { loja_id: lojaId } : {}),
      }),
    }
  )

  return {
    loaded: response.status === 1,
    reason: String(response.motivo ?? '').trim() || undefined,
    errorCode: String(response.error ?? '').trim() || undefined,
    requiresDocument: parseBooleanLike(response.requires_document),
    availableChannels: {
      whatsapp: parseBooleanLike(response?.canais_disponiveis?.whatsapp),
      email: parseBooleanLike(response?.canais_disponiveis?.email),
    },
    whatsappDestinations: mapPasswordlessWhatsappDestinations(response.whatsapp_destinos),
    emailDestinations: mapPasswordlessEmailDestinations(response.email_destinos),
  }
}

export async function requestPasswordlessContact(input: {
  identifier: string
  documento?: string
  nome: string
  whatsapp: string
  cargo: string
}): Promise<PasswordlessContactRequestResult> {
  const normalizedIdentifier = input.identifier.trim()
  const documentDigits = normalizeDigits(input.documento)
  const lojaId = getLojaIdValue()
  const response = await fetchApi<LojaVirtualCompradorPasswordlessContactRequestResponse>(
    '/loja-virtual/v1/auth/comprador/passwordless/contact-request',
    {
      method: 'POST',
      body: JSON.stringify({
        identificador: normalizedIdentifier,
        ...(documentDigits ? { documento: documentDigits } : {}),
        nome: input.nome.trim(),
        whatsapp: normalizePhoneDigits(input.whatsapp),
        cargo: input.cargo.trim(),
        ...(lojaId ? { loja_id: lojaId } : {}),
      }),
    }
  )

  return {
    requested: response.status === 1,
    contactId: normalizeEntidadeId(response.contato_id) ?? null,
    reason: String(response.motivo ?? '').trim() || undefined,
    errorCode: String(response.error ?? '').trim() || undefined,
    requiresDocument: parseBooleanLike(response.requires_document),
  }
}

export async function verifyPasswordlessLogin(input: {
  identifier: string
  documento?: string
  code: string
  channel: PasswordlessLoginChannel
  requestId?: string | number | null
}): Promise<LojaVirtualAuthenticatedBuyerResult> {
  const normalizedIdentifier = input.identifier.trim()
  const documentDigits = normalizeDigits(input.documento)
  const lojaId = getLojaIdValue()
  const response = await fetchApi<LojaVirtualCompradorLoginResponse>(
    '/loja-virtual/v1/auth/comprador/passwordless/verify',
    {
      method: 'POST',
      body: JSON.stringify({
        identificador: normalizedIdentifier,
        ...(documentDigits ? { documento: documentDigits } : {}),
        codigo: input.code.replace(/\D/g, ''),
        canal: input.channel,
        ...(input.requestId ? { solicitacao_id: input.requestId } : {}),
        ...(lojaId ? { loja_id: lojaId } : {}),
      }),
    }
  )

  return mapAuthenticatedBuyerResponse(response, normalizedIdentifier)
}

export async function requestPasswordRecoveryCode(
  identifier: string,
  channel: PasswordRecoveryChannel = 'whatsapp',
  selection?: PasswordlessDestinationSelection,
  documento?: string
): Promise<PasswordRecoveryRequestResult> {
  const normalizedIdentifier = identifier.trim()
  const documentDigits = normalizeDigits(documento)
  const lojaId = getLojaIdValue()
  const response = await fetchApi<LojaVirtualCompradorPasswordlessRequestResponse>(
    '/loja-virtual/v1/auth/comprador/password-recovery/request',
    {
      method: 'POST',
      body: JSON.stringify({
        identificador: normalizedIdentifier,
        canal: channel,
        ...(documentDigits ? { documento: documentDigits } : {}),
        ...(selection?.destinationType ? { destino_tipo: selection.destinationType } : {}),
        ...(selection?.contactId ? { contato_id: selection.contactId } : {}),
        ...(lojaId ? { loja_id: lojaId } : {}),
      }),
    }
  )

  const availableChannels = {
    whatsapp: parseBooleanLike(response?.canais_disponiveis?.whatsapp),
    email: parseBooleanLike(response?.canais_disponiveis?.email),
  }
  const expiresIn = Number.parseInt(String(response?.expires_in ?? ''), 10)
  const retryAfterSeconds = Number.parseInt(String(response?.retry_after_seconds ?? ''), 10)

  return {
    sent: response.status === 1,
    channel: normalizePasswordlessChannel(response.canal ?? channel),
    maskedDestination: String(response.destino_mascarado ?? '').trim(),
    requestId: response.solicitacao_id ?? null,
    expiresIn: Number.isFinite(expiresIn) && expiresIn > 0 ? expiresIn : 600,
    retryAfterSeconds: Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0 ? retryAfterSeconds : undefined,
    reason: String(response.motivo ?? '').trim() || undefined,
    errorCode: String(response.error ?? '').trim() || undefined,
    requiresDocument: parseBooleanLike(response.requires_document),
    availableChannels,
    whatsappDestinations: mapPasswordlessWhatsappDestinations(response.whatsapp_destinos),
    emailDestinations: mapPasswordlessEmailDestinations(response.email_destinos),
  }
}

export async function getPasswordRecoveryDestinations(
  identifier: string,
  documento?: string
): Promise<PasswordRecoveryDestinationsResult> {
  const normalizedIdentifier = identifier.trim()
  const documentDigits = normalizeDigits(documento)
  const lojaId = getLojaIdValue()
  const response = await fetchApi<LojaVirtualCompradorPasswordlessDestinationsResponse>(
    '/loja-virtual/v1/auth/comprador/password-recovery/destinations',
    {
      method: 'POST',
      body: JSON.stringify({
        identificador: normalizedIdentifier,
        ...(documentDigits ? { documento: documentDigits } : {}),
        ...(lojaId ? { loja_id: lojaId } : {}),
      }),
    }
  )

  return {
    loaded: response.status === 1,
    reason: String(response.motivo ?? '').trim() || undefined,
    errorCode: String(response.error ?? '').trim() || undefined,
    requiresDocument: parseBooleanLike(response.requires_document),
    availableChannels: {
      whatsapp: parseBooleanLike(response?.canais_disponiveis?.whatsapp),
      email: parseBooleanLike(response?.canais_disponiveis?.email),
    },
    whatsappDestinations: mapPasswordlessWhatsappDestinations(response.whatsapp_destinos),
    emailDestinations: mapPasswordlessEmailDestinations(response.email_destinos),
  }
}

export async function verifyPasswordRecoveryCode(input: {
  identifier: string
  documento?: string
  code: string
  channel: PasswordRecoveryChannel
  requestId?: string | number | null
}): Promise<PasswordRecoveryVerifyResult> {
  const normalizedIdentifier = input.identifier.trim()
  const documentDigits = normalizeDigits(input.documento)
  const lojaId = getLojaIdValue()
  const response = await fetchApi<LojaVirtualCompradorPasswordRecoveryVerifyResponse>(
    '/loja-virtual/v1/auth/comprador/password-recovery/verify',
    {
      method: 'POST',
      body: JSON.stringify({
        identificador: normalizedIdentifier,
        ...(documentDigits ? { documento: documentDigits } : {}),
        codigo: input.code.replace(/\D/g, ''),
        canal: input.channel,
        ...(input.requestId ? { solicitacao_id: input.requestId } : {}),
        ...(lojaId ? { loja_id: lojaId } : {}),
      }),
    }
  )
  const expiresIn = Number.parseInt(String(response?.expires_in ?? ''), 10)

  return {
    verified: response.status === 1,
    resetToken: String(response.reset_token ?? response.resetToken ?? response.token ?? '').trim() || null,
    reason: String(response.motivo ?? '').trim() || undefined,
    errorCode: String(response.error ?? '').trim() || undefined,
    requiresDocument: parseBooleanLike(response.requires_document),
    expiresIn: Number.isFinite(expiresIn) && expiresIn > 0 ? expiresIn : 600,
  }
}

export async function resetPasswordWithRecoveryToken(input: {
  identifier: string
  documento?: string
  resetToken: string
  password: string
}): Promise<PasswordRecoveryResetResult> {
  const normalizedIdentifier = input.identifier.trim()
  const documentDigits = normalizeDigits(input.documento)
  const lojaId = getLojaIdValue()
  const response = await fetchApi<LojaVirtualCompradorPasswordRecoveryResetResponse>(
    '/loja-virtual/v1/auth/comprador/password-recovery/reset',
    {
      method: 'POST',
      body: JSON.stringify({
        identificador: normalizedIdentifier,
        ...(documentDigits ? { documento: documentDigits } : {}),
        reset_token: input.resetToken.trim(),
        senha: input.password,
        ...(lojaId ? { loja_id: lojaId } : {}),
      }),
    }
  )

  return {
    reset: response.status === 1,
    message: String(response.message ?? '').trim() || undefined,
    reason: String(response.motivo ?? '').trim() || undefined,
    errorCode: String(response.error ?? '').trim() || undefined,
  }
}

export async function getBuyerPasswordChangeDestinations(
  entidadeId: number | string
): Promise<BuyerPasswordChangeDestinationsResult> {
  const normalizedEntidadeId = normalizeEntidadeId(entidadeId)
  const response = await fetchApi<LojaVirtualCompradorPasswordChangeDestinationsResponse>(
    `/loja-virtual/v1/compradores/${encodeURIComponent(String(normalizedEntidadeId ?? entidadeId))}/senha/destinos`
  )

  return {
    loaded: response.status === 1,
    reason: String(response.motivo ?? '').trim() || undefined,
    errorCode: String(response.error ?? '').trim() || undefined,
    requiresDocument: parseBooleanLike(response.requires_document),
    principalType: normalizeBuyerPasswordChangePrincipalType(response.principal_tipo),
    maskedEmail: String(response.email_mascarado ?? '').trim() || undefined,
    availableChannels: {
      whatsapp: parseBooleanLike(response?.canais_disponiveis?.whatsapp),
      email: parseBooleanLike(response?.canais_disponiveis?.email),
    },
    whatsappDestinations: mapPasswordlessWhatsappDestinations(response.whatsapp_destinos),
    emailDestinations: mapPasswordlessEmailDestinations(response.email_destinos),
  }
}

export async function requestBuyerPasswordChangeCode(input: {
  entidadeId: number | string
  channel: BuyerPasswordChangeChannel
  selection?: {
    destinationType?: PasswordlessWhatsappDestination['destinationType']
    contactId?: number | null
  }
}): Promise<BuyerPasswordChangeRequestResult> {
  const normalizedEntidadeId = normalizeEntidadeId(input.entidadeId)
  const response = await fetchApi<LojaVirtualCompradorPasswordChangeRequestResponse>(
    `/loja-virtual/v1/compradores/${encodeURIComponent(String(normalizedEntidadeId ?? input.entidadeId))}/senha/codigo`,
    {
      method: 'POST',
      body: JSON.stringify({
        canal: input.channel,
        ...(input.selection?.destinationType ? { destino_tipo: input.selection.destinationType } : {}),
        ...(input.selection?.contactId ? { contato_id: input.selection.contactId } : {}),
      }),
    }
  )
  const availableChannels = {
    whatsapp: parseBooleanLike(response?.canais_disponiveis?.whatsapp),
    email: parseBooleanLike(response?.canais_disponiveis?.email),
  }
  const expiresIn = Number.parseInt(String(response?.expires_in ?? ''), 10)
  const retryAfterSeconds = Number.parseInt(String(response?.retry_after_seconds ?? ''), 10)

  return {
    sent: response.status === 1,
    channel: normalizePasswordlessChannel(response.canal ?? input.channel),
    principalType: normalizeBuyerPasswordChangePrincipalType(response.principal_tipo),
    maskedDestination: String(response.destino_mascarado ?? '').trim(),
    requestId: response.solicitacao_id ?? null,
    expiresIn: Number.isFinite(expiresIn) && expiresIn > 0 ? expiresIn : 600,
    retryAfterSeconds: Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0 ? retryAfterSeconds : undefined,
    reason: String(response.motivo ?? '').trim() || undefined,
    errorCode: String(response.error ?? '').trim() || undefined,
    requiresDocument: parseBooleanLike(response.requires_document),
    availableChannels,
    whatsappDestinations: mapPasswordlessWhatsappDestinations(response.whatsapp_destinos),
    emailDestinations: mapPasswordlessEmailDestinations(response.email_destinos),
  }
}

export async function verifyBuyerPasswordChangeCode(input: {
  entidadeId: number | string
  code: string
  channel: BuyerPasswordChangeChannel
  requestId?: string | number | null
}): Promise<BuyerPasswordChangeVerifyResult> {
  const normalizedEntidadeId = normalizeEntidadeId(input.entidadeId)
  const response = await fetchApi<LojaVirtualCompradorPasswordRecoveryVerifyResponse>(
    `/loja-virtual/v1/compradores/${encodeURIComponent(String(normalizedEntidadeId ?? input.entidadeId))}/senha/validar`,
    {
      method: 'POST',
      body: JSON.stringify({
        codigo: input.code.replace(/\D/g, ''),
        canal: input.channel,
        ...(input.requestId ? { solicitacao_id: input.requestId } : {}),
      }),
    }
  )
  const expiresIn = Number.parseInt(String(response?.expires_in ?? ''), 10)

  return {
    verified: response.status === 1,
    resetToken: String(response.reset_token ?? response.resetToken ?? response.token ?? '').trim() || null,
    reason: String(response.motivo ?? '').trim() || undefined,
    errorCode: String(response.error ?? '').trim() || undefined,
    requiresDocument: parseBooleanLike(response.requires_document),
    expiresIn: Number.isFinite(expiresIn) && expiresIn > 0 ? expiresIn : 600,
  }
}

export async function resetBuyerPassword(input: {
  entidadeId: number | string
  resetToken: string
  password: string
}): Promise<BuyerPasswordChangeResetResult> {
  const normalizedEntidadeId = normalizeEntidadeId(input.entidadeId)
  const response = await fetchApi<LojaVirtualCompradorPasswordRecoveryResetResponse>(
    `/loja-virtual/v1/compradores/${encodeURIComponent(String(normalizedEntidadeId ?? input.entidadeId))}/senha`,
    {
      method: 'POST',
      body: JSON.stringify({
        reset_token: input.resetToken.trim(),
        senha: input.password,
      }),
    }
  )

  return {
    reset: response.status === 1,
    message: String(response.message ?? '').trim() || undefined,
    reason: String(response.motivo ?? '').trim() || undefined,
    errorCode: String(response.error ?? '').trim() || undefined,
  }
}

export async function consumeBuyerAccessTicket(
  ticket: string
): Promise<LojaVirtualAuthenticatedBuyerResult & { redirectPath?: string }> {
  const normalizedTicket = ticket.trim()
  const response = await fetchApi<LojaVirtualCompradorAccessConsumeResponse>(
    `/loja-virtual/v1/auth/comprador/acessos/${encodeURIComponent(normalizedTicket)}/consumir`,
    {
      method: 'POST',
    }
  )

  return {
    ...mapAuthenticatedBuyerResponse(response),
    redirectPath: normalizeStorefrontRelativePath(response.redirect_path ?? response.redirectPath),
  }
}

export async function consumeBuyerAccessShortCode(
  code: string
): Promise<LojaVirtualAuthenticatedBuyerResult & { redirectPath?: string }> {
  const normalizedCode = code.trim()
  const response = await fetchApi<LojaVirtualCompradorAccessConsumeResponse>(
    `/loja-virtual/v1/auth/comprador/links-curtos/${encodeURIComponent(normalizedCode)}/consumir`,
    {
      method: 'POST',
    }
  )

  return {
    ...mapAuthenticatedBuyerResponse(response),
    redirectPath: normalizeStorefrontRelativePath(response.redirect_path ?? response.redirectPath),
  }
}

export type PedidoAcompanhamentoMilestone = {
  key: 'received' | 'payment_confirmed' | 'separation' | 'invoiced' | 'ready_for_pickup' | 'collected' | 'delivered'
  label: string
  state: 'done' | 'current' | 'pending'
  occurredAt: string | null
}

export type PedidoAcompanhamentoResult = {
  phase: 'payment' | 'tracking'
  redirectPath?: string
  order?: {
    number: number
    createdAt: string | null
    freightMode: 'CIF' | 'FOB'
    carrierId: number | null
    carrierName: string | null
    currentMilestone: PedidoAcompanhamentoMilestone['key']
    deliveryAddress?: {
      recipientName: string | null
      street: string | null
      number: string | null
      complement: string | null
      neighborhood: string | null
      postalCode: string | null
      city: string | null
      state: string | null
    } | null
  }
  milestones: PedidoAcompanhamentoMilestone[]
  documents?: Array<{
    key: 'danfe' | 'xml' | 'boleto'
    label: string
    name: string | null
    downloadUrl: string
  }>
  tracking?: {
    collected: boolean
    url: string | null
    code?: string | null
    provider?: 'ssw' | 'sao_miguel' | null
    status?: 'pending' | 'available' | 'not_found' | 'unavailable' | null
    message?: string | null
    updatedAt?: string | null
    deliveryForecast?: string | null
    events?: Array<{
      occurredAt: string | null
      city: string | null
      occurrence: string
      description: string | null
      type: string | null
      branch: string | null
    }>
  }
}

type PedidoAcompanhamentoApiResponse = {
  status?: number | string
  phase?: string | null
  redirect_path?: string | null
  order?: PedidoAcompanhamentoResult['order'] | null
  milestones?: PedidoAcompanhamentoMilestone[] | null
  documents?: PedidoAcompanhamentoResult['documents'] | null
  tracking?: PedidoAcompanhamentoResult['tracking'] | null
}

export async function getPedidoAcompanhamentoByCode(code: string): Promise<PedidoAcompanhamentoResult> {
  const normalizedCode = code.trim()
  const response = await fetchApi<PedidoAcompanhamentoApiResponse>(
    `/loja-virtual/v1/pedidos/acompanhamento/${encodeURIComponent(normalizedCode)}`,
    {
      cache: 'no-store',
      skipBuyerSessionCredentials: true,
      skipBuyerSessionToken: true,
    }
  )

  if (response.status !== 1 && response.status !== '1') {
    throw new Error('Link de acompanhamento inválido ou indisponível.')
  }

  if (response.phase === 'payment') {
    return {
      phase: 'payment',
      redirectPath: normalizeStorefrontRelativePath(response.redirect_path),
      milestones: [],
    }
  }

  if (response.phase !== 'tracking' || !response.order || !Array.isArray(response.milestones)) {
    throw new Error('Resposta de acompanhamento inválida.')
  }

  return {
    phase: 'tracking',
    order: response.order,
    milestones: response.milestones,
    documents: Array.isArray(response.documents) ? response.documents : [],
    tracking: response.tracking ?? { collected: false, url: null },
  }
}

export async function solicitarPedidoAcompanhamentoWhatsappNotificationSubscription(
  code: string
): Promise<PedidoWhatsappNotificationSubscriptionResult> {
  const normalizedCode = code.trim()
  if (!/^[A-Za-z0-9_-]{6,24}$/.test(normalizedCode)) {
    throw new Error('Link de acompanhamento inválido ou indisponível.')
  }

  return fetchApi<PedidoWhatsappNotificationSubscriptionResult>(
    `/loja-virtual/v1/pedidos/acompanhamento/${encodeURIComponent(normalizedCode)}/notificacoes-whatsapp/inscricao`,
    {
      method: 'POST',
      cache: 'no-store',
      skipBuyerSessionCredentials: true,
      skipBuyerSessionToken: true,
    }
  )
}

export async function checkClienteExists(input: {
  cpfCnpj?: string
  email?: string
  vendedorCadastroToken?: string
}, options?: Pick<FetchApiOptions, 'skipBuyerSessionCredentials' | 'skipBuyerSessionToken'>): Promise<ClienteExistsResult> {
  const cpfCnpj = normalizeDigits(input.cpfCnpj ?? '')
  const email = typeof input.email === 'string' ? input.email.trim() : ''
  const vendedorCadastroToken = typeof input.vendedorCadastroToken === 'string'
    ? input.vendedorCadastroToken.trim()
    : ''
  const lojaId = getLojaIdValue()

  const response = await fetchApi<ClienteExistsApiResponse>('/clientes/exists', {
    method: 'POST',
    body: JSON.stringify({
      ...(cpfCnpj ? { cpf_cnpj: cpfCnpj } : {}),
      ...(email ? { email } : {}),
      ...(vendedorCadastroToken ? { vendedor_cadastro_token: vendedorCadastroToken } : {}),
      ...(lojaId ? { loja_id: lojaId } : {}),
    }),
    ...options,
  })

  const nome = String(response?.nome ?? response?.razao ?? '').trim()
  const id = String(response?.id ?? '').trim()

  return {
    exists: parseBooleanLike(response?.exists),
    id,
    nome,
    acessoLojaConfigurado: parseBooleanLike(response?.acesso_loja_configurado ?? response?.acessoLojaConfigurado),
    recuperacaoAcessoDisponivel: parseBooleanLike(response?.recuperacao_acesso_disponivel ?? response?.recuperacaoAcessoDisponivel),
    cadastroLojaCompletarPermitido: parseBooleanLike(
      response?.cadastro_loja_completar_permitido ?? response?.cadastroLojaCompletarPermitido
    ),
  }
}

export async function lookupPedidoTracking(input: {
  orderNumber: string | number
  document: string
}): Promise<OrderTrackingLookupResult> {
  const documentDigits = normalizeDigits(String(input.document ?? ''))
  const orderNumberDigits = normalizeDigits(String(input.orderNumber ?? ''))

  if (![11, 14].includes(documentDigits.length)) {
    throw new Error('Informe um CPF ou CNPJ válido.')
  }

  if (!orderNumberDigits) {
    throw new Error('Informe um número de pedido válido.')
  }

  let response: {
    status?: number
    entidadeId?: number
    buyer?: OrderTrackingLookupBuyer | null
    order?: any
    motivo?: string
    error?: string
    message?: string
  }

  try {
    response = await fetchApi<typeof response>(
      '/docs/pedidos/rastreamento',
      {
        method: 'POST',
        body: JSON.stringify({
          orderNumber: orderNumberDigits,
          document: documentDigits,
        }),
        skipBuyerSessionCredentials: true,
        skipBuyerSessionToken: true,
      }
    )
  } catch (error) {
    if (isApiRequestError(error) && error.status === 404) {
      throw new OrderTrackingLookupNotFoundError()
    }

    throw error
  }

  if (response?.status !== 1 || !response?.order || !response?.buyer || !response?.entidadeId) {
    throw new Error(
      String(response?.message ?? response?.motivo ?? response?.error ?? 'Não foi possível consultar o rastreamento.')
    )
  }

  return {
    entidadeId: Math.trunc(response.entidadeId),
    buyer: response.buyer,
    order: response.order,
  }
}

export async function getClienteById(id: string | number): Promise<any> {
  const normalizedId = encodeURIComponent(String(id).trim())
  return fetchApi(`/clientes/${normalizedId}`)
}

export async function getClientes(
  params?: Record<string, string | number | boolean | null | undefined>
): Promise<any> {
  if (params && typeof params === 'object') {
    const explicitId = params.id ?? params.cliente_id ?? params.clienteId
    if (explicitId !== null && explicitId !== undefined && String(explicitId).trim()) {
      return getClienteById(String(explicitId))
    }
  }

  console.warn('GET /clientes não está exposto no /api/docs. Use /clientes/{id} ou /clientes/exists.')
  return []
}

export async function fetchAtividadesTree(params: {
  paiId: number
  signal?: AbortSignal
}): Promise<{ items: AtividadeTreeItem[] }> {
  const searchParams = new URLSearchParams()
  searchParams.set('pai_id', String(Math.max(0, Math.trunc(params.paiId))))

  const response = await fetchApi<AtividadeTreeApiResponse>(
    `/atividades/tree?${searchParams.toString()}`,
    {
      method: 'GET',
      signal: params.signal,
    }
  )

  const rawItems = Array.isArray(response?.items)
    ? response.items
    : Array.isArray(response?.data)
      ? response.data
      : []

  const normalizeAtividadeFlag = (value: unknown): 0 | 1 | null => {
    if (value === null || value === undefined || value === '') return null
    if (typeof value === 'boolean') return value ? 1 : 0
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase()
      if (normalized === '1' || normalized === 'true') return 1
      if (normalized === '0' || normalized === 'false') return 0
      return null
    }

    const parsed = Number(value)
    if (parsed === 1 || parsed === 0) return parsed as 0 | 1
    return null
  }

  const items = rawItems
    .filter((item): item is Record<string, unknown> => Boolean(item && typeof item === 'object'))
    .map((item) => ({
      id: item.id as string | number,
      descricao: typeof item.descricao === 'string' ? item.descricao : null,
      pai_id: Number.isFinite(Number(item.pai_id)) ? Number(item.pai_id) : null,
      icone: typeof item.icone === 'string' ? item.icone : null,
      filhos: Number.isFinite(Number(item.filhos)) ? Number(item.filhos) : null,
      pessoa_fisica_cpf: normalizeAtividadeFlag(item.pessoa_fisica_cpf ?? item.pessoaFisicaCpf),
      consumo: normalizeAtividadeFlag(
        item.consumo
        ?? item.eh_consumo
        ?? item.ehConsumo
        ?? item.is_consumo
        ?? item.isConsumo
        ?? item.is_consumption
        ?? item.isConsumption
      ),
      revenda: normalizeAtividadeFlag(
        item.revenda
        ?? item.eh_revenda
        ?? item.ehRevenda
        ?? item.is_revenda
        ?? item.isRevenda
        ?? item.is_resale
        ?? item.isResale
      ),
      fabricacao: normalizeAtividadeFlag(
        item.fabricacao
        ?? item.fabricacao_propria
        ?? item.fabricacaoPropria
        ?? item.eh_fabricacao
        ?? item.ehFabricacao
        ?? item.is_fabricacao
        ?? item.isFabricacao
        ?? item.is_manufacturing
        ?? item.isManufacturing
      ),
    }))
    .filter((item) => String(item.id ?? '').trim() !== '')

  return { items }
}

export async function fetchAtividadesFilters(): Promise<AtividadesFiltersResponse> {
  return fetchApi<AtividadesFiltersResponse>('/atividades/filters')
}

export async function createLojaVirtualPreCadastro(payload: {
  nome: string
  whatsapp: string
  cpfCnpj?: string
  atividadeId?: number
  lojaId?: number
  origem?: string
}): Promise<LojaVirtualPreCadastroResult> {
  const lojaId = getLojaIdValue(payload.lojaId)
  const response = await fetchApi<LojaVirtualPreCadastroApiResponse>('/loja-virtual/v1/pre-cadastros', {
    method: 'POST',
    body: JSON.stringify({
      nome: String(payload.nome ?? '').trim(),
      whatsapp: toInternationalRequestPhone(payload.whatsapp),
      ...(payload.cpfCnpj ? { cpf_cnpj: normalizeDigits(payload.cpfCnpj) } : {}),
      ...(Number(payload.atividadeId) > 0 ? { atividade_id: Math.trunc(Number(payload.atividadeId)) } : {}),
      ...(lojaId ? { loja_id: lojaId } : {}),
      ...(payload.origem ? { origem: String(payload.origem).trim() } : {}),
    }),
  })

  return {
    id: Number(response?.id ?? 0) || 0,
    nome: String(response?.nome ?? '').trim(),
    whatsapp: normalizePhoneDigits(response?.whatsapp ?? ''),
    cpfCnpj: normalizeDigits(response?.cpf_cnpj ?? ''),
    origem: String(response?.origem ?? '').trim(),
    createdAt: typeof response?.created_at === 'string' && response.created_at.trim()
      ? response.created_at.trim()
      : null,
    cadastrado: parseBooleanLike(response?.cadastrado),
    jaExistia: parseBooleanLike(response?.ja_existia),
    motivo: String(response?.motivo ?? '').trim(),
  }
}

export async function getLojaVirtualPreCadastroRetomada(
  code: string
): Promise<LojaVirtualPreCadastroRetomadaResult> {
  const normalizedCode = String(code ?? '').trim()
  const response = await fetchApi<LojaVirtualPreCadastroRetomadaApiResponse>(
    `/loja-virtual/v1/pre-cadastros/retomada/${encodeURIComponent(normalizedCode)}`,
    { method: 'GET' }
  )
  if (!parseBooleanLike(response?.status)) {
    throw new Error(String(response?.motivo ?? '').trim() || 'Link de retomada inválido ou expirado.')
  }
  const preCadastro = response?.pre_cadastro ?? {}
  const cpfCnpj = normalizeDigits(preCadastro?.cpf_cnpj ?? '')
  const atividadeId = Number(preCadastro?.atividade_id ?? 0) || 0
  const lojaId = getLojaIdValue(Number(preCadastro?.loja_id ?? 0) || undefined) || 0

  return {
    preCadastroId: Number(preCadastro?.id ?? 0) || 0,
    nome: String(preCadastro?.nome ?? '').trim(),
    whatsapp: normalizePhoneDigits(preCadastro?.whatsapp ?? ''),
    cpfCnpj,
    atividadeId,
    atividade: String(preCadastro?.atividade ?? '').trim(),
    lojaId,
    documentType: cpfCnpj.length === 11 ? 'cpf' : 'cnpj',
    whatsappValidado: parseBooleanLike(response?.whatsapp_validado),
    motivo: String(response?.motivo ?? preCadastro?.motivo ?? '').trim(),
  }
}

export async function generateLojaVirtualPreCadastroPhoneCode(input: {
  whatsapp: string
  lojaId?: number
}): Promise<LojaVirtualPreCadastroPhoneCodeResult> {
  const lojaId = getLojaIdValue(input.lojaId)
  const response = await fetchApi<LojaVirtualPreCadastroPhoneCodeApiResponse>('/loja-virtual/v1/pre-cadastros/telefone/codigo', {
    method: 'POST',
    body: JSON.stringify({
      whatsapp: toInternationalRequestPhone(input.whatsapp),
      ...(lojaId ? { loja_id: lojaId } : {}),
    }),
  })

  return {
    id: Number(response?.id ?? 0) || 0,
    whatsapp: normalizePhoneDigits(response?.whatsapp ?? ''),
    codigo: String(response?.codigo ?? '').trim().toUpperCase(),
    expiresAt: typeof response?.expires_at === 'string' && response.expires_at.trim()
      ? response.expires_at.trim()
      : null,
    validatedAt: typeof response?.validated_at === 'string' && response.validated_at.trim()
      ? response.validated_at.trim()
      : null,
    createdAt: typeof response?.created_at === 'string' && response.created_at.trim()
      ? response.created_at.trim()
      : null,
    updatedAt: typeof response?.updated_at === 'string' && response.updated_at.trim()
      ? response.updated_at.trim()
      : null,
    whatsappOfficialStatus:
      response?.whatsapp_oficial_status === 'sent' || response?.whatsapp_oficial_status === 'error'
        ? response.whatsapp_oficial_status
        : null,
    whatsappOfficialMotivo: String(response?.whatsapp_oficial_motivo ?? '').trim(),
    whatsappLegacyStatus:
      response?.whatsapp_legado_status === 'sent' || response?.whatsapp_legado_status === 'error'
        ? response.whatsapp_legado_status
        : null,
    whatsappLegacyMotivo: String(response?.whatsapp_legado_motivo ?? '').trim(),
    motivo: String(response?.motivo ?? '').trim(),
  }
}

export async function sendLojaVirtualPreCadastroPhoneCodeByEmail(input: {
  whatsapp: string
  email: string
  lojaId: number
}): Promise<LojaVirtualPreCadastroPhoneCodeEmailResult> {
  const lojaId = getLojaIdValue(input.lojaId)
  if (!lojaId) {
    throw new Error('Loja inválida para o envio do código por e-mail.')
  }

  const response = await fetchApi<LojaVirtualPreCadastroPhoneCodeEmailApiResponse>(
    '/loja-virtual/v1/pre-cadastros/telefone/codigo/email',
    {
      method: 'POST',
      body: JSON.stringify({
        loja_id: lojaId,
        whatsapp: toInternationalRequestPhone(input.whatsapp),
        email: String(input.email ?? '').trim(),
      }),
    }
  )

  return {
    whatsapp: normalizePhoneDigits(response?.whatsapp ?? ''),
    expiresAt: typeof response?.expires_at === 'string' && response.expires_at.trim()
      ? response.expires_at.trim()
      : null,
    motivo: String(response?.motivo ?? '').trim(),
  }
}

export async function validateLojaVirtualPreCadastroPhoneCode(input: {
  whatsapp: string
  codigo: string
}): Promise<LojaVirtualPreCadastroPhoneValidationResult> {
  const response = await fetchApi<LojaVirtualPreCadastroPhoneValidationApiResponse>('/loja-virtual/v1/pre-cadastros/telefone/validar', {
    method: 'POST',
    body: JSON.stringify({
      whatsapp: toInternationalRequestPhone(input.whatsapp),
      codigo: String(input.codigo ?? '').trim(),
    }),
  })

  return {
    preCadastroId: Number(
      response?.precadastro_id
      ?? response?.precadastroId
      ?? response?.pre_cadastro_id
      ?? 0
    ) || 0,
    whatsapp: normalizePhoneDigits(response?.whatsapp ?? ''),
    validado: parseBooleanLike(response?.validado),
    validatedAt: typeof response?.validated_at === 'string' && response.validated_at.trim()
      ? response.validated_at.trim()
      : null,
    expiresAt: typeof response?.expires_at === 'string' && response.expires_at.trim()
      ? response.expires_at.trim()
      : null,
    motivo: String(response?.motivo ?? '').trim(),
  }
}

export async function sendIntegrationWhatsappMessage(input: {
  numero: string
  mensagem: string
}): Promise<IntegrationWhatsappSendResult> {
  const response = await fetchApi<IntegrationWhatsappSendApiResponse>('/integracoes/v1/whatsapp/send', {
    method: 'POST',
    body: JSON.stringify({
      numero: toInternationalRequestPhone(input.numero) || String(input.numero ?? '').trim(),
      mensagem: String(input.mensagem ?? '').trim(),
    }),
  })

  return {
    status: parseBooleanLike(response?.status),
    numero: String(response?.numero ?? '').trim(),
    chatId: String(response?.chat_id ?? '').trim(),
    messageId: String(response?.message_id ?? '').trim(),
    motivo: String(response?.motivo ?? '').trim(),
  }
}

export async function createCliente(payload: ClienteCreatePayload): Promise<any> {
  const lojaId = getLojaIdValue(payload.loja_id)
  return fetchApi('/clientes', {
    method: 'POST',
    body: JSON.stringify({
      ...payload,
      ...(lojaId ? { loja_id: lojaId } : {}),
    }),
  })
}

export async function updateCliente(id: string | number, payload: ClienteCreatePayload): Promise<any> {
  const clienteId = Math.trunc(Number(id))
  if (!Number.isFinite(clienteId) || clienteId <= 0) {
    throw new Error('Cliente inválido para atualização.')
  }

  const lojaId = getLojaIdValue(payload.loja_id)
  return fetchApi(`/clientes/${clienteId}`, {
    method: 'PUT',
    body: JSON.stringify({
      ...payload,
      ...(lojaId ? { loja_id: lojaId } : {}),
    }),
  })
}

export async function relateClientes(input: {
  id: number
  empresa1Id: number
  empresa2Id: number
}): Promise<LojaVirtualSimpleStatusResponse> {
  const id = Math.trunc(input.id)
  const empresa1Id = Math.trunc(input.empresa1Id)
  const empresa2Id = Math.trunc(input.empresa2Id)

  if (!Number.isFinite(id) || id <= 0 || !Number.isFinite(empresa1Id) || empresa1Id <= 0 || !Number.isFinite(empresa2Id) || empresa2Id <= 0) {
    throw new Error('Empresas inválidas para relacionamento.')
  }

  return fetchApi<LojaVirtualSimpleStatusResponse>(`/clientes/${id}/relacionar`, {
    method: 'POST',
    body: JSON.stringify({
      empresa1_id: empresa1Id,
      empresa2_id: empresa2Id,
    }),
  })
}

export async function copyCarrinhoBetweenCompradores(input: {
  entidadeId: number
  empresaOrigemId: number
  empresaDestinoId: number
  lojaId?: number
}): Promise<LojaVirtualSimpleStatusResponse> {
  const entidadeId = Math.trunc(input.entidadeId)
  const empresaOrigemId = Math.trunc(input.empresaOrigemId)
  const empresaDestinoId = Math.trunc(input.empresaDestinoId)
  const lojaId = getLojaIdValue(input.lojaId)

  if (
    !Number.isFinite(entidadeId) || entidadeId <= 0
    || !Number.isFinite(empresaOrigemId) || empresaOrigemId <= 0
    || !Number.isFinite(empresaDestinoId) || empresaDestinoId <= 0
  ) {
    throw new Error('Empresas inválidas para copiar o carrinho.')
  }

  return fetchApi<LojaVirtualSimpleStatusResponse>(`/loja-virtual/v1/compradores/${entidadeId}/carrinho/copiar`, {
    method: 'POST',
    body: JSON.stringify({
      empresa_origem_id: empresaOrigemId,
      empresa_destino_id: empresaDestinoId,
      ...(lojaId ? { loja_id: lojaId } : {}),
    }),
  })
}

export async function register(data: any): Promise<{ message: string }> {
  await fetchApi('/clientes', {
    method: 'POST',
    body: JSON.stringify(data),
  })
  return { message: 'Cliente cadastrado com sucesso.' }
}

export async function forgotPassword(email: string): Promise<{ message: string }> {
  const credential = email.trim()
  if (!credential) {
    throw new Error('Informe email ou CPF/CNPJ.')
  }

  const digits = credential.replace(/\D/g, '')
  const lojaId = getLojaIdValue()
  const payload: Record<string, unknown> = {
    ...(lojaId ? { loja_id: lojaId } : {}),
  }

  if (credential.includes('@')) {
    payload.email = credential
  } else if (digits) {
    payload.cpf_cnpj = digits
  } else {
    payload.email = credential
  }

  const response = await fetchApi<any>('/loja-virtual/v1/auth/comprador/forgot-password', {
    method: 'POST',
    body: JSON.stringify(payload),
  })

  return {
    message: response?.message || response?.motivo || 'Solicitação de recuperação enviada.',
  }
}

export async function consultaDocumento(document: string): Promise<any> {
  console.warn(`Endpoint de consulta de documento não existe no /api/docs (documento: ${document}).`)
  return { status: 0, motivo: 'Endpoint indisponível no /api/docs.' }
}

export async function getEnderecos(): Promise<any[]> {
  const entidadeId = resolveAuthenticatedEntidadeIdOrThrow('Listagem de endereços')
  const response = await fetchApi<unknown>(`/clientes/${entidadeId}/enderecos`)
  return extractListFromResponse(response, ['enderecos', 'addresses'])
}

export async function getCheckoutAddresses(): Promise<CheckoutAddressOption[]> {
  const result = await getCheckoutAddressData()
  return result.addresses
}

export async function createCheckoutAddress(
  input: CheckoutAddressCreateInput
): Promise<CheckoutAddressOption> {
  const entidadeId = resolveAuthenticatedEntidadeIdOrThrow('Cadastro de endereço de entrega')
  const lojaId = getLojaIdValue()
  const response = await fetchApi<any>(
    `/loja-virtual/v1/compradores/${entidadeId}/enderecos`,
    {
      method: 'POST',
      body: JSON.stringify({
        ...(lojaId ? { loja_id: lojaId } : {}),
        descricao: input.label,
        nome: input.recipient,
        cep: input.zipCode,
        endereco: input.street,
        numero: input.number,
        complemento: input.complement,
        bairro: input.neighborhood,
        cidade: input.city,
        uf: input.state,
      }),
    }
  )
  const record = isPlainRecord(response?.endereco)
    ? response.endereco
    : isPlainRecord(response)
      ? response
      : null
  const option = record ? buildCheckoutAddressOption(record, false) : null

  if (!option) {
    throw new Error(
      String(response?.motivo ?? response?.message ?? '').trim()
      || 'Endereço cadastrado, mas não foi possível carregá-lo.'
    )
  }

  return option
}

export async function updateClienteInscricaoEstadual(
  id: number,
  ie: string
): Promise<CheckoutCustomerProfile | null> {
  const clienteId = Math.trunc(id)
  const normalizedIe = String(ie ?? '').trim()

  if (!Number.isFinite(clienteId) || clienteId <= 0) {
    throw new Error('Cliente inválido para atualizar a inscrição estadual.')
  }

  if (!normalizedIe) {
    throw new Error('Informe a inscrição estadual.')
  }

  await fetchApi<unknown>(`/clientes/${clienteId}/inscricao`, {
    method: 'POST',
    body: JSON.stringify({ ie: normalizedIe }),
  })

  const updatedCliente = await fetchApi<unknown>(`/clientes/${clienteId}`)
  if (!updatedCliente || typeof updatedCliente !== 'object') return null
  return buildCheckoutCustomerProfile(updatedCliente as Record<string, unknown>)
}

export async function getCheckoutCustomerProfile(entidadeId?: number): Promise<CheckoutCustomerProfile | null> {
  const resolvedEntidadeId = resolveRequestedEntidadeIdOrThrow(entidadeId, 'Perfil do checkout')
  const response = await fetchApi<unknown>(`/clientes/${resolvedEntidadeId}`)
  if (!response || typeof response !== 'object') return null
  const profile = buildCheckoutCustomerProfile(response as Record<string, unknown>)
  return profile
    ? enrichCheckoutCustomerProfileWithLatestOrderFallback(profile, resolvedEntidadeId)
    : null
}

// Dedupe em voo de getCheckoutAddressData. O CartDrawer tem dois efeitos
// (price-preview e CIF-shipping) que disparam quase juntos ao abrir o carrinho
// e ambos chamam esta função, que por sua vez faz 3 requests (clientes,
// enderecos, pedidos). Colapsar as chamadas concorrentes evita a rajada que
// aciona a tela de manutenção. NÃO é cache pós-resposta — só junta o que está
// em voo, preservando o frescor dos dados de conta.
type CheckoutAddressDataOptions = {
  entidadeId?: number
  trackBackendAvailability?: boolean
  includeCustomerFallbacks?: boolean
}

const getCheckoutAddressDataRequest = new Map<string, Promise<CheckoutAddressData>>()

export async function getCheckoutAddressData(options?: CheckoutAddressDataOptions): Promise<CheckoutAddressData> {
  const entidadeId = resolveRequestedEntidadeIdOrThrow(options?.entidadeId, 'Checkout de endereço')
  const trackBackendAvailability = options?.trackBackendAvailability ?? true
  const includeCustomerFallbacks = options?.includeCustomerFallbacks ?? true
  const dedupeKey = `${entidadeId}|track:${trackBackendAvailability ? 1 : 0}|fallbacks:${includeCustomerFallbacks ? 1 : 0}`

  const pending = getCheckoutAddressDataRequest.get(dedupeKey)
  if (pending) {
    return pending
  }

  const request = getCheckoutAddressDataInner(entidadeId, {
    trackBackendAvailability,
    includeCustomerFallbacks,
  }).finally(() => {
    getCheckoutAddressDataRequest.delete(dedupeKey)
  })
  getCheckoutAddressDataRequest.set(dedupeKey, request)
  return request
}

async function getCheckoutAddressDataInner(
  entidadeId: number,
  options: Required<Pick<CheckoutAddressDataOptions, 'trackBackendAvailability' | 'includeCustomerFallbacks'>>
): Promise<CheckoutAddressData> {
  const [[clienteResult, enderecosResult], latestOrderFallback] = await Promise.all([
    Promise.allSettled([
      fetchApi<unknown>(`/clientes/${entidadeId}`, {
        trackBackendAvailability: options.trackBackendAvailability,
      }),
      fetchApi<unknown>(`/clientes/${entidadeId}/enderecos`, {
        trackBackendAvailability: options.trackBackendAvailability,
      }),
    ]),
    options.includeCustomerFallbacks
      ? fetchCheckoutLatestOrderFallback(entidadeId)
      : Promise.resolve(null),
  ])

  if (
    clienteResult.status === 'rejected'
    && enderecosResult.status === 'rejected'
    && !latestOrderFallback?.address
  ) {
    throw clienteResult.reason
  }

  const clienteResponse = clienteResult.status === 'fulfilled'
    ? clienteResult.value
    : null
  const enderecosResponse = enderecosResult.status === 'fulfilled'
    ? enderecosResult.value
    : null

  if (clienteResult.status === 'rejected') {
    console.warn('Failed to load checkout customer profile; using registered addresses only:', clienteResult.reason)
  }
  if (enderecosResult.status === 'rejected') {
    console.warn('Failed to load registered checkout addresses; using customer and latest purchase addresses:', enderecosResult.reason)
  }

  const baseCustomer =
    clienteResponse && typeof clienteResponse === 'object'
      ? buildCheckoutCustomerProfile(clienteResponse as Record<string, unknown>)
      : latestOrderFallback
        // O endpoint consolidado da última compra já possui os dados necessários
        // para endereço e contato. Mantemos um perfil mínimo para que o checkout
        // consiga aplicá-los mesmo se a consulta cadastral estiver indisponível.
        ? buildCheckoutCustomerProfile({ id: entidadeId })
        : null
  const customer = baseCustomer && options.includeCustomerFallbacks
    ? await enrichCheckoutCustomerProfileWithLatestOrderFallback(
        baseCustomer,
        entidadeId,
        latestOrderFallback
      )
    : baseCustomer

  const addressOptions: CheckoutAddressOption[] = []
  const seen = new Set<string>()
  const seenPhysicalAddresses = new Set<string>()

  if (clienteResponse && typeof clienteResponse === 'object') {
    const clienteOption = buildCheckoutAddressOption(clienteResponse as Record<string, unknown>, true)
    if (clienteOption) {
      const key = `${clienteOption.id}:${clienteOption.zipCode}:${clienteOption.street}:${clienteOption.number}`
      seen.add(key)
      seenPhysicalAddresses.add(buildCheckoutAddressPhysicalKey(clienteOption))
      addressOptions.push(clienteOption)
    }
  }

  const enderecos = extractListFromResponse(enderecosResponse, ['enderecos', 'addresses'])
  enderecos.forEach((item) => {
    if (!item || typeof item !== 'object') return
    const option = buildCheckoutAddressOption(item as Record<string, unknown>, false)
    if (!option) return
    const key = `${option.id}:${option.zipCode}:${option.street}:${option.number}`
    if (seen.has(key)) return
    seen.add(key)
    seenPhysicalAddresses.add(buildCheckoutAddressPhysicalKey(option))
    addressOptions.push(option)
  })

  const latestOrderOption = buildLatestOrderCheckoutAddressOption(
    customer?.ultimaCompraEnderecoEntrega ?? latestOrderFallback?.address,
    customer?.ultimaCompraContatoNome
      || latestOrderFallback?.contactName
      || customer?.name
      || customer?.corporateName
      || ''
  )
  if (latestOrderOption) {
    // A última compra é uma fotografia histórica: o ID salvo pode ter sido
    // desativado ou substituído. Se o mesmo local ainda existe no cadastro,
    // mantemos a opção canônica para nunca enviar ao checkout um ID obsoleto.
    const physicalKey = buildCheckoutAddressPhysicalKey(latestOrderOption)
    if (!seenPhysicalAddresses.has(physicalKey)) {
      seenPhysicalAddresses.add(physicalKey)
      addressOptions.push(latestOrderOption)
    }
  }

  return {
    customer,
    addresses: addressOptions,
  }
}

const getCheckoutPaymentOptionsRequest = new Map<string, Promise<CheckoutPaymentPreparation>>()

type CheckoutQuoteIdentifier = number | string

const normalizeCheckoutQuoteIdentifierPayload = (
  value: CheckoutQuoteIdentifier | null | undefined
): number | string | null => {
  if (typeof value === 'number') {
    return Number.isFinite(value) && value > 0 ? Math.trunc(value) : null
  }

  if (typeof value === 'string') {
    const normalized = value.trim()
    if (!normalized) return null

    if (/^\d+$/.test(normalized)) {
      const parsed = Number.parseInt(normalized, 10)
      return Number.isFinite(parsed) && parsed > 0 ? Math.trunc(parsed) : null
    }

    return normalized
  }

  return null
}

export async function getCheckoutPaymentPreparation(input?: {
  lojaId?: number
  carrinhoIds?: number[]
  orcamentoId?: CheckoutQuoteIdentifier
  quoteMode?: BusinessQuoteCheckoutMode
  useCartItems?: boolean
  orcamentoItensOverride?: Array<{ itemId: number; quantity: number }>
  pedidoId?: number
  pedidoItensOverride?: Array<{ itemId: number; quantity: number }>
  trackBackendAvailability?: boolean
}): Promise<CheckoutPaymentPreparation> {
  const entidadeId = resolveAuthenticatedEntidadeIdOrThrow('Checkout de pagamento')
  const payload: Record<string, unknown> = {}
  const lojaId = getLojaIdValue(input?.lojaId)
  if (lojaId) payload.loja_id = lojaId
  const orcamentoId = normalizeCheckoutQuoteIdentifierPayload(input?.orcamentoId)
  if (orcamentoId !== null) payload.orcamento_id = orcamentoId
  if (orcamentoId !== null && input?.quoteMode) payload.quote_mode = input.quoteMode
  if (orcamentoId !== null && input?.useCartItems) payload.usar_carrinho = 1
  if (typeof input?.pedidoId === 'number' && Number.isFinite(input.pedidoId) && input.pedidoId > 0) {
    payload.pedido_id = Math.trunc(input.pedidoId)
  }
  if (Array.isArray(input?.carrinhoIds) && input.carrinhoIds.length > 0) {
    payload.carrinho_ids = input.carrinhoIds
  }
  const orcamentoItensOverride = normalizeCheckoutQuoteItemOverridesPayload(input?.orcamentoItensOverride)
  if (orcamentoItensOverride.length > 0) {
    payload.orcamento_itens_override = orcamentoItensOverride
  }
  const pedidoItensOverride = normalizeCheckoutOrderItemOverridesPayload(input?.pedidoItensOverride)
  if (pedidoItensOverride.length > 0) {
    payload.pedido_itens_override = pedidoItensOverride
  }

  // Dedupe em voo por (entidade + payload). É um POST de CONSULTA (calcula
  // opções), idempotente; os dois efeitos do CartDrawer disparam com payload
  // idêntico quase juntos (gap ~16ms). Colapsar evita o POST duplicado.
  const payloadBody = JSON.stringify(payload)
  const trackBackendAvailability = input?.trackBackendAvailability ?? true
  const dedupeKey = `${entidadeId}|track:${trackBackendAvailability ? 1 : 0}|${payloadBody}`
  const pendingPayment = getCheckoutPaymentOptionsRequest.get(dedupeKey)
  if (pendingPayment) {
    return pendingPayment
  }

  const paymentRequest = (async () => {
    const response = await fetchCheckoutApi<LojaVirtualCheckoutPagamentosResponse>(
      buildCheckoutEndpoint(entidadeId, 'pagamentos/opcoes', lojaId),
      {
        method: 'POST',
        body: payloadBody,
        trackBackendAvailability,
      }
    )

    const options = (response.formas ?? []).flatMap((forma) =>
      (forma.condicoes ?? [])
        .map((condicao) => buildCheckoutPaymentOption(forma, condicao))
        .filter((option): option is CheckoutPaymentOption => Boolean(option))
    )
    const contextoPreco = normalizeCheckoutPriceContext(response.contexto_preco)
    const eligibleIds = new Set(
      (response.condicoes_x_elegiveis ?? [])
        .map((item) => `${Math.trunc(normalizeFreteNumber(item.forma_pagamento_id) ?? 0)}:${Math.trunc(normalizeFreteNumber(item.condicao_pagamento_id) ?? 0)}`)
    )

    return {
      options,
      contextoPreco,
      condicoesXElegiveis: options.filter((option) => eligibleIds.has(option.id)),
    }
  })().finally(() => {
    getCheckoutPaymentOptionsRequest.delete(dedupeKey)
  })

  getCheckoutPaymentOptionsRequest.set(dedupeKey, paymentRequest)
  return paymentRequest
}

export async function getCheckoutPaymentOptions(
  input?: Parameters<typeof getCheckoutPaymentPreparation>[0]
): Promise<CheckoutPaymentOption[]> {
  const preparation = await getCheckoutPaymentPreparation(input)
  return preparation.options
}

const SHIPPING_CALCULATING_ERROR_TOKENS = [
  'cotacao_cache_miss',
  'cotação',
  'cotacao',
  'calculando',
  'processando',
  'aguarde',
  'em andamento',
]

const isShippingCalculatingConflict = (status: number, detail?: string): boolean => {
  if (status !== 409) return false
  const normalizedDetail = normalizePaymentToken(detail)
  if (!normalizedDetail) return true
  return SHIPPING_CALCULATING_ERROR_TOKENS.some((token) => normalizedDetail.includes(normalizePaymentToken(token)))
}

async function processWithConcurrency<T, TResult>(
  items: T[],
  concurrency: number,
  processor: (item: T, index: number) => Promise<TResult[]>
): Promise<TResult[]> {
  if (items.length === 0) return []

  const normalizedConcurrency = Number.isFinite(concurrency)
    ? Math.max(1, Math.trunc(concurrency))
    : 1
  const workerCount = Math.min(normalizedConcurrency, items.length)
  let nextIndex = 0

  const worker = async (): Promise<TResult[]> => {
    const workerResults: TResult[] = []

    while (true) {
      const currentIndex = nextIndex
      nextIndex += 1

      if (currentIndex >= items.length) return workerResults

      const itemResults = await processor(items[currentIndex], currentIndex)
      workerResults.push(...itemResults)
    }
  }

  const settledWorkers = await Promise.allSettled(
    Array.from({ length: workerCount }, () => worker())
  )
  const results: TResult[] = []

  for (const settledWorker of settledWorkers) {
    if (settledWorker.status === 'fulfilled') {
      results.push(...settledWorker.value)
      continue
    }

    throw settledWorker.reason
  }

  return results
}

async function getCheckoutShippingOptionsConsolidated(input: {
  formaPagamentoId: number
  condicaoPagamentoId: number
  orcamentoId?: CheckoutQuoteIdentifier
  quoteMode?: BusinessQuoteCheckoutMode
  useCartItems?: boolean
  orcamentoItensOverride?: Array<{ itemId: number; quantity: number }>
  pedidoId?: number
  pedidoItensOverride?: Array<{ itemId: number; quantity: number }>
  enderecoId?: number
  lojaId?: number
  modoFrete?: 'auto' | 'fob' | 'cif'
  usarRedespacho?: 0 | 1
  transportadoraRedespachoId?: number | null
  transportadoraPreferencialId?: number | null
  carrinhoIds?: number[]
  preservarPagamentoAtualPedido?: boolean
  forceRefresh?: boolean
  trackBackendAvailability?: boolean
}): Promise<CheckoutShippingOption[]> {
  const entidadeId = resolveAuthenticatedEntidadeIdOrThrow('Checkout de frete')
  const lojaId = getLojaIdValue(input.lojaId)
  const endpoint = buildCheckoutEndpoint(entidadeId, 'frete/opcoes', lojaId)
  const orcamentoItensOverride = normalizeCheckoutQuoteItemOverridesPayload(input.orcamentoItensOverride)
  const pedidoItensOverride = normalizeCheckoutOrderItemOverridesPayload(input.pedidoItensOverride)
  const orcamentoId = normalizeCheckoutQuoteIdentifierPayload(input.orcamentoId)
  const payload = {
    forma_pagamento_id: Math.trunc(input.formaPagamentoId),
    condicao_pagamento_id: Math.trunc(input.condicaoPagamentoId),
    ...(orcamentoId !== null ? { orcamento_id: orcamentoId } : {}),
    ...(orcamentoId !== null && input.quoteMode ? { quote_mode: input.quoteMode } : {}),
    ...(orcamentoId !== null && input.useCartItems ? { usar_carrinho: 1 } : {}),
    ...(typeof input.pedidoId === 'number' && Number.isFinite(input.pedidoId) && input.pedidoId > 0
      ? { pedido_id: Math.trunc(input.pedidoId) }
      : {}),
    endereco_id: Math.max(0, Math.trunc(input.enderecoId ?? 0)),
    modo_frete: input.modoFrete ?? 'auto',
    ...(input.usarRedespacho === 0 || input.usarRedespacho === 1
      ? { usar_redespacho: input.usarRedespacho }
      : {}),
    ...(typeof input.transportadoraRedespachoId === 'number' && Number.isFinite(input.transportadoraRedespachoId)
      ? { transportadora_redespacho_id: Math.max(0, Math.trunc(input.transportadoraRedespachoId)) }
      : {}),
    ...(typeof input.transportadoraPreferencialId === 'number' && Number.isFinite(input.transportadoraPreferencialId)
      ? { transportadora_preferencial_id: Math.max(0, Math.trunc(input.transportadoraPreferencialId)) }
      : {}),
    ...(Array.isArray(input.carrinhoIds) && input.carrinhoIds.length > 0 ? { carrinho_ids: input.carrinhoIds } : {}),
    ...(orcamentoItensOverride.length > 0
      ? { orcamento_itens_override: orcamentoItensOverride }
      : {}),
    ...(pedidoItensOverride.length > 0
      ? { pedido_itens_override: pedidoItensOverride }
      : {}),
    ...(input.preservarPagamentoAtualPedido
      ? { preservar_pagamento_atual_pedido: 1 }
      : {}),
    ...(input.forceRefresh ? { forcar_recalculo: 1 } : {}),
    ...(lojaId ? { loja_id: lojaId } : {}),
  }
  const rawResponse = await fetchCheckoutApiResponse(endpoint, {
    method: 'POST',
    body: JSON.stringify(payload),
    trackBackendAvailability: input.trackBackendAvailability,
  })

  if (!rawResponse.ok) {
    const responsePayload = await parseJsonSafe(rawResponse)
    const detail = extractErrorMessage(responsePayload)
    if (input.modoFrete === 'cif' && isShippingCalculatingConflict(rawResponse.status, detail)) {
      throw new Error(CHECKOUT_SHIPPING_CALCULATING_MESSAGE)
    }

    throw new Error(`API error: ${rawResponse.status}${detail ? ` - ${detail}` : ''}`)
  }

  const response = await rawResponse.json() as LojaVirtualCheckoutFreteResponse
  const redespacho = resolveCheckoutRedespachoInfo(response.redespacho)

  const options = (response.transportadoras ?? [])
    .map((option) => buildCheckoutShippingOption(option, redespacho))
    .filter((option): option is CheckoutShippingOption => Boolean(option))

  // Durante a primeira entrada no checkout o backend pode devolver somente a
  // transportadora já persistida junto com `cotacao_cache_miss = 1`. Essa
  // relação ainda é parcial: aceitá-la como pronta faz "Alterar frete" abrir
  // apenas a opção atual até o usuário atualizar a página. Mantemos a carga em
  // andamento para o chamador repetir a consulta agregada quando o cache ficar
  // completo.
  if (input.modoFrete === 'cif' && response.cotacao_cache_miss === 1) {
    throw new Error(CHECKOUT_SHIPPING_CALCULATING_MESSAGE)
  }

  return options
}

export async function getCheckoutShippingOptions(input: {
  formaPagamentoId: number
  condicaoPagamentoId: number
  orcamentoId?: CheckoutQuoteIdentifier
  quoteMode?: BusinessQuoteCheckoutMode
  useCartItems?: boolean
  orcamentoItensOverride?: Array<{ itemId: number; quantity: number }>
  pedidoId?: number
  pedidoItensOverride?: Array<{ itemId: number; quantity: number }>
  enderecoId?: number
  lojaId?: number
  modoFrete?: 'auto' | 'fob' | 'cif'
  usarRedespacho?: 0 | 1
  transportadoraRedespachoId?: number | null
  transportadoraPreferencialId?: number | null
  customerProfile?: CheckoutCustomerProfile | null
  addressOptions?: CheckoutAddressOption[]
  carrinhoIds?: number[]
  items?: CartItem[]
  preservarPagamentoAtualPedido?: boolean
  forceRefresh?: boolean
  trackBackendAvailability?: boolean
}): Promise<CheckoutShippingOption[]> {
  // O endpoint agregado calcula todas as transportadoras em uma única chamada e
  // compartilha cache e requisições em andamento no backend.
  return getCheckoutShippingOptionsConsolidated(input)
}

export async function simulateCheckout(input: {
  formaPagamentoId?: number
  condicaoPagamentoId?: number
  formaPagamentoXId?: number
  condicaoPagamentoXId?: number
  summaryOnly?: boolean
  orcamentoId?: number
  quoteMode?: BusinessQuoteCheckoutMode
  useCartItems?: boolean
  orcamentoItensOverride?: Array<{ itemId: number; quantity: number }>
  pedidoId?: number
  pedidoItensOverride?: Array<{ itemId: number; quantity: number }>
  enderecoId?: number
  transportadoraId?: number
  cotacaoId?: number
  valorFrete?: number
  usarRedespacho?: 0 | 1
  transportadoraRedespachoId?: number | null
  cupom?: string
  usarCashback?: boolean
  faturarPainel?: boolean
  modoFrete?: 'auto' | 'fob' | 'cif'
  lojaId?: number
  carrinhoIds?: number[]
  trackBackendAvailability?: boolean
}): Promise<CheckoutSimulationResult> {
  const entidadeId = resolveAuthenticatedEntidadeIdOrThrow('Simulação de checkout')
  const lojaId = getLojaIdValue(input.lojaId)
  const orcamentoItensOverride = normalizeCheckoutQuoteItemOverridesPayload(input.orcamentoItensOverride)
  const pedidoItensOverride = normalizeCheckoutOrderItemOverridesPayload(input.pedidoItensOverride)
  const response = await fetchCheckoutApi<LojaVirtualCheckoutSimularResponse>(
    buildCheckoutEndpoint(entidadeId, 'simular', lojaId),
    {
      method: 'POST',
      body: JSON.stringify({
        ...(typeof input.formaPagamentoId === 'number' && Number.isFinite(input.formaPagamentoId) && input.formaPagamentoId > 0
          ? { forma_pagamento_id: Math.trunc(input.formaPagamentoId) }
          : {}),
        ...(typeof input.condicaoPagamentoId === 'number' && Number.isFinite(input.condicaoPagamentoId) && input.condicaoPagamentoId > 0
          ? { condicao_pagamento_id: Math.trunc(input.condicaoPagamentoId) }
          : {}),
        ...(typeof input.formaPagamentoXId === 'number' && Number.isFinite(input.formaPagamentoXId) && input.formaPagamentoXId > 0
          ? { forma_pagamento_x_id: Math.trunc(input.formaPagamentoXId) }
          : {}),
        ...(typeof input.condicaoPagamentoXId === 'number' && Number.isFinite(input.condicaoPagamentoXId) && input.condicaoPagamentoXId > 0
          ? { condicao_pagamento_x_id: Math.trunc(input.condicaoPagamentoXId) }
          : {}),
        ...(input.summaryOnly ? { somente_resumo: 1 } : {}),
        ...(typeof input.orcamentoId === 'number' && Number.isFinite(input.orcamentoId) && input.orcamentoId > 0
          ? { orcamento_id: Math.trunc(input.orcamentoId) }
          : {}),
        ...(typeof input.orcamentoId === 'number' && input.orcamentoId > 0 && input.quoteMode
          ? { quote_mode: input.quoteMode }
          : {}),
        ...(typeof input.orcamentoId === 'number' && input.orcamentoId > 0 && input.useCartItems
          ? { usar_carrinho: 1 }
          : {}),
        ...(typeof input.pedidoId === 'number' && Number.isFinite(input.pedidoId) && input.pedidoId > 0
          ? { pedido_id: Math.trunc(input.pedidoId) }
          : {}),
        endereco_id: Math.max(0, Math.trunc(input.enderecoId ?? 0)),
        transportadora_id: Math.max(0, Math.trunc(input.transportadoraId ?? 0)),
        cotacao_id: Math.max(0, Math.trunc(input.cotacaoId ?? 0)),
        valor_frete: Math.max(0, input.valorFrete ?? 0),
        ...(input.usarRedespacho === 0 || input.usarRedespacho === 1
          ? { usar_redespacho: input.usarRedespacho }
          : {}),
        ...(typeof input.transportadoraRedespachoId === 'number' && Number.isFinite(input.transportadoraRedespachoId)
          ? { transportadora_redespacho_id: Math.max(0, Math.trunc(input.transportadoraRedespachoId)) }
          : {}),
        cupom: normalizeCheckoutText(input.cupom),
        usar_cashback: input.usarCashback ? 1 : 0,
        ...(typeof input.faturarPainel === 'boolean'
          ? { faturar_painel: input.faturarPainel ? 1 : 0 }
          : {}),
        modo_frete: input.modoFrete ?? 'auto',
        ...(Array.isArray(input.carrinhoIds) && input.carrinhoIds.length > 0 ? { carrinho_ids: input.carrinhoIds } : {}),
        ...(orcamentoItensOverride.length > 0 ? { orcamento_itens_override: orcamentoItensOverride } : {}),
        ...(pedidoItensOverride.length > 0 ? { pedido_itens_override: pedidoItensOverride } : {}),
        ...(lojaId ? { loja_id: lojaId } : {}),
      }),
      trackBackendAvailability: input.trackBackendAvailability,
    }
  )
  const items = normalizeCheckoutSimulationItems(response.itens)
  const itemTaxTotals = items.reduce(
    (totals, item) => ({
      impostos: totals.impostos + item.impostos,
      ipi: totals.ipi + item.ipi,
      difal: totals.difal + item.difal,
    }),
    { impostos: 0, ipi: 0, difal: 0 }
  )
  const { impostos, ipi, difal } = normalizeTaxes(
    response.impostos ?? (items.length > 0 ? itemTaxTotals.impostos : undefined),
    response.ipi ?? response.valor_ipi ?? (items.length > 0 ? itemTaxTotals.ipi : undefined),
    response.difal ?? response.valor_difal ?? response.st ?? response.valor_st ?? (items.length > 0 ? itemTaxTotals.difal : undefined),
    normalizeFreteNumber
  )

  return {
    subtotal: normalizeFreteNumber(response.subtotal) ?? 0,
    desconto: normalizeFreteNumber(response.desconto) ?? 0,
    descontoPercentual: normalizeFreteNumber(response.desconto_percentual) ?? 0,
    frete: normalizeFreteNumber(response.frete) ?? 0,
    impostos,
    ipi,
    difal,
    total: normalizeFreteNumber(response.total) ?? 0,
    valorTotalFiscal: normalizeFreteNumber(response.valor_total_fiscal ?? response.total) ?? 0,
    faturarPainel: response.faturar_painel === 1,
    faturamentoPainelDisponivel: response.faturamento_painel_disponivel === 1,
    descontoPainel: normalizeFreteNumber(response.desconto_painel) ?? 0,
    descontoPainelPercentual: normalizeFreteNumber(response.desconto_painel_percentual) ?? 0,
    cupomOk: response.cupom_ok === 1,
    cupomMensagem: normalizeCheckoutText(response.cupom_mensagem),
    cashbackDisponivel: normalizeFreteNumber(response.cashback_disponivel) ?? 0,
    cashbackAplicado: normalizeFreteNumber(response.cashback_aplicado) ?? 0,
    partnerQuoteSummary: response.partner_quote_summary
      ? {
          productsTotal: normalizeFreteNumber(response.partner_quote_summary.products_total) ?? 0,
          taxesTotal: normalizeFreteNumber(response.partner_quote_summary.taxes_total) ?? 0,
          commissionTotal: normalizeFreteNumber(response.partner_quote_summary.commission_total) ?? 0,
          total: normalizeFreteNumber(response.partner_quote_summary.total) ?? 0,
          items: Array.isArray(response.partner_quote_summary.items)
            ? response.partner_quote_summary.items.flatMap((item) => {
                const productId = Math.max(0, Math.trunc(normalizeFreteNumber(item.product_id) ?? 0))
                const productGradeId = Math.max(0, Math.trunc(normalizeFreteNumber(item.product_grade_id) ?? 0))
                const quantity = Math.max(0, normalizeFreteNumber(item.quantity) ?? 0)
                if (productId <= 0 || quantity <= 0) return []
                return [{
                  key: normalizeCheckoutText(item.item_key),
                  productId,
                  productGradeId,
                  quantity,
                  unitPrice: Math.max(0, normalizeFreteNumber(item.unit_price) ?? 0),
                  commissionPercent: Math.max(0, normalizeFreteNumber(item.commission_percent) ?? 0),
                  commissionValue: Math.max(0, normalizeFreteNumber(item.commission_value) ?? 0),
                  valueUnitPrice: Math.max(0, normalizeFreteNumber(item.value_unit_price) ?? 0),
                }]
              })
            : [],
        }
      : null,
    statusFluxo: normalizeCheckoutStatusFluxo(response.status_fluxo),
    items,
  }
}

export type PanelBillingUpdateResult = {
  faturarPainel: boolean
  faturamentoPainelDisponivel: boolean
}

const updatePanelBilling = async (
  resourcePath: string,
  active: boolean,
  lojaId?: number
): Promise<PanelBillingUpdateResult> => {
  const entidadeId = resolveAuthenticatedEntidadeIdOrThrow('Faturamento como painel')
  const resolvedLojaId = getLojaIdValue(lojaId)
  const query = resolvedLojaId ? `?loja_id=${resolvedLojaId}` : ''
  const response = await fetchApi<{
    faturar_painel?: number
    faturamento_painel_disponivel?: number
  }>(`/loja-virtual/v1/compradores/${entidadeId}/${resourcePath}${query}`, {
    method: 'PUT',
    body: JSON.stringify({ faturar_painel: active ? 1 : 0 }),
  })
  return {
    faturarPainel: response.faturar_painel === 1,
    faturamentoPainelDisponivel: response.faturamento_painel_disponivel === 1,
  }
}

export const updateCartPanelBilling = (active: boolean, lojaId?: number) =>
  updatePanelBilling('carrinho/faturamento-painel', active, lojaId)

export const updateQuotePanelBilling = (orcamentoId: number | string, active: boolean, lojaId?: number) =>
  updatePanelBilling(`orcamentos/${encodeURIComponent(String(orcamentoId))}/faturamento-painel`, active, lojaId)

export const updateOrderPanelBilling = (pedidoId: number | string, active: boolean, lojaId?: number) =>
  updatePanelBilling(`pedidos/${encodeURIComponent(String(pedidoId))}/faturamento-painel`, active, lojaId)

export async function generateCheckoutOrder(
  input: CheckoutOrderGenerateInput
): Promise<CheckoutOrderGenerationResult> {
  const entidadeId = resolveAuthenticatedEntidadeIdOrThrow('Geração de pedido')
  const lojaId = getLojaIdValue(input.lojaId)
  const orcamentoItensOverride = normalizeCheckoutQuoteItemOverridesPayload(input.orcamentoItensOverride)
  const pedidoItensOverride = normalizeCheckoutOrderItemOverridesPayload(input.pedidoItensOverride)
  const attemptId = normalizeCheckoutText(input.attemptId)
  const attemptSource = normalizeCheckoutAttemptSource(input.attemptSource) ?? 'fresh_submit'
  const habilitarProgramarFaturamento = input.habilitarProgramarFaturamento === true
    || input.habilitar_programar_faturamento === true
    || input.habilitar_programar_faturamento === 1
  const dataProgramacao = normalizeCheckoutText(input.dataProgramacao ?? input.data_programacao)
  const snapshotFreteSelecionado = input.checkoutSnapshot?.freteSelecionado ?? null
  const transportadoraId = typeof input.transportadoraId === 'number' && Number.isFinite(input.transportadoraId)
    ? Math.max(0, Math.trunc(input.transportadoraId))
    : snapshotFreteSelecionado
      ? Math.max(0, Math.trunc(snapshotFreteSelecionado.transportadoraId))
      : null
  const cotacaoId = typeof input.cotacaoId === 'number' && Number.isFinite(input.cotacaoId)
    ? Math.max(0, Math.trunc(input.cotacaoId))
    : snapshotFreteSelecionado
      ? Math.max(0, Math.trunc(snapshotFreteSelecionado.cotacaoId))
      : null
  const valorFrete = typeof input.valorFrete === 'number' && Number.isFinite(input.valorFrete)
    ? Math.max(0, input.valorFrete)
    : snapshotFreteSelecionado
      ? Math.max(0, snapshotFreteSelecionado.valorFrete)
      : null
  const fretePorConta = input.fretePorConta === 1 || input.fretePorConta === 0
    ? input.fretePorConta
    : snapshotFreteSelecionado
      ? (snapshotFreteSelecionado.fretePorConta === 1 ? 1 : 0)
      : input.modoFrete === 'fob'
        ? 1
        : 0
  const usarRedespacho = input.usarRedespacho === 0 || input.usarRedespacho === 1
    ? input.usarRedespacho
    : snapshotFreteSelecionado?.usarRedespacho === 0 || snapshotFreteSelecionado?.usarRedespacho === 1
      ? snapshotFreteSelecionado.usarRedespacho
      : undefined
  const transportadoraRedespachoId = typeof input.transportadoraRedespachoId === 'number' && Number.isFinite(input.transportadoraRedespachoId)
    ? Math.max(0, Math.trunc(input.transportadoraRedespachoId))
    : snapshotFreteSelecionado?.transportadoraRedespachoId
      ? Math.max(0, Math.trunc(snapshotFreteSelecionado.transportadoraRedespachoId))
      : null
  const checkoutSnapshot = input.checkoutSnapshot
    ? {
        subtotal: Math.max(0, input.checkoutSnapshot.subtotal),
        desconto: Math.max(0, input.checkoutSnapshot.desconto),
        frete: Math.max(0, input.checkoutSnapshot.frete),
        impostos: Math.max(0, input.checkoutSnapshot.impostos),
        total: Math.max(0, input.checkoutSnapshot.total),
        ...(input.checkoutSnapshot.freteSelecionado
          ? {
              frete_selecionado: {
                transportadora_id: Math.max(0, Math.trunc(input.checkoutSnapshot.freteSelecionado.transportadoraId)),
                cotacao_id: Math.max(0, Math.trunc(input.checkoutSnapshot.freteSelecionado.cotacaoId)),
                tipo_frete: input.checkoutSnapshot.freteSelecionado.tipoFrete === 'fob' ? 'fob' : 'cif',
                frete_por_conta: input.checkoutSnapshot.freteSelecionado.fretePorConta === 1 ? 1 : 0,
                valor_frete: Math.max(0, input.checkoutSnapshot.freteSelecionado.valorFrete),
                valor_original: Math.max(0, input.checkoutSnapshot.freteSelecionado.valorOriginal),
                ...(input.checkoutSnapshot.freteSelecionado.usarRedespacho === 0 || input.checkoutSnapshot.freteSelecionado.usarRedespacho === 1
                  ? { usar_redespacho: input.checkoutSnapshot.freteSelecionado.usarRedespacho }
                  : {}),
                ...(typeof input.checkoutSnapshot.freteSelecionado.transportadoraRedespachoId === 'number'
                  && Number.isFinite(input.checkoutSnapshot.freteSelecionado.transportadoraRedespachoId)
                  ? {
                      transportadora_redespacho_id: Math.max(
                        0,
                        Math.trunc(input.checkoutSnapshot.freteSelecionado.transportadoraRedespachoId)
                      ),
                    }
                  : {}),
              },
            }
          : {}),
      }
    : null
  const response = await fetchCheckoutApi<LojaVirtualPedidoGerarResponse>(
    buildPedidoGerarEndpoint(entidadeId),
    {
      method: 'POST',
      trackBackendAvailability: !attemptId,
      body: JSON.stringify({
        ...(lojaId ? { loja_id: lojaId } : {}),
        ...(typeof input.orcamentoId === 'number' && Number.isFinite(input.orcamentoId) && input.orcamentoId > 0
          ? { orcamento_id: Math.trunc(input.orcamentoId) }
          : {}),
        ...(typeof input.orcamentoId === 'number' && input.orcamentoId > 0 && input.quoteMode
          ? { quote_mode: input.quoteMode }
          : {}),
        ...(orcamentoItensOverride.length > 0 ? { orcamento_itens_override: orcamentoItensOverride } : {}),
        ...(typeof input.pedidoId === 'number' && Number.isFinite(input.pedidoId) && input.pedidoId > 0
          ? { pedido_id: Math.trunc(input.pedidoId) }
          : {}),
        ...(pedidoItensOverride.length > 0 ? { pedido_itens_override: pedidoItensOverride } : {}),
        ...(input.recompraDireta ? { recompra_direta: 1 } : {}),
        ...(typeof input.formaPagamentoId === 'number' && Number.isFinite(input.formaPagamentoId) && input.formaPagamentoId > 0
          ? { forma_pagamento_id: Math.trunc(input.formaPagamentoId) }
          : {}),
        ...(typeof input.condicaoPagamentoId === 'number' && Number.isFinite(input.condicaoPagamentoId) && input.condicaoPagamentoId > 0
          ? { condicao_pagamento_id: Math.trunc(input.condicaoPagamentoId) }
          : {}),
        ...(typeof input.formaPagamentoXId === 'number' && Number.isFinite(input.formaPagamentoXId) && input.formaPagamentoXId > 0
          ? { forma_pagamento_x_id: Math.trunc(input.formaPagamentoXId) }
          : {}),
        ...(typeof input.condicaoPagamentoXId === 'number' && Number.isFinite(input.condicaoPagamentoXId) && input.condicaoPagamentoXId > 0
          ? { condicao_pagamento_x_id: Math.trunc(input.condicaoPagamentoXId) }
          : {}),
        ...(typeof input.enderecoId === 'number' && Number.isFinite(input.enderecoId)
          ? { endereco_id: Math.max(0, Math.trunc(input.enderecoId)) }
          : {}),
        ...(typeof transportadoraId === 'number' && transportadoraId > 0
          ? { transportadora_id: transportadoraId }
          : {}),
        ...(typeof cotacaoId === 'number' && cotacaoId > 0
          ? { cotacao_id: cotacaoId }
          : {}),
        ...(typeof valorFrete === 'number'
          ? { valor_frete: valorFrete }
          : {}),
        frete_por_conta: fretePorConta,
        ...(usarRedespacho === 0 || usarRedespacho === 1
          ? { usar_redespacho: usarRedespacho }
          : {}),
        ...(typeof transportadoraRedespachoId === 'number' && transportadoraRedespachoId > 0
          ? { transportadora_redespacho_id: transportadoraRedespachoId }
          : {}),
        cupom: normalizeCheckoutText(input.cupom),
        usar_cashback: input.usarCashback ? 1 : 0,
        ...(typeof input.faturarPainel === 'boolean'
          ? { faturar_painel: input.faturarPainel ? 1 : 0 }
          : {}),
        modo_frete: input.modoFrete ?? 'auto',
        ...(normalizeCheckoutText(input.origem)
          ? { origem: normalizeCheckoutText(input.origem) }
          : {}),
        nome_contato: normalizeCheckoutText(input.nomeContato),
        telefone_contato: normalizeCheckoutText(input.telefoneContato),
        aceita_marketing: input.aceitaMarketing ? 1 : 0,
        numero_ordem: normalizeCheckoutText(input.numeroOrdem),
        ...(normalizeCheckoutText(input.transportadoraCotacaoFreteNumero)
          ? {
              transportadora_cotacao_frete_numero: normalizeCheckoutText(input.transportadoraCotacaoFreteNumero),
              transportadora_cotacao_frete_valor: Math.max(0, input.transportadoraCotacaoFreteValor ?? 0),
            }
          : {}),
        finalidade:
          typeof input.finalidade === 'number' && Number.isFinite(input.finalidade)
            ? Math.max(0, Math.trunc(input.finalidade))
            : undefined,
        ...(habilitarProgramarFaturamento
          ? {
              habilitar_programar_faturamento: 1,
              data_programacao: dataProgramacao,
            }
          : {}),
        ...(normalizeCheckoutText(input.obsFichaCarregamento)
          ? { obs_ficha_carregamento: normalizeCheckoutText(input.obsFichaCarregamento) }
          : {}),
        ...(checkoutSnapshot ? { checkout_snapshot: checkoutSnapshot } : {}),
        ...(Array.isArray(input.carrinhoIds) && input.carrinhoIds.length > 0 ? { carrinho_ids: input.carrinhoIds } : {}),
        ...(attemptId ? { attempt_id: attemptId, attempt_source: attemptSource } : {}),
        ...(input.analytics ? { analytics: input.analytics } : {}),
      }),
    }
  )

  const pedidoId = Math.trunc(normalizeFreteNumber(response.pedido_id) ?? 0)
  const numeroPedido = Math.trunc(normalizeFreteNumber(response.numero_pedido) ?? 0)
  const attemptStatus = resolveCheckoutAttemptStatusFromResponse(
    response.attempt_status,
    pedidoId,
    numeroPedido
  )

  if (attemptStatus === 'processing' || pedidoId <= 0) {
    return {
      kind: 'processing',
      attemptId: normalizeCheckoutText(response.attempt_id) || attemptId || null,
      attemptStatus: 'processing',
    }
  }

  const statusFluxo = normalizeCheckoutStatusFluxo(response.status_fluxo)
  const responseStatusNumero = normalizeFreteNumber(response.status_numero)

  return {
    kind: 'success',
    attemptId: normalizeCheckoutText(response.attempt_id) || attemptId || null,
    attemptStatus: 'success',
    result: {
      pedidoId,
      numeroPedido,
      hash: normalizeCheckoutText(response.id),
      statusFluxo,
      statusNumero: typeof responseStatusNumero === 'number' && Number.isFinite(responseStatusNumero)
        ? Math.trunc(responseStatusNumero)
        : resolveCheckoutStatusNumero(statusFluxo),
      statusDescricao: normalizeCheckoutText(response.status_descricao),
      itensProcessados: Math.trunc(normalizeFreteNumber(response.itens_processados) ?? 0),
      checkoutSnapshot,
      paymentMethod: {
        formaPagamentoId: Math.trunc(input.formaPagamentoId ?? 0),
        condicaoPagamentoId: Math.trunc(input.condicaoPagamentoId ?? 0),
        label: '',
        description: '',
        detail: '',
      },
      paymentInstructions: extractCheckoutPaymentInstructions(response),
      paymentLink: normalizeCheckoutText(response.payment_link) || null,
    },
  }
}

export async function getCheckoutOrderAttemptStatus(input: {
  attemptId: string
  entidadeId?: number
  lojaId?: number
}): Promise<CheckoutOrderAttemptStatusResult> {
  const entidadeId = resolveRequestedEntidadeIdOrThrow(input.entidadeId, 'Status da tentativa do checkout')
  const lojaId = getLojaIdValue(input.lojaId)
  const attemptId = normalizeCheckoutText(input.attemptId)

  if (!attemptId) {
    throw new Error('Tentativa inválida para consultar o checkout.')
  }

  const response = await fetchApi<LojaVirtualPedidoGerarResponse>(
    buildPedidoAttemptStatusEndpoint(entidadeId, attemptId, lojaId),
    {
      method: 'GET',
      trackBackendAvailability: false,
    }
  )

  const statusFluxo = normalizeCheckoutStatusFluxo(response.status_fluxo)
  const responseStatusNumero = normalizeFreteNumber(response.status_numero)
  const pedidoId = Math.trunc(normalizeFreteNumber(response.pedido_id) ?? 0)
  const numeroPedido = Math.trunc(normalizeFreteNumber(response.numero_pedido) ?? 0)

  return {
    attemptId: normalizeCheckoutText(response.attempt_id) || attemptId,
    attemptSource: normalizeCheckoutAttemptSource(response.attempt_source),
    attemptStatus: resolveCheckoutAttemptStatusFromResponse(response.attempt_status, pedidoId, numeroPedido),
    pedidoId,
    numeroPedido,
    hash: normalizeCheckoutText(response.id),
    statusFluxo,
    statusNumero: typeof responseStatusNumero === 'number' && Number.isFinite(responseStatusNumero)
      ? Math.trunc(responseStatusNumero)
      : resolveCheckoutStatusNumero(statusFluxo),
    statusDescricao: normalizeCheckoutText(response.status_descricao),
    itensProcessados: Math.trunc(normalizeFreteNumber(response.itens_processados) ?? 0),
    parcial: typeof response.parcial === 'number' && Number.isFinite(response.parcial)
      ? Math.trunc(response.parcial)
      : null,
    motivo: normalizeCheckoutText(response.motivo),
    paymentInstructions: extractCheckoutPaymentInstructions(response),
    paymentLink: normalizeCheckoutText(response.payment_link) || null,
  }
}

export async function createPixCharge(input: CreatePixChargeInput): Promise<CheckoutPaymentInstructions | null> {
  const payload: Record<string, unknown> = {
    nota_id: Math.trunc(input.notaId),
  }

  if (typeof input.contaId === 'number' && Number.isFinite(input.contaId) && input.contaId > 0) {
    payload.conta_id = Math.trunc(input.contaId)
  }

  if (typeof input.expiracao === 'number' && Number.isFinite(input.expiracao) && input.expiracao > 0) {
    payload.expiracao = Math.trunc(input.expiracao)
  }

  const identificadorInterno = normalizeCheckoutText(input.identificadorInterno)
  if (identificadorInterno) {
    payload.identificador_interno = identificadorInterno
  }

  const solicitacaoPagador = normalizeCheckoutText(input.solicitacaoPagador)
  if (solicitacaoPagador) {
    payload.solicitacao_pagador = solicitacaoPagador
  }

  if (typeof input.valor === 'number' && Number.isFinite(input.valor) && input.valor >= 0) {
    payload.valor = input.valor
  }

  if (typeof input.valorCobrado === 'number' && Number.isFinite(input.valorCobrado) && input.valorCobrado >= 0) {
    payload.valor_cobrado = input.valorCobrado
  }

  const devedorNome = normalizeCheckoutText(input.devedorNome)
  if (devedorNome) {
    payload.devedor_nome = devedorNome
  }

  const devedorCpfCnpj = normalizeDigits(input.devedorCpfCnpj ?? '')
  if (devedorCpfCnpj) {
    payload.devedor_cpf_cnpj = devedorCpfCnpj
  }

  const webhookUrl = normalizeCheckoutText(input.webhookUrl)
  if (webhookUrl) {
    payload.webhook_url = webhookUrl
  }

  const response = await fetchApi<unknown>('/pix', {
    method: 'POST',
    body: JSON.stringify(payload),
  })

  return extractCheckoutPaymentInstructions(response)
}

async function resolvePromiseWithin<T>(promise: Promise<T>, timeoutMs: number): Promise<T | null> {
  const outcome = await resolvePromiseOutcomeWithin(promise, timeoutMs)
  if (outcome.status === 'fulfilled') {
    return outcome.value
  }
  return null
}

async function resolvePromiseOutcomeWithin<T>(
  promise: Promise<T>,
  timeoutMs: number
): Promise<
  | { status: 'fulfilled'; value: T }
  | { status: 'rejected'; error: unknown }
  | { status: 'timed_out' }
> {
  return new Promise((resolve) => {
    let settled = false
    const timeoutId = globalThis.setTimeout(() => {
      if (settled) return
      settled = true
      resolve({ status: 'timed_out' })
    }, Math.max(0, timeoutMs))

    promise
      .then((value) => {
        if (settled) return
        settled = true
        globalThis.clearTimeout(timeoutId)
        resolve({ status: 'fulfilled', value })
      })
      .catch((error) => {
        if (settled) return
        settled = true
        globalThis.clearTimeout(timeoutId)
        resolve({ status: 'rejected', error })
      })
  })
}

async function waitFor(ms: number): Promise<void> {
  await new Promise<void>((resolve) => {
    globalThis.setTimeout(resolve, Math.max(0, ms))
  })
}

export async function getPixChargeByIdentificadorInterno(
  identificadorInterno: string
): Promise<CheckoutPaymentInstructions | null> {
  const normalized = normalizeCheckoutText(identificadorInterno)
  if (!normalized) return null

  try {
    const response = await fetchApi<unknown>(`/pix/${encodeURIComponent(normalized)}`)
    return extractCheckoutPaymentInstructions(response)
  } catch (error) {
    if (isApiRequestError(error) && error.status === 404) {
      return null
    }
    throw error
  }
}

export async function resolvePixCharge(
  input: CreatePixChargeInput,
  options?: {
    createTimeoutMs?: number
    lookupAttempts?: number
    lookupTimeoutMs?: number
    lookupDelayMs?: number
  }
): Promise<CheckoutPaymentInstructions | null> {
  const createTimeoutMs = Math.max(250, options?.createTimeoutMs ?? 2500)
  const lookupAttempts = Math.max(0, Math.trunc(options?.lookupAttempts ?? 4))
  const lookupTimeoutMs = Math.max(250, options?.lookupTimeoutMs ?? 1200)
  const lookupDelayMs = Math.max(0, options?.lookupDelayMs ?? 650)
  const identificadorInterno = normalizeCheckoutText(input.identificadorInterno)

  const createdOutcome = await resolvePromiseOutcomeWithin(createPixCharge(input), createTimeoutMs)
  if (createdOutcome.status === 'fulfilled' && createdOutcome.value) {
    return createdOutcome.value
  }
  if (createdOutcome.status === 'rejected' && isTerminalPixCreateError(createdOutcome.error)) {
    const detail = isApiRequestError(createdOutcome.error)
      ? (createdOutcome.error.detail || 'PIX indisponivel para esta nota.')
      : 'PIX indisponivel para esta nota.'
    throw new PixUnavailableError(detail)
  }
  if (!identificadorInterno || lookupAttempts === 0) return null

  for (let attempt = 0; attempt < lookupAttempts; attempt += 1) {
    const existing = await resolvePromiseWithin(
      getPixChargeByIdentificadorInterno(identificadorInterno),
      lookupTimeoutMs
    )

    if (existing) return existing
    if (attempt < lookupAttempts - 1) {
      await waitFor(lookupDelayMs)
    }
  }

  return null
}

export async function getFinanceiro(
  page: number = 1,
  perPage: number = 20,
  status?: string,
  search?: string,
  days?: number
): Promise<PaginatedListResult<any>> {
  const entidadeId = resolveAuthenticatedEntidadeIdOrThrow('Financeiro do comprador')
  const params = new URLSearchParams()
  const lojaId = getLojaIdValue()
  if (lojaId) params.set('loja_id', String(lojaId))
  params.set('page', String(Math.max(1, Math.trunc(page))))
  params.set('per_page', String(Math.max(1, Math.trunc(perPage))))
  if (status?.trim()) params.set('status', status.trim())
  if (search?.trim()) params.set('search', search.trim())
  if (typeof days === 'number' && Number.isFinite(days) && days > 0) {
    params.set('days', String(Math.trunc(days)))
  }

  const query = params.toString()
  const response = await fetchApi<unknown>(
    `/loja-virtual/v1/compradores/${entidadeId}/areceber${query ? `?${query}` : ''}`,
    { suppressBuyerSessionExpiry: true }
  )

  const items = extractListFromResponse(response, ['areceber', 'a_receber', 'financeiro', 'items'])
  return buildPaginatedListResult(items, response, page, perPage)
}

type FinanceBoletoDownloadResult =
  | { type: 'url'; url: string }
  | { type: 'blob'; blob: Blob; fileName?: string | null }

type QuotePdfDownloadResult = FinanceBoletoDownloadResult
type AttendanceAttachmentDownloadResult = FinanceBoletoDownloadResult

function extractFileNameFromDisposition(contentDisposition: string | null): string | null {
  if (!contentDisposition) return null

  const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i)
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1])
    } catch {
      return utf8Match[1]
    }
  }

  const simpleMatch = contentDisposition.match(/filename="?([^"]+)"?/i)
  return simpleMatch?.[1]?.trim() || null
}

async function resolveFinanceDocumentDownload(
  path: string,
  acceptHeader: string,
  jsonUrlKeys: string[],
  unavailableMessage: string
): Promise<FinanceBoletoDownloadResult> {
  const response = await fetchApiResponse(path, {
    headers: {
      Accept: acceptHeader,
    },
  })

  const contentType = (response.headers.get('content-type') || '').toLocaleLowerCase('pt-BR')

  if (contentType.includes('application/json')) {
    const payload = await response.json() as Record<string, unknown>
    const urlCandidate = jsonUrlKeys
      .map((key) => payload[key])
      .find((value) => typeof value === 'string' && value.trim())

    if (typeof urlCandidate === 'string' && urlCandidate.trim()) {
      return {
        type: 'url',
        url: urlCandidate.trim(),
      }
    }

    throw new Error(unavailableMessage)
  }

  if (contentType.startsWith('text/')) {
    const textPayload = (await response.text()).trim()
    if (/^https?:\/\//i.test(textPayload)) {
      return {
        type: 'url',
        url: textPayload,
      }
    }
  }

  return {
    type: 'blob',
    blob: await response.blob(),
    fileName: extractFileNameFromDisposition(response.headers.get('content-disposition')),
  }
}

export async function getFinanceiroBoleto(areceberId: number): Promise<FinanceBoletoDownloadResult> {
  const entidadeId = resolveAuthenticatedEntidadeIdOrThrow('Boleto do financeiro')
  const params = new URLSearchParams()
  const lojaId = getLojaIdValue()
  if (lojaId) params.set('loja_id', String(lojaId))

  const query = params.toString()
  return resolveFinanceDocumentDownload(
    `/loja-virtual/v1/compradores/${entidadeId}/areceber/${encodeURIComponent(String(Math.trunc(areceberId)))}/boleto${query ? `?${query}` : ''}`,
    'application/pdf,application/octet-stream,application/json,text/plain,*/*',
    ['url', 'link', 'href', 'boleto', 'boleto_url', 'download_url', 'pdf_url'],
    'Boleto indisponível no momento.'
  )
}

export async function getFinanceiroNotaFiscal(areceberId: number): Promise<FinanceBoletoDownloadResult> {
  const entidadeId = resolveAuthenticatedEntidadeIdOrThrow('Nota fiscal do financeiro')
  const params = new URLSearchParams()
  const lojaId = getLojaIdValue()
  if (lojaId) params.set('loja_id', String(lojaId))

  const query = params.toString()
  return resolveFinanceDocumentDownload(
    `/loja-virtual/v1/compradores/${entidadeId}/areceber/${encodeURIComponent(String(Math.trunc(areceberId)))}/nota/pdf${query ? `?${query}` : ''}`,
    'application/pdf,application/octet-stream,application/json,text/plain,*/*',
    ['url', 'link', 'href', 'nota', 'nota_url', 'pdf_url', 'danfe', 'danfe_url', 'download_url'],
    'Nota fiscal indisponível no momento.'
  )
}

export async function getFinanceiroNotaFiscalXml(areceberId: number): Promise<FinanceBoletoDownloadResult> {
  const entidadeId = resolveAuthenticatedEntidadeIdOrThrow('XML da nota fiscal do financeiro')
  const params = new URLSearchParams()
  const lojaId = getLojaIdValue()
  if (lojaId) params.set('loja_id', String(lojaId))

  const query = params.toString()
  return resolveFinanceDocumentDownload(
    `/loja-virtual/v1/compradores/${entidadeId}/areceber/${encodeURIComponent(String(Math.trunc(areceberId)))}/nota/xml${query ? `?${query}` : ''}`,
    'application/xml,text/xml,application/octet-stream,application/json,text/plain,*/*',
    ['url', 'link', 'href', 'xml', 'xml_url', 'nota_xml', 'download_url'],
    'XML da nota fiscal indisponível no momento.'
  )
}

export async function getOrcamentoPdf(
  orcamentoId: number,
  options?: {
    fotos?: boolean
    preco?: 'real' | 'margem'
    paymentOption?: Pick<CheckoutPaymentOption, 'formaPagamentoId' | 'condicaoPagamentoId'> | null
    partnerSummary?: {
      subtotal: number
      ipi: number
      st: number
      total: number
    } | null
    checkoutSummary?: {
      subtotal: number
      ipi: number
      st: number
      total: number
    } | null
    checkoutItems?: Array<{
      itemId: number
      quantity: number
      unitPrice: number
      subtotal: number
      ipi: number
      st: number
      total: number
    }> | null
    freightQuote?: {
      transportadoraId?: number | null
      transportadoraNome: string
      tipoFrete?: 'cif' | 'fob'
      prazo?: string
      valorFrete: number
      modo?: 'separado' | 'diluido'
      usarRedespacho?: 0 | 1 | null
      transportadoraRedespachoId?: number | null
      transportadoraRedespachoNome?: string | null
      transportadoraRedespachoEnderecoResumo?: string | null
    } | null
    resaleProposal?: {
      includePrices: boolean
      issuer: {
        name: string
        document: string
        registration?: string
        phone: string
        address: string
        logoUrl?: string | null
      }
      buyer: {
        name: string
        document: string
        registration?: string
        phone: string
        address: string
      }
      items: Array<{
        code: string
        reference: string
        description: string
        imageUrl?: string | null
        ncm: string
        quantity: number
        unitPrice: number
        total: number
      }>
      subtotal: number
      ipi: number
      st: number
      total: number
      paymentTerms: string
      deliveryTerms: string
      validityTerms: string
    } | null
  }
): Promise<QuotePdfDownloadResult> {
  const entidadeId = resolveAuthenticatedEntidadeIdOrThrow('PDF do orçamento')
  const params = new URLSearchParams()
  const lojaId = getLojaIdValue()
  if (lojaId) params.set('loja_id', String(lojaId))
  if (options?.fotos === false) params.set('fotos', '0')
  if (options?.preco) params.set('preco', options.preco)
  if (options?.paymentOption) {
    const formaPagamentoId = Math.trunc(normalizeFreteNumber(options.paymentOption.formaPagamentoId) ?? 0)
    const condicaoPagamentoId = Math.trunc(normalizeFreteNumber(options.paymentOption.condicaoPagamentoId) ?? 0)
    if (formaPagamentoId > 0) params.set('forma_pagamento_id', String(formaPagamentoId))
    if (condicaoPagamentoId > 0) params.set('condicao_pagamento_id', String(condicaoPagamentoId))
  }
  if (options?.partnerSummary) {
    params.set('parceiro_subtotal', Math.max(0, options.partnerSummary.subtotal).toFixed(2))
    params.set('parceiro_ipi', Math.max(0, options.partnerSummary.ipi).toFixed(2))
    params.set('parceiro_st', Math.max(0, options.partnerSummary.st).toFixed(2))
    params.set('parceiro_total', Math.max(0, options.partnerSummary.total).toFixed(2))
  }
  if (options?.checkoutSummary) {
    params.set('checkout_subtotal', Math.max(0, options.checkoutSummary.subtotal).toFixed(2))
    params.set('checkout_ipi', Math.max(0, options.checkoutSummary.ipi).toFixed(2))
    params.set('checkout_st', Math.max(0, options.checkoutSummary.st).toFixed(2))
    params.set('checkout_total', Math.max(0, options.checkoutSummary.total).toFixed(2))
  }
  const hasExplicitFreightQuote = Object.prototype.hasOwnProperty.call(options ?? {}, 'freightQuote')
  if (options?.freightQuote) {
    const freightQuote = options.freightQuote
    const transportadoraNome = normalizeCheckoutText(freightQuote.transportadoraNome)
    const prazo = normalizeCheckoutText(freightQuote.prazo)
    const valorFrete = Math.max(0, normalizeFreteNumber(freightQuote.valorFrete) ?? 0)
    const transportadoraId = Math.trunc(normalizeFreteNumber(freightQuote.transportadoraId) ?? 0)

    if (transportadoraNome) params.set('frete_transportadora', transportadoraNome)
    if (transportadoraId > 0) params.set('frete_transportadora_id', String(transportadoraId))
    if (freightQuote.tipoFrete === 'cif' || freightQuote.tipoFrete === 'fob') params.set('frete_tipo', freightQuote.tipoFrete)
    if (prazo) params.set('frete_prazo', prazo)
    params.set('frete_valor', valorFrete.toFixed(2))
    if (freightQuote.modo === 'diluido' || freightQuote.modo === 'separado') params.set('frete_modo', freightQuote.modo)
    if (freightQuote.usarRedespacho === 0 || freightQuote.usarRedespacho === 1) {
      params.set('frete_usar_redespacho', String(freightQuote.usarRedespacho))
    }
    const transportadoraRedespachoId = Math.trunc(normalizeFreteNumber(freightQuote.transportadoraRedespachoId) ?? 0)
    const transportadoraRedespachoNome = normalizeCheckoutText(freightQuote.transportadoraRedespachoNome)
    const transportadoraRedespachoEnderecoResumo = normalizeCheckoutText(
      freightQuote.transportadoraRedespachoEnderecoResumo
    )
    if (transportadoraRedespachoId > 0) params.set('frete_redespacho_transportadora_id', String(transportadoraRedespachoId))
    if (transportadoraRedespachoNome) params.set('frete_redespacho_transportadora', transportadoraRedespachoNome)
    if (transportadoraRedespachoEnderecoResumo) params.set('frete_redespacho_endereco', transportadoraRedespachoEnderecoResumo)
  } else if (hasExplicitFreightQuote) {
    // `null` significa que a tela atual está explicitamente sem frete. O zero
    // impede o PDF de reutilizar uma seleção antiga persistida no orçamento.
    params.set('frete_valor', '0.00')
  }

  const query = params.toString()
  const resaleProposal = options?.resaleProposal
  const checkoutItems = Array.isArray(options?.checkoutItems)
    ? options.checkoutItems.filter((item) => (
        Number.isFinite(item.itemId)
        && item.itemId > 0
        && Number.isFinite(item.quantity)
        && item.quantity > 0
      ))
    : []
  const shouldPostPayload = Boolean(resaleProposal) || checkoutItems.length > 0
  const response = await fetchApiResponse(
    `/loja-virtual/v1/compradores/${entidadeId}/orcamentos/${encodeURIComponent(String(Math.trunc(orcamentoId)))}/pdf${query ? `?${query}` : ''}`,
    {
      ...(shouldPostPayload
        ? {
            method: 'POST',
            body: JSON.stringify({
              ...(resaleProposal
                ? {
                    proposta: {
                      tipo: 'revenda',
                      incluir_fotos: options?.fotos !== false,
                      incluir_precos: resaleProposal.includePrices,
                      emissor: {
                        razao: resaleProposal.issuer.name,
                        cnpj: resaleProposal.issuer.document,
                        ie: resaleProposal.issuer.registration || null,
                        telefone: resaleProposal.issuer.phone,
                        endereco: resaleProposal.issuer.address,
                        logo_url: resaleProposal.issuer.logoUrl || null,
                      },
                      comprador: {
                        razao: resaleProposal.buyer.name,
                        cpf_cnpj: resaleProposal.buyer.document,
                        ie: resaleProposal.buyer.registration || null,
                        telefone: resaleProposal.buyer.phone,
                        endereco: resaleProposal.buyer.address,
                      },
                      itens: resaleProposal.items.map((item) => ({
                        codigo: item.code,
                        referencia: item.reference,
                        descricao: item.description,
                        imagem_url: item.imageUrl || null,
                        ncm: item.ncm,
                        quantidade: item.quantity,
                        preco_unitario: item.unitPrice,
                        total: item.total,
                      })),
                      subtotal: resaleProposal.subtotal,
                      ipi: resaleProposal.ipi,
                      st: resaleProposal.st,
                      total: resaleProposal.total,
                      condicoes: {
                        forma_pagamento: resaleProposal.paymentTerms,
                        prazo_entrega: resaleProposal.deliveryTerms,
                        validade_cotacao: resaleProposal.validityTerms,
                      },
                    },
                  }
                : {}),
              ...(checkoutItems.length > 0
                ? {
                    checkout: {
                      itens: checkoutItems.map((item) => ({
                        item_id: Math.trunc(item.itemId),
                        quantidade: Math.max(0, item.quantity),
                        preco_unitario: Math.max(0, item.unitPrice),
                        subtotal: Math.max(0, item.subtotal),
                        ipi: Math.max(0, item.ipi),
                        st: Math.max(0, item.st),
                        total: Math.max(0, item.total),
                      })),
                    },
                  }
                : {}),
            }),
          }
        : {}),
      headers: {
        Accept: 'application/pdf,application/octet-stream,application/json,text/plain,*/*',
        ...(shouldPostPayload ? { 'Content-Type': 'application/json' } : {}),
      },
    }
  )

  return readQuotePdfDownloadResponse(response, 'PDF do orçamento indisponível no momento.')
}

export async function getOrcamentoPdfSemPreco(
  orcamentoId: number,
  options?: { fotos?: boolean }
): Promise<QuotePdfDownloadResult> {
  const entidadeId = resolveAuthenticatedEntidadeIdOrThrow('PDF do orçamento sem preço')
  const params = new URLSearchParams()
  const lojaId = getLojaIdValue()
  if (lojaId) params.set('loja_id', String(lojaId))
  if (options?.fotos === false) params.set('fotos', '0')

  const query = params.toString()
  const response = await fetchApiResponse(
    `/loja-virtual/v1/compradores/${entidadeId}/orcamentos/${encodeURIComponent(String(Math.trunc(orcamentoId)))}/pdf-sem-preco${query ? `?${query}` : ''}`,
    {
      headers: {
        Accept: 'application/pdf,application/octet-stream,application/json,text/plain,*/*',
      },
    }
  )

  return readQuotePdfDownloadResponse(response, 'PDF do orçamento sem preço indisponível no momento.')
}

async function readQuotePdfDownloadResponse(
  response: Response,
  unavailableMessage: string
): Promise<QuotePdfDownloadResult> {
  const contentType = (response.headers.get('content-type') || '').toLocaleLowerCase('pt-BR')

  if (contentType.includes('application/json')) {
    const payload = await response.json() as Record<string, unknown>
    const urlCandidate = [
      payload.url,
      payload.link,
      payload.href,
      payload.download_url,
      payload.pdf_url,
    ].find((value) => typeof value === 'string' && value.trim())

    if (typeof urlCandidate === 'string' && urlCandidate.trim()) {
      return {
        type: 'url',
        url: urlCandidate.trim(),
      }
    }

    throw new Error(unavailableMessage)
  }

  if (contentType.startsWith('text/')) {
    const textPayload = (await response.text()).trim()
    if (/^https?:\/\//i.test(textPayload)) {
      return {
        type: 'url',
        url: textPayload,
      }
    }
  }

  return {
    type: 'blob',
    blob: await response.blob(),
    fileName: extractFileNameFromDisposition(response.headers.get('content-disposition')),
  }
}

export async function salvarPdfOrcamentoNoCliente(input: {
  orcamentoId: number | string
  clienteId?: number | null
  clienteNome?: string | null
  clienteDocumento?: string | null
  orcamentoNome?: string | null
  arquivo: File | Blob
  fileName: string
}): Promise<{ message: string; payload: unknown }> {
  const entidadeId = resolveAuthenticatedEntidadeIdOrThrow('Salvar PDF do orçamento no cliente')
  const normalizedQuoteId = String(input.orcamentoId ?? '').trim()
  if (!normalizedQuoteId) {
    throw new Error('Orçamento inválido para salvar o PDF no cliente.')
  }

  const normalizedClientId = normalizePositiveInteger(input.clienteId)
  const params = new URLSearchParams()
  const lojaId = getLojaIdValue()
  if (lojaId) params.set('loja_id', String(lojaId))
  const query = params.toString()

  const candidateEndpoints = [
    ...(normalizedClientId
      ? [
          `/loja-virtual/v1/compradores/${entidadeId}/indicador/clientes/${encodeURIComponent(String(normalizedClientId))}/anexos${query ? `?${query}` : ''}`,
          `/loja-virtual/v1/compradores/${entidadeId}/indicador/clientes/${encodeURIComponent(String(normalizedClientId))}/arquivos${query ? `?${query}` : ''}`,
        ]
      : []),
    `/loja-virtual/v1/compradores/${entidadeId}/orcamentos/${encodeURIComponent(normalizedQuoteId)}/pdf-cliente${query ? `?${query}` : ''}`,
    `/loja-virtual/v1/compradores/${entidadeId}/orcamentos/${encodeURIComponent(normalizedQuoteId)}/anexos${query ? `?${query}` : ''}`,
  ]

  let lastError: unknown = null

  for (const endpoint of candidateEndpoints) {
    const formData = new FormData()
    formData.append('arquivo', input.arquivo, input.fileName)
    formData.append('tipo', 'orcamento_cliente_pdf')
    formData.append('origem', 'loja_virtual_orcamento_cliente')
    formData.append('orcamento_id', normalizedQuoteId)
    if (input.orcamentoNome?.trim()) formData.append('orcamento_nome', input.orcamentoNome.trim())
    if (input.clienteNome?.trim()) formData.append('cliente_nome', input.clienteNome.trim())
    if (input.clienteDocumento?.trim()) formData.append('cliente_documento', input.clienteDocumento.trim())
    if (normalizedClientId) formData.append('cliente_id', String(normalizedClientId))

    try {
      const response = await fetchApi<any>(endpoint, {
        method: 'POST',
        body: formData,
      })

      return {
        message: response?.message || response?.motivo || 'PDF salvo no cliente.',
        payload: response,
      }
    } catch (error) {
      lastError = error
      const message = error instanceof Error ? error.message : String(error ?? '')
      if (!/(404|405|not found|não encontrado|nao encontrado|method not allowed)/i.test(message)) {
        throw error
      }
    }
  }

  throw lastError instanceof Error
    ? lastError
    : new Error('Não foi possível salvar o PDF no cliente.')
}

export async function definirMargemOrcamento(
  orcamentoId: number | string,
  tipo: 'percentual' | 'valor' | null,
  valor: number,
  options?: {
    maoObraValor?: number
    maoObraDetalhes?: string
    materiaisMontagemInstalacaoValor?: number
    materiaisMontagemInstalacaoDetalhes?: string
  }
): Promise<void> {
  const entidadeId = resolveAuthenticatedEntidadeIdOrThrow('Margem do orçamento')
  const normalizedQuoteId = String(orcamentoId ?? '').trim()
  if (!normalizedQuoteId) {
    throw new Error('Identificador do orçamento inválido.')
  }
  const params = new URLSearchParams()
  const lojaId = getLojaIdValue()
  if (lojaId) params.set('loja_id', String(lojaId))

  const query = params.toString()
  await fetchApi(
    `/loja-virtual/v1/compradores/${entidadeId}/orcamentos/${encodeURIComponent(normalizedQuoteId)}/margem${query ? `?${query}` : ''}`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        tipo,
        valor,
        mao_obra_valor: options?.maoObraValor ?? 0,
        mao_obra_detalhes: options?.maoObraDetalhes ?? '',
        materiais_montagem_instalacao_valor: options?.materiaisMontagemInstalacaoValor ?? 0,
        materiais_montagem_instalacao_detalhes: options?.materiaisMontagemInstalacaoDetalhes ?? '',
        custos_adicionais: {
          mao_obra: {
            valor: options?.maoObraValor ?? 0,
            detalhes: options?.maoObraDetalhes ?? '',
          },
          materiais_montagem_instalacao: {
            valor: options?.materiaisMontagemInstalacaoValor ?? 0,
            detalhes: options?.materiaisMontagemInstalacaoDetalhes ?? '',
          },
        },
      }),
    }
  )
}

const buildQuoteCheckoutFreightPayload = (
  option: CheckoutShippingOption | null,
  redespachoChoice?: {
    usarRedespacho: 0 | 1
    transportadoraRedespachoId?: number | null
  } | null
) => {
  const redespachoTransportadoraId = (
    redespachoChoice?.transportadoraRedespachoId
    ?? option?.redespacho?.transportadoraId
    ?? null
  )
  const usarRedespacho = redespachoChoice?.usarRedespacho
    ?? (redespachoTransportadoraId ? 1 : 0)

  return option
    ? {
        tipo_frete: option.tipoFrete,
        transportadora_id: option.transportadoraId,
        transportadora_nome: option.label,
        cotacao_id: option.cotacaoId,
        numero_cotacao: option.quoteNumber ?? null,
        prazo: option.eta,
        valor: Math.max(0, option.price),
        usar_redespacho: usarRedespacho,
        transportadora_redespacho_id: usarRedespacho === 1 ? redespachoTransportadoraId : null,
        redespacho: usarRedespacho === 1 && option.redespacho
          ? {
              transportadora_id: redespachoTransportadoraId,
              transportadora_nome: option.redespacho.transportadoraNome,
              endereco_resumo: option.redespacho.enderecoResumo,
            }
          : null,
      }
    : null
}

export async function salvarFreteOrcamento(
  orcamentoId: number | string,
  option: CheckoutShippingOption | null,
  redespachoChoice?: {
    usarRedespacho: 0 | 1
    transportadoraRedespachoId?: number | null
  } | null
): Promise<{
  valorFrete: number
  freteSelecionado: Record<string, unknown> | null
}> {
  const entidadeId = resolveAuthenticatedEntidadeIdOrThrow('Frete do orçamento')
  const normalizedQuoteId = String(orcamentoId ?? '').trim()
  if (!normalizedQuoteId) {
    throw new Error('Identificador do orçamento inválido.')
  }

  const params = new URLSearchParams()
  const lojaId = getLojaIdValue()
  if (lojaId) params.set('loja_id', String(lojaId))

  const query = params.toString()
  const response = await fetchApi<any>(
    `/loja-virtual/v1/compradores/${entidadeId}/orcamentos/${encodeURIComponent(normalizedQuoteId)}/frete${query ? `?${query}` : ''}`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        frete_selecionado: buildQuoteCheckoutFreightPayload(option, redespachoChoice),
      }),
    }
  )

  return {
    valorFrete: Math.max(0, Number(response?.valor_frete ?? response?.valorFrete ?? 0) || 0),
    freteSelecionado: isPlainRecord(response?.frete_selecionado)
      ? response.frete_selecionado as Record<string, unknown>
      : null,
  }
}

export async function salvarPreparacaoCheckoutOrcamento(
  orcamentoId: number | string,
  input: {
    mode?: 'partner' | 'resale'
    enderecoId?: number | null
    contatoNome?: string | null
    contatoTelefone?: string | null
    formaPagamentoId?: number | null
    condicaoPagamentoId?: number | null
    finalidadePadrao?: number | null
    shippingOption?: CheckoutShippingOption | null
    persistFreight?: boolean
    usarRedespacho?: 0 | 1
    transportadoraRedespachoId?: number | null
    financialSummary?: CheckoutSimulationResult | null
  }
): Promise<Record<string, unknown> | null> {
  const entidadeId = resolveAuthenticatedEntidadeIdOrThrow('Preparação do checkout do orçamento')
  const normalizedQuoteId = String(orcamentoId ?? '').trim()
  if (!normalizedQuoteId) {
    throw new Error('Identificador do orçamento inválido.')
  }

  const params = new URLSearchParams()
  const lojaId = getLojaIdValue()
  if (lojaId) params.set('loja_id', String(lojaId))

  const body: Record<string, unknown> = {}
  if (input.mode) {
    body.modo = input.mode
  }
  if (Object.prototype.hasOwnProperty.call(input, 'enderecoId')) {
    body.endereco_id = input.enderecoId ?? null
  }
  if (
    Object.prototype.hasOwnProperty.call(input, 'contatoNome')
    || Object.prototype.hasOwnProperty.call(input, 'contatoTelefone')
  ) {
    body.contato_nome = input.contatoNome ?? null
    body.contato_telefone = input.contatoTelefone ?? null
  }
  if (
    Object.prototype.hasOwnProperty.call(input, 'formaPagamentoId')
    || Object.prototype.hasOwnProperty.call(input, 'condicaoPagamentoId')
  ) {
    body.forma_pagamento_id = input.formaPagamentoId ?? null
    body.condicao_pagamento_id = input.condicaoPagamentoId ?? null
  }
  if (Object.prototype.hasOwnProperty.call(input, 'finalidadePadrao')) {
    body.finalidade_padrao = input.finalidadePadrao ?? null
  }
  if (input.persistFreight) {
    body.frete_selecionado = buildQuoteCheckoutFreightPayload(
      input.shippingOption ?? null,
      {
        usarRedespacho: input.usarRedespacho ?? 0,
        transportadoraRedespachoId: input.transportadoraRedespachoId ?? null,
      }
    )
  }
  if (Object.prototype.hasOwnProperty.call(input, 'financialSummary')) {
    body.resumo_financeiro = input.financialSummary ?? null
  }

  const query = params.toString()
  const response = await fetchApi<any>(
    `/loja-virtual/v1/compradores/${entidadeId}/orcamentos/${encodeURIComponent(normalizedQuoteId)}/checkout-preparacao${query ? `?${query}` : ''}`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }
  )

  return isPlainRecord(response?.checkout_prefill)
    ? response.checkout_prefill as Record<string, unknown>
    : null
}

export async function getCupons(productId: number): Promise<any[]> {
  const entidadeId = resolveAuthenticatedEntidadeIdOrThrow('Listagem de cupons')
  const response = await fetchApi<unknown>(`/clientes/${entidadeId}/cupons?page=1&pageSize=100`)
  const items = extractListFromResponse(response, ['items', 'cupons', 'coupons'])

  const targetProductId = Math.trunc(productId)
  if (!Number.isFinite(targetProductId) || targetProductId <= 0) {
    return items
  }

  return items.filter((item) => {
    if (!item || typeof item !== 'object') return false
    const record = item as Record<string, unknown>
    const candidate = Number.parseInt(String(record.produto_id ?? record.produtoId ?? record.product_id ?? record.productId ?? ''), 10)
    return Number.isFinite(candidate) && candidate === targetProductId
  })
}

const getPedidosRequest = new Map<string, Promise<PaginatedListResult<any>>>()

export async function getPedidos(
  page: number = 1,
  perPage: number = 20,
  status?: string,
  search?: string,
  days?: number,
  entidadeId?: number
): Promise<PaginatedListResult<any>> {
  const resolvedEntidadeId = resolveRequestedEntidadeIdOrThrow(entidadeId, 'Listagem de pedidos')
  const params = new URLSearchParams()
  const lojaId = getLojaIdValue()
  if (lojaId) params.set('loja_id', String(lojaId))
  params.set('page', String(Math.max(1, Math.trunc(page))))
  params.set('per_page', String(Math.max(1, Math.trunc(perPage))))
  if (status?.trim()) params.set('status', status.trim())
  if (search?.trim()) params.set('search', search.trim())
  if (typeof days === 'number' && Number.isFinite(days) && days > 0) {
    params.set('days', String(Math.trunc(days)))
  }

  const query = params.toString()
  // Dedupe em voo: o fallback de "último pedido" do checkout dispara getPedidos
  // pelos dois efeitos do CartDrawer com os mesmos parâmetros.
  const dedupeKey = `${resolvedEntidadeId}|${query}`
  const pending = getPedidosRequest.get(dedupeKey)
  if (pending) {
    return pending
  }

  const request = (async () => {
    const response = await fetchApi<unknown>(
      `/loja-virtual/v1/compradores/${resolvedEntidadeId}/pedidos?${query}`,
      { suppressBuyerSessionExpiry: true }
    )
    const items = extractListFromResponse(response, ['pedidos', 'items'])
    return buildPaginatedListResult(items, response, page, perPage)
  })().finally(() => {
    getPedidosRequest.delete(dedupeKey)
  })

  getPedidosRequest.set(dedupeKey, request)
  return request
}

const getPedidoByIdRequest = new Map<string, Promise<any>>()

export async function getPedidoById(id: number, entidadeId?: number): Promise<any> {
  const resolvedEntidadeId = resolveRequestedEntidadeIdOrThrow(entidadeId, 'Detalhe de pedido')
  const normalizedPedidoId = Math.trunc(id)
  const params = new URLSearchParams()
  const lojaId = getLojaIdValue()
  if (lojaId) params.set('loja_id', String(lojaId))

  const query = params.toString()
  const dedupeKey = `${resolvedEntidadeId}|${normalizedPedidoId}|${query}`
  const pending = getPedidoByIdRequest.get(dedupeKey)
  if (pending) {
    return pending
  }

  const request = fetchApi<any>(
    `/loja-virtual/v1/compradores/${resolvedEntidadeId}/pedidos/${encodeURIComponent(String(normalizedPedidoId))}${query ? `?${query}` : ''}`,
    { suppressBuyerSessionExpiry: true }
  ).finally(() => {
    getPedidoByIdRequest.delete(dedupeKey)
  })

  getPedidoByIdRequest.set(dedupeKey, request)
  return request
}

export async function generatePedidoPixCharge(
  pedidoId: number,
  entidadeId?: number
): Promise<CheckoutPaymentInstructions | null> {
  const resolvedEntidadeId = resolveRequestedEntidadeIdOrThrow(entidadeId, 'Cobrança PIX do pedido')
  const normalizedPedidoId = Math.trunc(pedidoId)
  if (!Number.isFinite(normalizedPedidoId) || normalizedPedidoId <= 0) {
    throw new PixUnavailableError('Pedido inválido para gerar a cobrança PIX.')
  }

  const lojaId = getLojaIdValue()

  try {
    const response = await fetchApi<unknown>(
      `/loja-virtual/v1/compradores/${resolvedEntidadeId}/pedidos/${encodeURIComponent(String(normalizedPedidoId))}/pix`,
      {
        method: 'POST',
        body: JSON.stringify({
          ...(lojaId ? { loja_id: lojaId } : {}),
        }),
        suppressBuyerSessionExpiry: true,
      }
    )

    return extractCheckoutPaymentInstructions(response)
  } catch (error) {
    // Erros de estado/permissão não mudam ao repetir a mesma chamada. Marcá-los
    // como terminais evita o loop que antes martelava /api/pix com respostas 403.
    if (isApiRequestError(error) && [400, 403, 404, 409].includes(error.status)) {
      throw new PixUnavailableError(error.detail || 'PIX indisponível para este pedido.')
    }
    throw error
  }
}

export type OrcamentoPagamentoLinkState = {
  status: number
  orcamento_id: number
  nota_id: number
  forma_pagamento: 'pix' | 'mercado_pago'
  forma_pagamento_id: 8 | 9
  forma_pagamento_descricao: string | null
  valor: number
  tentativa_status: 'nao_iniciada' | 'aguardando_pagamento' | 'confirmando' | 'concluida' | 'conciliacao_pendente'
  pedido_id: number | null
  motivo?: string | null
  pix?: { copia_e_cola: string; txid: string } | null
  mercado_pago?: { checkout_url: string; preference_id: string } | null
}

const buildOrcamentoPagamentoLinkEndpoint = (
  entidadeId: number,
  orcamentoId: number,
  lojaId?: number,
  suffix = ''
) => {
  const normalizedOrcamentoId = Math.trunc(orcamentoId)
  if (!Number.isFinite(normalizedOrcamentoId) || normalizedOrcamentoId <= 0) {
    throw new Error('Orçamento inválido para pagamento.')
  }
  const params = new URLSearchParams()
  if (lojaId) params.set('loja_id', String(lojaId))
  const query = params.toString()
  return `/loja-virtual/v1/compradores/${entidadeId}/orcamentos/${encodeURIComponent(String(normalizedOrcamentoId))}/pagamento${suffix}${query ? `?${query}` : ''}`
}

export async function getOrcamentoPagamentoLinkState(
  orcamentoId: number,
  entidadeId?: number
): Promise<OrcamentoPagamentoLinkState> {
  const resolvedEntidadeId = resolveRequestedEntidadeIdOrThrow(entidadeId, 'Pagamento do orçamento')
  return fetchApi<OrcamentoPagamentoLinkState>(
    buildOrcamentoPagamentoLinkEndpoint(resolvedEntidadeId, orcamentoId, getLojaIdValue()),
    { suppressBuyerSessionExpiry: true, cache: 'no-store' }
  )
}

export async function iniciarOrcamentoPagamentoLink(
  orcamentoId: number,
  entidadeId?: number
): Promise<OrcamentoPagamentoLinkState> {
  const resolvedEntidadeId = resolveRequestedEntidadeIdOrThrow(entidadeId, 'Pagamento do orçamento')
  return fetchApi<OrcamentoPagamentoLinkState>(
    buildOrcamentoPagamentoLinkEndpoint(resolvedEntidadeId, orcamentoId, getLojaIdValue(), '/iniciar'),
    { method: 'POST', body: JSON.stringify({}), suppressBuyerSessionExpiry: true }
  )
}

export type PedidoAlteracaoPagamentoFreteContext = {
  status: number
  elegivel: number
  revisao: string
  pedido_id: number
  entidade_id: number
  loja_id: number
  endereco_id: number | null
  formas_pagamento_alteracao: number[]
  atual: {
    frete: {
      tipo: 'cif' | 'fob'
      transportadora_id: number | null
      transportadora: string | null
      cotacao_numero: string | null
      valor: number
    }
    pagamento: {
      forma_pagamento_id: number
      forma_pagamento: string | null
      condicao_pagamento_id: number
      condicao_pagamento: string | null
      forma_pagamento_entrada: number | null
      valor_entrada: number
      saldo_pix_aberto: number
    }
    total: number
    saldo_aberto: number
    valor_pago: number
  }
}

export type PedidoAlteracaoPagamentoFreteInput = {
  revisao: string
  simulacao_revisao?: string
  frete: {
    tipo: 'cif' | 'fob'
    transportadora_id: number
    cotacao_id?: number | null
  }
  pagamento: {
    forma_pagamento_id: number
    condicao_pagamento_id: number
  }
}

export type PedidoAlteracaoPagamentoFreteSimulation = {
  status: number
  revisao: string
  simulacao_revisao: string
  atual: PedidoAlteracaoPagamentoFreteContext['atual']
  novo: {
    frete: {
      tipo: 'cif' | 'fob'
      valor: number
      transportadora: {
        id: number
        cotacao_id: number
        nome: string | null
        cotacao_numero: string | null
      } | null
    }
    pagamento: {
      forma_pagamento_id: number
      condicao_pagamento_id: number
      pix_cif_separado: number
      valor_pix_cif: number
    }
    total: number
  }
  resumo: Record<string, unknown>
}

export type PedidoAlteracaoPagamentoFreteConfirmation = {
  status: number
  motivo?: string
  revisao: string
  atual: PedidoAlteracaoPagamentoFreteContext['atual']
  proxima_acao: {
    tipo: 'pix' | 'mercado_pago' | 'boleto' | 'financeiro'
    gerar_novo?: number
    finalidade?: 'principal' | 'frete_cif'
  }
}

const buildPedidoAlteracaoPagamentoFreteEndpoint = (
  pedidoId: number,
  entidadeId?: number,
  suffix = ''
) => {
  const resolvedEntidadeId = resolveRequestedEntidadeIdOrThrow(entidadeId, 'Alteração do pedido')
  const normalizedPedidoId = Math.trunc(pedidoId)
  const params = new URLSearchParams()
  const lojaId = getLojaIdValue()
  if (lojaId) params.set('loja_id', String(lojaId))
  const query = params.toString()
  return `/loja-virtual/v1/compradores/${resolvedEntidadeId}/pedidos/${encodeURIComponent(String(normalizedPedidoId))}/alteracao-pagamento-frete${suffix}${query ? `?${query}` : ''}`
}

export async function getPedidoAlteracaoPagamentoFreteContext(
  pedidoId: number,
  entidadeId?: number
): Promise<PedidoAlteracaoPagamentoFreteContext> {
  return fetchApi<PedidoAlteracaoPagamentoFreteContext>(
    buildPedidoAlteracaoPagamentoFreteEndpoint(pedidoId, entidadeId),
    { suppressBuyerSessionExpiry: true }
  )
}

export async function simulatePedidoAlteracaoPagamentoFrete(
  pedidoId: number,
  input: PedidoAlteracaoPagamentoFreteInput,
  entidadeId?: number
): Promise<PedidoAlteracaoPagamentoFreteSimulation> {
  const requestSimulation = (requestInput: PedidoAlteracaoPagamentoFreteInput) => (
    fetchApi<PedidoAlteracaoPagamentoFreteSimulation>(
      buildPedidoAlteracaoPagamentoFreteEndpoint(pedidoId, entidadeId, '/simular'),
      {
        method: 'POST',
        body: JSON.stringify(requestInput),
        suppressBuyerSessionExpiry: true,
      }
    )
  )

  try {
    return await requestSimulation(input)
  } catch (error) {
    if (
      !isApiRequestError(error)
      || error.status !== 409
      || error.code !== 'pedido_revisao_desatualizada'
    ) {
      throw error
    }

    const contextoAtual = await getPedidoAlteracaoPagamentoFreteContext(pedidoId, entidadeId)
    return requestSimulation({
      ...input,
      revisao: contextoAtual.revisao,
    })
  }
}

export async function confirmPedidoAlteracaoPagamentoFrete(
  pedidoId: number,
  input: PedidoAlteracaoPagamentoFreteInput,
  entidadeId?: number
): Promise<PedidoAlteracaoPagamentoFreteConfirmation> {
  return fetchApi<PedidoAlteracaoPagamentoFreteConfirmation>(
    buildPedidoAlteracaoPagamentoFreteEndpoint(pedidoId, entidadeId, '/confirmar'),
    {
      method: 'POST',
      body: JSON.stringify(input),
      suppressBuyerSessionExpiry: true,
    }
  )
}

export type PedidoWhatsappNotificationSubscriptionResult = {
  status?: number
  inscricao_status?: 'pendente' | 'ativa' | 'rejeitada' | 'cancelada' | string
  abrir_whatsapp?: boolean
  inscricao_id?: number
  numero_pedido?: number
  whatsapp?: string
  mensagem?: string
  url?: string
  error?: string
  message?: string
}

export async function solicitarPedidoWhatsappNotificationSubscription(
  pedidoId: number,
  entidadeId?: number
): Promise<PedidoWhatsappNotificationSubscriptionResult> {
  const resolvedEntidadeId = resolveRequestedEntidadeIdOrThrow(entidadeId, 'Inscrição nas notificações do pedido')
  const normalizedPedidoId = Math.trunc(pedidoId)
  const params = new URLSearchParams()
  const lojaId = getLojaIdValue()
  if (lojaId) params.set('loja_id', String(lojaId))

  const query = params.toString()
  return fetchApi<PedidoWhatsappNotificationSubscriptionResult>(
    `/loja-virtual/v1/compradores/${resolvedEntidadeId}/pedidos/${encodeURIComponent(String(normalizedPedidoId))}/notificacoes-whatsapp/inscricao${query ? `?${query}` : ''}`,
    {
      method: 'POST',
    }
  )
}

export async function getPedidoPdf(pedidoId: number, entidadeId?: number): Promise<FinanceBoletoDownloadResult> {
  const resolvedEntidadeId = resolveRequestedEntidadeIdOrThrow(entidadeId, 'PDF do pedido')
  const params = new URLSearchParams()
  const lojaId = getLojaIdValue()
  if (lojaId) params.set('loja_id', String(lojaId))

  const query = params.toString()
  return resolveFinanceDocumentDownload(
    `/loja-virtual/v1/compradores/${resolvedEntidadeId}/pedidos/${encodeURIComponent(String(Math.trunc(pedidoId)))}/pdf${query ? `?${query}` : ''}`,
    'application/pdf,application/octet-stream,application/json,text/plain,*/*',
    ['url', 'link', 'href', 'pedido_pdf', 'pedidoPdf', 'download_url', 'downloadUrl', 'pdf_url', 'pdfUrl'],
    'PDF do pedido indisponível no momento.'
  )
}

export async function getPedidoBoleto(pedidoId: number, entidadeId?: number): Promise<FinanceBoletoDownloadResult> {
  const resolvedEntidadeId = resolveRequestedEntidadeIdOrThrow(entidadeId, 'Boleto do pedido')
  const params = new URLSearchParams()
  const lojaId = getLojaIdValue()
  if (lojaId) params.set('loja_id', String(lojaId))

  const query = params.toString()
  return resolveFinanceDocumentDownload(
    `/loja-virtual/v1/compradores/${resolvedEntidadeId}/pedidos/${encodeURIComponent(String(Math.trunc(pedidoId)))}/documentos/boleto${query ? `?${query}` : ''}`,
    'application/pdf,application/octet-stream,application/json,text/plain,*/*',
    ['url', 'link', 'href', 'boleto', 'boleto_url', 'download_url', 'pdf_url'],
    'Boleto indisponível no momento.'
  )
}

export async function uploadPedidoPaymentReceipt(
  pedidoId: number,
  file: File,
  options?: {
    entidadeId?: number
    lojaId?: number
    source?: 'checkout_complete' | 'order_detail' | string
  }
): Promise<CheckoutPaymentReceiptUploadResult> {
  const resolvedEntidadeId = resolveRequestedEntidadeIdOrThrow(options?.entidadeId, 'Upload de comprovante')
  const params = new URLSearchParams()
  const lojaId = options?.lojaId ?? getLojaIdValue()
  if (lojaId) params.set('loja_id', String(lojaId))

  const formData = new FormData()
  formData.append('file', file, file.name)
  if (options?.source?.trim()) {
    formData.append('source', options.source.trim())
  }

  const query = params.toString()
  const response = await fetchApi<any>(
    `/loja-virtual/v1/compradores/${resolvedEntidadeId}/pedidos/${encodeURIComponent(String(Math.trunc(pedidoId)))}/comprovante${query ? `?${query}` : ''}`,
    {
      method: 'POST',
      body: formData,
    }
  )

  return {
    message: normalizeCheckoutText(response?.message ?? response?.motivo) || 'Comprovante enviado com sucesso.',
    attachmentId: normalizePositiveInteger(response?.anexo_id ?? response?.anexoId),
    size: normalizeFreteNumber(response?.tamanho ?? response?.size),
    uploadedAt: normalizeCheckoutText(response?.uploaded_at ?? response?.uploadedAt),
    fileName: normalizeCheckoutText(response?.file_name ?? response?.fileName),
    paymentReceipt: extractCheckoutPaymentReceiptSummary(response),
  }
}

export async function getPedidoPaymentReceipt(
  pedidoId: number,
  anexoId: number,
  options?: {
    entidadeId?: number
  }
): Promise<CheckoutPaymentReceiptDownloadResult> {
  const resolvedEntidadeId = resolveRequestedEntidadeIdOrThrow(options?.entidadeId, 'Download de comprovante')
  const params = new URLSearchParams()
  const lojaId = getLojaIdValue()
  if (lojaId) params.set('loja_id', String(lojaId))

  const query = params.toString()
  const response = await fetchApiResponse(
    `/loja-virtual/v1/compradores/${resolvedEntidadeId}/pedidos/${encodeURIComponent(String(Math.trunc(pedidoId)))}/comprovante/${encodeURIComponent(String(Math.trunc(anexoId)))}${query ? `?${query}` : ''}`,
    {
      headers: {
        Accept: 'application/pdf,image/*,application/octet-stream,*/*',
      },
    }
  )

  return {
    blob: await response.blob(),
    fileName: extractFileNameFromDisposition(response.headers.get('content-disposition')),
  }
}

export async function confirmarPedidoAguardandoConfirmacao(pedidoId: number): Promise<{ message: string }> {
  const entidadeId = resolveAuthenticatedEntidadeIdOrThrow('Confirmação de pedido')
  const lojaId = getLojaIdValue()
  const response = await fetchApi<any>(
    `/loja-virtual/v1/compradores/${entidadeId}/pedidos/${encodeURIComponent(String(Math.trunc(pedidoId)))}/confirmacao`,
    {
      method: 'POST',
      body: JSON.stringify({
        ...(lojaId ? { loja_id: lojaId } : {}),
      }),
    }
  )

  return {
    message: response?.message || response?.motivo || 'Pedido confirmado e enviado para separação.',
  }
}

export type PedidoAvaliacaoSectionState = {
  disponivel?: boolean
  realizada?: boolean
  nota?: number | null
}

export type PedidoAvaliacaoSummary = {
  prazo_dias?: number
  despacho_em?: string | null
  expira_em?: string | null
  pendente?: boolean
  expirada?: boolean
  compra?: PedidoAvaliacaoSectionState | null
  transportadora?: PedidoAvaliacaoSectionState | null
}

export type PedidoAvaliacaoInputSection = {
  nota: number
  comentario?: string
}

export type SubmitPedidoAvaliacaoPayload = {
  compra?: PedidoAvaliacaoInputSection
  transportadora?: PedidoAvaliacaoInputSection
}

export type SubmitPedidoAvaliacaoResult = {
  message: string
  avaliacao?: PedidoAvaliacaoSummary | null
}

export async function submitPedidoAvaliacao(
  pedidoId: number,
  payload: SubmitPedidoAvaliacaoPayload
): Promise<SubmitPedidoAvaliacaoResult> {
  const entidadeId = resolveAuthenticatedEntidadeIdOrThrow('Avaliação de pedido')
  const lojaId = getLojaIdValue()
  const response = await fetchApi<any>(
    `/loja-virtual/v1/compradores/${entidadeId}/pedidos/${encodeURIComponent(String(Math.trunc(pedidoId)))}/avaliacao`,
    {
      method: 'POST',
      body: JSON.stringify({
        ...(lojaId ? { loja_id: lojaId } : {}),
        ...payload,
      }),
    }
  )

  return {
    message: response?.message || response?.motivo || 'Avaliação publicada com sucesso.',
    avaliacao: (response?.avaliacao ?? null) as PedidoAvaliacaoSummary | null,
  }
}

type CheckoutMercadoPagoActionApiResponse = {
  status?: unknown
  motivo?: unknown
  url?: unknown
  id?: unknown
  collector_id?: unknown
  sandbox_url?: unknown
  checkout_pro?: unknown
  ambiente?: unknown
  preference_id?: unknown
  init_point?: unknown
  sandbox_init_point?: unknown
}

function normalizeCheckoutMercadoPagoActionResult(
  response: CheckoutMercadoPagoActionApiResponse
): CheckoutMercadoPagoActionResult {
  const checkoutPro = Math.trunc(normalizeFreteNumber(response?.checkout_pro) ?? 0) === 1
  const normalizedPayload = {
    status: Math.trunc(normalizeFreteNumber(response?.status) ?? 0),
    motivo: normalizeCheckoutText(response?.motivo),
    url: normalizeCheckoutText(response?.url ?? response?.init_point),
    id: normalizeCheckoutText(response?.id ?? response?.preference_id),
    collectorId: normalizeCheckoutText(response?.collector_id),
    sandboxUrl: normalizeCheckoutText(response?.sandbox_url ?? response?.sandbox_init_point),
  }

  const paymentInstructions = extractCheckoutPaymentInstructions({
    url: normalizedPayload.url,
    sandbox_url: checkoutPro ? '' : normalizedPayload.sandboxUrl,
    id: normalizedPayload.id,
    collector_id: normalizedPayload.collectorId,
    submit_label: 'Pagar com Cartão',
  })

  return {
    ...normalizedPayload,
    checkoutPro,
    paymentInstructions,
  }
}

async function runCheckoutMercadoPagoAction(
  pedidoId: number,
  action: 'gerar-link' | 'atualizar-link' | 'cancelar-link',
  options?: {
    lojaId?: number
    notificationUrl?: string | null
    backUrls?: CheckoutMercadoPagoBackUrls
  }
): Promise<CheckoutMercadoPagoActionResult> {
  const entidadeId = resolveAuthenticatedEntidadeIdOrThrow('Link do Mercado Pago')
  const lojaId = getLojaIdValue(options?.lojaId)
  const notificationUrl = normalizeCheckoutText(options?.notificationUrl)
  const backUrls = options?.backUrls
    ? {
        success: normalizeCheckoutText(options.backUrls.success),
        failure: normalizeCheckoutText(options.backUrls.failure),
        pending: normalizeCheckoutText(options.backUrls.pending),
      }
    : undefined

  const response = await fetchApi<CheckoutMercadoPagoActionApiResponse>(
    buildPedidoMercadoPagoEndpoint(entidadeId, pedidoId, action),
    {
      method: 'POST',
      body: JSON.stringify({
        ...(lojaId ? { loja_id: lojaId } : {}),
        ...(notificationUrl ? { notification_url: notificationUrl } : {}),
        ...(backUrls && (backUrls.success || backUrls.failure || backUrls.pending) ? { backUrls } : {}),
      }),
    }
  )

  return normalizeCheckoutMercadoPagoActionResult(response)
}

async function runCheckoutMercadoPagoCheckoutProPreference(
  pedidoId: number,
  options?: {
    lojaId?: number
    backUrls?: CheckoutMercadoPagoBackUrls
  }
): Promise<CheckoutMercadoPagoActionResult> {
  const entidadeId = resolveAuthenticatedEntidadeIdOrThrow('Checkout Pro Mercado Pago')
  const lojaId = getLojaIdValue(options?.lojaId)
  const backUrls = options?.backUrls
    ? {
        success: normalizeCheckoutText(options.backUrls.success),
        failure: normalizeCheckoutText(options.backUrls.failure),
        pending: normalizeCheckoutText(options.backUrls.pending),
      }
    : undefined
  const origin = typeof window !== 'undefined' ? window.location.origin : ''

  const response = await fetchApi<CheckoutMercadoPagoActionApiResponse>(
    buildPedidoMercadoPagoCheckoutProPreferenceEndpoint(entidadeId, pedidoId),
    {
      method: 'POST',
      body: JSON.stringify({
        ...(lojaId ? { loja_id: lojaId } : {}),
        ...(origin ? { origin } : {}),
        ambiente: resolveCheckoutProAmbiente(),
        ...(backUrls && (backUrls.success || backUrls.failure || backUrls.pending) ? { backUrls } : {}),
      }),
    }
  )

  return normalizeCheckoutMercadoPagoActionResult(response)
}

function shouldFallbackToLegacyMercadoPago(): boolean {
  if (typeof window === 'undefined') return true
  return !(/\b(localhost|127\.0\.0\.1|0\.0\.0\.0)\b|ngrok/i.test(window.location.host))
}

function resolveCheckoutProAmbiente(): 'test' | 'prod' {
  return 'prod'
}

export async function generatePedidoMercadoPagoLink(
  pedidoId: number,
  options?: {
    lojaId?: number
    notificationUrl?: string | null
    backUrls?: CheckoutMercadoPagoBackUrls
  }
): Promise<CheckoutMercadoPagoActionResult> {
  try {
    return await runCheckoutMercadoPagoCheckoutProPreference(pedidoId, options)
  } catch (error) {
    if (!shouldFallbackToLegacyMercadoPago()) throw error
    console.warn('Checkout Pro Mercado Pago indisponível; usando integração Mercado Pago legada.', error)
  }
  return runCheckoutMercadoPagoAction(pedidoId, 'gerar-link', options)
}

export async function updatePedidoMercadoPagoLink(
  pedidoId: number,
  options?: {
    lojaId?: number
    notificationUrl?: string | null
    backUrls?: CheckoutMercadoPagoBackUrls
  }
): Promise<CheckoutMercadoPagoActionResult> {
  try {
    return await runCheckoutMercadoPagoCheckoutProPreference(pedidoId, options)
  } catch (error) {
    if (!shouldFallbackToLegacyMercadoPago()) throw error
    console.warn('Checkout Pro Mercado Pago indisponível; atualizando integração Mercado Pago legada.', error)
  }
  return runCheckoutMercadoPagoAction(pedidoId, 'atualizar-link', options)
}

export async function cancelPedidoMercadoPagoLink(
  pedidoId: number,
  options?: {
    lojaId?: number
  }
): Promise<CheckoutMercadoPagoActionResult> {
  return runCheckoutMercadoPagoAction(pedidoId, 'cancelar-link', options)
}

export async function recomprarPedido(pedidoId: number): Promise<{ message: string }> {
  const entidadeId = resolveAuthenticatedEntidadeIdOrThrow('Recompra de pedido')
  const lojaId = getLojaIdValue()
  const response = await fetchApi<any>(
    `/loja-virtual/v1/compradores/${entidadeId}/pedidos/${encodeURIComponent(String(Math.trunc(pedidoId)))}/recomprar`,
    {
      method: 'POST',
      body: JSON.stringify({
        ...(lojaId ? { loja_id: lojaId } : {}),
      }),
    }
  )

  return {
    message: response?.message || response?.motivo || 'Itens do pedido inseridos no carrinho.',
  }
}

function normalizeQuoteFieldValue(value: unknown): string | null {
  if (typeof value === 'string') {
    const normalized = value.trim()
    return normalized || null
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value)
  }

  return null
}

function resolveQuoteField(record: Record<string, unknown>, keys: string[]): string | null {
  for (const key of keys) {
    const normalized = normalizeQuoteFieldValue(record[key])
    if (normalized) return normalized
  }

  return null
}

function resolveQuoteArchivedValue(record: Record<string, unknown>): 0 | 1 | null {
  const candidates = [
    record.isArchived,
    record.is_archived,
    record.archived,
    record.arquivado,
  ]

  for (const candidate of candidates) {
    if (typeof candidate === 'boolean') return candidate ? 1 : 0

    if (typeof candidate === 'number') {
      if (candidate === 1) return 1
      if (candidate === 0) return 0
    }

    if (typeof candidate === 'string') {
      const normalized = candidate.trim().toLocaleLowerCase('pt-BR')
      if (['1', 'true', 'sim', 'yes'].includes(normalized)) return 1
      if (['0', 'false', 'nao', 'não', 'no'].includes(normalized)) return 0
    }
  }

  return null
}

function normalizeQuoteRecord(record: Record<string, unknown>): Record<string, unknown> {
  const name = resolveQuoteField(record, ['nome', 'descricao', 'title', 'name'])
  const createdAt = resolveQuoteField(record, ['createdAt', 'data', 'created_at'])
  const expiresAt = resolveQuoteField(record, [
    'expiresAt',
    'validade',
    'expires_at',
    'data_validade',
    'validUntil',
    'valid_until',
    'vencimento',
    'data_vencimento',
    'due_date',
  ])
  const archived = resolveQuoteArchivedValue(record)
  const portalIndicadorRaw = record.portal_indicador ?? record.portalIndicador
  const portalIndicador = (
    typeof portalIndicadorRaw === 'boolean'
      ? portalIndicadorRaw
        ? 1
        : 0
      : typeof portalIndicadorRaw === 'number' && Number.isFinite(portalIndicadorRaw)
        ? portalIndicadorRaw === 1 ? 1 : 0
        : typeof portalIndicadorRaw === 'string'
          ? ['1', 'true', 'sim', 'yes'].includes(portalIndicadorRaw.trim().toLocaleLowerCase('pt-BR'))
            ? 1
            : ['0', 'false', 'nao', 'não', 'no'].includes(portalIndicadorRaw.trim().toLocaleLowerCase('pt-BR'))
              ? 0
              : null
          : null
  )
  const clienteIndicadorIdRaw = resolveQuoteField(record, ['cliente_indicador_id', 'clienteIndicadorId'])
  const clienteIndicadorId = clienteIndicadorIdRaw && /^\d+$/.test(clienteIndicadorIdRaw)
    ? Number.parseInt(clienteIndicadorIdRaw, 10)
    : null
  const clienteDestinatarioIdRaw = resolveQuoteField(record, ['cliente_destinatario_id', 'clienteDestinatarioId'])
  const clienteDestinatarioId = clienteDestinatarioIdRaw && /^\d+$/.test(clienteDestinatarioIdRaw)
    ? Number.parseInt(clienteDestinatarioIdRaw, 10)
    : null
  const indicadorClienteNome = resolveQuoteField(record, ['indicador_cliente_nome', 'indicadorClienteNome'])
  const clienteNome = resolveQuoteField(record, ['cliente_nome', 'clienteNome', 'cliente', 'indicador_cliente_nome', 'indicadorClienteNome'])
  const indicadorTitularTipo = resolveQuoteField(record, ['indicador_titular_tipo', 'indicadorTitularTipo'])
  const notaIdRaw = resolveQuoteField(record, ['nota_id', 'notaId'])
  const notaId = notaIdRaw && /^\d+$/.test(notaIdRaw)
    ? Number.parseInt(notaIdRaw, 10)
    : null
  const parentQuoteIdRaw = resolveQuoteField(record, ['orcamento_pai_id', 'orcamentoPaiId', 'parent_quote_id', 'parentQuoteId'])
  const parentQuoteId = parentQuoteIdRaw && /^\d+$/.test(parentQuoteIdRaw)
    ? Number.parseInt(parentQuoteIdRaw, 10)
    : null
  const childrenCountRaw = resolveQuoteField(record, ['filhos', 'children_count', 'childrenCount'])
  const childrenCount = childrenCountRaw && /^\d+$/.test(childrenCountRaw)
    ? Number.parseInt(childrenCountRaw, 10)
    : null
  const formaPagamentoProposta = resolveQuoteField(record, [
    'forma_pagamento_proposta',
    'formaPagamentoProposta',
    'pdf_forma_pagamento',
    'pdfFormaPagamento',
  ])
  const prazoEntregaProposta = resolveQuoteField(record, [
    'prazo_entrega_proposta',
    'prazoEntregaProposta',
    'pdf_prazo_entrega',
    'pdfPrazoEntrega',
  ])
  const validadeCotacaoProposta = resolveQuoteField(record, [
    'validade_cotacao_proposta',
    'validadeCotacaoProposta',
    'pdf_validade_cotacao',
    'pdfValidadeCotacao',
  ])

  return {
    ...record,
    ...(name ? { name, quoteName: name } : {}),
    ...(createdAt ? { createdAt } : {}),
    ...(expiresAt ? { expiresAt } : {}),
    ...(archived !== null ? { archived: archived === 1, isArchived: archived === 1, arquivado: archived } : {}),
    ...(portalIndicador !== null ? { portal_indicador: portalIndicador, portalIndicador: portalIndicador === 1 } : {}),
    ...(clienteIndicadorId !== null ? { cliente_indicador_id: clienteIndicadorId, clienteIndicadorId } : {}),
    ...(clienteDestinatarioId !== null ? {
      cliente_destinatario_id: clienteDestinatarioId,
      clienteDestinatarioId,
    } : {}),
    ...(indicadorClienteNome ? { indicador_cliente_nome: indicadorClienteNome, indicadorClienteNome } : {}),
    ...(clienteNome ? { cliente_nome: clienteNome, clienteNome } : {}),
    ...(indicadorTitularTipo ? { indicador_titular_tipo: indicadorTitularTipo, indicadorTitularTipo } : {}),
    ...(notaId !== null ? { nota_id: notaId, notaId } : {}),
    ...(parentQuoteId !== null ? { orcamento_pai_id: parentQuoteId, orcamentoPaiId: parentQuoteId } : {}),
    ...(childrenCount !== null ? { filhos: childrenCount, childrenCount } : {}),
    ...(formaPagamentoProposta ? {
      forma_pagamento_proposta: formaPagamentoProposta,
      formaPagamentoProposta,
    } : {}),
    ...(prazoEntregaProposta ? {
      prazo_entrega_proposta: prazoEntregaProposta,
      prazoEntregaProposta,
    } : {}),
    ...(validadeCotacaoProposta ? {
      validade_cotacao_proposta: validadeCotacaoProposta,
      validadeCotacaoProposta,
    } : {}),
    ...(notaId !== null ? { status: 'converted', status_label: 'Convertido', status_descricao: 'Convertido' } : {}),
  }
}

const QUOTE_IDENTIFIER_KEYS = [
  'codigo',
  'numero',
  'orcamento_id',
  'orcamentoId',
  'id',
  'nota_id',
  'notaId',
  'hash',
  'id_hash',
  'uuid',
  'identifier',
] as const

const normalizeQuoteIdentifier = (value: unknown) => {
  if (value === null || value === undefined) return ''
  return String(value).trim()
}

const resolveQuoteIdentifiers = (record: Record<string, unknown>) => {
  const identifiers: string[] = []

  for (const key of QUOTE_IDENTIFIER_KEYS) {
    const normalized = normalizeQuoteIdentifier(record[key])
    if (!normalized || identifiers.includes(normalized)) continue
    identifiers.push(normalized)
  }

  return identifiers
}

const quoteMatchesIdentifier = (record: Record<string, unknown>, identifier: string) => {
  const normalizedIdentifier = normalizeQuoteIdentifier(identifier)
  if (!normalizedIdentifier) return false

  return resolveQuoteIdentifiers(record).some((candidate) => candidate === normalizedIdentifier)
}

const resolveQuoteDetailLookupIds = (record: Record<string, unknown>, fallbackId: number | string) => {
  const candidates = [
    record.codigo,
    record.numero,
    record.orcamento_id,
    record.orcamentoId,
    record.nota_id,
    record.notaId,
    record.id,
    fallbackId,
  ]
  const ids: string[] = []

  for (const candidate of candidates) {
    const normalized = normalizeQuoteIdentifier(candidate)
    if (!normalized || ids.includes(normalized)) continue
    ids.push(normalized)
  }

  return ids
}

const resolveQuoteRequestIdentifiers = (identifier: number | string) => {
  const rawIdentifier = normalizeQuoteIdentifier(identifier)
  const candidates: string[] = []

  const addCandidate = (value: unknown) => {
    const normalized = normalizeQuoteIdentifier(value)
    if (!normalized || candidates.includes(normalized)) return
    candidates.push(normalized)
  }

  addCandidate(rawIdentifier)

  const leadingNumericMatch = rawIdentifier.match(/^\d+/)
  addCandidate(leadingNumericMatch?.[0])

  rawIdentifier.match(/\d+/g)?.forEach(addCandidate)

  return candidates
}

const isRecoverableQuoteDetailLookupError = (error: unknown) => {
  if (!isApiRequestError(error)) return false
  if (error.status === 404) return true
  if (error.status !== 400) return false

  const detail = error.detail.trim().toLocaleLowerCase('pt-BR')
  return detail.includes('orcamento_id inválido')
    || detail.includes('orcamento_id invalido')
    || detail.includes('invalid')
}

function parseQuoteExpirationTimestamp(record: Record<string, unknown>): number | null {
  const expiresAt = resolveQuoteField(record, [
    'expiresAt',
    'validade',
    'expires_at',
    'data_validade',
    'validUntil',
    'valid_until',
    'vencimento',
    'data_vencimento',
    'due_date',
  ])

  if (!expiresAt) return null

  const parsedDate = Date.parse(expiresAt)
  return Number.isFinite(parsedDate) ? parsedDate : null
}

export async function getOrcamentos(
  page: number = 1,
  perPage: number = 20,
  status?: string,
  search?: string,
  days?: number,
  options?: { includeArchived?: boolean; clientFilterMaxItems?: number; scope?: 'all' | 'clients' }
): Promise<PaginatedListResult<any>> {
  const entidadeId = resolveAuthenticatedEntidadeIdOrThrow('Listagem de orçamentos')
  const normalizedPage = Math.max(1, Math.trunc(page))
  const normalizedPerPage = Math.max(1, Math.trunc(perPage))
  const statusTerm = (status || '').trim().toLocaleLowerCase('pt-BR')
  const searchTerm = (search || '').trim().toLocaleLowerCase('pt-BR')
  const now = Date.now()
  const maxAgeMs = typeof days === 'number' && Number.isFinite(days) && days > 0
    ? days * 24 * 60 * 60 * 1000
    : null
  const hasClientSideFilters = (statusTerm && statusTerm !== 'all') || Boolean(searchTerm) || maxAgeMs !== null

  const buildQuery = (requestPage: number, requestPerPage: number) => {
    const params = new URLSearchParams()
    const lojaId = getLojaIdValue()
    if (lojaId) params.set('loja_id', String(lojaId))
    if (options?.includeArchived) {
      params.set('incluir_arquivados', '1')
    } else {
      params.set('arquivado', '0')
    }
    if (options?.scope === 'clients') params.set('escopo', 'clientes')
    params.set('page', String(Math.max(1, Math.trunc(requestPage))))
    params.set('per_page', String(Math.max(1, Math.trunc(requestPerPage))))
    return params.toString()
  }

  const fetchQuotesPage = async (requestPage: number, requestPerPage: number) => {
    const response = await fetchApi<unknown>(
      `/loja-virtual/v1/compradores/${entidadeId}/orcamentos?${buildQuery(requestPage, requestPerPage)}`
    )

    const items = extractListFromResponse(response, ['orcamentos', 'items']).map((item) => (
      isPlainRecord(item) ? normalizeQuoteRecord(item) : item
    ))

    return { response, items }
  }

  const filterQuotes = (items: any[]) => items.filter((item) => {
    if (!item || typeof item !== 'object') return false
    const record = item as Record<string, unknown>
    const statusValue = String(
      record.status
      ?? record.situacao
      ?? record.status_descricao
      ?? ''
    ).toLocaleLowerCase('pt-BR')
    const isArchived = resolveQuoteArchivedValue(record) === 1
    const expirationTimestamp = parseQuoteExpirationTimestamp(record)
    const isExpired = expirationTimestamp !== null && expirationTimestamp < now

    if (!options?.includeArchived && isArchived) {
      return false
    }

    if (statusTerm && statusTerm !== 'all') {
      if (statusTerm === 'valid') {
        if (isExpired) return false
      } else if (statusTerm === 'expired') {
        if (!isExpired) return false
      } else if (!statusValue.includes(statusTerm)) {
        return false
      }
    }

    if (searchTerm) {
      const searchable = JSON.stringify(record).toLocaleLowerCase('pt-BR')
      if (!searchable.includes(searchTerm)) return false
    }

    if (maxAgeMs !== null) {
      const dateRaw = String(
        record.data
        ?? record.created_at
        ?? record.createdAt
        ?? ''
      ).trim()
      if (dateRaw) {
        const parsedDate = Date.parse(dateRaw)
        if (Number.isFinite(parsedDate) && (now - parsedDate) > maxAgeMs) {
          return false
        }
      }
    }

    return true
  })

  if (!hasClientSideFilters) {
    const { response, items } = await fetchQuotesPage(normalizedPage, normalizedPerPage)
    return buildPaginatedListResult(items, response, normalizedPage, normalizedPerPage)
  }

  const requestPerPage = 100
  const filteredItems: any[] = []
  const clientFilterMaxItems = typeof options?.clientFilterMaxItems === 'number'
    && Number.isFinite(options.clientFilterMaxItems)
    && options.clientFilterMaxItems > 0
    ? Math.max(normalizedPage * normalizedPerPage, Math.trunc(options.clientFilterMaxItems))
    : null

  for (let currentPage = 1; currentPage <= 100; currentPage += 1) {
    const { response, items } = await fetchQuotesPage(currentPage, requestPerPage)
    filteredItems.push(...filterQuotes(items))

    const pagination = buildPaginatedListResult(items, response, currentPage, requestPerPage)
    if (
      currentPage >= pagination.totalPages
      || items.length < requestPerPage
      || (clientFilterMaxItems !== null && filteredItems.length >= clientFilterMaxItems)
    ) {
      break
    }
  }

  return buildLocalPaginatedListResult(filteredItems, normalizedPage, normalizedPerPage)
}

type QuoteArchiveMutationResult = {
  message: string
  archived: boolean
  quote: Record<string, unknown> | null
}

export type SavedQuoteClientContact = {
  id?: number
  name: string
  email: string
  phone: string
  role: string
}

export type SavedQuoteClientQuote = {
  id: number
  name: string
  date: string | null
}

export type SavedQuoteClientOrder = {
  id: number
  number: number | null
  date: string | null
}

export type SavedQuotePdfClient = {
  id: number
  sourceCode: string
  name: string
  contactName: string
  document: string
  registration: string
  email: string
  phone: string
  address: string
  updatedAt: string | null
  defaultCommissionPercent: number
  indicatorRelationship: boolean
  delegatedPurchaseAuthorized: boolean
  delegatedPurchaseAuthorizedAt: string | null
  releaseContact?: QuoteClientReleaseContact | null
  contacts: SavedQuoteClientContact[]
  quotes: SavedQuoteClientQuote[]
  orders: SavedQuoteClientOrder[]
}

export type PendingQuoteClientLink = SavedQuotePdfClient & {
  requestId: number
  clientId: number | null
  requestedAt: string | null
}

export type QuoteClientReleaseContact = {
  name: string
  whatsapp: string
}

export type QuoteClientDirectory = {
  clients: SavedQuotePdfClient[]
  pending: PendingQuoteClientLink[]
  releaseContact: QuoteClientReleaseContact | null
}

export type PublicCnpjStateRegistration = {
  registration: string
  active: boolean
  state: string
}

export type PublicCnpjCompanyLookup = {
  name: string
  legalName: string
  tradeName: string
  registration: string
  address: string
  phone: string
  zipCode: string
  street: string
  number: string
  complement: string
  district: string
  city: string
  state: string
  registrations: PublicCnpjStateRegistration[]
  simpleTax: boolean
}

const PUBLIC_CNPJ_LOOKUP_TIMEOUT_MS = 5_000
const publicCnpjLookupCache = new Map<string, PublicCnpjCompanyLookup | null>()

type PublicCnpjEstablishmentPayload = {
    nome_fantasia?: unknown
    ddd1?: unknown
    telefone1?: unknown
    cep?: unknown
    tipo_logradouro?: unknown
    logradouro?: unknown
    numero?: unknown
    complemento?: unknown
    bairro?: unknown
    inscricoes_estaduais?: Array<{
      inscricao_estadual?: unknown
      ativo?: unknown
      estado?: { sigla?: unknown }
    }> | unknown
    cidade?: {
      nome?: unknown
    } | unknown
    estado?: {
      sigla?: unknown
    } | unknown
}

type PublicCnpjApiPayload = {
  razao_social?: unknown
  simples?: unknown
  estabelecimento?: unknown
}

const formatPublicCnpjCep = (value: string) => {
  const digits = normalizeDigits(value).slice(0, 8)
  if (digits.length !== 8) return ''
  return digits.replace(/^(\d{5})(\d{3})$/, '$1-$2')
}

const selectPublicCnpjRegistration = (
  registrations: PublicCnpjStateRegistration[],
  establishmentState: string
) => {
  const state = establishmentState.trim().toUpperCase()
  return registrations.find((item) => item.active && item.state === state)?.registration
    ?? registrations.find((item) => item.active)?.registration
    ?? registrations.find((item) => item.state === state)?.registration
    ?? registrations[0]?.registration
    ?? ''
}

const buildPublicCnpjAddress = (
  street: string,
  number: string,
  complement: string,
  district: string,
  city: string,
  state: string,
  zipCode: string
) => {
  const location = [city, state].filter(Boolean).join(' / ')
  return [
    street,
    number,
    complement,
    district,
    location,
    zipCode ? `CEP ${zipCode}` : '',
  ]
    .filter(Boolean)
    .join(' - ')
}

const normalizeSavedQuoteClientContacts = (value: unknown): SavedQuoteClientContact[] => (
  Array.isArray(value)
    ? value
      .filter((item): item is Record<string, unknown> => isPlainRecord(item))
      .map((item) => {
        const id = Number(item.id ?? item.contato_id ?? item.contatoId ?? 0) || 0
        return {
          ...(id > 0 ? { id } : {}),
          name: normalizeCheckoutText(item.nome ?? item.name) || '',
          email: normalizeCheckoutText(item.email) || '',
          phone: normalizeCheckoutText(item.telefone ?? item.phone) || '',
          role: normalizeCheckoutText(item.cargo ?? item.role) || '',
        }
      })
      .filter((contact) => Boolean(contact.name || contact.email || contact.phone || contact.role))
    : []
)

const normalizeSavedQuoteClientQuotes = (value: unknown): SavedQuoteClientQuote[] => (
  Array.isArray(value)
    ? value
      .filter((item): item is Record<string, unknown> => isPlainRecord(item))
      .map((item) => {
        const id = Number(item.id ?? item.orcamento_id ?? item.orcamentoId ?? 0) || 0
        return {
          id,
          name: normalizeCheckoutText(item.nome ?? item.name) || `Orçamento #${id}`,
          date: normalizeCheckoutText(item.data ?? item.date) || null,
        }
      })
      .filter((quote) => quote.id > 0)
    : []
)

const normalizeSavedQuoteClientOrders = (value: unknown): SavedQuoteClientOrder[] => (
  Array.isArray(value)
    ? value
      .filter((item): item is Record<string, unknown> => isPlainRecord(item))
      .map((item) => {
        const id = Number(item.id ?? item.pedido_id ?? item.pedidoId ?? 0) || 0
        const number = Number(item.numero ?? item.numero_pedido ?? item.number ?? 0) || 0
        return {
          id,
          number: number > 0 ? number : null,
          date: normalizeCheckoutText(item.data ?? item.date) || null,
        }
      })
      .filter((order) => order.id > 0)
    : []
)

const normalizeSavedQuotePdfClient = (value: Record<string, unknown>): SavedQuotePdfClient => ({
  id: Number(value.id ?? value.cadastro_id ?? value.cadastroId ?? 0) || 0,
  sourceCode: normalizeCheckoutText(value.origem_codigo ?? value.source_code ?? value.sourceCode) || '',
  name: normalizeCheckoutText(value.nome ?? value.name) || '',
  contactName: normalizeCheckoutText(
    value.contato_nome
    ?? value.contatoName
    ?? value.comprador_nome
    ?? value.compradorName
    ?? value.buyer_contact_name
    ?? value.buyerContactName
  ) || '',
  document: normalizeCheckoutText(value.cpf_cnpj ?? value.documento ?? value.document) || '',
  registration: normalizeCheckoutText(value.ie ?? value.inscricao_estadual ?? value.registration) || '',
  email: normalizeCheckoutText(value.email ?? value.email_principal ?? value.emailPrincipal) || '',
  phone: normalizeCheckoutText(value.telefone ?? value.phone) || '',
  address: normalizeCheckoutText(value.endereco ?? value.address) || '',
  updatedAt: normalizeCheckoutText(value.updated_at ?? value.updatedAt ?? value.sinc_u) || null,
  defaultCommissionPercent: normalizeIndicatorNumber(
    value.comissao_percentual_padrao ?? value.default_commission_percent ?? value.defaultCommissionPercent
  ),
  indicatorRelationship: parseBooleanLike(
    value.indicador_relacionamento ?? value.indicator_relationship ?? value.indicatorRelationship
  ),
  delegatedPurchaseAuthorized: parseBooleanLike(
    value.compra_site_autorizada ?? value.delegated_purchase_authorized ?? value.delegatedPurchaseAuthorized
  ),
  delegatedPurchaseAuthorizedAt: normalizeCheckoutText(
    value.compra_site_autorizada_em
    ?? value.delegated_purchase_authorized_at
    ?? value.delegatedPurchaseAuthorizedAt
  ) || null,
  releaseContact: normalizeQuoteClientReleaseContact(
    value.contato_liberacao ?? value.release_contact ?? value.releaseContact
  ),
  contacts: normalizeSavedQuoteClientContacts(value.contatos ?? value.contacts),
  quotes: normalizeSavedQuoteClientQuotes(value.orcamentos ?? value.quotes),
  orders: normalizeSavedQuoteClientOrders(value.pedidos ?? value.orders),
})

const normalizePendingQuoteClientLink = (value: Record<string, unknown>): PendingQuoteClientLink => {
  const requested = isPlainRecord(value.requested) ? value.requested : {}

  return {
    id: Number(value.cliente_entidade_id ?? value.clienteEntidadeId ?? 0) || 0,
    sourceCode: '',
    requestId: Number(value.id ?? value.solicitacao_id ?? value.solicitacaoId ?? 0) || 0,
    clientId: Number(value.cliente_entidade_id ?? value.clienteEntidadeId ?? 0) || null,
    name: normalizeCheckoutText(requested.nome ?? requested.name) || 'Cliente sem nome',
    contactName: normalizeCheckoutText(requested.contato_nome ?? requested.contactName) || '',
    document: normalizeCheckoutText(
      requested.cpf_cnpj
      ?? requested.documento
      ?? requested.document
      ?? value.documento_digits
    ) || '',
    registration: normalizeCheckoutText(requested.ie ?? requested.registration) || '',
    email: normalizeCheckoutText(requested.email ?? value.email) || '',
    phone: normalizeCheckoutText(requested.telefone ?? requested.phone) || '',
    address: normalizeCheckoutText(requested.endereco ?? requested.address) || '',
    updatedAt: normalizeCheckoutText(value.sinc_u ?? value.updated_at ?? value.updatedAt) || null,
    defaultCommissionPercent: normalizeIndicatorNumber(
      requested.comissao_percentual_padrao
      ?? requested.default_commission_percent
      ?? requested.defaultCommissionPercent
    ),
    indicatorRelationship: false,
    delegatedPurchaseAuthorized: false,
    delegatedPurchaseAuthorizedAt: null,
    contacts: normalizeSavedQuoteClientContacts(requested.contatos ?? requested.contacts),
    quotes: [],
    orders: [],
    requestedAt: normalizeCheckoutText(value.sinc_a ?? value.requested_at ?? value.requestedAt) || null,
  }
}

const normalizeQuoteClientReleaseContact = (value: unknown): QuoteClientReleaseContact | null => {
  if (!isPlainRecord(value)) return null
  const name = normalizeCheckoutText(value.nome ?? value.name)
  const whatsapp = normalizeCheckoutText(value.whatsapp ?? value.telefone ?? value.phone)
  return name || whatsapp ? { name, whatsapp } : null
}

export async function lookupPublicCnpjCompany(cnpj: string): Promise<PublicCnpjCompanyLookup | null> {
  const cnpjDigits = normalizeDigits(cnpj).slice(0, 14)
  if (cnpjDigits.length !== 14) {
    throw new Error('Informe um CNPJ válido.')
  }

  if (publicCnpjLookupCache.has(cnpjDigits)) {
    return publicCnpjLookupCache.get(cnpjDigits) ?? null
  }

  const controller = new AbortController()
  const timeoutId = setTimeout(() => {
    controller.abort()
  }, PUBLIC_CNPJ_LOOKUP_TIMEOUT_MS)

  let response: Response
  try {
    response = await fetch(`https://publica.cnpj.ws/cnpj/${cnpjDigits}`, {
      signal: controller.signal,
    })
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new Error('A consulta do CNPJ demorou mais de 5 segundos. Continue com o preenchimento manual.')
    }
    throw error
  } finally {
    clearTimeout(timeoutId)
  }

  if (!response.ok) {
    if (response.status === 404) {
      publicCnpjLookupCache.set(cnpjDigits, null)
      return null
    }
    throw new Error('Não foi possível consultar os dados públicos do CNPJ.')
  }

  const payload = await response.json() as PublicCnpjApiPayload | null
  if (!payload || typeof payload !== 'object') {
    return null
  }

  const estabelecimento = payload.estabelecimento && typeof payload.estabelecimento === 'object'
    ? payload.estabelecimento as PublicCnpjEstablishmentPayload
    : null

  const inscricoesEstaduais = Array.isArray(estabelecimento?.inscricoes_estaduais)
    ? estabelecimento.inscricoes_estaduais
    : []
  const registrations = inscricoesEstaduais
    .filter((item) => item && typeof item === 'object')
    .map((item) => ({
      registration: normalizeCheckoutText(item.inscricao_estadual),
      active: item.ativo === true || String(item.ativo ?? '').toLowerCase() === 'true',
      state: normalizeCheckoutText(item.estado?.sigla).toUpperCase(),
    }))
    .filter((item) => Boolean(item.registration))

  const legalName = normalizeCheckoutText(payload.razao_social)
  const tradeName = normalizeCheckoutText(
    estabelecimento && typeof estabelecimento.nome_fantasia !== 'undefined'
      ? estabelecimento.nome_fantasia
      : ''
  )
  const companyName = tradeName || legalName

  const streetPrefix = normalizeCheckoutText(estabelecimento?.tipo_logradouro)
  const streetName = normalizeCheckoutText(estabelecimento?.logradouro)
  const street = [streetPrefix, streetName].filter(Boolean).join(' ').trim()
  const number = normalizeCheckoutText(estabelecimento?.numero)
  const complement = normalizeCheckoutText(estabelecimento?.complemento)
  const district = normalizeCheckoutText(estabelecimento?.bairro)
  const city = normalizeCheckoutText(
    estabelecimento?.cidade && typeof estabelecimento.cidade === 'object'
      ? (estabelecimento.cidade as { nome?: unknown }).nome
      : ''
  )
  const state = normalizeCheckoutText(
    estabelecimento?.estado && typeof estabelecimento.estado === 'object'
      ? (estabelecimento.estado as { sigla?: unknown }).sigla
      : ''
  ).toUpperCase()
  const zipCode = formatPublicCnpjCep(normalizeCheckoutText(estabelecimento?.cep))
  const phone = normalizePhoneToStoredDigits(
    `${normalizeCheckoutText(estabelecimento?.ddd1)}${normalizeCheckoutText(estabelecimento?.telefone1)}`
  )

  const result = {
    name: companyName,
    legalName,
    tradeName,
    registration: selectPublicCnpjRegistration(registrations, state),
    address: buildPublicCnpjAddress(street, number, complement, district, city, state, zipCode),
    phone,
    zipCode,
    street,
    number,
    complement,
    district,
    city,
    state,
    registrations,
    simpleTax: payload.simples && typeof payload.simples === 'object'
      ? normalizeCheckoutText((payload.simples as { simples?: unknown }).simples).toLowerCase() === 'sim'
      : false,
  }

  publicCnpjLookupCache.set(cnpjDigits, result)
  return result
}

async function mutateQuoteArchiveState(
  orcamentoId: number | string,
  action: 'arquivar' | 'desarquivar',
  fallbackMessage: string
): Promise<QuoteArchiveMutationResult> {
  const entidadeId = resolveAuthenticatedEntidadeIdOrThrow(
    action === 'arquivar' ? 'Arquivamento de orçamento' : 'Desarquivamento de orçamento'
  )
  const normalizedQuoteId = String(orcamentoId ?? '').trim()
  if (!normalizedQuoteId) {
    throw new Error('Identificador do orçamento inválido.')
  }

  const params = new URLSearchParams()
  const lojaId = getLojaIdValue()
  if (lojaId) params.set('loja_id', String(lojaId))

  const query = params.toString()
  const response = await fetchApi<any>(
    `/loja-virtual/v1/compradores/${entidadeId}/orcamentos/${encodeURIComponent(normalizedQuoteId)}/${action}${query ? `?${query}` : ''}`,
    {
      method: 'POST',
    }
  )

  const requestSucceeded = response?.status === undefined
    ? true
    : parseBooleanLike(response.status)

  if (!requestSucceeded) {
    throw new Error(response?.motivo || fallbackMessage)
  }

  const normalizedQuote = isPlainRecord(response)
    ? normalizeQuoteRecord(response as Record<string, unknown>)
    : null
  const archivedValue = resolveQuoteArchivedValue(
    normalizedQuote ?? (isPlainRecord(response) ? response as Record<string, unknown> : {})
  )
  const archived = archivedValue === null
    ? action === 'arquivar'
    : archivedValue === 1

  return {
    message: response?.message || response?.motivo || fallbackMessage,
    archived,
    quote: normalizedQuote,
  }
}

async function fetchOrcamentoById(id: number | string): Promise<any> {
  const entidadeId = resolveAuthenticatedEntidadeIdOrThrow('Detalhe de orçamento')
  const params = new URLSearchParams()
  const lojaId = getLojaIdValue()
  if (lojaId) params.set('loja_id', String(lojaId))

  const query = params.toString()
  const lookupIdentifiers = resolveQuoteRequestIdentifiers(id)
  const directLookupIdentifiers = lookupIdentifiers.filter((identifier) => /^\d+$/.test(identifier))
  const normalizedId = directLookupIdentifiers[0] ?? lookupIdentifiers[0]
  if (!normalizedId) {
    throw new Error('Detalhe de orçamento requer identificador válido.')
  }

  const fetchQuoteDetail = async (lookupId: string | number) => {
    const response = await fetchApi<any>(
      `/loja-virtual/v1/compradores/${entidadeId}/orcamentos/${encodeURIComponent(String(lookupId).trim())}${query ? `?${query}` : ''}`
    )

    if (!response || typeof response !== 'object') {
      return response ?? null
    }

    const record = response as Record<string, unknown>

    const nestedCandidates = [
      record.orcamento,
      record.quote,
      record.data,
      record.item,
    ]

    for (const candidate of nestedCandidates) {
      if (candidate && typeof candidate === 'object' && !Array.isArray(candidate)) {
        return normalizeQuoteRecord(candidate as Record<string, unknown>)
      }
    }

    return normalizeQuoteRecord(record)
  }

  let firstNotFoundError: unknown = null
  try {
    for (const lookupId of directLookupIdentifiers.length > 0 ? directLookupIdentifiers : lookupIdentifiers) {
      try {
        return await fetchQuoteDetail(lookupId)
      } catch (error) {
        if (!isRecoverableQuoteDetailLookupError(error)) {
          throw error
        }
        firstNotFoundError ??= error
      }
    }
  } catch (error) {
    if (!isRecoverableQuoteDetailLookupError(error)) {
      throw error
    }
    firstNotFoundError ??= error
  }

  for (let page = 1; page <= 5; page += 1) {
    const quotesPage = await getOrcamentos(page, 100, undefined, undefined, undefined, {
      includeArchived: true,
    })
    const items = Array.isArray(quotesPage.items) ? quotesPage.items : []
    const matchedQuote = items.find((item) => (
      item && typeof item === 'object'
        ? lookupIdentifiers.some((identifier) => quoteMatchesIdentifier(item as Record<string, unknown>, identifier))
        : false
    )) as Record<string, unknown> | undefined

    if (matchedQuote) {
      const lookupIds = resolveQuoteDetailLookupIds(matchedQuote, normalizedId)
      for (const lookupId of lookupIds) {
        try {
          return await fetchQuoteDetail(lookupId)
        } catch (lookupError) {
          if (!isRecoverableQuoteDetailLookupError(lookupError)) {
            throw lookupError
          }
        }
      }

      return normalizeQuoteRecord(matchedQuote)
    }

    if (items.length < 100) break
  }

  throw firstNotFoundError ?? new Error('Orçamento não encontrado.')
}

export type ActiveQuoteFiscalPreparation = {
  quoteId: number
  subtotal: number
  ipi: number
  difal: number
  total: number
}

const QUOTE_DETAIL_PREFETCH_TTL_MS = 15_000
const QUOTE_DETAIL_PREVIEW_TTL_MS = 60_000
const QUOTE_DETAIL_PREVIEW_MAX_ENTRIES = 50
const quoteDetailRequests = new Map<string, {
  promise: Promise<any>
  expiresAt: number
}>()
const quoteDetailPreviews = new Map<string, {
  data: any
  expiresAt: number
}>()
const activeQuotesFiscalPreparationRequests = new Map<string, Promise<ActiveQuoteFiscalPreparation[]>>()

const resolveActiveQuotesFiscalPreparationKey = () => {
  const entidadeId = resolveAuthenticatedEntidadeIdOrThrow('Preparação fiscal dos orçamentos')
  return `${entidadeId}:${getLojaIdValue() ?? ''}`
}

const resolveQuoteDetailRequestKey = (id: number | string) => {
  const entidadeId = resolveAuthenticatedEntidadeIdOrThrow('Detalhe de orçamento')
  const identifiers = resolveQuoteRequestIdentifiers(id)
  const canonicalId = identifiers.find((identifier) => /^\d+$/.test(identifier)) ?? identifiers[0]
  if (!canonicalId) {
    throw new Error('Detalhe de orçamento requer identificador válido.')
  }
  return `${entidadeId}:${getLojaIdValue() ?? ''}:${canonicalId}`
}

const cacheOrcamentoByIdPreview = (id: number | string, data: unknown) => {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return

  const key = resolveQuoteDetailRequestKey(id)
  const normalizedData = normalizeQuoteRecord(data as Record<string, unknown>)
  const existing = quoteDetailPreviews.get(key)
  const existingIsComplete = existing
    && existing.expiresAt > Date.now()
    && (Array.isArray(existing.data?.itens) || Array.isArray(existing.data?.items))
  const incomingIsComplete = Array.isArray(normalizedData?.itens) || Array.isArray(normalizedData?.items)

  // A prévia da listagem nunca deve substituir um detalhe completo ainda válido.
  if (existingIsComplete && !incomingIsComplete) return

  quoteDetailPreviews.delete(key)
  quoteDetailPreviews.set(key, {
    data: normalizedData,
    expiresAt: Date.now() + QUOTE_DETAIL_PREVIEW_TTL_MS,
  })

  while (quoteDetailPreviews.size > QUOTE_DETAIL_PREVIEW_MAX_ENTRIES) {
    const oldestKey = quoteDetailPreviews.keys().next().value
    if (!oldestKey) break
    quoteDetailPreviews.delete(oldestKey)
  }
}

export function seedOrcamentoByIdPreview(id: number | string, data: unknown): void {
  try {
    cacheOrcamentoByIdPreview(id, data)
  } catch {
    // A prévia é apenas uma otimização; a busca oficial continua como fallback.
  }
}

export function getOrcamentoByIdPreview(id: number | string): any | null {
  try {
    const key = resolveQuoteDetailRequestKey(id)
    const preview = quoteDetailPreviews.get(key)
    if (!preview) return null
    if (preview.expiresAt <= Date.now()) {
      quoteDetailPreviews.delete(key)
      return null
    }
    return preview.data
  } catch {
    return null
  }
}

const requestOrcamentoById = (id: number | string) => {
  const key = resolveQuoteDetailRequestKey(id)
  const now = Date.now()
  const existing = quoteDetailRequests.get(key)
  if (existing && existing.expiresAt > now) {
    return { key, promise: existing.promise }
  }

  const pendingFiscalPreparation = activeQuotesFiscalPreparationRequests.get(
    resolveActiveQuotesFiscalPreparationKey()
  )
  const promise = (pendingFiscalPreparation
    ? pendingFiscalPreparation.catch(() => [])
    : Promise.resolve([])
  ).then(() => fetchOrcamentoById(id)).then((data) => {
    cacheOrcamentoByIdPreview(id, data)
    return data
  })
  quoteDetailRequests.set(key, {
    promise,
    expiresAt: now + QUOTE_DETAIL_PREFETCH_TTL_MS,
  })
  void promise.catch(() => {
    if (quoteDetailRequests.get(key)?.promise === promise) {
      quoteDetailRequests.delete(key)
    }
  })
  return { key, promise }
}

export function prefetchOrcamentoById(id: number | string): Promise<any> {
  return requestOrcamentoById(id).promise
}

export async function getOrcamentoById(id: number | string): Promise<any> {
  const { key, promise } = requestOrcamentoById(id)
  try {
    return await promise
  } finally {
    if (quoteDetailRequests.get(key)?.promise === promise) {
      quoteDetailRequests.delete(key)
    }
  }
}

export async function salvarOrcamento(data: any): Promise<{
  message: string
  name: string | null
  createdAt: string | null
  expiresAt: string | null
  quote: Record<string, unknown> | null
}> {
  const entidadeId = resolveAuthenticatedEntidadeIdOrThrow('Criação de orçamento')
  const params = new URLSearchParams()
  const lojaId = getLojaIdValue()
  if (lojaId) params.set('loja_id', String(lojaId))

  const query = params.toString()
  const response = await fetchApi<any>(`/loja-virtual/v1/compradores/${entidadeId}/orcamentos${query ? `?${query}` : ''}`, {
    method: 'POST',
    body: JSON.stringify({
      ...data,
      ...(lojaId ? { loja_id: lojaId } : {}),
    }),
  })

  const normalizedQuote = isPlainRecord(response) ? normalizeQuoteRecord(response) : null

  return {
    message: resolveStorefrontSuccessMessage(
      response?.message ?? response?.motivo,
      'Orçamento criado com sucesso.'
    ),
    name: typeof normalizedQuote?.name === 'string' ? normalizedQuote.name : null,
    createdAt: typeof normalizedQuote?.createdAt === 'string' ? normalizedQuote.createdAt : null,
    expiresAt: typeof normalizedQuote?.expiresAt === 'string' ? normalizedQuote.expiresAt : null,
    quote: normalizedQuote,
  }
}

export function prepararOrcamentosAtivos(): Promise<ActiveQuoteFiscalPreparation[]> {
  const entidadeId = resolveAuthenticatedEntidadeIdOrThrow('Preparação fiscal dos orçamentos')
  const params = new URLSearchParams()
  const lojaId = getLojaIdValue()
  const preparationKey = `${entidadeId}:${lojaId ?? ''}`
  const existingRequest = activeQuotesFiscalPreparationRequests.get(preparationKey)
  if (existingRequest) return existingRequest

  if (lojaId) params.set('loja_id', String(lojaId))
  const query = params.toString()

  const request = fetchApi<any>(
    `/loja-virtual/v1/compradores/${entidadeId}/orcamentos/preparar-ativos${query ? `?${query}` : ''}`,
    { method: 'POST' }
  ).then((response) => {
    const rawItems = Array.isArray(response?.orcamentos_atualizados)
      ? response.orcamentos_atualizados
      : []

    return rawItems.flatMap((rawItem: unknown): ActiveQuoteFiscalPreparation[] => {
      if (!isPlainRecord(rawItem)) return []
      const quoteId = Math.max(0, Math.trunc(Number(rawItem.orcamento_id ?? rawItem.quoteId) || 0))
      if (!quoteId) return []
      return [{
        quoteId,
        subtotal: Math.max(0, Number(rawItem.subtotal) || 0),
        ipi: Math.max(0, Number(rawItem.ipi) || 0),
        difal: Math.max(0, Number(rawItem.difal ?? rawItem.st) || 0),
        total: Math.max(0, Number(rawItem.total) || 0),
      }]
    })
  }).then((preparedQuotes) => {
    for (const preparedQuote of preparedQuotes) {
      try {
        const key = resolveQuoteDetailRequestKey(preparedQuote.quoteId)
        quoteDetailRequests.delete(key)
        quoteDetailPreviews.delete(key)
      } catch {
        // A atualização fiscal permanece válida mesmo sem uma prévia local para invalidar.
      }
    }
    return preparedQuotes
  }).finally(() => {
    if (activeQuotesFiscalPreparationRequests.get(preparationKey) === request) {
      activeQuotesFiscalPreparationRequests.delete(preparationKey)
    }
  })

  activeQuotesFiscalPreparationRequests.set(preparationKey, request)
  return request
}

export type QuoteItemMutationPatch = {
  items: Array<Record<string, unknown>>
  summary: Record<string, unknown> | null
}

const normalizeQuoteItemMutationPatch = (value: unknown): QuoteItemMutationPatch | null => {
  if (!isPlainRecord(value) || !Array.isArray(value.items)) return null
  return {
    items: value.items.filter(isPlainRecord),
    summary: isPlainRecord(value.summary) ? value.summary : null,
  }
}

export async function updateQuoteItemCommissions(
  orcamentoId: number | string,
  items: Array<{ itemId: number; percent: number }>
): Promise<{
  message: string
  updatedItems: number
  patch: QuoteItemMutationPatch | null
}> {
  const entidadeId = resolveAuthenticatedEntidadeIdOrThrow('Comissão dos itens do orçamento')
  const params = new URLSearchParams()
  const lojaId = getLojaIdValue()
  if (lojaId) params.set('loja_id', String(lojaId))
  const query = params.toString()
  const response = await fetchApi<any>(
    `/loja-virtual/v1/compradores/${entidadeId}/orcamentos/${encodeURIComponent(String(orcamentoId))}/itens/comissoes${query ? `?${query}` : ''}`,
    {
      method: 'PUT',
      body: JSON.stringify({
        itens: items.map((item) => ({ item_id: item.itemId, percentual: item.percent })),
      }),
    }
  )
  return {
    message: response?.message || response?.motivo || 'Comissão dos produtos atualizada.',
    updatedItems: Number(response?.itens_atualizados ?? 0) || 0,
    patch: normalizeQuoteItemMutationPatch(response?.patch),
  }
}

const isGenericStorefrontSuccessMessage = (value: unknown) => {
  if (typeof value !== 'string') return true
  const normalized = value.trim().toLocaleLowerCase('pt-BR')
  if (!normalized) return true
  return ['ok', 'success', 'sucesso', 'successful', 'done'].includes(normalized)
}

const resolveStorefrontSuccessMessage = (value: unknown, fallback: string) => (
  isGenericStorefrontSuccessMessage(value) ? fallback : String(value).trim()
)

export async function renomearOrcamento(
  orcamentoId: number | string,
  nome: string,
  options: {
    clienteId?: number | null
    clienteNome?: string | null
    clienteDocumento?: string | null
    clienteIe?: string | null
    clienteTelefone?: string | null
    clienteEndereco?: string | null
    clienteContatoNome?: string | null
    dataValidade?: string | null
    formaPagamentoProposta?: string | null
    prazoEntregaProposta?: string | null
    validadeCotacaoProposta?: string | null
  } = {}
): Promise<{ message: string }> {
  const entidadeId = resolveAuthenticatedEntidadeIdOrThrow('Renomeação de orçamento')
  const params = new URLSearchParams()
  const lojaId = getLojaIdValue()
  if (lojaId) params.set('loja_id', String(lojaId))

  const query = params.toString()
  const trimmedClienteDocumento = typeof options.clienteDocumento === 'string'
    ? options.clienteDocumento.trim()
    : null
  const hasFormaPagamentoProposta = Object.prototype.hasOwnProperty.call(options, 'formaPagamentoProposta')
  const hasPrazoEntregaProposta = Object.prototype.hasOwnProperty.call(options, 'prazoEntregaProposta')
  const hasValidadeCotacaoProposta = Object.prototype.hasOwnProperty.call(options, 'validadeCotacaoProposta')
  const hasClienteId = Object.prototype.hasOwnProperty.call(options, 'clienteId')
  const clienteId = Number(options.clienteId ?? 0)
  const response = await fetchApi<any>(
    `/loja-virtual/v1/compradores/${entidadeId}/orcamentos/${encodeURIComponent(String(orcamentoId))}/nome${query ? `?${query}` : ''}`,
    {
      method: 'POST',
      body: JSON.stringify({
        nome: String(nome ?? '').trim(),
        ...(hasClienteId ? {
          cliente_destinatario_id: Number.isSafeInteger(clienteId) && clienteId > 0 ? clienteId : null,
        } : {}),
        cliente_nome: String(options.clienteNome ?? '').trim() || undefined,
        ...(trimmedClienteDocumento !== null ? { cliente_documento: trimmedClienteDocumento } : {}),
        cliente_ie: String(options.clienteIe ?? '').trim() || undefined,
        cliente_telefone: String(options.clienteTelefone ?? '').trim() || undefined,
        cliente_endereco: String(options.clienteEndereco ?? '').trim() || undefined,
        cliente_contato_nome: String(options.clienteContatoNome ?? '').trim() || undefined,
        data_validade: String(options.dataValidade ?? '').trim() || undefined,
        ...(hasFormaPagamentoProposta
          ? { forma_pagamento_proposta: String(options.formaPagamentoProposta ?? '').trim() }
          : {}),
        ...(hasPrazoEntregaProposta
          ? { prazo_entrega_proposta: String(options.prazoEntregaProposta ?? '').trim() }
          : {}),
        ...(hasValidadeCotacaoProposta
          ? { validade_cotacao_proposta: String(options.validadeCotacaoProposta ?? '').trim() }
          : {}),
      }),
    }
  )

  return { message: response?.message || response?.motivo || 'Nome do orçamento atualizado com sucesso.' }
}

const resolveCreatedQuoteIdFromPayload = (payload: unknown): number | null => {
  const payloads: unknown[] = [payload]
  if (isPlainRecord(payload)) {
    payloads.push(payload.data, payload.orcamento, payload.quote)
    if (isPlainRecord(payload.data)) {
      payloads.push(payload.data.orcamento, payload.data.quote)
    }
  }

  for (const currentPayload of payloads) {
    if (!isPlainRecord(currentPayload)) continue

    const idRaw = currentPayload.orcamento_id
      ?? currentPayload.orcamentoId
      ?? currentPayload.quote_id
      ?? currentPayload.quoteId
      ?? currentPayload.id

    const id = Number(idRaw)
    if (Number.isFinite(id) && id > 0) {
      return Math.trunc(id)
    }
  }

  return null
}

export async function criarFilhoOrcamento(
  orcamentoId: number | string,
  input: {
    nome: string
    clienteNome: string
    clienteDocumento?: string | null
    clienteIe?: string | null
    clienteTelefone?: string | null
    clienteEndereco?: string | null
    clienteContatoNome?: string | null
    margemTipo?: 'percentual' | 'valor' | null
    margemValor?: number | null
    dataValidade?: string | null
  }
): Promise<{
  message: string
  quoteId: number | null
  quote: Record<string, unknown> | null
}> {
  const entidadeId = resolveAuthenticatedEntidadeIdOrThrow('Criação de variação de orçamento')
  const normalizedQuoteId = String(orcamentoId ?? '').trim()
  if (!normalizedQuoteId) {
    throw new Error('Identificador do orçamento inválido.')
  }

  const params = new URLSearchParams()
  const lojaId = getLojaIdValue()
  if (lojaId) params.set('loja_id', String(lojaId))

  const margemValor = typeof input.margemValor === 'number' && Number.isFinite(input.margemValor)
    ? input.margemValor
    : 0
  const margemTipo = margemValor > 0 && input.margemTipo ? input.margemTipo : null
  const trimmedClienteDocumento = typeof input.clienteDocumento === 'string'
    ? input.clienteDocumento.trim()
    : null
  const query = params.toString()
  const response = await fetchApi<any>(
    `/loja-virtual/v1/compradores/${entidadeId}/orcamentos/${encodeURIComponent(normalizedQuoteId)}/filhos${query ? `?${query}` : ''}`,
    {
      method: 'POST',
      body: JSON.stringify({
        nome: String(input.nome ?? '').trim(),
        cliente_nome: String(input.clienteNome ?? '').trim(),
        ...(trimmedClienteDocumento !== null ? { cliente_documento: trimmedClienteDocumento } : {}),
        cliente_ie: String(input.clienteIe ?? '').trim() || undefined,
        cliente_telefone: String(input.clienteTelefone ?? '').trim() || undefined,
        cliente_endereco: String(input.clienteEndereco ?? '').trim() || undefined,
        cliente_contato_nome: String(input.clienteContatoNome ?? '').trim() || undefined,
        margem_tipo: margemTipo,
        margem_valor: margemValor,
        data_validade: String(input.dataValidade ?? '').trim() || undefined,
      }),
    }
  )

  const quoteId = resolveCreatedQuoteIdFromPayload(response)
  const normalizedQuote = isPlainRecord(response?.orcamento)
    ? normalizeQuoteRecord(response.orcamento)
    : isPlainRecord(response?.data) && isPlainRecord(response.data.orcamento)
      ? normalizeQuoteRecord(response.data.orcamento)
    : isPlainRecord(response)
      ? normalizeQuoteRecord(response)
      : null

  return {
    message: response?.message || response?.motivo || 'Variação criada com sucesso.',
    quoteId,
    quote: normalizedQuote,
  }
}

export async function getFilhosOrcamento(orcamentoId: number | string): Promise<Record<string, unknown>[]> {
  const entidadeId = resolveAuthenticatedEntidadeIdOrThrow('Listagem de orçamentos para cliente')
  const normalizedQuoteId = String(orcamentoId ?? '').trim()
  if (!normalizedQuoteId) {
    throw new Error('Identificador do orçamento inválido.')
  }

  const params = new URLSearchParams()
  const lojaId = getLojaIdValue()
  if (lojaId) params.set('loja_id', String(lojaId))

  const query = params.toString()
  try {
    const response = await fetchApi<unknown>(
      `/loja-virtual/v1/compradores/${entidadeId}/orcamentos/${encodeURIComponent(normalizedQuoteId)}/filhos${query ? `?${query}` : ''}`
    )
    const nestedData = isPlainRecord(response) ? response.data : null
    const responsePayloads = isPlainRecord(nestedData) ? [response, nestedData] : [response]
    const directItems = responsePayloads.flatMap((payload) => (
      extractListFromResponse(payload, ['orcamentos', 'filhos', 'children', 'items', 'data'])
    )).map((item) => (
      isPlainRecord(item) ? normalizeQuoteRecord(item) : item
    )).filter((item): item is Record<string, unknown> => (
      isPlainRecord(item) && resolveQuoteArchivedValue(item) !== 1
    ))

    if (directItems.length > 0) {
      return directItems
    }
  } catch (childrenError) {
    if (!isIntegrationRouteNotAllowedError(childrenError)) {
      console.warn('Failed to load quote children from direct endpoint:', childrenError)
    }
  }

  const normalizedParentKey = normalizedQuoteId.toLocaleLowerCase('pt-BR')
  const parentId = Number.parseInt(normalizedQuoteId, 10)

  const fallbackQuotes = await getOrcamentos(1, 100, undefined, undefined, undefined, { includeArchived: true })
  return fallbackQuotes.items.filter((item) => {
    if (!isPlainRecord(item)) return false
    const normalized = normalizeQuoteRecord(item)
    if (resolveQuoteArchivedValue(normalized) === 1) return false

    const parentCandidates = [
      normalized.orcamento_pai_id,
      normalized.orcamentoPaiId,
      normalized.parent_quote_id,
      normalized.parentQuoteId,
      normalized.parent_id,
      normalized.parentId,
    ]

    return parentCandidates.some((candidate) => {
      if (candidate === null || candidate === undefined) return false
      const candidateText = String(candidate).trim().toLocaleLowerCase('pt-BR')
      if (!candidateText) return false
      if (candidateText === normalizedParentKey) return true
      const candidateNumber = Number.parseInt(candidateText, 10)
      return Number.isFinite(parentId)
        && parentId > 0
        && Number.isFinite(candidateNumber)
        && candidateNumber === parentId
    })
  }) as Record<string, unknown>[]
}

// Clientes reais vinculados ao comprador para o orçamento (substitui o antigo
// "cliente salvo para PDF"). Os cadastros antigos de PDF não são lidos (corte seco).
export async function getQuoteClientDirectory(): Promise<QuoteClientDirectory> {
  const entidadeId = resolveAuthenticatedEntidadeIdOrThrow('Listagem de clientes vinculados')
  const params = new URLSearchParams()
  const lojaId = getLojaIdValue()
  if (lojaId) params.set('loja_id', String(lojaId))

  const query = params.toString()
  const response = await fetchApi<unknown>(
    `/loja-virtual/v1/compradores/${entidadeId}/orcamentos/clientes${query ? `?${query}` : ''}`
  )

  const payloads: unknown[] = [response]
  if (isPlainRecord(response) && isPlainRecord(response.data)) {
    payloads.push(response.data)
  }

  const items = payloads.flatMap((payload) => (
    extractListFromResponse(payload, ['clientes', 'cadastros', 'items', 'data'])
  ))

  const pendingItems = payloads.flatMap((payload) => (
    extractListFromResponse(payload, ['solicitacoes_pendentes', 'solicitacoesPendentes'])
  ))
  const releaseContact = payloads
    .map((payload) => isPlainRecord(payload)
      ? normalizeQuoteClientReleaseContact(payload.contato_liberacao ?? payload.releaseContact)
      : null)
    .find((item): item is QuoteClientReleaseContact => item !== null) ?? null

  return {
    clients: items
      .filter((item): item is Record<string, unknown> => isPlainRecord(item))
      .map((item) => normalizeSavedQuotePdfClient(item))
      .filter((item) => item.id > 0 && item.name.trim().length > 0),
    pending: pendingItems
      .filter((item): item is Record<string, unknown> => isPlainRecord(item))
      .map((item) => normalizePendingQuoteClientLink(item))
      .filter((item) => item.requestId > 0 && item.name.trim().length > 0),
    releaseContact,
  }
}

export type IndicatorClientDelegationStart = {
  buyerSessionToken: string
  authorized: boolean
  relationshipId: number
  indicator: SavedQuotePdfClient
  client: SavedQuotePdfClient
}

export async function startIndicatorClientDelegation(clientId: number): Promise<IndicatorClientDelegationStart> {
  const indicatorId = resolveAuthenticatedEntidadeIdOrThrow('Acesso à conta do cliente')
  const response = await fetchApi<unknown>(
    `/loja-virtual/v1/compradores/${indicatorId}/clientes/${Math.trunc(clientId)}/acessar`,
    { method: 'POST' }
  )
  if (!isPlainRecord(response)) {
    throw new Error('Resposta inválida ao acessar a conta do cliente.')
  }
  const token = normalizeCheckoutText(response.buyer_session_token ?? response.buyerSessionToken)
  const indicatorRaw = isPlainRecord(response.indicador) ? response.indicador : {}
  const clientRaw = isPlainRecord(response.cliente) ? response.cliente : {}
  if (!token || Number(clientRaw.id ?? 0) <= 0 || Number(indicatorRaw.id ?? 0) <= 0) {
    throw new Error(normalizeCheckoutText(response.motivo) || 'Não foi possível acessar a conta do cliente.')
  }
  return {
    buyerSessionToken: token,
    authorized: parseBooleanLike(response.autorizado),
    relationshipId: Number(response.relacionamento_id ?? response.relationshipId ?? 0) || 0,
    indicator: normalizeSavedQuotePdfClient(indicatorRaw),
    client: normalizeSavedQuotePdfClient(clientRaw),
  }
}

export async function getSavedQuotePdfClients(): Promise<SavedQuotePdfClient[]> {
  const directory = await getQuoteClientDirectory()
  return directory.clients
}

export function normalizeQuoteClientDirectoryForTest(value: unknown): QuoteClientDirectory {
  if (!isPlainRecord(value)) {
    return { clients: [], pending: [], releaseContact: null }
  }

  const clients = extractListFromResponse(value, ['clientes'])
    .filter((item): item is Record<string, unknown> => isPlainRecord(item))
    .map((item) => normalizeSavedQuotePdfClient(item))
    .filter((item) => item.id > 0 && item.name.trim().length > 0)

  const pending = extractListFromResponse(value, ['solicitacoes_pendentes', 'solicitacoesPendentes'])
    .filter((item): item is Record<string, unknown> => isPlainRecord(item))
    .map((item) => normalizePendingQuoteClientLink(item))
    .filter((item) => item.requestId > 0 && item.name.trim().length > 0)

  return {
    clients,
    pending,
    releaseContact: normalizeQuoteClientReleaseContact(value.contato_liberacao ?? value.releaseContact),
  }
}

export type QuoteClientLookupResult = {
  resultado: 'vinculado' | 'existente_nao_vinculado' | 'inexistente'
  client: SavedQuotePdfClient | null
}

// Consulta interna por CPF/CNPJ (cobre CPF e CNPJ, diferente do lookup público).
export async function lookupQuoteClient(documento: string): Promise<QuoteClientLookupResult> {
  const entidadeId = resolveAuthenticatedEntidadeIdOrThrow('Consulta de cliente do orçamento')
  const lojaId = getLojaIdValue()
  const response = await fetchApi<any>(
    `/loja-virtual/v1/compradores/${entidadeId}/orcamentos/clientes/lookup`,
    {
      method: 'POST',
      body: JSON.stringify({
        documento: String(documento ?? '').trim(),
        loja_id: lojaId || undefined,
      }),
    }
  )

  const resultado = String(response?.resultado ?? 'inexistente') as QuoteClientLookupResult['resultado']
  const candidate = isPlainRecord(response?.cliente) ? response.cliente : null
  return {
    resultado: (['vinculado', 'existente_nao_vinculado', 'inexistente'] as const).includes(resultado)
      ? resultado
      : 'inexistente',
    client: candidate ? normalizeSavedQuotePdfClient(candidate) : null,
  }
}

// Adiciona um cliente recém-cadastrado à carteira privada do comprador logado.
export async function vincularQuoteClient(clienteEntidadeId: number): Promise<SavedQuotePdfClient | null> {
  const entidadeId = resolveAuthenticatedEntidadeIdOrThrow('Vínculo de cliente do orçamento')
  const lojaId = getLojaIdValue()
  const response = await fetchApi<any>(
    `/loja-virtual/v1/compradores/${entidadeId}/orcamentos/clientes/${clienteEntidadeId}/vincular`,
    {
      method: 'POST',
      body: JSON.stringify({ loja_id: lojaId || undefined }),
    }
  )
  const candidate = isPlainRecord(response?.cliente) ? response.cliente : null
  return candidate ? normalizeSavedQuotePdfClient(candidate) : null
}

export async function updateQuoteClient(
  clienteEntidadeId: number,
  input: {
    name: string
    contactName?: string | null
    registration?: string | null
    email?: string | null
    phone?: string | null
    address?: string | null
    contacts?: SavedQuoteClientContact[]
  }
): Promise<{ message: string; client: SavedQuotePdfClient | null }> {
  const entidadeId = resolveAuthenticatedEntidadeIdOrThrow('Atualização de cliente vinculado')
  const lojaId = getLojaIdValue()
  const response = await fetchApi<any>(
    `/loja-virtual/v1/compradores/${entidadeId}/orcamentos/clientes/${clienteEntidadeId}/atualizar`,
    {
      method: 'POST',
      body: JSON.stringify({
        loja_id: lojaId || undefined,
        nome: String(input.name ?? '').trim(),
        contato_nome: String(input.contactName ?? '').trim(),
        ie: String(input.registration ?? '').trim(),
        email: String(input.email ?? '').trim(),
        telefone: String(input.phone ?? '').trim(),
        endereco: String(input.address ?? '').trim(),
        contatos: (input.contacts ?? []).map((contact) => ({
          id: Number(contact.id ?? 0) || undefined,
          nome: String(contact.name ?? '').trim(),
          email: String(contact.email ?? '').trim(),
          telefone: String(contact.phone ?? '').trim(),
          cargo: String(contact.role ?? '').trim(),
        })),
      }),
    }
  )

  const candidate = isPlainRecord(response?.cliente) ? response.cliente : null
  return {
    message: response?.message || response?.motivo || 'Cadastro do cliente atualizado.',
    client: candidate ? normalizeSavedQuotePdfClient(candidate) : null,
  }
}

export async function updateQuoteClientDefaultCommission(
  clienteEntidadeId: number,
  percentual: number
): Promise<{ message: string; client: SavedQuotePdfClient | null }> {
  const entidadeId = resolveAuthenticatedEntidadeIdOrThrow('Comissão padrão do cliente')
  const lojaId = getLojaIdValue()
  const response = await fetchApi<any>(
    `/loja-virtual/v1/compradores/${entidadeId}/orcamentos/clientes/${clienteEntidadeId}/comissao-padrao`,
    {
      method: 'PUT',
      body: JSON.stringify({
        loja_id: lojaId || undefined,
        percentual,
      }),
    }
  )

  const candidate = isPlainRecord(response?.cliente) ? response.cliente : null
  return {
    message: response?.message || response?.motivo || 'Comissão padrão atualizada.',
    client: candidate ? normalizeSavedQuotePdfClient(candidate) : null,
  }
}

export async function unlinkQuoteClient(
  clienteEntidadeId: number
): Promise<{ message: string; clientId: number }> {
  const entidadeId = resolveAuthenticatedEntidadeIdOrThrow('Desvinculação de cliente')
  const lojaId = getLojaIdValue()
  const response = await fetchApi<any>(
    `/loja-virtual/v1/compradores/${entidadeId}/orcamentos/clientes/${clienteEntidadeId}/desvincular`,
    {
      method: 'POST',
      body: JSON.stringify({ loja_id: lojaId || undefined }),
    }
  )

  return {
    message: response?.message || response?.motivo || 'Cliente desvinculado da sua carteira.',
    clientId: Number(response?.cliente_entidade_id ?? clienteEntidadeId) || clienteEntidadeId,
  }
}

export type QuoteClientLinkRequestResult = {
  resultado: string
  message: string
  client: SavedQuotePdfClient | null
}

// Adiciona ou atualiza um cliente já existente na carteira privada do comprador.
export async function requestQuoteClientLink(input: {
  name: string
  contactName?: string | null
  document?: string | null
  registration?: string | null
  phone?: string | null
  email?: string | null
  address?: string | null
}): Promise<QuoteClientLinkRequestResult> {
  const entidadeId = resolveAuthenticatedEntidadeIdOrThrow('Solicitação de vínculo de cliente')
  const lojaId = getLojaIdValue()
  const response = await fetchApi<any>(
    `/loja-virtual/v1/compradores/${entidadeId}/orcamentos/clientes/solicitacoes`,
    {
      method: 'POST',
      body: JSON.stringify({
        loja_id: lojaId || undefined,
        nome: String(input.name ?? '').trim(),
        contato_nome: String(input.contactName ?? '').trim() || undefined,
        documento: String(input.document ?? '').trim() || undefined,
        ie: String(input.registration ?? '').trim() || undefined,
        telefone: String(input.phone ?? '').trim() || undefined,
        email: String(input.email ?? '').trim() || undefined,
        endereco: String(input.address ?? '').trim() || undefined,
      }),
    }
  )

  const resultado = String(response?.resultado ?? '')
  const candidate = isPlainRecord(response?.cliente) ? response.cliente : null
  const message = resultado === 'ja_vinculado'
    ? 'Dados atualizados na sua carteira de clientes.'
    : 'Cliente adicionado à sua carteira.'

  return {
    resultado,
    message: response?.message || response?.motivo || message,
    client: candidate ? normalizeSavedQuotePdfClient(candidate) : null,
  }
}

export async function arquivarOrcamento(orcamentoId: number | string): Promise<QuoteArchiveMutationResult> {
  return mutateQuoteArchiveState(orcamentoId, 'arquivar', 'Orçamento arquivado.')
}

export async function desarquivarOrcamento(orcamentoId: number | string): Promise<QuoteArchiveMutationResult> {
  return mutateQuoteArchiveState(orcamentoId, 'desarquivar', 'Orçamento desarquivado.')
}

export async function replaceOrcamento(
  orcamentoId: number,
  options?: { freightSelection?: Record<string, unknown> | null }
): Promise<{ message: string }> {
  const entidadeId = resolveAuthenticatedEntidadeIdOrThrow('Substituição de orçamento pelo carrinho')
  const params = new URLSearchParams()
  const lojaId = getLojaIdValue()
  if (lojaId) params.set('loja_id', String(lojaId))

  const query = params.toString()
  const response = await fetchApi<any>(
    `/loja-virtual/v1/compradores/${entidadeId}/orcamentos/${encodeURIComponent(String(orcamentoId))}/replace${query ? `?${query}` : ''}`,
    {
      method: 'POST',
      body: JSON.stringify({
        ...(options && Object.prototype.hasOwnProperty.call(options, 'freightSelection')
          ? { frete_selecionado: options.freightSelection ?? null }
          : {}),
      }),
    }
  )

  return {
    message: resolveStorefrontSuccessMessage(
      response?.message ?? response?.motivo,
      'Orçamento substituído pelos itens atuais do carrinho.'
    )
  }
}

export async function comprarOrcamento(
  orcamentoId: number | string,
  options?: { replaceExistingCart?: boolean }
): Promise<{ message: string }> {
  const entidadeId = resolveAuthenticatedEntidadeIdOrThrow('Conversão de orçamento em carrinho')
  const params = new URLSearchParams()
  const lojaId = getLojaIdValue()
  if (lojaId) params.set('loja_id', String(lojaId))

  const query = params.toString()
  const response = await fetchApi<any>(
    `/loja-virtual/v1/compradores/${entidadeId}/orcamentos/${encodeURIComponent(String(orcamentoId))}/comprar${query ? `?${query}` : ''}`,
    {
      method: 'POST',
      body: JSON.stringify(options?.replaceExistingCart ? { substituir_carrinho: 1 } : {}),
    }
  )
  return {
    message: resolveStorefrontSuccessMessage(
      response?.message ?? response?.motivo,
      'Itens do orçamento enviados para o carrinho.'
    )
  }
}

export async function recalcularOrcamento(orcamentoId: number | string): Promise<{
  message: string
  invalidItems: any[]
  todosItensAtivos: boolean
  quoteUpdated: boolean
  quote: Record<string, unknown> | null
}> {
  const entidadeId = resolveAuthenticatedEntidadeIdOrThrow('Recálculo de orçamento')
  const normalizedQuoteId = String(orcamentoId ?? '').trim()
  if (!normalizedQuoteId) {
    throw new Error('Identificador do orçamento inválido.')
  }
  const params = new URLSearchParams()
  const lojaId = getLojaIdValue()
  if (lojaId) params.set('loja_id', String(lojaId))

  const query = params.toString()
  const response = await fetchApi<any>(
    `/loja-virtual/v1/compradores/${entidadeId}/orcamentos/${encodeURIComponent(normalizedQuoteId)}/recalcular${query ? `?${query}` : ''}`,
    {
      method: 'POST',
    }
  )

  const invalidItems = Array.isArray(response?.itens_invalidos)
    ? response.itens_invalidos
    : []
  const quote = isPlainRecord(response?.orcamento_atualizado)
    ? normalizeQuoteRecord(response.orcamento_atualizado)
    : null
  const todosItensAtivos = response?.todos_itens_ativos === undefined
    ? invalidItems.length === 0
    : parseBooleanLike(response.todos_itens_ativos)
  const quoteUpdated = quote !== null
    ? true
    : (
      response?.orcamento_atualizado === undefined
        ? todosItensAtivos
        : parseBooleanLike(response.orcamento_atualizado)
    )

  return {
    message: response?.message || response?.motivo || 'Orçamento recalculado com sucesso.',
    invalidItems,
    todosItensAtivos,
    quoteUpdated,
    quote,
  }
}

export async function updateQuoteItemQuantity(
  orcamentoId: number,
  itemId: number,
  qtde: number
): Promise<{
  message: string
  quote: Record<string, unknown> | null
  patch: QuoteItemMutationPatch | null
}> {
  const entidadeId = resolveAuthenticatedEntidadeIdOrThrow('Atualização de item do orçamento')
  const params = new URLSearchParams()
  const lojaId = getLojaIdValue()
  if (lojaId) params.set('loja_id', String(lojaId))

  const query = params.toString()
  const response = await fetchApi<any>(
    `/loja-virtual/v1/compradores/${entidadeId}/orcamentos/${encodeURIComponent(String(Math.trunc(orcamentoId)))}/itens/${encodeURIComponent(String(Math.trunc(itemId)))}${query ? `?${query}` : ''}`,
    {
      method: 'POST',
      body: JSON.stringify({
        qtde: Math.max(0, qtde),
        ...(lojaId ? { loja_id: lojaId } : {}),
      }),
    }
  )

  const normalizedQuote = isPlainRecord(response?.orcamento)
    ? normalizeQuoteRecord(response.orcamento)
    : isPlainRecord(response?.quote)
      ? normalizeQuoteRecord(response.quote)
      : isPlainRecord(response)
        ? normalizeQuoteRecord(response)
        : null

  return {
    message: response?.message || response?.motivo || 'Quantidade do item atualizada.',
    quote: normalizedQuote,
    patch: normalizeQuoteItemMutationPatch(response?.patch),
  }
}

export async function removerItemOrcamento(orcamentoId: number, itemId: number): Promise<{
  message: string
  quote: Record<string, unknown> | null
}> {
  const entidadeId = resolveAuthenticatedEntidadeIdOrThrow('Remoção de item do orçamento')
  const params = new URLSearchParams()
  const lojaId = getLojaIdValue()
  if (lojaId) params.set('loja_id', String(lojaId))

  const query = params.toString()
  const response = await fetchApi<any>(
    `/loja-virtual/v1/compradores/${entidadeId}/orcamentos/${encodeURIComponent(String(Math.trunc(orcamentoId)))}/itens/${encodeURIComponent(String(Math.trunc(itemId)))}${query ? `?${query}` : ''}`,
    {
      method: 'DELETE',
    }
  )

  const normalizedQuote = isPlainRecord(response?.orcamento)
    ? normalizeQuoteRecord(response.orcamento)
    : isPlainRecord(response?.quote)
      ? normalizeQuoteRecord(response.quote)
      : isPlainRecord(response)
        ? normalizeQuoteRecord(response)
        : null

  return {
    message: response?.message || response?.motivo || 'Item removido do orçamento.',
    quote: normalizedQuote,
  }
}

export async function enviarTrabalheConosco(input: {
  cpf: string
  nome: string
  email: string
  dataNascimento: string
  telefone: string
  whatsapp: string
  cep: string
  endereco: string
  numero?: string
  complemento?: string
  bairro?: string
  cidade: string
  uf: string
  areaInteresse: number
  cnpj?: string
  arquivos: File[]
}): Promise<{
  message: string
  id: number | null
  attachments: Array<Record<string, unknown>>
}> {
  const formData = new FormData()

  formData.append('cpf', String(input.cpf ?? '').trim())
  formData.append('nome', String(input.nome ?? '').trim())
  formData.append('email', String(input.email ?? '').trim())
  formData.append('data_nascimento', String(input.dataNascimento ?? '').trim())
  formData.append('telefone', String(input.telefone ?? '').trim())
  formData.append('whatsapp', String(input.whatsapp ?? '').trim())
  formData.append('cep', String(input.cep ?? '').trim())
  formData.append('endereco', String(input.endereco ?? '').trim())
  formData.append('cidade', String(input.cidade ?? '').trim())
  formData.append('uf', String(input.uf ?? '').trim())
  formData.append('tpvagaemprego_setor', String(Math.trunc(input.areaInteresse)))

  const numero = String(input.numero ?? '').trim()
  if (numero) formData.append('numero', numero)

  const complemento = String(input.complemento ?? '').trim()
  if (complemento) formData.append('complemento', complemento)

  const bairro = String(input.bairro ?? '').trim()
  if (bairro) formData.append('bairro', bairro)

  const cnpj = String(input.cnpj ?? '').trim()
  if (cnpj) formData.append('cnpj', cnpj)

  input.arquivos.forEach((file) => {
    formData.append('arquivo', file, file.name)
  })

  const response = await fetchApi<any>('/loja-virtual/v1/trabalhe-conosco', {
    method: 'POST',
    body: formData,
  })

  const attachments = Array.isArray(response?.anexos)
    ? response.anexos.filter((attachment: unknown): attachment is Record<string, unknown> => (
      Boolean(attachment) && typeof attachment === 'object' && !Array.isArray(attachment)
    ))
    : []

  return {
    message: response?.message || response?.motivo || 'Currículo enviado com sucesso.',
    id: normalizePositiveInteger(response?.id),
    attachments,
  }
}

function resolveAttendanceField(record: Record<string, unknown>, keys: string[]): string | null {
  for (const key of keys) {
    const normalized = normalizeQuoteFieldValue(record[key])
    if (normalized) return normalized
  }

  return null
}

function resolveAttendanceRecordId(record: Record<string, unknown>): string | null {
  return resolveAttendanceField(record, [
    'id',
    'atendimento_id',
    'atendimentoId',
    'ticket_id',
    'ticketId',
    'site_reclamacao_id',
    'siteReclamacaoId',
    'reclamacao_id',
    'reclamacaoId',
    'protocolo_id',
    'protocoloId',
    'protocol_id',
    'protocolId',
    'protocol',
    'protocolo',
    'numero',
    'codigo',
  ])
}

const ATTENDANCE_CREATED_AT_KEYS = [
  'createdAt',
  'created_at',
  'data',
  'datahora',
  'data_hora',
  'aberto_em',
  'abertoEm',
] as const

const ATTENDANCE_UPDATED_AT_KEYS = [
  'updatedAt',
  'updated_at',
  'ultima_interacao_em',
  'ultimaInteracaoEm',
  'lastInteractionAt',
  'data_atualizacao',
  'datahora',
  'data_hora',
  ...ATTENDANCE_CREATED_AT_KEYS,
] as const

function resolveAttendanceLifecycle(record: Record<string, unknown>): 'open' | 'closed' | 'unknown' {
  const rawStatus = resolveAttendanceField(record, [
    'status',
    'situacao',
    'status_id',
    'statusId',
    'situacao_id',
    'situacaoId',
    'state',
    'lifecycle',
  ])
  const explicitLifecycle = resolveAttendanceField(record, ['lifecycle', 'attendanceState'])
  if (explicitLifecycle === 'open' || explicitLifecycle === 'closed') {
    return explicitLifecycle
  }

  const statusLabel = resolveAttendanceField(record, [
    'statusLabel',
    'status_label',
    'status_descricao',
    'statusDescricao',
    'situacao_descricao',
    'situacaoDescricao',
    'status_texto',
    'situacao_texto',
    'descricao_status',
    'descricao',
  ])

  const parsedStatus = rawStatus ? Number.parseInt(rawStatus, 10) : Number.NaN
  if (Number.isFinite(parsedStatus)) {
    if (parsedStatus === 0) return 'open'
    if (parsedStatus > 0) return 'closed'
  }

  const normalizedLabel = (statusLabel || rawStatus || '').toLocaleLowerCase('pt-BR')
  if (/(abert|reabert|andament|pendente|analise|aguard)/.test(normalizedLabel)) return 'open'
  if (/(fech|encerr|finaliz|conclu|resolvi|atendid|respondid)/.test(normalizedLabel)) return 'closed'

  return 'unknown'
}

function resolveAttendanceStatusLabel(record: Record<string, unknown>): string | null {
  const explicitLabel = resolveAttendanceField(record, [
    'statusLabel',
    'status_label',
    'status_descricao',
    'statusDescricao',
    'situacao_descricao',
    'situacaoDescricao',
    'status_texto',
    'situacao_texto',
  ])

  if (explicitLabel) return explicitLabel

  const lifecycle = resolveAttendanceLifecycle(record)
  if (lifecycle === 'open') return 'Em aberto'
  if (lifecycle === 'closed') return 'Encerrado'
  return null
}

function normalizeAttendanceAttachmentRecord(record: Record<string, unknown>) {
  const id = resolveAttendanceField(record, ['id', 'anexo_id', 'anexoId', 'arquivo_id', 'arquivoId', 'attachment_id'])
  const fileName = resolveAttendanceField(record, [
    'fileName',
    'file_name',
    'nome_arquivo',
    'nomeArquivo',
    'arquivo_nome',
    'arquivoNome',
    'filename',
    'nome',
    'arquivo',
    'descricao',
    'title',
  ])
  const createdAt = resolveAttendanceField(record, ['createdAt', 'created_at', 'data', 'datahora', 'data_hora', 'uploaded_at'])
  const commentId = resolveAttendanceField(record, ['commentId', 'comentario_id', 'comentarioId', 'comment_id'])
  const responseId = resolveAttendanceField(record, ['responseId', 'resposta_id', 'respostaId', 'reply_id'])
  const mimeType = resolveAttendanceField(record, ['mimeType', 'mime_type', 'content_type', 'contentType', 'tipo'])
  const url = resolveAttendanceField(record, ['url', 'href', 'link', 'download_url', 'downloadUrl'])
  const size = normalizePositiveInteger(
    record.size
    ?? record.tamanho
    ?? record.bytes
    ?? record.file_size
    ?? record.fileSize
  )

  return {
    ...record,
    ...(id ? { id } : {}),
    ...(fileName ? { name: fileName, fileName } : {}),
    ...(createdAt ? { createdAt } : {}),
    ...(commentId ? { commentId } : {}),
    ...(responseId ? { responseId } : {}),
    ...(mimeType ? { mimeType } : {}),
    ...(url ? { url } : {}),
    ...(typeof size === 'number' ? { size } : {}),
  }
}

function normalizeAttendanceAttachments(value: unknown): Array<Record<string, unknown>> {
  if (Array.isArray(value)) {
    return value
      .filter((item): item is Record<string, unknown> => isPlainRecord(item))
      .map((item) => normalizeAttendanceAttachmentRecord(item))
  }

  if (isPlainRecord(value)) {
    const nestedItems = extractListFromResponse(value, ['anexos', 'attachments', 'files', 'arquivos', 'items'])
    if (nestedItems.length > 0) {
      return nestedItems
        .filter((item): item is Record<string, unknown> => isPlainRecord(item))
        .map((item) => normalizeAttendanceAttachmentRecord(item))
    }

    return [normalizeAttendanceAttachmentRecord(value)]
  }

  return []
}

function mergeAttendanceAttachments(
  ...attachmentLists: Array<Array<Record<string, unknown>>>
): Array<Record<string, unknown>> {
  const attachmentsByKey = new Map<string, Record<string, unknown>>()

  attachmentLists.flat().forEach((attachment, index) => {
    const key = resolveAttendanceField(attachment, ['id', 'fileName', 'name', 'url'])
      || `attachment-${index}`
    if (!attachmentsByKey.has(key)) {
      attachmentsByKey.set(key, attachment)
    }
  })

  return Array.from(attachmentsByKey.values())
}

function normalizeAttendanceHistoryEntry(
  record: Record<string, unknown>,
  rootAttachments: Array<Record<string, unknown>>
) {
  const commentId = resolveAttendanceField(record, ['commentId', 'comentario_id', 'comentarioId', 'comment_id', 'id'])
  const responseId = resolveAttendanceField(record, ['responseId', 'resposta_id', 'respostaId', 'reply_id'])
  const message = resolveAttendanceField(record, [
    'message',
    'mensagem',
    'comentario',
    'texto',
    'resposta',
    'descricao',
    'content',
    'body',
  ])
  const author = resolveAttendanceField(record, [
    'author',
    'autor',
    'nome',
    'usuario',
    'usuario_nome',
    'usuarioNome',
    'responsavel',
    'created_by',
    'createdBy',
    'operador',
  ])
  const createdAt = resolveAttendanceField(record, [
    'createdAt',
    'created_at',
    'data',
    'datahora',
    'data_hora',
    'updated_at',
    'updatedAt',
  ])

  const nestedAttachments = mergeAttendanceAttachments(
    normalizeAttendanceAttachments(record.anexos),
    normalizeAttendanceAttachments(record.attachments),
    normalizeAttendanceAttachments(record.files),
    normalizeAttendanceAttachments(record.arquivos)
  )

  const matchedRootAttachments = rootAttachments.filter((attachment) => {
    const attachmentCommentId = resolveAttendanceField(attachment, ['commentId', 'comentario_id', 'comentarioId', 'comment_id'])
    const attachmentResponseId = resolveAttendanceField(attachment, ['responseId', 'resposta_id', 'respostaId', 'reply_id'])

    if (commentId && attachmentCommentId && attachmentCommentId === commentId) return true
    if (responseId && attachmentResponseId && attachmentResponseId === responseId) return true
    if (commentId && attachmentResponseId && attachmentResponseId === commentId) return true

    return false
  })

  return {
    ...record,
    ...(commentId ? { commentId } : {}),
    ...(responseId ? { responseId } : {}),
    ...(message ? { message, text: message } : {}),
    ...(author ? { author } : {}),
    ...(createdAt ? { createdAt } : {}),
    attachments: mergeAttendanceAttachments(nestedAttachments, matchedRootAttachments),
  }
}

function normalizeAttendanceRecord(record: Record<string, unknown>) {
  const attendanceId = resolveAttendanceRecordId(record)
  const protocol = resolveAttendanceField(record, [
    'protocol',
    'protocolo',
    'numero',
    'codigo',
    'ticket',
    'ticket_id',
    'ticketId',
    'id',
  ])
  const subject = resolveAttendanceField(record, [
    'subject',
    'assunto',
    'titulo',
    'title',
    'topico',
    'topic',
    'nome',
  ])
  const createdAt = resolveAttendanceField(record, [...ATTENDANCE_CREATED_AT_KEYS])
  const updatedAt = resolveAttendanceField(record, [...ATTENDANCE_UPDATED_AT_KEYS])
  const preview = resolveAttendanceField(record, [
    'preview',
    'ultima_mensagem',
    'ultimaMensagem',
    'descricao',
    'resumo',
    'summary',
    'mensagem',
  ])

  const rootAttachments = mergeAttendanceAttachments(
    normalizeAttendanceAttachments(record.anexos),
    normalizeAttendanceAttachments(record.attachments),
    normalizeAttendanceAttachments(record.files),
    normalizeAttendanceAttachments(record.arquivos)
  )

  const rawHistory = extractListFromResponse(record, [
    'history',
    'historico',
    'comentarios',
    'comments',
    'mensagens',
    'messages',
    'respostas',
    'responses',
    'interacoes',
  ])

  const history = rawHistory
    .filter((item): item is Record<string, unknown> => isPlainRecord(item))
    .map((item) => normalizeAttendanceHistoryEntry(item, rootAttachments))

  const statusLabel = resolveAttendanceStatusLabel(record)
  const lifecycle = resolveAttendanceLifecycle(record)

  return {
    ...record,
    ...(attendanceId ? { id: attendanceId, attendanceId } : {}),
    ...(protocol ? { protocol } : {}),
    ...(subject ? { subject } : {}),
    ...(createdAt ? { createdAt } : {}),
    ...(updatedAt ? { updatedAt } : {}),
    ...(preview ? { preview } : {}),
    ...(statusLabel ? { statusLabel } : {}),
    lifecycle,
    attachments: rootAttachments,
    history,
  }
}

function extractAttendanceDetailRecord(payload: unknown): Record<string, unknown> | null {
  if (!isPlainRecord(payload)) return null

  const nestedCandidate = [
    payload.atendimento,
    payload.ticket,
    payload.item,
    payload.data,
  ].find((candidate) => isPlainRecord(candidate))

  if (isPlainRecord(nestedCandidate)) {
    return {
      ...payload,
      ...nestedCandidate,
      history: nestedCandidate.history ?? payload.history,
      historico: nestedCandidate.historico ?? payload.historico,
      comentarios: nestedCandidate.comentarios ?? payload.comentarios,
      messages: nestedCandidate.messages ?? payload.messages,
      mensagens: nestedCandidate.mensagens ?? payload.mensagens,
      respostas: nestedCandidate.respostas ?? payload.respostas,
      attachments: nestedCandidate.attachments ?? payload.attachments,
      anexos: nestedCandidate.anexos ?? payload.anexos,
      files: nestedCandidate.files ?? payload.files,
      arquivos: nestedCandidate.arquivos ?? payload.arquivos,
    }
  }

  return payload
}

export async function getAtendimentos(
  page: number = 1,
  perPage: number = 20,
  status?: string,
  search?: string,
  days?: number
): Promise<PaginatedListResult<any>> {
  const entidadeId = resolveAuthenticatedEntidadeIdOrThrow('Listagem de atendimentos')
  const normalizedPage = Math.max(1, Math.trunc(page))
  const normalizedPerPage = Math.max(1, Math.trunc(perPage))
  const statusTerm = (status || '').trim().toLocaleLowerCase('pt-BR')
  const searchTerm = (search || '').trim().toLocaleLowerCase('pt-BR')
  const now = Date.now()
  const maxAgeMs = typeof days === 'number' && Number.isFinite(days) && days > 0
    ? days * 24 * 60 * 60 * 1000
    : null
  const hasClientSideFilters = (statusTerm && statusTerm !== 'all') || Boolean(searchTerm) || maxAgeMs !== null

  const buildQuery = (requestPage: number, requestPerPage: number) => {
    const params = new URLSearchParams()
    const lojaId = getLojaIdValue()
    if (lojaId) params.set('loja_id', String(lojaId))
    params.set('page', String(Math.max(1, Math.trunc(requestPage))))
    params.set('per_page', String(Math.max(1, Math.trunc(requestPerPage))))
    return params.toString()
  }

  const fetchAttendancesPage = async (requestPage: number, requestPerPage: number) => {
    const response = await fetchApi<unknown>(
      `/loja-virtual/v1/compradores/${entidadeId}/atendimentos?${buildQuery(requestPage, requestPerPage)}`
    )

    const items = extractListFromResponse(response, ['atendimentos', 'items', 'tickets'])
      .filter((item): item is Record<string, unknown> => isPlainRecord(item))
      .map((item) => normalizeAttendanceRecord(item))

    return { response, items }
  }

  const filterAttendances = (items: Record<string, any>[]) => items.filter((item) => {
    const lifecycle = resolveAttendanceLifecycle(item)

    if (statusTerm && statusTerm !== 'all') {
      if (statusTerm === 'open' && lifecycle !== 'open') return false
      if (statusTerm === 'closed' && lifecycle !== 'closed') return false
    }

    if (searchTerm) {
      const searchable = JSON.stringify(item).toLocaleLowerCase('pt-BR')
      if (!searchable.includes(searchTerm)) return false
    }

    if (maxAgeMs !== null) {
      const referenceDate = resolveAttendanceField(item, [...ATTENDANCE_UPDATED_AT_KEYS])

      if (referenceDate) {
        const parsedDate = Date.parse(referenceDate)
        if (Number.isFinite(parsedDate) && (now - parsedDate) > maxAgeMs) {
          return false
        }
      }
    }

    return true
  })

  if (!hasClientSideFilters) {
    const { response, items } = await fetchAttendancesPage(normalizedPage, normalizedPerPage)
    return buildPaginatedListResult(items, response, normalizedPage, normalizedPerPage)
  }

  const requestPerPage = 100
  const allItems: Record<string, any>[] = []

  for (let currentPage = 1; currentPage <= 100; currentPage += 1) {
    const { response, items } = await fetchAttendancesPage(currentPage, requestPerPage)
    allItems.push(...items)

    const pagination = buildPaginatedListResult(items, response, currentPage, requestPerPage)
    if (currentPage >= pagination.totalPages || items.length < requestPerPage) {
      break
    }
  }

  return buildLocalPaginatedListResult(filterAttendances(allItems), normalizedPage, normalizedPerPage)
}

export async function getAtendimentoById(atendimentoId: number): Promise<any> {
  const entidadeId = resolveAuthenticatedEntidadeIdOrThrow('Detalhe de atendimento')
  const params = new URLSearchParams()
  const lojaId = getLojaIdValue()
  if (lojaId) params.set('loja_id', String(lojaId))

  const query = params.toString()
  const response = await fetchApi<unknown>(
    `/loja-virtual/v1/compradores/${entidadeId}/atendimentos/${encodeURIComponent(String(Math.trunc(atendimentoId)))}${query ? `?${query}` : ''}`
  )

  const record = extractAttendanceDetailRecord(response)
  return record ? normalizeAttendanceRecord(record) : response
}

export async function responderAtendimento(
  atendimentoId: number,
  mensagem: string
): Promise<{
  message: string
  commentId: number | null
  responseId: number | null
  attendance: Record<string, unknown> | null
}> {
  const entidadeId = resolveAuthenticatedEntidadeIdOrThrow('Resposta de atendimento')
  const params = new URLSearchParams()
  const lojaId = getLojaIdValue()
  if (lojaId) params.set('loja_id', String(lojaId))

  const query = params.toString()
  const normalizedMessage = String(mensagem ?? '').trim()
  const response = await fetchApi<any>(
    `/loja-virtual/v1/compradores/${entidadeId}/atendimentos/${encodeURIComponent(String(Math.trunc(atendimentoId)))}/responder${query ? `?${query}` : ''}`,
    {
      method: 'POST',
      body: JSON.stringify({
        mensagem: normalizedMessage,
        comentario: normalizedMessage,
        resposta: normalizedMessage,
        texto: normalizedMessage,
      }),
    }
  )

  const commentId = normalizePositiveInteger(
    response?.comentario_id
    ?? response?.comentario?.id
    ?? response?.comment_id
    ?? response?.comment?.id
  )
  const responseId = normalizePositiveInteger(
    response?.resposta_id
    ?? response?.resposta?.id
    ?? response?.reply_id
    ?? response?.reply?.id
  )
  const attendanceRecord = extractAttendanceDetailRecord(response)

  return {
    message: response?.message || response?.motivo || 'Atendimento atualizado com sucesso.',
    commentId,
    responseId,
    attendance: attendanceRecord ? normalizeAttendanceRecord(attendanceRecord) : null,
  }
}

export async function uploadAtendimentoAnexos(
  atendimentoId: number,
  files: File[],
  options?: {
    entidadeId?: number | null
  }
): Promise<{
  message: string
  attachments: Array<Record<string, unknown>>
}> {
  const entidadeId = normalizePositiveInteger(options?.entidadeId)
    ?? resolveAuthenticatedEntidadeIdOrThrow('Upload de anexos do atendimento')
  const params = new URLSearchParams()
  const lojaId = getLojaIdValue()
  if (lojaId) params.set('loja_id', String(lojaId))

  const formData = new FormData()
  files.forEach((file) => {
    formData.append('arquivo[]', file, file.name)
  })

  const query = params.toString()
  const response = await fetchApi<any>(
    `/loja-virtual/v1/compradores/${entidadeId}/atendimentos/${encodeURIComponent(String(Math.trunc(atendimentoId)))}/anexos${query ? `?${query}` : ''}`,
    {
      method: 'POST',
      body: formData,
    }
  )

  return {
    message: response?.message || response?.motivo || 'Anexos enviados com sucesso.',
    attachments: normalizeAttendanceAttachments(response),
  }
}

export async function anexarAtendimentoAnexos(
  atendimentoId: number,
  input: {
    commentId?: number | null
    responseId?: number | null
    attachmentIds?: Array<number | string>
    attachments?: Array<Record<string, unknown>>
  }
): Promise<{
  message: string
  attendance: Record<string, unknown> | null
}> {
  const entidadeId = resolveAuthenticatedEntidadeIdOrThrow('Vinculação de anexos do atendimento')
  const params = new URLSearchParams()
  const lojaId = getLojaIdValue()
  if (lojaId) params.set('loja_id', String(lojaId))

  const normalizedAttachmentIds = (input.attachmentIds || [])
    .map((value) => normalizePositiveInteger(value) ?? String(value).trim())
    .filter((value) => (typeof value === 'number' && value > 0) || (typeof value === 'string' && value))

  const normalizedAttachments = (input.attachments || []).map((attachment) => (
    isPlainRecord(attachment) ? normalizeAttendanceAttachmentRecord(attachment) : attachment
  ))

  const query = params.toString()
  const response = await fetchApi<any>(
    `/loja-virtual/v1/compradores/${entidadeId}/atendimentos/${encodeURIComponent(String(Math.trunc(atendimentoId)))}/anexar${query ? `?${query}` : ''}`,
    {
      method: 'POST',
      body: JSON.stringify({
        ...(typeof input.commentId === 'number' && input.commentId > 0
          ? {
              comentario_id: input.commentId,
              comentarioId: input.commentId,
            }
          : {}),
        ...(typeof input.responseId === 'number' && input.responseId > 0
          ? {
              resposta_id: input.responseId,
              respostaId: input.responseId,
            }
          : {}),
        ...(normalizedAttachmentIds.length > 0
          ? {
              anexo_ids: normalizedAttachmentIds,
              anexoIds: normalizedAttachmentIds,
            }
          : {}),
        ...(normalizedAttachments.length > 0
          ? {
              anexos: normalizedAttachments,
            }
          : {}),
      }),
    }
  )

  const attendanceRecord = extractAttendanceDetailRecord(response)

  return {
    message: response?.message || response?.motivo || 'Anexos vinculados com sucesso.',
    attendance: attendanceRecord ? normalizeAttendanceRecord(attendanceRecord) : null,
  }
}

export async function getAtendimentoAnexo(
  atendimentoId: number,
  anexoId: number
): Promise<AttendanceAttachmentDownloadResult> {
  const entidadeId = resolveAuthenticatedEntidadeIdOrThrow('Download de anexo do atendimento')
  const params = new URLSearchParams()
  const lojaId = getLojaIdValue()
  if (lojaId) params.set('loja_id', String(lojaId))

  const query = params.toString()
  const response = await fetchApiResponse(
    `/loja-virtual/v1/compradores/${entidadeId}/atendimentos/${encodeURIComponent(String(Math.trunc(atendimentoId)))}/anexos/${encodeURIComponent(String(Math.trunc(anexoId)))}${query ? `?${query}` : ''}`,
    {
      headers: {
        Accept: 'application/octet-stream,application/pdf,application/json,text/plain,*/*',
      },
    }
  )

  const contentType = (response.headers.get('content-type') || '').toLocaleLowerCase('pt-BR')

  if (contentType.includes('application/json')) {
    const payload = await response.json() as Record<string, unknown>
    const urlCandidate = [
      payload.url,
      payload.link,
      payload.href,
      payload.download_url,
      payload.downloadUrl,
      payload.file_url,
      payload.fileUrl,
    ].find((value) => typeof value === 'string' && value.trim())

    if (typeof urlCandidate === 'string' && urlCandidate.trim()) {
      return {
        type: 'url',
        url: urlCandidate.trim(),
      }
    }

    throw new Error('Anexo indisponível no momento.')
  }

  if (contentType.startsWith('text/')) {
    const textPayload = (await response.text()).trim()
    if (/^https?:\/\//i.test(textPayload)) {
      return {
        type: 'url',
        url: textPayload,
      }
    }
  }

  return {
    type: 'blob',
    blob: await response.blob(),
    fileName: extractFileNameFromDisposition(response.headers.get('content-disposition')),
  }
}

const PUBLIC_TICKET_FETCH_OPTIONS = {
  skipBuyerSessionCredentials: true,
  skipBuyerSessionToken: true,
} as const

const normalizePublicTicketCodeOrThrow = (code: string) => {
  const normalized = String(code ?? '').trim()
  if (!/^[A-Za-z0-9_-]{16,64}$/.test(normalized)) {
    throw new Error('Código do ticket inválido.')
  }
  return normalized
}

const buildPublicTicketQuery = () => {
  const params = new URLSearchParams()
  const lojaId = getLojaIdValue()
  if (lojaId) params.set('loja_id', String(lojaId))
  const query = params.toString()
  return query ? `?${query}` : ''
}

export async function getPublicTicketByCode(code: string): Promise<any> {
  const normalizedCode = normalizePublicTicketCodeOrThrow(code)
  const response = await fetchApi<unknown>(
    `/loja-virtual/v1/tickets/publicos/${encodeURIComponent(normalizedCode)}${buildPublicTicketQuery()}`,
    PUBLIC_TICKET_FETCH_OPTIONS
  )

  const record = extractAttendanceDetailRecord(response)
  return record ? normalizeAttendanceRecord(record) : response
}

export async function responderPublicTicket(
  code: string,
  mensagem: string
): Promise<{
  message: string
  commentId: number | null
  responseId: number | null
  attendance: Record<string, unknown> | null
}> {
  const normalizedCode = normalizePublicTicketCodeOrThrow(code)
  const normalizedMessage = String(mensagem ?? '').trim()
  const response = await fetchApi<any>(
    `/loja-virtual/v1/tickets/publicos/${encodeURIComponent(normalizedCode)}/responder`,
    {
      ...PUBLIC_TICKET_FETCH_OPTIONS,
      method: 'POST',
      body: JSON.stringify({
        mensagem: normalizedMessage,
        comentario: normalizedMessage,
        resposta: normalizedMessage,
        texto: normalizedMessage,
      }),
    }
  )

  const commentId = normalizePositiveInteger(
    response?.comentario_id
    ?? response?.comentario?.id
    ?? response?.comment_id
    ?? response?.comment?.id
  )
  const responseId = normalizePositiveInteger(
    response?.resposta_id
    ?? response?.resposta?.id
    ?? response?.reply_id
    ?? response?.reply?.id
  )
  const attendanceRecord = extractAttendanceDetailRecord(response)

  return {
    message: response?.message || response?.motivo || 'Resposta enviada com sucesso.',
    commentId,
    responseId,
    attendance: attendanceRecord ? normalizeAttendanceRecord(attendanceRecord) : null,
  }
}

export async function uploadPublicTicketAnexos(
  code: string,
  files: File[]
): Promise<{
  message: string
  attachments: Array<Record<string, unknown>>
}> {
  const normalizedCode = normalizePublicTicketCodeOrThrow(code)
  const formData = new FormData()
  files.forEach((file) => {
    formData.append('arquivo[]', file, file.name)
  })

  const response = await fetchApi<any>(
    `/loja-virtual/v1/tickets/publicos/${encodeURIComponent(normalizedCode)}/anexos${buildPublicTicketQuery()}`,
    {
      ...PUBLIC_TICKET_FETCH_OPTIONS,
      method: 'POST',
      body: formData,
    }
  )

  return {
    message: response?.message || response?.motivo || 'Anexos enviados com sucesso.',
    attachments: normalizeAttendanceAttachments(response),
  }
}

export async function getPublicTicketAnexo(
  code: string,
  anexoId: number
): Promise<AttendanceAttachmentDownloadResult> {
  const normalizedCode = normalizePublicTicketCodeOrThrow(code)
  const normalizedAnexoId = normalizePositiveInteger(anexoId)
  if (!normalizedAnexoId) {
    throw new Error('Anexo inválido.')
  }

  const response = await fetchApiResponse(
    `/loja-virtual/v1/tickets/publicos/${encodeURIComponent(normalizedCode)}/anexos/${encodeURIComponent(String(normalizedAnexoId))}${buildPublicTicketQuery()}`,
    {
      ...PUBLIC_TICKET_FETCH_OPTIONS,
      headers: {
        Accept: 'application/octet-stream,application/pdf,application/json,text/plain,*/*',
      },
    }
  )

  const contentType = (response.headers.get('content-type') || '').toLocaleLowerCase('pt-BR')

  if (contentType.includes('application/json')) {
    const payload = await response.json() as Record<string, unknown>
    const urlCandidate = [
      payload.url,
      payload.link,
      payload.href,
      payload.download_url,
      payload.downloadUrl,
      payload.file_url,
      payload.fileUrl,
    ].find((value) => typeof value === 'string' && value.trim())

    if (typeof urlCandidate === 'string' && urlCandidate.trim()) {
      return {
        type: 'url',
        url: urlCandidate.trim(),
      }
    }

    throw new Error('Anexo indisponível no momento.')
  }

  if (contentType.startsWith('text/')) {
    const textPayload = (await response.text()).trim()
    if (/^https?:\/\//i.test(textPayload)) {
      return {
        type: 'url',
        url: textPayload,
      }
    }
  }

  return {
    type: 'blob',
    blob: await response.blob(),
    fileName: extractFileNameFromDisposition(response.headers.get('content-disposition')),
  }
}

export type PublicReturnDocumentValidation = {
  id: number
  nota_id: number
  destinatario_id: number
  tipo_documento: string | number
  numero_documento: string
  data_documento: string
  dias: number
  limite: number
  permite: number
  razao: string
  doc: string
}

export type PublicReturnItemValidation = {
  nota_id: number
  produto_id: number
  produto_nome: string
  produto_codigo: string
  unidade: string
  qtde: number
  foto: string
  permite: number
  dias: number
  infoproduto: string
}

export type PublicReturnValidationResponse = {
  status: number
  documento: PublicReturnDocumentValidation
  itens: PublicReturnItemValidation[]
  total_itens: number
  motivo?: string
  error?: string
}

export type PublicReturnRequestPayload = {
  nota_id: number
  destinatario_id: number
  tipoDoc: number
  tipoDevolucao: number
  token: string
  numDoc: string
  contato_nome: string
  contato_telefone: string
  tecnico_nome: string
  tecnico_telefone: string
  solicitanteNome: string
  solicitantecpf: string
  solicitanteemail: string
  itens: Array<{
    produto_id: number
    qtde: number
    motivo: string
    nome: string
  }>
}

export type PublicReturnRequestResponse = {
  status: number
  protocolo: number
  num_autorizacao: string
  motivo?: string
  error?: string
}

export const DEFAULT_DEVOLUCAO_TOKEN = '_token_devolucao_brasiltec_'

export async function validatePublicReturnDocument(input: {
  cpfCnpj: string
  orderNumber?: string
  invoiceNumber?: string
  typeId?: number
}): Promise<PublicReturnValidationResponse> {
  const cpfCnpj = normalizeDigits(input.cpfCnpj)
  const orderNumber = input.orderNumber?.trim()
  const invoiceNumber = input.invoiceNumber?.trim()

  if (!cpfCnpj || (!orderNumber && !invoiceNumber)) {
    throw new Error('Informe CPF/CNPJ e um número de pedido ou nota fiscal.')
  }

  const payload: Record<string, unknown> = {
    cpf_cnpj: cpfCnpj,
  }

  if (orderNumber) payload.numero_pedido = orderNumber
  if (invoiceNumber) payload.numero_nota = invoiceNumber
  if (typeof input.typeId === 'number' && Number.isFinite(input.typeId)) {
    payload.tipo_id = Math.trunc(input.typeId)
  }

  const response = await fetchApi<PublicReturnValidationResponse>(
    '/integracoes/v1/devolucoes/validar',
    {
      method: 'POST',
      body: JSON.stringify(payload),
    }
  )

  if (response.status !== 1) {
    throw new Error(response.motivo ?? response.error ?? 'Não encontramos nota ou pedido para os dados informados.')
  }

  return response
}

export async function createPublicReturnRequest(
  payload: PublicReturnRequestPayload
): Promise<PublicReturnRequestResponse> {
  const response = await fetchApi<PublicReturnRequestResponse>(
    '/devolucoes',
    {
      method: 'POST',
      body: JSON.stringify(payload),
    }
  )

  if (response.status !== 1) {
    throw new Error(response.motivo ?? response.error ?? 'Erro ao criar solicitação de devolução.')
  }

  return response
}

const extractConsertoDevolucaoDetailFromResponse = (payload: unknown): any | null => {
  if (!payload || typeof payload !== 'object') return null

  const record = payload as Record<string, unknown>
  const directCandidates = [
    record.devolucao,
    record.conserto_devolucao,
    record.consertoDevolucao,
    record.item,
    record.data,
  ]

  for (const candidate of directCandidates) {
    if (candidate && typeof candidate === 'object' && !Array.isArray(candidate)) {
      return candidate
    }
  }

  if (
    'id' in record
    || 'numero' in record
    || 'situacao' in record
    || Array.isArray(record.itens)
    || Array.isArray(record.items)
  ) {
    return record
  }

  const listCandidate = extractListFromResponse(payload, [
    'devolucoes',
    'consertos_devolucoes',
    'consertosDevolucoes',
    'items',
  ])[0]

  return listCandidate && typeof listCandidate === 'object' ? listCandidate : null
}

const matchesConsertoDevolucaoId = (item: Record<string, any>, id: number) => {
  const candidates = [
    item.id,
    item.devolucao_id,
    item.devolucaoId,
    item.codigo,
    item.numero,
    item.protocolo,
    item.return_id,
  ]

  return candidates.some((candidate) => normalizePositiveInteger(candidate) === id)
}

export async function getConsertosDevolucoes(page: number = 1, perPage: number = 20, status?: string, type?: string, search?: string, days?: number): Promise<any[]> {
  const entidadeId = resolveAuthenticatedEntidadeIdOrThrow('Listagem de devoluções')
  const params = new URLSearchParams()
  const lojaId = getLojaIdValue()
  if (lojaId) params.set('loja_id', String(lojaId))
  params.set('page', String(Math.max(1, Math.trunc(page))))
  params.set('per_page', String(Math.max(1, Math.trunc(perPage))))
  if (status?.trim()) params.set('status', status.trim())
  if (type?.trim()) params.set('type', type.trim())
  if (search?.trim()) params.set('search', search.trim())
  if (typeof days === 'number' && Number.isFinite(days) && days > 0) {
    params.set('days', String(Math.trunc(days)))
  }

  const response = await fetchApi<unknown>(
    `/loja-virtual/v1/compradores/${entidadeId}/consertos-devolucoes?${params.toString()}`
  )

  return extractListFromResponse(response, [
    'devolucoes',
    'consertos_devolucoes',
    'consertosDevolucoes',
    'items',
  ])
}

export async function getConsertoDevolucaoById(id: number): Promise<any | null> {
  const entidadeId = resolveAuthenticatedEntidadeIdOrThrow('Detalhe de devolução')
  const normalizedId = Math.max(1, Math.trunc(id))
  const params = new URLSearchParams()
  const lojaId = getLojaIdValue()
  if (lojaId) params.set('loja_id', String(lojaId))
  const suffix = params.toString() ? `?${params.toString()}` : ''

  try {
    const detailResponse = await fetchApi<unknown>(
      `/loja-virtual/v1/compradores/${entidadeId}/consertos-devolucoes/${encodeURIComponent(String(normalizedId))}${suffix}`
    )
    const detail = extractConsertoDevolucaoDetailFromResponse(detailResponse)
    if (detail) return detail
  } catch {
    // Backends antigos ainda podem expor apenas a listagem. O fallback abaixo
    // mantém o detalhe funcional quando a lista já vem com os itens aninhados.
  }

  const fallbackItems = await getConsertosDevolucoes(1, 100, undefined, undefined, String(normalizedId))
  return (
    fallbackItems.find((item) => item && typeof item === 'object' && matchesConsertoDevolucaoId(item, normalizedId))
    ?? null
  )
}

export async function getAfiliado(): Promise<any> {
  const entidadeId = resolveAuthenticatedEntidadeIdOrThrow('Leitura de afiliado')
  return fetchApi<any>(`/loja-virtual/v1/compradores/${entidadeId}/afiliados`)
}

export async function criarAfiliado(): Promise<{ message: string }> {
  const entidadeId = resolveAuthenticatedEntidadeIdOrThrow('Ativação de afiliado')
  const response = await fetchApi<any>(`/loja-virtual/v1/compradores/${entidadeId}/afiliados/status`, {
    method: 'POST',
    body: JSON.stringify({
      ativo: 1,
      status: 1,
      habilitado: 1,
    }),
  })
  return { message: response?.motivo || 'Afiliado ativado.' }
}

export async function excluirAfiliado(data: any): Promise<{ message: string }> {
  const entidadeId = resolveAuthenticatedEntidadeIdOrThrow('Desativação de afiliado')
  const response = await fetchApi<any>(`/loja-virtual/v1/compradores/${entidadeId}/afiliados/status`, {
    method: 'POST',
    body: JSON.stringify({
      ...data,
      ativo: 0,
      status: 0,
      habilitado: 0,
    }),
  })
  return { message: response?.motivo || 'Afiliado desativado.' }
}

export type BuyerBeneficios = {
  permite_escolher_finalidade: number
  habilitar_crediario_na_loja: number
  habilitar_entrega_propria_na_loja: number
  permitir_selecionar_endereco_diferente: number
  permitir_cadastrar_endereco: number
  exibir_estoque_real_na_loja: number
  exibir_ipi_no_pdf: number
  exibir_icms_no_pdf: number
  considerar_ipi_sobre_preco_unitario: number
  cotacoes_concorrentes_habilitar: number
  cotacoes_concorrentes_valor_minimo: number
  parcelamento_liberado_sem_primeira_compra: number
  habilitar_afiliado: number
  habilitar_portal_indicador: number
  habilitar_preco_distribuicao: number
  habilitar_marketplace: number
  habilitar_frete_cif_em_venda_parcelada: number
  habilitar_faturamento_painel_na_loja?: number
  modo_preco_loja?: 'NORMAL' | 'BENEFICIO'
  percentual_reducao_impostos?: 0 | 50 | 70 | 90 | 95 | 100
  percentual_impostos?: 0 | 5 | 10 | 30 | 50 | 100
}

export type IndicatorPortalPeriod = 'mes_atual' | '30d' | '90d' | '365d'

export type IndicatorSaldoResumo = {
  totalGerado: number
  aguardandoFaturamento: number
  pendente: number
  liberado: number
}

export type IndicatorPerformance = {
  periodo: {
    token: string
    dataInicio: string | null
    dataFim: string | null
  }
  totalVendido: number
  ticketMedio: number
  conversao: number
  clientesAtivos: number
  orcamentosTotal: number
  vendasConvertidas: number
}

export type IndicatorResumo = {
  entidadeId: number
  lojaId: number
  saldo: IndicatorSaldoResumo
  performance: IndicatorPerformance
}

export type IndicatorCliente = {
  id: number
  razao: string
  nome: string
  cpfCnpj: string
  email: string
  telefone: string
  whatsapp: string
  endereco: string
  numero: string
  bairro: string
  complemento: string
  cep: string
  cidade: string
  uf: string
  tipoPessoa: number | null
  precadastro: number
  liberaSite: number
  datahora: string | null
}

export type IndicatorClientesResult = {
  page: number
  perPage: number
  total: number
  items: IndicatorCliente[]
}

export type IndicatorOrcamentoResumo = {
  id: string
  clienteId: number | null
  clienteNome: string
  nome: string
  status: string
  titularTipo: string
  modoApresentacao: string
  modoComissao: string
  totalBase: number
  comissaoValor: number
  totalApresentado: number
  datahora: string | null
  notaId: number | null
}

export type IndicatorOrcamentosResult = {
  page: number
  perPage: number
  total: number
  items: IndicatorOrcamentoResumo[]
}

export type IndicatorPedidoResumo = {
  id: number
  numeroPedido: number | null
  numeroNota: number | null
  clienteId: number | null
  clienteNome: string
  clienteCpfCnpj: string
  dataPedido: string | null
  valorPedido: number
  status: string
  itens: number
  transportadoraId: number | null
  transportadoraNome: string
  rastreamentoDisponivel: boolean
  trackingUrl: string
}

export type IndicatorPedidoDetalheItem = {
  id: number
  produtoId: number | null
  produtoGradeId: number | null
  referencia: string
  nome: string
  quantidade: number
  valorUnitario: number
  valorTotal: number
  itemCaixa: number
  estoqueNivel: number
}

export type IndicatorPedidoDetalhe = {
  id: number
  numeroPedido: number | null
  clienteId: number | null
  clienteNome: string
  clienteCpfCnpj: string
  dataPedido: string | null
  valorPedido: number
  transportadoraNome: string
  itens: IndicatorPedidoDetalheItem[]
}

export type IndicatorPedidosResult = {
  page: number
  perPage: number
  total: number
  totalValor: number
  clientesAtendidos: number
  items: IndicatorPedidoResumo[]
}

export type IndicatorComissaoHistoricoItem = {
  id: string
  origemTipo: string
  origemId: string
  produtoAfiliadoId: number | null
  indicadorOrcamentoId: string
  notaId: number | null
  numeroPedido: number | null
  numeroNota: number | null
  parcela: number | null
  notaItemId: number | null
  clienteId: number | null
  clienteNome: string
  valorVenda: number
  baseCalculo: number
  comissaoValor: number
  faturadoEm: string | null
  liberacaoPrevistaEm: string | null
  dataPagamento: string | null
  dataVencimento: string | null
  status: 'aguardando_faturamento' | 'pendente' | 'liberado' | 'recebida' | 'a_receber' | 'cancelado' | string
  cancelado: number
  datahora: string | null
}

export type IndicatorComissoes = IndicatorSaldoResumo & {
  entidadeId: number
  lojaId: number
  page: number
  perPage: number
  total: number
  totalAll: number
  totalReceived: number
  totalReceivable: number
  recebidas: number
  aReceber: number
  historico: IndicatorComissaoHistoricoItem[]
}

export type IndicatorClienteCreatePayload = {
  razao: string
  nome?: string
  cpf_cnpj?: string
  email?: string
  telefone?: string
  whatsapp?: string
  contato?: string
  contato_cargo?: string
  contato_aceita_marketing?: number
  endereco?: string
  numero?: string
  bairro?: string
  complemento?: string
  cep?: string
  cidade?: string
  uf?: string
  ie?: string
  tipo_pessoa?: number
  tipo_entidade_atividade_id?: number
  atividade?: string
  finalidade?: number
  simples?: number
}

export type IndicatorQuoteDraftItemPayload = {
  tipo_item?: 'catalogo' | 'manual' | 'painel_resumo'
  categoria_manual?: '' | 'mao_de_obra' | 'materiais_diversos' | 'painel'
  produto_id?: number | string
  produto_grade_id?: number | string
  quantidade?: number
  nome?: string
  descricao?: string
  imagem_url?: string
  valor_unitario?: number
  valor_total?: number
}

export type IndicatorQuotePayload = {
  loja_id?: number
  cliente_id?: number | null
  cliente_nome?: string
  nome?: string
  descricao?: string
  observacao?: string
  titular_tipo?: 'indicador' | 'cliente'
  modo_apresentacao?: 'detalhado' | 'painel'
  modo_comissao?: 'percentual' | 'valor' | 'preco_final'
  comissao_percentual?: number
  comissao_valor?: number
  preco_final_desejado?: number
  itens: IndicatorQuoteDraftItemPayload[]
}

export type IndicatorQuoteCalculationItem = {
  ordem: number
  tipoItem: string
  categoriaManual: string
  produtoId: number | null
  produtoGradeId: number | null
  emissorId: number | null
  codigo: string
  referencia: string
  nome: string
  descricao: string
  imagemUrl: string
  quantidade: number
  percentualIpi: number
  valorIpi: number
  valorSt: number
  subtotalBase: number
  precoUnitarioBase: number
  totalBase: number
  precoUnitarioApresentado: number
  totalApresentado: number
  exibirCliente: number
}

export type IndicatorQuotePreview = {
  clienteId: number | null
  clienteNome: string
  nome: string
  descricao: string
  observacao: string
  titularTipo: string
  modoApresentacao: string
  modoComissao: string
  subtotalBase: number
  impostosBase: number
  totalBase: number
  comissaoPercentual: number
  comissaoValor: number
  comissaoPercentualEfetivo: number
  precoFinalDesejado: number
  totalApresentado: number
  itensInternos: IndicatorQuoteCalculationItem[]
  itensApresentados: IndicatorQuoteCalculationItem[]
}

export type IndicatorQuoteDetail = IndicatorQuotePreview & {
  id: string
  lojaId: number
  status: string
  notaId: number | null
  solicitadoFaturamentoEm: string | null
  datahora: string | null
  datahoraAtualizacao: string | null
}

const normalizeIndicatorNumber = (value: unknown) => {
  const parsed = Number(value ?? 0)
  return Number.isFinite(parsed) ? parsed : 0
}

const normalizeIndicatorInteger = (value: unknown) => {
  const parsed = Number.parseInt(String(value ?? '').trim(), 10)
  return Number.isFinite(parsed) ? Math.trunc(parsed) : 0
}

const normalizeIndicatorNullableId = (value: unknown) => {
  const parsed = normalizeEntidadeId(value)
  return typeof parsed === 'number' ? parsed : null
}

const normalizeIndicatorText = (value: unknown) => normalizeCheckoutText(value)

function mapIndicatorSaldoResumo(raw: unknown): IndicatorSaldoResumo {
  const record = isPlainRecord(raw) ? raw : {}
  return {
    totalGerado: normalizeIndicatorNumber(record.total_gerado ?? record.totalGerado),
    aguardandoFaturamento: normalizeIndicatorNumber(record.aguardando_faturamento ?? record.aguardandoFaturamento),
    pendente: normalizeIndicatorNumber(record.pendente),
    liberado: normalizeIndicatorNumber(record.liberado),
  }
}

function mapIndicatorPerformance(raw: unknown): IndicatorPerformance {
  const record = isPlainRecord(raw) ? raw : {}
  const periodo = isPlainRecord(record.periodo) ? record.periodo : {}

  return {
    periodo: {
      token: normalizeIndicatorText(periodo.token) || 'mes_atual',
      dataInicio: normalizeIndicatorText(periodo.data_inicio ?? periodo.dataInicio) || null,
      dataFim: normalizeIndicatorText(periodo.data_fim ?? periodo.dataFim) || null,
    },
    totalVendido: normalizeIndicatorNumber(record.total_vendido ?? record.totalVendido),
    ticketMedio: normalizeIndicatorNumber(record.ticket_medio ?? record.ticketMedio),
    conversao: normalizeIndicatorNumber(record.conversao),
    clientesAtivos: normalizeIndicatorInteger(record.clientes_ativos ?? record.clientesAtivos),
    orcamentosTotal: normalizeIndicatorInteger(record.orcamentos_total ?? record.orcamentosTotal),
    vendasConvertidas: normalizeIndicatorInteger(record.vendas_convertidas ?? record.vendasConvertidas),
  }
}

function mapIndicatorCliente(raw: unknown): IndicatorCliente | null {
  if (!isPlainRecord(raw)) return null
  const id = normalizeEntidadeId(raw.id)
  if (!id) return null

  return {
    id,
    razao: normalizeIndicatorText(raw.razao),
    nome: normalizeIndicatorText(raw.nome),
    cpfCnpj: normalizeIndicatorText(raw.cpf_cnpj ?? raw.cpfCnpj),
    email: normalizeIndicatorText(raw.email),
    telefone: normalizeIndicatorText(raw.telefone),
    whatsapp: normalizeIndicatorText(raw.whatsapp),
    endereco: normalizeIndicatorText(raw.endereco),
    numero: normalizeIndicatorText(raw.numero),
    bairro: normalizeIndicatorText(raw.bairro),
    complemento: normalizeIndicatorText(raw.complemento),
    cep: normalizeIndicatorText(raw.cep),
    cidade: normalizeIndicatorText(raw.cidade),
    uf: normalizeIndicatorText(raw.uf),
    tipoPessoa: normalizeIndicatorNullableId(raw.tipo_pessoa ?? raw.tipoPessoa),
    precadastro: normalizeIndicatorInteger(raw.precadastro),
    liberaSite: normalizeIndicatorInteger(raw.libera_site ?? raw.liberaSite),
    datahora: normalizeIndicatorText(raw.datahora) || null,
  }
}

function mapIndicatorOrcamentoResumo(raw: unknown): IndicatorOrcamentoResumo | null {
  if (!isPlainRecord(raw)) return null
  const id = normalizeIndicatorText(raw.id)
  if (!id) return null

  return {
    id,
    clienteId: normalizeIndicatorNullableId(raw.cliente_id ?? raw.clienteId),
    clienteNome: normalizeIndicatorText(raw.cliente_nome ?? raw.clienteNome),
    nome: normalizeIndicatorText(raw.nome),
    status: normalizeIndicatorText(raw.status) || 'rascunho',
    titularTipo: normalizeIndicatorText(raw.titular_tipo ?? raw.titularTipo) || 'indicador',
    modoApresentacao: normalizeIndicatorText(raw.modo_apresentacao ?? raw.modoApresentacao) || 'detalhado',
    modoComissao: normalizeIndicatorText(raw.modo_comissao ?? raw.modoComissao) || 'valor',
    totalBase: normalizeIndicatorNumber(raw.total_base ?? raw.totalBase),
    comissaoValor: normalizeIndicatorNumber(raw.comissao_valor ?? raw.comissaoValor),
    totalApresentado: normalizeIndicatorNumber(raw.total_apresentado ?? raw.totalApresentado),
    datahora: normalizeIndicatorText(raw.datahora) || null,
    notaId: normalizeIndicatorNullableId(raw.nota_id ?? raw.notaId),
  }
}

function mapIndicatorPedidoResumo(raw: unknown): IndicatorPedidoResumo | null {
  if (!isPlainRecord(raw)) return null
  const id = normalizeIndicatorInteger(raw.id)
  if (id <= 0) return null

  return {
    id,
    numeroPedido: normalizeIndicatorNullableId(raw.numero_pedido ?? raw.numeroPedido),
    numeroNota: normalizeIndicatorNullableId(raw.numero_nota ?? raw.numeroNota),
    clienteId: normalizeIndicatorNullableId(raw.cliente_id ?? raw.clienteId),
    clienteNome: normalizeIndicatorText(raw.cliente_nome ?? raw.clienteNome),
    clienteCpfCnpj: normalizeIndicatorText(raw.cliente_cpf_cnpj ?? raw.clienteCpfCnpj),
    dataPedido: normalizeIndicatorText(raw.data_pedido ?? raw.dataPedido) || null,
    valorPedido: normalizeIndicatorNumber(raw.valor_pedido ?? raw.valorPedido),
    status: normalizeIndicatorText(raw.status) || 'em_andamento',
    itens: normalizeIndicatorInteger(raw.itens),
    transportadoraId: normalizeIndicatorNullableId(raw.transportadora_id ?? raw.transportadoraId),
    transportadoraNome: normalizeIndicatorText(raw.transportadora_nome ?? raw.transportadoraNome),
    rastreamentoDisponivel: raw.rastreamento_disponivel === true || raw.rastreamentoDisponivel === true,
    trackingUrl: normalizeIndicatorText(raw.tracking_url ?? raw.trackingUrl),
  }
}

function mapIndicatorQuoteCalculationItem(raw: unknown): IndicatorQuoteCalculationItem | null {
  if (!isPlainRecord(raw)) return null

  return {
    ordem: normalizeIndicatorInteger(raw.ordem),
    tipoItem: normalizeIndicatorText(raw.tipo_item ?? raw.tipoItem),
    categoriaManual: normalizeIndicatorText(raw.categoria_manual ?? raw.categoriaManual),
    produtoId: normalizeIndicatorNullableId(raw.produto_id ?? raw.produtoId),
    produtoGradeId: normalizeIndicatorNullableId(raw.produto_grade_id ?? raw.produtoGradeId),
    emissorId: normalizeIndicatorNullableId(raw.emissor_id ?? raw.emissorId),
    codigo: normalizeIndicatorText(raw.codigo),
    referencia: normalizeIndicatorText(raw.referencia),
    nome: normalizeIndicatorText(raw.nome),
    descricao: normalizeIndicatorText(raw.descricao),
    imagemUrl: normalizeIndicatorText(raw.imagem_url ?? raw.imagemUrl),
    quantidade: normalizeIndicatorNumber(raw.quantidade),
    percentualIpi: normalizeIndicatorNumber(raw.percentual_ipi ?? raw.percentualIpi),
    valorIpi: normalizeIndicatorNumber(raw.valor_ipi ?? raw.valorIpi),
    valorSt: normalizeIndicatorNumber(raw.valor_st ?? raw.valorSt),
    subtotalBase: normalizeIndicatorNumber(raw.subtotal_base ?? raw.subtotalBase),
    precoUnitarioBase: normalizeIndicatorNumber(raw.preco_unitario_base ?? raw.precoUnitarioBase),
    totalBase: normalizeIndicatorNumber(raw.total_base ?? raw.totalBase),
    precoUnitarioApresentado: normalizeIndicatorNumber(
      raw.preco_unitario_apresentado ?? raw.precoUnitarioApresentado
    ),
    totalApresentado: normalizeIndicatorNumber(raw.total_apresentado ?? raw.totalApresentado),
    exibirCliente: normalizeIndicatorInteger(raw.exibir_cliente ?? raw.exibirCliente),
  }
}

function mapIndicatorQuotePreview(raw: unknown): IndicatorQuotePreview {
  const record = isPlainRecord(raw) ? raw : {}
  const rawItensInternos = record.itens_internos ?? record.itensInternos
  const rawItensApresentados = record.itens_apresentados ?? record.itensApresentados
  const itensInternos = Array.isArray(rawItensInternos)
    ? rawItensInternos
      .map(mapIndicatorQuoteCalculationItem)
      .filter((item): item is IndicatorQuoteCalculationItem => Boolean(item))
    : []
  const itensApresentados = Array.isArray(rawItensApresentados)
    ? rawItensApresentados
      .map(mapIndicatorQuoteCalculationItem)
      .filter((item): item is IndicatorQuoteCalculationItem => Boolean(item))
    : []

  return {
    clienteId: normalizeIndicatorNullableId(record.cliente_id ?? record.clienteId),
    clienteNome: normalizeIndicatorText(record.cliente_nome ?? record.clienteNome),
    nome: normalizeIndicatorText(record.nome),
    descricao: normalizeIndicatorText(record.descricao),
    observacao: normalizeIndicatorText(record.observacao),
    titularTipo: normalizeIndicatorText(record.titular_tipo ?? record.titularTipo) || 'indicador',
    modoApresentacao: normalizeIndicatorText(record.modo_apresentacao ?? record.modoApresentacao) || 'detalhado',
    modoComissao: normalizeIndicatorText(record.modo_comissao ?? record.modoComissao) || 'valor',
    subtotalBase: normalizeIndicatorNumber(record.subtotal_base ?? record.subtotalBase),
    impostosBase: normalizeIndicatorNumber(record.impostos_base ?? record.impostosBase),
    totalBase: normalizeIndicatorNumber(record.total_base ?? record.totalBase),
    comissaoPercentual: normalizeIndicatorNumber(record.comissao_percentual ?? record.comissaoPercentual),
    comissaoValor: normalizeIndicatorNumber(record.comissao_valor ?? record.comissaoValor),
    comissaoPercentualEfetivo: normalizeIndicatorNumber(
      record.comissao_percentual_efetivo ?? record.comissaoPercentualEfetivo
    ),
    precoFinalDesejado: normalizeIndicatorNumber(record.preco_final_desejado ?? record.precoFinalDesejado),
    totalApresentado: normalizeIndicatorNumber(record.total_apresentado ?? record.totalApresentado),
    itensInternos,
    itensApresentados,
  }
}

function mapIndicatorQuoteDetail(raw: unknown): IndicatorQuoteDetail | null {
  if (!isPlainRecord(raw)) return null
  const id = normalizeIndicatorText(raw.id)
  if (!id) return null

  return {
    ...mapIndicatorQuotePreview(raw),
    id,
    lojaId: normalizeIndicatorInteger(raw.loja_id ?? raw.lojaId),
    status: normalizeIndicatorText(raw.status) || 'rascunho',
    notaId: normalizeIndicatorNullableId(raw.nota_id ?? raw.notaId),
    solicitadoFaturamentoEm: normalizeIndicatorText(
      raw.solicitado_faturamento_em ?? raw.solicitadoFaturamentoEm
    ) || null,
    datahora: normalizeIndicatorText(raw.datahora) || null,
    datahoraAtualizacao: normalizeIndicatorText(raw.datahora_atualizacao ?? raw.datahoraAtualizacao) || null,
  }
}

function mapIndicatorDetailItemToPayload(item: IndicatorQuoteCalculationItem): IndicatorQuoteDraftItemPayload | null {
  const quantity = item.quantidade > 0 ? item.quantidade : 1
  const tipoItem = item.tipoItem.trim().toLocaleLowerCase('pt-BR')
  const isManual = tipoItem === 'manual' || tipoItem === 'painel_resumo' || !item.produtoId

  if (isManual) {
    const categoriaManual = (
      item.categoriaManual === 'mao_de_obra'
      || item.categoriaManual === 'materiais_diversos'
      || item.categoriaManual === 'painel'
    )
      ? item.categoriaManual
      : tipoItem === 'painel_resumo'
        ? 'painel'
        : ''

    return {
      tipo_item: 'manual',
      categoria_manual: categoriaManual,
      quantidade: quantity,
      nome: item.nome || undefined,
      descricao: item.descricao || undefined,
      imagem_url: item.imagemUrl || undefined,
      valor_unitario: item.precoUnitarioBase || item.precoUnitarioApresentado || 0,
    }
  }

  return {
    tipo_item: 'catalogo',
    produto_id: item.produtoId,
    produto_grade_id: item.produtoGradeId || undefined,
    quantidade: quantity,
    descricao: item.descricao || undefined,
  }
}

function buildIndicatorQuotePayloadFromDetail(detail: IndicatorQuoteDetail): IndicatorQuotePayload {
  const modoComissao = detail.modoComissao === 'percentual'
    ? 'percentual'
    : detail.modoComissao === 'valor'
      ? 'valor'
      : 'preco_final'
  const modoApresentacao = detail.modoApresentacao === 'painel' ? 'painel' : 'detalhado'
  const titularTipo = detail.titularTipo === 'cliente' ? 'cliente' : 'indicador'
  const itens = detail.itensInternos
    .map(mapIndicatorDetailItemToPayload)
    .filter((item): item is IndicatorQuoteDraftItemPayload => Boolean(item))

  return {
    loja_id: detail.lojaId || undefined,
    cliente_id: titularTipo === 'cliente' ? detail.clienteId : null,
    cliente_nome: titularTipo === 'cliente' ? detail.clienteNome || undefined : undefined,
    nome: detail.nome || undefined,
    descricao: detail.descricao || undefined,
    observacao: detail.observacao || undefined,
    titular_tipo: titularTipo,
    modo_apresentacao: modoApresentacao,
    modo_comissao: modoComissao,
    comissao_percentual: detail.comissaoPercentual,
    comissao_valor: detail.comissaoValor,
    preco_final_desejado: detail.precoFinalDesejado,
    itens,
  }
}

async function resolveIndicatorQuoteDetailForRecalculation(
  orcamentoId: string | number,
  notaId?: number | null
): Promise<{ id: string; detail: IndicatorQuoteDetail }> {
  const normalizedQuoteId = String(orcamentoId ?? '').trim()

  try {
    const detail = await getIndicadorOrcamento(normalizedQuoteId)
    return { id: normalizedQuoteId, detail }
  } catch (primaryError) {
    if (!notaId) throw primaryError

    try {
      const quotes = await getIndicadorOrcamentos(1, 100)
      const match = quotes.items.find((item) => (
        item.notaId === notaId
        || item.id === normalizedQuoteId
      ))

      if (match) {
        const detail = await getIndicadorOrcamento(match.id)
        return { id: match.id, detail }
      }
    } catch {
      // Keep the original, more specific load error.
    }

    throw primaryError
  }
}

export async function recalcularIndicadorOrcamento(
  orcamentoId: string | number,
  options?: { notaId?: number | null }
): Promise<{
  message: string
  invalidItems: any[]
  todosItensAtivos: boolean
  quoteUpdated: boolean
  quote: Record<string, unknown> | null
}> {
  const { id, detail } = await resolveIndicatorQuoteDetailForRecalculation(orcamentoId, options?.notaId)
  await updateIndicadorOrcamento(
    id,
    buildIndicatorQuotePayloadFromDetail(detail),
    detail.lojaId || undefined
  )

  return {
    message: 'Validade do orçamento do indicador atualizada.',
    invalidItems: [],
    todosItensAtivos: true,
    quoteUpdated: true,
    quote: null,
  }
}

function mapIndicatorComissaoHistoricoItem(raw: unknown): IndicatorComissaoHistoricoItem | null {
  if (!isPlainRecord(raw)) return null
  const id = normalizeIndicatorText(raw.id)
  if (!id) return null

  return {
    id,
    origemTipo: normalizeIndicatorText(raw.origem_tipo ?? raw.origemTipo),
    origemId: normalizeIndicatorText(raw.origem_id ?? raw.origemId),
    produtoAfiliadoId: normalizeIndicatorNullableId(raw.produto_afiliado_id ?? raw.produtoAfiliadoId),
    indicadorOrcamentoId: normalizeIndicatorText(raw.indicador_orcamento_id ?? raw.indicadorOrcamentoId),
    notaId: normalizeIndicatorNullableId(raw.nota_id ?? raw.notaId),
    numeroPedido: normalizeIndicatorNullableId(raw.numero_pedido ?? raw.numeroPedido),
    numeroNota: normalizeIndicatorNullableId(raw.numero_nota ?? raw.numeroNota),
    parcela: normalizeIndicatorNullableId(raw.parcela),
    notaItemId: normalizeIndicatorNullableId(raw.nota_item_id ?? raw.notaItemId),
    clienteId: normalizeIndicatorNullableId(raw.cliente_id ?? raw.clienteId),
    clienteNome: normalizeIndicatorText(raw.cliente_nome ?? raw.clienteNome),
    valorVenda: normalizeIndicatorNumber(raw.valor_venda ?? raw.valorVenda),
    baseCalculo: normalizeIndicatorNumber(raw.base_calculo ?? raw.baseCalculo),
    comissaoValor: normalizeIndicatorNumber(raw.comissao_valor ?? raw.comissaoValor),
    faturadoEm: normalizeIndicatorText(raw.faturado_em ?? raw.faturadoEm) || null,
    liberacaoPrevistaEm: normalizeIndicatorText(raw.liberacao_prevista_em ?? raw.liberacaoPrevistaEm) || null,
    dataPagamento: normalizeIndicatorText(raw.data_pagamento ?? raw.dataPagamento) || null,
    dataVencimento: normalizeIndicatorText(raw.data_vencimento ?? raw.dataVencimento) || null,
    status: normalizeIndicatorText(raw.status) || 'pendente',
    cancelado: normalizeIndicatorInteger(raw.cancelado),
    datahora: normalizeIndicatorText(raw.datahora) || null,
  }
}

export async function getIndicadorResumo(lojaId?: number): Promise<IndicatorResumo> {
  const entidadeId = resolveAuthenticatedEntidadeIdOrThrow('Resumo do portal de indicador')
  const resolvedLojaId = resolveLojaIdOrThrow('Resumo do portal de indicador', lojaId)
  const response = await fetchApi<any>(
    `/loja-virtual/v1/compradores/${entidadeId}/indicador/resumo?loja_id=${encodeURIComponent(String(resolvedLojaId))}`
  )

  if (response?.status !== 1) {
    throw new Error(response?.motivo || 'Não foi possível carregar o resumo do portal de indicador.')
  }

  return {
    entidadeId,
    lojaId: resolvedLojaId,
    saldo: mapIndicatorSaldoResumo(response?.saldo),
    performance: mapIndicatorPerformance(response?.performance),
  }
}

export async function getIndicadorPerformance(
  periodo: IndicatorPortalPeriod = 'mes_atual',
  lojaId?: number
): Promise<IndicatorPerformance> {
  const entidadeId = resolveAuthenticatedEntidadeIdOrThrow('Performance do indicador')
  const resolvedLojaId = resolveLojaIdOrThrow('Performance do indicador', lojaId)
  const params = new URLSearchParams()
  params.set('loja_id', String(resolvedLojaId))
  if (periodo !== 'mes_atual') {
    params.set('periodo', periodo)
  }

  const response = await fetchApi<any>(
    `/loja-virtual/v1/compradores/${entidadeId}/indicador/performance?${params.toString()}`
  )

  if (response?.status !== 1) {
    throw new Error(response?.motivo || 'Não foi possível carregar a performance do indicador.')
  }

  return mapIndicatorPerformance(response)
}

export async function getIndicadorClientes(
  page: number = 1,
  perPage: number = 20,
  search?: string
): Promise<IndicatorClientesResult> {
  const entidadeId = resolveAuthenticatedEntidadeIdOrThrow('Clientes do indicador')
  const params = new URLSearchParams()
  params.set('page', String(Math.max(1, Math.trunc(page))))
  params.set('per_page', String(Math.max(1, Math.trunc(perPage))))
  if (search?.trim()) {
    params.set('q', search.trim())
  }

  const response = await fetchApi<any>(
    `/loja-virtual/v1/compradores/${entidadeId}/indicador/clientes?${params.toString()}`
  )

  if (response?.status !== 1) {
    throw new Error(response?.motivo || 'Não foi possível carregar os clientes do indicador.')
  }

  const items = Array.isArray(response?.clientes)
    ? response.clientes.map(mapIndicatorCliente).filter((item): item is IndicatorCliente => Boolean(item))
    : []

  return {
    page: normalizeIndicatorInteger(response?.page) || Math.max(1, Math.trunc(page)),
    perPage: normalizeIndicatorInteger(response?.per_page ?? response?.perPage) || Math.max(1, Math.trunc(perPage)),
    total: normalizeIndicatorInteger(response?.total),
    items,
  }
}

export async function createIndicadorCliente(
  payload: IndicatorClienteCreatePayload
): Promise<IndicatorCliente> {
  const entidadeId = resolveAuthenticatedEntidadeIdOrThrow('Cadastro de cliente do indicador')
  const response = await fetchApi<any>(
    `/loja-virtual/v1/compradores/${entidadeId}/indicador/clientes`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    }
  )

  if (response?.status !== 1) {
    throw new Error(response?.motivo || 'Não foi possível salvar o cliente do indicador.')
  }

  const cliente = mapIndicatorCliente(response?.cliente)
  if (!cliente) {
    throw new Error('Cliente do indicador salvo, mas a resposta veio inválida.')
  }

  return cliente
}

export async function unlinkIndicadorCliente(
  clienteEntidadeId: number
): Promise<{ message: string; clientId: number }> {
  const entidadeId = resolveAuthenticatedEntidadeIdOrThrow('Desvinculação de cliente do indicador')
  const normalizedClientId = Math.max(0, Math.trunc(Number(clienteEntidadeId) || 0))
  if (!normalizedClientId) {
    throw new Error('Cliente inválido para desvinculação.')
  }

  const response = await fetchApi<any>(
    `/loja-virtual/v1/compradores/${entidadeId}/indicador/clientes/${normalizedClientId}/desvincular`,
    { method: 'POST' }
  )

  if (response?.status !== 1) {
    throw new Error(response?.motivo || 'Não foi possível desvincular o cliente.')
  }

  return {
    message: response?.message || response?.motivo || 'Cliente desvinculado da sua carteira.',
    clientId: Number(response?.cliente_entidade_id ?? normalizedClientId) || normalizedClientId,
  }
}

export async function getIndicadorOrcamentos(
  page: number = 1,
  perPage: number = 20,
  status?: string,
  lojaId?: number
): Promise<IndicatorOrcamentosResult> {
  const entidadeId = resolveAuthenticatedEntidadeIdOrThrow('Orçamentos do indicador')
  const params = new URLSearchParams()
  params.set('page', String(Math.max(1, Math.trunc(page))))
  params.set('per_page', String(Math.max(1, Math.trunc(perPage))))
  const resolvedLojaId = getLojaIdValue(lojaId)
  if (resolvedLojaId) {
    params.set('loja_id', String(resolvedLojaId))
  }
  if (status?.trim()) {
    params.set('status', status.trim())
  }

  const endpoint = `/loja-virtual/v1/compradores/${entidadeId}/indicador/orcamentos?${params.toString()}`
  let response: any

  try {
    response = await fetchApi<any>(endpoint)
  } catch (error) {
    if (!isIntegrationRouteNotAllowedError(error)) throw error
    response = await fetchApi<any>(endpoint, { skipIntegrationAuth: true })
  }

  if (response?.status !== 1) {
    throw new Error(response?.motivo || 'Não foi possível carregar os orçamentos do indicador.')
  }

  const items = Array.isArray(response?.orcamentos)
    ? response.orcamentos
      .map(mapIndicatorOrcamentoResumo)
      .filter((item): item is IndicatorOrcamentoResumo => Boolean(item))
    : []

  return {
    page: normalizeIndicatorInteger(response?.page) || Math.max(1, Math.trunc(page)),
    perPage: normalizeIndicatorInteger(response?.per_page ?? response?.perPage) || Math.max(1, Math.trunc(perPage)),
    total: normalizeIndicatorInteger(response?.total),
    items,
  }
}

export async function getIndicadorPedidos(
  page: number = 1,
  perPage: number = 20,
  lojaId?: number
): Promise<IndicatorPedidosResult> {
  const entidadeId = resolveAuthenticatedEntidadeIdOrThrow('Pedidos dos clientes do indicador')
  const resolvedLojaId = resolveLojaIdOrThrow('Pedidos dos clientes do indicador', lojaId)
  const params = new URLSearchParams()
  params.set('page', String(Math.max(1, Math.trunc(page))))
  params.set('per_page', String(Math.max(1, Math.trunc(perPage))))
  params.set('loja_id', String(resolvedLojaId))

  const response = await fetchApi<any>(
    `/loja-virtual/v1/compradores/${entidadeId}/indicador/pedidos?${params.toString()}`
  )
  if (response?.status !== 1) {
    throw new Error(response?.motivo || 'Não foi possível carregar os pedidos dos clientes vinculados.')
  }

  const items = Array.isArray(response?.pedidos)
    ? response.pedidos
      .map(mapIndicatorPedidoResumo)
      .filter((item): item is IndicatorPedidoResumo => Boolean(item))
    : []

  return {
    page: normalizeIndicatorInteger(response?.page) || Math.max(1, Math.trunc(page)),
    perPage: normalizeIndicatorInteger(response?.per_page ?? response?.perPage) || Math.max(1, Math.trunc(perPage)),
    total: normalizeIndicatorInteger(response?.total),
    totalValor: normalizeIndicatorNumber(response?.total_valor ?? response?.totalValor),
    clientesAtendidos: normalizeIndicatorInteger(response?.clientes_atendidos ?? response?.clientesAtendidos),
    items,
  }
}

export async function getIndicadorPedidosAsAccountOrders(
  page: number = 1,
  perPage: number = 20,
  status?: string,
  search?: string,
  days?: number,
  lojaId?: number
): Promise<PaginatedListResult<any>> {
  const entidadeId = resolveAuthenticatedEntidadeIdOrThrow('Pedidos dos clientes do indicador')
  const resolvedLojaId = resolveLojaIdOrThrow('Pedidos dos clientes do indicador', lojaId)
  const params = new URLSearchParams({
    page: String(Math.max(1, Math.trunc(page))),
    per_page: String(Math.max(1, Math.trunc(perPage))),
    loja_id: String(resolvedLojaId),
  })
  if (status?.trim()) params.set('status', status.trim())
  if (search?.trim()) params.set('search', search.trim())
  if (typeof days === 'number' && Number.isFinite(days) && days > 0) params.set('days', String(Math.trunc(days)))

  const response = await fetchApi<any>(
    `/loja-virtual/v1/compradores/${entidadeId}/indicador/pedidos?${params.toString()}`
  )
  if (response?.status !== 1) throw new Error(response?.motivo || 'Não foi possível carregar os pedidos dos clientes vinculados.')

  const items = Array.isArray(response?.pedidos) ? response.pedidos.map((item: Record<string, unknown>) => ({
    ...item,
    data: item.data_pedido ?? item.data,
    valor_nota: item.valor_pedido ?? item.valor_nota,
    transportadora: item.transportadora_nome ?? item.transportadora,
    cliente_nome: toPtBrTitleCase(normalizeIndicatorText(item.cliente_nome ?? item.clienteNome)),
    cliente_cpf_cnpj: normalizeIndicatorText(item.cliente_cpf_cnpj ?? item.clienteCpfCnpj),
    rastreamento_url: item.tracking_url ?? item.rastreamento_url,
  })) : []

  return buildPaginatedListResult(items, response, page, perPage)
}

export async function getIndicadorPedidoDetalhe(
  pedidoId: number,
  lojaId?: number
): Promise<IndicatorPedidoDetalhe> {
  const entidadeId = resolveAuthenticatedEntidadeIdOrThrow('Detalhe do pedido do cliente')
  const resolvedLojaId = resolveLojaIdOrThrow('Detalhe do pedido do cliente', lojaId)
  const normalizedPedidoId = Math.max(0, Math.trunc(Number(pedidoId) || 0))
  if (!normalizedPedidoId) throw new Error('Pedido inválido.')

  const response = await fetchApi<any>(
    `/loja-virtual/v1/compradores/${entidadeId}/indicador/pedidos/${normalizedPedidoId}?loja_id=${encodeURIComponent(String(resolvedLojaId))}`
  )
  if (response?.status !== 1 || !isPlainRecord(response?.pedido)) {
    throw new Error(response?.motivo || 'Não foi possível carregar o detalhe do pedido.')
  }

  const raw = response.pedido
  const items = Array.isArray(raw.itens) ? raw.itens : []
  return {
    id: normalizeIndicatorInteger(raw.id),
    numeroPedido: normalizeIndicatorNullableId(raw.numero_pedido ?? raw.numeroPedido),
    clienteId: normalizeIndicatorNullableId(raw.cliente_id ?? raw.clienteId),
    clienteNome: normalizeIndicatorText(raw.cliente_nome ?? raw.clienteNome),
    clienteCpfCnpj: normalizeIndicatorText(raw.cliente_cpf_cnpj ?? raw.clienteCpfCnpj),
    dataPedido: normalizeIndicatorText(raw.data_pedido ?? raw.dataPedido) || null,
    valorPedido: normalizeIndicatorNumber(raw.valor_pedido ?? raw.valorPedido),
    transportadoraNome: normalizeIndicatorText(raw.transportadora_nome ?? raw.transportadoraNome),
    itens: items.filter(isPlainRecord).map((item) => ({
      id: normalizeIndicatorInteger(item.id),
      produtoId: normalizeIndicatorNullableId(item.produto_id ?? item.produtoId),
      produtoGradeId: normalizeIndicatorNullableId(item.produto_grade_id ?? item.produtoGradeId),
      referencia: normalizeIndicatorText(item.referencia),
      nome: normalizeIndicatorText(item.nome),
      quantidade: normalizeIndicatorNumber(item.quantidade ?? item.qtde),
      valorUnitario: normalizeIndicatorNumber(item.valor_unitario ?? item.valorUnitario),
      valorTotal: normalizeIndicatorNumber(item.valor_total ?? item.valorTotal),
      itemCaixa: normalizeIndicatorInteger(item.item_caixa ?? item.itemCaixa),
      estoqueNivel: normalizeIndicatorInteger(item.estoque_nivel ?? item.estoqueNivel),
    })),
  }
}

export async function getIndicadorPedidoDetalheAsAccountOrder(
  pedidoId: number,
  lojaId?: number
): Promise<any> {
  const entidadeId = resolveAuthenticatedEntidadeIdOrThrow('Detalhe do pedido do cliente')
  const resolvedLojaId = resolveLojaIdOrThrow('Detalhe do pedido do cliente', lojaId)
  const normalizedPedidoId = Math.max(0, Math.trunc(Number(pedidoId) || 0))
  if (!normalizedPedidoId) throw new Error('Pedido inválido.')

  const params = new URLSearchParams({
    loja_id: String(resolvedLojaId),
    escopo: 'clientes',
  })
  return fetchApi<any>(
    `/loja-virtual/v1/compradores/${entidadeId}/pedidos/${normalizedPedidoId}?${params.toString()}`
  )
}

export async function previewIndicadorOrcamento(
  payload: IndicatorQuotePayload,
  lojaId?: number
): Promise<IndicatorQuotePreview> {
  const entidadeId = resolveAuthenticatedEntidadeIdOrThrow('Preview do orçamento do indicador')
  const resolvedLojaId = resolveLojaIdOrThrow('Preview do orçamento do indicador', lojaId ?? payload.loja_id)
  const endpoint = `/loja-virtual/v1/compradores/${entidadeId}/indicador/orcamentos/preview`
  const requestOptions = {
    method: 'POST',
    body: JSON.stringify({
      ...payload,
      loja_id: resolvedLojaId,
    }),
  } satisfies FetchApiOptions
  let response: any

  try {
    response = await fetchApi<any>(endpoint, requestOptions)
  } catch (error) {
    if (!isIntegrationRouteNotAllowedError(error)) throw error
    response = await fetchApi<any>(endpoint, { ...requestOptions, skipIntegrationAuth: true })
  }

  if (response?.status !== 1) {
    throw new Error(response?.motivo || 'Não foi possível simular o orçamento do indicador.')
  }

  return mapIndicatorQuotePreview(response?.preview)
}

export async function createIndicadorOrcamento(
  payload: IndicatorQuotePayload,
  lojaId?: number
): Promise<{ id: string | null; orcamento: IndicatorQuotePreview }> {
  const entidadeId = resolveAuthenticatedEntidadeIdOrThrow('Criação de orçamento do indicador')
  const resolvedLojaId = resolveLojaIdOrThrow('Criação de orçamento do indicador', lojaId ?? payload.loja_id)
  const endpoint = `/loja-virtual/v1/compradores/${entidadeId}/indicador/orcamentos`
  const requestOptions = {
    method: 'POST',
    body: JSON.stringify({
      ...payload,
      loja_id: resolvedLojaId,
    }),
  } satisfies FetchApiOptions
  let response: any

  try {
    response = await fetchApi<any>(endpoint, requestOptions)
  } catch (error) {
    if (!isIntegrationRouteNotAllowedError(error)) throw error
    response = await fetchApi<any>(endpoint, { ...requestOptions, skipIntegrationAuth: true })
  }

  if (response?.status !== 1) {
    throw new Error(response?.motivo || 'Não foi possível salvar o orçamento do indicador.')
  }

  return {
    id: normalizeIndicatorText(response?.orcamento_id ?? response?.orcamentoId) || null,
    orcamento: mapIndicatorQuotePreview(response?.orcamento),
  }
}

export async function updateIndicadorOrcamento(
  orcamentoId: string | number,
  payload: IndicatorQuotePayload,
  lojaId?: number
): Promise<{ id: string | null; orcamento: IndicatorQuotePreview }> {
  const entidadeId = resolveAuthenticatedEntidadeIdOrThrow('Atualização de orçamento do indicador')
  const normalizedQuoteId = String(orcamentoId ?? '').trim()
  if (!normalizedQuoteId) {
    throw new Error('Orçamento do indicador inválido.')
  }

  const resolvedLojaId = resolveLojaIdOrThrow('Atualização de orçamento do indicador', lojaId ?? payload.loja_id)
  const endpoint = `/loja-virtual/v1/compradores/${entidadeId}/indicador/orcamentos/${encodeURIComponent(normalizedQuoteId)}`
  const requestOptions = {
    method: 'PATCH',
    body: JSON.stringify({
      ...payload,
      loja_id: resolvedLojaId,
    }),
  } satisfies FetchApiOptions
  let response: any

  try {
    response = await fetchApi<any>(endpoint, requestOptions)
  } catch (error) {
    if (!isIntegrationRouteNotAllowedError(error)) throw error
    response = await fetchApi<any>(endpoint, { ...requestOptions, skipIntegrationAuth: true })
  }

  if (response?.status !== 1) {
    throw new Error(response?.motivo || 'Não foi possível atualizar o orçamento do indicador.')
  }

  return {
    id: normalizeIndicatorText(response?.orcamento_id ?? response?.orcamentoId) || normalizedQuoteId,
    orcamento: mapIndicatorQuotePreview(response?.orcamento),
  }
}

export async function getIndicadorOrcamento(
  orcamentoId: string | number
): Promise<IndicatorQuoteDetail> {
  const entidadeId = resolveAuthenticatedEntidadeIdOrThrow('Detalhe do orçamento do indicador')
  const normalizedQuoteId = String(orcamentoId).trim()
  if (!normalizedQuoteId) {
    throw new Error('Orçamento do indicador inválido.')
  }

  const endpoint = `/loja-virtual/v1/compradores/${entidadeId}/indicador/orcamentos/${encodeURIComponent(normalizedQuoteId)}`
  let response: any

  try {
    response = await fetchApi<any>(endpoint)
  } catch (error) {
    if (!isIntegrationRouteNotAllowedError(error)) throw error
    response = await fetchApi<any>(endpoint, { skipIntegrationAuth: true })
  }

  if (response?.status !== 1) {
    throw new Error(response?.motivo || 'Não foi possível carregar o orçamento do indicador.')
  }

  const orcamento = mapIndicatorQuoteDetail(response?.orcamento)
  if (!orcamento) {
    throw new Error('Orçamento do indicador carregado, mas a resposta veio inválida.')
  }

  return orcamento
}

export async function getIndicadorComissoes(
  page = 1,
  perPage = 30,
  filter: 'all' | 'received' | 'receivable' = 'all',
  lojaId?: number,
): Promise<IndicatorComissoes> {
  const entidadeId = resolveAuthenticatedEntidadeIdOrThrow('Comissões do indicador')
  const resolvedLojaId = resolveLojaIdOrThrow('Comissões do indicador', lojaId)
  const params = new URLSearchParams({
    loja_id: String(resolvedLojaId),
    page: String(Math.max(1, Math.trunc(page))),
    per_page: String(Math.min(100, Math.max(1, Math.trunc(perPage)))),
    filter,
  })
  const response = await fetchApi<any>(
    `/loja-virtual/v1/compradores/${entidadeId}/indicador/comissoes?${params.toString()}`
  )

  if (response?.status !== 1) {
    throw new Error(response?.motivo || 'Não foi possível carregar as comissões do indicador.')
  }

  const historico = Array.isArray(response?.historico)
    ? response.historico
      .map(mapIndicatorComissaoHistoricoItem)
      .filter((item): item is IndicatorComissaoHistoricoItem => Boolean(item))
    : []

  return {
    entidadeId,
    lojaId: resolvedLojaId,
    page: Math.max(1, Math.trunc(normalizeIndicatorNumber(response?.page) || 1)),
    perPage: Math.max(1, Math.trunc(normalizeIndicatorNumber(response?.per_page ?? response?.perPage) || 30)),
    total: normalizeIndicatorNumber(response?.total),
    totalAll: normalizeIndicatorNumber(response?.total_all ?? response?.totalAll),
    totalReceived: normalizeIndicatorNumber(response?.total_received ?? response?.totalReceived),
    totalReceivable: normalizeIndicatorNumber(response?.total_receivable ?? response?.totalReceivable),
    ...mapIndicatorSaldoResumo(response),
    recebidas: normalizeIndicatorNumber(response?.recebidas),
    aReceber: normalizeIndicatorNumber(response?.a_receber ?? response?.aReceber),
    historico,
  }
}

export async function getIndicadorOrcamentoPdf(orcamentoId: string | number): Promise<QuotePdfDownloadResult> {
  const entidadeId = resolveAuthenticatedEntidadeIdOrThrow('PDF do orçamento do indicador')
  const normalizedQuoteId = String(orcamentoId).trim()
  if (!normalizedQuoteId) {
    throw new Error('Orçamento do indicador inválido para gerar o PDF.')
  }

  const endpoint = `/loja-virtual/v1/compradores/${entidadeId}/indicador/orcamentos/${encodeURIComponent(normalizedQuoteId)}/pdf`
  const requestOptions = {
    headers: {
      Accept: 'application/pdf,application/octet-stream,application/json,text/plain,*/*',
    },
  } satisfies FetchApiOptions
  let response: Response

  try {
    response = await fetchApiResponse(endpoint, requestOptions)
  } catch (error) {
    if (!isIntegrationRouteNotAllowedError(error)) throw error
    response = await fetchApiResponse(endpoint, { ...requestOptions, skipIntegrationAuth: true })
  }

  const contentType = (response.headers.get('content-type') || '').toLocaleLowerCase('pt-BR')
  if (contentType.includes('application/json')) {
    const payload = await response.json() as Record<string, unknown>
    throw new Error(
      normalizeIndicatorText(payload.motivo ?? payload.error ?? payload.message)
      || 'PDF do orçamento do indicador indisponível no momento.'
    )
  }

  return {
    type: 'blob',
    blob: await response.blob(),
    fileName: extractFileNameFromDisposition(response.headers.get('content-disposition')),
  }
}

export async function solicitarFaturamentoIndicadorOrcamento(
  orcamentoId: string | number,
  observacaoInterna?: string
): Promise<{ message: string; notaId: number | null; numeroOrcamentoInterno: number | null }> {
  const entidadeId = resolveAuthenticatedEntidadeIdOrThrow('Solicitação de faturamento do indicador')
  const normalizedQuoteId = String(orcamentoId).trim()
  if (!normalizedQuoteId) {
    throw new Error('Orçamento do indicador inválido para solicitar faturamento.')
  }

  const payload = observacaoInterna?.trim()
    ? { observacao_interna: observacaoInterna.trim() }
    : {}

  const endpoint = `/loja-virtual/v1/compradores/${entidadeId}/indicador/orcamentos/${encodeURIComponent(normalizedQuoteId)}/solicitar-faturamento`
  const requestOptions = {
    method: 'POST',
    body: JSON.stringify(payload),
  } satisfies FetchApiOptions
  let response: any

  try {
    response = await fetchApi<any>(endpoint, requestOptions)
  } catch (error) {
    if (!isIntegrationRouteNotAllowedError(error)) throw error
    response = await fetchApi<any>(endpoint, { ...requestOptions, skipIntegrationAuth: true })
  }

  if (response?.status !== 1) {
    throw new Error(response?.motivo || 'Não foi possível encaminhar o orçamento para faturamento.')
  }

  return {
    message: normalizeIndicatorText(response?.motivo) || 'Solicitação de faturamento registrada.',
    notaId: normalizeIndicatorNullableId(response?.nota_id ?? response?.notaId),
    numeroOrcamentoInterno: normalizeIndicatorNullableId(
      response?.numero_orcamento_interno ?? response?.numeroOrcamentoInterno
    ),
  }
}

export type BuyerDistribuicao = {
  tabela_especial: 0 | 1
  faturamento_mensal_esperado: number
  faturamento_medio_ultimos_3_meses: number
  faturamento_mes_atual: number
  data_para_alcancar: string | null
  prazo_ativo: number | null
  beneficio_ativo: number | null
  meta_alcancada: number | null
  meta_alcancada_no_mes: number | null
  meta_alcancada_media_3_meses: number | null
}

export type BuyerPriceTableSegment = {
  id: number
  title: string
  subtitle?: string
  order: number
}

export type BuyerPriceTableGroupSummary = {
  id: number
  segmentId: number
  title: string
  subtitle?: string
  order: number
}

export type BuyerPriceTableGroupDetail = {
  id: number
  title: string
  subtitle?: string
  products: Product[]
}

function normalizePriceTableText(value: unknown): string | undefined {
  const normalized = normalizeCheckoutText(value)
  return normalized || undefined
}

function normalizePriceTableOrder(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value)) return Math.trunc(value)
  if (typeof value === 'string') {
    const parsed = Number.parseInt(value, 10)
    if (Number.isFinite(parsed)) return Math.trunc(parsed)
  }
  return Number.MAX_SAFE_INTEGER
}

function pickFirstRecordValue(record: Record<string, unknown>, keys: string[]): unknown {
  for (const key of keys) {
    if (key in record) return record[key]
  }
  return undefined
}

function pickFirstArrayValue(record: Record<string, unknown>, keys: string[]): unknown[] {
  for (const key of keys) {
    const value = record[key]
    if (Array.isArray(value)) return value
    const parsed = parseCheckoutJsonCandidate(value)
    if (Array.isArray(parsed)) return parsed
    if (isPlainRecord(value)) {
      const nested = pickFirstArrayValue(value, [
        ...keys,
        'items',
        'data',
        'segmentos',
        'grupos',
        'produtos',
        'itens',
      ])
      if (nested.length > 0) return nested
    }
  }
  return []
}

function normalizePriceTableArray(payload: unknown, keys: string[]): unknown[] {
  if (Array.isArray(payload)) return payload
  if (!isPlainRecord(payload)) return []
  return pickFirstArrayValue(payload, keys)
}

function mapBuyerPriceTableSegment(item: unknown): BuyerPriceTableSegment | null {
  if (!isPlainRecord(item)) return null

  const id = normalizeEntidadeId(
    pickFirstRecordValue(item, ['segmento_id', 'id', 'site_menu_id', 'menu_id'])
  )
  const title = normalizePriceTableText(
    pickFirstRecordValue(item, ['segmento_nome', 'nome', 'descricao', 'titulo', 'title', 'label'])
  )

  if (!id || !title) return null

  return {
    id,
    title,
    subtitle: normalizePriceTableText(
      pickFirstRecordValue(item, ['subtitulo', 'subtitle', 'descricao_curta', 'descricao_completa', 'caminho'])
    ),
    order: normalizePriceTableOrder(
      pickFirstRecordValue(item, ['ordem', 'order', 'sequencia', 'posicao'])
    ),
  }
}

function mapBuyerPriceTableGroupSummary(
  item: unknown,
  fallbackSegmentId: number
): BuyerPriceTableGroupSummary | null {
  if (!isPlainRecord(item)) return null

  const id = normalizeEntidadeId(
    pickFirstRecordValue(item, ['grupo_id', 'id', 'produto_grupo_id'])
  )
  const title = normalizePriceTableText(
    pickFirstRecordValue(item, ['grupo_nome', 'nome', 'descricao', 'titulo', 'title', 'label'])
  )

  if (!id || !title) return null

  return {
    id,
    segmentId: normalizeEntidadeId(
      pickFirstRecordValue(item, ['segmento_id', 'site_menu_id', 'menu_id'])
    ) ?? fallbackSegmentId,
    title,
    subtitle: normalizePriceTableText(
      pickFirstRecordValue(item, [
        'subtitulo',
        'subtitle',
        'descricao_curta',
        'descricao_completa',
        'segmento_nome',
        'caminho',
      ])
    ),
    order: normalizePriceTableOrder(
      pickFirstRecordValue(item, ['ordem', 'order', 'sequencia', 'posicao'])
    ),
  }
}

function mapBuyerPriceTableProduct(item: unknown): Product | null {
  if (!isPlainRecord(item)) return null

  if (normalizeEntidadeId(item.produto_id)) {
    return mapLojaVirtualProductToProduct(item as LojaVirtualProdutoItem)
  }

  if (
    normalizeEntidadeId(item.id)
    && (
      Array.isArray(item.fotos)
      || Array.isArray(item.variacoes)
      || 'produto_grupo_nome' in item
      || 'produto_grupo_familia_nome' in item
    )
  ) {
    return mapLojaVirtualProductDetailToProduct(item as LojaVirtualProdutoDetalhe)
  }

  if (
    normalizeEntidadeId(item.id)
    && (
      'discountedPrice' in item
      || 'originalPrice' in item
      || 'distribuitionPrice' in item
      || 'distributionPriceEnabled' in item
    )
  ) {
    return mapApiProductToProduct(item as ApiProduct)
  }

  const id = normalizeEntidadeId(item.id)
  if (!id) return null

  const code = normalizeCheckoutText(item.referencia ?? item.codigo ?? item.code) || String(id)
  const images = buildPreferredProductImageList({
    image: normalizeCheckoutText(item.imagem ?? item.image ?? item.foto) || null,
    imageMid: normalizeCheckoutText(item.imagem_mid ?? item.image_mid ?? item.imageMid) || null,
    imageHigh: normalizeCheckoutText(
      item.imagem_high
      ?? item.image_high
      ?? item.imageHigh
      ?? item.high
    ) || null,
    photos: Array.isArray(item.fotos) ? item.fotos as LojaVirtualProdutoDetalheFoto[] : null,
    extraImages: Array.isArray(item.images) ? item.images as Array<string | null | undefined> : [],
  })
  const image = images[0] || ''
  const price = normalizePriceValue(item.preco ?? item.discountedPrice ?? item.price)
  const originalPrice = normalizePriceValue(item.originalPrice ?? item.preco_original ?? item.preco_lista)
    ?? price
  const wholesalePrice = normalizePriceValue(item.preco_atacado ?? item.precoAtacado ?? item.wholesalePrice)
  const cnpjIePrice = normalizePriceValue(item.preco_cnpj_ie ?? item.cnpjIePrice)
  const distributionPrice = normalizeDistributionPriceValue(item)
  const rawName = normalizeCheckoutText(
    item.nome_curto
    ?? item.nome
    ?? item.title
    ?? item.titulo
    ?? item.descricao
  ) || `Produto ${id}`
  const stock = normalizePriceValue(item.estoque ?? item.estoque_atual ?? item.stock ?? item.stockQuantity)
  const variationAttributes = parseVariationAttributes(
    Array.isArray(item.caracteristicas)
      ? item.caracteristicas as LojaVirtualVariacaoCaracteristica[]
      : Array.isArray(item.atributos)
        ? item.atributos as LojaVirtualVariacaoCaracteristica[]
        : null
  )
  const variationLabel = variationAttributes.find((attribute) => attribute.value)?.value
  const variationAdditionalInfo = normalizeCheckoutText(item.informacoes_adicionais ?? item.variationAdditionalInfo)
  const serie = normalizeCheckoutText(item.serie)
  const tamanho = normalizeCheckoutText(item.tamanho)
  const modelo = normalizeCheckoutText(item.modelo)
  const normalizedBaseName = resolveProductBaseName(rawName, {
    serie,
    modelo,
    tamanho,
  })

  return {
    id: String(id),
    code,
    manufacturerCode: code,
    name: normalizeProductName(rawName),
    baseName: normalizedBaseName,
    serie: serie || undefined,
    tamanho: tamanho || undefined,
    modelo: modelo || undefined,
    variationLabel,
    variationAdditionalInfo: variationAdditionalInfo || undefined,
    variationAttributes,
    brand: normalizeCheckoutText(item.marca ?? item.brand) || 'Sem marca',
    category: normalizeCheckoutText(item.produto_grupo_familia_nome ?? item.category) || 'Produtos Industriais',
    subcategory: normalizeCheckoutText(item.produto_grupo_nome ?? item.subcategory) || '',
    image: images[0] || '',
    images,
    specs: {},
    inStock: typeof stock === 'number' ? stock > 0 : true,
    stockQuantity: typeof stock === 'number' ? stock : null,
    priceVisible: [price, wholesalePrice, cnpjIePrice, distributionPrice].some((value) => typeof value === 'number' && value > 0),
    needsQuote: ![price, wholesalePrice, cnpjIePrice, distributionPrice].some((value) => typeof value === 'number' && value > 0),
    groupId: normalizeEntidadeId(item.produto_grupo_id ?? item.grupo_id)
      ? String(normalizeEntidadeId(item.produto_grupo_id ?? item.grupo_id))
      : undefined,
    hasVariations: Boolean(
      item.possui_variacoes === 1
      || item.produto_com_variacoes === 1
      || item.hasVariations === true
    ),
    hasForm: inferHasProductFormFlag(item),
    price,
    originalPrice,
    discountedPrice: price,
    wholesalePrice,
    cnpjIePrice,
    distributionPrice,
  }
}

function sortPriceTableEntries<T extends { order: number; title: string }>(items: T[]): T[] {
  return [...items].sort((left, right) => {
    const orderDiff = left.order - right.order
    if (orderDiff !== 0) return orderDiff
    return left.title.localeCompare(right.title, 'pt-BR')
  })
}

export async function getBuyerPriceTableIndex(entidadeId?: number | null): Promise<BuyerPriceTableSegment[]> {
  const resolvedEntidadeId = resolveRequestedEntidadeIdOrThrow(entidadeId ?? undefined, 'Índice da tabela da loja')
  const response = await fetchApi<unknown>(
    `/loja-virtual/v1/compradores/${resolvedEntidadeId}/tabela`
  )

  const segments = normalizePriceTableArray(response, ['segmentos', 'items', 'indice', 'data'])
    .map(mapBuyerPriceTableSegment)
    .filter((item): item is BuyerPriceTableSegment => Boolean(item))

  return sortPriceTableEntries(segments)
}

export async function getBuyerPriceTableSegmentGroups(
  segmentoId: number,
  entidadeId?: number | null,
  options: { includePhotos?: boolean } = {},
): Promise<BuyerPriceTableGroupSummary[]> {
  const resolvedEntidadeId = resolveRequestedEntidadeIdOrThrow(entidadeId ?? undefined, 'Grupos de segmento da tabela da loja')
  const normalizedSegmentoId = Math.trunc(segmentoId)

  if (!Number.isFinite(normalizedSegmentoId) || normalizedSegmentoId <= 0) {
    throw new Error('Segmento inválido para a tabela da loja.')
  }

  const params = new URLSearchParams()
  if (options.includePhotos === false) params.set('include_fotos', '0')
  const query = params.size > 0 ? `?${params.toString()}` : ''
  const response = await fetchApi<unknown>(
    `/loja-virtual/v1/compradores/${resolvedEntidadeId}/tabela/segmentos/${encodeURIComponent(String(normalizedSegmentoId))}/grupos${query}`
  )

  const groups = normalizePriceTableArray(response, ['grupos', 'items', 'data'])
    .map((item) => mapBuyerPriceTableGroupSummary(item, normalizedSegmentoId))
    .filter((item): item is BuyerPriceTableGroupSummary => Boolean(item))

  return sortPriceTableEntries(groups)
}

export async function getBuyerPriceTableGroupDetail(
  grupoId: number,
  entidadeId?: number | null,
  options: { includePhotos?: boolean } = {},
): Promise<BuyerPriceTableGroupDetail | null> {
  const resolvedEntidadeId = resolveRequestedEntidadeIdOrThrow(entidadeId ?? undefined, 'Detalhe de grupo da tabela da loja')
  const normalizedGrupoId = Math.trunc(grupoId)

  if (!Number.isFinite(normalizedGrupoId) || normalizedGrupoId <= 0) {
    throw new Error('Grupo inválido para a tabela da loja.')
  }

  const params = new URLSearchParams()
  if (options.includePhotos === false) params.set('include_fotos', '0')
  const query = params.size > 0 ? `?${params.toString()}` : ''
  const response = await fetchApi<unknown>(
    `/loja-virtual/v1/compradores/${resolvedEntidadeId}/tabela/grupos/${encodeURIComponent(String(normalizedGrupoId))}${query}`
  )

  const rootRecord = isPlainRecord(response) ? response : null
  const groupRecord = rootRecord && isPlainRecord(rootRecord.grupo)
    ? rootRecord.grupo
    : rootRecord

  const rootProducts = normalizePriceTableArray(rootRecord, ['produtos', 'items', 'itens'])
  const groupProducts = normalizePriceTableArray(groupRecord, ['produtos', 'items', 'itens'])
  const productsSource = rootProducts.length > 0 ? rootProducts : groupProducts

  const products = productsSource
    .map(mapBuyerPriceTableProduct)
    .filter((item): item is Product => Boolean(item))

  if (!groupRecord && products.length === 0) return null

  return {
    id: normalizeEntidadeId(
      groupRecord
        ? pickFirstRecordValue(groupRecord, ['grupo_id', 'id', 'produto_grupo_id'])
        : normalizedGrupoId
    ) ?? normalizedGrupoId,
    title: groupRecord
      ? normalizePriceTableText(
        pickFirstRecordValue(groupRecord, ['grupo_nome', 'nome', 'descricao', 'titulo', 'title', 'label'])
      ) || `Grupo ${normalizedGrupoId}`
      : `Grupo ${normalizedGrupoId}`,
    subtitle: groupRecord
      ? normalizePriceTableText(
        pickFirstRecordValue(groupRecord, [
          'subtitulo',
          'subtitle',
          'descricao_curta',
          'descricao_completa',
          'segmento_nome',
          'caminho',
        ])
      )
      : undefined,
    products,
  }
}

export async function getProductPrimaryPhotoUrl(productId: string | number): Promise<string> {
  const normalizedProductId = normalizeEntidadeId(productId)
  if (!normalizedProductId) return ''

  const response = await fetchApi<unknown>(
    `/produtos/${encodeURIComponent(String(normalizedProductId))}/foto-principal`
  )
  if (!isPlainRecord(response) || !isPlainRecord(response.foto)) return ''

  const photo = response.foto
  return normalizeProductImageUrl(
    pickFirstRecordValue(photo, [
      'imagem_high',
      'imagemHigh',
      'high',
      'imagem_mid',
      'imagemMid',
      'mid',
      'imagem',
      'image',
      'url',
      'render_url',
      'renderUrl',
    ]) as string | null | undefined
  ) || ''
}

const getBuyerBeneficiosRequest = new Map<number, Promise<BuyerBeneficios | null>>()

export async function getBuyerBeneficios(entidadeId?: number): Promise<BuyerBeneficios | null> {
  const resolvedEntidadeId = resolveRequestedEntidadeIdOrThrow(entidadeId, 'Benefícios do comprador')
  const pending = getBuyerBeneficiosRequest.get(resolvedEntidadeId)
  if (pending) return pending

  const request = (async () => {
    try {
      const response = await fetchApi<any>(
        `/loja-virtual/v1/compradores/${resolvedEntidadeId}/dados`,
        { suppressBuyerSessionExpiry: true }
      )
      const comprador = response?.comprador && typeof response.comprador === 'object' ? response.comprador : response
      const beneficios = comprador?.beneficios ?? null
      return beneficios
    } catch {
      return null
    }
  })().finally(() => {
    getBuyerBeneficiosRequest.delete(resolvedEntidadeId)
  })

  // As duas preparações da cotação usam a mesma conta faturada. Colapsamos
  // apenas chamadas concorrentes; após a resposta não há cache e os dados
  // continuam sempre atuais.
  getBuyerBeneficiosRequest.set(resolvedEntidadeId, request)
  return request
}

export type BuyerCashbackItem = {
  id: number
  data: string | null
  descricao: string
  valor: number
  tipo: 'credito' | 'resgate'
  situacao: 'disponivel' | 'expirado' | 'resgatado'
  validade: string | null
  pedidoNumero: number | null
}

export type BuyerCashback = {
  resumo: {
    saldoDisponivel: number
    creditosVencem30Dias: number
    proximaExpiracao: string | null
  }
  items: BuyerCashbackItem[]
  page: number
  pageSize: number
  total: number
}

const normalizeBuyerCashbackNumber = (value: unknown) => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

export async function getBuyerCashback(input: { page?: number; pageSize?: number; lojaId?: number } = {}): Promise<BuyerCashback> {
  const entidadeId = resolveAuthenticatedEntidadeIdOrThrow('Cashback do comprador')
  const params = new URLSearchParams()
  const lojaId = getLojaIdValue(input.lojaId)
  const page = Math.max(1, Math.trunc(input.page ?? 1))
  const pageSize = Math.max(1, Math.min(50, Math.trunc(input.pageSize ?? 20)))

  if (lojaId) params.set('loja_id', String(lojaId))
  params.set('page', String(page))
  params.set('pageSize', String(pageSize))

  const response = await fetchApi<any>(
    `/loja-virtual/v1/compradores/${entidadeId}/cashback?${params.toString()}`,
    { suppressBuyerSessionExpiry: true }
  )
  const resumo = response?.resumo && typeof response.resumo === 'object' ? response.resumo : {}
  const rawItems = Array.isArray(response?.items) ? response.items : []

  return {
    resumo: {
      saldoDisponivel: normalizeBuyerCashbackNumber(resumo.saldo_disponivel ?? resumo.saldoDisponivel),
      creditosVencem30Dias: normalizeBuyerCashbackNumber(
        resumo.creditos_vencem_30_dias ?? resumo.creditosVencem30Dias
      ),
      proximaExpiracao: typeof (resumo.proxima_expiracao ?? resumo.proximaExpiracao) === 'string'
        ? String(resumo.proxima_expiracao ?? resumo.proximaExpiracao)
        : null,
    },
    items: rawItems.map((item: any): BuyerCashbackItem => ({
      id: Math.max(0, Math.trunc(normalizeBuyerCashbackNumber(item?.id))),
      data: typeof item?.data === 'string' ? item.data : null,
      descricao: typeof item?.descricao === 'string' ? item.descricao : '',
      valor: normalizeBuyerCashbackNumber(item?.valor),
      tipo: item?.tipo === 'resgate' ? 'resgate' : 'credito',
      situacao: item?.situacao === 'resgatado' || item?.situacao === 'expirado'
        ? item.situacao
        : 'disponivel',
      validade: typeof item?.validade === 'string' ? item.validade : null,
      pedidoNumero: Math.max(0, Math.trunc(normalizeBuyerCashbackNumber(item?.pedido_numero ?? item?.pedidoNumero))) || null,
    })),
    page: Math.max(1, Math.trunc(normalizeBuyerCashbackNumber(response?.page)) || page),
    pageSize: Math.max(1, Math.trunc(normalizeBuyerCashbackNumber(response?.pageSize)) || pageSize),
    total: Math.max(0, Math.trunc(normalizeBuyerCashbackNumber(response?.total))),
  }
}

export type BuyerAccountSummary = {
  entidadeId: number
  corporateName: string
  name: string
  email: string
  document: string
  activity: string
  stateRegistration: string
  phone: string
  street: string
  number: string
  complement: string
  neighborhood: string
  city: string
  state: string
  zipCode: string
  buyerSessionToken?: string
  buyerUser?: LojaVirtualAuthenticatedBuyerResult['buyerUser']
  commerceContext: BuyerCommerceContext
}

export type BuyerProfileData = Record<string, unknown>

export type BuyerRotatingSearchTerm = {
  label: string
  produtoId?: number
  origem: 'activity' | 'global'
}

export type BuyerRotatingSearchTermsResponse = {
  atividadeId: number | null
  snapshotDate: string | null
  termos: BuyerRotatingSearchTerm[]
}

export type StorefrontRotatingSearchTermsResponse = BuyerRotatingSearchTermsResponse

export type BuyerStoreUser = {
  id: number
  nome: string
  email: string
  telefone: string
  cargo: string
  acessoLojaAtivo: boolean
  senhaConfigurada: boolean
  inativo: boolean
  tipoUsuario: 'admin' | 'vendedor'
  markupPercentual: number
  acessoMarketplaceAnuncios: boolean
  acessoMarketplacePerguntas: boolean
  isSelf: boolean
}

export type BuyerStoreUserInput = {
  nome: string
  email?: string
  telefone?: string
  cargo?: string
  acessoLojaAtivo?: boolean
  senha?: string
  tipoUsuario?: 'admin' | 'vendedor'
  markupPercentual?: number
  acessoMarketplaceAnuncios?: boolean
  acessoMarketplacePerguntas?: boolean
  inativo?: boolean
}

export type BuyerAccessStatus = {
  entidadeId: number
  active: boolean
  reason?: string
  priceTableAccessCode?: string
  priceTableRedirectPath?: string
  priceTableExpiresAt?: string
}

export type BuyerPriceTableAccessLink = {
  accessCode: string
  redirectPath: string
  shortPath?: string
  shortUrl?: string
  expiresAt?: string
}

function normalizeBuyerAccessShortCode(value: unknown): string {
  const normalized = String(value ?? '').trim()
  return /^[A-Za-z0-9_-]{6,128}$/.test(normalized) ? normalized : ''
}

function extractBuyerPriceTableAccessCodeFromPath(value: unknown): string {
  const path = normalizeCheckoutText(value)
  if (!path) return ''

  try {
    const parsed = new URL(path, 'https://storefront.local')
    const segments = parsed.pathname.split('/').filter(Boolean)
    const lastSegment = segments[segments.length - 1]
    return normalizeBuyerAccessShortCode(lastSegment)
  } catch {
    const segments = path.split(/[/?#]/)[0]?.split('/').filter(Boolean) ?? []
    const lastSegment = segments[segments.length - 1]
    return normalizeBuyerAccessShortCode(lastSegment)
  }
}

function resolveBuyerPriceTableAccessCode(record: Record<string, unknown>): string {
  return normalizeBuyerAccessShortCode(
    record.price_table_short_code
    ?? record.priceTableShortCode
    ?? record.short_code
    ?? record.shortCode
    ?? record.price_table_access_code
    ?? record.priceTableAccessCode
    ?? record.codigo_tabela
    ?? record.codigoTabela
  ) || extractBuyerPriceTableAccessCodeFromPath(
    record.price_table_redirect_path
    ?? record.priceTableRedirectPath
    ?? record.redirect_path
    ?? record.redirectPath
  )
}

function normalizeBuyerRotatingSearchTermOrigin(value: unknown): 'activity' | 'global' {
  return String(value ?? '').trim().toLowerCase() === 'activity' ? 'activity' : 'global'
}

function normalizeBuyerRotatingSearchTerm(value: unknown): BuyerRotatingSearchTerm | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null

  const record = value as Record<string, unknown>
  const label = normalizeCheckoutText(record.label ?? record.termo ?? record.nome)
  if (!label) return null

  const produtoId = normalizeEntidadeId(record.produto_id ?? record.produtoId)
  return {
    label,
    ...(produtoId ? { produtoId } : {}),
    origem: normalizeBuyerRotatingSearchTermOrigin(record.origem ?? record.origin),
  }
}

function normalizeBuyerActivityValue(value: unknown): string {
  if (typeof value === 'string') {
    return normalizeCheckoutText(value)
  }

  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const record = value as Record<string, unknown>
    return normalizeCheckoutText(
      record.descricao
      ?? record.description
      ?? record.nome
      ?? record.name
      ?? record.label
      ?? record.titulo
      ?? record.title
    )
  }

  return ''
}

function mapBuyerStoreUser(value: Record<string, unknown>): BuyerStoreUser {
  const markupPercentual = normalizeFreteNumber(
    value.loja_virtual_markup_percentual
    ?? value.lojaVirtualMarkupPercentual
    ?? value.markup_percentual
    ?? value.markupPercentual
  )
  return {
    id: normalizeEntidadeId(value.id) ?? 0,
    nome: normalizeCheckoutText(value.nome),
    email: normalizeCheckoutText(value.email),
    telefone: normalizeCheckoutText(value.telefone),
    cargo: normalizeCheckoutText(value.cargo),
    acessoLojaAtivo: parseBooleanLike(value.acesso_loja_ativo ?? value.acessoLojaAtivo),
    senhaConfigurada: parseBooleanLike(value.senha_configurada ?? value.senhaConfigurada),
    inativo: parseBooleanLike(value.inativo),
    tipoUsuario: String(
      value.loja_virtual_tipo_usuario
      ?? value.lojaVirtualTipoUsuario
      ?? value.tipo_usuario
      ?? value.tipoUsuario
      ?? ''
    ).trim().toLowerCase() === 'vendedor' ? 'vendedor' : 'admin',
    markupPercentual: typeof markupPercentual === 'number' && Number.isFinite(markupPercentual)
      ? Number(markupPercentual.toFixed(2))
      : 0,
    acessoMarketplaceAnuncios: parseBooleanLike(value.acessoMarketplaceAnuncios ?? value.marketplace_anuncios_habilitar),
    acessoMarketplacePerguntas: parseBooleanLike(value.acessoMarketplacePerguntas ?? value.marketplace_perguntas_habilitar),
    isSelf: parseBooleanLike(value.is_self ?? value.isSelf),
  }
}

function resolveBuyerBillingAddressRecord(record: Record<string, unknown>): Record<string, unknown> | null {
  const candidates = [
    record.endereco_cobranca,
    record.enderecoCobranca,
    record.endereco_faturamento,
    record.enderecoFaturamento,
    record.billing_address,
    record.billingAddress,
    record.cobranca,
    record.endereco && typeof record.endereco === 'object' ? record.endereco : null,
  ]

  for (const candidate of candidates) {
    if (candidate && typeof candidate === 'object' && !Array.isArray(candidate)) {
      return candidate as Record<string, unknown>
    }
  }

  return null
}

export async function getBuyerAccessStatus(entidadeId?: number): Promise<BuyerAccessStatus | null> {
  const resolvedEntidadeId = normalizeEntidadeId(entidadeId) ?? getAuthenticatedEntidadeId()
  if (!resolvedEntidadeId) return null

  try {
    const response = await fetchApi<any>(
      `/loja-virtual/v1/compradores/${resolvedEntidadeId}/acesso`,
      { suppressBuyerSessionExpiry: true }
    )
    const record = response && typeof response === 'object' ? response as Record<string, unknown> : {}
    const status = Number.parseInt(String(record.status ?? ''), 10)
    const hasExplicitActive = [
      'acesso_ativo',
      'acessoAtivo',
      'ativo',
      'active',
    ].some((key) => Object.prototype.hasOwnProperty.call(record, key))
    const explicitActive = parseBooleanLike(
      record.acesso_ativo
      ?? record.acessoAtivo
      ?? record.ativo
      ?? record.active
    )
    const blocked = parseBooleanLike(record.bloqueado ?? record.blocked)
    const active = status === 1 && !blocked && (!hasExplicitActive || explicitActive)
    const priceTableAccessCode = active ? resolveBuyerPriceTableAccessCode(record) : ''
    const priceTableRedirectPath = priceTableAccessCode
      ? normalizeCheckoutText(record.price_table_redirect_path ?? record.priceTableRedirectPath)
      : ''
    const priceTableExpiresAt = priceTableAccessCode
      ? normalizeCheckoutText(record.price_table_expires_at ?? record.priceTableExpiresAt)
      : ''

    return {
      entidadeId: normalizeEntidadeId(record.entidade_id ?? record.entidadeId) ?? resolvedEntidadeId,
      active,
      reason: normalizeCheckoutText(record.motivo ?? record.reason ?? record.message) || undefined,
      priceTableAccessCode: priceTableAccessCode || undefined,
      priceTableRedirectPath: priceTableRedirectPath || undefined,
      priceTableExpiresAt: priceTableExpiresAt || undefined,
    }
  } catch (error) {
    if (isAuthenticationFailureError(error)) {
      return null
    }

    console.warn('Failed to validate buyer access status:', error)
    return null
  }
}

export async function createBuyerPriceTableAccessLink(
  entidadeId?: number | null
): Promise<BuyerPriceTableAccessLink | null> {
  const resolvedEntidadeId = normalizeEntidadeId(entidadeId) ?? getAuthenticatedEntidadeId()
  if (!resolvedEntidadeId) return null

  const response = await fetchApi<any>(
    '/loja-virtual/v1/auth/comprador/acessos',
    {
      method: 'POST',
      body: JSON.stringify({
        entidade_id: resolvedEntidadeId,
        loja_id: getLojaIdValue(),
        redirect_path: BUYER_PRICE_TABLE_REDIRECT_PATH,
        ...(typeof window !== 'undefined' && window.location?.origin
          ? { base_url: window.location.origin }
          : {}),
      }),
    },
  )

  const record = response && typeof response === 'object' ? response as Record<string, unknown> : {}
  const accessCode = resolveBuyerPriceTableAccessCode(record)
  if (!accessCode) return null

  return {
    accessCode,
    redirectPath: normalizeCheckoutText(record.redirect_path ?? record.redirectPath)
      || `${BUYER_PRICE_TABLE_REDIRECT_PATH}/${accessCode}`,
    shortPath: normalizeCheckoutText(record.short_path ?? record.shortPath) || undefined,
    shortUrl: normalizeCheckoutText(record.short_url ?? record.shortUrl) || undefined,
    expiresAt: normalizeCheckoutText(record.expires_at ?? record.expiresAt) || undefined,
  }
}

export async function getBuyerAccountSummary(): Promise<BuyerAccountSummary | null> {
  try {
    const entidadeId = resolveAuthenticatedEntidadeIdOrThrow('Resumo do comprador')
    const response = await fetchApi<any>(
      `/loja-virtual/v1/compradores/${entidadeId}/dados`,
      { suppressBuyerSessionExpiry: true }
    )
    const comprador = response?.comprador && typeof response.comprador === 'object' ? response.comprador : response
    if (!comprador || typeof comprador !== 'object') return null
    const buyerUser = normalizeBuyerUserPayload(response?.usuarioLoja ?? response?.usuario_loja)
    const commerceContext = normalizeBuyerCommerceContext(
      response?.commerce_context ?? response?.commerceContext
    )

    const record = comprador as Record<string, unknown>
    const billingAddressRecord = resolveBuyerBillingAddressRecord(record)
    const checkoutProfile = buildCheckoutCustomerProfile({
      id: entidadeId,
      ...record,
      ...(billingAddressRecord
        ? {
            endereco:
              billingAddressRecord.endereco
              ?? billingAddressRecord.logradouro
              ?? billingAddressRecord.rua
              ?? record.endereco
              ?? record.logradouro
              ?? record.rua,
            numero: billingAddressRecord.numero ?? record.numero,
            complemento: billingAddressRecord.complemento ?? record.complemento,
            bairro: billingAddressRecord.bairro ?? record.bairro,
            cidade: billingAddressRecord.cidade ?? record.cidade,
            cep: billingAddressRecord.cep ?? record.cep,
            uf: billingAddressRecord.uf ?? billingAddressRecord.estado ?? record.uf ?? record.estado,
            estado: billingAddressRecord.estado ?? billingAddressRecord.uf ?? record.estado ?? record.uf,
          }
        : {}),
    })
    if (!checkoutProfile) return null

    return {
      entidadeId,
      corporateName: checkoutProfile.corporateName,
      name: checkoutProfile.name,
      email: checkoutProfile.email,
      document: checkoutProfile.document,
      activity: checkoutProfile.activity || normalizeBuyerActivityValue(
        record.atividade
        ?? record.atividade_descricao
        ?? record.atividadeDescricao
        ?? record.tipo_entidade_atividade_descricao
        ?? record.tipoEntidadeAtividadeDescricao
        ?? record.ramo_atividade
        ?? record.ramoAtividade
        ?? record.segmento
        ?? record.segmento_nome
      ),
      stateRegistration: checkoutProfile.stateRegistration,
      phone: checkoutProfile.phone,
      street: checkoutProfile.street,
      number: checkoutProfile.number,
      complement: checkoutProfile.complement,
      neighborhood: checkoutProfile.neighborhood,
      city: checkoutProfile.city,
      state: checkoutProfile.state,
      zipCode: checkoutProfile.zipCode,
      buyerSessionToken: buyerUser.buyerSessionToken,
      commerceContext,
      buyerUser: {
        contatoId: buyerUser.contatoId,
        nome: buyerUser.nome,
        tipoUsuario: buyerUser.tipoUsuario,
        papel: buyerUser.papel,
        markupPercentual: buyerUser.markupPercentual,
        acessoFinanceiro: buyerUser.acessoFinanceiro,
        acessoPedidos: buyerUser.acessoPedidos,
        acessoUsuarios: buyerUser.acessoUsuarios,
        acessoMarketplaceAnuncios: buyerUser.acessoMarketplaceAnuncios,
        acessoMarketplacePerguntas: buyerUser.acessoMarketplacePerguntas,
        modoAcesso: buyerUser.modoAcesso,
      }
    }
  } catch {
    return null
  }
}

export async function getBuyerProfile(
  entidadeId?: number | null
): Promise<BuyerProfileData | null> {
  const resolvedEntidadeId = normalizeEntidadeId(entidadeId) ?? getAuthenticatedEntidadeId()
  if (!resolvedEntidadeId) return null

  const response = await fetchApi<any>(
    `/loja-virtual/v1/compradores/${resolvedEntidadeId}/perfil`
  )

  const perfil = response?.perfil
  if (!perfil || typeof perfil !== 'object' || Array.isArray(perfil)) {
    return {}
  }

  return perfil as BuyerProfileData
}

export async function saveBuyerProfile(
  perfil: BuyerProfileData,
  entidadeId?: number | null
): Promise<BuyerProfileData> {
  const resolvedEntidadeId = normalizeEntidadeId(entidadeId) ?? getAuthenticatedEntidadeId()
  if (!resolvedEntidadeId) {
    throw new Error('Comprador inválido para salvar o perfil.')
  }

  const response = await fetchApi<any>(
    `/loja-virtual/v1/compradores/${resolvedEntidadeId}/perfil`,
    {
      method: 'POST',
      body: JSON.stringify({ perfil }),
    }
  )

  const responsePerfil = response?.perfil
  if (!responsePerfil || typeof responsePerfil !== 'object' || Array.isArray(responsePerfil)) {
    return perfil
  }

  return responsePerfil as BuyerProfileData
}

export async function getBuyerRotatingSearchTerms(
  entidadeId?: number | null
): Promise<BuyerRotatingSearchTermsResponse | null> {
  const resolvedEntidadeId = normalizeEntidadeId(entidadeId) ?? getAuthenticatedEntidadeId()
  if (!resolvedEntidadeId) return null

  try {
    const response = await fetchApi<any>(
      `/loja-virtual/v1/compradores/${resolvedEntidadeId}/busca/termos-rotativos`
    )
    const record = response && typeof response === 'object' ? response as Record<string, unknown> : {}

    return {
      atividadeId: normalizeEntidadeId(record.atividade_id ?? record.atividadeId) ?? null,
      snapshotDate: normalizeCheckoutText(record.snapshot_date ?? record.snapshotDate) || null,
      termos: Array.isArray(record.termos)
        ? record.termos
          .map((item) => normalizeBuyerRotatingSearchTerm(item))
          .filter((item): item is BuyerRotatingSearchTerm => Boolean(item))
        : [],
    }
  } catch (error) {
    if (isAuthenticationFailureError(error)) {
      clearAuthenticatedEntidadeId()
      return null
    }

    console.warn('Failed to load buyer rotating search terms:', error)
    return null
  }
}

export async function getStorefrontRotatingSearchTerms(
  lojaId?: number | null
): Promise<StorefrontRotatingSearchTermsResponse | null> {
  const resolvedLojaId = getLojaIdValue(typeof lojaId === 'number' ? lojaId : undefined)
  if (!resolvedLojaId) return null

  try {
    const response = await fetchApi<any>(
      `/loja-virtual/v1/lojas/${resolvedLojaId}/busca/termos-rotativos`
    )
    const record = response && typeof response === 'object' ? response as Record<string, unknown> : {}

    return {
      atividadeId: normalizeEntidadeId(record.atividade_id ?? record.atividadeId) ?? null,
      snapshotDate: normalizeCheckoutText(record.snapshot_date ?? record.snapshotDate) || null,
      termos: Array.isArray(record.termos)
        ? record.termos
          .map((item) => normalizeBuyerRotatingSearchTerm(item))
          .filter((item): item is BuyerRotatingSearchTerm => Boolean(item))
        : [],
    }
  } catch (error) {
    console.warn('Failed to load storefront rotating search terms:', error)
    return null
  }
}

export async function getBuyerStoreUsers(): Promise<BuyerStoreUser[]> {
  const entidadeId = resolveAuthenticatedEntidadeIdOrThrow('Usuários da conta')
  const response = await fetchApi<any>(
    `/loja-virtual/v1/compradores/${entidadeId}/usuarios`
  )
  const items = Array.isArray(response?.items) ? response.items : []
  return items
    .filter((item): item is Record<string, unknown> => Boolean(item) && typeof item === 'object')
    .map(mapBuyerStoreUser)
}

export async function createBuyerStoreUser(input: BuyerStoreUserInput): Promise<BuyerStoreUser | null> {
  const entidadeId = resolveAuthenticatedEntidadeIdOrThrow('Criação de usuário da conta')
  const response = await fetchApi<any>(
    `/loja-virtual/v1/compradores/${entidadeId}/usuarios`,
    {
      method: 'POST',
      body: JSON.stringify({
        nome: input.nome.trim(),
        email: typeof input.email === 'string' ? input.email.trim() : '',
        telefone: typeof input.telefone === 'string' ? normalizePhoneToStoredDigits(input.telefone) : '',
        cargo: typeof input.cargo === 'string' ? input.cargo.trim() : '',
        acessoLojaAtivo: input.acessoLojaAtivo === false ? 0 : 1,
        ...(input.senha ? { senha: input.senha } : {}),
        lojaVirtualTipoUsuario: input.tipoUsuario === 'vendedor' ? 'vendedor' : 'admin',
        lojaVirtualMarkupPercentual: Number(input.markupPercentual ?? 0),
        acessoMarketplaceAnuncios: input.acessoMarketplaceAnuncios ? 1 : 0,
        acessoMarketplacePerguntas: input.acessoMarketplacePerguntas ? 1 : 0,
        inativo: input.inativo ? 1 : 0,
      }),
    }
  )
  return response?.item && typeof response.item === 'object'
    ? mapBuyerStoreUser(response.item as Record<string, unknown>)
    : null
}

export async function updateBuyerStoreUser(
  contatoId: number,
  input: BuyerStoreUserInput
): Promise<BuyerStoreUser | null> {
  const entidadeId = resolveAuthenticatedEntidadeIdOrThrow('Atualização de usuário da conta')
  const response = await fetchApi<any>(
    `/loja-virtual/v1/compradores/${entidadeId}/usuarios/${encodeURIComponent(String(Math.trunc(contatoId)))}`,
    {
      method: 'PUT',
      body: JSON.stringify({
        nome: input.nome.trim(),
        email: typeof input.email === 'string' ? input.email.trim() : '',
        telefone: typeof input.telefone === 'string' ? normalizePhoneToStoredDigits(input.telefone) : '',
        cargo: typeof input.cargo === 'string' ? input.cargo.trim() : '',
        acessoLojaAtivo: input.acessoLojaAtivo ? 1 : 0,
        ...(input.senha ? { senha: input.senha } : {}),
        lojaVirtualTipoUsuario: input.tipoUsuario === 'vendedor' ? 'vendedor' : 'admin',
        lojaVirtualMarkupPercentual: Number(input.markupPercentual ?? 0),
        acessoMarketplaceAnuncios: input.acessoMarketplaceAnuncios ? 1 : 0,
        acessoMarketplacePerguntas: input.acessoMarketplacePerguntas ? 1 : 0,
        inativo: input.inativo ? 1 : 0,
      }),
    }
  )
  return response?.item && typeof response.item === 'object'
    ? mapBuyerStoreUser(response.item as Record<string, unknown>)
    : null
}

export type MarketplaceVitrinePerfil = {
  id: number | null
  nomeComercial: string
  whatsapp: string
  telefone: string
  email: string
  linkExterno: string
  consentimentoContatoPublico: boolean
  consentimentoEm: string | null
}

export type MarketplaceVitrineFoto = {
  id: number
  url: string
  ordem: number
  principal: boolean
}

export type MarketplaceVitrineVideo = {
  id: number
  url: string
  contentType: string
  tamanhoBytes: number
}

export type MarketplaceVitrineManual = {
  id: number
  url: string
  nomeArquivo: string
  contentType: string
  tamanhoBytes: number
}

export type MarketplaceVitrineCategoria = {
  id: string
  nome: string
  caminho: string
}

export type MarketplaceVitrineVariacao = {
  id: number | null
  codigo: string
  ean: string | null
  marca: string | null
  modelo: string | null
  condicao: MarketplaceVitrineCondicao | null
  descricao: string | null
  atributos: Record<string, string>
  estoque: number
  preco: number | null
  descontoPercentual: number
  ordem: number
  fotoId: number | null
  fotoUrl: string | null
  fotos: MarketplaceVitrineFoto[]
  video: MarketplaceVitrineVideo | null
  manual: MarketplaceVitrineManual | null
  manuais: MarketplaceVitrineManual[]
}

export type MarketplaceVitrineAnuncioStatus = 'rascunho' | 'ativo' | 'pausado' | 'bloqueado' | 'arquivado'
export type MarketplaceVitrinePrecoTipo = 'preco_informado' | 'sob_consulta'
export type MarketplaceVitrineCondicao = 'novo' | 'usado'
export type MarketplaceVitrineContatoCanal = 'whatsapp' | 'telefone' | 'email' | 'link'

export type MarketplaceVitrineMetricasCanais = {
  whatsapp: number
  telefone: number
  email: number
  linkExterno: number
}

export type MarketplaceVitrineMetricasResumo = {
  aberturas: number
  acoes: number
  conversao: number
  canais: MarketplaceVitrineMetricasCanais
}

export type MarketplaceVitrineMetricasDiarias = {
  data: string
  aberturas: number
  acoes: number
}

export type MarketplaceVitrineMetricasAnuncio = MarketplaceVitrineMetricasResumo & {
  anuncioId: number
}

export type MarketplaceVitrineMetricas = {
  periodo: { inicio: string; fim: string; dadosDesde: string | null }
  totais: MarketplaceVitrineMetricasResumo
  serie: MarketplaceVitrineMetricasDiarias[]
  anuncios: MarketplaceVitrineMetricasAnuncio[]
}

export type MarketplaceVitrineAnuncio = {
  id: number
  codigo: string
  titulo: string
  descricao: string
  produtoGrupoId: number | null
  produtoGrupoNome: string | null
  categoriaMarketplaceId: string | null
  marca: string | null
  modelo: string | null
  condicao: MarketplaceVitrineCondicao
  cidade: string | null
  uf: string | null
  estoqueStatus: 'disponivel' | 'esgotado'
  estoque: number | null
  disponivel: boolean
  precoTipo: MarketplaceVitrinePrecoTipo
  preco: number | null
  descontoPercentual: number
  status: MarketplaceVitrineAnuncioStatus
  bloqueioMotivo: string | null
  vendedorNome: string
  vendedorEntidadeId: number | null
  contatoPrincipal: MarketplaceVitrineContatoCanal | null
  contatos: { whatsapp: string | null; telefone: string | null; email: string | null; link: string | null }
  metricas: { visualizacoes: number; cliquesContato: number }
  imagem: string | null
  fotos: MarketplaceVitrineFoto[]
  video: MarketplaceVitrineVideo | null
  manual: MarketplaceVitrineManual | null
  manuais: MarketplaceVitrineManual[]
  variacoes: MarketplaceVitrineVariacao[]
  publicadoEm: string | null
  atualizadoEm: string | null
}

export type MarketplaceVitrinePublicSeller = {
  entidadeId: number
  nome: string
}

export type MarketplaceVitrinePergunta = {
  id: number
  anuncioId: number
  codigo: string
  titulo: string
  autor: string
  visitanteNome: string
  visitanteWhatsapp: string
  pergunta: string
  resposta: string
  criadoEm: string | null
  respondidoEm: string | null
}

export type MarketplaceVitrineAnuncioInput = {
  codigo: string
  titulo: string
  descricao: string
  produtoGrupoId?: number | null
  categoria?: string | null
  categoriaMarketplaceId?: string | null
  marca?: string | null
  modelo?: string | null
  condicao: MarketplaceVitrineCondicao
  cidade?: string | null
  uf?: string | null
  estoque: number
  precoTipo: MarketplaceVitrinePrecoTipo
  preco?: number | null
  descontoPercentual?: number
  exibirWhatsapp: boolean
  exibirTelefone: boolean
  exibirEmail: boolean
  exibirLink: boolean
  contatoPrincipal?: MarketplaceVitrineContatoCanal | null
  status: Exclude<MarketplaceVitrineAnuncioStatus, 'bloqueado'>
}

function mapMarketplaceVitrineFoto(value: unknown): MarketplaceVitrineFoto | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  const record = value as Record<string, unknown>
  const id = normalizeEntidadeId(record.id)
  const url = normalizeCheckoutText(record.url)
  if (!id || !url) return null
  return {
    id,
    url,
    ordem: Math.max(0, Math.trunc(normalizeFreteNumber(record.ordem) ?? 0)),
    principal: parseBooleanLike(record.principal),
  }
}

function mapMarketplaceVitrineVideo(value: unknown): MarketplaceVitrineVideo | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  const record = value as Record<string, unknown>
  const id = normalizeEntidadeId(record.id)
  const url = normalizeCheckoutText(record.url)
  if (!id || !url) return null
  return {
    id,
    url,
    contentType: normalizeCheckoutText(record.content_type ?? record.contentType),
    tamanhoBytes: Math.max(0, Math.trunc(normalizeFreteNumber(record.tamanho_bytes ?? record.tamanhoBytes) ?? 0)),
  }
}

function mapMarketplaceVitrineManual(value: unknown): MarketplaceVitrineManual | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  const record = value as Record<string, unknown>
  const id = normalizeEntidadeId(record.id)
  const url = normalizeCheckoutText(record.url)
  if (!id || !url) return null
  return {
    id,
    url,
    nomeArquivo: normalizeCheckoutText(record.nome_arquivo ?? record.nomeArquivo) || 'Manual de instruções.pdf',
    contentType: normalizeCheckoutText(record.content_type ?? record.contentType) || 'application/pdf',
    tamanhoBytes: Math.max(0, Math.trunc(normalizeFreteNumber(record.tamanho_bytes ?? record.tamanhoBytes) ?? 0)),
  }
}

function mapMarketplaceVitrineVariacao(value: unknown): MarketplaceVitrineVariacao | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  const record = value as Record<string, unknown>
  const codigo = normalizeCheckoutText(record.codigo)
  const rawAttributes = record.atributos && typeof record.atributos === 'object' && !Array.isArray(record.atributos)
    ? record.atributos as Record<string, unknown>
    : {}
  const atributos = Object.fromEntries(
    Object.entries(rawAttributes)
      .map(([name, attributeValue]) => [normalizeCheckoutText(name), normalizeCheckoutText(attributeValue)])
      .filter(([name, attributeValue]) => Boolean(name && attributeValue)),
  )
  if (!codigo || Object.keys(atributos).length === 0) return null
  const manuais = Array.isArray(record.manuais)
    ? record.manuais.map(mapMarketplaceVitrineManual).filter((item): item is MarketplaceVitrineManual => Boolean(item))
    : []
  const manual = mapMarketplaceVitrineManual(record.manual) ?? manuais[0] ?? null
  return {
    id: normalizeEntidadeId(record.id) ?? null,
    codigo,
    ean: normalizeCheckoutText(record.ean) || null,
    marca: normalizeCheckoutText(record.marca) || null,
    modelo: normalizeCheckoutText(record.modelo) || null,
    condicao: normalizeCheckoutText(record.condicao).toLocaleLowerCase('pt-BR') === 'usado'
      ? 'usado'
      : normalizeCheckoutText(record.condicao).toLocaleLowerCase('pt-BR') === 'novo'
        ? 'novo'
        : null,
    descricao: normalizeCheckoutText(record.descricao) || null,
    atributos,
    estoque: Math.max(0, normalizeFreteNumber(record.estoque) ?? 0),
    preco: normalizeFreteNumber(record.preco) ?? null,
    descontoPercentual: Math.max(0, Math.min(99.99, normalizeFreteNumber(record.desconto_percentual ?? record.descontoPercentual) ?? 0)),
    ordem: Math.max(0, Math.trunc(normalizeFreteNumber(record.ordem) ?? 0)),
    fotoId: normalizeEntidadeId(record.foto_id ?? record.fotoId) ?? null,
    fotoUrl: normalizeCheckoutText(record.foto_url ?? record.fotoUrl) || null,
    fotos: Array.isArray(record.fotos)
      ? record.fotos.map(mapMarketplaceVitrineFoto).filter((item): item is MarketplaceVitrineFoto => Boolean(item))
      : [],
    video: mapMarketplaceVitrineVideo(record.video),
    manual,
    manuais: manuais.length > 0 ? manuais : manual ? [manual] : [],
  }
}

const sortMarketplaceVitrineVariacoes = (variacoes: MarketplaceVitrineVariacao[]) => [...variacoes].sort((first, second) => {
  const orderDifference = first.ordem - second.ordem
  if (orderDifference !== 0) return orderDifference

  const idDifference = (first.id ?? Number.MAX_SAFE_INTEGER) - (second.id ?? Number.MAX_SAFE_INTEGER)
  if (idDifference !== 0) return idDifference
  return first.codigo.localeCompare(second.codigo, 'pt-BR')
})

function mapMarketplaceVitrineAnuncio(value: unknown): MarketplaceVitrineAnuncio | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  const record = value as Record<string, unknown>
  const id = normalizeEntidadeId(record.anuncio_id ?? record.id)
  if (!id) return null
  const vendedor = record.vendedor && typeof record.vendedor === 'object' && !Array.isArray(record.vendedor)
    ? record.vendedor as Record<string, unknown>
    : {}
  const contatos = vendedor.contatos && typeof vendedor.contatos === 'object' && !Array.isArray(vendedor.contatos)
    ? vendedor.contatos as Record<string, unknown>
    : {}
  const rawStatus = normalizeCheckoutText(record.status)
  const status: MarketplaceVitrineAnuncioStatus = ['ativo', 'pausado', 'bloqueado', 'arquivado'].includes(rawStatus)
    ? rawStatus as MarketplaceVitrineAnuncioStatus
    : 'rascunho'
  const precoTipo: MarketplaceVitrinePrecoTipo = record.preco_tipo === 'preco_informado' ? 'preco_informado' : 'sob_consulta'
  const preco = precoTipo === 'preco_informado' ? normalizeFreteNumber(record.preco) : null
  const estoque = normalizeFreteNumber(record.estoque)
  const contatoPrincipalRaw = normalizeCheckoutText(vendedor.contato_principal)
  const contatoPrincipal = ['whatsapp', 'telefone', 'email', 'link'].includes(contatoPrincipalRaw)
    ? contatoPrincipalRaw as MarketplaceVitrineContatoCanal
    : null
  const metricas = record.metricas && typeof record.metricas === 'object' && !Array.isArray(record.metricas)
    ? record.metricas as Record<string, unknown>
    : {}
  const manuais = Array.isArray(record.manuais)
    ? record.manuais.map(mapMarketplaceVitrineManual).filter((item): item is MarketplaceVitrineManual => Boolean(item))
    : []
  const manual = mapMarketplaceVitrineManual(record.manual) ?? manuais[0] ?? null
  return {
    id,
    codigo: normalizeCheckoutText(record.codigo),
    titulo: normalizeCheckoutText(record.titulo ?? record.nome),
    descricao: normalizeCheckoutText(record.descricao),
    produtoGrupoId: normalizeEntidadeId(record.produto_grupo_id) ?? null,
    produtoGrupoNome: normalizeCheckoutText(record.produto_grupo_nome) || null,
    categoriaMarketplaceId: normalizeCheckoutText(record.categoria_marketplace_id ?? record.categoriaMarketplaceId) || null,
    marca: normalizeCheckoutText(record.marca) || null,
    modelo: normalizeCheckoutText(record.modelo) || null,
    condicao: record.condicao === 'usado' ? 'usado' : 'novo',
    cidade: normalizeCheckoutText(record.cidade) || null,
    uf: normalizeCheckoutText(record.uf).toUpperCase() || null,
    estoqueStatus: record.estoque_status === 'esgotado' ? 'esgotado' : 'disponivel',
    estoque: typeof estoque === 'number' ? estoque : null,
    disponivel: parseBooleanLike(record.disponivel),
    precoTipo,
    preco: typeof preco === 'number' && Number.isFinite(preco) ? preco : null,
    descontoPercentual: Math.max(0, Math.min(99.99, normalizeFreteNumber(record.desconto_percentual ?? record.descontoPercentual) ?? 0)),
    status,
    bloqueioMotivo: normalizeCheckoutText(record.bloqueio_motivo) || null,
    vendedorNome: normalizeCheckoutText(vendedor.nome),
    vendedorEntidadeId: normalizeEntidadeId(record.entidade_id ?? record.entidadeId) ?? null,
    contatoPrincipal,
    contatos: {
      whatsapp: normalizeCheckoutText(contatos.whatsapp) || null,
      telefone: normalizeCheckoutText(contatos.telefone) || null,
      email: normalizeCheckoutText(contatos.email) || null,
      link: normalizeCheckoutText(contatos.link) || null,
    },
    metricas: {
      visualizacoes: Math.max(0, Math.trunc(normalizeFreteNumber(metricas.visualizacoes) ?? 0)),
      cliquesContato: Math.max(0, Math.trunc(normalizeFreteNumber(metricas.cliques_contato ?? metricas.cliquesContato) ?? 0)),
    },
    imagem: normalizeCheckoutText(record.imagem) || null,
    fotos: Array.isArray(record.fotos)
      ? record.fotos.map(mapMarketplaceVitrineFoto).filter((item): item is MarketplaceVitrineFoto => Boolean(item))
      : [],
    video: mapMarketplaceVitrineVideo(record.video),
    manual,
    manuais: manuais.length > 0 ? manuais : manual ? [manual] : [],
    variacoes: sortMarketplaceVitrineVariacoes(Array.isArray(record.variacoes)
      ? record.variacoes.map(mapMarketplaceVitrineVariacao).filter((item): item is MarketplaceVitrineVariacao => Boolean(item))
      : []),
    publicadoEm: normalizeCheckoutText(record.publicado_em) || null,
    atualizadoEm: normalizeCheckoutText(record.atualizado_em) || null,
  }
}

export function marketplaceVitrineAnuncioToProduct(anuncio: MarketplaceVitrineAnuncio): Product {
  const representativeVariation = anuncio.variacoes[0] ?? null
  const representativePhotos = representativeVariation?.fotos.length
    ? representativeVariation.fotos
    : anuncio.fotos
  const image = representativePhotos[0]?.url || representativeVariation?.fotoUrl || anuncio.imagem || ''
  const price = representativeVariation?.preco ?? anuncio.preco
  const discountPercent = representativeVariation?.descontoPercentual ?? anuncio.descontoPercentual
  const finalPrice = price == null ? null : Number(Math.max(0.01, price * (1 - discountPercent / 100)).toFixed(2))
  return {
    id: `marketplace-${anuncio.id}`,
    origem: 'marketplace_vitrine',
    acaoPrincipal: 'contato_externo',
    marketplaceAnuncioId: anuncio.id,
    marketplaceSellerName: anuncio.vendedorNome,
    marketplacePriceType: anuncio.precoTipo,
    marketplaceContacts: anuncio.contatos,
    code: representativeVariation?.codigo || anuncio.codigo,
    manufacturerCode: representativeVariation?.codigo || anuncio.codigo,
    name: anuncio.titulo,
    brand: representativeVariation?.marca || anuncio.marca || 'Sem marca',
    category: anuncio.produtoGrupoNome || 'Marketplace',
    subcategory: 'Anúncio de cliente',
    image,
    images: representativePhotos.map((foto) => foto.url),
    specs: {},
    inStock: anuncio.disponivel,
    stockQuantity: null,
    priceVisible: true,
    needsQuote: anuncio.precoTipo === 'sob_consulta',
    price: finalPrice ?? undefined,
    originalPrice: price ?? undefined,
    discountedPrice: finalPrice ?? undefined,
  }
}

export async function getMarketplaceVitrinePublicAnuncios(params: {
  q?: string
  produtoGrupoId?: number | null
  limit?: number
  offset?: number
  lojaId?: number
  onlyAvailable?: boolean
  signal?: AbortSignal
} = {}): Promise<{ items: MarketplaceVitrineAnuncio[]; total: number; limit: number; offset: number }> {
  const query = new URLSearchParams()
  query.set('loja_id', String(params.lojaId ?? getLojaIdValue() ?? 101))
  query.set('limit', String(Math.max(1, Math.min(60, Math.trunc(params.limit ?? 24)))))
  query.set('offset', String(Math.max(0, Math.trunc(params.offset ?? 0))))
  if (params.q?.trim()) query.set('q', params.q.trim())
  if (params.produtoGrupoId && params.produtoGrupoId > 0) query.set('produto_grupo_id', String(Math.trunc(params.produtoGrupoId)))
  if (params.onlyAvailable) query.set('somente_disponiveis', '1')
  const response = await fetchApi<any>(
    `/loja-virtual/v1/marketplace-vitrine/anuncios?${query.toString()}`,
    {
      signal: params.signal,
      // A Vitrine é uma fonte secundária da busca. Sua indisponibilidade não
      // pode colocar a loja principal inteira em modo de manutenção.
      trackBackendAvailability: false,
    },
  )
  const items = Array.isArray(response?.items)
    ? response.items.map(mapMarketplaceVitrineAnuncio).filter((item: MarketplaceVitrineAnuncio | null): item is MarketplaceVitrineAnuncio => Boolean(item))
    : []
  return {
    items,
    total: Math.max(0, Math.trunc(normalizeFreteNumber(response?.total) ?? items.length)),
    limit: Math.max(1, Math.trunc(normalizeFreteNumber(response?.limit) ?? params.limit ?? 24)),
    offset: Math.max(0, Math.trunc(normalizeFreteNumber(response?.offset) ?? params.offset ?? 0)),
  }
}

export async function getMarketplaceVitrinePublicSellerAnuncios(
  entidadeId: number,
  params: {
    limit?: number
    offset?: number
    lojaId?: number
    signal?: AbortSignal
  } = {},
): Promise<{ vendedor: MarketplaceVitrinePublicSeller; items: MarketplaceVitrineAnuncio[]; total: number; limit: number; offset: number }> {
  const normalizedEntidadeId = Math.max(0, Math.trunc(entidadeId))
  if (normalizedEntidadeId <= 0) throw new Error('Vendedor inválido.')

  const query = new URLSearchParams()
  query.set('loja_id', String(params.lojaId ?? getLojaIdValue() ?? 101))
  query.set('limit', String(Math.max(1, Math.min(200, Math.trunc(params.limit ?? 200)))))
  query.set('offset', String(Math.max(0, Math.trunc(params.offset ?? 0))))
  const response = await fetchApi<any>(
    `/loja-virtual/v1/marketplace-vitrine/vendedores/${normalizedEntidadeId}/anuncios?${query.toString()}`,
    {
      signal: params.signal,
      trackBackendAvailability: false,
    },
  )
  const vendedor = response?.vendedor && typeof response.vendedor === 'object'
    ? response.vendedor as Record<string, unknown>
    : {}
  const sellerName = normalizeCheckoutText(vendedor.nome ?? vendedor.nome_comercial)
  const sellerId = normalizeEntidadeId(vendedor.entidade_id ?? vendedor.entidadeId) ?? normalizedEntidadeId
  if (!sellerName) throw new Error('Vendedor não encontrado.')

  const items = Array.isArray(response?.items)
    ? response.items.map(mapMarketplaceVitrineAnuncio).filter((item: MarketplaceVitrineAnuncio | null): item is MarketplaceVitrineAnuncio => Boolean(item))
    : []

  return {
    vendedor: { entidadeId: sellerId, nome: sellerName },
    items,
    total: Math.max(0, Math.trunc(normalizeFreteNumber(response?.total) ?? items.length)),
    limit: Math.max(1, Math.trunc(normalizeFreteNumber(response?.limit) ?? params.limit ?? 200)),
    offset: Math.max(0, Math.trunc(normalizeFreteNumber(response?.offset) ?? params.offset ?? 0)),
  }
}

export async function searchMarketplaceVitrineProductsPage(
  query: string,
  options: {
    limit?: number
    offset?: number
    lojaId?: number
    onlyAvailable?: boolean
    signal?: AbortSignal
  } = {},
): Promise<{ items: Product[]; total: number; limit: number; offset: number }> {
  const page = await getMarketplaceVitrinePublicAnuncios({
    q: query,
    limit: options.limit,
    offset: options.offset,
    lojaId: options.lojaId,
    onlyAvailable: options.onlyAvailable,
    signal: options.signal,
  })

  return {
    ...page,
    items: page.items.map(marketplaceVitrineAnuncioToProduct),
  }
}

export async function getMarketplaceVitrinePublicAnuncio(id: number): Promise<MarketplaceVitrineAnuncio> {
  const response = await fetchApi<any>(`/loja-virtual/v1/marketplace-vitrine/anuncios/${Math.trunc(id)}`, { cache: 'no-store' })
  const anuncio = mapMarketplaceVitrineAnuncio(response?.anuncio)
  if (!anuncio) throw new Error('Anúncio não encontrado.')
  return anuncio
}

export async function registerMarketplaceVitrineContactClick(id: number, canal: MarketplaceVitrineContatoCanal) {
  return fetchApi<any>(`/loja-virtual/v1/marketplace-vitrine/anuncios/${Math.trunc(id)}/contato`, {
    method: 'POST',
    keepalive: true,
    body: JSON.stringify({ canal }),
  })
}

export async function getMarketplaceVitrinePublicPerguntas(id: number): Promise<MarketplaceVitrinePergunta[]> {
  const response = await fetchApi<any>(`/loja-virtual/v1/marketplace-vitrine/anuncios/${Math.trunc(id)}/perguntas`)
  return (Array.isArray(response?.perguntas) ? response.perguntas : []).map((item: Record<string, unknown>) => ({
    id: normalizeEntidadeId(item.id) ?? 0,
    anuncioId: id,
    codigo: '',
    titulo: '',
    autor: normalizeCheckoutText(item.autor) || 'Cliente',
    visitanteNome: '',
    visitanteWhatsapp: '',
    pergunta: normalizeCheckoutText(item.pergunta),
    resposta: normalizeCheckoutText(item.resposta),
    criadoEm: normalizeCheckoutText(item.criado_em) || null,
    respondidoEm: normalizeCheckoutText(item.respondido_em) || null,
  }))
}

export async function sendMarketplaceVitrinePergunta(id: number, input: { pergunta: string; nome?: string; whatsapp?: string }) {
  return fetchApi<any>(`/loja-virtual/v1/marketplace-vitrine/anuncios/${Math.trunc(id)}/perguntas`, {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

export async function getMarketplaceVitrinePerfil(): Promise<MarketplaceVitrinePerfil> {
  const entidadeId = resolveAuthenticatedEntidadeIdOrThrow('Perfil do Marketplace')
  const response = await fetchApi<any>(`/loja-virtual/v1/compradores/${entidadeId}/marketplace-vitrine/perfil`)
  const perfil = response?.perfil ?? {}
  return {
    id: normalizeEntidadeId(perfil.id) ?? null,
    nomeComercial: normalizeCheckoutText(perfil.nome_comercial),
    whatsapp: normalizeCheckoutText(perfil.whatsapp),
    telefone: normalizeCheckoutText(perfil.telefone),
    email: normalizeCheckoutText(perfil.email),
    linkExterno: normalizeCheckoutText(perfil.link_externo),
    consentimentoContatoPublico: parseBooleanLike(perfil.consentimento_contato_publico),
    consentimentoEm: normalizeCheckoutText(perfil.consentimento_em) || null,
  }
}

export async function saveMarketplaceVitrinePerfil(input: Omit<MarketplaceVitrinePerfil, 'id' | 'consentimentoEm'>) {
  const entidadeId = resolveAuthenticatedEntidadeIdOrThrow('Perfil do Marketplace')
  return fetchApi<any>(`/loja-virtual/v1/compradores/${entidadeId}/marketplace-vitrine/perfil`, {
    method: 'PUT',
    body: JSON.stringify({
      nomeComercial: input.nomeComercial,
      whatsapp: input.whatsapp,
      telefone: input.telefone,
      email: input.email,
      linkExterno: input.linkExterno,
      consentimentoContatoPublico: input.consentimentoContatoPublico,
    }),
  })
}

export async function getMarketplaceVitrineSellerAnuncios(): Promise<MarketplaceVitrineAnuncio[]> {
  const entidadeId = resolveAuthenticatedEntidadeIdOrThrow('Anúncios do Marketplace')
  const response = await fetchApi<any>(`/loja-virtual/v1/compradores/${entidadeId}/marketplace-vitrine/anuncios`)
  return (Array.isArray(response?.items) ? response.items : [])
    .map(mapMarketplaceVitrineAnuncio)
    .filter((item: MarketplaceVitrineAnuncio | null): item is MarketplaceVitrineAnuncio => Boolean(item))
}

const mapMarketplaceVitrineMetricasResumo = (value: unknown): MarketplaceVitrineMetricasResumo => {
  const record = value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {}
  const canaisRecord = record.canais && typeof record.canais === 'object' && !Array.isArray(record.canais)
    ? record.canais as Record<string, unknown>
    : {}
  const canais: MarketplaceVitrineMetricasCanais = {
    whatsapp: Math.max(0, Math.trunc(normalizeFreteNumber(canaisRecord.whatsapp) ?? 0)),
    telefone: Math.max(0, Math.trunc(normalizeFreteNumber(canaisRecord.telefone) ?? 0)),
    email: Math.max(0, Math.trunc(normalizeFreteNumber(canaisRecord.email) ?? 0)),
    linkExterno: Math.max(0, Math.trunc(normalizeFreteNumber(canaisRecord.linkExterno ?? canaisRecord.link_externo) ?? 0)),
  }
  const aberturas = Math.max(0, Math.trunc(normalizeFreteNumber(record.aberturas) ?? 0))
  const acoes = Math.max(0, Math.trunc(normalizeFreteNumber(record.acoes) ?? (
    canais.whatsapp + canais.telefone + canais.email + canais.linkExterno
  )))
  const conversao = aberturas > 0
    ? Number((normalizeFreteNumber(record.conversao) ?? ((acoes / aberturas) * 100)).toFixed(2))
    : 0
  return { aberturas, acoes, conversao, canais }
}

export async function getMarketplaceVitrineMetricas(params: {
  inicio: string
  fim: string
}): Promise<MarketplaceVitrineMetricas> {
  const entidadeId = resolveAuthenticatedEntidadeIdOrThrow('Métricas do Marketplace')
  const query = new URLSearchParams({ inicio: params.inicio, fim: params.fim })
  const response = await fetchApi<any>(
    `/loja-virtual/v1/compradores/${entidadeId}/marketplace-vitrine/metricas?${query.toString()}`,
  )
  const periodo = response?.periodo && typeof response.periodo === 'object' && !Array.isArray(response.periodo)
    ? response.periodo as Record<string, unknown>
    : {}
  const serie = Array.isArray(response?.serie)
    ? response.serie.map((value: unknown): MarketplaceVitrineMetricasDiarias | null => {
        if (!value || typeof value !== 'object' || Array.isArray(value)) return null
        const record = value as Record<string, unknown>
        const data = normalizeCheckoutText(record.data)
        if (!data) return null
        return {
          data,
          aberturas: Math.max(0, Math.trunc(normalizeFreteNumber(record.aberturas) ?? 0)),
          acoes: Math.max(0, Math.trunc(normalizeFreteNumber(record.acoes) ?? 0)),
        }
      }).filter((value: MarketplaceVitrineMetricasDiarias | null): value is MarketplaceVitrineMetricasDiarias => Boolean(value))
    : []
  const anuncios = Array.isArray(response?.anuncios)
    ? response.anuncios.map((value: unknown): MarketplaceVitrineMetricasAnuncio | null => {
        if (!value || typeof value !== 'object' || Array.isArray(value)) return null
        const record = value as Record<string, unknown>
        const anuncioId = normalizeEntidadeId(record.anuncioId ?? record.anuncio_id)
        return anuncioId ? { anuncioId, ...mapMarketplaceVitrineMetricasResumo(record) } : null
      }).filter((value: MarketplaceVitrineMetricasAnuncio | null): value is MarketplaceVitrineMetricasAnuncio => Boolean(value))
    : []
  return {
    periodo: {
      inicio: normalizeCheckoutText(periodo.inicio) || params.inicio,
      fim: normalizeCheckoutText(periodo.fim) || params.fim,
      dadosDesde: normalizeCheckoutText(periodo.dadosDesde ?? periodo.dados_desde) || null,
    },
    totais: mapMarketplaceVitrineMetricasResumo(response?.totais),
    serie,
    anuncios,
  }
}

export async function getMarketplaceVitrineCategorias(params: {
  q?: string
  titulo?: string
  limit?: number
} = {}): Promise<{ categorias: MarketplaceVitrineCategoria[]; sugestao: MarketplaceVitrineCategoria | null }> {
  const entidadeId = resolveAuthenticatedEntidadeIdOrThrow('Categorias do Marketplace')
  const query = new URLSearchParams()
  if (params.q?.trim()) query.set('q', params.q.trim())
  if (params.titulo?.trim()) query.set('titulo', params.titulo.trim())
  if (params.limit) query.set('limit', String(Math.max(1, Math.trunc(params.limit))))
  const response = await fetchApi<any>(
    `/loja-virtual/v1/compradores/${entidadeId}/marketplace-vitrine/categorias?${query.toString()}`,
  )
  const mapCategory = (value: unknown): MarketplaceVitrineCategoria | null => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return null
    const record = value as Record<string, unknown>
    const id = normalizeCheckoutText(record.id)
    const nome = normalizeCheckoutText(record.nome ?? record.name)
    const caminho = normalizeCheckoutText(record.caminho ?? record.path) || nome
    return id && nome ? { id, nome, caminho } : null
  }
  const categorias = Array.isArray(response?.categorias)
    ? response.categorias.map(mapCategory).filter((item: MarketplaceVitrineCategoria | null): item is MarketplaceVitrineCategoria => Boolean(item))
    : []
  return { categorias, sugestao: mapCategory(response?.sugestao) }
}

export async function createMarketplaceVitrineAnuncio(input: MarketplaceVitrineAnuncioInput): Promise<number> {
  const entidadeId = resolveAuthenticatedEntidadeIdOrThrow('Criação de anúncio do Marketplace')
  const response = await fetchApi<any>(`/loja-virtual/v1/compradores/${entidadeId}/marketplace-vitrine/anuncios`, {
    method: 'POST',
    body: JSON.stringify(input),
  })
  const id = normalizeEntidadeId(response?.anuncio_id)
  if (!id) throw new Error('O anúncio foi salvo, mas não retornou um identificador.')
  return id
}

export async function updateMarketplaceVitrineAnuncio(id: number, input: MarketplaceVitrineAnuncioInput) {
  const entidadeId = resolveAuthenticatedEntidadeIdOrThrow('Atualização de anúncio do Marketplace')
  return fetchApi<any>(`/loja-virtual/v1/compradores/${entidadeId}/marketplace-vitrine/anuncios/${Math.trunc(id)}`, {
    method: 'PUT',
    body: JSON.stringify(input),
  })
}

export async function deleteMarketplaceVitrineAnuncio(id: number) {
  const entidadeId = resolveAuthenticatedEntidadeIdOrThrow('Exclusão de anúncio do Marketplace')
  return fetchApi<any>(`/loja-virtual/v1/compradores/${entidadeId}/marketplace-vitrine/anuncios/${Math.trunc(id)}`, {
    method: 'DELETE',
  })
}

export async function uploadMarketplaceVitrineFoto(anuncioId: number, file: File, variacaoId?: number | null): Promise<MarketplaceVitrineFoto> {
  const entidadeId = resolveAuthenticatedEntidadeIdOrThrow('Foto do anúncio do Marketplace')
  const body = new FormData()
  body.append('foto', file)
  const variationQuery = variacaoId ? `?variacaoId=${Math.trunc(variacaoId)}` : ''
  const response = await fetchApi<any>(`/loja-virtual/v1/compradores/${entidadeId}/marketplace-vitrine/anuncios/${Math.trunc(anuncioId)}/fotos${variationQuery}`, {
    method: 'POST',
    body,
  })
  const foto = mapMarketplaceVitrineFoto(response?.foto)
  if (!foto) throw new Error('A foto foi enviada, mas não retornou um identificador.')
  return foto
}

export async function deleteMarketplaceVitrineFoto(anuncioId: number, fotoId: number) {
  const entidadeId = resolveAuthenticatedEntidadeIdOrThrow('Foto do anúncio do Marketplace')
  return fetchApi<any>(`/loja-virtual/v1/compradores/${entidadeId}/marketplace-vitrine/anuncios/${Math.trunc(anuncioId)}/fotos/${Math.trunc(fotoId)}`, {
    method: 'DELETE',
  })
}

export async function reorderMarketplaceVitrineFotos(anuncioId: number, fotoIds: number[], variacaoId?: number | null) {
  const entidadeId = resolveAuthenticatedEntidadeIdOrThrow('Ordem das fotos do Marketplace')
  return fetchApi<any>(`/loja-virtual/v1/compradores/${entidadeId}/marketplace-vitrine/anuncios/${Math.trunc(anuncioId)}/fotos/ordem`, {
    method: 'PUT',
    body: JSON.stringify({ fotoIds, variacaoId: variacaoId ?? null }),
  })
}

export async function uploadMarketplaceVitrineVideo(anuncioId: number, file: File, variacaoId?: number | null) {
  const entidadeId = resolveAuthenticatedEntidadeIdOrThrow('Vídeo do anúncio do Marketplace')
  const body = new FormData()
  body.append('video', file)
  const variationQuery = variacaoId ? `?variacaoId=${Math.trunc(variacaoId)}` : ''
  return fetchApi<any>(`/loja-virtual/v1/compradores/${entidadeId}/marketplace-vitrine/anuncios/${Math.trunc(anuncioId)}/video${variationQuery}`, {
    method: 'POST',
    body,
  })
}

export async function deleteMarketplaceVitrineVideo(anuncioId: number, variacaoId?: number | null) {
  const entidadeId = resolveAuthenticatedEntidadeIdOrThrow('Vídeo do anúncio do Marketplace')
  const variationQuery = variacaoId ? `?variacaoId=${Math.trunc(variacaoId)}` : ''
  return fetchApi<any>(`/loja-virtual/v1/compradores/${entidadeId}/marketplace-vitrine/anuncios/${Math.trunc(anuncioId)}/video${variationQuery}`, {
    method: 'DELETE',
  })
}

export async function uploadMarketplaceVitrineManuais(anuncioId: number, files: File[], variacaoId?: number | null): Promise<MarketplaceVitrineManual[]> {
  const entidadeId = resolveAuthenticatedEntidadeIdOrThrow('Anexos do anúncio do Marketplace')
  if (files.length === 0) return []
  const body = new FormData()
  files.forEach((file) => body.append('manual', file))
  const variationQuery = variacaoId ? `?variacaoId=${Math.trunc(variacaoId)}` : ''
  const response = await fetchApi<any>(`/loja-virtual/v1/compradores/${entidadeId}/marketplace-vitrine/anuncios/${Math.trunc(anuncioId)}/manual${variationQuery}`, {
    method: 'POST',
    body,
  })
  const manuals = (Array.isArray(response?.manuais) ? response.manuais : [response?.manual])
    .map(mapMarketplaceVitrineManual)
    .filter((manual: MarketplaceVitrineManual | null): manual is MarketplaceVitrineManual => Boolean(manual))
  if (manuals.length !== files.length) throw new Error('Os anexos foram enviados, mas o retorno ficou incompleto.')
  return manuals
}

export async function uploadMarketplaceVitrineManual(anuncioId: number, file: File, variacaoId?: number | null): Promise<MarketplaceVitrineManual> {
  const manual = (await uploadMarketplaceVitrineManuais(anuncioId, [file], variacaoId))[0]
  if (!manual) throw new Error('O manual foi enviado, mas não retornou um identificador.')
  return manual
}

export async function deleteMarketplaceVitrineManual(anuncioId: number, variacaoId?: number | null) {
  const entidadeId = resolveAuthenticatedEntidadeIdOrThrow('Manual do anúncio do Marketplace')
  const variationQuery = variacaoId ? `?variacaoId=${Math.trunc(variacaoId)}` : ''
  return fetchApi<any>(`/loja-virtual/v1/compradores/${entidadeId}/marketplace-vitrine/anuncios/${Math.trunc(anuncioId)}/manual${variationQuery}`, {
    method: 'DELETE',
  })
}

export async function deleteMarketplaceVitrineManualById(anuncioId: number, manualId: number, variacaoId?: number | null) {
  const entidadeId = resolveAuthenticatedEntidadeIdOrThrow('Anexo do anúncio do Marketplace')
  const query = new URLSearchParams({ manualId: String(Math.trunc(manualId)) })
  if (variacaoId) query.set('variacaoId', String(Math.trunc(variacaoId)))
  return fetchApi<any>(`/loja-virtual/v1/compradores/${entidadeId}/marketplace-vitrine/anuncios/${Math.trunc(anuncioId)}/manual?${query.toString()}`, {
    method: 'DELETE',
  })
}

export async function replaceMarketplaceVitrineVariacoes(
  anuncioId: number,
  variacoes: Array<Pick<MarketplaceVitrineVariacao, 'id' | 'codigo' | 'ean' | 'marca' | 'modelo' | 'condicao' | 'descricao' | 'atributos' | 'estoque' | 'preco' | 'descontoPercentual' | 'fotoId'>>,
): Promise<MarketplaceVitrineVariacao[]> {
  const entidadeId = resolveAuthenticatedEntidadeIdOrThrow('Variações do anúncio do Marketplace')
  const response = await fetchApi<any>(`/loja-virtual/v1/compradores/${entidadeId}/marketplace-vitrine/anuncios/${Math.trunc(anuncioId)}/variacoes`, {
    method: 'PUT',
    body: JSON.stringify({ variacoes }),
  })
  return (Array.isArray(response?.variacoes) ? response.variacoes : [])
    .map(mapMarketplaceVitrineVariacao)
    .filter((item: MarketplaceVitrineVariacao | null): item is MarketplaceVitrineVariacao => Boolean(item))
}

export async function getMarketplaceVitrineSellerPerguntas(): Promise<{ items: MarketplaceVitrinePergunta[]; pendentes: number }> {
  const entidadeId = resolveAuthenticatedEntidadeIdOrThrow('Perguntas do Marketplace')
  const response = await fetchApi<any>(`/loja-virtual/v1/compradores/${entidadeId}/marketplace-vitrine/perguntas`)
  const items = (Array.isArray(response?.items) ? response.items : []).map((item: Record<string, unknown>) => ({
    id: normalizeEntidadeId(item.id) ?? 0,
    anuncioId: normalizeEntidadeId(item.anuncio_id) ?? 0,
    codigo: normalizeCheckoutText(item.codigo),
    titulo: normalizeCheckoutText(item.titulo),
    autor: normalizeCheckoutText(item.visitante_nome) || 'Cliente',
    visitanteNome: normalizeCheckoutText(item.visitante_nome),
    visitanteWhatsapp: normalizeCheckoutText(item.visitante_whatsapp),
    pergunta: normalizeCheckoutText(item.pergunta),
    resposta: normalizeCheckoutText(item.resposta),
    criadoEm: normalizeCheckoutText(item.criado_em) || null,
    respondidoEm: normalizeCheckoutText(item.respondido_em) || null,
  }))
  return { items, pendentes: Math.max(0, Math.trunc(normalizeFreteNumber(response?.pendentes) ?? 0)) }
}

export async function answerMarketplaceVitrinePergunta(id: number, resposta: string) {
  const entidadeId = resolveAuthenticatedEntidadeIdOrThrow('Resposta do Marketplace')
  return fetchApi<any>(`/loja-virtual/v1/compradores/${entidadeId}/marketplace-vitrine/perguntas/${Math.trunc(id)}/resposta`, {
    method: 'PUT',
    body: JSON.stringify({ resposta }),
  })
}

export async function getBuyerDistribuicao(): Promise<BuyerDistribuicao | null> {
  try {
    const entidadeId = resolveAuthenticatedEntidadeIdOrThrow('Meta de distribuição do comprador')
    const response = await fetchApi<any>(
      `/loja-virtual/v1/compradores/${entidadeId}/distribuicao`
    )
    const distribuicao = response?.distribuicao && typeof response.distribuicao === 'object'
      ? response.distribuicao
      : response

    if (!distribuicao || typeof distribuicao !== 'object') return null

    return {
      tabela_especial: Number(distribuicao.tabela_especial ?? 0) === 1 ? 1 : 0,
      faturamento_mensal_esperado: Number(distribuicao.faturamento_mensal_esperado ?? 0) || 0,
      faturamento_medio_ultimos_3_meses: Number(distribuicao.faturamento_medio_ultimos_3_meses ?? 0) || 0,
      faturamento_mes_atual: Number(distribuicao.faturamento_mes_atual ?? 0) || 0,
      data_para_alcancar: typeof distribuicao.data_para_alcancar === 'string'
        ? distribuicao.data_para_alcancar
        : null,
      prazo_ativo: distribuicao.prazo_ativo == null
        ? null
        : Number(distribuicao.prazo_ativo),
      beneficio_ativo: distribuicao.beneficio_ativo == null
        ? null
        : Number(distribuicao.beneficio_ativo),
      meta_alcancada: distribuicao.meta_alcancada == null
        ? null
        : Number(distribuicao.meta_alcancada),
      meta_alcancada_no_mes: distribuicao.meta_alcancada_no_mes == null
        ? null
        : Number(distribuicao.meta_alcancada_no_mes),
      meta_alcancada_media_3_meses: distribuicao.meta_alcancada_media_3_meses == null
        ? null
        : Number(distribuicao.meta_alcancada_media_3_meses),
    }
  } catch {
    return null
  }
}

export async function getMetrics(): Promise<any[]> {
  const entidadeId = resolveAuthenticatedEntidadeIdOrThrow('Métricas da conta')

  const [dadosResult, primeiraCompraResult] = await Promise.allSettled([
    fetchApi<any>(`/loja-virtual/v1/compradores/${entidadeId}/dados`),
    fetchApi<any>(`/loja-virtual/v1/compradores/${entidadeId}/primeira-compra`),
  ])

  const metrics: any[] = []

  if (dadosResult.status === 'fulfilled') {
    const dados = dadosResult.value
    const comprador = dados?.comprador && typeof dados.comprador === 'object'
      ? dados.comprador
      : dados

    const nome = String(comprador?.nome ?? '').trim()
    const email = String(comprador?.email ?? '').trim()
    const doc = String(comprador?.cpf_cnpj ?? comprador?.documento ?? '').trim()

    if (nome) {
      metrics.push({ id: 'comprador-nome', label: 'Comprador', value: nome, descricao: email || '' })
    }
    if (doc) {
      metrics.push({ id: 'comprador-doc', label: 'Documento', value: doc })
    }
  }

  if (primeiraCompraResult.status === 'fulfilled') {
    const primeiraCompra = primeiraCompraResult.value
    const isPrimeiraCompra = parseBooleanLike(
      primeiraCompra?.primeira_compra
      ?? primeiraCompra?.is_primeira_compra
      ?? primeiraCompra?.first_purchase
    )
    metrics.push({
      id: 'primeira-compra',
      label: 'Primeira compra',
      value: isPrimeiraCompra ? 'Sim' : 'Não',
    })
  }

  return metrics
}

export async function getBlogPosts(categoria?: string): Promise<any[]> {
  console.warn(`Endpoints de blog não existem no /api/docs (categoria: ${categoria || 'todas'}).`)
  return []
}

export async function getBlogCategories(): Promise<any[]> {
  console.warn('Endpoints de blog não existem no /api/docs.')
  return []
}

export async function getBlogPost(slug: string, categoria?: string): Promise<any> {
  console.warn(`Endpoint de detalhe do blog não existe no /api/docs (slug: ${slug}, categoria: ${categoria || 'n/a'}).`)
  return null
}

type AgentCallResponse = {
  status?: number
  resposta?: string | null
  http_code?: number | null
  motivo?: string | null
}

export type SupportTopicSuggestionCandidate = {
  id: string
  title: string
  categoryTitle?: string | null
}

export type SupportTopicSuggestionMatch = {
  topicId: string | null
  topicTitle: string | null
  reason?: string | null
}

export type SupportTopicSuggestion = {
  topicId: string | null
  topicTitle: string | null
  topics: SupportTopicSuggestionMatch[]
  reason: string | null
  rawResponse: string
}

export type TicketProtocolMode = 'atendimento' | 'reclamacao'
export type TicketNotificationChannel = 'whatsapp' | 'email'

export type TicketProtocolCreateInput = {
  mode?: TicketProtocolMode
  topicoId: number
  nome: string
  mensagem: string
  nota?: string | null
  empresa?: string | null
  email?: string | null
  whatsapp?: string | null
  cpfCnpj?: string | null
  produtoId?: number | null
  entidadeId?: number | null
  lojaId?: number | null
  canal?: number | null
  sobre?: number | null
  notificationChannel?: TicketNotificationChannel | null
  attachments?: File[] | null
}

export type TicketProtocolCreateResult = {
  id: number
  protocolo: string
  topicoId: number
  entidadeId: number | null
  assunto: string | null
  whatsappOptIn: TicketWhatsappPublicOptIn | null
  attachments: Array<Record<string, unknown>>
  raw: Record<string, unknown>
}

export type TicketWhatsappPublicOptIn = {
  sessionId: string
  phone: string
  message: string
  url: string
}

export type TicketWhatsappConsentPrepareResult = {
  authorized: boolean
  whatsappOptIn: TicketWhatsappPublicOptIn | null
}

function normalizeSuggestionText(value: unknown): string | null {
  if (typeof value === 'string') {
    const trimmed = value.trim()
    return trimmed || null
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value)
  }

  return null
}

function stripMarkdownJsonFence(value: string): string {
  return value
    .trim()
    .replace(/^```(?:json)?\s*/i, '')
    .replace(/\s*```$/i, '')
    .trim()
}

function parseSupportTopicSuggestionMatch(payload: unknown): SupportTopicSuggestionMatch | null {
  if (!payload || typeof payload !== 'object') return null
  const record = payload as Record<string, unknown>
  const topicId = normalizeSuggestionText(record.topicId ?? record.id ?? record.subjectId ?? record.topicoId)
  const topicTitle = normalizeSuggestionText(
    record.topicTitle
    ?? record.title
    ?? record.subjectTitle
    ?? record.topic
    ?? record.topico
  )
  const reason = normalizeSuggestionText(
    record.reason
    ?? record.motivo
    ?? record.justification
    ?? record.justificativa
  )

  if (!topicId && !topicTitle) return null

  return {
    topicId,
    topicTitle,
    reason,
  }
}

function parseSupportTopicSuggestionMatches(payload: unknown): SupportTopicSuggestionMatch[] {
  const source = Array.isArray(payload)
    ? payload
    : payload && typeof payload === 'object'
      ? (
          (payload as Record<string, unknown>).topics
          ?? (payload as Record<string, unknown>).results
          ?? (payload as Record<string, unknown>).suggestions
          ?? (payload as Record<string, unknown>).matches
          ?? (payload as Record<string, unknown>).topicos
          ?? payload
        )
      : []

  const items = Array.isArray(source) ? source : [source]
  const seenKeys = new Set<string>()

  return items.reduce<SupportTopicSuggestionMatch[]>((accumulator, item) => {
    const match = parseSupportTopicSuggestionMatch(item)
    if (!match) return accumulator

    const key = `${normalizeSuggestionText(match.topicId)?.toLocaleLowerCase('pt-BR') || ''}|${normalizeSuggestionText(match.topicTitle)?.toLocaleLowerCase('pt-BR') || ''}`
    if (seenKeys.has(key)) return accumulator

    seenKeys.add(key)
    accumulator.push(match)
    return accumulator
  }, [])
}

function parseSupportTopicSuggestionPayload(payload: unknown, rawResponse: string): SupportTopicSuggestion | null {
  const matches = parseSupportTopicSuggestionMatches(payload)
  if (matches.length === 0) return null

  const record = payload && typeof payload === 'object' && !Array.isArray(payload)
    ? payload as Record<string, unknown>
    : {}
  const [primaryMatch] = matches
  const reason = normalizeSuggestionText(
    record.reason
    ?? record.motivo
    ?? record.justification
    ?? record.justificativa
    ?? record.summary
    ?? record.resumo
  ) ?? matches.find((match) => match.reason)?.reason ?? null

  return {
    topicId: primaryMatch.topicId,
    topicTitle: primaryMatch.topicTitle,
    topics: matches,
    reason,
    rawResponse,
  }
}

function parseSupportTopicSuggestionResponse(rawResponse: string): SupportTopicSuggestion | null {
  const normalized = stripMarkdownJsonFence(rawResponse)
  if (!normalized) return null

  const parseCandidate = (candidate: string) => {
    try {
      return JSON.parse(candidate)
    } catch {
      return null
    }
  }

  const directPayload = parseCandidate(normalized)
  const directSuggestion = parseSupportTopicSuggestionPayload(directPayload, rawResponse)
  if (directSuggestion) {
    return directSuggestion
  }

  const jsonStart = normalized.indexOf('{')
  const jsonEnd = normalized.lastIndexOf('}')
  const arrayStart = normalized.indexOf('[')
  const arrayEnd = normalized.lastIndexOf(']')

  if (jsonStart !== -1 && jsonEnd > jsonStart) {
    const nestedPayload = parseCandidate(normalized.slice(jsonStart, jsonEnd + 1))
    const nestedSuggestion = parseSupportTopicSuggestionPayload(nestedPayload, rawResponse)
    if (nestedSuggestion) {
      return nestedSuggestion
    }
  }

  if (arrayStart === -1 || arrayEnd <= arrayStart) {
    return null
  }

  const nestedArrayPayload = parseCandidate(normalized.slice(arrayStart, arrayEnd + 1))
  return parseSupportTopicSuggestionPayload(nestedArrayPayload, rawResponse)
}

function buildSupportTopicSuggestionPrompt(
  customerNeed: string,
  candidates: SupportTopicSuggestionCandidate[]
): string {
  const topicsList = candidates
    .map((candidate, index) => {
      const categoryTitle = normalizeDescriptorValue(candidate.categoryTitle)
      return `${index + 1}. topicId="${candidate.id}" | categoria="${categoryTitle || 'Sem categoria'}" | titulo="${candidate.title}"`
    })
    .join('\n')

  return [
    'Você classifica solicitações de atendimento de uma loja B2B.',
    'Analise a dúvida do cliente e escolha de 1 a 3 tópicos da lista disponível, em ordem de aderência.',
    'Se existir uma opção claramente melhor, coloque-a primeiro e mantenha só ela se fizer sentido.',
    'Se a dúvida estiver ambígua, traga até 3 tópicos plausíveis.',
    'Use somente topicIds listados abaixo. Não invente ids, títulos ou categorias.',
    'Não repita tópicos.',
    'Se nenhum tópico servir de forma razoável, retorne "topics" vazio.',
    'Responda somente com JSON válido em uma única linha, sem markdown.',
    'Formato obrigatório: {"topics":[{"topicId":"...","topicTitle":"..."}],"reason":"..."}',
    'Mantenha "reason" curto, em português e com no máximo 140 caracteres.',
    'Tópicos disponíveis:',
    topicsList,
    `Dúvida do cliente: """${customerNeed.trim()}"""`,
  ].join('\n\n')
}

export async function suggestSupportTopic(
  customerNeed: string,
  candidates: SupportTopicSuggestionCandidate[],
  options?: {
    provider?: 'generate' | 'gemini'
  }
): Promise<SupportTopicSuggestion> {
  const normalizedNeed = customerNeed.trim()
  const normalizedCandidates = candidates
    .map((candidate) => ({
      id: normalizeDescriptorValue(candidate.id),
      title: normalizeDescriptorValue(candidate.title),
      categoryTitle: normalizeDescriptorValue(candidate.categoryTitle ?? ''),
    }))
    .filter((candidate) => candidate.id && candidate.title)

  if (!normalizedNeed) {
    throw new Error('Consulta de atendimento vazia.')
  }

  if (normalizedCandidates.length === 0) {
    throw new Error('Nenhum tópico de atendimento disponível para sugestão.')
  }

  const response = await fetchApi<AgentCallResponse>('/chat/call', {
    method: 'POST',
    body: JSON.stringify({
      text: buildSupportTopicSuggestionPrompt(normalizedNeed, normalizedCandidates),
      provider: options?.provider ?? 'generate',
    }),
  })

  const rawResponse = normalizeSuggestionText(response?.resposta)
  if (!rawResponse) {
    throw new Error(response?.motivo || 'Resposta vazia da IA para atendimento.')
  }

  const suggestion = parseSupportTopicSuggestionResponse(rawResponse)
  if (!suggestion) {
    throw new Error('Resposta da IA fora do formato esperado para atendimento.')
  }

  return suggestion
}

export async function getAtendimentoTopicos(): Promise<any[]> {
  const pageSize = 100
  const maxPages = 50
  const topicsByKey = new Map<string, any>()

  for (let page = 1; page <= maxPages; page += 1) {
    const response = await fetchApi<unknown>(`/integracoes/v1/tickets/topicos?page=${page}&pageSize=${pageSize}`)
    const items = extractListFromResponse(response, ['items', 'topicos', 'topics'])

    items.forEach((item, index) => {
      if (item && typeof item === 'object') {
        const record = item as Record<string, unknown>
        const key = String(record.id ?? record.codigo ?? record.titulo ?? record.nome ?? `${page}-${index}`)
        topicsByKey.set(key, item)
        return
      }

      topicsByKey.set(`${page}-${index}`, item)
    })

    const totalPages = extractTotalPagesFromResponse(response)
    if (totalPages && page >= totalPages) break
    if (items.length < pageSize) break
  }

  return Array.from(topicsByKey.values())
}

export async function getAtendimentoTexto(id: number): Promise<any> {
  const response = await fetchApi<any>(`/integracoes/v1/tickets/topicos/${id}`)
  if (response && typeof response === 'object' && response.item && typeof response.item === 'object') {
    return response.item
  }
  return response
}

function normalizeTicketProtocolText(value: unknown): string {
  if (typeof value === 'string') return value.trim()
  if (typeof value === 'number' && Number.isFinite(value)) return String(value)
  return ''
}

function normalizeTicketWhatsappPublicOptIn(value: unknown): TicketWhatsappPublicOptIn | null {
  if (!value || typeof value !== 'object') return null
  const record = value as Record<string, unknown>
  const available = record.available === true || Number(record.available ?? 0) === 1
  const sessionId = normalizeTicketProtocolText(record.session_id ?? record.sessionId)
  const phone = normalizeTicketProtocolText(record.phone).replace(/\D+/g, '')
  const message = normalizeTicketProtocolText(record.message)
  const url = normalizeTicketProtocolText(record.url)
  if (!available || !sessionId || !phone || !message || !url) return null

  try {
    const parsed = new URL(url)
    if (parsed.protocol !== 'https:' || parsed.hostname.toLowerCase() !== 'wa.me') return null
  } catch {
    return null
  }

  return { sessionId, phone, message, url }
}

export async function prepareTicketWhatsappConsent(
  whatsapp: string
): Promise<TicketWhatsappConsentPrepareResult> {
  const response = await fetchApi<unknown>('/integracoes/v1/tickets/whatsapp/consentimento/preparar', {
    method: 'POST',
    body: JSON.stringify({ whatsapp }),
  })

  if (!response || typeof response !== 'object') {
    throw new Error('Resposta inválida ao preparar a permissão do WhatsApp.')
  }

  const record = response as Record<string, unknown>
  const authorized = record.autorizado === true || Number(record.autorizado ?? 0) === 1
  const whatsappOptIn = normalizeTicketWhatsappPublicOptIn(record.whatsapp_opt_in)
  if (!authorized && !whatsappOptIn) {
    throw new Error('Não foi possível preparar a permissão do WhatsApp.')
  }

  return { authorized, whatsappOptIn }
}

export async function checkTicketWhatsappConsent(whatsapp: string): Promise<boolean> {
  const response = await fetchApi<unknown>('/integracoes/v1/tickets/whatsapp/consentimento/status', {
    method: 'POST',
    body: JSON.stringify({ whatsapp }),
  })

  if (!response || typeof response !== 'object') {
    throw new Error('Resposta inválida ao verificar a permissão do WhatsApp.')
  }

  const record = response as Record<string, unknown>
  return record.autorizado === true || Number(record.autorizado ?? 0) === 1
}

function normalizeNonNegativeInteger(value: unknown, fallback = 0): number {
  if (typeof value === 'number' && Number.isFinite(value) && value >= 0) {
    return Math.trunc(value)
  }

  if (typeof value === 'string') {
    const parsed = Number.parseInt(value, 10)
    if (Number.isFinite(parsed) && parsed >= 0) {
      return parsed
    }
  }

  return fallback
}

export async function createTicketProtocol(input: TicketProtocolCreateInput): Promise<TicketProtocolCreateResult> {
  const mode: TicketProtocolMode = input.mode === 'reclamacao' ? 'reclamacao' : 'atendimento'
  const topicoId = normalizePositiveInteger(input.topicoId)
  const nome = normalizeTicketProtocolText(input.nome)
  const mensagem = normalizeTicketProtocolText(input.mensagem)
  const attachments = Array.isArray(input.attachments)
    ? input.attachments.filter((file): file is File => file instanceof File)
    : []

  if (!topicoId) {
    throw new Error('Tópico de atendimento inválido.')
  }

  if (!nome) {
    throw new Error('Nome é obrigatório para abrir o atendimento.')
  }

  if (!mensagem) {
    throw new Error('Mensagem é obrigatória para abrir o atendimento.')
  }

  const body: Record<string, unknown> = {
    topico_id: topicoId,
    nome,
    mensagem,
    canal: normalizeNonNegativeInteger(input.canal, 0),
    sobre: normalizeNonNegativeInteger(input.sobre, 0),
  }

  if (input.notificationChannel === 'whatsapp' || input.notificationChannel === 'email') {
    body.canal_notificacao = input.notificationChannel
  }

  const lojaId = getLojaIdValue(input.lojaId ?? undefined)
  const entidadeId = normalizePositiveInteger(input.entidadeId)
  const produtoId = normalizePositiveInteger(input.produtoId)
  const cpfCnpj = normalizeTicketProtocolText(input.cpfCnpj).replace(/\D+/g, '')

  if (lojaId) body.loja_id = lojaId
  if (entidadeId) body.entidade_id = entidadeId
  if (produtoId) body.produto_id = produtoId
  if (cpfCnpj) body.cpf_cnpj = cpfCnpj

  const optionalTextFields: Array<[string, unknown]> = [
    ['nota', input.nota],
    ['empresa', input.empresa],
    ['email', input.email],
    ['whatsapp', input.whatsapp],
  ]

  optionalTextFields.forEach(([key, value]) => {
    const normalized = normalizeTicketProtocolText(value)
    if (normalized) body[key] = normalized
  })

  const requestBody = (() => {
    if (attachments.length === 0) {
      return JSON.stringify(body)
    }

    const formData = new FormData()
    Object.entries(body).forEach(([key, value]) => {
      if (value === undefined || value === null) return
      formData.append(key, String(value))
    })
    attachments.forEach((file) => {
      formData.append('anexos', file, file.name)
    })
    return formData
  })()

  const response = await fetchApi<unknown>(`/integracoes/v1/tickets/protocolos/${mode}`, {
    method: 'POST',
    body: requestBody,
  })

  if (!response || typeof response !== 'object') {
    throw new Error('Resposta inválida ao criar atendimento.')
  }

  const record = response as Record<string, unknown>
  const status = Number(record.status ?? 1)
  if (status === 0) {
    throw new Error(normalizeTicketProtocolText(record.motivo) || 'Não foi possível criar o atendimento.')
  }

  const id = normalizePositiveInteger(record.id)
  const protocolo = normalizeTicketProtocolText(record.protocolo)

  if (!id || !protocolo) {
    throw new Error('Atendimento criado sem protocolo válido.')
  }

  return {
    id,
    protocolo,
    topicoId: normalizePositiveInteger(record.topico_id) ?? topicoId,
    entidadeId: normalizePositiveInteger(record.entidade_id),
    assunto: normalizeTicketProtocolText(record.assunto) || null,
    whatsappOptIn: normalizeTicketWhatsappPublicOptIn(record.whatsapp_opt_in),
    attachments: normalizeAttendanceAttachments(record),
    raw: record,
  }
}

const normalizeCategoryModel = (value: StorefrontCategoryModel | string | null | undefined): StorefrontCategoryModel =>
  value === 'classes' ? 'classes' : 'grupos'

export async function getMenuTopLevel(options?: { categoryModel?: StorefrontCategoryModel }): Promise<any[]> {
  const categoryModel = normalizeCategoryModel(options?.categoryModel)
  const cacheKey = categoryModel

  const topLevelCache = menuTreeTopLevelCache.get(cacheKey)
  if (topLevelCache) {
    return topLevelCache
  }

  const shallowCache = menuTreeShallowCache.get(cacheKey)
  if (shallowCache) {
    const topLevelFromShallow = stripMenuTreeToTopLevel(shallowCache)
    menuTreeTopLevelCache.set(cacheKey, topLevelFromShallow)
    return topLevelFromShallow
  }

  const fullCache = menuTreeCache.get(cacheKey)
  if (fullCache) {
    const topLevelFromFull = stripMenuTreeToTopLevel(fullCache)
    menuTreeTopLevelCache.set(cacheKey, topLevelFromFull)
    return topLevelFromFull
  }

  const pendingTopLevelRequest = menuTreeTopLevelRequest.get(cacheKey)
  if (pendingTopLevelRequest) {
    return pendingTopLevelRequest
  }

  const request = (async () => {
    try {
      const menuTree = categoryModel === 'classes'
        ? stripMenuTreeToTopLevel(await fetchMenuTreeFromClassesEndpoint())
        : stripMenuTreeToTopLevel(await fetchMenuTreeWithStaleFallback(false))
      menuTreeTopLevelCache.set(cacheKey, menuTree)
      return menuTree
    } finally {
      menuTreeTopLevelRequest.delete(cacheKey)
    }
  })()

  menuTreeTopLevelRequest.set(cacheKey, request)
  return request
}

export async function getMenu(options?: { includeProductGroups?: boolean; categoryModel?: StorefrontCategoryModel }): Promise<any[]> {
  const includeProductGroups = options?.includeProductGroups ?? true
  const categoryModel = normalizeCategoryModel(options?.categoryModel)
  const cacheKey = categoryModel

  if (!includeProductGroups) {
    const shallowCache = menuTreeShallowCache.get(cacheKey)
    if (shallowCache) {
      return shallowCache
    }

    const fullCache = menuTreeCache.get(cacheKey)
    if (fullCache) {
      const shallowFromFull = stripMenuTreeToShallow(fullCache)
      menuTreeShallowCache.set(cacheKey, shallowFromFull)
      return shallowFromFull
    }

    const pendingShallowRequest = menuTreeShallowRequest.get(cacheKey)
    if (pendingShallowRequest) {
      return pendingShallowRequest
    }

    const request = (async () => {
      try {
        const menuTree = categoryModel === 'classes'
          ? await fetchMenuTreeFromClassesEndpoint()
          : await fetchMenuTreeWithStaleFallback(false)
        menuTreeShallowCache.set(cacheKey, menuTree)
        return menuTree
      } finally {
        menuTreeShallowRequest.delete(cacheKey)
      }
    })()

    menuTreeShallowRequest.set(cacheKey, request)
    return request
  }

  const fullCache = menuTreeCache.get(cacheKey)
  if (fullCache) {
    return fullCache
  }

  const pendingFullRequest = menuTreeRequest.get(cacheKey)
  if (pendingFullRequest) {
    return pendingFullRequest
  }

  const request = (async () => {
    try {
      const menuTree = categoryModel === 'classes'
        ? await fetchMenuTreeFromClassesEndpoint()
        : await fetchMenuTreeWithStaleFallback(true)
      menuTreeCache.set(cacheKey, menuTree)
      if (!menuTreeShallowCache.has(cacheKey)) {
        menuTreeShallowCache.set(cacheKey, stripMenuTreeToShallow(menuTree))
      }
      return menuTree
    } finally {
      menuTreeRequest.delete(cacheKey)
    }
  })()

  menuTreeRequest.set(cacheKey, request)
  return request
}

export async function subscribePush(subscription: any, userId?: number): Promise<{ message: string }> {
  console.warn('Endpoint de push/subscribe não existe no /api/docs.')
  void subscription
  void userId
  return { message: 'Endpoint de push indisponível no /api/docs.' }
}

export async function createAffiliateLink(
  produtoId: number,
  desiredEarnings: number,
  buyer?: AffiliateBuyerInput | null
): Promise<AffiliateLinkCreateResponse> {
  const entidadeId = resolveAuthenticatedEntidadeIdOrThrow('Geração de link de afiliado')
  return fetchApi<AffiliateLinkCreateResponse>(`/loja-virtual/v1/compradores/${entidadeId}/afiliados/links`, {
    method: 'POST',
    body: JSON.stringify({
      produto_id: produtoId,
      loja_id: getLojaIdValue(),
      desired_earnings: desiredEarnings,
      comprador: buildAffiliateBuyerPayload(buyer),
    }),
  })
}

export const buildAffiliateBuyerPayload = (buyer?: AffiliateBuyerInput | null) => {
  if (!buyer) return undefined
  if ('entidadeId' in buyer) return { entidade_id: buyer.entidadeId }
  return {
    tipo_pessoa: buyer.tipoPessoa,
    nome: buyer.nome,
    razao: buyer.razaoSocial || buyer.nome,
    cpf_cnpj: buyer.cpfCnpj,
    ie: buyer.inscricaoEstadual,
    simples: buyer.simples,
    email: buyer.email,
    whatsapp: buyer.whatsapp,
    telefone: buyer.whatsapp,
    contato: buyer.contato,
    contato_cargo: buyer.contatoCargo,
    contato_aceita_marketing: buyer.aceitaMarketing ? 1 : 0,
    tipo_entidade_atividade_id: buyer.atividadeId,
    atividade: buyer.atividade,
    finalidade: buyer.finalidade,
    cep: buyer.cep,
    endereco: buyer.endereco,
    numero: buyer.numero,
    bairro: buyer.bairro,
    complemento: buyer.complemento,
    cidade: buyer.cidade,
    uf: buyer.uf,
  }
}

const AFFILIATE_PRICE_CACHE_TTL_MS = 30_000
const AFFILIATE_PRICE_CACHE_MAX_ENTRIES = 100
const affiliatePriceCache = new Map<string, { value: AffiliatePriceResponse; expiresAt: number }>()
const affiliatePriceRequests = new Map<string, Promise<AffiliatePriceResponse>>()

const buildAffiliatePriceCacheKey = (
  entidadeId: number,
  produtoId: number,
  desiredEarnings: number,
  buyer?: AffiliateBuyerInput | null,
) => JSON.stringify([
  entidadeId,
  getLojaIdValue() ?? 0,
  produtoId,
  Math.round(desiredEarnings * 100) / 100,
  buildAffiliateBuyerPayload(buyer) ?? null,
])

const readAffiliatePriceCache = (cacheKey: string) => {
  const cached = affiliatePriceCache.get(cacheKey)
  if (!cached) return null
  if (cached.expiresAt <= Date.now()) {
    affiliatePriceCache.delete(cacheKey)
    return null
  }
  return cached.value
}

const writeAffiliatePriceCache = (cacheKey: string, value: AffiliatePriceResponse) => {
  if (affiliatePriceCache.size >= AFFILIATE_PRICE_CACHE_MAX_ENTRIES) {
    const oldestKey = affiliatePriceCache.keys().next().value
    if (typeof oldestKey === 'string') affiliatePriceCache.delete(oldestKey)
  }
  affiliatePriceCache.set(cacheKey, {
    value,
    expiresAt: Date.now() + AFFILIATE_PRICE_CACHE_TTL_MS,
  })
}

export function getCachedAffiliatePrice(
  produtoId: number,
  desiredEarnings: number,
  buyer?: AffiliateBuyerInput | null,
): AffiliatePriceResponse | null {
  try {
    const entidadeId = resolveAuthenticatedEntidadeIdOrThrow('Consulta do preço de afiliado em cache')
    return readAffiliatePriceCache(buildAffiliatePriceCacheKey(entidadeId, produtoId, desiredEarnings, buyer))
  } catch {
    return null
  }
}

export async function calculateAffiliatePrice(
  produtoId: number,
  desiredEarnings: number,
  buyer?: AffiliateBuyerInput | null
): Promise<AffiliatePriceResponse> {
  const entidadeId = resolveAuthenticatedEntidadeIdOrThrow('Cálculo de preço de afiliado')
  const cacheKey = buildAffiliatePriceCacheKey(entidadeId, produtoId, desiredEarnings, buyer)
  const cached = readAffiliatePriceCache(cacheKey)
  if (cached) return cached

  const pending = affiliatePriceRequests.get(cacheKey)
  if (pending) return pending

  const request = fetchApi<AffiliatePriceResponse>(`/loja-virtual/v1/compradores/${entidadeId}/afiliados/preco`, {
    method: 'POST',
    body: JSON.stringify({
      produto_id: produtoId,
      loja_id: getLojaIdValue(),
      desired_earnings: desiredEarnings,
      comprador: buildAffiliateBuyerPayload(buyer),
    }),
  })
    .then((response) => {
      writeAffiliatePriceCache(cacheKey, response)
      return response
    })
    .finally(() => {
      affiliatePriceRequests.delete(cacheKey)
    })

  affiliatePriceRequests.set(cacheKey, request)
  return request
}

export function prefetchAffiliatePrice(
  produtoId: number,
  desiredEarnings = 0,
  buyer?: AffiliateBuyerInput | null,
) {
  void calculateAffiliatePrice(produtoId, desiredEarnings, buyer).catch(() => undefined)
}

const mapAffiliateCartItems = (items: AffiliateCartItemInput[]) => items.map((item) => ({
  produto_id: item.productId,
  quantidade: item.quantity,
  desired_earnings: item.desiredEarnings,
}))

export async function calculateAffiliateCartPrice(
  items: AffiliateCartItemInput[]
): Promise<AffiliateCartPriceResponse> {
  const entidadeId = resolveAuthenticatedEntidadeIdOrThrow('Cálculo do carrinho afiliado')
  return fetchApi<AffiliateCartPriceResponse>(
    `/loja-virtual/v1/compradores/${entidadeId}/afiliados/carrinho/preco`,
    {
      method: 'POST',
      body: JSON.stringify({ loja_id: getLojaIdValue(), itens: mapAffiliateCartItems(items) }),
    }
  )
}

export async function createAffiliateCartLink(
  items: AffiliateCartItemInput[]
): Promise<AffiliateCartLinkCreateResponse> {
  const entidadeId = resolveAuthenticatedEntidadeIdOrThrow('Geração de link do carrinho afiliado')
  return fetchApi<AffiliateCartLinkCreateResponse>(
    `/loja-virtual/v1/compradores/${entidadeId}/afiliados/carrinho/links`,
    {
      method: 'POST',
      body: JSON.stringify({ loja_id: getLojaIdValue(), itens: mapAffiliateCartItems(items) }),
    }
  )
}

export async function getAffiliateCheckoutContext(
  code: string
): Promise<AffiliateCheckoutContextResponse> {
  return fetchApi<AffiliateCheckoutContextResponse>(
    `/loja-virtual/v1/checkout-rapido/afiliados/${encodeURIComponent(code)}`,
    QUICK_CHECKOUT_REQUEST_OPTIONS
  )
}

export async function addProductTag(id: number, tag: string): Promise<{ message: string }> {
  const numericId = Number(id)
  if (!Number.isFinite(numericId) || numericId <= 0) {
    throw new Error('Produto invalido para envio da sugestao.')
  }

  const normalizedTag = tag.trim().replace(/\s+/g, ' ')
  if (!normalizedTag) {
    throw new Error('Sugestao vazia.')
  }

  const lojaId = getLojaIdValue()
  const query = new URLSearchParams()
  if (lojaId) {
    query.set('loja_id', String(lojaId))
  }

  const response = await fetchApi<Record<string, unknown>>(
    `/loja-virtual/v1/produtos/${Math.trunc(numericId)}/tags${query.toString() ? `?${query.toString()}` : ''}`,
    {
      method: 'POST',
      body: JSON.stringify({
        produto_id: Math.trunc(numericId),
        tag: normalizedTag,
      }),
    }
  )

  return {
    message: String(response?.motivo || response?.message || 'Sugestão enviada.'),
  }
}
